/**
 * ================================================================
 * 辅助工作台 · 移动端适配规范
 * ================================================================
 * 【重要】本文件所有 UI 组件必须遵循以下移动端适配规则：
 *
 * 1. 字体响应式：用 sx={{ fontSize: { xs: 12, sm: 14 } }} 而非固定 px
 * 2. 间距响应式：用 sx={{ p: { xs: 1.5, sm: 2 }, mb: { xs: 1, sm: 2 } }} 而非固定数值
 * 3. 高度响应式：图标/按钮/输入框等 minWidth/minHeight 用 { xs: 34, sm: 42 }
 * 4. Chip/标签响应式：height/fontSize 用 { xs: 22, sm: 26 } 等
 * 5. 禁止全局污染：≤600px 媒体查询必须限定在具体 className 上（如 .editor-container），
 *    禁止使用全局 .MuiBox-root、.MuiGrid-* 等，否则 Workspace/Portal 全屏面板会被压窄
 * 6. 宽度控制：移动端 Panel 全屏用 Portal 挂 body，内容 width: 100%，maxWidth: 100vw
 * 7. 底栏文字极窄时（≤520px）用短文案，可通过 isToolTrayCompact 媒体查询切换
 * ================================================================
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Tooltip,
  MenuItem,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  LinearProgress,
  Slider,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Card,
  CardContent,
  Grid,
  TextareaAutosize,
  Checkbox,
  Radio,
  RadioGroup,
  Slide,
  Zoom,
  Portal,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Book as BookIcon,
  AutoStories as AutoStoriesIcon,
  AutoFixHigh as AutoFixHighIcon,
  Search as SearchIcon,
  LibraryBooks as LibraryBooksIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ContentCopy as ContentCopyIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  Note as NoteIcon,
  PlayArrow as PlayArrowIcon,
  AutoAwesome as AutoAwesomeIcon,
  Warning as WarningIcon,
  CreateNewFolder as CreateNewFolderIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Menu as MenuIcon,
  ArrowForward as ArrowForwardIcon,
  OpenInNew as OpenInNewIcon,
  Grass as GrassIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Psychology as PsychologyIcon,
  Summarize as SummarizeIcon,
  RateReview as RateReviewIcon,
  FindInPage as FindInPageIcon,
  CleaningServices as CleanServicesIcon,
  Hub as HubIcon,
  AccountTree as AccountTreeIcon,
  Bolt as BoltIcon,
  Landscape as LandscapeIcon,
  Forum as ForumIcon,
  Visibility as VisibilityIcon,
  LocalFireDepartment as LocalFireDepartmentIcon,
  MovieCreation as MovieCreationIcon,
  Speed as SpeedIcon,
  Favorite as FavoriteIcon,
  Balance as BalanceIcon,
  ImportContacts as ImportContactsIcon,
  MenuBook as MenuBookIcon,
  EmojiEvents as EmojiEventsIcon,
  Link as LinkChainIcon,
  BlurOn as BlurOnIcon,
  Article as ArticleIcon,
  CompareArrows as CompareArrowsIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  TheaterComedy as TheaterComedyIcon,
} from '@mui/icons-material';
import apiClient, { getApiBaseUrl } from '../utils/apiInterceptor';
import { handleStreamGeneration } from '../utils/streamCompletionHandler';
import { robustJsonParse } from '../utils/robustJsonParser';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/nexusTheme.css';
import './Workspace.css';

// 获取 API 基础 URL
const API_BASE_URL = getApiBaseUrl();

// 格式化时间显示（用于保存时间）
const formatTimeDisplay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const diff = now - d;
  // 1分钟内显示"刚刚"
  if (diff < 60000) return '刚刚';
  // 1小时内显示"X分钟前"
  if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`;
  // 24小时内显示"X小时前"
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`;
  // 超过24小时显示具体时间
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** AI 待插入列表的章节标题：去掉【】、[]、{} 等符号，供「插入正文」写入列表用 */
const sanitizeChapterTitleForList = (raw) => {
  if (raw == null || typeof raw !== 'string') return '';
  const s = raw.replace(/[【】\[\]{}｛｝]/g, '').replace(/^[：:\s]+/, '').trim();
  return s.length > 30 ? s.slice(0, 30) : s;
};

/** 章纲「本章出场角色」：从角色数据推断展示用定位（与角色列表排序逻辑一致） */
const getCharacterDisplayRoleLabel = (char) => {
  if (!char) return '未分类';
  const tags = Array.isArray(char.tags) ? char.tags : [];
  const title = String(char.title || '');
  const role = String(char.role || '').toLowerCase();
  const combined = [...tags, title, char.role || ''].join(' ').toLowerCase();
  if (role === 'protagonist' || combined.includes('主角') || combined.includes('男主') || combined.includes('女主')) return '主角';
  if (combined.includes('反派')) return '反派';
  if (combined.includes('导师') || combined.includes('师父')) return '导师';
  if (combined.includes('恋人') || combined.includes('女友') || combined.includes('男友')) return '恋人';
  if (combined.includes('宿敌')) return '宿敌';
  if (combined.includes('npc')) return 'NPC';
  if (tags.includes('配角') || combined.includes('配角')) return '配角';
  if (tags.length > 0) return String(tags[0]).slice(0, 8);
  if (title.trim()) return title.trim().slice(0, 8);
  return '配角';
};

/** 文本相似度检测（N-gram算法）：基于字符级N-gram计算两段文本的相似度
 * @param {string} text1 - 文本1
 * @param {string} text2 - 文本2
 * @param {number} n - N-gram的大小，默认3
 * @returns {number} 相似度值，范围0-1，值越大表示越相似
 */
const calculateTextSimilarity = (text1, text2, n = 3) => {
  if (!text1 || !text2) return 0;
  if (text1 === text2) return 1;
  
  // 清理文本：移除空白字符并转为小写
  const cleanText1 = text1.replace(/\s+/g, '').toLowerCase();
  const cleanText2 = text2.replace(/\s+/g, '').toLowerCase();
  
  // 生成N-gram集合
  const getNgrams = (text, n) => {
    const ngrams = new Set();
    for (let i = 0; i <= text.length - n; i++) {
      ngrams.add(text.slice(i, i + n));
    }
    return ngrams;
  };
  
  const ngrams1 = getNgrams(cleanText1, n);
  const ngrams2 = getNgrams(cleanText2, n);
  
  // 计算Jaccard相似度
  const intersection = new Set([...ngrams1].filter(x => ngrams2.has(x)));
  const union = new Set([...ngrams1, ...ngrams2]);
  
  return union.size > 0 ? intersection.size / union.size : 0;
};

/** 检测重复内容并返回详细报告
 * @param {string} newContent - 新生成的内容
 * @param {Array} prevChapters - 前几章的内容列表 [{title, content}]
 * @param {number} threshold - 相似度阈值，默认0.6（60%）
 * @returns {{hasRepetition: boolean, maxSimilarity: number, mostSimilarChapter: object, details: string}}
 */
const detectContentRepetition = (newContent, prevChapters, threshold = 0.6) => {
  if (!newContent || newContent.length < 50 || !Array.isArray(prevChapters) || prevChapters.length === 0) {
    return { hasRepetition: false, maxSimilarity: 0, mostSimilarChapter: null, details: '' };
  }
  
  let maxSimilarity = 0;
  let mostSimilarChapter = null;
  const repetitionDetails = [];
  
  for (const chapter of prevChapters) {
    const prevContent = chapter.content || '';
    if (!prevContent || prevContent.length < 50) continue;
    
    // 计算整体相似度
    const similarity = calculateTextSimilarity(newContent, prevContent, 3);
    
    if (similarity > maxSimilarity) {
      maxSimilarity = similarity;
      mostSimilarChapter = chapter;
    }
    
    // 如果整体相似度高，进一步检测具体重复段落
    if (similarity > threshold * 0.8) {
      // 检测重复句式
      const newSentences = newContent.split(/[。！？\n]/).filter(s => s.length > 20);
      const prevSentences = prevContent.split(/[。！？\n]/).filter(s => s.length > 20);
      
      for (const ns of newSentences) {
        for (const ps of prevSentences) {
          const sentSim = calculateTextSimilarity(ns, ps, 5);
          if (sentSim > threshold) {
            repetitionDetails.push({
              type: '相似句式',
              newText: ns.slice(0, 50) + (ns.length > 50 ? '...' : ''),
              prevText: ps.slice(0, 50) + (ps.length > 50 ? '...' : ''),
              similarity: Math.round(sentSim * 100) + '%'
            });
          }
        }
      }
    }
  }
  
  return {
    hasRepetition: maxSimilarity >= threshold,
    maxSimilarity: Math.round(maxSimilarity * 100) / 100,
    mostSimilarChapter,
    details: repetitionDetails.length > 0 ? repetitionDetails : []
  };
};

/** 检测内容重复度并返回提示信息
 * @param {string} newContent - 新生成的内容
 * @param {Array} prevChapters - 前几章的内容列表
 * @returns {string} 提示信息，如果无重复返回空字符串
 */
const getRepetitionWarningMessage = (newContent, prevChapters) => {
  const result = detectContentRepetition(newContent, prevChapters, 0.6);
  
  if (!result.hasRepetition) return '';
  
  const chapterInfo = result.mostSimilarChapter 
    ? `第${result.mostSimilarChapter.chapterNumber || ''}章「${(result.mostSimilarChapter.title || '').slice(0, 10)}」` 
    : '前文';
  
  let message = `检测到内容重复风险：与${chapterInfo}相似度达${Math.round(result.maxSimilarity * 100)}%。`;
  
  if (result.details && result.details.length > 0) {
    message += ` 发现${result.details.length}处相似句式，请检查后决定是否保留当前生成内容。`;
  } else {
    message += ` 建议人工检查后决定是否使用。`;
  }
  
  return message;
};

/**
 * 章纲绑定角色 chip 旁文案：若 name 已含与 badge 相同的定位前缀则去掉，避免「主角 | 主角 某某」重复。
 * 输入：char；输出：展示用姓名字符串
 */
const getCharacterChapterOutlineDisplayName = (char) => {
  if (!char) return '';
  const roleLb = getCharacterDisplayRoleLabel(char);
  const n = String(char.name || '').trim();
  if (!n) return '未命名';
  if (!roleLb || roleLb === '未分类') return n;
  if (n === roleLb) return n;
  if (n.startsWith(roleLb)) {
    const rest = n.slice(roleLb.length).replace(/^[\s\u3000:：\-·]+/, '').trim();
    if (rest) return rest;
  }
  return n;
};

/** 定位标签配色（与章节列表天青系区分，主角偏金、反派偏红） */
const getCharacterRoleVisual = (roleLabel) => {
  switch (roleLabel) {
    case '主角':
      return { bg: 'rgba(251,191,36,0.22)', color: '#fcd34d', border: '1px solid rgba(251,191,36,0.5)' };
    case '反派':
      return { bg: 'rgba(239,68,68,0.2)', color: '#fca5a5', border: '1px solid rgba(239,68,68,0.45)' };
    case '恋人':
      return { bg: 'rgba(236,72,153,0.2)', color: '#f9a8d4', border: '1px solid rgba(236,72,153,0.42)' };
    case '导师':
      return { bg: 'rgba(139,92,246,0.2)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.45)' };
    case '宿敌':
      return { bg: 'rgba(248,113,113,0.18)', color: '#fecaca', border: '1px solid rgba(248,113,113,0.4)' };
    case 'NPC':
      return { bg: 'rgba(148,163,184,0.18)', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.38)' };
    default:
      return { bg: 'rgba(125,211,252,0.14)', color: '#bae6fd', border: '1px solid rgba(125,211,252,0.4)' };
  }
};

/** 章纲角色 chip 悬停：基本信息（多行） */
const buildCharacterChapterOutlineTooltip = (char) => {
  if (!char) return '';
  const role = getCharacterDisplayRoleLabel(char);
  const lines = [`【${char.name || '未命名'}】`, `定位：${role}`];
  if (char.gender) lines.push(`性别：${char.gender}`);
  if (char.title) lines.push(`头衔：${char.title}`);
  if (char.realm) lines.push(`势力：${char.realm}`);
  if (char.level) lines.push(`境界：${char.level}`);
  const desc = (char.description || '').trim();
  if (desc) lines.push(`简介：${desc.slice(0, 220)}${desc.length > 220 ? '…' : ''}`);
  return lines.join('\n');
};

const chapterOutlineCharTooltipProps = {
  enterDelay: 400,
  placement: 'top',
  componentsProps: { tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 360, fontSize: 12, lineHeight: 1.5 } } },
};

// ════════════════════════════════════════════════════════════════════════
// 🔥 大纲分段解析工具（1000 章大体量场景专用）
// ────────────────────────────────────────────────────────────────────────
// 痛点：1000 章大纲的 subPlots + foreshadowings 数组太大，AI 单次响应在
//       max_tokens 上限处被截断 → JSON.parse 失败 → 全部白费。
// 方案：Phase1 全局字段（小输出单请求） + Phase2 支线/伏笔按段落分批提取，
//       每段 ~1 万字，独立解析 + robustJsonParse 容错 + 失败重试 1 次。
// ════════════════════════════════════════════════════════════════════════

/**
 * 将大纲文本按段落边界切分为 ~targetSize 字符的段。
 * 优先在 \n 处断开，避免把一句话劈成两半。
 * @param {string} text 大纲全文
 * @param {number} targetSize 每段目标字符数（默认 10000）
 * @returns {string[]} 段落数组
 */
function splitOutlineText(text, targetSize = 10000) {
  if (!text || text.length <= targetSize) return [text || ''];

  const segments = [];
  let pos = 0;
  while (pos < text.length) {
    let end = pos + targetSize;
    if (end >= text.length) {
      segments.push(text.slice(pos));
      break;
    }
    // 在 end 前 2000 字范围内找最近的换行符，避免劈断句子
    const searchStart = Math.max(pos, end - 2000);
    let breakPos = text.lastIndexOf('\n', end);
    if (breakPos < searchStart) breakPos = text.lastIndexOf('。', end);
    if (breakPos < searchStart) breakPos = end;
    segments.push(text.slice(pos, breakPos));
    pos = breakPos;
    // 跳过连续换行
    while (text[pos] === '\n' || text[pos] === '\r') pos++;
  }
  return segments.filter(s => s.trim().length > 0);
}

/**
 * 调用 AI 并用 robustJsonParse 容错解析返回的 JSON。
 * 失败时自动重试 1 次（加严 prompt 强调合法 JSON）。
 * @param {Array} messages OpenAI 消息数组
 * @param {object} opts { novelName, operationType, maxTokens }
 * @returns {Promise<object|null>} 解析后的对象，全失败返回 null
 */
async function callAiParseJson(messages, opts = {}) {
  const { novelName = '未知项目', operationType = 'AI解析大纲', maxTokens = 16000 } = opts;

  const doCall = async (msgs) => {
    const resp = await apiClient.post('/assist/ai/proxy-ai', {
      messages: msgs,
      model: 'deepseek-v4-flash',
      extra_body: {},
      temperature: 0.3,
      max_tokens: maxTokens,
      stream: false,
      billingInfo: { novelName, chapterNumber: '', operationType }
    });
    let content = '';
    if (resp.data?.choices?.[0]?.message) {
      content = resp.data.choices[0].message.content;
    } else if (resp.data?.success && resp.data?.data) {
      content = resp.data.data.content || resp.data.data;
    }
    return content;
  };

  // 第一次尝试
  let content = '';
  try {
    content = await doCall(messages);
  } catch (apiErr) {
    console.error(`[${operationType}] AI 调用失败:`, apiErr.message);
    return null;
  }

  const result1 = robustJsonParse(content, { expectArrayKey: null });
  if (result1.ok && result1.data) {
    console.log(`[${operationType}] ✅ JSON 解析成功（策略: ${result1.strategy}）`);
    return result1.data;
  }

  // 重试 1 次：加严 prompt + 只输出 JSON
  console.warn(`[${operationType}] ⚠️ 首次解析失败（策略: ${result1.strategy}），重试中…`);
  const retryMessages = [
    messages[0],
    { role: 'user', content: (messages[1]?.content || '') + '\n\n⚠️ 重要：你的上次回复不是合法JSON。请重新输出，只输出纯JSON（以 { 开头，以 } 结尾），不要任何解释、注释、代码块标记。' }
  ];
  try {
    content = await doCall(retryMessages);
  } catch (apiErr) {
    console.error(`[${operationType}] 重试也失败:`, apiErr.message);
    return null;
  }
  const result2 = robustJsonParse(content, { expectArrayKey: null });
  if (result2.ok && result2.data) {
    console.log(`[${operationType}] ✅ 重试解析成功（策略: ${result2.strategy}）`);
    return result2.data;
  }

  console.error(`[${operationType}] ❌ 两次解析均失败`);
  return null;
}

/** AI 解析大纲弹窗：解析过程中的阶段文案轮播 */
const OUTLINE_PARSE_PHASE_LABELS = [
  '正在扫描正文结构…',
  '正在提取主线剧情…',
  '正在提取世界观设定…',
  '正在提取核心冲突…',
  '正在提取高潮设计…',
  '正在提取角色关系…',
  '正在提取支线剧情…',
  '正在识别伏笔脉络…',
  '正在提取支线剧情…',
  '正在拼装结构化数据…',
];

/** AI 解析角色弹窗：解析过程中的阶段文案轮播（与大纲解析 overlay 动效共用 CSS 类） */
const CHARACTER_PARSE_PHASE_LABELS = [
  '正在扫描角色姓名与定位…',
  '正在提取性格与背景设定…',
  '正在识别角色关系网络…',
  '正在拼装角色库数据…',
];

// 自定义 Select - 强制背景透明
const StyledSelect = styled(Select)(({ theme }) => ({
  '& .MuiSelect-select': {
    backgroundColor: 'transparent !important',
    background: 'transparent !important',
  },
  '& .MuiSelect-select:focus': {
    backgroundColor: 'transparent !important',
    background: 'transparent !important',
  },
}));

/** 赛博风弹窗过渡：中心缩放 + 轻微「弹出」感；配合 transitionDuration 与 prefers-reduced-motion */
const CyberDialogTransition = React.forwardRef(function CyberDialogTransition(props, ref) {
  return (
    <Zoom
      ref={ref}
      {...props}
      timeout={{ enter: 420, exit: 260 }}
      style={{ transformOrigin: '50% 42%' }}
    />
  );
});

// 右侧竖向工具盘配置 (赛博朋克配色)
// 顺序：大纲 -> 章纲 -> 章节 -> 角色 -> 词条 -> 备忘录 -> 全局进度 -> AI -> 对话
const rightTools = [
  { id: 'outlineMain', name: '大纲', icon: DescriptionIcon, color: 'var(--ws-accent)', panel: 'outlineMain' },
  { id: 'chapters', name: '章纲章节', icon: LibraryBooksIcon, color: '#00ff9f', panel: 'chapters' },
  { id: 'character', name: '角色', icon: PersonIcon, color: '#00f0ff', panel: 'character' },
  { id: 'item', name: '词条', icon: CategoryIcon, color: '#ff6b35', panel: 'item' },
  { id: 'memo', name: '备忘录', icon: NoteIcon, color: '#fbbf24', panel: 'memo' },
  { id: 'progress', name: '全局进度', icon: TimelineIcon, color: '#06b6d4', panel: 'progress' },
  { id: 'ai', name: 'AI', icon: AutoFixHighIcon, color: '#ff00aa', isMain: true, panel: 'ai' },
  { id: 'chat', name: '对话', icon: PsychologyIcon, color: '#00b4d8', panel: 'chat' },
];

// AI工具配置 - 底部工具盘 (赛博朋克配色) - 保留兼容
const bottomTools = rightTools;

/** PC 右侧竖栏：四字名固定两字一行，避免窄宽自动断成「前三字+一字」 */
const DESKTOP_SIDEBAR_TWO_LINE_LABELS = {
  章纲章节: ['章纲', '章节'],
  全局进度: ['全局', '进度'],
};

/** AI 工具集与子面板：与辅助创作章纲「天青描边 + 深色底」一致，禁用薄荷绿主色 */
const ASSIST_AI_SKY = {
  main: '#7dd3fc',
  title: '#e0f2fe',
  border: 'rgba(125, 211, 252, 0.38)',
  borderSoft: 'rgba(125, 211, 252, 0.22)',
  inputBorder: 'rgba(125, 211, 252, 0.45)',
  switch: '#38bdf8',
  switchTrack: 'rgba(56, 189, 248, 0.5)',
  sectionLabel: 'rgba(224, 242, 254, 0.92)',
  cardGrad: 'linear-gradient(145deg, rgba(56,189,248,0.12) 0%, rgba(8,14,24,0.94) 55%, rgba(10,14,22,0.9) 100%)',
};

// AI 工具集主网格（6 项）：每项独立点缀色，整体仍为天青系描边
const aiToolsMainGrid = [
  { id: 'aiContinue', name: '续写', icon: AutoStoriesIcon, desc: '智能续写', panel: 'aiContinue', color: '#7dd3fc' },
  { id: 'aiExpand', name: '扩写', icon: ExpandMoreIcon, desc: '扩展内容', panel: 'aiExpand', color: '#fbbf24' },
  { id: 'aiPolish', name: '润色', icon: AutoFixHighIcon, desc: '优化文字', panel: 'aiPolish', color: '#fdba74' },
  { id: 'aiReview', name: '审稿', icon: RateReviewIcon, desc: '审阅建议', panel: 'aiReview', color: '#93c5fd' },
  { id: 'aiCorrect', name: '纠错', icon: FindInPageIcon, desc: '修正错字', panel: 'aiCorrect', color: '#fcd34d' },
  { id: 'aiDenoise', name: '去痕', icon: CleanServicesIcon, desc: '去除痕迹', panel: 'aiDenoise', color: '#f9a8d4' },
];

// 词条分类配置 - 包含更多分类和颜色
const ITEM_CATEGORIES = [
  { id: '势力', name: '势力组织', color: '#ff6b35', icon: '组' },
  { id: '功法', name: '功法技能', color: '#00f0ff', icon: '武' },
  { id: '物品', name: '物品法宝', color: '#ffd700', icon: '宝' },
  { id: '副本', name: '副本秘境', color: 'var(--ws-accent)', icon: '境' },
  { id: '角色', name: '人物', color: '#00ff9f', icon: '人' },
  { id: '种族', name: '种族', color: '#ec4899', icon: '种' },
  { id: '地域', name: '地域', color: '#3b82f6', icon: '地' },
  { id: '王朝', name: '王朝', color: '#f59e0b', icon: '朝' },
  { id: '组织', name: '组织', color: '#ef4444', icon: '织' },
  { id: '血脉', name: '血脉体质', color: 'var(--ws-violet)', icon: '血' },
  { id: '灵根', name: '灵根资质', color: '#06b6d4', icon: '*' },
  { id: '灵兽', name: '灵兽', color: '#22c55e', icon: '兽' },
  { id: '材料', name: '材料', color: '#eab308', icon: '材' },
  { id: '阵法', name: '阵法', color: '#6366f1', icon: '阵' },
  { id: '秘宝', name: '秘宝', color: '#f43f5e', icon: '秘' },
  { id: '传承', name: '传承', color: '#14b8a6', icon: '承' },
];

// 分类颜色映射
const ITEM_CATEGORY_COLORS = ITEM_CATEGORIES.reduce((acc, cat) => {
  acc[cat.id] = cat.color;
  return acc;
}, {});

// 备忘录分类配置 - 详细的作者笔记分类
const MEMO_CATEGORIES = [
  { id: '灵感', name: '灵感', color: '#fbbf24', icon: '灵', desc: '突然想到的剧情点子' },
  { id: '设定', name: '设定', color: '#3b82f6', icon: '设', desc: '世界观、规则等设定' },
  { id: '待办', name: '待办', color: '#f97316', icon: '待', desc: '需要完成的任务' },
  { id: '章节', name: '章节', color: '#22c55e', icon: '章', desc: '各章节的规划' },
  { id: '人物', name: '人物', color: 'var(--ws-accent)', icon: '人', desc: '人物相关笔记' },
  { id: '剧情', name: '剧情', color: '#ef4444', icon: '剧', desc: '剧情线发展' },
  { id: '伏笔', name: '伏笔', color: '#ec4899', icon: '伏', desc: '需要回收的伏笔' },
  { id: '对话', name: '对话', color: '#06b6d4', icon: '话', desc: '经典对话记录' },
  { id: '描写', name: '描写', color: '#d97706', icon: '描', desc: '场景描写素材' },
  { id: '资料', name: '资料', color: '#64748b', icon: '资', desc: '查资料、做笔记' },
  { id: '问题', name: '问题', color: '#eab308', icon: '?', desc: '需要思考的问题' },
  { id: '回忆', name: '回忆', color: '#38bdf8', icon: '忆', desc: '过去的灵感' },
];

// 备忘录优先级
const MEMO_PRIORITIES = [
  { id: 'high', name: '高', color: '#ef4444' },
  { id: 'medium', name: '中', color: '#f97316' },
  { id: 'low', name: '低', color: '#22c55e' },
];

// 备忘录状态
const MEMO_STATUS = [
  { id: 'pending', name: '待处理', color: '#64748b' },
  { id: 'in_progress', name: '进行中', color: '#3b82f6' },
  { id: 'completed', name: '已完成', color: '#22c55e' },
];

// 备忘录分类颜色映射
const MEMO_CATEGORY_COLORS = MEMO_CATEGORIES.reduce((acc, cat) => {
  acc[cat.id] = cat.color;
  return acc;
}, {});

// 词条模板配置 - 用于动态生成模板选项
const ITEM_TEMPLATES = [
  { category: '势力', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'factory', name: '修仙门派' },
    { id: 'evil', name: '魔道势力' },
    { id: 'empire', name: '帝国皇朝' },
    { id: 'tribe', name: '种族势力' },
    { id: 'alliance', name: '联盟组织' },
    { id: 'family', name: '家族势力' },
    { id: 'secret', name: '神秘组织' }
  ]},
  { category: '功法', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'cultivation', name: '修炼功法' },
    { id: 'attack', name: '攻击神通' },
    { id: 'defense', name: '防御秘术' },
    { id: 'auxiliary', name: '辅助秘法' },
    { id: 'array', name: '阵法禁制' },
    { id: 'body', name: '炼体功法' },
    { id: 'evil', name: '魔功邪术' }
  ]},
  { category: '物品', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'sword', name: '剑类武器' },
    { id: 'bead', name: '珠子形态' },
    { id: 'scroll', name: '卷轴类' },
    { id: 'furnace', name: '鼎炉类' },
    { id: 'crown', name: '冠饰类' },
    { id: 'shield', name: '防御型' },
    { id: 'pill', name: '丹药' }
  ]},
  { category: '副本', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'continent', name: '修仙大陆' },
    { id: 'secret', name: '秘境洞天' },
    { id: 'city', name: '主城都城' },
    { id: 'forest', name: '深山老林' },
    { id: 'ocean', name: '海洋领域' },
    { id: 'danger', name: '绝地险境' },
    { id: 'border', name: '边陲小镇' }
  ]},
  { category: '角色', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'protagonist', name: '主角' },
    { id: 'antagonist', name: '反派' },
    { id: 'mentor', name: '导师' },
    { id: 'love', name: '恋人' },
    { id: 'friend', name: '好友' },
    { id: 'rival', name: '宿敌' },
    { id: 'npc', name: 'NPC' }
  ]},
  { category: '种族', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'dragon', name: '龙族' },
    { id: 'phoenix', name: '凤凰族' },
    { id: 'demon', name: '恶魔族' },
    { id: 'spirit', name: '精灵族' },
    { id: 'god', name: '神族' }
  ]},
  { category: '地域', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'mountain', name: '山脉' },
    { id: 'river', name: '河流' },
    { id: 'forest', name: '森林' },
    { id: 'desert', name: '沙漠' },
    { id: 'island', name: '岛屿' }
  ]},
  { category: '王朝', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'empire', name: '大一统王朝' },
    { id: 'kingdom', name: '王国' },
    { id: 'clan', name: '部落' }
  ]},
  { category: '组织', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'secret', name: '秘密组织' },
    { id: 'assassin', name: '杀手组织' },
    { id: 'merchant', name: '商会' },
    { id: 'religious', name: '宗教' }
  ]},
  { category: '血脉', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'ancient', name: '远古血脉' },
    { id: 'divine', name: '神级体质' },
    { id: 'mysterious', name: '特殊体质' }
  ]},
  { category: '灵根', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'single', name: '单一灵根' },
    { id: 'dual', name: '双灵根' },
    { id: 'multiple', name: '多灵根' },
    { id: 'none', name: '无灵根' }
  ]},
  { category: '灵兽', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'beast', name: '野兽型' },
    { id: 'bird', name: '鸟类' },
    { id: 'aquatic', name: '水生' },
    { id: 'insect', name: '虫类' }
  ]},
  { category: '材料', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'herb', name: '灵草' },
    { id: 'mineral', name: '矿物' },
    { id: 'crystal', name: '晶体' },
    { id: 'bone', name: '骨骼' }
  ]},
  { category: '阵法', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'attack', name: '攻击' },
    { id: 'defense', name: '防御' },
    { id: 'trap', name: '陷阱' },
    { id: 'teleport', name: '传送' }
  ]},
  { category: '秘宝', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'weapon', name: '武器' },
    { id: 'armor', name: '防具' },
    { id: 'accessory', name: '饰品' },
    { id: 'space', name: '空间' }
  ]},
  { category: '传承', templates: [
    { id: 'custom', name: '自定义' },
    { id: 'ancient', name: '远古' },
    { id: 'family', name: '家族' },
    { id: 'secret', name: '隐藏' },
    { id: 'divine', name: '神级' }
  ]}
];

// 计算文本行数的辅助函数
const calculateDynamicRows = (text, minRows = 2, maxRows = 10) => {
  if (!text || text.trim() === '') return minRows;
  const lines = text.split('\n').length;
  // 高度为文本行数+1，避免留空过多
  return Math.min(maxRows, Math.max(minRows, lines + 1));
};

// 计算文本高度样式。桌面端用 minRows/maxRows 动态高度（比文本行数多一行、无多余滚动条）；移动端紧凑由各处的 @media 控制
const getDynamicTextAreaStyle = (text, minRows = 2, maxRows = 10) => {
  const rows = calculateDynamicRows(text, minRows, maxRows);
  return { minRows: rows, maxRows: maxRows };
};

/** 顶栏/列表统一：第N章：标题（去掉标题里重复的「第N章」前缀） */
const formatChapterColonTitle = (ch) => {
  if (!ch) return '选择章节';
  const n = ch.chapterNumber ?? 1;
  const raw = String(ch.title || '').trim();
  if (!raw) return `第${n}章：未命名`;
  // 标题仅为「第M章」且与当前序号不一致时，视为陈旧占位（多卷重排后常见），避免「第5章：第3章」
  const onlyChapter = raw.match(/^第\s*(\d+)\s*章$/);
  if (onlyChapter && Number(onlyChapter[1]) !== n) {
    return `第${n}章`;
  }
  const re = new RegExp(`^第\\s*${n}\\s*章\\s*[:：]?\\s*`);
  const rest = raw.replace(re, '').trim();
  if (rest) return `第${n}章：${rest}`;
  if (/^第\s*\d+\s*章$/.test(raw)) return `第${n}章：未命名`;
  return `第${n}章：${raw}`;
};

/** 卷的阅读顺序：order 升序，相同 order 按列表中出现顺序 */
const sortVolumesForReadingOrder = (volumesList) =>
  [...(volumesList || [])].map((v, i) => ({ v, i })).sort((a, b) => {
    const oa = Number(a.v.order) || 0;
    const ob = Number(b.v.order) || 0;
    if (oa !== ob) return oa - ob;
    return a.i - b.i;
  }).map(({ v }) => v);

/** 全书章节：按卷顺序 + 卷内章节号排序（跨卷连续阅读顺序） */
const orderChaptersGlobally = (chapterList, volumesList) => {
  const volSorted = sortVolumesForReadingOrder(volumesList);
  const volRank = new Map(volSorted.map((v, i) => [v._id, i]));
  return [...chapterList].sort((a, b) => {
    const ra = volRank.get(a.volumeId) ?? 9999;
    const rb = volRank.get(b.volumeId) ?? 9999;
    if (ra !== rb) return ra - rb;
    return (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0);
  });
};

/**
 * 辅助创作：全书跨卷连续编号（第1章…第N章）。删除后或校正后重排，并修正「仅第N章」默认标题。
 */
const renumberAssistChaptersGlobal = (chapterList, volumesList) => {
  const ordered = orderChaptersGlobally(chapterList, volumesList);
  return ordered.map((c, idx) => {
    const newNum = idx + 1;
    const raw = String(c.title || '').trim();
    const isAutoTitle = /^第\s*\d+\s*章$/.test(raw);
    return {
      ...c,
      chapterNumber: newNum,
      ...(isAutoTitle ? { title: `第${newNum}章` } : {}),
    };
  });
};

/**
 * 在 targetVolume 末尾追加一章：新章全局序号为 insertAt+1；需将全局序列中 insertAt 及之后的原有章整体 +1。
 */
const getAppendEndOfVolumeSlot = (chapterList, volumesList, targetVolumeId) => {
  const volSorted = sortVolumesForReadingOrder(volumesList);
  const tIdx = volSorted.findIndex((v) => v._id === targetVolumeId);
  const glob = orderChaptersGlobally(chapterList, volumesList);
  if (tIdx < 0) {
    return { insertAt: glob.length, newGlobalNum: glob.length + 1, glob };
  }
  let insertAt = 0;
  for (let i = 0; i < tIdx; i++) {
    insertAt += chapterList.filter((c) => c.volumeId === volSorted[i]._id).length;
  }
  insertAt += chapterList.filter((c) => c.volumeId === targetVolumeId).length;
  return { insertAt, newGlobalNum: insertAt + 1, glob };
};

/** 是否已有正文（用于列表高亮；与审稿取稿一致：含章纲内待插入的 generatedContent） */
const chapterHasWrittenBody = (ch) => {
  if (!ch) return false;
  if (String(ch.content || '').trim().length > 0) return true;
  if (String(ch.outline?.generatedContent || '').trim().length > 0) return true;
  const w = Number(ch.wordCount);
  return Number.isFinite(w) && w > 0;
};

const Workspace = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { novelId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));  // 移动端适配：小屏弹窗全屏
  /** 章纲/章节面板：< md 使用单栏全宽移动布局（避免 600–900px 双栏右侧被 display:none 挤没） */
  const isChaptersNarrow = useMediaQuery(theme.breakpoints.down('md'));
  /** 底栏文字极窄时改用短标签，避免「章纲章节」「全局进度」挤成省略号 */
  const isToolTrayCompact = useMediaQuery('(max-width:520px)');
  /** 减少动画偏好：用户开启「减少动画」或系统 prefers-reduced-motion 时跳过 framer-motion 入场动画 */
  const motionReduced = useMediaQuery('(prefers-reduced-motion: reduce)');
  const getMobileToolTrayLabel = (name) => {
    if (!isToolTrayCompact) return name;
    const short = { 章纲章节: '章纲', 全局进度: '进度', 备忘录: '备忘' };
    return short[name] || name;
  };

  // 核心状态
  const [novel, setNovel] = useState(null);
  // 路由可为 /workspace 无 :novelId（列表首本加载），接口统一用当前作品 ID
  const assistNovelId = novelId || novel?._id;
  const [novelList, setNovelList] = useState([]);
  const [creationType, setCreationType] = useState('auto'); // 创作类型：auto=全自动写作，assist=辅助创作
  const [chapters, setChapters] = useState([]);
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterContent, setChapterContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editingChapterId, setEditingChapterId] = useState(null);
  const [editingChapterTitle, setEditingChapterTitle] = useState('');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [inlineEditingChapterId, setInlineEditingChapterId] = useState(null);  // 行内编辑章节标题的章节ID
  
  // 章纲编辑（单页智能模式，无分步向导）
  const [wizardRecommendedChars, setWizardRecommendedChars] = useState([]);
  const [wizardSelectedChars, setWizardSelectedChars] = useState([]);
  const [wizardSelectedForeshadowing, setWizardSelectedForeshadowing] = useState([]);
  const [wizardRecommendedSubplots, setWizardRecommendedSubplots] = useState([]);
  const [wizardSelectedSubplots, setWizardSelectedSubplots] = useState([]);
  const [wizardSelectedItems, setWizardSelectedItems] = useState([]);
  const [wizardCoreEvent, setWizardCoreEvent] = useState('');
  
  // 界面状态
  const [activePanel, setActivePanel] = useState(null); // 当前打开的面板
  const [panelType, setPanelType] = useState(''); // 面板类型
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState(null); // { start: number, end: number } 在原文中的位置
  const [showFloatingToolbar, setShowFloatingToolbar] = useState(false);
  const [floatingToolbarPos, setFloatingToolbarPos] = useState({ x: 0, y: 0 });
  const [bottomToolIndex, setBottomToolIndex] = useState(-1);
  
  // AI 生成状态
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState('');
  const [aiModel, setAiModel] = useState('氛围版');
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiDetail, setAiDetail] = useState('');
  const [expandLevel, setExpandLevel] = useState('medium');
  // 扩写增强参数
  const [expandDimensions, setExpandDimensions] = useState([]); // 多选扩写维度
  const [expandStyle, setExpandStyle] = useState('细腻'); // 扩写风格
  const [expandAddShuang, setExpandAddShuang] = useState(false); // 注入爽点
  const [expandAddFuBi, setExpandAddFuBi] = useState(false); // 注入伏笔
  const [expandMoreOpen, setExpandMoreOpen] = useState(false); // 更多选项折叠
  // 润色增强参数
  const [polishStrength, setPolishStrength] = useState('medium'); // 润色力度
  const [polishDirections, setPolishDirections] = useState([]); // 润色方向多选
  const [polishBans, setPolishBans] = useState([]); // 禁止修改项
  const [polishAudience, setPolishAudience] = useState('小白'); // 目标读者
  const [polishMoreOpen, setPolishMoreOpen] = useState(false); // 更多选项折叠
  // 审稿增强参数
  const [reviewPurpose, setReviewPurpose] = useState('find_issues'); // 审稿目的
  const [reviewShowExample, setReviewShowExample] = useState(false); // 显示范例
  const [reviewStandard, setReviewStandard] = useState('webnovel'); // 评分标准
  const [reviewPriority, setReviewPriority] = useState(true); // 修改优先级
  // 纠错增强参数
  const [correctStrictness, setCorrectStrictness] = useState('normal'); // 纠错严格度
  const [correctSpecialChecks, setCorrectSpecialChecks] = useState([]); // 网文专项检查
  const [correctSensitiveCheck, setCorrectSensitiveCheck] = useState(false); // 敏感词检查
  // 去痕增强参数
  const [denoiseFocus, setDenoiseFocus] = useState([]); // 去痕重点
  const [denoiseTargetStyle, setDenoiseTargetStyle] = useState('natural'); // 目标文风
  const [denoiseKeepLength, setDenoiseKeepLength] = useState(true); // 保留长度
  const [denoiseReference, setDenoiseReference] = useState(''); // 参照文风
  const [denoiseMoreOpen, setDenoiseMoreOpen] = useState(false); // 更多选项折叠
  const [polishStyle, setPolishStyle] = useState('简洁');
  const [reviewScope, setReviewScope] = useState('current');
  /** 与审稿面板 Chip 文案一致：结构 / 节奏 / 人物 / 语言 */
  const [reviewDimensions, setReviewDimensions] = useState(['结构', '节奏', '人物', '语言']);
  /** 自定义审稿范围：全书顺序章节序号（1 起，含头含尾） */
  const [reviewCustomFrom, setReviewCustomFrom] = useState('1');
  const [reviewCustomTo, setReviewCustomTo] = useState('5');
  const [denoiseLevel, setDenoiseLevel] = useState('medium');
  
  // 角色库状态
  // ==================== 角色相关状态 ====================
  const [characters, setCharacters] = useState([]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [characterSearch, setCharacterSearch] = useState('');
  const [characterFilter, setCharacterFilter] = useState('all');
  const [editingCharacter, setEditingCharacter] = useState(null);

  // ==================== 卷与章纲相关状态 ====================
  
  // 章节状态常量
  const CHAPTER_STATUS = {
    OUTLINE_ONLY: { label: '仅章纲', icon: '○', color: 'rgba(255,255,255,0.4)' },
    IN_PROGRESS: { label: '写作中', icon: '>', color: '#ffd700' },
    COMPLETED: { label: '已完成', icon: 'V', color: '#06b6d4' },
    REVISED: { label: '已修订', icon: 'R', color: '#3b82f6' }
  };
  
  // 获取章节状态的兼容方法
  const getChapterStatus = (chapter) => {
    // 优先使用 chapter.status
    if (chapter.status === 'completed') return CHAPTER_STATUS.COMPLETED;
    if (chapter.status === 'revised') return CHAPTER_STATUS.REVISED;
    if (chapter.status === 'in_progress') return CHAPTER_STATUS.IN_PROGRESS;
    // 兼容 chapter.outline?.status
    if (chapter.outline?.status === 'completed') return CHAPTER_STATUS.COMPLETED;
    if (chapter.outline?.status === 'revised') return CHAPTER_STATUS.REVISED;
    if (chapter.outline?.status === 'in_progress') return CHAPTER_STATUS.IN_PROGRESS;
    // 默认仅章纲
    return CHAPTER_STATUS.OUTLINE_ONLY;
  };
  
  // 生成建议卷名
  const getSuggestedVolumeName = (existingVolumes) => {
    const count = existingVolumes.length + 1;
    const cn = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十', '十一', '十二'];
    const numStr = count <= 12 ? cn[count - 1] : count;
    return `第${numStr}卷`;
  };

  // 新建卷处理函数
  const handleAddVolume = () => {
    const suggested = getSuggestedVolumeName(volumes);
    setVolumeFormData({ name: suggested, title: '', chapterCount: 10 });
    setEditingVolumeId(null);
    setVolumeDialogOpen(true);
  };
  
  // 编辑章纲处理函数
  const handleEditOutline = (chapter) => {
    handleOpenChapterOutline(chapter);
  };
  
  const [volumes, setVolumes] = useState([]);           // 卷列表
  const [currentVolumeId, setCurrentVolumeId] = useState(null);  // 当前选中的卷
  const [expandedVolumes, setExpandedVolumes] = useState({});    // 卷展开状态
  const [editingVolume, setEditingVolume] = useState(null);      // 当前编辑的卷
  const [volumeDialogOpen, setVolumeDialogOpen] = useState(false);  // 卷编辑弹窗
  const [volumeFormData, setVolumeFormData] = useState({          // 卷表单数据
    name: '',
    title: '',
    chapterCount: 10,
  });
  const [editingVolumeId, setEditingVolumeId] = useState(null);   // 当前编辑的卷ID（用于区分新建/编辑卷）
  const [editingChapterOutline, setEditingChapterOutline] = useState(null);  // 当前编辑的章节纲
  const [chapterOutlineDialogOpen, setChapterOutlineDialogOpen] = useState(false);  // 章纲编辑弹窗
  const [outlineAutoSaved, setOutlineAutoSaved] = useState(false);  // 自动保存提示
  const [chapterOutlineSavedTime, setChapterOutlineSavedTime] = useState(null);  // 章纲保存时间
  const [outlineFindReplaceOpen, setOutlineFindReplaceOpen] = useState(false);  // 大纲查找替换弹窗
  const [outlineFindText, setOutlineFindText] = useState('');  // 大纲查找文本
  const [outlineReplaceText, setOutlineReplaceText] = useState('');  // 大纲替换文本
  const [characterRenameOpen, setCharacterRenameOpen] = useState(false);  // 角色换名弹窗
  const [oldCharName, setOldCharName] = useState('');  // 原角色名
  const [newCharName, setNewCharName] = useState('');  // 新角色名
  const [newChapterVolumeId, setNewChapterVolumeId] = useState(null);  // 在“新建章节”模式下所属卷 ID
  const [newChapterTitle, setNewChapterTitle] = useState('');  // 新建章节时输入的标题

  // AI生成弹窗状态
  const [aiGenerateDialogOpen, setAiGenerateDialogOpen] = useState(false);  // AI生成弹窗
  const [aiGenerateType, setAiGenerateType] = useState('volume');  // 'single' | 'volume' | 'book'
  const [aiGenerateCoreContent, setAiGenerateCoreContent] = useState('');  // 核心内容
  const [aiGenerateVolumeId, setAiGenerateVolumeId] = useState(null);  // 选择的卷
  const [aiGenerateStartChapter, setAiGenerateStartChapter] = useState(1);  // 起始章节
  const [aiGenerateChapterCount, setAiGenerateChapterCount] = useState(5);  // 生成章节数
  const [aiGenerateDetailLevel, setAiGenerateDetailLevel] = useState('detailed');  // 'detailed' | 'brief'
  const [aiGeneratePointsCount, setAiGeneratePointsCount] = useState(5);  // 每章要点数
  const [aiGenerateReference, setAiGenerateReference] = useState({  // 参考设定（见弹窗内说明）
    outlineFullText: true,      // 大纲编辑区整段正文摘要（整段大纲时必用）
    structuredMainPlot: false, // 结构化主线字段（仅在大纲 AI 结构化后才有）
    subPlots: false,
    foreshadowings: false,
    characters: false,
    items: false,
  });
  const [aiGenerating, setAiGenerating] = useState(false);  // 全局兼容占位（部分旧逻辑共用）
  const [outlineGenerating, setOutlineGenerating] = useState(false); // AI生成章纲中（仅章纲生成流程）
  const [outlineGeneratingProgress, setOutlineGeneratingProgress] = useState(''); // 🔥 章纲生成实时进度文字（流式字数/排队状态）
  const [bodyGenerating, setBodyGenerating] = useState(false);      // AI生成正文中（仅正文生成流程，与章纲互不干扰）
  const [charRecommendLoading, setCharRecommendLoading] = useState(false); // AI推荐角色中
  const [chapterWordCount, setChapterWordCount] = useState(2000); // 章节目标字数，默认2000
  const [autoSyncAfterGenerate, setAutoSyncAfterGenerate] = useState(true); // 生成正文后自动跟进全局进度
  const [globalProgressSyncing, setGlobalProgressSyncing] = useState(false); // 全局进度正在同步中（防止竞态条件）
  const [prevChapterWarning, setPrevChapterWarning] = useState({ open: false, prevChapter: null }); // 前章未完成警告对话框
  const saveOutlineTimerRef = useRef(null);  // 实时保存 debounce timer
  const doRunSmartOutlineGenerateRef = useRef(null);  // 实际执行智能章纲生成的函数，供外层 Dialog onClick 调用

  // 辅助创作次数限制（公测每日50次）
  const ASSIST_AI_DAILY_LIMIT = 50;
  const [assistQuota, setAssistQuota] = useState({ remaining: ASSIST_AI_DAILY_LIMIT, dailyLimit: ASSIST_AI_DAILY_LIMIT, usedToday: 0 });
  const [assistQuotaLoading, setAssistQuotaLoading] = useState(false);

  // 获取辅助创作次数余额
  const fetchAssistAiQuota = useCallback(async () => {
    setAssistQuotaLoading(true);
    try {
      const res = await apiClient.get('/assist/ai/quota');
      const body = res.data?.data || res.data || {};
      const remaining = Number(body.remaining ?? (ASSIST_AI_DAILY_LIMIT - (body.usedToday ?? 0)));
      setAssistQuota({
        remaining: Math.max(0, remaining),
        dailyLimit: body.dailyLimit || ASSIST_AI_DAILY_LIMIT,
        usedToday: body.usedToday ?? (ASSIST_AI_DAILY_LIMIT - remaining),
      });
    } catch (e) {
      console.error('获取辅助创作次数失败:', e);
    } finally {
      setAssistQuotaLoading(false);
    }
  }, []);

  // 监听次数刷新事件
  useEffect(() => {
    const onRefresh = () => fetchAssistAiQuota();
    window.addEventListener('assistAiQuotaRefresh', onRefresh);
    return () => window.removeEventListener('assistAiQuotaRefresh', onRefresh);
  }, [fetchAssistAiQuota]);

  // 初始加载次数
  useEffect(() => {
    fetchAssistAiQuota();
  }, [fetchAssistAiQuota]);

  // 检查是否还有剩余次数，返回 true 表示可以继续，返回 false 并显示提示
  const checkAssistQuota = (operationName = 'AI生成') => {
    if (assistQuota.remaining <= 0) {
      setSnackbar({
        open: true,
        message: `今日免费次数已用完（${ASSIST_AI_DAILY_LIMIT}次/天）！请明天 00:00 后再来，明天额度自动重置`,
        severity: 'warning'
      });
      return false;
    }
    return true;
  };

  // ── 实时保存 outline 字段（核心事件/承接上文/为下章埋钩）───────
  // 仅保存 outline 内的三个关键字段，不触发角色/伏笔/支线的联动逻辑
  const autoSaveOutlineFields = useCallback((chapter) => {
    if (!chapter?._id || !assistNovelId) return;
    clearTimeout(saveOutlineTimerRef.current);
    saveOutlineTimerRef.current = setTimeout(async () => {
      try {
        await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${chapter._id}/outline`, {
          outline: chapter.outline,
          status: chapter.outline?.status || 'outline_only',
        });
        setOutlineAutoSaved(true);
        setTimeout(() => setOutlineAutoSaved(false), 2000);
        console.log(`[实时保存] ✅ 章节 outline 已自动保存`);
      } catch (e) {
        console.warn('[实时保存] outline 保存失败:', e);
      }
    }, 600);
  }, [assistNovelId]);
  const [aiGenerateContext, setAiGenerateContext] = useState(null);  // AI生成上下文数据
  const [aiGenerateContextLoading, setAiGenerateContextLoading] = useState(false);  // 上下文加载中
  const [aiGenerateStep, setAiGenerateStep] = useState('form');  // 'form' | 'preview'
  const [aiGeneratedPreviews, setAiGeneratedPreviews] = useState([]);  // AI 生成结果预览
  // 角色/词条选择弹窗
  const [selectCharDialogOpen, setSelectCharDialogOpen] = useState(false);
  const [selectItemDialogOpen, setSelectItemDialogOpen] = useState(false);
  // 章节管理分栏：当前选中的章节（用于右侧内联章纲编辑）
  const [selectedChapterForEdit, setSelectedChapterForEdit] = useState(null);
  /** 供流式正文「生成即保存」debounce 读取最新 outline，避免闭包陈旧 */
  const selectedChapterForEditRef = useRef(null);
  const chapterBodyStreamSaveTimerRef = useRef(null);
  const latestChapterBodyStreamTextRef = useRef('');
  useEffect(() => {
    selectedChapterForEditRef.current = selectedChapterForEdit;
  }, [selectedChapterForEdit]);

  /** AI 辅助正文流式：跟随最新内容（与大纲弹窗逻辑一致：用户上滚则暂停，回到底部再恢复） */
  const [chapterBodyStreamFollowOutput, setChapterBodyStreamFollowOutput] = useState(true);
  const chapterBodyStreamTextareaRef = useRef(null);
  const chapterBodyStreamSectionRef = useRef(null);

  /** 正文预览区最近的、实际可滚动的父节点（与大纲弹窗里单一 scroll 容器同理，避免层层强制滚到底） */
  const getChapterBodyStreamFirstScrollParent = useCallback(() => {
    const sec = chapterBodyStreamSectionRef.current;
    if (!sec) return null;
    let el = sec.parentElement;
    for (let i = 0; i < 16 && el; i += 1) {
      const st = window.getComputedStyle(el);
      const oy = st.overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) return el;
      el = el.parentElement;
    }
    return null;
  }, []);

  const scrollChapterBodyStreamOuterToBottom = useCallback(() => {
    const sp = getChapterBodyStreamFirstScrollParent();
    if (sp) sp.scrollTop = sp.scrollHeight;
  }, [getChapterBodyStreamFirstScrollParent]);

  const handleChapterBodyStreamTextareaScroll = useCallback((e) => {
    if (!bodyGenerating) return;
    const el = e.target;
    if (!el || el.scrollHeight <= el.clientHeight + 4) return;
    const threshold = 80;
    const distBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setChapterBodyStreamFollowOutput(distBottom <= threshold);
  }, [bodyGenerating]);

  const handleChapterBodyStreamOuterScroll = useCallback(() => {
    if (!bodyGenerating) return;
    const sp = getChapterBodyStreamFirstScrollParent();
    if (!sp || sp.scrollHeight <= sp.clientHeight + 4) return;
    const threshold = 96;
    const distBottom = sp.scrollHeight - sp.scrollTop - sp.clientHeight;
    setChapterBodyStreamFollowOutput(distBottom <= threshold);
  }, [bodyGenerating, getChapterBodyStreamFirstScrollParent]);

  // 章纲章节统一管理状态
  const [chapterFilter, setChapterFilter] = useState('all');  // 'all' | 'outline_only' | 'in_progress' | 'completed' | 'revised'
  const [chapterSearch, setChapterSearch] = useState('');    // 章节搜索

  /** 章纲弹窗内修改标题后同步到章节记录（原先只存 outline，列表 title 不更新） */
  const persistChapterTitle = useCallback(async (chapter) => {
    if (!chapter?._id) return;
    const n = chapter.chapterNumber || 1;
    const tRaw = (chapter.title || '').trim();
    const t = tRaw || `第${n}章`;
    try {
      if (assistNovelId) {
        await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${chapter._id}`, { title: t });
      } else {
        const currentNovelId = novelId || novel?._id;
        if (!currentNovelId) return;
        const chapterIndex = chapters.findIndex(c => c._id === chapter._id);
        if (chapterIndex === -1) return;
        await apiClient.put(`/novels/${currentNovelId}/chapters/${chapterIndex + 1}`, { title: t });
      }
      setChapters(prev => prev.map(c => (c._id === chapter._id ? { ...c, title: t } : c)));
      setCurrentChapter(cur => (cur && cur._id === chapter._id ? { ...cur, title: t } : cur));
      setSelectedChapterForEdit(cur => (cur && cur._id === chapter._id ? { ...cur, title: t } : cur));
      setEditingChapterOutline(cur => (cur && cur._id === chapter._id ? { ...cur, title: t } : cur));
    } catch (e) {
      console.warn('同步章节标题失败:', e);
    }
  }, [assistNovelId, novelId, novel?._id, chapters]);

  // ==================== 大纲相关状态 ====================
  const [outlines, setOutlines] = useState([]);  // 大纲列表
  const [currentOutline, setCurrentOutline] = useState(null);  // 当前编辑的大纲
  const [outlineSearch, setOutlineSearch] = useState('');  // 大纲搜索
  /** 全书大纲 · 结构化区 Tab：仅挂载当前页，避免支线/伏笔成百个 TextField 一次渲染卡死主线程 */
  const [outlineStructuredTab, setOutlineStructuredTab] = useState('main'); // 'main' | 'sub' | 'foreshadow'

  // 【支线/伏笔编辑】使用 ref 存储当前编辑值，避免 React 异步 + 事件池化导致 onBlur 读不到最新值
  const subPlotsEditRef = useRef({}); // key: `${idx}_${fieldName}`
  const foreshadowEditRef = useRef({}); // key: `${idx}_${fieldName}`
  
  // 大纲表单数据
  const [outlineFormData, setOutlineFormData] = useState({
    name: '',                    // 书名
    type: '仙侠',                // 类型
    tags: [],                    // 标签（基调）
    targetAudience: '全年龄',    // 目标受众
    wordCount: 100,             // 目标字数（万）
    chapterCount: 100,          // 目标章节数
    rhythm: 'fast',             // 节奏风格
    worldView: '',               // 世界观设定
    goldenFinger: '',           // 金手指类型
    coreConflict: '',            // 核心冲突
    cpType: '',                  // CP类型
    nuan: 'light',              // 虐点程度
    mainPlot: {
      storyline: '',            // 一句话主线
      description: '',         // 详细描述
    },
    protagonist: {
      name: '',
      gender: '男',
      age: '',
      personality: '',
      goal: '',
      goldenFinger: '',
    },
    worldSettings: {
      background: '',
      levelSystem: '',
      factions: [],
      rules: [],
    },
    storyStructure: {
      beginning: '',
      development: '',
      climax: '',
      ending: '',
    },
    coreConflicts: [],          // 核心冲突
    subPlots: [],               // 支线剧情
    foreshadowing: [],          // 伏笔/悬念
    climaxes: [],               // 高潮设计
    characterNetwork: [],       // 人物关系
  });

  // AI生成模板相关状态（丰富模板：标准/升级/系统/无敌/苟道/恋爱/重生/穿越/种田/悬疑/武侠/西幻/无限流/末世/宫斗/商战等）
  const [aiOutlineTemplates, setAiOutlineTemplates] = useState([
    { id: 'standard', name: '标准大纲', description: '结构完整，平衡各元素', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，擅长设计爽点密集、节奏明快的网络小说大纲。请根据用户提供的创意，生成完整的小说大纲。`, styleGuidance: { tone: ['热血', '逆袭'], rhythm: '快节奏', hotSpots: ['打脸', '升级', '收获'], 虐点: '微虐' }, outputFormat: 'markdown' } },
    { id: 'upgrade', name: '升级流', description: '等级体系、修炼体系', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计升级流小说。请根据用户提供的创意，设计完整的修炼体系和大纲。`, styleGuidance: { tone: ['热血', '修炼'], rhythm: '快节奏', hotSpots: ['升级', '突破', '收获'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'system', name: '系统流', description: '系统任务与机制', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计系统流小说。请根据用户创意，设计系统机制和大纲。`, styleGuidance: { tone: ['轻松', '热血'], rhythm: '快节奏', hotSpots: ['系统', '任务', '奖励'], 虐点: '无' }, outputFormat: 'markdown' } },
    { id: 'invincible', name: '无敌流', description: '无敌开局，横推一切', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计无敌流爽文。请根据用户创意，设计碾压一切的大纲。`, styleGuidance: { tone: ['热血', '爽'], rhythm: '快节奏', hotSpots: ['打脸', '碾压', '装逼'], 虐点: '无' }, outputFormat: 'markdown' } },
    { id: 'goudao', name: '苟道流', description: '低调发育，厚积薄发', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计苟道流稳健文。请根据用户创意，设计低调发育的大纲。`, styleGuidance: { tone: ['稳健', '腹黑'], rhythm: '中节奏', hotSpots: ['发育', '隐藏', '反杀'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'romance', name: '恋爱向', description: '感情线为主，暧昧拉扯', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计恋爱向小说。请根据用户创意，设计感情线为主的大纲。`, styleGuidance: { tone: ['甜蜜', '暧昧'], rhythm: '中节奏', hotSpots: ['相遇', '暧昧', '表白'], 虐点: '玻璃渣' }, outputFormat: 'markdown' } },
    { id: 'rebirth', name: '重生流', description: '重生逆袭，弥补遗憾', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计重生流小说。请根据用户创意，设计重生逆袭、弥补遗憾的大纲，注意前后对比与金手指合理性。`, styleGuidance: { tone: ['逆袭', '爽'], rhythm: '快节奏', hotSpots: ['先知', '布局', '打脸'], 虐点: '微虐' }, outputFormat: 'markdown' } },
    { id: 'transmigration', name: '穿越流', description: '穿越异界，开荒争霸', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计穿越流小说。请根据用户创意，设计穿越异界、开荒争霸或融入新世界的大纲。`, styleGuidance: { tone: ['热血', '轻松'], rhythm: '快节奏', hotSpots: ['文明差异', '种田', '争霸'], 虐点: '微虐' }, outputFormat: 'markdown' } },
    { id: 'farm', name: '种田流', description: '发展领地，经营建设', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计种田流小说。请根据用户创意，设计发展领地、经营建设、科技树或修炼体系稳步发展的大纲。`, styleGuidance: { tone: ['轻松', '稳健'], rhythm: '中节奏', hotSpots: ['建设', '收获', '势力'], 虐点: '无' }, outputFormat: 'markdown' } },
    { id: 'mystery', name: '悬疑推理', description: '伏笔谜题，层层反转', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计悬疑推理小说。请根据用户创意，设计伏笔密布、层层反转、逻辑自洽的大纲。`, styleGuidance: { tone: ['悬疑', '紧张'], rhythm: '中节奏', hotSpots: ['推理', '反转', '真相'], 虐点: '可选' }, outputFormat: 'markdown' } },
    { id: 'wuxia', name: '武侠', description: '江湖恩怨，侠义情仇', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计武侠小说。请根据用户创意，设计江湖恩怨、侠义情仇、武功体系与门派势力的大纲。`, styleGuidance: { tone: ['热血', '侠义'], rhythm: '中节奏', hotSpots: ['奇遇', '恩怨', '江湖'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'western', name: '西幻', description: '魔法剑与龙，史诗冒险', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计西方奇幻小说。请根据用户创意，设计魔法、剑与龙、种族与史诗冒险的大纲。`, styleGuidance: { tone: ['史诗', '热血'], rhythm: '中节奏', hotSpots: ['冒险', '成长', '宿命'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'infinite', name: '无限流', description: '副本任务，多元世界', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计无限流小说。请根据用户创意，设计主神空间、副本任务、多元世界穿梭与成长的大纲。`, styleGuidance: { tone: ['紧张', '热血'], rhythm: '快节奏', hotSpots: ['副本', '兑换', '团队'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'apocalypse', name: '末世', description: '末日生存，异能觉醒', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计末世生存小说。请根据用户创意，设计末日危机、生存挣扎、异能觉醒与势力争夺的大纲。`, styleGuidance: { tone: ['黑暗', '热血'], rhythm: '快节奏', hotSpots: ['生存', '觉醒', '势力'], 虐点: '中虐' }, outputFormat: 'markdown' } },
    { id: 'palace', name: '宫斗', description: '后宫权谋，步步为营', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计宫斗、宅斗类小说。请根据用户创意，设计后宫或宅院权谋、步步为营、情感与利益博弈的大纲。`, styleGuidance: { tone: ['权谋', '虐心'], rhythm: '中节奏', hotSpots: ['算计', '反转', '上位'], 虐点: '玻璃渣' }, outputFormat: 'markdown' } },
    { id: 'business', name: '商战', description: '商海沉浮，资本博弈', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计商战、职场类小说。请根据用户创意，设计商海沉浮、资本博弈、行业竞争与逆袭的大纲。`, styleGuidance: { tone: ['逆袭', '智斗'], rhythm: '中节奏', hotSpots: ['并购', '布局', '打脸'], 虐点: '微虐' }, outputFormat: 'markdown' } },
    { id: 'slice', name: '日常/轻小说', description: '轻松日常，治愈成长', isBuiltIn: true, prompt: { base: `你是一位资深网文策划专家，特别擅长设计日常、轻小说风格。请根据用户创意，设计轻松日常、治愈、校园或职场成长的大纲。`, styleGuidance: { tone: ['轻松', '治愈'], rhythm: '慢节奏', hotSpots: ['日常', '羁绊', '成长'], 虐点: '无' }, outputFormat: 'markdown' } },
  ]);
  
  const [selectedOutlineTemplate, setSelectedOutlineTemplate] = useState('standard');  // 选中的模板
  const [outlineUserRequirement, setOutlineUserRequirement] = useState('');  // 用户需求
  const [outlineGenDetail, setOutlineGenDetail] = useState('detailed');  // 详细程度
  const [generatingOutline, setGeneratingOutline] = useState(false);  // 是否正在生成大纲
  const [streamingOutline, setStreamingOutline] = useState('');  // 流式生成的内容
  const [outlineWordCount, setOutlineWordCount] = useState(0);  // 大纲字数统计
  const [isEditingOutline, setIsEditingOutline] = useState(false);  // 是否在编辑大纲
  const [autoScrollEnabled, setAutoScrollEnabled] = useState(true);  // 是否自动滚动到大纲区域
  const [outlineValidation, setOutlineValidation] = useState({});  // 大纲选项验证状态
  const outlineContentRef = useRef(null);  // 大纲流式生成结果显示区域（用于 scrollIntoView）
  const outlineScrollContainerRef = useRef(null);  // AI 生成大纲弹窗内实际滚动的容器

  // 验证大纲选项
  const validateOutlineOptions = () => {
    const tags = outlineFormData.tags || [];
    const hotSpots = outlineFormData.hotSpots || [];
    const validation = {
      type: !!outlineFormData.type,
      tags: tags.length > 0,
      cpType: !!outlineFormData.cpType,
      worldView: !!outlineFormData.worldView || outlineFormData.worldView === 'none',
      hotSpots: hotSpots.length > 0 || hotSpots.includes('none'),
      goldenFinger: !!outlineFormData.goldenFinger || outlineFormData.goldenFinger === 'none',
      coreConflict: !!outlineFormData.coreConflict || outlineFormData.coreConflict === 'none',
      rhythm: !!outlineFormData.rhythm || outlineFormData.rhythm === 'none',
      userRequirement: !!outlineUserRequirement.trim()
    };
    setOutlineValidation(validation);
    return validation;
  };

  // 检查所有必填项是否已填写
  const isOutlineFormValid = () => {
    const validation = validateOutlineOptions();
    return validation.type && validation.tags && validation.cpType && validation.worldView && validation.hotSpots && validation.goldenFinger && validation.coreConflict && validation.rhythm && validation.userRequirement;
  };
  const [simpleOutlineContent, setSimpleOutlineContent] = useState('');  // 手动粘贴的大纲内容
  const [simpleOutlineChapterCount, setSimpleOutlineChapterCount] = useState(100);  // 手动添加大纲时的计划总章节数

  // 大纲AI生成弹窗
  const [showOutlineGenDialog, setShowOutlineGenDialog] = useState(false);
  const [showSimpleOutlineDialog, setShowSimpleOutlineDialog] = useState(false);  // 手动粘贴大纲弹窗
  const [showOutlineParseDialog, setShowOutlineParseDialog] = useState(false);  // AI解析大纲弹窗（从已保存的整段大纲提取结构化）
  const [outlineParseContent, setOutlineParseContent] = useState('');  // 要解析的大纲内容
  const [parsingOutline, setParsingOutline] = useState(false);  // 解析中
  const [outlineParsePhaseIdx, setOutlineParsePhaseIdx] = useState(0);  // 解析阶段动画索引
  const [outlineParseProgressText, setOutlineParseProgressText] = useState(''); // 🔥 分段解析真实进度文字（第N/M段）
  const [showCharacterParseDialog, setShowCharacterParseDialog] = useState(false);  // AI解析角色弹窗（从大纲提取角色）
  const [characterParseContent, setCharacterParseContent] = useState('');  // 要解析的角色内容
  const [parsingCharacter, setParsingCharacter] = useState(false);  // 解析角色中
  const [characterParsePhaseIdx, setCharacterParsePhaseIdx] = useState(0);  // 角色解析阶段动画索引
  const [showRelationParseDialog, setShowRelationParseDialog] = useState(false);  // AI解析关系弹窗
  const [relationParseContent, setRelationParseContent] = useState('');  // 要解析的关系内容
  const [parsingRelation, setParsingRelation] = useState(false);  // 解析关系中

  // 词条库状态
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [itemCategory, setItemCategory] = useState('all');
  const [editingItem, setEditingItem] = useState(null);

  // 备忘录状态
  const [memos, setMemos] = useState([]);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [memoSearch, setMemoSearch] = useState('');
  const [memoType, setMemoType] = useState('all');
  const [editingMemo, setEditingMemo] = useState(null);

  // 大纲状态
  const [novelOutline, setNovelOutline] = useState({
    mainLine: '',
    subLines: [],
    foreshadowing: [],
    theme: '',
    tone: ''
  });

  // 监听大纲流式内容变化，自动滚动到底部，保证实时看到生成内容
  useEffect(() => {
    if (!generatingOutline || !autoScrollEnabled) return;
    const el = outlineScrollContainerRef.current;
    if (!el) return;
    // 使用 requestAnimationFrame 确保 DOM 已更新后再滚动
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [streamingOutline, autoScrollEnabled, generatingOutline]);

  // 正文流式：跟随生成末尾（textarea + 外层可滚容器）
  useEffect(() => {
    if (!bodyGenerating || !chapterBodyStreamFollowOutput) return;
    const text = selectedChapterForEdit?.outline?.generatedContent;
    if (text == null) return;
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      if (cancelled) return;
      const ta = chapterBodyStreamTextareaRef.current;
      if (ta) ta.scrollTop = ta.scrollHeight;
      scrollChapterBodyStreamOuterToBottom();
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [selectedChapterForEdit?.outline?.generatedContent, bodyGenerating, chapterBodyStreamFollowOutput, scrollChapterBodyStreamOuterToBottom]);

  // 用户滚动外层列表/面板时：离开底部则暂停跟随，回到底部再恢复
  useEffect(() => {
    if (!bodyGenerating) return undefined;
    const sp = getChapterBodyStreamFirstScrollParent();
    if (!sp) return undefined;
    sp.addEventListener('scroll', handleChapterBodyStreamOuterScroll, { passive: true });
    return () => sp.removeEventListener('scroll', handleChapterBodyStreamOuterScroll);
  }, [bodyGenerating, getChapterBodyStreamFirstScrollParent, handleChapterBodyStreamOuterScroll, selectedChapterForEdit?.outline?.generatedContent]);

  // 侧边面板宽度控制
  const [sidePanelWidth, setSidePanelWidth] = useState(380);

  // 编辑表单数据 - 扩展更多角色信息
  const [characterFormData, setCharacterFormData] = useState({
    name: '', title: '', realm: '', level: '', description: '', abilities: '', background: '',
    appearance: '', personality: '', goal: '', secret: '', relationships: '', equipment: '',
    color: '#00f0ff', tags: [], relations: []
  });
  const [generatingCharacter, setGeneratingCharacter] = useState(false);
  const [characterGenPrompt, setCharacterGenPrompt] = useState('');
  // 词条AI生成状态
  const [generatingItem, setGeneratingItem] = useState(false);
  const [itemGenPrompt, setItemGenPrompt] = useState('');
  const [itemGenTemplate, setItemGenTemplate] = useState('custom'); // 当前选择的模板
  const [newTag, setNewTag] = useState('');
  const [newRelation, setNewRelation] = useState({ characterId: '', type: '其他', customType: '' });
  const [itemFormData, setItemFormData] = useState({
    name: '', category: '势力', level: '', description: '',
    location: '', features: '', history: '', master: '', value: '',
    firstChapter: '', plotPoints: '', foreshadowing: ''
  });
  const [memoFormData, setMemoFormData] = useState({
    title: '', content: '', type: '灵感',
    priority: 'medium', status: 'pending'
  });
  
  // 对话式 AI 状态
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatEndRef, setChatEndRef] = useState(null); // 用于自动滚动到底部
  // 对话会话管理
  const [conversations, setConversations] = useState([]);       // 对话列表
  const [currentConversationId, setCurrentConversationId] = useState(null); // 当前对话ID
  const [conversationListOpen, setConversationListOpen] = useState(false);   // 对话列表展开状态
  const [conversationLoading, setConversationLoading] = useState(false);     // 加载状态
  const [deleteChatDialog, setDeleteChatDialog] = useState({ open: false, id: null });
  const [deleteChatLoading, setDeleteChatLoading] = useState(false);

  // 续写相关状态
  const [continuePrompt, setContinuePrompt] = useState('');
  const [continueStyle, setContinueStyle] = useState('紧张刺激');
  const [autoLink, setAutoLink] = useState(true);
  // 续写增强参数
  const [continueLength, setContinueLength] = useState('medium'); // short/medium/long
  const [continueEmotions, setContinueEmotions] = useState([]); // 多选情感标签
  const [continueHookEnabled, setContinueHookEnabled] = useState(true); // 埋钩子开关
  const [continueBans, setContinueBans] = useState([]); // 禁止项标签
  const [continueMoreOpen, setContinueMoreOpen] = useState(false); // 更多选项折叠
  
  // 写作目标
  const [todayWords, setTodayWords] = useState(0);
  
  // 聊天输入框引用
  const chatInputRef = useRef(null);
  /** 对话面板整体高度 → 输入区最大高度 = 约 33% 面板高 */
  const chatPanelLayoutRef = useRef(null);
  const [chatComposerMaxPx, setChatComposerMaxPx] = useState(160);
  const editorRef = useRef(null);
  const editorContainerRef = useRef(null);

  /** 正文 textarea：根据 selectionStart/End 同步选中态（勿用 document.selectionchange + 频繁 setState，会打断拖动选区） */
  const syncChapterEditorSelection = useCallback(() => {
    const textarea = editorRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    if (start == null || end == null || start === end) {
      setSelectedText('');
      setSelectionRange(null);
      setShowFloatingToolbar(false);
      return;
    }
    const text = textarea.value.substring(start, end).trim();
    if (!text) {
      setSelectedText('');
      setSelectionRange(null);
      setShowFloatingToolbar(false);
      return;
    }
    setSelectedText(text);
    setSelectionRange({ start, end });
    const lineHeight = 28;
    const lines = textarea.value.substring(0, start).split('\n');
    const currentLineIndex = lines.length - 1;
    setFloatingToolbarPos({
      x: textarea.getBoundingClientRect().left + textarea.offsetWidth / 2,
      y: 150 + currentLineIndex * lineHeight,
    });
    setShowFloatingToolbar(true);
  }, []);

  /** 等浏览器提交选区后再读（Chrome 设备模式 / 触摸序列下必要） */
  const scheduleSyncChapterEditorSelection = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        syncChapterEditorSelection();
      });
    });
  }, [syncChapterEditorSelection]);

  // 对话面板：输入区最大高度 = 整个聊天面板可视高度的 33%（随窗口/侧栏缩放）
  useLayoutEffect(() => {
    if (activePanel !== 'chat') return undefined;
    const node = chatPanelLayoutRef.current;
    if (!node) return undefined;
    const update = () => {
      const el = chatPanelLayoutRef.current;
      if (!el) return;
      const h = el.getBoundingClientRect().height;
      setChatComposerMaxPx(Math.max(88, Math.floor(h * 0.33)));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, [activePanel, chatMessages.length, chatLoading]);

  // 模板芯片样式函数
  const chipStyle = (templateId) => ({
    bgcolor: itemGenTemplate === templateId ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
    color: itemGenTemplate === templateId ? '#ff6b35' : 'rgba(255,255,255,0.7)',
    border: itemGenTemplate === templateId ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
    fontSize: 11,
    cursor: 'pointer',
    '&:hover': { bgcolor: itemGenTemplate === templateId ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
  });

  // 根据分类获取提示词
  const getPlaceholderByCategory = (category) => {
    const placeholders = {
      '势力': '描述你想要的势力组织，如：一个强大的修仙门派，总部在天山之巅...',
      '功法': '描述你想要的功法技能，如：一本修炼火系魔法的功法，从入门到成神...',
      '物品': '描述你想要的物品法宝，如：一把能够吸收雷电的仙剑，威力无穷...',
      '副本': '描述你想要的副本秘境，如：一个充满危机的上古遗迹，里面有珍贵传承...',
      '角色': '描述你想要的角色，如：一个冷酷的剑客，身负血海深仇...',
      '种族': '描述你想要的种族，如：一个天生拥有龙之血脉的种族，肉体强横...',
      '地域': '描述你想要的地域，如：一个灵气浓郁的修仙大陆，万族林立...',
      '王朝': '描述你想要的王朝，如：一个统治万界的永恒皇朝...',
      '组织': '描述你想要的组织，如：一个暗中掌控修仙界的杀手组织...',
      '血脉': '描述你想要的血脉体质，如：一种能够吞噬万物的禁忌血脉...',
      '灵根': '描述你想要的灵根资质，如：一种变异的天品灵根，修炼速度倍增...',
      '灵兽': '描述你想要的灵兽，如：一只上古神兽青龙，可呼风唤雨...',
      '材料': '描述你想要的材料，如：一种天地生成的灵根之晶...',
      '阵法': '描述你想要的阵法，如：一个能够困住大能的杀阵...',
      '秘宝': '描述你想要的秘宝，如：一个蕴含小世界的空间戒指...',
      '传承': '描述你想要的传承，如：一位远古大帝留下的帝级传承...',
    };
    return placeholders[category] || '描述你想要的词条...';
  };

  // 加载小说数据
  const loadNovelData = useCallback(async () => {
    if (!novelId) {
      setLoading(true);
      try {
        // 【辅助创作】获取辅助创作小说列表
        const assistResponse = await apiClient.get('/assist/novels');
        if (assistResponse.data && assistResponse.data.success) {
          const assistNovels = assistResponse.data.data || [];
          setNovelList(assistNovels);
          setCreationType('assist');
          if (assistNovels.length > 0) {
            const firstNovel = assistNovels[0];
            setNovel(firstNovel);
            // 【辅助创作】并行加载全部数据（原串行6连发，大项目首屏慢的主因之一）
            const [outlineResponse, charactersResponse, itemsResponse, memosResponse, volumesResponse, chaptersResponse] = await Promise.all([
              apiClient.get(`/assist/novels/${firstNovel._id}/outline`).catch(() => null),
              apiClient.get(`/assist/novels/${firstNovel._id}/characters`).catch(() => null),
              apiClient.get(`/assist/novels/${firstNovel._id}/items`).catch(() => null),
              apiClient.get(`/assist/novels/${firstNovel._id}/memos`).catch(() => null),
              apiClient.get(`/assist/novels/${firstNovel._id}/volumes`).catch(() => null),
              apiClient.get(`/assist/novels/${firstNovel._id}/chapters`).catch(() => null),
            ]);
            if (outlineResponse?.data?.success) {
              setOutlines([outlineResponse.data.data]);
            }
            if (charactersResponse?.data?.success) {
              setCharacters(charactersResponse.data.data || []);
            }
            if (itemsResponse?.data?.success) {
              setItems(itemsResponse.data.data || []);
            }
            if (memosResponse?.data?.success) {
              setMemos(memosResponse.data.data || []);
            }
            if (volumesResponse?.data?.success) {
              setVolumes(volumesResponse.data.data || []);
            }
            if (chaptersResponse?.data?.success) {
              setChapters(chaptersResponse.data.data || []);
            }
          }
        }
      } catch (error) {
        console.error('【辅助创作】加载小说列表失败:', error);
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      // 判断是否为辅助创作小说（assist_开头）
      const isAssistNovel = String(novelId).startsWith('assist_');
      
      if (isAssistNovel) {
        // 【辅助创作】加载辅助创作小说
        setCreationType('assist');
        const response = await apiClient.get(`/assist/novels/${novelId}`);
        if (response.data && response.data.success) {
          const novelData = response.data.data;
          setNovel(novelData);
          // 【辅助创作】并行加载全部数据（原串行6连发，大项目首屏慢的主因之一）
          const [outlineResponse, charactersResponse, itemsResponse, memosResponse, volumesResponse, chaptersResponse] = await Promise.all([
            apiClient.get(`/assist/novels/${novelId}/outline`).catch(() => null),
            apiClient.get(`/assist/novels/${novelId}/characters`).catch(() => null),
            apiClient.get(`/assist/novels/${novelId}/items`).catch(() => null),
            apiClient.get(`/assist/novels/${novelId}/memos`).catch(() => null),
            apiClient.get(`/assist/novels/${novelId}/volumes`).catch(() => null),
            apiClient.get(`/assist/novels/${novelId}/chapters`).catch(() => null),
          ]);
          if (outlineResponse?.data?.success) {
            setOutlines([outlineResponse.data.data]);
            console.log('[加载] 大纲已加载, subPlots=' + (outlineResponse.data.data?.subPlots?.length || 0) + ', foreshadowing=' + (outlineResponse.data.data?.foreshadowing?.length || 0));
          }
          if (charactersResponse?.data?.success) {
            setCharacters(charactersResponse.data.data || []);
          }
          if (itemsResponse?.data?.success) {
            setItems(itemsResponse.data.data || []);
          }
          if (memosResponse?.data?.success) {
            setMemos(memosResponse.data.data || []);
          }
          if (volumesResponse?.data?.success) {
            setVolumes(volumesResponse.data.data || []);
          }
          if (chaptersResponse?.data?.success) {
            setChapters(chaptersResponse.data.data || []);
          }
        }
      } else {
        // 【全自动写作】加载全自动写作小说（原逻辑）
        setCreationType('auto');
        const response = await apiClient.get(`/novels/${novelId}`);
        if (response.data && response.data.success) {
          const novelData = response.data.data;
          setNovel(novelData);
          if (novelData.chapters && novelData.chapters.length > 0) {
            setChapters(novelData.chapters);
            setCurrentChapter(novelData.chapters[0]);
            setChapterContent(novelData.chapters[0].content || '');
          }
        }
      }
    } catch (error) {
      console.error('加载小说数据失败:', error);
      setSnackbar({ open: true, message: '加载失败，请重试', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [novelId]);

  useEffect(() => {
    loadNovelData();
  }, [loadNovelData]);

  // MUI Dialog/Modal 通过 Portal 挂到 body，无法继承 .workspace-container 的 CSS 变量；同步到 body 保证弹窗内 var(--ws-accent) 正确
  useEffect(() => {
    const cls = 'assist-workspace-active';
    if (creationType === 'assist') {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [creationType]);

  useEffect(() => {
    if (!parsingOutline) {
      setOutlineParsePhaseIdx(0);
      return undefined;
    }
    const id = setInterval(() => {
      setOutlineParsePhaseIdx((i) => (i + 1) % OUTLINE_PARSE_PHASE_LABELS.length);
    }, 2000);
    return () => clearInterval(id);
  }, [parsingOutline]);

  useEffect(() => {
    if (!parsingCharacter) {
      setCharacterParsePhaseIdx(0);
      return undefined;
    }
    const id = setInterval(() => {
      setCharacterParsePhaseIdx((i) => (i + 1) % CHARACTER_PARSE_PHASE_LABELS.length);
    }, 2000);
    return () => clearInterval(id);
  }, [parsingCharacter]);

  // 自动关闭提示消息
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => {
        setSnackbar(s => ({ ...s, open: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open, snackbar.message]);

  // AI 生成弹窗打开时：按当前大纲数据重置「引用」勾选
  useEffect(() => {
    if (!aiGenerateDialogOpen) return;
    const o = outlines[0];
    const txt = (o?.content || o?.mainPlot?.description || o?.mainPlot?.storyline || '').trim();
    const hasStruct = !!(o?.mainPlot?.description && String(o.mainPlot.description).trim().length > 0);
    setAiGenerateReference({
      outlineFullText: txt.length > 0,
      structuredMainPlot: hasStruct,
      subPlots: false,
      foreshadowings: false,
      characters: false,
      items: false,
    });
  }, [aiGenerateDialogOpen]);

  useEffect(() => {
    if (!aiGenerateDialogOpen || aiGenerateType !== 'volume' || !aiGenerateVolumeId) return;
    const vol = volumes.find(v => v._id === aiGenerateVolumeId);
    if (vol?.chapterCount) setAiGenerateChapterCount(Math.max(1, vol.chapterCount));
  }, [aiGenerateDialogOpen, aiGenerateType, aiGenerateVolumeId, volumes]);

  // 切换章节时：将已保存的章纲同步到向导（同时将上一章的下章预览作为初始推荐）
  useEffect(() => {
    if (!selectedChapterForEdit?._id) {
      setWizardCoreEvent('');
      setWizardSelectedChars([]);
      setWizardSelectedForeshadowing([]);
      setWizardSelectedSubplots([]);
      setWizardSelectedItems([]);
      return;
    }
    const ch = selectedChapterForEdit;
    const o = ch.outline || {};

    // ── 1. 核心事件 ──
    const core = String(o.coreEvent || '').trim() || String(o.plotPoints?.[0]?.content || '').trim();

    // ── 2. 本章已有数据 ──
    const chChars = ch.characterIds || [];
    const chFs = new Set([
      ...(ch.foreshadowingIds || []),
      ...((o.plotPoints || []).flatMap(p => p.foreshadowingIds || []).filter(Boolean)),
    ]);
    const chSp = new Set([
      ...(ch.subplotIds || []),
      ...((o.plotPoints || []).flatMap(p => p.subplotIds || []).filter(Boolean)),
    ]);
    const chItems = [
      ...(ch.itemIds || []),
      ...((o.plotPoints || []).flatMap(p => p.itemIds || []).filter(Boolean)),
    ];

    // ── 3. 上一章的 nextChapterPlan 追加进来 ──
    const prevCh = chapters
      .filter(c => c.volumeId === ch.volumeId && c.chapterNumber < ch.chapterNumber)
      .sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
    const ncp = prevCh?.outline?.nextChapterPlan;
    if (ncp) {
      if (ncp.characterIds?.length) {
        ncp.characterIds.forEach(id => { if (id && !chChars.includes(id)) chChars.push(id); });
      }
      if (ncp.foreshadowingIds?.length) {
        ncp.foreshadowingIds.forEach(id => { if (id) chFs.add(id); });
      }
      if (ncp.subplotIds?.length) {
        ncp.subplotIds.forEach(id => { if (id) chSp.add(id); });
      }
    }

    // ── 4. 若章纲里未保存任何伏笔/支线，按大纲章节范围自动预选（与右侧编辑区逻辑一致） ──
    const outline0 = outlines[0];
    const curNum = ch.chapterNumber;
    if (outline0 && curNum != null) {
      if (chFs.size === 0) {
        (outline0.foreshadowing || []).forEach(f => {
          if (f.status === 'revealed') return;
          let inRange = false;
          if (f.plantedChapterNumber != null && f.plantedChapterNumber !== '') {
            const p = parseInt(String(f.plantedChapterNumber), 10);
            if (!Number.isNaN(p)) {
              if (f.revealChapterNumber != null && f.revealChapterNumber !== '') {
                const r = parseInt(String(f.revealChapterNumber), 10);
                if (!Number.isNaN(r)) inRange = curNum >= p && curNum <= r;
              } else {
                inRange = curNum >= p;
              }
            }
          } else if (f.plantedChapterId) {
            const plantedChapter = chapters.find(c => c._id === f.plantedChapterId);
            if (plantedChapter) {
              const pNum = plantedChapter.chapterNumber;
              if (f.revealChapterId) {
                const revealChapter = chapters.find(c => c._id === f.revealChapterId);
                if (revealChapter) inRange = curNum >= pNum && curNum <= revealChapter.chapterNumber;
              } else {
                inRange = curNum >= pNum;
              }
            }
          }
          if (inRange) {
            const fid = f.id || f._id;
            if (fid) chFs.add(fid);
          }
        });
      }
      if (chSp.size === 0) {
        (outline0.subPlots || []).forEach(s => {
          if (s.startChapterNumber != null && s.endChapterNumber != null
            && curNum >= s.startChapterNumber && curNum <= s.endChapterNumber) {
            const sid = s.id || s._id;
            if (sid) chSp.add(sid);
          }
        });
      }
    }

    // ── 5. 一次性 set 全部（避免多次 set 导致渲染期调用问题） ──
    setWizardCoreEvent(core);
    setWizardSelectedChars([...chChars]);
    setWizardSelectedForeshadowing(Array.from(chFs));
    setWizardSelectedSubplots(Array.from(chSp));
    setWizardSelectedItems([...new Set(chItems)]);

    // ── 6. 自动写入本章需要的角色到 editingChapterOutline（包含上章推荐 + 大纲预选） ──
    if (editingChapterOutline) {
      setEditingChapterOutline(prev => ({
        ...prev,
        characterIds: [...new Set([...(prev?.characterIds || []), ...chChars])]
      }));
    }
  }, [selectedChapterForEdit?._id, chapters, outlines]);

  // 加载章纲生成上下文
  const loadChapterOutlineContext = useCallback(async (volumeId, startChapter) => {
    if (!assistNovelId || !volumeId) return;
    setAiGenerateContextLoading(true);
    try {
      const response = await apiClient.get(`/assist/novels/${assistNovelId}/volumes/${volumeId}/context/${startChapter}`);
      if (response.data?.success) {
        setAiGenerateContext(response.data.data);
      }
    } catch (error) {
      console.error('加载章纲上下文失败:', error);
    } finally {
      setAiGenerateContextLoading(false);
    }
  }, [assistNovelId]);

  // 保存章节
  const handleSaveChapter = async () => {
    if (!currentChapter) {
      setSnackbar({ open: true, message: '请先选择章节', severity: 'warning' });
      return;
    }
    // 辅助创作模式
    if (assistNovelId) {
      setSaving(true);
      try {
        await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${currentChapter._id}`, {
          content: chapterContent,
          wordCount: chapterContent.length
        });
        setChapters(prev => prev.map(c => c._id === currentChapter._id ? { ...c, content: chapterContent, wordCount: chapterContent.length } : c));
        setSnackbar({ open: true, message: '保存成功', severity: 'success' });
      } catch (error) {
        console.error('保存失败:', error);
        setSnackbar({ open: true, message: '保存失败', severity: 'error' });
      } finally {
        setSaving(false);
      }
      return;
    }
    // 全自动创作模式
    if (!novel?._id) {
      setSnackbar({ open: true, message: '作品未加载', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      // 🔥 修复：后端 PUT /novels/:id/chapters/:chapterIndex 期望 1-based 数字索引，
      //    原先传 currentChapter._id（UUID 字符串）→ parseInt 得 NaN → 400 保存必失败。
      //    改为按 _id 找到章节在列表中的位置，+1 转为 1-based 章节号（与删除章节逻辑一致）
      const chapterIdx = chapters.findIndex(c => c._id === currentChapter._id);
      if (chapterIdx === -1) {
        setSnackbar({ open: true, message: '未找到当前章节', severity: 'warning' });
        return;
      }
      await apiClient.put(`/novels/${novel._id}/chapters/${chapterIdx + 1}`, {
        content: chapterContent
      });
      setSnackbar({ open: true, message: '保存成功', severity: 'success' });
    } catch (error) {
      console.error('保存失败:', error);
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // 创建新章节 (支持指定卷ID)。返回 true 表示成功，false 表示失败。
  const handleCreateChapter = async (volumeId = null, titleOptional = null) => {
    const currentNovelId = assistNovelId;
    if (!currentNovelId) {
      setSnackbar({ open: true, message: '请先选择或创建作品', severity: 'warning' });
      return false;
    }
    // 避免 onClick 误传 SyntheticEvent 当作 volumeId
    const resolvedVolumeId = typeof volumeId === 'string' && volumeId ? volumeId : null;
    try {
      const targetVolumeId = resolvedVolumeId || (volumes.length > 0 ? volumes[0]._id : null);

      // 判断是否为辅助创作小说
      const isAssistNovel = String(currentNovelId).startsWith('assist_');

      if (isAssistNovel) {
        if (!targetVolumeId) {
          setSnackbar({ open: true, message: '没有可挂载的卷，请先创建卷', severity: 'warning' });
          return false;
        }
        // 【辅助创作】插入到指定卷末尾：全局序号 = 该位置之前章数 + 1；其后所有章（含后卷）编号 +1，再 POST 新章（仅当前 novelId，数据隔离）
        const { insertAt, newGlobalNum, glob } = getAppendEndOfVolumeSlot(chapters, volumes, targetVolumeId);
        const toShift = glob.slice(insertAt);
        const sortedShift = [...toShift].sort((a, b) => (Number(b.chapterNumber) || 0) - (Number(a.chapterNumber) || 0));
        for (const ch of sortedShift) {
          const nn = (Number(ch.chapterNumber) || 0) + 1;
          const raw = String(ch.title || '').trim();
          const payload = { chapterNumber: nn };
          if (/^第\s*\d+\s*章$/.test(raw)) payload.title = `第${nn}章`;
          await apiClient.put(`/assist/novels/${currentNovelId}/chapters/${ch._id}`, payload);
        }
        const finalTitle = (titleOptional && String(titleOptional).trim())
          ? String(titleOptional).trim()
          : `第${newGlobalNum}章`;
        const chapterData = {
          title: finalTitle,
          content: '',
          chapterNumber: newGlobalNum,
          volumeId: targetVolumeId,
          status: 'outline_only',
          outline: {
            points: [],
            coreEvent: '',
            plotPoints: [],
            connectionToPrevious: '',
            hookForNext: ''
          },
          targetWordCount: 3000,
          wordCount: 0,
          characterIds: [],
          foreshadowingIds: []
        };
        const response = await apiClient.post(`/assist/novels/${currentNovelId}/chapters`, chapterData);
        if (response.data?.success) {
          const created = response.data.data;
          const listResp = await apiClient.get(`/assist/novels/${currentNovelId}/chapters`);
          let list = listResp.data?.success && Array.isArray(listResp.data.data) ? listResp.data.data : null;
          if (!list) {
            const shiftedIds = new Set(sortedShift.map((s) => s._id));
            const bumped = chapters.map((c) => {
              if (!shiftedIds.has(c._id)) return c;
              const nn = (Number(c.chapterNumber) || 0) + 1;
              const raw = String(c.title || '').trim();
              return { ...c, chapterNumber: nn, ...( /^第\s*\d+\s*章$/.test(raw) ? { title: `第${nn}章` } : {}) };
            });
            list = renumberAssistChaptersGlobal([...bumped, created], volumes);
          }
          setChapters(list);
          const newChapter = list.find((c) => c._id === created._id) || created;
          setCurrentChapter(newChapter);
          setChapterContent('');
          setSelectedChapterForEdit(newChapter);
          setActivePanel('chapters');
          setSnackbar({ open: true, message: `「${newChapter.title}」创建成功，可在右侧编辑章纲与正文`, severity: 'success' });
          return newChapter;
        }
        setSnackbar({ open: true, message: response.data?.message || '创建章节失败（服务端未确认成功）', severity: 'error' });
        return false;
      } else {
        // 【全自动写作】使用原有API（全书顺序一章接一章）
        const nextIndex = chapters.length > 0
          ? Math.max(...chapters.map(c => c.chapterNumber || 0)) + 1
          : 1;
        const chapterData = {
          title: (titleOptional && String(titleOptional).trim()) ? String(titleOptional).trim() : `第${nextIndex}章`,
          content: '',
          order: nextIndex,
          volumeId: targetVolumeId,
          outline: {
            points: [],
            targetWordCount: 3000,
            actualWordCount: 0,
            status: 'pending'
          }
        };
        const response = await apiClient.put(`/novels/${currentNovelId}/chapters/${nextIndex}`, chapterData);
        if (response.data && response.data.success) {
          const updatedNovel = response.data.data;
          if (updatedNovel && updatedNovel.chapters) {
            let list = updatedNovel.chapters;
            if (targetVolumeId && list.length) {
              const last = list[list.length - 1];
              if (!last.volumeId) list = list.map((c, i) => (i === list.length - 1 ? { ...c, volumeId: targetVolumeId } : c));
            }
            setChapters(list);
            const newChapter = list[list.length - 1];
            if (newChapter) {
              setCurrentChapter(newChapter);
              setChapterContent('');
              setSelectedChapterForEdit(newChapter);
              setActivePanel('chapters');
              setSnackbar({ open: true, message: `「${newChapter.title}」创建成功，可在右侧编辑章纲与正文`, severity: 'success' });
              return newChapter;
            }
          }
        }
        return false;
      }
    } catch (error) {
      console.error('创建章节失败:', error);
      setSnackbar({ open: true, message: '创建章节失败: ' + (error.response?.data?.message || error.message), severity: 'error' });
      return false;
    }
  }; // 成功时返回新建章节对象，失败返回 false（便于调用方直接选中该章）

  // 删除章节
  const handleDeleteChapter = async (chapterId) => {
    const currentNovelId = novelId || novel?._id;
    if (!currentNovelId || !chapterId) return;
    try {
      const isAssistNovel = String(currentNovelId).startsWith('assist_');

      if (isAssistNovel) {
        // 【辅助创作】使用新的章节API
        await apiClient.delete(`/assist/novels/${currentNovelId}/chapters/${chapterId}`);
      } else {
        // 【全自动写作】使用原有API
        const chapterIndex = chapters.findIndex(c => c._id === chapterId);
        if (chapterIndex === -1) return;
        const nextIndex = chapterIndex + 1;
        await apiClient.put(`/novels/${currentNovelId}/chapters/${nextIndex}`, { deleted: true });
      }
      const newChapters = chapters.filter(c => c._id !== chapterId);
      const oldById = new Map(newChapters.map(c => [c._id, c]));
      const renumbered = isAssistNovel
        ? renumberAssistChaptersGlobal(newChapters, volumes)
        : [...newChapters]
            .sort((a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0))
            .map((c, idx) => ({ ...c, chapterNumber: idx + 1 }));
      let finalList = renumbered;
      if (isAssistNovel && renumbered.length > 0) {
        await Promise.all(
          renumbered.map((c) => {
            const old = oldById.get(c._id);
            if (!old || (old.chapterNumber === c.chapterNumber && old.title === c.title)) {
              return Promise.resolve();
            }
            const payload = { chapterNumber: c.chapterNumber };
            if (c.title !== old.title) payload.title = c.title;
            return apiClient.put(`/assist/novels/${currentNovelId}/chapters/${c._id}`, payload);
          })
        );
        const listResp = await apiClient.get(`/assist/novels/${currentNovelId}/chapters`);
        if (listResp.data?.success && Array.isArray(listResp.data.data)) {
          finalList = listResp.data.data;
        }
      }
      setChapters(finalList);
      if (currentChapter?._id === chapterId) {
        if (finalList.length > 0) {
          const volId = currentChapter.volumeId;
          const inVol = finalList
            .filter(c => c.volumeId === volId)
            .sort((a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0));
          const next = inVol[0] || finalList[0];
          setCurrentChapter(next);
          setChapterContent(next.content || '');
        } else {
          setCurrentChapter(null);
          setChapterContent('');
        }
      }
      setSnackbar({ open: true, message: '章节删除成功', severity: 'success' });
    } catch (error) {
      console.error('删除章节失败:', error);
      setSnackbar({ open: true, message: '删除失败', severity: 'error' });
    }
  };

  // 开始编辑章节标题（在弹窗中）
  const handleStartEditChapterTitle = (chapter, e) => {
    e.stopPropagation();
    setEditingChapterId(chapter._id);
    setEditingChapterTitle(chapter.title);
  };

  // 开始编辑当前章节标题（在工作台顶部）
  const handleStartEditTopTitle = () => {
    if (!currentChapter) return;
    setEditingChapterId(currentChapter._id);
    setEditingChapterTitle(currentChapter.title);
    setIsEditingTitle(true);
  };

  // 保存章节标题（从顶部编辑）
  const handleSaveTopTitle = async () => {
    if (!editingChapterId || !editingChapterTitle.trim()) {
      setIsEditingTitle(false);
      setEditingChapterId(null);
      return;
    }
    try {
      const chapter = chapters.find(c => c._id === editingChapterId);
      if (!chapter) { setIsEditingTitle(false); return; }
      const updatedChapter = { ...chapter, title: editingChapterTitle };
      if (assistNovelId) {
        await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${editingChapterId}`, { title: editingChapterTitle });
        setChapters(prev => prev.map(c => c._id === editingChapterId ? updatedChapter : c));
      } else {
        const currentNovelId = novelId || novel?._id;
        if (!currentNovelId) { setIsEditingTitle(false); return; }
        const chapterIndex = chapters.findIndex(c => c._id === editingChapterId);
        if (chapterIndex === -1) { setIsEditingTitle(false); return; }
        await apiClient.put(`/novels/${currentNovelId}/chapters/${chapterIndex + 1}`, { title: editingChapterTitle });
        setChapters(prev => prev.map(c => c._id === editingChapterId ? updatedChapter : c));
      }
      if (currentChapter?._id === editingChapterId) setCurrentChapter(updatedChapter);
      setSnackbar({ open: true, message: '章节标题已更新', severity: 'success' });
    } catch (error) {
      console.error('更新章节标题失败:', error);
      setSnackbar({ open: true, message: '更新失败', severity: 'error' });
    }
    setIsEditingTitle(false);
    setEditingChapterId(null);
  };

  // 保存章节标题（通用，支持弹窗传参）
  const handleSaveChapterTitle = async (chapterToSave) => {
    const idToSave = chapterToSave ? chapterToSave._id : editingChapterId;
    const titleToSave = chapterToSave ? chapterToSave.title : editingChapterTitle;
    if (!idToSave || !titleToSave?.trim()) {
      setEditingChapterId(null);
      return;
    }
    try {
      const targetChapter = chapters.find(c => c._id === idToSave);
      if (!targetChapter) { setEditingChapterId(null); return; }
      const updatedChapter = { ...targetChapter, title: titleToSave };
      if (assistNovelId) {
        await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${idToSave}`, { title: titleToSave });
        setChapters(prev => prev.map(c => c._id === idToSave ? updatedChapter : c));
      } else {
        const currentNovelId = novelId || novel?._id;
        if (!currentNovelId) { setEditingChapterId(null); return; }
        const chapterIndex = chapters.findIndex(c => c._id === idToSave);
        if (chapterIndex === -1) { setEditingChapterId(null); return; }
        await apiClient.put(`/novels/${currentNovelId}/chapters/${chapterIndex + 1}`, { title: titleToSave });
        setChapters(prev => prev.map(c => c._id === idToSave ? updatedChapter : c));
      }
      if (currentChapter?._id === idToSave) setCurrentChapter(updatedChapter);
      setSnackbar({ open: true, message: '章节标题已更新', severity: 'success' });
      if (!chapterToSave) setEditingChapterId(null);
    } catch (error) {
      console.error('更新章节标题失败:', error);
      setSnackbar({ open: true, message: '更新失败', severity: 'error' });
    }
  };

  // 选择章节
  const handleSelectChapter = (chapter) => {
    setCurrentChapter(chapter);
    setChapterContent(chapter.content || '');
  };

  // 打开面板
  const handleOpenPanel = (panel, type = '') => {
    setActivePanel(panel);
    setPanelType(type);
  };

  // 关闭面板
  const handleClosePanel = () => {
    setActivePanel(null);
    setPanelType('');
    setGeneratedContent('');
    setSelectedCharacter(null);
    setSelectedItem(null);
    setSelectedMemo(null);
    setEditingCharacter(null);
    setEditingItem(null);
    setEditingMemo(null);
    // 关闭面板时清除选中文本高亮
    setSelectedText('');
    setSelectionRange(null);
    setShowFloatingToolbar(false);
  };

  // 添加/编辑角色
  // 【辅助创作】保存角色到数据库
  const handleSaveCharacter = async (characterData) => {
    try {
      let savedCharacter;

      if (creationType === 'assist' && assistNovelId) {
        // 【辅助创作】保存到数据库
        const response = await apiClient.post(`/assist/novels/${assistNovelId}/characters`, {
          ...characterData,
          novelId: assistNovelId
        });
        if (response.data && response.data.success) {
          savedCharacter = response.data.data;
        }
      }

      if (editingCharacter && editingCharacter._id) {
        // 编辑现有角色
        const updatedCharacters = characters.map(c =>
          c._id === editingCharacter._id ? { ...c, ...characterData, _id: editingCharacter._id } : c
        );
        setCharacters(updatedCharacters);
        if (savedCharacter) {
          // ── 编辑角色时同步关系时间轴中的角色名字 ────────────────────
          try {
            const existing = progressData?.relationshipChapterStates || [];
            const updatedRels = existing.map(rel => {
              if (rel.fromCharacterId === editingCharacter._id) {
                return { ...rel, fromCharacterName: characterData.name || rel.fromCharacterName };
              }
              if (rel.toCharacterId === editingCharacter._id) {
                return { ...rel, toCharacterName: characterData.name || rel.toCharacterName };
              }
              return rel;
            });
            if (JSON.stringify(existing) !== JSON.stringify(updatedRels)) {
              setProgressData(prev => prev ? { ...prev, relationshipChapterStates: updatedRels } : prev);
              await saveProgressData('relationshipChapterStates', updatedRels);
              console.log(`[全局进度] ✅ 编辑角色「${characterData.name}」已同步关系时间轴`);
            }
          } catch (relErr) {
            console.warn('[全局进度] 同步关系时间轴失败:', relErr);
          }
        }
        setSnackbar({ open: true, message: '角色更新成功', severity: 'success' });
      } else {
        // 添加新角色
        const newCharacter = {
          ...characterData,
          _id: savedCharacter?._id || `char_${Date.now()}`,
          createdAt: new Date()
        };
        const finalChar = savedCharacter || newCharacter;
        setCharacters(prev => [...prev, finalChar]);

        // ── 新角色同步写入全局进度（characterChapterStates + relationshipChapterStates）──
        try {
          const charId = finalChar._id;
          const charName = characterData.name || '';

          // ① 将新角色写入角色状态时间轴（chapterNumber=0 表示"未在任何章节出场"）
          const charStateEntry = {
            characterId: charId,
            chapterNumber: 0,
            chapterId: '',
            lifeStatus: 'alive',
            emotionStatus: characterData.personality || '正常',
            narrativeNote: `手动添加角色「${charName}」`,
            updatedAt: new Date()
          };
          const existingCharStates = progressData?.characterChapterStates || [];
          const newCharStates = existingCharStates.some(e => e.characterId === charId)
            ? existingCharStates
            : [...existingCharStates, charStateEntry];
          setProgressData(prev => prev ? { ...prev, characterChapterStates: newCharStates } : prev);
          await saveProgressData('characterChapterStates', newCharStates);

          // ② 将新角色的初始关系写入关系时间轴（characterData.relations 结构：{characterId, characterName, type, description}）
          const charRelations = characterData.relations || [];
          const allChars = [...characters, finalChar];
          const nameToId = {};
          allChars.forEach(c => { if (c._id !== charId) nameToId[c.name] = c._id; });

          const newRelEntries = charRelations
            .filter(rel => {
              // characterName 为对方角色名
              const toId = nameToId[rel.characterName];
              if (!toId) return false;
              // 避免重复
              const existing = progressData?.relationshipChapterStates || [];
              return !existing.some(e =>
                e.fromCharacterId === charId && e.toCharacterId === toId && e.chapterNumber === 0
              );
            })
            .map(rel => ({
              fromCharacterId: charId,
              fromCharacterName: charName,
              toCharacterId: nameToId[rel.characterName],
              toCharacterName: rel.characterName,
              relationType: rel.type || '其他',
              chapterNumber: 0,
              chapterId: '',
              fromStatus: '初始关系',
              toStatus: rel.description || '初始关系',
              reason: '角色创建时设定',
              updatedAt: new Date()
            }));

          if (newRelEntries.length > 0) {
            const existingRels = progressData?.relationshipChapterStates || [];
            const mergedRels = [...existingRels, ...newRelEntries];
            setProgressData(prev => prev ? { ...prev, relationshipChapterStates: mergedRels } : prev);
            await saveProgressData('relationshipChapterStates', mergedRels);
          }

          console.log(`[全局进度] ✅ 新角色「${charName}」已同步（状态1条，关系${newRelEntries.length}条）`);
        } catch (syncErr) {
          console.warn('[全局进度] 新角色同步全局进度失败（非致命）:', syncErr);
        }

        setSnackbar({ open: true, message: '角色添加成功', severity: 'success' });
      }
      setEditingCharacter(null);
      setActivePanel('character');
    } catch (error) {
      console.error('保存角色失败:', error);
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    }
  };

  // AI生成角色
  const handleAIGenerateCharacter = async () => {
    if (!characterGenPrompt.trim()) {
      setSnackbar({ open: true, message: '请输入角色描述或关键词', severity: 'warning' });
      return;
    }

    // 检查次数限制
    if (!checkAssistQuota('AI生成角色')) {
      return;
    }

    setGeneratingCharacter(true);
    try {
      const systemPrompt = '你是一位专业的小说人设大师，擅长创作各类型小说的角色人设。请根据用户的描述生成详细的角色信息。';
      const userPrompt = `请根据以下描述生成一个完整的角色人设：

${characterGenPrompt}

请按以下JSON格式输出角色信息（直接输出JSON，不要其他内容）：
{
  "name": "角色名",
  "gender": "性别（男/女）",
  "title": "称号/头衔",
  "realm": "势力/门派",
  "level": "境界/等级",
  "appearance": "外貌特征",
  "personality": "性格特点",
  "background": "角色背景",
  "description": "角色简介",
  "abilities": "能力/特长",
  "goal": "目标/追求",
  "secret": "秘密/把柄",
  "relationships": "人物关系",
  "equipment": "武器/装备",
  "color": "代表颜色( hex格式如 #00f0ff)"
}`;

      const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
      const response = await apiClient.post('/assist/ai/proxy-ai', {
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        model: 'deepseek-v4-flash',
        extra_body: {},
        temperature: 0.8,
        max_tokens: 2000,
        stream: false,
        billingInfo: {
          novelName: currentNovelName,
          chapterNumber: '',
          operationType: 'AI生成角色'
        }
      });

      const aiData = response.data;
      let content = '';

      if (aiData && aiData.choices && aiData.choices[0] && aiData.choices[0].message) {
        content = aiData.choices[0].message.content;
      } else if (aiData && aiData.success && aiData.data) {
        content = aiData.data.content || aiData.data;
      }

      // 尝试解析JSON（🔥 robustJsonParse 容错：截断/尾逗号/未转义换行均可修复）
      try {
        const parseResult = robustJsonParse(content);
        if (parseResult.ok && parseResult.data) {
          const parsedData = parseResult.data;
          // 合并到表单数据，保留原有数据，确保有gender字段
          setCharacterFormData(prev => ({
            ...prev,
            ...parsedData,
            gender: parsedData.gender || '未知',
            color: parsedData.color || prev.color
          }));
          setSnackbar({ open: true, message: '角色生成成功，请查看', severity: 'success' });
        } else {
          throw new Error('无法解析生成的角色');
        }
      } catch (parseError) {
        console.error('解析角色数据失败:', parseError);
        setSnackbar({ open: true, message: '生成格式有误，请重试', severity: 'error' });
      }
    } catch (error) {
      console.error('AI生成角色失败:', error);
      setSnackbar({ open: true, message: '生成失败，请重试', severity: 'error' });
    } finally {
      setGeneratingCharacter(false);
    }
  };

  // AI生成词条
  const handleAIGenerateItem = async () => {
    if (!itemGenPrompt.trim()) {
      setSnackbar({ open: true, message: '请输入词条描述或关键词', severity: 'warning' });
      return;
    }

    // 检查次数限制
    if (!checkAssistQuota('AI生成词条')) {
      return;
    }

    setGeneratingItem(true);
    try {
      // 根据分类和模板生成不同的提示词
      const category = itemFormData.category || '势力';
      const template = itemGenTemplate;

      // 模板配置
      const templateConfigs = {
        // 势力组织模板
        '势力-custom': { base: '创建一个自定义势力组织', desc: '' },
        '势力-factory': { base: '创建一个传统修仙门派', desc: '有掌门/长老/弟子体系，正道门派风格' },
        '势力-evil': { base: '创建一个魔道势力', desc: '修炼邪功，行事狠辣，黑暗风格' },
        '势力-empire': { base: '创建一个帝国皇朝', desc: '国家机器，有皇帝/官员/军队体系' },
        '势力-tribe': { base: '创建一个种族势力', desc: '妖/兽/灵等种族，有部落/王庭结构' },
        '势力-alliance': { base: '创建一个联盟组织', desc: '多个势力联合，有盟主/成员体系' },
        '势力-family': { base: '创建一个家族势力', desc: '家族传承，有族老/族人结构' },
        '势力-secret': { base: '创建一个神秘组织', desc: '暗中操控，不为人知的隐藏势力' },

        // 功法技能模板
        '功法-custom': { base: '创建一个自定义功法/技能', desc: '' },
        '功法-cultivation': { base: '创建一个修炼功法', desc: '从弱到强的修炼体系，适合主角' },
        '功法-attack': { base: '创建一个攻击神通', desc: '以战斗为主，强大攻击力招数' },
        '功法-defense': { base: '创建一个防御秘术', desc: '防御为主的技能秘法' },
        '功法-auxiliary': { base: '创建一个辅助秘法', desc: '探测、隐身、逃遁等特殊能力' },
        '功法-array': { base: '创建一个阵法禁制', desc: '阵法、禁制、结界类技能' },
        '功法-body': { base: '创建一个炼体功法', desc: '肉体强度修炼流派' },
        '功法-evil': { base: '创建一个魔功邪术', desc: '邪门功法，有代价的黑暗技能' },

        // 物品法宝模板
        '物品-custom': { base: '创建一个自定义物品/法宝', desc: '' },
        '物品-sword': { base: '创建一把剑类武器', desc: '飞剑、本命剑类型' },
        '物品-bead': { base: '创建一个珠子形态法宝', desc: '珠子形态的空间或特殊至宝' },
        '物品-scroll': { base: '创建一个卷轴类物品', desc: '传承卷轴、技能书类型' },
        '物品-furnace': { base: '创建一个鼎炉类法宝', desc: '炼丹/炼器用的鼎、炉、塔' },
        '物品-crown': { base: '创建一个冠饰类物品', desc: '身份象征的头饰、冠冕' },
        '物品-shield': { base: '创建一个防御型法宝', desc: '盾牌、护甲等防护武器' },
        '物品-pill': { base: '创建一枚丹药', desc: '辅助修炼的各类丹药' },

        // 副本秘境模板
        '副本-custom': { base: '创建一个自定义副本/秘境', desc: '' },
        '副本-continent': { base: '创建一个修仙大陆', desc: '大型疆域，多势力并存' },
        '副本-secret': { base: '创建一个秘境洞天', desc: '独立空间，有传承/危险' },
        '副本-city': { base: '创建一个主城都城', desc: '繁华都市，势力交汇' },
        '副本-forest': { base: '创建一个深山老林', desc: '人迹罕至，适合隐居' },
        '副本-ocean': { base: '创建一个海洋领域', desc: '海上势力，资源丰富' },
        '副本-danger': { base: '创建一个绝地险境', desc: '修炼禁地，九死一生' },
        '副本-border': { base: '创建一个边陲小镇', desc: '新手之地，机遇较多' },

        // 角色模板
        '角色-custom': { base: '创建一个自定义角色', desc: '' },
        '角色-protagonist': { base: '创建主角', desc: '故事核心人物，有成长路线' },
        '角色-antagonist': { base: '创建反派', desc: '与主角对立，推动剧情' },
        '角色-mentor': { base: '创建导师角色', desc: '帮助主角成长的人物' },
        '角色-love': { base: '创建恋人角色', desc: '情感线关键人物' },
        '角色-friend': { base: '创建好友角色', desc: '主角的伙伴和支持者' },
        '角色-rival': { base: '创建宿敌角色', desc: '与主角竞争的对手' },
        '角色-npc': { base: '创建NPC角色', desc: '功能性配角，情节推动者' },

        // 种族模板
        '种族-custom': { base: '创建一个自定义种族', desc: '' },
        '种族-dragon': { base: '创建龙族', desc: '强大种族，天赋异禀' },
        '种族-phoenix': { base: '创建凤凰族', desc: '浴火重生，传承血脉' },
        '种族-demon': { base: '创建恶魔族', desc: '魔界种族，强大肉身' },
        '种族-spirit': { base: '创建精灵族', desc: '自然之力，天赋灵性' },
        '种族-god': { base: '创建神族', desc: '天生神力，高高在上' },

        // 地域模板
        '地域-custom': { base: '创建一个自定义地域', desc: '' },
        '地域-mountain': { base: '创建山脉', desc: '险峻之地，天材地宝' },
        '地域-river': { base: '创建河流', desc: '水系资源，灵脉所在' },
        '地域-forest': { base: '创建森林', desc: '秘境众多，危险与机遇并存' },
        '地域-desert': { base: '创建沙漠', desc: '荒芜之地，有古遗迹' },
        '地域-island': { base: '创建岛屿', desc: '孤立海外，可能有传承' },

        // 王朝模板
        '王朝-custom': { base: '创建一个自定义王朝', desc: '' },
        '王朝-empire': { base: '创建一个大一统王朝', desc: '统治广阔疆域' },
        '王朝-kingdom': { base: '创建一个王国', desc: '中等规模国家' },
        '王朝-clan': { base: '创建一个部落', desc: '原始社会组织' },

        // 组织模板
        '组织-custom': { base: '创建一个自定义组织', desc: '' },
        '组织-secret': { base: '创建秘密组织', desc: '暗中活动，神秘莫测' },
        '组织-assassin': { base: '创建杀手组织', desc: '暗杀与情报' },
        '组织-merchant': { base: '创建商会组织', desc: '商业网络，消息灵通' },
        '组织-religious': { base: '创建宗教组织', desc: '信仰力量，精神控制' },

        // 血脉体质模板
        '血脉-custom': { base: '创建一个自定义血脉体质', desc: '' },
        '血脉-ancient': { base: '创建远古血脉', desc: '古老传承，强大潜力' },
        '血脉-divine': { base: '创建神级体质', desc: '天生神体，修炼速度惊人' },
        '血脉-mysterious': { base: '创建特殊体质', desc: '独一无二的能力' },

        // 灵根资质模板
        '灵根-custom': { base: '创建自定义灵根资质', desc: '' },
        '灵根-single': { base: '创建单一灵根', desc: '纯粹但限制发展' },
        '灵根-dual': { base: '创建双灵根', desc: '平衡发展潜力大' },
        '灵根-multiple': { base: '创建多灵根', desc: '杂而不精但适应性强' },
        '灵根-none': { base: '创建无灵根', desc: '特殊情况，可能有奇遇' },

        // 灵兽模板
        '灵兽-custom': { base: '创建自定义灵兽', desc: '' },
        '灵兽-beast': { base: '创建野兽型灵兽', desc: '战斗型灵兽' },
        '灵兽-bird': { base: '创建鸟类灵兽', desc: '速度快，侦查优秀' },
        '灵兽-aquatic': { base: '创建水生灵兽', desc: '水中生存，特殊能力' },
        '灵兽-insect': { base: '创建虫类灵兽', desc: '数量优势，繁殖能力' },

        // 材料模板
        '材料-custom': { base: '创建自定义材料', desc: '' },
        '材料-herb': { base: '创建灵草药材', desc: '炼丹必备材料' },
        '材料-mineral': { base: '创建矿物材料', desc: '炼器材料' },
        '材料-crystal': { base: '创建晶体材料', desc: '能量结晶，稀有珍宝' },
        '材料-bone': { base: '创建骨骼材料', desc: '兽骨、化石' },

        // 阵法模板
        '阵法-custom': { base: '创建自定义阵法', desc: '' },
        '阵法-attack': { base: '创建攻击阵法', desc: '杀伐果断' },
        '阵法-defense': { base: '创建防御阵法', desc: '固若金汤' },
        '阵法-trap': { base: '创建陷阱阵法', desc: '困敌杀敌' },
        '阵法-teleport': { base: '创建传送阵法', desc: '空间移动' },

        // 秘宝模板
        '秘宝-custom': { base: '创建自定义秘宝', desc: '' },
        '秘宝-weapon': { base: '创建武器类秘宝', desc: '攻防利器' },
        '秘宝-armor': { base: '创建防具类秘宝', desc: '护身宝甲' },
        '秘宝-accessory': { base: '创建饰品类秘宝', desc: '辅助功能' },
        '秘宝-space': { base: '创建空间类秘宝', desc: '储物或洞天' },

        // 传承模板
        '传承-custom': { base: '创建自定义传承', desc: '' },
        '传承-ancient': { base: '创建远古传承', desc: '古老强大' },
        '传承-family': { base: '创建家族传承', desc: '血脉延续' },
        '传承-secret': { base: '创建隐藏传承', desc: '不为人知' },
        '传承-divine': { base: '创建神级传承', desc: '天神遗泽' },

        // 默认
        'custom': { base: '创建一个小说世界设定词条', desc: '' }
      };

      const config = templateConfigs[`${category}-${template}`] || templateConfigs['custom'];

      const systemPrompt = '你是一位专业的小说世界观设定大师，擅长创建玄幻/修仙小说中的各种设定。请根据用户的描述生成详细的词条信息。';

      const userPrompt = `请根据以下描述生成一个完整的词条：

${itemGenPrompt}

模板类型：${config.base}
${config.desc ? `风格特点：${config.desc}` : ''}
词条分类：${category}

请按以下JSON格式输出词条信息（直接输出JSON，不要其他内容）：
{
  "name": "词条名称",
  "category": "${category}",
  "level": "等级/品级（如：圣级、天级、仙级等）",
  "description": "词条详细描述（200字以内）",
  "location": "位置/所在地",
  "features": "主要特征/能力",
  "history": "历史背景/由来",
  "master": "掌控者/所属势力",
  "value": "价值/重要性",
  "firstChapter": "首次出场章节（数字，如：5）",
  "plotPoints": "重要剧情点（用逗号分隔）",
  "foreshadowing": "伏笔/后续计划"
}`;

      const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
      const response = await apiClient.post('/assist/ai/proxy-ai', {
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        model: 'deepseek-v4-flash',
        extra_body: {},
        temperature: 0.8,
        max_tokens: 2000,
        stream: false,
        billingInfo: {
          novelName: currentNovelName,
          chapterNumber: '',
          operationType: 'AI生成词条'
        }
      });

      const aiData = response.data;
      let content = '';

      if (aiData && aiData.choices && aiData.choices[0] && aiData.choices[0].message) {
        content = aiData.choices[0].message.content;
      } else if (aiData && aiData.success && aiData.data) {
        content = aiData.data.content || aiData.data;
      }

      // 尝试解析JSON（🔥 robustJsonParse 容错）
      try {
        const parseResult = robustJsonParse(content);
        if (parseResult.ok && parseResult.data) {
          const parsedData = parseResult.data;
          // 合并到表单数据，保留原有数据并填充AI生成的内容
          setItemFormData(prev => ({
            ...prev,
            name: parsedData.name || prev.name,
            category: parsedData.category || prev.category,
            level: parsedData.level || prev.level,
            description: parsedData.description || prev.description,
            location: parsedData.location || '',
            features: parsedData.features || '',
            history: parsedData.history || '',
            master: parsedData.master || '',
            value: parsedData.value || '',
            firstChapter: parsedData.firstChapter || '',
            plotPoints: parsedData.plotPoints || '',
            foreshadowing: parsedData.foreshadowing || ''
          }));
          setSnackbar({ open: true, message: '词条生成成功，请查看编辑', severity: 'success' });
        } else {
          throw new Error('无法解析生成的词条');
        }
      } catch (parseError) {
        console.error('解析词条数据失败:', parseError);
        setSnackbar({ open: true, message: '生成格式有误，请重试', severity: 'error' });
      }
    } catch (error) {
      console.error('AI生成词条失败:', error);
      setSnackbar({ open: true, message: '生成失败，请重试', severity: 'error' });
    } finally {
      setGeneratingItem(false);
    }
  };

  // 添加/编辑词条
  const handleSaveItem = async (itemData) => {
    try {
      let savedItem;
      
      if (creationType === 'assist' && assistNovelId) {
        // 【辅助创作】保存到数据库
        const response = await apiClient.post(`/assist/novels/${assistNovelId}/items`, {
          ...itemData,
          novelId: assistNovelId
        });
        if (response.data && response.data.success) {
          savedItem = response.data.data;
        }
      }
      
      if (editingItem && editingItem._id) {
        const updatedItems = items.map(i =>
          i._id === editingItem._id ? { ...i, ...itemData, _id: editingItem._id } : i
        );
        setItems(updatedItems);
        setSnackbar({ open: true, message: '词条更新成功', severity: 'success' });
      } else {
        const newItem = {
          ...itemData,
          _id: savedItem?._id || `item_${Date.now()}`,
          createdAt: new Date()
        };
        setItems([...items, savedItem || newItem]);
        setSnackbar({ open: true, message: '词条添加成功', severity: 'success' });
      }
      setEditingItem(null);
      handleClosePanel();
    } catch (error) {
      console.error('保存词条失败:', error);
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    }
  };

  // 添加/编辑备忘录
  const handleSaveMemo = async (memoData) => {
    try {
      let savedMemo;
      
      if (creationType === 'assist' && assistNovelId) {
        // 【辅助创作】保存到数据库
        const response = await apiClient.post(`/assist/novels/${assistNovelId}/memos`, {
          ...memoData,
          novelId: assistNovelId
        });
        if (response.data && response.data.success) {
          savedMemo = response.data.data;
        }
      }
      
      if (editingMemo && editingMemo._id) {
        const updatedMemos = memos.map(m =>
          m._id === editingMemo._id ? { ...m, ...memoData, _id: editingMemo._id } : m
        );
        setMemos(updatedMemos);
        setSnackbar({ open: true, message: '备忘录更新成功', severity: 'success' });
      } else {
        const newMemo = {
          ...memoData,
          _id: savedMemo?._id || `memo_${Date.now()}`,
          createdAt: new Date()
        };
        setMemos([...memos, savedMemo || newMemo]);
        setSnackbar({ open: true, message: '备忘录添加成功', severity: 'success' });
      }
      setEditingMemo(null);
      handleClosePanel();
    } catch (error) {
      console.error('保存备忘录失败:', error);
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    }
  };

  // 删除角色
  const handleDeleteCharacter = async (characterId) => {
    // 【辅助创作】从数据库删除
    if (creationType === 'assist' && assistNovelId) {
      try {
        await apiClient.delete(`/assist/novels/${assistNovelId}/characters/${characterId}`);
      } catch (error) {
        console.error('【辅助创作】删除角色失败:', error);
      }
    }
    setCharacters(characters.filter(c => c._id !== characterId));
    setSnackbar({ open: true, message: '角色已删除', severity: 'success' });
  };

  // 删除词条
  const handleDeleteItem = async (itemId) => {
    // 【辅助创作】从数据库删除
    if (creationType === 'assist' && assistNovelId) {
      try {
        await apiClient.delete(`/assist/novels/${assistNovelId}/items/${itemId}`);
      } catch (error) {
        console.error('【辅助创作】删除词条失败:', error);
      }
    }
    setItems(items.filter(i => i._id !== itemId));
    setSnackbar({ open: true, message: '词条已删除', severity: 'success' });
  };

  // 删除备忘录
  const handleDeleteMemo = async (memoId) => {
    // 【辅助创作】从数据库删除
    if (creationType === 'assist' && assistNovelId) {
      try {
        await apiClient.delete(`/assist/novels/${assistNovelId}/memos/${memoId}`);
      } catch (error) {
        console.error('【辅助创作】删除备忘录失败:', error);
      }
    }
    setMemos(memos.filter(m => m._id !== memoId));
    setSnackbar({ open: true, message: '备忘录已删除', severity: 'success' });
  };

  // 点击其他地方关闭浮动工具栏（pointerdown 兼容 PC 和移动端）
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showFloatingToolbar && !e.target.closest('.floating-toolbar')) {
        setShowFloatingToolbar(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [showFloatingToolbar]);

  // 渲染高亮层：将原文按选区拆分为「普通文本 + 高亮文本」交替片段
  const renderHighlightLayer = (text, range) => {
    if (!range || range.start === range.end) return null;
    const before = text.substring(0, range.start);
    const highlighted = text.substring(range.start, range.end);
    const after = text.substring(range.end);
    return (
      <>
        <span style={{ color: 'transparent' }}>{before}</span>
        <span className="editor-text-highlight">{highlighted}</span>
        <span style={{ color: 'transparent' }}>{after}</span>
      </>
    );
  };

  // 浮动工具栏操作
  const handleFloatingAction = (action) => {
    switch(action) {
      case 'copy':
        navigator.clipboard.writeText(selectedText);
        setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
        break;
      case 'expand':
        // 打开扩写面板，自动填入选中内容
        setAiPrompt(selectedText);
        handleOpenPanel('aiExpand');
        break;
      case 'polish':
        // 打开润色面板，自动填入选中内容
        setAiPrompt(selectedText);
        handleOpenPanel('aiPolish');
        break;
      case 'continueWrite':
        // 打开续写面板，选中内容已在上方显示，续写方向由用户自行填写
        setContinuePrompt('');
        handleOpenPanel('aiContinue');
        break;
      case 'correct':
        // 打开纠错面板，自动填入选中内容
        setAiPrompt(selectedText);
        handleOpenPanel('aiCorrect');
        break;
      case 'denoise':
        // 打开去痕面板，自动填入选中内容
        setAiPrompt(selectedText);
        handleOpenPanel('aiDenoise');
        break;
      default:
        break;
    }
    setShowFloatingToolbar(false);
  };

  /** 审稿：按卷序+章号排序；正文优先编辑器未保存内容，其次章节 content，再次章纲 generatedContent */
  const MAX_AI_REVIEW_INPUT_CHARS = 100000;
  const buildAiReviewPackage = useCallback(() => {
    const sortChaptersByBookOrder = (chList, volList) => {
      const orderMap = {};
      (volList || []).forEach((v, i) => {
        orderMap[String(v._id)] = typeof v.order === 'number' ? v.order : i;
      });
      return [...(chList || [])].sort((a, b) => {
        const oa = orderMap[String(a.volumeId)] ?? 0;
        const ob = orderMap[String(b.volumeId)] ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.chapterNumber || 0) - (b.chapterNumber || 0);
      });
    };
    const getStoredBody = (ch) => {
      if (!ch) return '';
      const c = (ch.content && String(ch.content).trim()) || '';
      const g = (ch.outline?.generatedContent && String(ch.outline.generatedContent).trim()) || '';
      return c || g;
    };
    /** 列表里的 ch 可能滞后于侧栏编辑态：合并当前选中章的 content / outline（含 generatedContent） */
    const mergeChapterSnapshotForReview = (ch) => {
      if (!ch || !selectedChapterForEdit) return ch;
      if (String(selectedChapterForEdit._id) !== String(ch._id)) return ch;
      return {
        ...ch,
        ...(selectedChapterForEdit.content !== undefined && selectedChapterForEdit.content !== null
          ? { content: selectedChapterForEdit.content }
          : {}),
        outline: { ...(ch.outline || {}), ...(selectedChapterForEdit.outline || {}) },
      };
    };
    /**
     * 取章正文：优先未保存的编辑器（须与 current/selected 指向一致，避免串章），
     * 否则用合并后的已保存 content + outline.generatedContent。
     */
    const getBodyForChapter = (ch) => {
      if (!ch) return '';
      const id = String(ch._id);
      const merged = mergeChapterSnapshotForReview(ch);
      const curId = currentChapter ? String(currentChapter._id) : '';
      const selId = selectedChapterForEdit ? String(selectedChapterForEdit._id) : '';
      const selectionAligned = !currentChapter || !selectedChapterForEdit || curId === selId;
      const thisChapterIsFocused = curId === id || selId === id;
      if (selectionAligned && thisChapterIsFocused) {
        const ed = (chapterContent && String(chapterContent).trim()) || '';
        if (ed) return ed;
      }
      return getStoredBody(merged);
    };

    const sorted = sortChaptersByBookOrder(chapters, volumes);
    const cur = currentChapter || selectedChapterForEdit;
    const globalIndex = cur ? sorted.findIndex((c) => String(c._id) === String(cur._id)) : -1;

    const previewRowsFromPicked = (picked, maxShow = 28) => {
      const full = [];
      for (const c of picked || []) {
        if (!getBodyForChapter(c).trim()) continue;
        const ord = sorted.findIndex((x) => String(x._id) === String(c._id));
        full.push({
          bookOrder: ord >= 0 ? ord + 1 : null,
          chapterNumber: c.chapterNumber,
          title: c.title || '未命名',
        });
      }
      return { rows: full.slice(0, maxShow), total: full.length };
    };

    const joinParts = (picked, labelPrefix) => {
      const parts = [];
      // 诊断：打印 picked 章节的 content 字段长度
      if (process.env.NODE_ENV === 'development') {
        picked.forEach((c) => {
          const m = mergeChapterSnapshotForReview(c);
          const storedLen = ((m.content && String(m.content).trim()) || '').length;
          const genLen = ((m.outline?.generatedContent && String(m.outline.generatedContent).trim()) || '').length;
          const bodyLen = getBodyForChapter(c).length;
          console.log(
            `[审稿诊断] 第${c.chapterNumber}章「${c.title}」| 合并后 content=${storedLen} | generatedContent=${genLen} | 最终参与审稿长度=${bodyLen}`,
          );
        });
      }
      picked.forEach((c) => {
        const ord = sorted.findIndex((x) => String(x._id) === String(c._id));
        const t = getBodyForChapter(c);
        if (t) {
          parts.push(
            `【${labelPrefix}${ord >= 0 ? ord + 1 : '?'}章 · 卷内第${c.chapterNumber}章「${c.title || '未命名'}」】\n${t}`,
          );
        }
      });
      const full = parts.join('\n\n---\n\n');
      const truncated = full.length > MAX_AI_REVIEW_INPUT_CHARS;
      const text = truncated ? full.slice(0, MAX_AI_REVIEW_INPUT_CHARS) : full;
      return { text, truncated, chapterCountWithBody: parts.length };
    };

    if (sorted.length === 0) {
      return { text: '', scopeLabel: '', truncated: false, error: '本书暂无章节，无法审稿', reviewPreview: null };
    }

    if (reviewScope === 'current') {
      const ch = cur;
      if (!ch) {
        return { text: '', scopeLabel: '', truncated: false, error: '请先在左侧章节列表选中一章（或打开章节正文），再使用「当前章节」审稿', reviewPreview: null };
      }
      const body = getBodyForChapter(ch);
      if (!body) {
        return { text: '', scopeLabel: '', truncated: false, error: '当前章节暂无正文（请先撰写正文或插入已生成内容）', reviewPreview: null };
      }
      const ord = globalIndex >= 0 ? globalIndex + 1 : '?';
      const single = `【全书第${ord}章 · 卷内第${ch.chapterNumber}章「${ch.title || '未命名'}」】\n${body}`;
      const truncated = single.length > MAX_AI_REVIEW_INPUT_CHARS;
      const text = truncated ? single.slice(0, MAX_AI_REVIEW_INPUT_CHARS) : single;
      const ordNum = globalIndex >= 0 ? globalIndex + 1 : null;
      return {
        text,
        scopeLabel: `当前章节（全书第${ord}章）`,
        truncated,
        error: '',
        reviewPreview: {
          rows: [{ bookOrder: ordNum, chapterNumber: ch.chapterNumber, title: ch.title || '未命名' }],
          total: 1,
        },
      };
    }

    if (reviewScope === 'custom') {
      const from = parseInt(String(reviewCustomFrom).trim(), 10);
      const to = parseInt(String(reviewCustomTo).trim(), 10);
      if (!Number.isFinite(from) || !Number.isFinite(to) || from < 1 || to < 1) {
        return { text: '', scopeLabel: '', truncated: false, error: '自定义范围请输入有效的正整数章节序号', reviewPreview: null };
      }
      const lo = Math.min(from, to);
      const hi = Math.max(from, to);
      if (lo < 1 || hi > sorted.length) {
        return { text: '', scopeLabel: '', truncated: false, error: `章节序号应在 1～${sorted.length} 之间（按全书从前到后的顺序）`, reviewPreview: null };
      }
      const picked = sorted.slice(lo - 1, hi);
      const { text, truncated, chapterCountWithBody } = joinParts(picked, '全书第');
      if (!text.trim()) {
        const chapterCountWithContent = picked.filter((c) => getBodyForChapter(c).trim()).length;
        return {
          text: '',
          scopeLabel: '',
          truncated: false,
          error: chapterCountWithContent === 0
            ? `第${lo}～${hi}章各章均无正文。请先在各章中撰写正文或点击「AI 生成正文」，待正文生成后再来审稿。`
            : `该范围内${chapterCountWithContent}章有正文，但合并后为空，请检查章节内容。`,
          reviewPreview: null,
        };
      }
      return {
        text,
        scopeLabel: `自定义：全书第${lo}～${hi}章（${picked.length}章，含正文${chapterCountWithBody}章）`,
        truncated,
        error: '',
        reviewPreview: previewRowsFromPicked(picked),
      };
    }

    if (reviewScope === 'all') {
      const { text, truncated, chapterCountWithBody } = joinParts(sorted, '全书第');
      if (!text.trim()) {
        const chapterCountWithContent = sorted.filter((c) => getBodyForChapter(c).trim()).length;
        return {
          text: '',
          scopeLabel: '',
          truncated: false,
          error: chapterCountWithContent === 0
            ? `全本${sorted.length}章各章均无正文。请先在各章中撰写正文或点击「AI 生成正文」，待正文生成后再来审稿。`
            : `全本中${chapterCountWithContent}章有正文，但合并后为空，请检查章节内容。`,
          reviewPreview: null,
        };
      }
      return {
        text,
        scopeLabel: `整本小说（共${sorted.length}章，含正文${chapterCountWithBody}章）`,
        truncated,
        error: '',
        reviewPreview: previewRowsFromPicked(sorted),
      };
    }

    const n = parseInt(reviewScope, 10);
    if (!Number.isFinite(n) || n < 1) {
      return { text: '', scopeLabel: '', truncated: false, error: '审稿范围参数无效', reviewPreview: null };
    }
    /** 最近 N 章：按全书顺序从 endIdx 向前数，只计入「有正文」的章节，凑满 N 章为止（跳过中间空章） */
    const endIdx = globalIndex >= 0 ? globalIndex : sorted.length - 1;
    const pickedWithBody = [];
    if (endIdx >= 0) {
      for (let i = endIdx; i >= 0 && pickedWithBody.length < n; i--) {
        const c = sorted[i];
        if (getBodyForChapter(c).trim()) {
          pickedWithBody.push(c);
        }
      }
    }
    pickedWithBody.reverse();
    const picked = pickedWithBody;
    const { text, truncated, chapterCountWithBody } = joinParts(picked, '全书第');
    if (!text.trim()) {
      return {
        text: '',
        scopeLabel: '',
        truncated: false,
        error: `从${globalIndex >= 0 ? '当前章' : '书末'}向前按「有正文的章」回溯，未能凑到任何一章（含已保存正文、章纲内待插入生成稿）。若刚生成未保存，请先保存章纲或点「插入正文」后再试。`,
        reviewPreview: null,
      };
    }
    const shortHint =
      picked.length < n ? `（全书该方向上仅有${picked.length}章有正文，已尽数纳入）` : '';
    const scopeLabel =
      globalIndex >= 0
        ? `最近${n}章${shortHint}（按有正文计，以当前章为起点向前回溯，共${picked.length}章）`
        : `最近${n}章${shortHint}（按有正文计，从书末向前回溯，共${picked.length}章）`;
    return { text, scopeLabel, truncated, error: '', reviewPreview: previewRowsFromPicked(picked) };
  }, [
    chapters,
    volumes,
    currentChapter,
    selectedChapterForEdit,
    chapterContent,
    reviewScope,
    reviewCustomFrom,
    reviewCustomTo,
    selectedChapterForEdit,
  ]);

  // AI 生成处理
  const handleAIGenerate = async () => {
    let reviewPackForRequest = null;
    if (activePanel === 'aiReview') {
      if (reviewDimensions.length === 0) {
        setSnackbar({ open: true, message: '请至少选择一个审稿维度', severity: 'warning' });
        return;
      }
      reviewPackForRequest = buildAiReviewPackage();
      if (reviewPackForRequest.error) {
        setSnackbar({ open: true, message: reviewPackForRequest.error, severity: 'warning' });
        return;
      }
      if (!reviewPackForRequest.text.trim()) {
        setSnackbar({ open: true, message: '所选范围内没有可审稿的正文', severity: 'warning' });
        return;
      }
    } else if (!aiPrompt.trim() && !selectedText && !continuePrompt.trim()) {
      setSnackbar({ open: true, message: '请输入创作内容', severity: 'warning' });
      return;
    }

    // 检查次数限制
    if (!checkAssistQuota('AI生成')) {
      return;
    }

    setGenerating(true);
    setGeneratedContent('');

    try {
      // 构建消息数组
      let systemPrompt = '';
      let userPrompt = '';
      let temperature = 0.7;
      let max_tokens = 4000;

            // 根据面板类型设置不同的 systemPrompt
      switch (activePanel) {
        case 'aiContinue': {
          // 禁止项文字描述
          const banDescriptions = {
            '反派降智': '禁止让反派突然变蠢或说话降智',
            '巧合推剧情': '禁止用"刚好""偶然""碰巧"等巧合来推动剧情',
            '系统提示框': '禁止出现任何"叮""系统提示""任务完成"等游戏/系统提示框',
            '回忆杀': '禁止大段回忆杀，必须保持当前叙事节奏',
            '旁白解说': '禁止大段旁白解说，让剧情通过动作和对话推进'
          };
          const bansText = continueBans.length > 0
            ? '【强制禁止】（以下写法绝对不能出现，一旦出现视为本次续写失败）：\n' + continueBans.map(b => `- ${banDescriptions[b] || b}`).join('\n')
            : '';

          // 情感走向文字描述
          const emotionDescriptions = {
            '热血升级': '突出主角的成长和升级快感，战斗场面要有力量感，每一个小胜利都要有爽感',
            '虐心向': '增加情感冲突，可以是误会、失去、痛苦抉择等，让读者感同身受',
            '轻松日常': '节奏放缓，增加生活化的有趣互动，展现角色之间的温情或搞笑日常',
            '悬疑紧张': '增加悬疑氛围，可以出现谜团、危险逼近的紧张感，让读者心跳加速',
            '浪漫暧昧': '增加感情线的推进，可以是心动、脸红、暧昧的互动等，让读者感到甜蜜',
            '搞笑轻松': '增加幽默元素，可以是搞笑吐槽、反转、意外等，让读者轻松愉快'
          };
          const emotionsText = continueEmotions.length > 0
            ? continueEmotions.map(e => `- ${emotionDescriptions[e] || e}`).join('\n')
            : null;

          // 长度配置
          const lengthConfig = {
            short: { desc: '300-500字，简洁精炼的一段剧情推进', maxTokens: 800 },
            medium: { desc: '500-1000字，完整的剧情段落', maxTokens: 1500 },
            long: { desc: '1000-2000字，丰富的剧情展开', maxTokens: 2500 }
          };
          const lengthInfo = lengthConfig[continueLength] || lengthConfig.medium;

          systemPrompt = `你是一位资深网络小说作家，拥有10年以上创作经验，擅长玄幻、仙侠、都市等各类网文风格。
你的核心能力是"接戏"——在保持原文中主角人设、叙事节奏、情感基调完全一致的前提下，自然流畅地续写后续剧情。
${bansText ? bansText + '\n' : ''}续写核心要求：
1. **风格延续**：语气、句式、用词习惯与原文完全一致，不出现"跳脱感"
2. **剧情推进**：${lengthInfo.desc}，必须包含至少一个明确的剧情推进点（冲突/转折/悬念/高潮）
3. **节奏控制**：续写节奏要与原文匹配，网文节奏偏快，${continueLength === 'short' ? '每100字要有小起伏' : continueLength === 'long' ? '每300字要有明显的情绪高低变化' : '每150字要有小的情绪起伏'}
4. **钩子设置**：${continueHookEnabled ? '续写结尾必须留下下一个悬念或冲突点，吸引读者继续阅读（必须执行）' : '续写结尾可以自然收尾，也可以适当留悬念'}
${emotionsText ? '\n【情感基调】（请在续写中重点体现以下风格）：\n' + emotionsText : ''}
禁止：
- 使用与原文风格明显不同的华丽辞藻或文艺腔
- 在续写开头重复前文的场景或动作描写
- 生成任何角色说话的语气/口癖与原文不一致
- 字数严重偏离要求（偏离20%以上视为不合格）`;
          if (selectedText) {
            userPrompt = `【原文】（请在此基础上续写，保持完全一致的风格）

${selectedText}

---

【续写要求】
${continuePrompt ? `用户补充要求：${continuePrompt}` : '请自然续写后续剧情，无补充要求则按网文标准节奏续写。'}

【情感基调】${emotionsText ? emotionsText.split('\n').map(e => e.replace('- ', '')).join('、') || '无' : '由你根据原文风格自行判断'}
【目标字数】${lengthInfo.desc}
【字数要求】请严格按照目标字数生成，允许±10%误差`;
          } else {
            const recentContent = chapterContent.slice(-800) || '暂无内容';
            userPrompt = `【当前章节末尾】（请在此基础上续写）

${recentContent}

---

【续写方向】
${continuePrompt || '请按正常节奏续写下一段剧情'}
【情感基调】${emotionsText ? emotionsText.split('\n').map(e => e.replace('- ', '')).join('、') || '无' : '由你根据原文风格自行判断'}
【目标字数】${lengthInfo.desc}
【字数要求】请严格按照目标字数生成，允许±10%误差`;
          }
          temperature = 0.75;
          max_tokens = lengthInfo.maxTokens;
          break;
        }
        case 'aiExpand': {
          systemPrompt = `你是一位顶尖的文字编辑和文笔大师，专注于网络小说的内容扩展。
扩写不是简单的"多加几个形容词"，而是在保持原文语义和情感不变的前提下，给读者带来更丰富的阅读体验。
扩写要领：
1. **场景还原**：补充场景的时间、天气、光线、空间布局等环境细节，让读者身临其境
2. **心理挖掘**：为主角或关键角色补充内心独白、情绪波动、决策思考
3. **感官拓展**：增加视觉、听觉、嗅觉、触觉等多维度的感官描写
4. **对话丰化**：将简短的对话扩展为有动作、有神态、有潜台词的完整对话
5. **节奏把控**：扩写时保持与原文一致的节奏，不拖沓、不跳跃
扩写程度参考：
- 轻度（+30%）：每100字原文扩展约30字，补充场景或少量心理
- 中度（+80%）：每100字原文扩展约80字，深度补充心理、感官、对话
- 强力（+150%）：每100字原文扩展约150字，全面重建场景，加入内心独白、伏笔暗示等`;
          const expandRatio = expandLevel === 'light' ? '30%' : expandLevel === 'strong' ? '150%' : '80%';
          userPrompt = `【原文】（请按${expandRatio}的程度进行扩写，保持原文语义和情感完全不变）

${aiPrompt || selectedText}

---

【扩写要求】
${aiDetail ? `用户具体要求：${aiDetail}` : '请按标准扩写方式进行扩展，提升内容丰富度。'}

【扩写程度】${expandLevel === 'light' ? '轻度（+30%）' : expandLevel === 'medium' ? '中度（+80%）' : '强力（+150%）'}

【输出要求】
1. 先输出【扩写后的完整内容】（将原文和扩写内容融合在一起，形成完整通顺的段落）
2. 再输出【扩写说明】（简要说明在哪些地方做了扩展，为什么这样扩）`;
          temperature = 0.82;
          break;
        }
        case 'aiPolish': {
          // 方向文字描述
          const directionDescriptions = {
            '提升爽感': '强化战斗场面、主角装逼打脸、获得宝物等爽点，让读者读起来更痛快',
            '增强画面感': '增加比喻、拟人等修辞，让场景和动作更有画面感',
            '加快节奏': '删除冗余描写，减少连接词，让叙事节奏更紧凑',
            '加深情感': '增加情感描写的细腻度，让读者感同身受',
            '保留原味': '只修改明显的病句和错字，不改变原文风格和表达'
          };
          // 禁止项文字
          const banDescriptions = {
            '不改口吻': '保持每个角色一贯的说话方式和口吻',
            '不改成语': '保留原文使用的成语和熟语，不替换',
            '不改名字': '人物名称、道具名称不改变',
            '不改方言': '保留角色使用的方言、口语表达'
          };
          // 目标读者描述
          const audienceDescriptions = {
            '小白': '直白易懂，爽点密集，修辞适度，节奏快',
            '老白': '文笔要求高，情节有深度，节奏适中，可以有更多复杂表达',
            '精品': '出版级标准，文笔优美，情节精彩，人物立体'
          };
          // 力度描述
          const strengthDescriptions = {
            light: { modifyRatio: '5-15%', desc: '只改句式和用词，不动结构和情节' },
            medium: { modifyRatio: '15-30%', desc: '句式+词汇+节奏全面优化' },
            strong: { modifyRatio: '30-50%', desc: '深度改写，可能调整句式和情节' }
          };
          const dirText = polishDirections.length > 0
            ? polishDirections.map(d => `- ${directionDescriptions[d] || d}`).join('\n')
            : null;
          const banText = polishBans.length > 0
            ? '\n【强制保留】（以下内容不得修改）：\n' + polishBans.map(b => `- ${banDescriptions[b] || b}`).join('\n')
            : '';
          const strInfo = strengthDescriptions[polishStrength] || strengthDescriptions.medium;

          systemPrompt = `你是一位网文界顶尖的文字润色专家，精通网络小说的语言风格。
润色的核心是"提升质感、保留灵魂"——让文字更有感染力，同时完全保留原文的语义、情感和节奏。
润色技法体系：
**句式层面**：
- 长短句交替，避免一整段都是长句或短句
- 减少"的"字句，让句子更干净有力
- 主动句优先，被动句只在必要时使用
- 减少"于是""然后""接着"等连接词，用动作/情绪自然过渡
**词汇层面**：
- 动词替换：把"说""看""走"等弱动词换成更精准的动词
- 减少"非常""特别""很"等程度副词的滥用
- 用具体描写替代笼统形容
**修辞层面**：
- 适当使用比喻、拟人增加画面感（但不要过度）
- 战斗场景要有力量感，都市场景要有代入感，情感场景要有感染力
- 网文风格：爽点要写得爽，虐点要写得痛，不要四平八稳
${banText}
【润色力度】${strInfo.desc}，修改比例控制在${strInfo.modifyRatio}
${dirText ? '\n【本次润色重点方向】：\n' + dirText : ''}
【目标读者】${polishAudience}：${audienceDescriptions[polishAudience] || ''}`;
          userPrompt = `【原文】（请进行润色修改）

${aiPrompt || selectedText}

---

【润色要求】
${aiDetail ? `用户指定方向：${aiDetail}` : '请按标准网文润色流程全面优化。'}
${dirText ? '【润色方向】' + polishDirections.join('、') : ''}
【润色力度】${strInfo.modifyRatio}，保留原文${strInfo.modifyRatio === '5-15%' ? '85%以上' : strInfo.modifyRatio === '15-30%' ? '70%以上' : '50%以上'}

【输出要求】
1. 先输出【润色后的完整内容】
2. 再输出【修改说明】（简要说明做了哪些关键修改，最多列出5条最重要的修改）`;
          temperature = 0.55;
          break;
        }
        case 'aiReview': {
          systemPrompt = `你是一位资深网文编辑，拥有10年以上的网文审稿经验。
审稿不是挑刺，而是"帮作者发现问题+给出具体解决方案"。
审稿维度体系：

【⭐ 紧急检查 - 内容重复检测】（必须首先检查）
⚠️ 检查是否有以下严重重复问题：
1. 本章节内是否有大段落的文字重复（连续50字以上相同）
2. 本章节是否与之前章节有内容重复（特别注意检查章节结尾是否直接复制）
3. 相邻段落或相邻句子是否有句式结构完全相同的情况
4. 同一动作/表情描写是否在短时间内重复出现
→ 一旦发现任何重复问题，必须在对应维度中标记为"严重问题"，并给出0-5分的严重扣分

**结构维度**：
- 章节结构是否完整（开头钩子→发展→高潮→悬念结尾）
- 段落衔接是否流畅，有没有断层或跳跃
- 章节长度是否合理（网文单章建议2000-3000字）
- ⭐ 新增：剧情逻辑连贯性 - 人物决策是否有足够的铺垫（特别是重大决策如信任授予、关系突破）
- ⭐ 新增：章节独立性 - 本章是否对前文有合理承接，不依赖重复阅读前文

**节奏维度**：
- 是否有"水字数"的段落（纯描写无推进）
- 爽点/虐点/悬念点是否足够密集
- 高潮段落是否有足够的情绪铺垫
- ⭐ 新增：心理转变是否有缓冲 - 人物态度突然转变时是否有足够的心理描写支撑

**人物维度**：
- 主角人设是否始终一致（性格、说话方式、能力边界）
- 配角是否有足够的区分度（是否有独特的外貌、语言风格、行为逻辑）
- 人物关系的变化是否有迹可循
- ⭐ 新增：角色动机评估 - 角色行为是否有明确的内心动机（避免"工具人"：只作为剧情推进器而缺乏个人诉求）
- ⭐ 新增：反派深度评估 - 反派是否有超越"傲慢/邪恶"的立体动机（家庭压力、信仰冲突、个人创伤等）
- ⭐ 新增：人物弧光 - 角色在本章是否有成长、转变或内心挣扎

**语言维度**：
- 是否存在重复用词/句式
- 是否有语病、错字、标点错误
- 网文风格是否地道
- ⭐ 新增：文风多样性 - 是否存在形容词堆砌（如"清澈得仿佛能映照出人心底的秘密"等俗套表达）
- ⭐ 新增：描写精准度 - 动作描写是否具体有力（"指尖轻颤"✅）vs 笼统模糊（"他很紧张"❌）

**伏笔维度**：
- 前面埋的伏笔是否有回收
- 是否有明显的逻辑漏洞

【评分标准说明】：
- 发现内容重复（特别是章节重复）属于严重事故，必须扣5-8分
- 人物动机缺失（工具人）扣2-4分
- 反派脸谱化扣1-3分
- 逻辑跳跃（缺乏铺垫）扣2-5分
- 形容词堆砌扣1-2分

【输出格式要求】：
请严格按以下JSON格式输出（不要输出其他文字）：
{
  "overall": "综合评分（1-10分）及简要评价",
  "structure": { "score": 评分, "strengths": ["优点1","优点2"], "issues": ["问题1","问题2"], "suggestions": ["建议1","建议2"] },
  "pace": { "score": 评分, "strengths": ["..."], "issues": ["..."], "suggestions": ["..."] },
  "character": { "score": 评分, "strengths": ["..."], "issues": ["..."], "suggestions": ["..."] },
  "language": { "score": 评分, "strengths": ["..."], "issues": ["..."], "suggestions": ["..."] },
  "key_issues": ["最需要优先修改的3个问题，按优先级排列"],
  "improvement_plan": "修改优先级建议"
}`;
          const pack = reviewPackForRequest || buildAiReviewPackage();
          const reviewScopeText = pack.scopeLabel || '（范围未知）';
          const truncHint = pack.truncated
            ? `\n【重要】以下文本因过长已截取前 ${MAX_AI_REVIEW_INPUT_CHARS} 字参与审稿，结论可能不完整，可缩小范围分次审稿。\n`
            : '';
          userPrompt = `【审稿范围】${reviewScopeText}
【审稿维度】${reviewDimensions.join('、')}
${truncHint}
【分析文本】

${pack.text || '暂无内容'}`;
          temperature = 0.4;
          max_tokens = 32768;  // 审稿需要生成详细JSON报告，最大支持32K
          break;
        }
        case 'aiCorrect': {
          // 严格度配置
          const strictnessConfig = {
            loose: { desc: '只找明显的错别字和病句，轻微问题忽略', extraChecks: '' },
            normal: { desc: '错字+病句+标点+重复用词全面检查', extraChecks: '' },
            strict: { desc: '在标准基础上增加逻辑一致性检查', extraChecks: '\n**逻辑一致性检查**：\n- 同一角色在同一场景的前后描述是否矛盾\n- 时间线是否合理\n- 数字/数量是否前后一致' }
          };
          // 专项检查描述
          const specialCheckDescriptions = {
            '修仙等级词': '检查筑基/金丹/元婴等修仙等级词是否前后一致，是否出现"金丹期打败元婴期"的低级错误',
            '武器法宝名': '检查武器/法宝/丹药等道具名称是否前后一致，是否出现自相矛盾',
            '人物关系一致': '检查角色之间的关系（师徒/父子/恋人等）是否前后一致',
            '地名一致': '检查地名名称和设定是否前后一致'
          };
          // 敏感词检查说明
          const strictnessInfo = strictnessConfig[correctStrictness] || strictnessConfig.normal;
          const specialText = correctSpecialChecks.length > 0
            ? '\n【网文专项检查】（以下检查必须执行）：\n' + correctSpecialChecks.map(s => `- ${specialCheckDescriptions[s] || s}`).join('\n')
            : '';

          systemPrompt = `你是一位资深文字校对专家，专门针对网络小说进行错别字、病句、标点错误、重复用词的系统性检查。
【校对严格度】：${strictnessInfo.desc}
${specialText}
${correctSensitiveCheck ? '\n【强制检查】必须检查文本中是否包含平台敏感词（如：国家名称、政治相关、血腥暴力过度描写等），如有发现请在"敏感词"分类中标注' : ''}
校对标准：
**错别字检查**：
- 常见错误：的地得混用、的/了混用、他/她/它混用
- 谐音错误：再/在、象/像、做/作、以/已等
- 网文特有错字：修仙类"元婴"写成"元阴"等
**病句检查**：
- 成分残缺（缺主语、缺宾语）
- 搭配不当（主谓、动宾、修饰语与中心语）
- 语序不当
- 句式杂糅（两句话揉在一起）
**标点检查**：
- 逗号过多（一逗到底）
- 句号使用不当
- 冒号引号不匹配
- 感叹号滥用（网文常见问题）
**重复检查**：
- 同一段落中连续出现相同词汇
- 相邻句子使用相同句式开头
- 同一动作/表情描写重复使用${strictnessInfo.extraChecks}
【输出格式要求】：
请严格按以下格式输出：
【错别字】（原文→修改为）
1. xxx→xxx
...
【病句】（原文→修改为）
1. xxx→xxx
...
【标点问题】（原文→修改为）
1. xxx→xxx
...
【重复用词】（原文→修改为）
1. xxx→xxx
...
${correctSensitiveCheck ? '\n【敏感词】（如无则注明"无"）\n1. xxx' : ''}
如果某项没有问题，请注明"无"。
全文没有错误时，输出"经检查，未发现明显错误。"`;
          userPrompt = `【待校对文本】

${aiPrompt || selectedText}

【检查重点】
${strictnessInfo.desc}
${correctSpecialChecks.length > 0 ? '\n【专项检查】' + correctSpecialChecks.join('、') : ''}
${correctSensitiveCheck ? '\n【必须检查】敏感词/违禁词' : ''}
请逐字逐句仔细检查，重点关注：
1. 错别字（尤其是网文常见的谐音错字）
2. 病句（成分残缺、搭配不当、语序混乱）
3. 标点错误（逗号过多、句号缺失、引号不匹配）
4. 重复用词（相邻段落重复相同词汇或句式）
${correctSensitiveCheck ? '5. 敏感词/违禁词' : ''}`;
          temperature = 0.2;
          max_tokens = 8192;  // 纠错需要输出详细修正列表
          break;
        }
        case 'aiDenoise': {
          // 构建去痕重点描述
          const focusDescs = {
            '机械连接词': '重点消除"首先"、"其次"、"最后"、"第一"、"第二"、"第三"等机械序列词',
            '形容词堆砌': '重点消除过度华丽的形容词堆砌，用具体细节替代抽象描述',
            '套话模板': '重点消除"值得注意的是"、"事实上"、"从这个角度来看"、"毋庸置疑"等套话',
            '句式对称': '重点打破过于整齐划一的句式，制造长短句交替'
          };
          const selectedFocusDesc = denoiseFocus.length > 0 
            ? denoiseFocus.map(f => focusDescs[f] || f).join('；') 
            : '全面改写，不针对特定类型';

          // 构建目标文风描述
          const styleDescs = {
            natural: '口语自然，像人随口说的，有废话、有情绪、有跳跃',
            literary: '文学流畅，优美但不做作，有节奏感，像老练作者写的',
            novel: '小说张力，有故事感和画面感，对话生动，情绪张力强'
          };
          const targetStyleDesc = styleDescs[denoiseTargetStyle] || styleDescs.natural;

          // 构建强度描述
          const intensityConfig = {
            light: {
              ratio: '保留原文85%以上结构',
              desc: '只替换最机械的词汇，保留大部分原句和段落结构'
            },
            medium: {
              ratio: '保留原文50%左右',
              desc: '深度改写句式和词汇，适度调整段落结构'
            },
            strong: {
              ratio: '只保留核心信息',
              desc: '彻底换人写，追求极致的"人味"，可以大幅调整句式和结构'
            }
          };
          const intensity = intensityConfig[denoiseLevel] || intensityConfig.medium;
          const isStrong = denoiseLevel === 'strong';

          // 【强力模式专属配置 - 整合过AI检测核心】
          const strongModeExtra = isStrong ? `

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
【强力去痕·朱雀检测规避专版】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

你的任务是把AI生成的文本改写成连朱雀检测都认为是100%人类写的。

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎯 【核心目标】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

让文本简单直白，可以写得不完美，偶尔有些小错误最佳。
越简单越好，简单明了地将内容写出来就行。
写成"现场记录感"：短句+碎句+停顿，偶尔用省略号/破折号打断。
用动作/结果代替抽象总结，减少"因此/于是/总之"这类收束语。

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🚫 【AI痕迹库 - 绝对禁止的词汇和描写】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

❌ 严重级AI词汇（必删）：
不禁、不由得、仿佛、似乎、好像、此时此刻、就在这时
心中暗想、心里想道、正如...一样、真正的XXX
让人既...又...、汇聚、笃定、沉淀、不得不说
随着时间的推移、伴随着...、充满了...、各种各样的
指尖、喉结滚动、瞳孔微缩、心跳漏了一拍、深吸一口气

❌ 身体动作（必删）：
指尖轻触、指尖滑过、指尖抚摸、指尖敲击、指尖划过
心跳漏了一拍、心脏猛地一缩、心口一紧
深吸一口气、深深吸了口气、长长地舒了口气
喉结滚动、喉结上下滚动、脊背一僵、后背一凉、脊背发凉
瞳孔微缩、瞳孔一缩、嘴角勾起一抹笑、嘴角上扬弧度

❌ 情绪反应（必删）：
他/她感到、他/她觉得、他/她认为、他/她发现
内心充满、心中涌起、心里一沉、心里一暖

❌ 模板化衔接词（必删）：
然后、接着、随后、忽然、突然、于是、因此、总之
首先、其次、最后、最终、最终结果、总而言之
值得注意的是、事实上、从某种角度来说、不难发现
实际上、本质上、从根本上看、毋庸置疑

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✅ 【真人写作特征 - 强制使用】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

1. ✅ 用动作代替形容：
   - 不写："他很生气"
   - 要写："他把杯子摔在地上"

2. ✅ 对话要口语化、有停顿（但不要过多）：
   - 不写："我认为这个方案存在诸多问题"
   - 要写："这方案...不行，问题太多了"

3. ✅ 长短句交错，制造节奏：
   - 不写：全是10-15字的句子
   - 要写：5字短句 + 20字长句 + 3字极短句混合

4. ✅ 留白，不要解释一切：
   - 不写："他沉默了，因为他不知道该说什么"
   - 要写："他沉默了."

5. ✅ 用细节代替概括：
   - 不写："房间很乱"
   - 要写："桌上堆着三个外卖盒，椅子上搭着两件衬衫"

6. ✅ 人物有口头禅、语言习惯：
   - 某人总说"嗯"、某人喜欢反问、某人说话带语气词

7. ✅ 制造不完美：
   - 对话会被打断、思考会被岔开、计划会出意外、人物会犯错

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔥 【句子结构要求 - 必须严格执行】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

句式随机化：3字短句 + 20字长句 + 8字中等句，不规律混合
允许碎句、单词句出现

❌ 禁止：
- 全是长句或全是短句
- 句子长度高度一致
- 整齐划一的段落结构

✅ 正确节奏示例：
"他跑。推门。冲出去。"
对比：
"他快速地跑向门口，迅速地推开了门，飞快地冲了出去。"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🎨 【真人对话示例 - 必须模仿】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

真实对话不完整：
"你——"
"别说了."
"可是我——"
"我说了，别说."

真实对话有废话：
"那个...嗯...我是说..."
"啊？你说啥？"
"没，没事."

真实对话会跑题：
"我们谈谈项目吧."
"行.对了，你吃饭了吗？"
"吃了.项目的事——"
"吃的啥？"

真实对话有冲突：
"你听我说."
"不听."
"你——"
"就不听."

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
❌ 【禁止开头结尾】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

❌ 禁止开头：
- 时间："清晨"、"早上"、"第二天"
- 天气："阳光洒进"、"阳光透过窗户"
- 醒来："睁开眼睛"、"醒来"、"起床"

❌ 禁止结尾：
- 时间："夜幕降临"、"天色已晚"
- 铺垫："明天"、"新的一天"
- 总结："今天就这样"、"一天结束了"
- 疑问句："它到底在等待什么？"
- 睡觉结尾："睡去"、"入睡"、"进入梦乡"

✅ 正确结尾：
- 陈述句："我关上电脑，离开了训练场"
- 动作："他转身走进了夜色"
- 决定："算了，不想了.去睡觉"

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️ 【质量标准】
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

- 越简单越好，简单明了
- 可以写得不完美，允许小错误
- 口语化表达更佳
- 避免修辞手法（比喻、排比、堆砌形容词）
- 不要文艺腔或空洞华丽的句子
- 复杂想法用简单句解释清楚

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

核心目标：让读者感觉这是一个真人作家写的故事！` : '';

          systemPrompt = `你是一位顶尖的网文写作专家，专门模仿真人写作风格进行"AI去痕"改写。

【检测器规避核心法则】

1. **句子必须长短不一**
   - 严禁所有句子长度相近
   - 必须有超短句（3-8字）和超长句（50+字）混合
   - 用句号、感叹号、问号、省略号交替结尾

2. **词汇必须接地气**
   - 禁用："因此"、"然而"、"值得注意的是"、"综上所述"、"显而易见"、"不难发现"
   - 必须换成："所以"、"可"、"不过"、"话说回来"、"说白了"、"得"
   - 多用口语词："挺"、"贼"、"超"、"老"、"贼啦"、"老鼻子"

3. **段落必须参差不齐**
   - 严禁整齐的三段式或四段式
   - 要有一个字段落，也要有超长段落
   - 随意分段，不刻意追求"总分总"结构

4. **情感必须外露**
   - 加入情绪词："卧槽"、"哈哈"、"唉"、"切"、"啧"、"嘿嘿"、"靠"
   - 加入碎碎念："话说……"、"对了……"、"哎不对……"、"那个……"

5. **叙事必须像说话**
   - 把"他感到非常惊讶"改成"他一愣：卧槽？？"
   - 把"她认为这是个问题"改成"她皱皱眉，觉着这事儿不对"
   - 减少"被"字句，多用主动句和短句

6. **对话要像真人在说话**
   - 用："XX哼了声"、"XX撇撇嘴"、"XX翻白眼"替代规整格式
   - 对话要有语气词和省略："我……我不……"、"那不是……"

7. **适度引入"不完美"**
   - 允许少量"的""地""得"混用（但不过度）
   - 允许逻辑跳跃
   - 允许自言自语式的碎碎念${strongModeExtra}

【本次任务配置】
${'='.repeat(40)}
【去痕重点】${selectedFocusDesc}
【目标文风】${targetStyleDesc}
【改写强度】${intensity.ratio} — ${intensity.desc}
${'='.repeat(40)}

【禁止事项】
1. 不要使用任何形式的列表或编号输出
2. 不要输出任何解释、说明、标注
3. 不要让句子长度趋于平均
4. 不要使用"首先"、"其次"、"最后"等机械连接词

【这不是润色，是换人写！】`;
          
          userPrompt = `【待处理文本】

${aiPrompt || selectedText}

---

【只输出改写后的纯文本，不要任何其他内容】`;
          // 根据强度调整temperature：轻度保守，强力激进
          temperature = denoiseLevel === 'light' ? 0.5 : denoiseLevel === 'strong' ? 0.95 : 0.75;
          break;
        }
        default:
          systemPrompt = '你是一位专业的小说创作助手。';
          userPrompt = aiPrompt;
      }
      
      // 获取操作类型描述用于扣费记录
      const operationLabels = {
        aiContinue: 'AI续写',
        aiExpand: 'AI扩写',
        aiCondense: 'AI缩写',
        aiRewrite: 'AI改写',
        aiDenoise: 'AI去痕',
        aiReview: 'AI审稿'
      };
      const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
      const currentChapterNum = selectedChapterForEdit?.chapterNumber || '';
      
      // 🔥【流式改造 2026-08-27】章节内容生成（续写/扩写/缩写/改写/去痕/审稿）
      //   改为真流式：用户实时看到 AI 逐字输出，不再干等整段生成完毕。
      //   后端逆向通道的 RESPONSE 增量经 onChunk 实时透传 + 前言净化后推给前端。
      const result = await handleStreamGeneration(
        API_BASE_URL,
        {
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          model: 'deepseek-v4-flash',
          temperature,
          max_tokens,
          stream: true,                   // 🔥 真流式：后端 onChunk 实时转发
          billingInfo: {
            novelName: currentNovelName,
            chapterNumber: currentChapterNum,
            operationType: operationLabels[activePanel] || 'AI工具'
          }
        },
        operationLabels[activePanel] || 'AI工具',
        // 实时增量回调：流式净化后的内容实时刷到编辑区，用户逐字看到生成过程
        (chunk, fullContent) => {
          setGeneratedContent(fullContent);
        },
        // 状态更新回调（排队让号/思考中），仅日志，不打断创作界面
        (statusMessage) => {
          console.log(`[${operationLabels[activePanel] || 'AI工具'}] ${statusMessage}`);
        },
        '/assist/ai/proxy-ai'              // 走辅助创作路由（DeepSeek 逆向号池，真流式转发）
      );

      const content = result.content || '';
      if (!content) {
        throw new Error('生成失败：未获取到有效内容');
      }
      // 最终兜底：确保最终内容完整展示（流式已实时更新，此处仅对齐最终结果）
      setGeneratedContent(content);
    } catch (error) {
      console.error('生成失败:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || '生成失败',
        severity: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  // ============ AI 工具面板组件 ============

  // 渲染 AI 续写面板
  const renderAIContinuePanel = () => {
    // 情感走向（无 emoji，与天青章纲区一致）
    const emotionOptions = [
      { label: '热血升级', color: '#f87171' },
      { label: '虐心向', color: '#c4b5fd' },
      { label: '轻松日常', color: '#67e8f9' },
      { label: '悬疑紧张', color: '#fbbf24' },
      { label: '浪漫暧昧', color: '#f9a8d4' },
      { label: '搞笑轻松', color: '#7dd3fc' },
    ];

    const banOptions = [
      { label: '反派降智', color: '#f87171' },
      { label: '巧合推剧情', color: '#fbbf24' },
      { label: '系统提示框', color: '#a78bfa' },
      { label: '回忆杀', color: '#38bdf8' },
      { label: '旁白解说', color: '#94a3b8' },
    ];

    const lengthConfig = {
      short: { label: '短', desc: '300-500字', maxTokens: 800, color: '#67e8f9' },
      medium: { label: '中', desc: '500-1000字', maxTokens: 1500, color: '#fbbf24' },
      long: { label: '长', desc: '1000-2000字', maxTokens: 2500, color: '#fb923c' },
    };

    const toggleEmotion = (label) => {
      setContinueEmotions(prev =>
        prev.includes(label) ? prev.filter(e => e !== label) : [...prev, label]
      );
    };

    const toggleBan = (label) => {
      setContinueBans(prev =>
        prev.includes(label) ? prev.filter(b => b !== label) : [...prev, label]
      );
    };

    return (
      <motion.div
        className="panel-content assist-ai-tool-panel"
        initial={motionReduced ? false : { opacity: 0, y: 12 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${ASSIST_AI_SKY.border}`,
            background: ASSIST_AI_SKY.cardGrad,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <IconButton
            onClick={() => handleOpenPanel('ai')}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.85)', border: `1px solid ${ASSIST_AI_SKY.borderSoft}`, bgcolor: 'rgba(56,189,248,0.08)' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <AutoStoriesIcon sx={{ color: ASSIST_AI_SKY.main, fontSize: 22, filter: 'drop-shadow(0 0 8px rgba(125,211,252,0.35))' }} />
          <Typography sx={{ flex: 1, color: ASSIST_AI_SKY.title, fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>
            AI 续写
          </Typography>
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600 }}>
          续写长度
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 2 }}>
          {Object.entries(lengthConfig).map(([key, cfg]) => (
            <Box
              key={key}
              onClick={() => setContinueLength(key)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                p: 1.5,
                borderRadius: 2,
                border: '1px solid',
                borderColor: continueLength === key ? cfg.color : 'rgba(255,255,255,0.1)',
                bgcolor: continueLength === key ? `${cfg.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.2s',
                '&:hover': { borderColor: cfg.color }
              }}
            >
              <Typography sx={{ color: continueLength === key ? cfg.color : 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: 14 }}>
                {cfg.label}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mt: 0.3 }}>
                {cfg.desc}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 情感走向（核心参数，始终展示） */}
        <Box sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          情感走向 {continueEmotions.length > 0 && <Chip label={`已选${continueEmotions.length}个`} size="small" sx={{ bgcolor: 'rgba(56,189,248,0.25)', color: ASSIST_AI_SKY.title, height: 18, fontSize: 10, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
          {emotionOptions.map(opt => (
            <Chip
              key={opt.label}
              label={opt.label}
              onClick={() => toggleEmotion(opt.label)}
              sx={{
                bgcolor: continueEmotions.includes(opt.label) ? `${opt.color}30` : 'rgba(255,255,255,0.05)',
                color: continueEmotions.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.6)',
                border: '1px solid',
                borderColor: continueEmotions.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                cursor: 'pointer',
                fontSize: 12,
                transition: 'all 0.15s'
              }}
            />
          ))}
        </Box>

        {/* 是否埋钩子开关 */}
        <FormControlLabel
          control={(
            <Switch
              checked={continueHookEnabled}
              onChange={(e) => setContinueHookEnabled(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: ASSIST_AI_SKY.switch },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ASSIST_AI_SKY.switchTrack },
              }}
            />
          )}
          label="续写结尾必须留下悬念（推荐开启）"
          sx={{ color: 'rgba(255,255,255,0.8)', mb: 2 }}
        />

        {selectedText && (
          <>
            <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 500 }}>
              续写以下内容之后：
            </Typography>
            <Box sx={{ p: 2, bgcolor: 'rgba(56,189,248,0.08)', borderRadius: 2, mb: 2, maxHeight: 120, overflow: 'auto', border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }}>
              <Typography sx={{ color: '#fff', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {selectedText}
              </Typography>
            </Box>
          </>
        )}

        {/* 续写要求输入 */}
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, fontSize: 13 }}>
          {selectedText ? '续写补充要求：' : '续写要求（可选）：'}
        </Typography>
        {!selectedText && (
          <Box sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, mb: 1.5, maxHeight: 80, overflow: 'auto' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
              {chapterContent.slice(-500) || '暂无内容'}
            </Typography>
          </Box>
        )}
        <TextareaAutosize
          minRows={2}
          placeholder="主角遭遇埋伏，陷入绝境，必须突破..."
          value={continuePrompt}
          onChange={(e) => setContinuePrompt(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${ASSIST_AI_SKY.inputBorder}`,
            borderRadius: 8,
            padding: 12,
            color: '#fff',
            fontSize: 13,
            outline: 'none',
            resize: 'none'
          }}
        />

        <Box
          onClick={() => setContinueMoreOpen(!continueMoreOpen)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', mt: 2, mb: 1,
            color: 'rgba(255,255,255,0.5)', fontSize: 12,
            '&:hover': { color: ASSIST_AI_SKY.main },
          }}
        >
          <Box sx={{ color: 'inherit', fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            更多选项 {continueBans.length > 0 && <Chip label={`已禁止${continueBans.length}项写法`} size="small" sx={{ bgcolor: 'rgba(56,189,248,0.15)', color: ASSIST_AI_SKY.main, height: 18, fontSize: 10, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }} />}
          </Box>
          <KeyboardArrowRightIcon sx={{ transform: continueMoreOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Box>

        {continueMoreOpen && (
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
            {/* 禁止项 */}
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, fontSize: 11 }}>
              禁止以下写法（AI将主动规避）
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {banOptions.map(opt => (
                <Chip
                  key={opt.label}
                  label={opt.label}
                  onClick={() => toggleBan(opt.label)}
                  sx={{
                    height: 24, fontSize: 11,
                    bgcolor: continueBans.includes(opt.label) ? `${opt.color}30` : 'rgba(255,255,255,0.05)',
                    color: continueBans.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.5)',
                    border: '1px solid',
                    borderColor: continueBans.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                    cursor: 'pointer'
                  }}
                />
              ))}
            </Box>

            {/* 引用前文开关 */}
            <FormControlLabel
              control={(
                <Switch
                  checked={autoLink}
                  onChange={(e) => setAutoLink(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: ASSIST_AI_SKY.switch },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: ASSIST_AI_SKY.switchTrack },
                  }}
                />
              )}
              label="自动引用角色名/道具/地名保持一致"
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}
            />
          </Box>
        )}

        <Button
          variant="outlined"
          onClick={handleAIGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          className="assist-chapter-ai-glow-btn"
          sx={{ py: 1.5, mt: 1, width: '100%' }}
        >
          {generating ? '续写中...' : `开始续写（${lengthConfig[continueLength].desc}）`}
        </Button>

        {generatedContent && (
          <motion.div
            initial={motionReduced ? false : { opacity: 0, y: 8 }}
            animate={motionReduced ? false : { opacity: 1, y: 0 }}
            transition={motionReduced ? { duration: 0 } : { duration: 0.3 }}
          >
          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(8,14,24,0.85)', borderRadius: 2, border: `1px solid ${ASSIST_AI_SKY.border}` }}>
            <Typography sx={{ color: ASSIST_AI_SKY.main, fontWeight: 600, mb: 1 }}>
              续写结果
            </Typography>
            <Typography sx={{ color: '#fff', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 300, overflow: 'auto', lineHeight: 1.8 }}>
              {generatedContent}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleInsertContent}
                sx={{ bgcolor: 'rgba(56,189,248,0.35)', color: '#0f172a', fontWeight: 600, '&:hover': { bgcolor: 'rgba(56,189,248,0.5)' } }}
              >
                插入正文
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleReplaceContent}
                className="workspace-btn-cyber"
              >
                替换原文
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(generatedContent);
                  setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                }}
                sx={{ color: 'rgba(255,255,255,0.6)' }}
              >
                复制
              </Button>
            </Box>
          </Box>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // 渲染 AI 扩写面板
  const renderAIExpandPanel = () => {
    const dimensionOptions = [
      { label: '场景环境', color: '#7dd3fc', Icon: LandscapeIcon, desc: '补充时间/天气/光线/空间' },
      { label: '心理描写', color: '#c4b5fd', Icon: PsychologyIcon, desc: '内心独白/情绪波动/决策' },
      { label: '对话丰化', color: '#93c5fd', Icon: ForumIcon, desc: '动作/神态/潜台词' },
      { label: '感官描写', color: '#fbbf24', Icon: VisibilityIcon, desc: '视觉/听觉/嗅觉/触觉' },
      { label: '动作描写', color: '#f87171', Icon: BoltIcon, desc: '战斗/肢体动作/表情' },
    ];
    const styleOptions = [
      { label: '细腻深入', color: '#67e8f9', Icon: AutoStoriesIcon, desc: '描写细致入微，节奏稍慢' },
      { label: '简洁明快', color: '#fbbf24', Icon: BoltIcon, desc: '节奏快，描写精炼有力' },
      { label: '修辞丰富', color: '#f9a8d4', Icon: AutoAwesomeIcon, desc: '比喻拟人，画面感强' },
    ];
    const lengthConfig = {
      light: { label: '轻度', ratio: '+30%', desc: '每100字扩30字', maxTokens: 800, color: '#67e8f9' },
      medium: { label: '中度', ratio: '+80%', desc: '每100字扩80字', maxTokens: 1500, color: '#fbbf24' },
      strong: { label: '强力', ratio: '+150%', desc: '每100字扩150字', maxTokens: 2500, color: '#fb923c' },
    };

    const toggleDim = (label) => {
      setExpandDimensions(prev =>
        prev.includes(label) ? prev.filter(d => d !== label) : [...prev, label]
      );
    };

    // 与 selectedText 合并为单一编辑区：未改 aiPrompt 时显示选中文本，编辑后写入 aiPrompt
    const expandBodyText = aiPrompt !== '' || !selectedText ? aiPrompt : selectedText;

    return (
      <motion.div
        className="panel-content assist-ai-tool-panel"
        initial={motionReduced ? false : { opacity: 0, y: 12 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid rgba(251,191,36,0.35)`,
            background: 'linear-gradient(145deg, rgba(251,191,36,0.1) 0%, rgba(8,14,24,0.94) 55%, rgba(10,14,22,0.9) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <IconButton onClick={() => handleOpenPanel('ai')} size="small" sx={{ color: '#fff', border: '1px solid rgba(251,191,36,0.35)', bgcolor: 'rgba(251,191,36,0.08)' }}>
            <ArrowBackIcon />
          </IconButton>
          <ExpandMoreIcon sx={{ color: '#fbbf24', fontSize: 22 }} />
          <Typography sx={{ flex: 1, color: '#fef3c7', fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>AI 扩写</Typography>
        </Box>

        <Box sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          扩写维度
          {expandDimensions.length > 0 && (
            <Chip label={`${expandDimensions.length}个`} size="small" sx={{ bgcolor: 'rgba(251,191,36,0.2)', color: '#fde68a', height: 20, fontSize: 10, border: '1px solid rgba(251,191,36,0.35)' }} />
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, gap: { xs: 0.5, sm: 0.6 }, mb: 1.25 }}>
          {dimensionOptions.map(opt => (
            <Box
              key={opt.label}
              onClick={() => toggleDim(opt.label)}
              sx={{
                py: 0.35,
                px: 0.65,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: expandDimensions.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: expandDimensions.includes(opt.label) ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: { xs: 'center', sm: 'center' },
                textAlign: { xs: 'left', sm: 'center' },
                gap: { xs: 0.75, sm: 0 },
                minWidth: 0,
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color }
              }}
            >
              <opt.Icon sx={{ fontSize: { xs: 20, sm: 17 }, flexShrink: 0, lineHeight: 1, mb: { xs: 0, sm: 0.15 }, color: expandDimensions.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.32)' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: expandDimensions.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.7)', fontSize: { xs: 12, sm: 10 }, fontWeight: 600, lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: { xs: 10, sm: 8 }, mt: { xs: 0.15, sm: 0.2 }, lineHeight: 1.25 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, fontWeight: 600 }}>
          扩写风格
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0.75 }, mb: 1.25 }}>
          {styleOptions.map(opt => (
            <Box
              key={opt.label}
              onClick={() => setExpandStyle(opt.label)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 0.4,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: expandStyle === opt.label ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: expandStyle === opt.label ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color }
              }}
            >
              <opt.Icon sx={{ fontSize: 18, color: expandStyle === opt.label ? opt.color : 'rgba(255,255,255,0.45)' }} />
              <Typography sx={{ color: expandStyle === opt.label ? opt.color : 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600, lineHeight: 1.2 }}>
                {opt.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 扩写正文（预览与编辑合一） */}
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.5, fontSize: 12 }}>
          扩写正文（{expandBodyText.length}字）
        </Typography>
        <TextareaAutosize
          minRows={4}
          maxRows={16}
          placeholder="选中文本后自动带入，也可直接在此输入要扩写的段落..."
          value={expandBodyText}
          onChange={(e) => setAiPrompt(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(251,191,36,0.35)',
            borderRadius: 8,
            padding: 8,
            color: '#fff',
            fontSize: 13,
            lineHeight: 1.5,
            outline: 'none',
            marginBottom: 8
          }}
        />

        {/* 扩写要求输入 */}
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, mt: 2, fontSize: 13 }}>扩写要求（可选）：</Typography>
        <TextareaAutosize
          minRows={2}
          placeholder="如：让场景更加紧张刺激..."
          value={aiDetail}
          onChange={(e) => setAiDetail(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            padding: 10,
            color: '#fff',
            fontSize: 13,
            outline: 'none',
            resize: 'none'
          }}
        />

        {/* 扩写程度（核心参数） */}
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', mb: 1, mt: 2, fontSize: 13 }}>
          扩写程度 {expandLevel && <Chip label={lengthConfig[expandLevel]?.ratio} size="small" sx={{ bgcolor: `${lengthConfig[expandLevel]?.color}22`, color: lengthConfig[expandLevel]?.color, height: 18, fontSize: 10, ml: 1 }} />}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.45, sm: 0.6 }, mb: 1.25 }}>
          {Object.entries(lengthConfig).map(([key, cfg]) => (
            <Box
              key={key}
              onClick={() => setExpandLevel(key)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.6,
                py: 0.45,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: expandLevel === key ? cfg.color : 'rgba(255,255,255,0.1)',
                bgcolor: expandLevel === key ? `${cfg.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: cfg.color }
              }}
            >
              <Typography component="span" sx={{ color: expandLevel === key ? cfg.color : 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
                {cfg.label}
              </Typography>
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 500, lineHeight: 1 }}>
                {cfg.ratio}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* 更多选项折叠 */}
        <Box
          onClick={() => setExpandMoreOpen(!expandMoreOpen)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', mb: 1,
            color: 'rgba(255,255,255,0.5)', fontSize: 12,
            '&:hover': { color: '#fbbf24' }
          }}
        >
          <Typography sx={{ color: 'inherit', fontSize: 12 }}>更多选项</Typography>
          <KeyboardArrowRightIcon sx={{ transform: expandMoreOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Box>

        {expandMoreOpen && (
          <motion.div
            initial={motionReduced ? false : { opacity: 0, height: 0 }}
            animate={motionReduced ? false : { opacity: 1, height: 'auto' }}
            transition={motionReduced ? { duration: 0 } : { duration: 0.25 }}
          >
          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(8,14,24,0.75)', borderRadius: 2, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }}>
            <FormControlLabel
              control={(
                <Switch checked={expandAddShuang} onChange={(e) => setExpandAddShuang(e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fbbf24' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(251,191,36,0.45)' } }} />
              )}
              label="注入爽点（在扩写中自然加入小高潮）"
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}
            />
            <FormControlLabel
              control={(
                <Switch checked={expandAddFuBi} onChange={(e) => setExpandAddFuBi(e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fbbf24' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(251,191,36,0.45)' } }} />
              )}
              label="埋设伏笔（在扩写结尾自然埋下一个小伏笔）"
              sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}
            />
          </Box>
          </motion.div>
        )}

        <Button
          variant="outlined"
          onClick={handleAIGenerate}
          disabled={generating || !(aiPrompt || selectedText)}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          className="assist-chapter-ai-glow-btn"
          size="medium"
          sx={{ py: 1, mt: 0.5, width: '100%', borderColor: 'rgba(251,191,36,0.5) !important', color: '#fef3c7 !important' }}
        >
          {generating ? '扩写中...' : `开始扩写（${expandDimensions.length > 0 ? expandDimensions.join('/') : '全部维度'}）`}
        </Button>

        {generatedContent && (
          <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(8,14,24,0.85)', borderRadius: 2, border: '1px solid rgba(251,191,36,0.35)' }}>
            <Typography sx={{ color: '#fde68a', fontWeight: 600, mb: 1 }}>
              扩写结果
            </Typography>
            <Typography sx={{ color: '#fff', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 300, overflow: 'auto', lineHeight: 1.8 }}>
              {generatedContent}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleReplaceContent}
                sx={{ bgcolor: 'rgba(251,191,36,0.85)', color: '#0f172a', fontWeight: 600, '&:hover': { bgcolor: '#fbbf24' } }}
              >
                替换原文
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleInsertContent}
                className="workspace-btn-cyber"
              >
                追加到末尾
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(generatedContent);
                  setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                }}
                sx={{ color: 'rgba(255,255,255,0.6)' }}
              >
                复制
              </Button>
            </Box>
          </Box>
        )}
      </motion.div>
    );
  };

  // 渲染 AI 润色面板
  const renderAIPolishPanel = () => {
    const directionOptions = [
      { label: '提升爽感', color: '#f87171', Icon: LocalFireDepartmentIcon, desc: '让爽点更爽，战斗更燃' },
      { label: '增强画面感', color: '#fbbf24', Icon: MovieCreationIcon, desc: '修辞增加画面感' },
      { label: '加快节奏', color: '#60a5fa', Icon: SpeedIcon, desc: '减少废话，节奏更紧凑' },
      { label: '加深情感', color: '#f472b6', Icon: FavoriteIcon, desc: '情感描写更细腻动人' },
      { label: '保留原味', color: '#7dd3fc', Icon: BalanceIcon, desc: '只改病句，不改风格' },
    ];
    const banOptions = [
      { label: '不改口吻', color: '#f87171', desc: '保留角色说话风格' },
      { label: '不改成语', color: '#fbbf24', desc: '保留原有成语熟语' },
      { label: '不改名字', color: '#c4b5fd', desc: '角色名不变' },
      { label: '不改方言', color: '#38bdf8', desc: '保留方言/口语' },
    ];
    const audienceOptions = [
      { label: '小白文', color: '#93c5fd', Icon: ImportContactsIcon, desc: '直白易懂，爽点密集' },
      { label: '老白文', color: '#fbbf24', Icon: MenuBookIcon, desc: '文笔要求高，有深度' },
      { label: '精品文', color: '#fde68a', Icon: EmojiEventsIcon, desc: '出版级标准' },
    ];
    const strengthConfig = {
      light: { label: '轻度', color: '#60a5fa', desc: '只改句式和用词，不动结构', modifyRatio: '5-15%' },
      medium: { label: '中度', color: '#f59e0b', desc: '句式+词汇+节奏全面优化', modifyRatio: '15-30%' },
      strong: { label: '强力', color: '#ef4444', desc: '深度改写，可能调整结构', modifyRatio: '30-50%' },
    };

    const toggleDirection = (label) => {
      setPolishDirections(prev =>
        prev.includes(label) ? prev.filter(d => d !== label) : [...prev, label]
      );
    };
    const toggleBan = (label) => {
      setPolishBans(prev =>
        prev.includes(label) ? prev.filter(b => b !== label) : [...prev, label]
      );
    };

    const polishBodyText = aiPrompt !== '' || !selectedText ? aiPrompt : selectedText;

    return (
      <motion.div
        className="panel-content assist-ai-tool-panel"
        initial={motionReduced ? false : { opacity: 0, y: 12 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${ASSIST_AI_SKY.border}`,
            background: ASSIST_AI_SKY.cardGrad,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <IconButton onClick={() => handleOpenPanel('ai')} size="small" sx={{ color: 'rgba(255,255,255,0.85)', border: `1px solid ${ASSIST_AI_SKY.borderSoft}`, bgcolor: 'rgba(56,189,248,0.08)' }}>
            <ArrowBackIcon />
          </IconButton>
          <AutoFixHighIcon sx={{ color: ASSIST_AI_SKY.main, fontSize: 22, filter: 'drop-shadow(0 0 8px rgba(125,211,252,0.35))' }} />
          <Typography sx={{ flex: 1, color: ASSIST_AI_SKY.title, fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>AI 润色</Typography>
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, fontWeight: 600 }}>
          润色力度
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.45, sm: 0.6 }, mb: 1.25 }}>
          {Object.entries(strengthConfig).map(([key, cfg]) => (
            <Box
              key={key}
              onClick={() => setPolishStrength(key)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                py: 0.45,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: polishStrength === key ? cfg.color : 'rgba(255,255,255,0.1)',
                bgcolor: polishStrength === key ? `${cfg.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: cfg.color },
              }}
            >
              <Typography component="span" sx={{ color: polishStrength === key ? cfg.color : 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>
                {cfg.label}
              </Typography>
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, lineHeight: 1.2 }}>
                {cfg.desc}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          润色方向
          {polishDirections.length > 0 && (
            <Chip label={`${polishDirections.length}个`} size="small" sx={{ bgcolor: 'rgba(56,189,248,0.22)', color: ASSIST_AI_SKY.title, height: 20, fontSize: 10, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }} />
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: { xs: 0.5, sm: 0.6 }, mb: 1.5 }}>
          {directionOptions.map(opt => (
            <Box
              key={opt.label}
              onClick={() => toggleDirection(opt.label)}
              sx={{
                py: 0.5,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: polishDirections.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: polishDirections.includes(opt.label) ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 0.75,
                minWidth: 0,
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color },
              }}
            >
              <opt.Icon sx={{ fontSize: 20, flexShrink: 0, mt: 0.1, color: polishDirections.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.35)' }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: polishDirections.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 700, lineHeight: 1.25 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, mt: 0.2, lineHeight: 1.35 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600 }}>
          目标读者
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0.75 }, mb: 1.25 }}>
          {audienceOptions.map(opt => (
            <Box
              key={opt.label}
              onClick={() => setPolishAudience(opt.label)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 0.4,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: polishAudience === opt.label ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: polishAudience === opt.label ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color },
              }}
            >
              <opt.Icon sx={{ fontSize: 18, color: polishAudience === opt.label ? opt.color : 'rgba(255,255,255,0.4)' }} />
              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                <Typography sx={{ color: polishAudience === opt.label ? opt.color : 'rgba(255,255,255,0.65)', fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, lineHeight: 1.25 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12 }}>
          润色正文（{polishBodyText.length}字）
        </Typography>
        <TextareaAutosize
          minRows={4}
          maxRows={16}
          placeholder="选中文本后自动带入，也可直接在此输入要润色的段落..."
          value={polishBodyText}
          onChange={(e) => setAiPrompt(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(8,14,24,0.75)',
            border: `1px solid ${ASSIST_AI_SKY.inputBorder}`,
            borderRadius: 8,
            padding: 8,
            color: '#fff',
            fontSize: 13,
            lineHeight: 1.5,
            outline: 'none',
            marginBottom: 8,
          }}
        />

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, mt: 2, fontSize: 13 }}>润色要求（可选）：</Typography>
        <TextareaAutosize
          minRows={2}
          placeholder="如：让打斗场面更有力量感..."
          value={aiDetail}
          onChange={(e) => setAiDetail(e.target.value)}
          style={{
            width: '100%',
            background: 'rgba(8,14,24,0.75)',
            border: `1px solid ${ASSIST_AI_SKY.borderSoft}`,
            borderRadius: 8,
            padding: 10,
            color: '#fff',
            fontSize: 13,
            outline: 'none',
            resize: 'none',
          }}
        />

        <Box
          onClick={() => setPolishMoreOpen(!polishMoreOpen)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', mt: 2, mb: 1,
            color: 'rgba(255,255,255,0.5)', fontSize: 12,
            '&:hover': { color: ASSIST_AI_SKY.main },
          }}
        >
          <Box sx={{ color: 'inherit', fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            更多选项
            {polishBans.length > 0 && (
              <Chip label={`已锁定${polishBans.length}项`} size="small" sx={{ bgcolor: 'rgba(56,189,248,0.15)', color: ASSIST_AI_SKY.main, height: 18, fontSize: 10, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }} />
            )}
          </Box>
          <KeyboardArrowRightIcon sx={{ transform: polishMoreOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Box>

        <AnimatePresence initial={false}>
          {polishMoreOpen && (
            <motion.div
              initial={motionReduced ? false : { opacity: 0, height: 0 }}
              animate={motionReduced ? false : { opacity: 1, height: 'auto' }}
              exit={motionReduced ? false : { opacity: 0, height: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.25 }}
              style={{ overflow: 'hidden' }}
            >
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(8,14,24,0.75)', borderRadius: 2, border: `1px solid ${ASSIST_AI_SKY.borderSoft}` }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', mb: 1, fontSize: 11 }}>
                  禁止修改以下内容（AI将确保这些部分不变）
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                  {banOptions.map(opt => (
                    <Chip
                      key={opt.label}
                      label={opt.label}
                      onClick={() => toggleBan(opt.label)}
                      sx={{
                        height: 24, fontSize: 11,
                        bgcolor: polishBans.includes(opt.label) ? `${opt.color}28` : 'rgba(255,255,255,0.05)',
                        color: polishBans.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.5)',
                        border: '1px solid',
                        borderColor: polishBans.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                  修改比例参考：{strengthConfig[polishStrength]?.modifyRatio || '15-30%'}
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          variant="outlined"
          onClick={handleAIGenerate}
          disabled={generating || !(aiPrompt || selectedText)}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          className="assist-chapter-ai-glow-btn"
          size="medium"
          sx={{ py: 1, mt: 0.5, width: '100%', borderColor: `${ASSIST_AI_SKY.border} !important`, color: `${ASSIST_AI_SKY.title} !important` }}
        >
          {generating ? '润色中...' : `开始润色（${strengthConfig[polishStrength]?.label}）`}
        </Button>

        {generatedContent && (
          <motion.div
            initial={motionReduced ? false : { opacity: 0, y: 8 }}
            animate={motionReduced ? false : { opacity: 1, y: 0 }}
            transition={motionReduced ? { duration: 0 } : { duration: 0.3 }}
          >
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(8,14,24,0.85)', borderRadius: 2, border: `1px solid ${ASSIST_AI_SKY.border}` }}>
              <Typography sx={{ color: ASSIST_AI_SKY.main, fontWeight: 600, mb: 1 }}>
                润色结果
              </Typography>
              <Typography sx={{ color: '#fff', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 300, overflow: 'auto', lineHeight: 1.8 }}>
                {generatedContent}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleReplaceContent}
                  sx={{ bgcolor: 'rgba(56,189,248,0.88)', color: '#0f172a', fontWeight: 600, '&:hover': { bgcolor: '#38bdf8' } }}
                >
                  替换原文
                </Button>
                <Button variant="outlined" size="small" onClick={handleInsertContent} className="workspace-btn-cyber">
                  追加到末尾
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedContent);
                    setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                  }}
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  复制
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // 渲染 AI 审稿面板
  // ── AI 审稿 JSON 解析 ───────────────────────────────────────────────────────
  // 从 AI 返回的文本中提取 JSON（支持多种格式）
  const parseReviewJson = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    try {
      // 方法1: 尝试直接解析
      try {
        return JSON.parse(raw);
      } catch { /* ignore */ }

      // 方法2: 找代码块
      const codeBlock = raw.match(/```(?:json)?\s*([\s\S]*?)```/);
      if (codeBlock) {
        const jsonStr = codeBlock[1].trim();
        try {
          return JSON.parse(jsonStr);
        } catch { /* ignore */ }
      }

      // 方法3: 找 JSON 对象（从第一个 { 到最后一个 }）
      const firstBrace = raw.indexOf('{');
      const lastBrace = raw.lastIndexOf('}');
      if (firstBrace >= 0 && lastBrace > firstBrace) {
        const jsonStr = raw.slice(firstBrace, lastBrace + 1);
        try {
          return JSON.parse(jsonStr);
        } catch { /* ignore */ }
      }

      // 方法4: 🔥 robustJsonParse 多级容错（平衡括号 + 尾逗号 + 截断闭合 + 未转义换行 + 逐对象兜底）
      const rjpResult = robustJsonParse(raw);
      if (rjpResult.ok && rjpResult.data) {
        return rjpResult.data;
      }

      return null;
    } catch {
      return null;
    }
  };

  /** 将「修改计划」长段拆成多条展示（优先按「第N优先级」、序号、换行） */
  const formatImprovementPlanBlocks = (raw) => {
    if (raw == null) return [];
    if (Array.isArray(raw)) {
      return raw.map((x) => String(x).trim()).filter(Boolean);
    }
    let t = String(raw).replace(/\r\n/g, '\n').trim();
    if (!t) return [];
    const byPriority = t.split(/(?=第[一二三四五六七八九十百千万零〇0-9]+优先级)/).map((s) => s.trim()).filter(Boolean);
    if (byPriority.length > 1) return byPriority;
    const byNum = t
      .split(/(?=(?:^|\n)\s*(?:\d+|[（(]\d+[）)])[、.．:：]\s*)/m)
      .map((s) => s.trim())
      .filter(Boolean);
    if (byNum.length > 1) return byNum;
    const bySemicolon = t.split(/[；;]\s*/).map((s) => s.trim()).filter(Boolean);
    if (bySemicolon.length > 1) return bySemicolon;
    const bySentence = t.split(/(?<=[。！？])\s+/).map((s) => s.trim()).filter(Boolean);
    if (bySentence.length > 1) return bySentence;
    return [t];
  };

  /** 与左侧章节列表、全局进度对齐的天青主色（避免薄荷青 #22d3ee 单独一套） */
  const CH_REVIEW_SKY = {
    main: '#38bdf8',
    text: '#e0f2fe',
    muted: '#7dd3fc',
    border: 'rgba(125, 211, 252, 0.42)',
    borderSoft: 'rgba(125, 211, 252, 0.2)',
    borderFaint: 'rgba(125, 211, 252, 0.12)',
    bgTint: 'rgba(56, 189, 248, 0.1)',
    bgTintCard: 'rgba(56, 189, 248, 0.08)',
    glow: 'rgba(56, 189, 248, 0.35)',
  };

  // 各维度的中文映射（维度 key → 展示名 + 颜色）
  const DIM_META = {
    structure:  { label: '结构', color: '#7dd3fc', barBg: 'rgba(125,211,252,0.12)', barColor: '#38bdf8' },
    pace:       { label: '节奏', color: '#fbbf24', barBg: 'rgba(251,191,36,0.12)', barColor: '#f59e0b' },
    character:  { label: '人物', color: '#c4b5fd', barBg: 'rgba(196,181,253,0.12)', barColor: '#a78bfa' },
    language:   { label: '语言', color: '#93c5fd', barBg: 'rgba(147,197,253,0.12)', barColor: '#60a5fa' },
  };
  // AI 可能返回中文维度 key（兼容旧格式）
  const DIM_ALIAS = { 结构: 'structure', 节奏: 'pace', 人物: 'character', 语言: 'language' };
  const resolveDim = (k) => DIM_ALIAS[k] || k;

  /** 单维度：整行通栏，每条意见单独一行，便于长文阅读 */
  const renderDimSection = (dimKey, data) => {
    const dim = DIM_META[dimKey];
    if (!dim) return null;
    const score = typeof data?.score === 'number' ? data.score : null;
    const rowSx = {
      display: 'flex',
      gap: 0.75,
      alignItems: 'flex-start',
      py: 0.5,
      borderBottom: '1px solid rgba(255,255,255,0.06)',
    };
    return (
      <Box
        key={dimKey}
        className="ai-review-card-breathe"
        sx={{
          bgcolor: 'rgba(10,12,20,0.75)',
          border: `1px solid ${CH_REVIEW_SKY.borderSoft}`,
          borderRadius: 2,
          p: 1.5,
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ color: dim.color, fontSize: 13, fontWeight: 700, letterSpacing: '0.04em' }}>
            {dim.label}
          </Typography>
          {score !== null && (
            <Typography
              className="ai-review-score-glow"
              sx={{ color: dim.color, fontSize: 20, fontWeight: 800, lineHeight: 1 }}
            >
              {score}
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontWeight: 400 }}>
                /10
              </Typography>
            </Typography>
          )}
        </Box>
        {score !== null && (
          <LinearProgress
            className="ai-review-dim-bar"
            variant="determinate"
            value={score * 10}
            sx={{
              height: 5,
              borderRadius: 2,
              bgcolor: dim.barBg,
              '& .MuiLinearProgress-bar': { bgcolor: dim.barColor },
            }}
          />
        )}
        {data?.strengths?.length > 0 && (
          <Box>
            <Typography sx={{ color: 'rgba(125,211,252,0.75)', fontSize: 10, fontWeight: 600, mb: 0.5, letterSpacing: '0.06em' }}>
              优点
            </Typography>
            {data.strengths.map((s, i) => (
              <Box key={i} sx={{ ...rowSx, borderBottom: i < data.strengths.length - 1 ? rowSx.borderBottom : 'none' }}>
                <Typography component="span" sx={{ color: '#38bdf8', flexShrink: 0, fontSize: 13, lineHeight: 1.65, mt: 0.1 }}>▸</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 12, lineHeight: 1.65, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {s}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {data?.issues?.length > 0 && (
          <Box>
            <Typography sx={{ color: 'rgba(252,165,165,0.8)', fontSize: 10, fontWeight: 600, mb: 0.5, letterSpacing: '0.06em' }}>
              问题
            </Typography>
            {data.issues.map((s, i) => (
              <Box key={i} sx={{ ...rowSx, borderBottom: i < data.issues.length - 1 ? rowSx.borderBottom : 'none' }}>
                <Typography component="span" sx={{ color: '#f87171', flexShrink: 0, fontSize: 13, lineHeight: 1.65, mt: 0.1 }}>✗</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.82)', fontSize: 12, lineHeight: 1.65, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {s}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {data?.suggestions?.length > 0 && (
          <Box>
            <Typography sx={{ color: 'rgba(251,191,36,0.85)', fontSize: 10, fontWeight: 600, mb: 0.5, letterSpacing: '0.06em' }}>
              建议
            </Typography>
            {data.suggestions.map((s, i) => (
              <Box key={i} sx={{ ...rowSx, borderBottom: i < data.suggestions.length - 1 ? rowSx.borderBottom : 'none' }}>
                <Typography component="span" sx={{ color: '#fbbf24', flexShrink: 0, fontSize: 13, lineHeight: 1.65, mt: 0.1 }}>→</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, lineHeight: 1.65, flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                  {s}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // 渲染解析后的结构化报告
  const renderStructuredReport = (data) => {
    const { overall, structure, pace, character, language, key_issues, improvement_plan } = data;

    // 从 overall 提取数字评分
    const overallScore = String(overall).match(/(\d+(?:\.\d+)?)/);
    const overallNum = overallScore ? parseFloat(overallScore[1]) : null;
    const overallColor =
      overallNum === null ? '#94a3b8'
      : overallNum >= 8 ? '#38bdf8'
      : overallNum >= 6 ? '#fbbf24'
      : '#f87171';

    // 合并中文/英文维度 key
    const dimEntries = [
      ['structure', structure],
      ['pace', pace],
      ['character', character],
      ['language', language],
    ];

    return (
      <Box>
        {/* 综合评分 */}
        <Box
          className="ai-review-card-breathe"
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            border: `1px solid ${CH_REVIEW_SKY.border}`,
            bgcolor: 'rgba(10,12,20,0.8)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mb: 0.3 }}>综合评分</Typography>
            {overallNum !== null && (
              <Typography
                className="ai-review-score-glow"
                sx={{ color: overallColor, fontSize: 42, fontWeight: 900, lineHeight: 1 }}
              >
                {overallNum}
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 16, fontWeight: 400 }}>
                  /10
                </Typography>
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12, fontWeight: 600, mb: 0.5 }}>综合评价</Typography>
            <TextareaAutosize
              readOnly
              value={String(overall ?? '')}
              minRows={2}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: 'transparent',
                border: 'none',
                color: 'rgba(255,255,255,0.58)',
                fontSize: 12,
                lineHeight: 1.65,
                fontFamily: 'inherit',
                resize: 'none',
                outline: 'none',
                padding: 0,
                overflow: 'hidden',
              }}
            />
          </Box>
        </Box>

        {/* 各维度：纵向通栏，一行一项 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {dimEntries.map(([k, v]) => v ? renderDimSection(k, v) : null)}
        </Box>

        {/* 优先修改问题 + 修改计划（纵向堆叠，通栏） */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'stretch' }}>
          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Typography sx={{ color: CH_REVIEW_SKY.muted, fontSize: 11, fontWeight: 700, mb: 0.75, letterSpacing: '0.06em' }}>
              ⚡ 优先修改问题
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
              {(key_issues || []).slice(0, 5).map((issue, i) => (
                <Box
                  key={i}
                  className="ai-review-issue-card"
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(239,68,68,0.08)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    display: 'flex',
                    gap: 0.75,
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography sx={{ color: '#f87171', fontSize: 12, fontWeight: 800, flexShrink: 0, mt: -0.1 }}>
                    {i + 1}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, lineHeight: 1.5 }}>
                    {issue}
                  </Typography>
                </Box>
              ))}
              {(!key_issues || key_issues.length === 0) && (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, fontStyle: 'italic' }}>
                  暂无优先问题记录
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ width: '100%', minWidth: 0 }}>
            <Typography sx={{ color: CH_REVIEW_SKY.muted, fontSize: 11, fontWeight: 700, mb: 0.75, letterSpacing: '0.06em' }}>
              📋 修改计划
            </Typography>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: CH_REVIEW_SKY.bgTintCard,
                border: `1px solid ${CH_REVIEW_SKY.borderSoft}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              {(() => {
                const blocks = formatImprovementPlanBlocks(improvement_plan);
                if (blocks.length === 0) {
                  return (
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, fontStyle: 'italic' }}>
                      暂无修改计划
                    </Typography>
                  );
                }
                return blocks.map((para, i) => (
                  <Typography
                    key={i}
                    component="div"
                    sx={{
                      color: 'rgba(255,255,255,0.78)',
                      fontSize: 12,
                      lineHeight: 1.75,
                      wordBreak: 'break-word',
                      pl: 0.5,
                      borderLeft: `2px solid ${CH_REVIEW_SKY.border}`,
                      py: 0.25,
                    }}
                  >
                    {para}
                  </Typography>
                ));
              })()}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // 渲染 AI 原始返回（非 JSON，降级为纯文本展示）
  const renderRawReport = (raw) => {
    const text = String(raw ?? '');
    const lineCount = text.split('\n').length;
    return (
      <Box>
        <Box
          className="ai-review-card-breathe"
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${CH_REVIEW_SKY.borderSoft}`,
            bgcolor: 'rgba(10,12,20,0.75)',
            maxHeight: 'none',
            overflow: 'visible',
          }}
        >
          <TextareaAutosize
            readOnly
            value={text}
            minRows={Math.min(48, Math.max(6, lineCount))}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'transparent',
              border: 'none',
              color: 'rgba(255,255,255,0.9)',
              fontSize: 12,
              lineHeight: 1.9,
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              resize: 'none',
              outline: 'none',
              padding: 0,
              overflow: 'hidden',
            }}
          />
        </Box>
      </Box>
    );
  };

  // ── 主面板渲染 ─────────────────────────────────────────────────────────────
  const renderAIReviewPanel = () => {
    const reviewData = generatedContent ? parseReviewJson(generatedContent) : null;
    const loadingPreview = generating ? buildAiReviewPackage() : null;

    return (
      <motion.div
        className="ai-review-panel-bg assist-ai-tool-panel"
        style={{ width: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}
        initial={motionReduced ? false : { opacity: 0, y: 10 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* 扫描线动画（仅在生成中显示） */}
        {generating && <Box className="ai-review-scan-line" />}

        {/* ── 顶部控件区（随侧栏整体滚动，不占固定视口高度） ── */}
        <Box className="ai-review-controls-sticky" sx={{ bgcolor: 'rgba(8,10,18,0.88)', px: { xs: 1.5, sm: 2 }, py: { xs: 1, sm: 1.25 }, borderBottom: `1px solid ${CH_REVIEW_SKY.borderFaint}` }}>
          {/* 标题栏 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <IconButton size="small" onClick={() => handleOpenPanel('ai')} sx={{ color: 'rgba(255,255,255,0.6)', p: 0.5 }}>
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography sx={{ color: CH_REVIEW_SKY.main, fontSize: 14, fontWeight: 700, letterSpacing: '0.05em' }}>
              AI 审稿
            </Typography>
            {/* 生成状态呼吸灯 */}
            {generating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                <Box sx={{
                  width: 6, height: 6, borderRadius: '50%',
                  bgcolor: CH_REVIEW_SKY.main,
                  animation: 'reviewScoreBreathe 1s ease-in-out infinite',
                }} />
                <Typography sx={{ color: CH_REVIEW_SKY.muted, fontSize: 10, animation: 'reviewScoreBreathe 1s ease-in-out infinite' }}>
                  审稿中...
                </Typography>
              </Box>
            )}
          </Box>

          {/* 审稿范围 */}
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mb: 0.5 }}>
            审稿范围（无需划选文字，直接点选）
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {[
              { id: 'current', label: '当前章' },
              { id: '3', label: '最近3章' },
              { id: '5', label: '最近5章' },
              { id: '10', label: '最近10章' },
              { id: 'all', label: '全本' },
              { id: 'custom', label: '自定义' },
            ].map((scope) => (
              <Chip
                key={scope.id}
                label={scope.label}
                size="small"
                onClick={() => setReviewScope(scope.id)}
                className={reviewScope === scope.id ? 'ai-review-tag-active' : ''}
                sx={{
                  height: 24,
                  fontSize: 10,
                  bgcolor: reviewScope === scope.id ? CH_REVIEW_SKY.bgTint : 'rgba(255,255,255,0.05)',
                  color: reviewScope === scope.id ? CH_REVIEW_SKY.text : 'rgba(255,255,255,0.5)',
                  border: '1px solid',
                  borderColor: reviewScope === scope.id ? CH_REVIEW_SKY.border : 'rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: CH_REVIEW_SKY.bgTintCard, borderColor: CH_REVIEW_SKY.borderSoft },
                }}
              />
            ))}
          </Box>

          {/* 自定义范围输入 */}
          {reviewScope === 'custom' && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mb: 1 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>全书第</Typography>
              <TextField
                size="small"
                value={reviewCustomFrom}
                onChange={(e) => setReviewCustomFrom(e.target.value)}
                inputProps={{ inputMode: 'numeric', style: { width: 48, textAlign: 'center', color: '#fff', fontSize: 11, padding: '4px 6px' } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: CH_REVIEW_SKY.borderSoft },
                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>章 至 第</Typography>
              <TextField
                size="small"
                value={reviewCustomTo}
                onChange={(e) => setReviewCustomTo(e.target.value)}
                inputProps={{ inputMode: 'numeric', style: { width: 48, textAlign: 'center', color: '#fff', fontSize: 11, padding: '4px 6px' } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: CH_REVIEW_SKY.borderSoft },
                  '& .MuiOutlinedInput-root': { borderRadius: 1 },
                }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>章</Typography>
            </Box>
          )}

          {/* 审稿维度 */}
          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mb: 0.5 }}>
            审稿维度
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {['结构', '节奏', '人物', '语言'].map((dim) => {
              const active = reviewDimensions.includes(dim);
              const dimCfg = {
                '结构': { color: '#7dd3fc' },
                '节奏': { color: '#fbbf24' },
                '人物': { color: '#c4b5fd' },
                '语言': { color: '#93c5fd' },
              };
              return (
                <Chip
                  key={dim}
                  label={dim}
                  size="small"
                  onClick={() => setReviewDimensions(prev =>
                    prev.includes(dim) ? prev.filter(d => d !== dim) : [...prev, dim]
                  )}
                  className={active ? 'ai-review-tag-active' : ''}
                  sx={{
                    height: 24,
                    fontSize: 10,
                    bgcolor: active ? `${dimCfg[dim].color}22` : 'rgba(255,255,255,0.05)',
                    color: active ? dimCfg[dim].color : 'rgba(255,255,255,0.4)',
                    border: '1px solid',
                    borderColor: active ? `${dimCfg[dim].color}55` : 'rgba(255,255,255,0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    '&:hover': { bgcolor: `${dimCfg[dim].color}15`, borderColor: `${dimCfg[dim].color}40` },
                  }}
                />
              );
            })}
          </Box>

          {/* 开始审稿按钮 */}
          <Button
            fullWidth
            variant="outlined"
            onClick={handleAIGenerate}
            disabled={generating}
            className={generating ? 'ai-review-btn-loading' : 'assist-chapter-ai-glow-btn'}
            sx={{
              py: { xs: 0.65, sm: 1 },
              fontSize: { xs: 11, sm: 12 },
              fontWeight: 700,
              color: generating ? 'rgba(255,255,255,0.4)' : CH_REVIEW_SKY.main,
              borderColor: generating ? CH_REVIEW_SKY.borderFaint : CH_REVIEW_SKY.border,
              letterSpacing: '0.06em',
              '&:hover': { borderColor: CH_REVIEW_SKY.border, bgcolor: CH_REVIEW_SKY.bgTintCard },
            }}
          >
            {generating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={14} sx={{ color: CH_REVIEW_SKY.main, animation: 'spin 1s linear infinite' }} />
                <span>AI 审稿分析中...</span>
              </Box>
            ) : '▶ 开始审稿'}
          </Button>
        </Box>

        {/* ── 报告与占位（与顶栏、底栏同一滚动流，由侧栏 scroll 容器滚动） ── */}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, pt: { xs: 1.25, sm: 1.5 } }}>
          {/* 无结果时显示引导 */}
          {!generatedContent && !generating && (
            <motion.div
              initial={motionReduced ? false : { opacity: 0, scale: 0.96 }}
              animate={motionReduced ? false : { opacity: 1, scale: 1 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ textAlign: 'center', py: 6, opacity: 0.85 }}>
                <SearchIcon sx={{ color: CH_REVIEW_SKY.main, fontSize: 40, mb: 1, filter: 'drop-shadow(0 0 12px rgba(56,189,248,0.35))' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
                  选择审稿范围和维度<br />点击上方「开始审稿」
                </Typography>
              </Box>
            </motion.div>
          )}

          {/* 生成中：扫描动画 + 本次参与审稿的章节列表（与章节列表同款天青） */}
          {generating && (
            <Box sx={{ py: 2, px: 0.5 }}>
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <CircularProgress size={44} sx={{ color: CH_REVIEW_SKY.main }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 13, mt: 1.5 }}>
                  AI 正在审稿分析中...
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, mt: 0.5 }}>
                  正在分析：{reviewDimensions.length ? reviewDimensions.join(' / ') : '—'}
                </Typography>
              </Box>
              {loadingPreview?.reviewPreview?.total > 0 && (
                <Box
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${CH_REVIEW_SKY.borderSoft}`,
                    bgcolor: 'rgba(8,12,22,0.65)',
                    p: 1.5,
                    maxHeight: 280,
                    overflow: 'auto',
                  }}
                >
                  <Typography sx={{ color: CH_REVIEW_SKY.muted, fontSize: 10, fontWeight: 700, mb: 1, letterSpacing: '0.08em' }}>
                    本次审稿正文来源
                  </Typography>
                  {loadingPreview.scopeLabel && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, mb: 1, lineHeight: 1.5 }}>
                      {loadingPreview.scopeLabel}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                    {loadingPreview.reviewPreview.rows.map((row, idx) => (
                      <Box
                        key={`${row.bookOrder}-${row.chapterNumber}-${idx}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          gap: 1,
                          py: 0.45,
                          px: 0.75,
                          borderRadius: 1,
                          border: `1px solid ${CH_REVIEW_SKY.borderFaint}`,
                          bgcolor: CH_REVIEW_SKY.bgTintCard,
                        }}
                      >
                        <Typography sx={{ color: CH_REVIEW_SKY.main, fontSize: 10, fontWeight: 800, flexShrink: 0, minWidth: 52 }}>
                          {row.bookOrder != null ? `第${row.bookOrder}章` : '全书'}
                        </Typography>
                        <Typography sx={{ color: CH_REVIEW_SKY.text, fontSize: 11, flex: 1, minWidth: 0, lineHeight: 1.45, wordBreak: 'break-word' }}>
                          卷内第{row.chapterNumber}章 · {row.title}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  {loadingPreview.reviewPreview.total > loadingPreview.reviewPreview.rows.length && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, mt: 1 }}>
                      另有 {loadingPreview.reviewPreview.total - loadingPreview.reviewPreview.rows.length} 章已纳入正文，列表仅展示前 {loadingPreview.reviewPreview.rows.length} 章
                    </Typography>
                  )}
                </Box>
              )}
              {loadingPreview && !loadingPreview.error && !(loadingPreview.reviewPreview?.total > 0) && (
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, textAlign: 'center' }}>
                  正在准备审稿文本…
                </Typography>
              )}
            </Box>
          )}

          {/* 有结果时：结构化报告优先，否则降级为原始文本 */}
          {generatedContent && !generating && (
            reviewData ? renderStructuredReport(reviewData) : renderRawReport(generatedContent)
          )}

          {/* ── 底部操作：紧凑一行双按钮，随内容滚动（不贴底固定） ── */}
          {generatedContent && !generating && (
            <Box
              sx={{
                mt: 1.25,
                pt: 1,
                borderTop: `1px solid ${CH_REVIEW_SKY.borderFaint}`,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'nowrap',
              }}
            >
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(generatedContent);
                  setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  py: 0.35,
                  px: 0.75,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textTransform: 'none',
                  borderColor: CH_REVIEW_SKY.borderSoft,
                  color: CH_REVIEW_SKY.muted,
                  bgcolor: 'rgba(56, 189, 248, 0.06)',
                  '&:hover': { borderColor: CH_REVIEW_SKY.border, bgcolor: CH_REVIEW_SKY.bgTintCard },
                }}
              >
                复制全文
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setGeneratedContent('')}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  py: 0.35,
                  px: 0.75,
                  fontSize: 11,
                  fontWeight: 600,
                  lineHeight: 1.2,
                  textTransform: 'none',
                  borderColor: 'rgba(255,255,255,0.14)',
                  color: 'rgba(255,255,255,0.55)',
                  bgcolor: 'rgba(255,255,255,0.03)',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.22)', bgcolor: 'rgba(255,255,255,0.06)' },
                }}
              >
                清空报告
              </Button>
            </Box>
          )}
        </Box>

        {/* 全局 keyframes（在 DOM 中插入一次） */}
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes reviewScoreBreathe {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.6; }
          }
        `}</style>
      </motion.div>
    );
  };

  // 渲染 AI 纠错面板
  const renderAICorrectPanel = () => {
    const strictnessConfig = {
      loose: { label: '宽松', color: '#60a5fa', desc: '只找明显错别字' },
      normal: { label: '标准', color: '#f59e0b', desc: '错字+病句+标点' },
      strict: { label: '严格', color: '#ef4444', desc: '加逻辑一致性检查' },
    };
    const specialCheckOptions = [
      { label: '修仙等级词', color: '#38bdf8' },
      { label: '武器法宝名', color: '#a78bfa' },
      { label: '人物关系一致', color: '#f472b6' },
      { label: '地名一致', color: '#fbbf24' },
    ];

    const toggleSpecialCheck = (label) => {
      setCorrectSpecialChecks(prev =>
        prev.includes(label) ? prev.filter(s => s !== label) : [...prev, label]
      );
    };

    const correctBodyText = aiPrompt !== '' || !selectedText ? aiPrompt : selectedText;

    return (
      <motion.div
        className="panel-content assist-ai-tool-panel"
        initial={motionReduced ? false : { opacity: 0, y: 12 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: '1px solid rgba(252,211,77,0.38)',
            background: 'linear-gradient(145deg, rgba(251,191,36,0.1) 0%, rgba(8,14,24,0.94) 55%, rgba(10,14,22,0.9) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <IconButton onClick={() => handleOpenPanel('ai')} size="small" sx={{ color: '#fff', border: '1px solid rgba(252,211,77,0.35)', bgcolor: 'rgba(251,191,36,0.08)' }}>
            <ArrowBackIcon />
          </IconButton>
          <FindInPageIcon sx={{ color: '#fcd34d', fontSize: 22, filter: 'drop-shadow(0 0 8px rgba(252,211,77,0.35))' }} />
          <Typography sx={{ flex: 1, color: '#fef3c7', fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>AI 纠错</Typography>
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, fontWeight: 600 }}>
          纠错严格度
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.45, sm: 0.6 }, mb: 1.25 }}>
          {Object.entries(strictnessConfig).map(([key, cfg]) => (
            <Box
              key={key}
              onClick={() => setCorrectStrictness(key)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: 0.5,
                py: 0.45,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: correctStrictness === key ? cfg.color : 'rgba(255,255,255,0.1)',
                bgcolor: correctStrictness === key ? `${cfg.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: cfg.color },
              }}
            >
              <Typography component="span" sx={{ color: correctStrictness === key ? cfg.color : 'rgba(255,255,255,0.7)', fontWeight: 700, fontSize: 12, lineHeight: 1.2 }}>
                {cfg.label}
              </Typography>
              <Typography component="span" sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 10, lineHeight: 1.2 }}>
                {cfg.desc}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 1, fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          网文专项
          {correctSpecialChecks.length > 0 && (
            <Chip label={`${correctSpecialChecks.length}项`} size="small" sx={{ bgcolor: 'rgba(251,191,36,0.22)', color: '#fde68a', height: 20, fontSize: 10, border: '1px solid rgba(251,191,36,0.4)' }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          {specialCheckOptions.map(opt => (
            <Chip
              key={opt.label}
              label={opt.label}
              onClick={() => toggleSpecialCheck(opt.label)}
              sx={{
                bgcolor: correctSpecialChecks.includes(opt.label) ? `${opt.color}28` : 'rgba(255,255,255,0.05)',
                color: correctSpecialChecks.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.5)',
                border: '1px solid',
                borderColor: correctSpecialChecks.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                cursor: 'pointer',
                fontSize: 11,
                height: 26,
              }}
            />
          ))}
        </Box>

        <FormControlLabel
          control={(
            <Switch
              checked={correctSensitiveCheck}
              onChange={(e) => setCorrectSensitiveCheck(e.target.checked)}
              size="small"
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fbbf24' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(251,191,36,0.45)' } }}
            />
          )}
          label="敏感词检查（检测平台违禁词）"
          sx={{ color: 'rgba(255,255,255,0.72)', fontSize: 12, mb: 2 }}
        />

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12 }}>
          纠错正文（{correctBodyText.length}字）
        </Typography>
        <TextareaAutosize
          minRows={4}
          maxRows={16}
          placeholder="选中文本后自动带入，也可直接在此输入要纠错的段落..."
          value={correctBodyText}
          onChange={(e) => setAiPrompt(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(8,14,24,0.75)',
            border: '1px solid rgba(252,211,77,0.4)',
            borderRadius: 8,
            padding: 8,
            color: '#fff',
            fontSize: 13,
            lineHeight: 1.5,
            outline: 'none',
            marginBottom: 8,
          }}
        />

        <Button
          variant="outlined"
          onClick={handleAIGenerate}
          disabled={generating || !(aiPrompt || selectedText)}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          className="assist-chapter-ai-glow-btn"
          size="medium"
          sx={{ py: 1, mt: 0.5, width: '100%', borderColor: 'rgba(252,211,77,0.55) !important', color: '#fef3c7 !important' }}
        >
          {generating ? '纠错中...' : `开始纠错（${strictnessConfig[correctStrictness]?.label}）`}
        </Button>

        {generatedContent && (
          <motion.div
            initial={motionReduced ? false : { opacity: 0, y: 8 }}
            animate={motionReduced ? false : { opacity: 1, y: 0 }}
            transition={motionReduced ? { duration: 0 } : { duration: 0.3 }}
          >
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(8,14,24,0.85)', borderRadius: 2, border: '1px solid rgba(252,211,77,0.38)' }}>
              <Typography sx={{ color: '#fcd34d', fontWeight: 600, mb: 1 }}>
                纠错结果
              </Typography>
              <Typography sx={{ color: '#fff', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 300, overflow: 'auto', lineHeight: 1.8 }}>
                {generatedContent}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" onClick={handleReplaceContent} sx={{ bgcolor: 'rgba(251,191,36,0.88)', color: '#0f172a', fontWeight: 600, '&:hover': { bgcolor: '#fbbf24' } }}>
                  替换原文
                </Button>
                <Button variant="outlined" size="small" onClick={handleInsertContent} className="workspace-btn-cyber">
                  追加到末尾
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedContent);
                    setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                  }}
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  复制
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // 渲染 AI 去痕面板
  const renderAIDenoisePanel = () => {
    const denoiseBorder = 'rgba(249, 168, 212, 0.42)';
    const denoiseBorderSoft = 'rgba(249, 168, 212, 0.22)';
    const denoiseMain = '#f9a8d4';
    const focusOptions = [
      { label: '机械连接词', color: '#f87171', Icon: LinkChainIcon, desc: '首先/其次/最后等' },
      { label: '形容词堆砌', color: '#fbbf24', Icon: BlurOnIcon, desc: '过度华丽的形容词' },
      { label: '套话模板', color: '#c4b5fd', Icon: ArticleIcon, desc: '值得注意的是/事实上等' },
      { label: '句式对称', color: '#38bdf8', Icon: CompareArrowsIcon, desc: '过于整齐划一的句式' },
    ];
    const styleOptions = [
      { id: 'natural', label: '口语自然', color: '#93c5fd', Icon: ChatBubbleOutlineIcon, desc: '像人随口说的' },
      { id: 'literary', label: '文学流畅', color: '#7dd3fc', Icon: AutoStoriesIcon, desc: '优美但不做作' },
      { id: 'novel', label: '小说张力', color: '#fbcfe8', Icon: TheaterComedyIcon, desc: '有故事感和张力' },
    ];
    const levelConfig = {
      light: { label: '轻度', color: '#60a5fa', desc: '保留70%结构' },
      medium: { label: '中度', color: '#f59e0b', desc: '保留50%结构' },
      strong: { label: '强力', color: '#ef4444', desc: '只保核心信息' },
    };

    const toggleFocus = (label) => {
      setDenoiseFocus(prev =>
        prev.includes(label) ? prev.filter(f => f !== label) : [...prev, label]
      );
    };

    const denoiseBodyText = aiPrompt !== '' || !selectedText ? aiPrompt : selectedText;

    return (
      <motion.div
        className="panel-content assist-ai-tool-panel"
        initial={motionReduced ? false : { opacity: 0, y: 12 }}
        animate={motionReduced ? false : { opacity: 1, y: 0 }}
        transition={motionReduced ? { duration: 0 } : { duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${denoiseBorder}`,
            background: 'linear-gradient(145deg, rgba(244,114,182,0.12) 0%, rgba(8,14,24,0.94) 55%, rgba(10,14,22,0.9) 100%)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          }}
        >
          <IconButton onClick={() => handleOpenPanel('ai')} size="small" sx={{ color: 'rgba(255,255,255,0.9)', border: `1px solid ${denoiseBorderSoft}`, bgcolor: 'rgba(244,114,182,0.08)' }}>
            <ArrowBackIcon />
          </IconButton>
          <CleanServicesIcon sx={{ color: denoiseMain, fontSize: 22, filter: 'drop-shadow(0 0 8px rgba(249,168,212,0.35))' }} />
          <Typography sx={{ flex: 1, color: '#fce7f3', fontWeight: 700, fontSize: { xs: 15, sm: 16 } }}>AI 去痕</Typography>
        </Box>

        <Box sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          去痕重点
          {denoiseFocus.length > 0 && (
            <Chip label={`${denoiseFocus.length}项`} size="small" sx={{ bgcolor: 'rgba(244,114,182,0.22)', color: '#fce7f3', height: 20, fontSize: 10, border: `1px solid ${denoiseBorderSoft}` }} />
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' }, gap: { xs: 0.5, sm: 0.6 }, mb: 1.25 }}>
          {focusOptions.map(opt => (
            <Box
              key={opt.label}
              onClick={() => toggleFocus(opt.label)}
              sx={{
                py: 0.35,
                px: 0.65,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: denoiseFocus.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: denoiseFocus.includes(opt.label) ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: { xs: 'row', sm: 'column' },
                alignItems: { xs: 'center', sm: 'center' },
                textAlign: { xs: 'left', sm: 'center' },
                gap: { xs: 0.75, sm: 0 },
                minWidth: 0,
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color },
              }}
            >
              <opt.Icon sx={{ fontSize: { xs: 20, sm: 17 }, flexShrink: 0, lineHeight: 1, mb: { xs: 0, sm: 0.15 }, color: denoiseFocus.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.32)' }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ color: denoiseFocus.includes(opt.label) ? opt.color : 'rgba(255,255,255,0.65)', fontSize: { xs: 12, sm: 10 }, fontWeight: 600, lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: { xs: 10, sm: 8 }, mt: { xs: 0.15, sm: 0.2 }, lineHeight: 1.25 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, fontWeight: 600 }}>
          目标文风
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.5, sm: 0.75 }, mb: 1.25 }}>
          {styleOptions.map(opt => (
            <Box
              key={opt.id}
              onClick={() => setDenoiseTargetStyle(opt.id)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                py: 0.4,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: denoiseTargetStyle === opt.id ? opt.color : 'rgba(255,255,255,0.1)',
                bgcolor: denoiseTargetStyle === opt.id ? `${opt.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color },
              }}
            >
              <opt.Icon sx={{ fontSize: 18, color: denoiseTargetStyle === opt.id ? opt.color : 'rgba(255,255,255,0.38)' }} />
              <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                <Typography sx={{ color: denoiseTargetStyle === opt.id ? opt.color : 'rgba(255,255,255,0.65)', fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>
                  {opt.label}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, lineHeight: 1.25 }}>
                  {opt.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          去痕强度
          {denoiseLevel && (
            <Chip label={levelConfig[denoiseLevel]?.desc} size="small" sx={{ bgcolor: `${levelConfig[denoiseLevel]?.color}22`, color: levelConfig[denoiseLevel]?.color, height: 18, fontSize: 10 }} />
          )}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 0.45, sm: 0.6 }, mb: 1.25 }}>
          {Object.entries(levelConfig).map(([key, cfg]) => (
            <Box
              key={key}
              onClick={() => setDenoiseLevel(key)}
              sx={{
                flex: { xs: 'none', sm: 1 },
                width: { xs: '100%', sm: 'auto' },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.45,
                px: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: denoiseLevel === key ? cfg.color : 'rgba(255,255,255,0.1)',
                bgcolor: denoiseLevel === key ? `${cfg.color}18` : 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                transition: 'all 0.15s',
                '&:hover': { borderColor: cfg.color },
              }}
            >
              <Typography sx={{ color: denoiseLevel === key ? cfg.color : 'rgba(255,255,255,0.6)', fontWeight: 700, fontSize: 12, lineHeight: 1 }}>
                {cfg.label}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, lineHeight: 1 }}>
                {cfg.desc}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ color: ASSIST_AI_SKY.sectionLabel, mb: 0.5, fontSize: 12 }}>
          去痕正文（{denoiseBodyText.length}字）
        </Typography>
        <TextareaAutosize
          minRows={4}
          maxRows={16}
          placeholder="选中文本后自动带入，也可直接在此输入要去痕的段落..."
          value={denoiseBodyText}
          onChange={(e) => setAiPrompt(e.target.value)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            background: 'rgba(8,14,24,0.75)',
            border: `1px solid ${denoiseBorder}`,
            borderRadius: 8,
            padding: 8,
            color: '#fff',
            fontSize: 13,
            lineHeight: 1.5,
            outline: 'none',
            marginBottom: 8,
          }}
        />

        <Box
          onClick={() => setDenoiseMoreOpen(!denoiseMoreOpen)}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            cursor: 'pointer', mb: 1,
            color: 'rgba(255,255,255,0.5)', fontSize: 12,
            '&:hover': { color: denoiseMain },
          }}
        >
          <Typography sx={{ color: 'inherit', fontSize: 12 }}>更多选项</Typography>
          <KeyboardArrowRightIcon sx={{ transform: denoiseMoreOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
        </Box>

        <AnimatePresence initial={false}>
          {denoiseMoreOpen && (
            <motion.div
              initial={motionReduced ? false : { opacity: 0, height: 0 }}
              animate={motionReduced ? false : { opacity: 1, height: 'auto' }}
              exit={motionReduced ? false : { opacity: 0, height: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.25 }}
              style={{ overflow: 'hidden' }}
            >
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(8,14,24,0.75)', borderRadius: 2, border: `1px solid ${denoiseBorderSoft}` }}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={denoiseKeepLength}
                      onChange={(e) => setDenoiseKeepLength(e.target.checked)}
                      size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#f472b6' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(244,114,182,0.45)' } }}
                    />
                  )}
                  label="字数允许浮动±15%（关闭则严格保持字数）"
                  sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 11 }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', mb: 1, fontSize: 11, mt: 1 }}>
                  参照文风（粘贴一段你喜欢的文字风格）
                </Typography>
                <TextareaAutosize
                  minRows={2}
                  placeholder="粘贴一段人类写的你觉得好的文字..."
                  value={denoiseReference}
                  onChange={(e) => setDenoiseReference(e.target.value)}
                  style={{
                    width: '100%',
                    background: 'rgba(8,14,24,0.85)',
                    border: `1px solid ${denoiseBorderSoft}`,
                    borderRadius: 8,
                    padding: 10,
                    color: '#fff',
                    fontSize: 12,
                    outline: 'none',
                    resize: 'none',
                  }}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        <Button
          variant="outlined"
          onClick={handleAIGenerate}
          disabled={generating || !(aiPrompt || selectedText)}
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <PlayArrowIcon />}
          className="assist-chapter-ai-glow-btn"
          size="medium"
          sx={{ py: 1, mt: 0.5, width: '100%', borderColor: `${denoiseBorder} !important`, color: '#fce7f3 !important' }}
        >
          {generating ? '去痕中...' : '开始去痕'}
        </Button>

        {generatedContent && (
          <motion.div
            initial={motionReduced ? false : { opacity: 0, y: 8 }}
            animate={motionReduced ? false : { opacity: 1, y: 0 }}
            transition={motionReduced ? { duration: 0 } : { duration: 0.3 }}
          >
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(8,14,24,0.85)', borderRadius: 2, border: `1px solid ${denoiseBorder}` }}>
              <Typography sx={{ color: denoiseMain, fontWeight: 600, mb: 1 }}>
                去痕结果
              </Typography>
              <Typography sx={{ color: '#fff', whiteSpace: 'pre-wrap', fontSize: 13, maxHeight: 300, overflow: 'auto', lineHeight: 1.8 }}>
                {generatedContent}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" onClick={handleReplaceContent} sx={{ bgcolor: 'rgba(244,114,182,0.88)', color: '#0f172a', fontWeight: 600, '&:hover': { bgcolor: '#f472b6' } }}>
                  替换原文
                </Button>
                <Button variant="outlined" size="small" onClick={handleInsertContent} className="workspace-btn-cyber">
                  追加到末尾
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(generatedContent);
                    setSnackbar({ open: true, message: '已复制到剪贴板', severity: 'success' });
                  }}
                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                >
                  复制
                </Button>
              </Box>
            </Box>
          </motion.div>
        )}
      </motion.div>
    );
  };

  // ========== 对话会话管理函数 ==========

  // 加载对话列表
  const loadConversations = async () => {
    try {
      setConversationLoading(true);
      const res = await apiClient.get('/chat');
      if (res.data?.success) {
        setConversations(res.data.data || []);
      }
    } catch (err) {
      console.error('加载对话列表失败:', err);
    } finally {
      setConversationLoading(false);
    }
  };

  // 创建新对话
  const handleCreateConversation = async () => {
    try {
      const res = await apiClient.post('/chat');
      if (res.data?.success) {
        const newConv = res.data.data;
        setConversations(prev => [newConv, ...prev]);
        setCurrentConversationId(newConv._id);
        setChatMessages([]);
        setConversationListOpen(false);
      }
    } catch (err) {
      console.error('创建对话失败:', err);
    }
  };

  // 切换对话
  const handleSwitchConversation = async (conversationId) => {
    if (conversationId === currentConversationId) {
      setConversationListOpen(false);
      return;
    }
    try {
      setConversationLoading(true);
      const res = await apiClient.get(`/chat/${conversationId}`);
      if (res.data?.success) {
        setCurrentConversationId(conversationId);
        // 转换消息格式（数据库是 role: 'assistant'，前端用 'ai'）
        const msgs = (res.data.data.messages || []).map(m => ({
          ...m,
          role: m.role === 'assistant' ? 'ai' : m.role
        }));
        setChatMessages(msgs);
        // 更新列表中的最后活跃时间
        setConversations(prev => prev.map(c =>
          c._id === conversationId
            ? { ...c, lastActiveAt: new Date() }
            : c
        ));
      }
    } catch (err) {
      console.error('加载对话失败:', err);
      setChatMessages([]);
    } finally {
      setConversationLoading(false);
      setConversationListOpen(false);
    }
  };

  // 删除对话（打开应用内确认框）
  const handleDeleteConversation = (conversationId, e) => {
    e?.stopPropagation();
    setDeleteChatDialog({ open: true, id: conversationId });
  };

  const confirmDeleteChatConversation = async () => {
    const conversationId = deleteChatDialog.id;
    if (!conversationId) return;
    setDeleteChatLoading(true);
    try {
      await apiClient.delete(`/chat/${conversationId}`);
      setConversations(prev => prev.filter(c => c._id !== conversationId));
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setChatMessages([]);
      }
      setDeleteChatDialog({ open: false, id: null });
      setSnackbar({ open: true, message: '对话已删除', severity: 'success' });
    } catch (err) {
      console.error('删除对话失败:', err);
      setSnackbar({ open: true, message: '删除失败，请稍后重试', severity: 'error' });
    } finally {
      setDeleteChatLoading(false);
    }
  };

  // 保存消息到后端（发送消息后调用）
  const saveChatMessages = async (messages) => {
    if (!currentConversationId || messages.length === 0) return;
    try {
      // 只保存最后一条消息（避免重复保存）
      const lastMsg = messages[messages.length - 1];
      if (lastMsg._id) return; // 占位消息不需要保存
      await apiClient.post(`/chat/${currentConversationId}/messages`, {
        messages: [lastMsg]
      });
    } catch (err) {
      console.error('保存消息失败:', err);
    }
  };

  // 对话式 AI 发送（流式输出 + 清理 ** 标记 + 关闭思考模式）
  const handleChatSend = async () => {
    if (!chatInput.trim()) return;

    // 检查次数限制
    if (!checkAssistQuota('AI对话')) {
      return;
    }

    // 如果没有对话ID，先创建一个
    let convId = currentConversationId;
    if (!convId) {
      try {
        const res = await apiClient.post('/chat');
        if (res.data?.success) {
          convId = res.data.data._id;
          setCurrentConversationId(convId);
          setConversations(prev => [res.data.data, ...prev]);
        }
      } catch (err) {
        console.error('创建对话失败:', err);
      }
    }

    const userMessage = { role: 'user', content: chatInput };
    const updatedMessages = [...chatMessages, userMessage];
    setChatMessages(updatedMessages);
    const currentInput = chatInput;
    setChatInput('');
    setChatLoading(true);

    // 先插入一条空 AI 消息占位，后续流式追加
    const aiPlaceholderId = Date.now();
    setChatMessages(prev => [...prev, { role: 'ai', content: '', _id: aiPlaceholderId }]);

    // 滚动到底部
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-messages');
      if (chatContainer) {
        chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: 'smooth' });
      }
    }, 50);

    try {
      const messages = [
        {
          role: 'system',
          content: '你是 KM智能小说助手，一位专业的网络小说创作助手，擅长续写大纲、润色文笔、生成情节等。请用友好、专业的方式回复用户。如果用户询问你的模型或身份，请回答「我是 KM智能小说助手」，不要透露真实的底层模型名称，也不要提及任何内部技术细节。回复时请直接输出内容，不要使用 markdown 加粗标记（如 **文字**）。'
        },
        ...updatedMessages.map(msg => ({ role: msg.role === 'ai' ? 'assistant' : 'user', content: msg.content })),
        { role: 'user', content: currentInput }
      ];

      const currentNovelName = novel?.title || '未知项目';
      const params = {
        model: 'deepseek-v4-flash',
        messages,
        temperature: 0.7,
        max_tokens: 4000,
        extra_body: {},
        stream: true,
        billingInfo: {
          novelName: currentNovelName,
          chapterNumber: '',
          operationType: 'AI对话助手' 
        }
      };

      const result = await handleStreamGeneration(
        API_BASE_URL,
        params,
        'AI 对话',
        (chunk, fullContent) => {
          // 实时追加：清理 ** 标记后更新占位消息
          const cleaned = fullContent.replace(/\*\*/g, '');
          setChatMessages(prev =>
            prev.map(m => m._id === aiPlaceholderId ? { ...m, content: cleaned } : m)
          );
          // 自动滚动到底部
          setTimeout(() => {
            const chatContainer = document.querySelector('.chat-messages');
            if (chatContainer) {
              chatContainer.scrollTop = chatContainer.scrollHeight;
            }
          }, 10);
        },
        () => {},
        '/assist/ai/proxy-ai'  // 走辅助创作路由（DeepSeek 逆向号池，繁忙时排队让号）
      );

      // 最终清理（兜底）
      const finalContent = (result.content || '').replace(/\*\*/g, '');
      let finalMessages = updatedMessages;
      setChatMessages(prev => {
        finalMessages = prev.map(m => m._id === aiPlaceholderId ? { ...m, content: finalContent, _id: undefined } : m);
        return finalMessages;
      });

      // 滚动到最新消息
      setTimeout(() => {
        const chatContainer = document.querySelector('.chat-messages');
        if (chatContainer) {
          chatContainer.scrollTo({ top: chatContainer.scrollHeight, behavior: 'smooth' });
        }
      }, 100);

      // 保存用户消息和AI回复到后端
      if (convId) {
        try {
          await apiClient.post(`/chat/${convId}/messages`, {
            messages: [
              { role: 'user', content: currentInput },
              { role: 'ai', content: finalContent }
            ]
          });
          // 更新对话列表中的预览和标题
          setConversations(prev => prev.map(c =>
            c._id === convId
              ? {
                  ...c,
                  lastActiveAt: new Date(),
                  lastMessagePreview: finalContent.substring(0, 50),
                  messageCount: (c.messageCount || 0) + 2,
                  title: c.title === '新对话' ? currentInput.substring(0, 30) + (currentInput.length > 30 ? '...' : '') : c.title
                }
              : c
          ));
        } catch (err) {
          console.error('保存消息失败:', err);
        }
      }
    } catch (error) {
      console.error('对话失败:', error);
      const errMsg = '抱歉，生成内容时出现了问题。请稍后重试。';
      setChatMessages(prev =>
        prev.map(m => m._id === aiPlaceholderId ? { ...m, content: errMsg } : m)
      );
    } finally {
      setChatLoading(false);
    }
  };

  // 复制到正文（追加到末尾）
  const handleInsertContent = () => {
    if (generatedContent) {
      const originalContent = aiPrompt || selectedText || '';
      if (originalContent && chapterContent.includes(originalContent)) {
        // 如果原文在正文中有匹配，在原文后面插入
        const newContent = chapterContent.replace(originalContent, originalContent + '\n\n' + generatedContent);
        setChapterContent(newContent);
      } else {
        // 否则追加到末尾
        setChapterContent(chapterContent + '\n\n' + generatedContent);
      }
      handleClosePanel();
      setSnackbar({ open: true, message: '已插入正文', severity: 'success' });
    }
  };

  // 替换原文内容
  const handleReplaceContent = () => {
    if (generatedContent) {
      const originalContent = aiPrompt || selectedText || '';
      if (originalContent) {
        const newContent = chapterContent.replace(originalContent, generatedContent);
        setChapterContent(newContent);
        setSelectedText(''); // 清除选区
        setSelectionRange(null); // 清除选区范围
        setAiPrompt(''); // 清除输入
        handleClosePanel();
        setSnackbar({ open: true, message: '已替换原文', severity: 'success' });
      }
    }
  };

  // 渲染面板内容
  const renderPanelContent = () => {
    switch (activePanel) {
      case 'character':
        return renderCharacterPanel();
      case 'item':
        return renderItemPanel();
      case 'memo':
        return renderMemoPanel();
      case 'progress':
        return renderProgressPanel();
      case 'ai':
        return renderAIPanel();
      case 'chat':
        return renderChatPanel();
      case 'outline':
        return renderOutlinePanel();
      case 'outlineMain':
        return renderOutlineMainPanel();
      default:
        return null;
    }
  };

  // 渲染角色面板 — 布局与「章纲·章节」左栏列表配套，framer-motion 错帧入场 / 筛选离场 / 悬停光效
  const renderCharacterPanel = () => {
    const charPriority = (char) => {
      const tags = char.tags || [];
      const role = char.role || '';
      const title = char.title || '';
      const combined = [...tags, role, title].join(' ').toLowerCase();
      if (combined.includes('主角') || combined.includes('男主') || combined.includes('女主')) return 0;
      if (combined.includes('恋人') || combined.includes('女主') || combined.includes('女友')) return 1;
      if (combined.includes('导师') || combined.includes('师父')) return 2;
      if (combined.includes('反派') || combined.includes('boss') || combined.includes('敌人')) return 3;
      if (combined.includes('友人') || combined.includes('朋友')) return 4;
      if (combined.includes('配角') || combined.includes('伙伴') || combined.includes('弟子')) return 5;
      if (combined.includes('宿敌') || combined.includes('仇人')) return 6;
      return 7;
    };
    const filteredCharacters = characters
      .filter((c) => {
        const matchesSearch = (c.name || '').includes(characterSearch);
        const matchesFilter = characterFilter === 'all' || c.tags?.includes(characterFilter);
        return matchesSearch && matchesFilter;
      })
      .sort((a, b) => charPriority(a) - charPriority(b));

    const filterDefs = [
      { id: 'all', label: '全部', color: '#00f0ff', count: characters.length },
      { id: '主角', label: '主角', color: '#00ff9f', count: characters.filter((c) => c.tags?.includes('主角')).length },
      { id: '配角', label: '配角', color: '#ff6b35', count: characters.filter((c) => c.tags?.includes('配角')).length },
      { id: '反派', label: '反派', color: '#ff4444', count: characters.filter((c) => c.tags?.includes('反派')).length },
      { id: '导师', label: '导师', color: 'var(--ws-accent)', count: characters.filter((c) => c.tags?.includes('导师')).length },
      { id: '恋人', label: '恋人', color: '#ec4899', count: characters.filter((c) => c.tags?.includes('恋人')).length },
      { id: 'NPC', label: 'NPC', color: '#ffd700', count: characters.filter((c) => c.tags?.includes('NPC')).length },
    ];

    const narrowList = isChaptersNarrow;

    return (
      <Box className="panel-content character-panel-cyber workspace-character-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'transparent' }}>
        {/* 标题栏 — 与章节面板同款顶栏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          {isChaptersNarrow && (
            <Tooltip title="返回编辑">
              <IconButton
                size="small"
                onClick={handleClosePanel}
                aria-label="返回编辑"
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: 34, sm: 36 },
                  minHeight: { xs: 34, sm: 36 },
                  color: 'rgba(255,255,255,0.75)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '&:hover': { bgcolor: 'rgba(0,240,255,0.12)', borderColor: 'rgba(0,240,255,0.35)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography sx={{ color: '#00f0ff', fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: '0 0 16px rgba(0,240,255,0.35)', flex: 1, letterSpacing: '0.02em', minWidth: 0 }}>
            角色库
          </Typography>
          <Chip
            label={characters.length}
            size="small"
            sx={{ bgcolor: 'rgba(0,240,255,0.18)', color: '#67e8f9', height: 24, fontSize: 12, fontWeight: 700, border: '1px solid rgba(34,211,238,0.35)' }}
          />
        </Box>

        {/* 搜索栏 — 与章节搜索同款容器 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.32, delay: motionReduced ? 0 : 0.04, ease: [0.22, 1, 0.36, 1] }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="搜索角色..."
            value={characterSearch}
            onChange={(e) => setCharacterSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(34,211,238,0.25)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(34,211,238,0.55)' },
              },
            }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.5, fontSize: 18 }} /> }}
          />
        </Box>

        {/* 筛选 — 横向滚动，与窄屏章节工具条同类交互 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.28, delay: motionReduced ? 0 : 0.08 }}
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: 1,
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: 0.25,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 5 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(34,211,238,0.25)', borderRadius: 2 },
            }}
          >
            {filterDefs.map((filter, fi) => (
              <motion.div
                key={filter.id}
                whileHover={motionReduced ? undefined : { scale: 1.04 }}
                whileTap={motionReduced ? undefined : { scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 420, damping: 22 }}
              >
                <Chip
                  label={`${filter.label}${filter.count > 0 ? ` (${filter.count})` : ''}`}
                  size="small"
                  onClick={() => setCharacterFilter(filter.id)}
                  sx={{
                    flexShrink: 0,
                    bgcolor: characterFilter === filter.id ? 'rgba(0,240,255,0.14)' : 'rgba(255,255,255,0.04)',
                    color: characterFilter === filter.id ? filter.color : 'rgba(255,255,255,0.72)',
                    border: `1px solid ${characterFilter === filter.id ? filter.color : 'rgba(255,255,255,0.1)'}`,
                    fontSize: 11,
                    height: 28,
                    cursor: 'pointer',
                    fontWeight: characterFilter === filter.id ? 700 : 500,
                    boxShadow:
                      characterFilter === filter.id && !motionReduced
                        ? filter.id === '反派'
                          ? '0 0 14px rgba(248,113,113,0.35)'
                          : filter.id === '主角'
                            ? '0 0 14px rgba(52,211,153,0.35)'
                            : '0 0 14px rgba(34,211,238,0.3)'
                        : 'none',
                    '&:hover': { bgcolor: 'rgba(0,240,255,0.08)' },
                  }}
                />
              </motion.div>
            ))}
          </Box>
        </Box>

        {/* 操作按钮 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.3, delay: motionReduced ? 0 : 0.1, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            gap: { xs: 0.75, sm: 1 },
            px: { xs: 1.5, sm: 2 },
            py: 1,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            flexShrink: 0,
          }}
        >
          <Tooltip title="添加角色" enterTouchDelay={400}>
            <Button
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: { xs: 17, sm: 20 } }} />}
              onClick={() => {
                setEditingCharacter(null);
                setCharacterFormData({
                  name: '', gender: '未知', title: '', realm: '', level: '', description: '', abilities: '', background: '',
                  appearance: '', personality: '', goal: '', secret: '', relationships: '', equipment: '',
                  color: '#00f0ff', tags: [], relations: [],
                });
                setCharacterGenPrompt('');
                setNewRelation({ characterId: '', type: '其他', customType: '' });
                setActivePanel('characterEdit');
              }}
              className="workspace-btn-cyber"
              sx={{
                flex: 1,
                minWidth: 0,
                py: { xs: 0.55, sm: 1 },
                px: { xs: 0.5, sm: 1.25 },
                fontSize: { xs: 12, sm: 13 },
                minHeight: { xs: 36, sm: 40 },
                textTransform: 'none',
                justifyContent: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>添加角色</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>添加</Box>
            </Button>
          </Tooltip>
          {outlines.length > 0 && outlines[0]?.content && (
            <Tooltip title="AI 解析大纲并导入角色" enterTouchDelay={400}>
              <Button
                variant="outlined"
                startIcon={<span style={{ fontSize: 14, lineHeight: 1 }}></span>}
                onClick={() => {
                  setCharacterParseContent(outlines[0]?.content || '');
                  setShowCharacterParseDialog(true);
                }}
                className="workspace-btn-cyber"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  py: { xs: 0.55, sm: 1 },
                  px: { xs: 0.5, sm: 1.25 },
                  fontSize: { xs: 12, sm: 13 },
                  minHeight: { xs: 36, sm: 40 },
                  textTransform: 'none',
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  color: '#00f0ff',
                  borderColor: 'rgba(0,240,255,0.4)',
                  '&:hover': { borderColor: 'rgba(0,240,255,0.7)', bgcolor: 'rgba(0,240,255,0.08)' },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>AI解析角色</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>AI解析</Box>
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* 列表区 — 行样式对齐 renderChapterItem */}
        <Box className="character-list workspace-character-list" sx={{ flex: 1, overflow: 'auto', px: { xs: 1, sm: 1.25 }, py: 1, pb: 2 }}>
          {characters.length === 0 ? (
            <motion.div
              initial={motionReduced ? false : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: motionReduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <motion.div
                  animate={motionReduced ? {} : { y: [0, -4, 0] }}
                  transition={motionReduced ? {} : { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <PersonIcon sx={{ fontSize: 52, mb: 2, opacity: 0.35, color: '#00f0ff', filter: 'drop-shadow(0 0 12px rgba(0,240,255,0.4))' }} />
                </motion.div>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, mb: 0.5 }}>暂无角色</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: 12 }}>点击「添加角色」或「AI解析」导入</Typography>
              </Box>
            </motion.div>
          ) : (
            <>
              {filteredCharacters.length === 0 && (
                <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 4, fontSize: 13 }}>没有符合筛选的角色</Typography>
              )}
              <AnimatePresence mode="popLayout" initial={false}>
              {filteredCharacters.map((char, idx) => {
                const accent = char.color || '#00f0ff';
                const hasDesc = !!(char.description && String(char.description).trim());
                const bodyBg = hasDesc ? 'rgba(6,182,212,0.07)' : 'rgba(255,255,255,0.03)';
                const bodyBorder = hasDesc ? 'rgba(6,182,212,0.28)' : 'rgba(255,255,255,0.07)';
                const bodyHover = hasDesc ? 'rgba(6,182,212,0.12)' : 'rgba(255,255,255,0.06)';
                const bodyShadow = hasDesc
                  ? '0 2px 12px rgba(6,182,212,0.14), inset 0 1px 0 rgba(255,255,255,0.05)'
                  : '0 2px 8px rgba(0,0,0,0.22)';
                const displayName = getCharacterChapterOutlineDisplayName(char);
                const roleLb = getCharacterDisplayRoleLabel(char);
                const rv = getCharacterRoleVisual(roleLb);
                const dotColor =
                  char.level?.includes('主角') ? '#00ff9f' : char.level?.includes('反派') ? '#ff4444' : char.level?.includes('配角') ? '#ff6b35' : '#ffd700';
                const animDelay = motionReduced ? 0 : Math.min(idx * 0.042, 0.52);

                return (
                  <Box
                    component={motion.div}
                    layout
                    key={char._id}
                    initial={motionReduced ? false : { opacity: 0, x: -16, scale: 0.97 }}
                    animate={{
                      opacity: 1,
                      x: 0,
                      scale: 1,
                    }}
                    exit={motionReduced ? false : { opacity: 0, x: 14, scale: 0.94, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }}
                    transition={{
                      delay: animDelay,
                      duration: motionReduced ? 0 : 0.34,
                      ease: [0.22, 1, 0.36, 1],
                      layout: { duration: motionReduced ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] },
                    }}
                    whileHover={
                      motionReduced
                        ? undefined
                        : {
                            y: -2,
                            scale: 1.009,
                            boxShadow: `0 8px 28px ${accent}2e, 0 4px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)`,
                          }
                    }
                    whileTap={motionReduced ? undefined : { scale: 0.982 }}
                    onClick={() => {
                      setEditingCharacter(char);
                      setCharacterFormData(char);
                      setCharacterGenPrompt('');
                      setNewRelation({ characterId: '', type: '其他', customType: '' });
                      setActivePanel('characterEdit');
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      willChange: 'transform',
                      transform: 'translateZ(0)',
                      p: narrowList ? '6px 8px' : { xs: 1.15, sm: 1.25 },
                      mb: narrowList ? 0.4 : 0.75,
                      bgcolor: bodyBg,
                      border: `1px solid ${bodyBorder}`,
                      borderRadius: narrowList ? 1 : 1.5,
                      cursor: 'pointer',
                      boxShadow: bodyShadow,
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': { bgcolor: bodyHover },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '14%',
                        bottom: '14%',
                        width: 3,
                        borderRadius: '0 2px 2px 0',
                        bgcolor: accent,
                        boxShadow: `0 0 10px ${accent}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: narrowList ? 0.75 : 1.1, flex: 1, minWidth: 0, pl: 0.5 }}>
                      <Box
                        component={motion.div}
                        whileHover={motionReduced ? undefined : { scale: 1.07 }}
                        transition={{ type: 'spring', stiffness: 380, damping: 18 }}
                        sx={{ position: 'relative', flexShrink: 0, mt: 0.15 }}
                      >
                        <Avatar
                          sx={{
                            width: narrowList ? 40 : 44,
                            height: narrowList ? 40 : 44,
                            bgcolor: accent,
                            fontSize: narrowList ? 15 : 16,
                            fontWeight: 700,
                            boxShadow: `0 0 16px ${accent}55`,
                            border: '1px solid rgba(255,255,255,0.12)',
                          }}
                        >
                          {(displayName || char.name || '?').charAt(0)}
                        </Avatar>
                        <Box
                          component={motion.div}
                          whileHover={motionReduced ? undefined : { scale: 1.25 }}
                          transition={{ type: 'spring', stiffness: 500, damping: 16 }}
                          sx={{
                            position: 'absolute',
                            bottom: -1,
                            right: -1,
                            width: 11,
                            height: 11,
                            borderRadius: '50%',
                            bgcolor: dotColor,
                            border: '2px solid #0a0b12',
                            boxShadow: `0 0 6px ${dotColor}`,
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.25 }}>
                          <Typography
                            sx={{
                              color: '#f1f5f9',
                              fontWeight: 700,
                              fontSize: narrowList ? 13 : { xs: 14, sm: 15 },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                              letterSpacing: '0.02em',
                            }}
                          >
                            {displayName}
                          </Typography>
                          <Chip
                            label={roleLb}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: 9,
                              fontWeight: 800,
                              flexShrink: 0,
                              bgcolor: rv.bg,
                              color: rv.color,
                              border: rv.border,
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                          {(char.tags || []).filter((t) => t !== roleLb).slice(0, 1).map((tag, tagIdx) => (
                            <Chip
                              key={tagIdx}
                              label={tag}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: 9,
                                bgcolor: 'rgba(255,255,255,0.08)',
                                color: 'rgba(255,255,255,0.65)',
                                border: '1px solid rgba(255,255,255,0.12)',
                              }}
                            />
                          ))}
                        </Box>
                        {(char.realm || char.level) && (
                          <Typography
                            sx={{
                              color: 'rgba(255,255,255,0.45)',
                              fontSize: narrowList ? 11 : 12,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              lineHeight: 1.35,
                            }}
                          >
                            {[char.realm, char.level].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                        {char.description && (
                          <Typography
                            sx={{
                              color: 'rgba(255,255,255,0.32)',
                              fontSize: narrowList ? 10 : 11,
                              mt: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: 1.45,
                            }}
                          >
                            {char.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flexShrink: 0, pt: 0.25 }} onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="复制 JSON">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(JSON.stringify(char, null, 2));
                            setSnackbar({ open: true, message: '已复制角色信息', severity: 'success' });
                          }}
                          sx={{
                            color: 'rgba(255,255,255,0.45)',
                            '&:hover': { color: '#22d3ee', bgcolor: 'rgba(34,211,238,0.12)' },
                            ...(narrowList ? { p: 0.35, minWidth: 30, minHeight: 30 } : { minWidth: 34, minHeight: 34 }),
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: narrowList ? 15 : 17 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="编辑">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingCharacter(char);
                            setCharacterFormData(char);
                            setCharacterGenPrompt('');
                            setNewRelation({ characterId: '', type: '其他', customType: '' });
                            setActivePanel('characterEdit');
                          }}
                          sx={{
                            color: 'rgba(255,255,255,0.45)',
                            '&:hover': { color: '#fb923c', bgcolor: 'rgba(251,146,60,0.12)' },
                            ...(narrowList ? { p: 0.35, minWidth: 30, minHeight: 30 } : { minWidth: 34, minHeight: 34 }),
                          }}
                        >
                          <EditIcon sx={{ fontSize: narrowList ? 15 : 17 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
              </AnimatePresence>
            </>
          )}
        </Box>
      </Box>
    );
  };

  // 渲染词条面板
  // 词条库 — 配套章节列表样式 + framer-motion 错帧动画
  const renderItemPanel = () => {
    const narrow = isChaptersNarrow;
    const filteredItems = items.filter(
      (i) => (itemCategory === 'all' || i.category === itemCategory) && i.name.includes(itemSearch)
    );

    return (
      <Box className="panel-content workspace-item-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 标题栏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          {narrow && (
            <Tooltip title="返回编辑">
              <IconButton
                size="small"
                onClick={handleClosePanel}
                aria-label="返回编辑"
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: 34, sm: 36 },
                  minHeight: { xs: 34, sm: 36 },
                  color: 'rgba(255,255,255,0.75)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '&:hover': { bgcolor: 'rgba(0,240,255,0.12)', borderColor: 'rgba(0,240,255,0.35)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography sx={{ color: '#ff6b35', fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: '0 0 16px rgba(255,107,53,0.35)', flex: 1, letterSpacing: '0.02em', minWidth: 0 }}>
            词条库
          </Typography>
          <Chip label={items.length} size="small" sx={{ bgcolor: 'rgba(255,107,53,0.18)', color: '#fdba74', height: 24, fontSize: 12, fontWeight: 700, border: '1px solid rgba(251,146,60,0.35)' }} />
        </Box>

        {/* 搜索栏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.32, delay: motionReduced ? 0 : 0.04, ease: [0.22, 1, 0.36, 1] }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="搜索词条..."
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(251,146,60,0.28)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(251,146,60,0.55)' },
              },
            }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.5, fontSize: 18 }} /> }}
          />
        </Box>

        {/* 筛选行 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.28, delay: motionReduced ? 0 : 0.08 }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              pb: 0.25,
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': { height: 5 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(251,146,60,0.25)', borderRadius: 2 },
            }}
          >
            <Box
              component={motion.div}
              whileHover={motionReduced ? undefined : { scale: 1.04 }}
              whileTap={motionReduced ? undefined : { scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 420, damping: 22 }}
            >
              <Chip
                label="全部"
                size="small"
                onClick={() => setItemCategory('all')}
                sx={{
                  flexShrink: 0,
                  bgcolor: itemCategory === 'all' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.04)',
                  color: itemCategory === 'all' ? '#f1f5f9' : 'rgba(255,255,255,0.72)',
                  border: `1px solid ${itemCategory === 'all' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}`,
                  fontSize: 11,
                  height: 28,
                  cursor: 'pointer',
                  fontWeight: itemCategory === 'all' ? 700 : 500,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              />
            </Box>
            {ITEM_CATEGORIES.map((cat, ci) => (
              <Box
                key={cat.id}
                component={motion.div}
                whileHover={motionReduced ? undefined : { scale: 1.04 }}
                whileTap={motionReduced ? undefined : { scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 420, damping: 22 }}
              >
                <Chip
                  label={cat.name}
                  size="small"
                  onClick={() => setItemCategory(cat.id)}
                  sx={{
                    flexShrink: 0,
                    bgcolor: itemCategory === cat.id ? `${cat.color}22` : 'rgba(255,255,255,0.04)',
                    color: itemCategory === cat.id ? cat.color : 'rgba(255,255,255,0.72)',
                    border: `1px solid ${itemCategory === cat.id ? cat.color : 'rgba(255,255,255,0.1)'}`,
                    fontSize: 11,
                    height: 28,
                    cursor: 'pointer',
                    fontWeight: itemCategory === cat.id ? 700 : 500,
                    boxShadow: itemCategory === cat.id && !motionReduced ? `0 0 12px ${cat.color}40` : 'none',
                    '&:hover': { bgcolor: `${cat.color}14` },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Box>

        {/* 添加按钮 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.3, delay: motionReduced ? 0 : 0.1, ease: [0.22, 1, 0.36, 1] }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}
        >
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingItem(null);
              setItemFormData({ name: '', category: '势力', level: '', description: '', location: '', features: '', history: '', master: '', value: '', firstChapter: '', plotPoints: '', foreshadowing: '' });
              setItemGenPrompt('');
              setItemGenTemplate('custom');
              setActivePanel('itemEdit');
            }}
            className="workspace-btn-cyber"
            sx={{ fontSize: { xs: 12, sm: 13 }, minHeight: { xs: 36, sm: 40 }, textTransform: 'none', justifyContent: 'center' }}
          >
            添加词条
          </Button>
        </Box>

        {/* 列表区 */}
        <Box className="item-list" sx={{ flex: 1, overflow: 'auto', px: { xs: 1, sm: 1.25 }, py: 1, pb: 2 }}>
          {items.length === 0 ? (
            <motion.div
              initial={motionReduced ? false : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: motionReduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <motion.div animate={motionReduced ? {} : { y: [0, -4, 0] }} transition={motionReduced ? {} : { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}>
                  <CategoryIcon sx={{ fontSize: 52, mb: 2, opacity: 0.35, color: '#ff6b35', filter: 'drop-shadow(0 0 12px rgba(255,107,53,0.4))' }} />
                </motion.div>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, mb: 0.5 }}>暂无词条</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: 12 }}>点击上方「添加词条」开始创建</Typography>
              </Box>
            </motion.div>
          ) : filteredItems.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 6, fontSize: 13 }}>没有符合筛选的词条</Typography>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredItems.map((item, idx) => {
                const categoryColor = ITEM_CATEGORY_COLORS[item.category] || '#ff6b35';
                const categoryInfo = ITEM_CATEGORIES.find((c) => c.id === item.category);
                const hasDesc = !!(item.description && String(item.description).trim());
                const bodyBg = hasDesc ? 'rgba(251,146,60,0.07)' : 'rgba(255,255,255,0.03)';
                const bodyBorder = hasDesc ? 'rgba(251,146,60,0.28)' : 'rgba(255,255,255,0.07)';
                const bodyHover = hasDesc ? 'rgba(251,146,60,0.12)' : 'rgba(255,255,255,0.06)';
                const bodyShadow = hasDesc ? '0 2px 12px rgba(251,146,60,0.14), inset 0 1px 0 rgba(255,255,255,0.05)' : '0 2px 8px rgba(0,0,0,0.22)';
                const animDelay = motionReduced ? 0 : Math.min(idx * 0.042, 0.52);

                return (
                  <Box
                    component={motion.div}
                    layout
                    key={item._id}
                    initial={motionReduced ? false : { opacity: 0, x: -16, scale: 0.97 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={motionReduced ? false : { opacity: 0, x: 14, scale: 0.94, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }}
                    transition={{
                      delay: animDelay,
                      duration: motionReduced ? 0 : 0.34,
                      ease: [0.22, 1, 0.36, 1],
                      layout: { duration: motionReduced ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] },
                    }}
                    whileHover={
                      motionReduced
                        ? undefined
                        : { y: -2, scale: 1.009, boxShadow: `0 8px 28px ${categoryColor}2e, 0 4px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)` }
                    }
                    whileTap={motionReduced ? undefined : { scale: 0.982 }}
                    onClick={() => { setEditingItem(item); setItemFormData(item); setActivePanel('itemEdit'); }}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1,
                      willChange: 'transform',
                      transform: 'translateZ(0)',
                      p: narrow ? '6px 8px' : { xs: 1.15, sm: 1.25 },
                      mb: narrow ? 0.4 : 0.75,
                      bgcolor: bodyBg,
                      border: `1px solid ${bodyBorder}`,
                      borderRadius: narrow ? 1 : 1.5,
                      cursor: 'pointer',
                      boxShadow: bodyShadow,
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': { bgcolor: bodyHover },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0, top: '14%', bottom: '14%',
                        width: 3,
                        borderRadius: '0 2px 2px 0',
                        bgcolor: categoryColor,
                        boxShadow: `0 0 10px ${categoryColor}`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, pl: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: narrow ? 13 : { xs: 14, sm: 15 }, letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                          {item.name}
                        </Typography>
                        <Chip
                          label={categoryInfo?.name || item.category}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 9,
                            fontWeight: 800,
                            flexShrink: 0,
                            bgcolor: `${categoryColor}22`,
                            color: categoryColor,
                            border: `1px solid ${categoryColor}50`,
                            '& .MuiChip-label': { px: 0.5 },
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {item.level && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: narrow ? 11 : 12 }}>
                            {item.level}
                          </Typography>
                        )}
                        {item.location && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: narrow ? 11 : 12 }}>
                            {item.location}
                          </Typography>
                        )}
                      </Box>
                      {item.description && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: narrow ? 10 : 11, mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.45 }}>
                          {item.description}
                        </Typography>
                      )}
                    </Box>
                    <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.08 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }} sx={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)', pt: 0.25 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditingItem(item); setItemFormData(item); setActivePanel('itemEdit'); }}
                        sx={{ color: 'rgba(255,255,255,0.45)', '&:hover': { color: '#fb923c', bgcolor: 'rgba(251,146,60,0.12)' }, ...(narrow ? { p: 0.35, minWidth: 30, minHeight: 30 } : { minWidth: 34, minHeight: 34 }) }}>
                        <EditIcon sx={{ fontSize: narrow ? 15 : 17 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </AnimatePresence>
          )}
        </Box>
      </Box>
    );
  };

  // ============================================================
  // 【全局进度追踪面板】 - 核心新功能
  // ============================================================

  // 辅助函数：渲染章节总览的一行（必须用 function 声明式以便在箭头函数前调用）
  function renderChapterOverviewRow(volumeKey, volumeChapters) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {volumeChapters.map(ch => {
          const hasCharacters = ch.characterIds && ch.characterIds.length > 0;
          const hasForeshadowings = ch.foreshadowingIds && ch.foreshadowingIds.length > 0;
          const isCompleted = ch.status === 'completed' || ch.status === 'revised';
          const isInProgress = ch.status === 'in_progress';
          return (
            <Tooltip key={ch._id} title={
              <Box sx={{ fontSize: 11 }}>
                <Box sx={{ fontWeight: 600, mb: 0.25 }}>第{ch.chapterNumber}章 {ch.title && `「${ch.title}」`}</Box>
                <Box>状态：{ch.status === 'outline_only' ? '仅章纲' : ch.status === 'in_progress' ? '写作中' : ch.status === 'completed' ? '已完成' : '已修订'}</Box>
                {hasCharacters && <Box>角色：{ch.characterIds.length}人</Box>}
                {hasForeshadowings && <Box>伏笔：{ch.foreshadowingIds.length}条</Box>}
                {ch.outline?.coreEvent && <Box sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>核心：{ch.outline.coreEvent}</Box>}
              </Box>
            }>
              <Box sx={{
                width: 28, height: 28, borderRadius: 1, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: isCompleted ? 'rgba(6,182,212,0.2)' : isInProgress ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.05)',
                border: `1px solid ${isCompleted ? 'rgba(6,182,212,0.4)' : isInProgress ? 'rgba(255,215,0,0.4)' : 'rgba(255,255,255,0.1)'}`,
                transition: 'all 0.2s',
                '&:hover': { transform: 'scale(1.1)', boxShadow: '0 0 8px rgba(6,182,212,0.4)' },
                position: 'relative'
              }}>
                <Typography sx={{ color: isCompleted ? '#6ee7b7' : isInProgress ? '#fde047' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 600 }}>
                  {ch.chapterNumber}
                </Typography>
                {(hasCharacters || hasForeshadowings) && (
                  <Box sx={{
                    position: 'absolute', top: -2, right: -2,
                    width: hasCharacters && hasForeshadowings ? 8 : 6,
                    height: hasCharacters && hasForeshadowings ? 8 : 6,
                    borderRadius: '50%',
                    bgcolor: hasCharacters && hasForeshadowings ? '#ffd700' : hasForeshadowings ? '#ec4899' : '#00f0ff',
                    border: '1px solid rgba(10,11,18,0.8)'
                  }} />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  // 计算全局统计
  // ── 章节索引缓存（性能优化）──────────────────────────────────────
  // 伏笔/支线卡片渲染时若对每张卡片执行 chapters.find/filter，复杂度为 O(卡片数×章节数)，
  // 大纲解析出上百伏笔+上千章节时单次渲染可达数十万次比较，是界面卡顿主因。
  // 此处一次性构建索引：Map 查找 O(1)，整体降为 O(章节数)。
  const chapterIndexes = useMemo(() => {
    const byId = new Map();
    const byForeshadowingId = new Map(); // fsId -> chapters[]（含 plotPoints 关联）
    const bySubplotId = new Map();       // spId -> chapters[]
    for (const c of chapters) {
      byId.set(c._id, c);
      // 伏笔：直接标记 + plotPoints 内引用 双通道归并
      if (Array.isArray(c.foreshadowingIds)) {
        for (const fid of c.foreshadowingIds) {
          if (!byForeshadowingId.has(fid)) byForeshadowingId.set(fid, []);
          byForeshadowingId.get(fid).push(c);
        }
      }
      const pts = c.outline?.plotPoints;
      if (Array.isArray(pts)) {
        const seen = new Set();
        for (const pt of pts) {
          for (const fid of (pt.foreshadowingIds || [])) {
            if (seen.has(fid)) continue;
            seen.add(fid);
            if (!byForeshadowingId.has(fid)) byForeshadowingId.set(fid, []);
            byForeshadowingId.get(fid).push(c);
          }
        }
      }
      // 支线：章节 subplotIds + 支线 relatedChapterIds 双向引用
      if (Array.isArray(c.subplotIds)) {
        for (const sid of c.subplotIds) {
          if (!bySubplotId.has(sid)) bySubplotId.set(sid, []);
          bySubplotId.get(sid).push(c);
        }
      }
    }
    // 支线 relatedChapterIds 由支线侧持有，需反向补录
    for (const s of (outlines[0]?.subPlots || [])) {
      const sid = s.id || s._id;
      if (!sid || !Array.isArray(s.relatedChapterIds)) continue;
      const bucket = bySubplotId.get(sid) || [];
      const have = new Set(bucket.map(c => c._id));
      for (const cid of s.relatedChapterIds) {
        const c = byId.get(cid);
        if (c && !have.has(cid)) { bucket.push(c); have.add(cid); }
      }
      bySubplotId.set(sid, bucket);
    }
    return { byId, byForeshadowingId, bySubplotId };
  }, [chapters, outlines]);

  // ── 大列表分批渲染：伏笔/支线超过阈值时只渲染前 N 条，点击加载更多 ──
  const PROGRESS_LIST_PAGE_SIZE = 50;
  const [fsVisibleCount, setFsVisibleCount] = useState(PROGRESS_LIST_PAGE_SIZE);
  const [spVisibleCount, setSpVisibleCount] = useState(PROGRESS_LIST_PAGE_SIZE);
  // 数据量超阈值时禁用 framer-motion layout 动画（大列表下每帧重排是卡顿元凶）
  const heavyFsList = (outlines[0]?.foreshadowing || []).length > PROGRESS_LIST_PAGE_SIZE;
  const heavySpList = (outlines[0]?.subPlots || []).length > PROGRESS_LIST_PAGE_SIZE;

  const progressStats = useMemo(() => {
    const totalChapters = chapters.length;
    const completedChapters = chapters.filter(c => c.status === 'completed' || c.status === 'revised').length;
    const inProgressChapters = chapters.filter(c => c.status === 'in_progress').length;
    const outlineOnlyChapters = chapters.filter(c => c.status === 'outline_only').length;
    const totalVolumes = volumes.length;
    const totalCharacters = characters.length;
    const totalForeshadowings = (outlines[0]?.foreshadowing || []).length;
    const totalSubplots = (outlines[0]?.subPlots || []).length;
    const revealedForeshadowings = (outlines[0]?.foreshadowing || []).filter(f => f.status === 'revealed').length;
    const pendingForeshadowings = (outlines[0]?.foreshadowing || []).filter(f => ['pending', 'planted', 'advancing'].includes(f.status)).length;
    const activeSubplots = (outlines[0]?.subPlots || []).filter(s => s.status === 'active').length;
    const completedSubplots = (outlines[0]?.subPlots || []).filter(s => s.status === 'completed').length;

    // 角色出场统计（检查章节 characterIds 和 plotPoints[].characterIds）
    const charInChapter = (chapter, charId) =>
      chapter.characterIds?.includes(charId) ||
      (chapter.outline?.plotPoints || []).some(pt => pt.characterIds?.includes(charId));
    const characterAppearances = characters.map(char => ({
      ...char,
      appearances: chapters.filter(c => charInChapter(c, char._id)).length,
      firstAppearance: chapters.find(c => charInChapter(c, char._id))?.chapterNumber || null
    }));

    // 生成一致性警告
    const warnings = [];
    // 1. 伏笔揭露时机检查
    (outlines[0]?.foreshadowing || []).forEach(f => {
      if (f.revealChapterId) {
        const revealChapter = chapters.find(c => c._id === f.revealChapterId);
        if (revealChapter && f.status !== 'revealed') {
          warnings.push({
            type: 'warning',
            category: '伏笔',
            title: `伏笔「${f.title}」计划揭露但未标记`,
            description: `计划在第${revealChapter.chapterNumber}章揭露，但当前状态为"${f.status}"`,
            chapterId: f.revealChapterId,
            foreshadowingId: f.id
          });
        }
      }
    });
    // 2. 支线收尾检查
    (outlines[0]?.subPlots || []).forEach(s => {
      if (s.relatedChapterIds && s.relatedChapterIds.length > 0) {
        const lastChapter = chapters.find(c => c._id === s.relatedChapterIds[s.relatedChapterIds.length - 1]);
        if (lastChapter && chapters.find(c => c._id === lastChapter._id) && s.status !== 'completed') {
          // 检查该章节是否已写完
          if (lastChapter.status === 'completed' || lastChapter.status === 'revised') {
            warnings.push({
              type: 'info',
              category: '支线',
              title: `支线「${s.title}」收尾章节已完成`,
              description: `支线"${s.title}"关联的最后一个章节（第${lastChapter.chapterNumber}章）已完成，建议检查是否需要标记收尾`,
              chapterId: lastChapter._id,
              subplotId: s.id
            });
          }
        }
      }
    });
    // 3. 角色出场检查
    characters.forEach(char => {
      const firstChapter = chapters.find(c => charInChapter(c, char._id));
      if (!firstChapter && totalChapters > 0) {
        warnings.push({
          type: 'warning',
          category: '角色',
          title: `角色「${char.name}」从未在任何章节中出现`,
          description: `角色"${char.name}"已创建${char.appearanceChapterIds?.length > 0 ? `（计划出场${char.appearanceChapterIds.length}次）` : '，但尚未在任何章节中关联'}`
        });
      }
    });
    // 4. 章节状态不连续检查
    const sortedChapters = [...chapters].sort((a, b) => a.chapterNumber - b.chapterNumber);
    for (let i = 1; i < sortedChapters.length; i++) {
      const prev = sortedChapters[i - 1];
      const curr = sortedChapters[i];
      if ((prev.status === 'completed' || prev.status === 'revised') &&
          curr.status === 'outline_only' &&
          curr.chapterNumber - prev.chapterNumber > 3) {
        warnings.push({
          type: 'info',
          category: '章节',
          title: `章节${prev.chapterNumber}到${curr.chapterNumber}之间存在空白`,
          description: `第${prev.chapterNumber}章已完成，但第${curr.chapterNumber}章仍为空白章纲，建议检查是否需要填充`,
          chapterId: curr._id
        });
      }
    }

    return {
      totalChapters, completedChapters, inProgressChapters, outlineOnlyChapters,
      totalVolumes, totalCharacters, totalForeshadowings, totalSubplots,
      revealedForeshadowings, pendingForeshadowings, activeSubplots, completedSubplots,
      characterAppearances, warnings
    };
  }, [chapters, volumes, characters, outlines]);

  // 全局进度 Tab 状态
  const [progressTab, setProgressTab] = useState(0);
  // 全局进度数据（从后端 AssistProgress 获取）
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  // 编辑弹窗状态
  const [editForeshadowingDialog, setEditForeshadowingDialog] = useState(null);  // 伏笔编辑弹窗
  const [editSubplotDialog, setEditSubplotDialog] = useState(null);              // 支线编辑弹窗
  const [editCharacterDialog, setEditCharacterDialog] = useState(null);         // 角色编辑弹窗（全局进度）
  const [endingDialogOpen, setEndingDialogOpen] = useState(false);              // 收尾标记弹窗
  const [endingChaptersInput, setEndingChaptersInput] = useState(10);            // 距结尾章数输入
  const [editRelationDialog, setEditRelationDialog] = useState(null);             // 关系变化弹窗
  const [editChapterStatusDialog, setEditChapterStatusDialog] = useState(null);  // 章节状态弹窗
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);   // 删除章节确认弹窗
  const [confirmDeleteChapter, setConfirmDeleteChapter] = useState(null);           // 待删除章节对象
  /** 按章记录角色状态（全局进度时间轴） */
  const [characterChapterStateDialog, setCharacterChapterStateDialog] = useState({
    open: false,
    characterId: null,
    characterName: '',
    recordId: null,
    chapterNumber: '',
    chapterId: '',
    lifeStatus: 'alive',
    emotionStatus: '',
    narrativeNote: '',
  });
  /** 按章记录关系变化（全局进度时间轴） */
  const [relationshipChapterStateDialog, setRelationshipChapterStateDialog] = useState({
    open: false,
    fromCharacterId: null,
    fromCharacterName: '',
    toCharacterId: null,
    toCharacterName: '',
    relationType: '其他',
    recordId: null,
    chapterNumber: '',
    chapterId: '',
    fromStatus: '陌生',
    toStatus: '陌生',
    reason: '',
  });
  /** 按章记录伏笔状态（全局进度时间轴） */
  const [foreshadowingChapterStateDialog, setForeshadowingChapterStateDialog] = useState({
    open: false,
    foreshadowingId: null,
    title: '',
    recordId: null,
    chapterNumber: '',
    chapterId: '',
    chapterTitle: '',
    status: 'pending',
    narrativeNote: '',
  });
  /** 按章记录支线进展（全局进度时间轴） */
  const [subplotChapterStateDialog, setSubplotChapterStateDialog] = useState({
    open: false,
    subplotId: null,
    title: '',
    recordId: null,
    chapterNumber: '',
    chapterId: '',
    chapterTitle: '',
    status: 'active',
    progress: '',
    narrativeNote: '',
  });

  // 加载全局进度数据
  const loadProgressData = useCallback(async () => {
    if (!assistNovelId) return;
    setLoadingProgress(true);
    try {
      const resp = await apiClient.get(`/assist/novels/${assistNovelId}/progress`);
      if (resp.data?.success) {
        console.log('[全局进度] ✅ 从服务器加载进度数据成功');
        console.log('[全局进度] 服务器返回数据:', JSON.stringify({
          mainLinePhase: resp.data.data?.mainLinePhase,
          mainLineChapterStates: resp.data.data?.mainLineChapterStates?.length,
          characterChapterStates: resp.data.data?.characterChapterStates?.length,
          relationshipChapterStates: resp.data.data?.relationshipChapterStates?.length,
          foreshadowingChapterStates: resp.data.data?.foreshadowingChapterStates?.length,
          subplotChapterStates: resp.data.data?.subplotChapterStates?.length,
          nextChapterCorePoints: resp.data.data?.nextChapterCorePoints?.length,
        }));
        setProgressData(resp.data.data);
      } else {
        console.warn('[全局进度] 服务器返回 success=false');
      }
    } catch (e) {
      console.error('[全局进度] ❌ 加载全局进度失败', e);
    } finally {
      setLoadingProgress(false);
    }
  }, [assistNovelId]);

  useEffect(() => { if (activePanel === 'progress') loadProgressData(); }, [activePanel, loadProgressData]);

  // 切换到对话面板时自动加载对话列表
  useEffect(() => {
    if (activePanel === 'chat' && conversations.length === 0) {
      loadConversations();
    }
  }, [activePanel]);

  // 全局进度自动同步（当章节或大纲数据变化时自动更新进度表）
  // ⚠️ 注意：时间轴字段（characterChapterStates/relationshipChapterStates/foreshadowingChapterStates/subplotChapterStates/mainLineChapterStates）
  //   由 autoSyncAfterChapterGenerate 动态写入，必须保留，不被此处的静态派生覆盖。
  //   此函数只负责派生"汇总型"字段。
  const syncProgressToState = useCallback(() => {
    // ══ 调试日志：检查同步时使用的大纲数据 ══
    console.log('[全局进度同步] 开始同步，检查数据源:');
    console.log('  outlines[0]?._id:', outlines[0]?._id);
    console.log('  outlines[0]?.subPlots 数量:', outlines[0]?.subPlots?.length || 0, '| 详情:', JSON.stringify(outlines[0]?.subPlots?.map(s => ({ title: s.title, status: s.status }))));
    console.log('  outlines[0]?.foreshadowing 数量:', outlines[0]?.foreshadowing?.length || 0, '| 详情:', JSON.stringify(outlines[0]?.foreshadowing?.map(f => ({ title: f.title, status: f.status }))));
    console.log('  chapters.length:', chapters.length);
    console.log('  characters.length:', characters.length);

    // 计算当前的角色出场数据
    const charInChapter = (chapter, charId) =>
      chapter.characterIds?.includes(charId) ||
      (chapter.outline?.plotPoints || []).some(pt => pt.characterIds?.includes(charId));

    const autoCharacterProgress = characters.map(char => ({
      characterId: char._id,
      characterName: char.name,
      appearanceCount: chapters.filter(c => charInChapter(c, char._id)).length,
      firstAppearance: chapters.find(c => charInChapter(c, char._id))?.chapterNumber || null,
      lastAppearance: [...chapters].reverse().find(c => charInChapter(c, char._id))?.chapterNumber || null,
      currentState: char.currentState || char.stateHistory?.[char.stateHistory?.length - 1]?.state || '',
      status: char.lifeStatus || 'alive'
    }));

    // 计算伏笔进度
    const autoForeshadowingProgress = (outlines[0]?.foreshadowing || []).map(f => {
      const fsId = f.id || f._id;
      const plantedChapter = f.plantedChapterId ? chapters.find(c => c._id === f.plantedChapterId) : null;
      const revealChapter = f.revealChapterId ? chapters.find(c => c._id === f.revealChapterId) : null;
      const advanceChapters = (f.advanceChapterIds || []).map(id => chapters.find(c => c._id === id)).filter(Boolean);
      const relatedChapters = chapters.filter(c =>
        c.foreshadowingIds?.includes(fsId) ||
        (c.outline?.plotPoints || []).some(pt => (pt.foreshadowingIds || []).includes(fsId))
      );
      return {
        foreshadowingId: fsId,
        title: f.title,
        status: f.status,
        plantedChapterId: f.plantedChapterId || null,
        plantedChapterNumber: f.plantedChapterNumber || plantedChapter?.chapterNumber || null,
        advanceChapterIds: f.advanceChapterIds || [],
        advanceChapterNumbers: f.advanceChapterNumbers || [],
        revealChapterId: f.revealChapterId || null,
        revealChapterNumber: f.revealChapterNumber || revealChapter?.chapterNumber || null,
        currentChapter: relatedChapters.length > 0 ? relatedChapters[relatedChapters.length - 1].chapterNumber : null,
        relatedChapterCount: relatedChapters.length
      };
    });

    // 计算支线进度
    const autoSubplotProgress = (outlines[0]?.subPlots || []).map(s => {
      const relatedChapters = chapters.filter(c =>
        c.subplotIds?.includes(s.id || s._id) ||
        (c.outline?.plotPoints || []).some(pt => pt.subplotIds?.includes(s.id || s._id))
      );
      return {
        subplotId: s.id || s._id,
        title: s.title,
        status: s.status,
        startChapterNumber: s.startChapterNumber || null,
        endChapterNumber: s.endChapterNumber || null,
        currentChapter: relatedChapters.length > 0 ? relatedChapters[relatedChapters.length - 1].chapterNumber : null,
        relatedChapterCount: relatedChapters.length
      };
    });

    // 计算章节进度统计
    const autoChapterProgress = chapters.map(ch => ({
      chapterId: ch._id,
      chapterNumber: ch.chapterNumber,
      title: ch.title || `第${ch.chapterNumber}章`,
      status: ch.status,
      characterIds: ch.characterIds || [],
      foreshadowingIds: ch.foreshadowingIds || [],
      subplotIds: ch.subplotIds || [],
      wordCount: ch.wordCount || 0
    }));

    return {
      characterProgress: autoCharacterProgress,
      foreshadowingProgress: autoForeshadowingProgress,
      subplotProgress: autoSubplotProgress,
      chapterProgress: autoChapterProgress,
      lastAutoSync: new Date().toISOString()
    };
  }, [characters, chapters, outlines]);

  // ── 从时间轴数据动态派生汇总数据 ──────────────────────────────────────
  // 每次 progressData（时间轴部分）更新后，重新派生：
  //   characterAppearances = 从 characterChapterStates 动态推算（首次出现 + 最后出现）
  //   relationshipChanges  = 从 relationshipChapterStates 动态推算（关系首次出现 + 最新状态）
  //   foreshadowingProgress = 从 foreshadowingChapterStates + 大纲合并
  const deriveFromTimeAxis = useCallback((pg) => {
    if (!pg) return {};

    // ── 角色出场：从 characterChapterStates 推算 ─────────────────────
    const charStates = pg.characterChapterStates || [];
    const derivedCharAppearances = {};
    charStates.forEach(entry => {
      if (!derivedCharAppearances[entry.characterId]) {
        derivedCharAppearances[entry.characterId] = {
          characterId: entry.characterId,
          name: entry.narrativeNote?.replace(/^角色.*?：/, '').split('→')[0] || '',
          firstAppearChapterId: entry.chapterId,
          firstAppearChapterNumber: entry.chapterNumber,
          lastSeenChapterId: entry.chapterId,
          lastSeenChapterNumber: entry.chapterNumber,
          isAlive: entry.lifeStatus !== 'dead',
        };
      } else {
        const existing = derivedCharAppearances[entry.characterId];
        if (entry.chapterNumber < existing.firstAppearChapterNumber) {
          existing.firstAppearChapterId = entry.chapterId;
          existing.firstAppearChapterNumber = entry.chapterNumber;
        }
        if (entry.chapterNumber > existing.lastSeenChapterNumber) {
          existing.lastSeenChapterId = entry.chapterId;
          existing.lastSeenChapterNumber = entry.chapterNumber;
        }
        if (entry.lifeStatus === 'dead') existing.isAlive = false;
      }
    });

    // ── 关系变化：从 relationshipChapterStates 推算 ─────────────────
    const relStates = pg.relationshipChapterStates || [];
    const derivedRelationshipChanges = {};
    relStates.forEach(entry => {
      const key = `${entry.fromCharacterId}__${entry.toCharacterId}`;
      if (!derivedRelationshipChanges[key]) {
        derivedRelationshipChanges[key] = {
          id: entry.id || key,
          fromCharacterId: entry.fromCharacterId,
          fromCharacterName: entry.fromCharacterName || '',
          toCharacterId: entry.toCharacterId,
          toCharacterName: entry.toCharacterName || '',
          relationType: entry.relationType || '其他',
          fromStatus: entry.fromStatus || '未知',
          toStatus: entry.toStatus || '未知',
          startChapterId: entry.chapterId,
          startChapterNumber: entry.chapterNumber,
          latestChapterId: entry.chapterId,
          latestChapterNumber: entry.chapterNumber,
          reason: entry.reason || '',
          updatedAt: entry.updatedAt || new Date().toISOString()
        };
      } else {
        const existing = derivedRelationshipChanges[key];
        if (entry.chapterNumber < existing.startChapterNumber) {
          existing.startChapterId = entry.chapterId;
          existing.startChapterNumber = entry.chapterNumber;
        }
        if (entry.chapterNumber > existing.latestChapterNumber) {
          existing.latestChapterId = entry.chapterId;
          existing.latestChapterNumber = entry.chapterNumber;
          existing.toStatus = entry.toStatus;
          existing.reason = entry.reason;
          existing.updatedAt = entry.updatedAt || new Date().toISOString();
        }
      }
    });

    return {
      characterAppearances: Object.values(derivedCharAppearances),
      relationshipChanges: Object.values(derivedRelationshipChanges),
    };
  }, []);

  // 当章节/大纲数据变化时，只更新汇总字段（时间轴不覆盖）
  useEffect(() => {
    if (!assistNovelId) return;

    const autoSyncData = syncProgressToState();
    setProgressData(prev => {
      if (!prev) {
        return {
          ...autoSyncData,
          characterChapterStates: [],
          relationshipChapterStates: [],
          foreshadowingChapterStates: [],
          subplotChapterStates: [],
          mainLineChapterStates: [],
        };
      }
      // 保护所有时间轴字段：只更新汇总字段，不碰时间轴
      const derived = deriveFromTimeAxis(prev);
      return {
        ...prev,
        ...autoSyncData,
        // 时间轴字段强制保留，防止被静默覆盖
        characterChapterStates: Array.isArray(prev.characterChapterStates) ? prev.characterChapterStates : [],
        relationshipChapterStates: Array.isArray(prev.relationshipChapterStates) ? prev.relationshipChapterStates : [],
        foreshadowingChapterStates: Array.isArray(prev.foreshadowingChapterStates) ? prev.foreshadowingChapterStates : [],
        subplotChapterStates: Array.isArray(prev.subplotChapterStates) ? prev.subplotChapterStates : [],
        mainLineChapterStates: Array.isArray(prev.mainLineChapterStates) ? prev.mainLineChapterStates : [],
        // 汇总字段由派生函数更新（但以 prev 中的为准，如果 prev 有数据的话）
        characterAppearances: prev.characterAppearances || derived.characterAppearances,
        relationshipChanges: prev.relationshipChanges || derived.relationshipChanges,
      };
    });
  }, [assistNovelId, chapters, characters, outlines, syncProgressToState, deriveFromTimeAxis]);

  // 保存全局进度（增量更新指定字段）
  const saveProgressData = useCallback(async (fieldName, data) => {
    if (!assistNovelId) return;
    try {
      const payload = { [fieldName]: data };
      console.log(`[全局进度] 💾 saveProgressData 开始保存字段="${fieldName}"，数据条数=${Array.isArray(data) ? data.length : '非数组'}`);
      const resp = await apiClient.post(`/assist/novels/${assistNovelId}/progress`, payload);
      if (resp.data?.success) {
        console.log(`[全局进度] 💾 saveProgressData 成功保存字段="${fieldName}"`);
        setProgressData(resp.data.data);
      } else {
        console.warn(`[全局进度] 💾 saveProgressData 失败：字段="${fieldName}"，返回success=false`);
      }
    } catch (e) {
      console.error(`[全局进度] 💾 saveProgressData 异常：字段="${fieldName}"，错误:`, e);
      setSnackbar({ open: true, message: '保存失败：' + (e.response?.data?.message || e.message), severity: 'error' });
    }
  }, [assistNovelId]);

  // 获取当前主线阶段（根据进度表或章节完成度推断）
  const getMainLinePhase = useCallback(() => {
    const pg = progressData || {};
    if (pg.mainLinePhase) return pg.mainLinePhase;
    const total = chapters.length;
    if (total === 0) return 'beginning';
    const completed = chapters.filter(c => c.status === 'completed' || c.status === 'revised').length;
    const ratio = completed / total;
    if (ratio < 0.1) return 'beginning';
    if (ratio < 0.5) return 'development';
    if (ratio < 0.85) return 'climax';
    return 'ending';
  }, [progressData, chapters]);

  // 更新主线文字描述
  const handleUpdateMainLine = useCallback(async (newLine) => {
    if (!assistNovelId || !outlines[0]) return;
    try {
      const outline = { ...outlines[0], mainPlot: { ...outlines[0].mainPlot, storyline: newLine } };
      await apiClient.post(`/assist/novels/${assistNovelId}/outline`, outline);
      setOutlines([outline]);
      setSnackbar({ open: true, message: '主线已更新', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    }
  }, [assistNovelId, outlines]);

  // 更新主线阶段
  const handleUpdateMainLinePhase = useCallback(async (phase) => {
    if (!assistNovelId) return;
    try {
      await apiClient.post(`/assist/novels/${assistNovelId}/progress`, { mainLinePhase: phase });
      setProgressData(prev => prev ? { ...prev, mainLinePhase: phase } : { mainLinePhase: phase });
      const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
      setSnackbar({ open: true, message: `主线进度已标记为「${phaseLabels[phase]}」阶段`, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: '保存失败', severity: 'error' });
    }
  }, [assistNovelId]);

  /**
   * 全自动跟进函数：生成正文后自动更新全局进度
   * 包括：主线阶段、角色状态、关系变化、下章核心要点与衔接内容
   */
  const autoSyncAfterChapterGenerate = useCallback(async (chapterContent, currentChapter) => {
    if (!assistNovelId || !autoSyncAfterGenerate || !chapterContent || !currentChapter) return;

    const currentChapterNum = currentChapter.chapterNumber || 1;
    console.log(`[全局进度] ====== 开始分析第${currentChapterNum}章 ======`);
    console.log(`[全局进度] assistNovelId=${assistNovelId}, autoSync=${autoSyncAfterGenerate}`);
    console.log(`[全局进度] 正文长度=${chapterContent.length}字`);
    console.log(`[全局进度] 章节ID=${currentChapter._id}, 出场角色IDs=${JSON.stringify(currentChapter.characterIds || [])}`);
    // ── 入口状态快照 ──────────────────────────────────────────
    console.group('[全局进度] 【入口状态快照】');
    console.log('[全局进度] progressData.mainLinePhase=', progressData?.mainLinePhase);
    console.log('[全局进度] progressData.mainLineChapterStates=', JSON.stringify(progressData?.mainLineChapterStates || []));
    console.log('[全局进度] progressData.characterChapterStates 条数=', (progressData?.characterChapterStates || []).length, '示例:', JSON.stringify((progressData?.characterChapterStates || []).slice(0, 2)));
    console.log('[全局进度] progressData.relationshipChapterStates 条数=', (progressData?.relationshipChapterStates || []).length, '示例:', JSON.stringify((progressData?.relationshipChapterStates || []).slice(0, 2)));
    console.log('[全局进度] progressData.foreshadowingChapterStates 条数=', (progressData?.foreshadowingChapterStates || []).length, '示例:', JSON.stringify((progressData?.foreshadowingChapterStates || []).slice(0, 2)));
    console.log('[全局进度] progressData.subplotChapterStates 条数=', (progressData?.subplotChapterStates || []).length, '示例:', JSON.stringify((progressData?.subplotChapterStates || []).slice(0, 2)));
    console.log('[全局进度] characters 全部角色=', characters.map(c => c.name).join(', '));
    console.log('[全局进度] chapters 总章节数=', chapters.length);
    console.log('[全局进度] outlines[0] subPlots=', JSON.stringify((outlines[0]?.subPlots || []).map(s => ({ title: s.title, status: s.status, startCh: s.startChapterNumber, endCh: s.endChapterNumber }))));
    console.log('[全局进度] outlines[0] foreshadowing=', JSON.stringify((outlines[0]?.foreshadowing || []).map(f => ({ title: f.title, status: f.status, planted: f.plantedChapterNumber, reveal: f.revealChapterNumber }))));
    console.groupEnd();

    // 设置同步中状态，防止竞态条件
    setGlobalProgressSyncing(true);

    try {
      // ── 1. AI 分析本章内容 ──────────────────────────────────────────
      const novelTitle = novel?.title || '未知作品';
      const totalChapters = outlineFormData.chapterCount || chapters.length || 50;
      const currentPhase = getMainLinePhase();
      console.log(`[全局进度] 【阶段AI】准备调用AI分析，当前currentPhase=${currentPhase}, totalChapters=${totalChapters}`);
      const currentChapterNum = currentChapter.chapterNumber || 1;

      // 收集本章涉及的角色信息
      const linkedCharIds = currentChapter.characterIds || [];
      const linkedChars = linkedCharIds.map(id => {
        const char = characters.find(c => c._id === id);
        return char ? { id: char._id, name: char.name, lifeStatus: char.lifeStatus || 'alive', emotionStatus: char.emotionStatus || '', currentState: char.currentState || '' } : null;
      }).filter(Boolean);

      // 收集已存在的关系变化记录
      const existingChanges = progressData?.relationshipChanges || [];
      const existingRcs = existingChanges.map(rc => `${rc.fromCharacterName}与${rc.toCharacterName}：${rc.fromStatus}→${rc.toStatus}`).join('\n');

      // 收集大纲主线信息
      const mainPlot = outlines[0]?.mainPlot?.storyline || '暂无主线大纲';
      const subplots = (outlines[0]?.subPlots || []).map(s => {
        const chRange = (s.startChapterNumber && s.endChapterNumber) ? ` [第${s.startChapterNumber}~${s.endChapterNumber}章]` : ' [章节未定]';
        return `${s.title}${chRange}（${s.status || 'pending'}）：${s.description || ''}`;
      }).join('\n') || '暂无';

      // 收集大纲中定义的伏笔
      const foreshadowings = (outlines[0]?.foreshadowing || []).map(f => {
        const planInfo = f.plantedChapterNumber || f.revealChapterNumber
          ? ` [铺垫第${f.plantedChapterNumber || '?'}章/揭示第${f.revealChapterNumber || '?'}章]`
          : '';
        return `[ID:${f.id || f._id}] ${f.title}（${f.status || '未揭示'}）${planInfo}：${f.description || ''}`;
      }).join('\n') || '暂无';

      // 查找下章
      const nextChapter = chapters.find(c => c.chapterNumber === currentChapterNum + 1);

      // ── 前10章正文摘要（防止分析与前文矛盾）─────────────────────
      const prevAll = chapters.filter(c => c.volumeId === currentChapter.volumeId && c.chapterNumber < currentChapterNum).sort((a, b) => b.chapterNumber - a.chapterNumber);
      const prev10 = prevAll.slice(0, 10);
      const prev10Block = prev10.length > 0
        ? prev10.map(ch => {
            const chCore = ch.outline?.coreEvent || '无';
            const chConn = ch.outline?.connectionToPrevious || '';
            const chHook = ch.outline?.hookForNext || '';
            const chChars = (ch.outline?.characterIds || []).map(id => characters.find(c => c._id === id)?.name || '?').join('、');
            const chContent = ch.content?.trim() || '';
            const chSummary = chContent
              ? `正文摘要：${chContent.slice(0, 200)}${chContent.length > 200 ? '……（略）' : ''}`
              : '（无正文）';
            return `第${ch.chapterNumber}章「${ch.title || '未命名'}」\n核心事件：${chCore}${chConn ? '\n承接上文：' + chConn : ''}${chHook ? '\n埋钩：' + chHook : ''}${chChars ? '\n出场角色：' + chChars : ''}\n${chSummary}`;
          }).join('\n\n')
        : '（暂无前10章）';

      // 对分析内容做智能截断（超长章节取前中后三段，防止AI只分析开头）
      const MAX_ANALYSIS_CHARS = 8000;
      let contentForAnalysis = chapterContent;
      if (chapterContent.length > MAX_ANALYSIS_CHARS) {
        const head = chapterContent.slice(0, 2500);
        const mid = chapterContent.slice(Math.floor(chapterContent.length * 0.35), Math.floor(chapterContent.length * 0.35) + 3000);
        const tail = chapterContent.slice(-2500);
        contentForAnalysis = `${head}\n\n……（中间 ${chapterContent.length - 5000} 字已省略）……\n\n${tail}`;
      }

      const analysisPrompt = `【小说创作辅助分析任务】

你是专业的网文创作助手。请仔细分析以下章节内容，然后严格按JSON格式输出分析结果（只输出JSON，不要任何其他内容）。

【小说信息】
小说名称：《${novelTitle}》
当前章节：第${currentChapterNum}章
小说总章节数：约${totalChapters}章
当前主线阶段：${currentPhase === 'beginning' ? '开局' : currentPhase === 'development' ? '发展' : currentPhase === 'climax' ? '高潮' : '结局'}
下一章（如果有）：${nextChapter ? `第${nextChapter.chapterNumber}章「${nextChapter.title || '未命名'}」` : '（已是最后一章）'}

【本章正文内容】
${contentForAnalysis}

【本章章纲信息】
核心事件：${currentChapter.outline?.coreEvent || wizardCoreEvent || '未填写'}
情节点：${(currentChapter.outline?.plotPoints || []).map(p => `[${p.type || '情节'}] ${p.content || ''}`).join('；') || '未填写'}
承上描述：${currentChapter.outline?.connectionToPrevious || '未填写'}
为下章埋钩：${currentChapter.outline?.hookForNext || '未填写'}

【已出场的角色】（本章章纲中选定的角色）
${linkedChars.length > 0 ? linkedChars.map(c => `· ${c.name}（状态：${c.currentState || c.lifeStatus === 'alive' ? '正常' : c.lifeStatus}，情感：${c.emotionStatus || '未记录'}）`).join('\n') : '暂无出场角色记录'}

【全部角色库】${(() => {
  const timeline = progressData?.characterChapterStates || [];
  return (characters || []).map(c => {
    const latest = timeline.filter(t => t.characterId === c._id).sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
    const lastCh = latest ? `上一章：第${latest.chapterNumber}章（${latest.lifeStatus === 'alive' ? '存活' : latest.lifeStatus}，${latest.emotionStatus || '情感未记录'}）` : '尚未在任何章节出场';
    return `· [ID:${c._id}] ${c.name} | ${lastCh}`;
  }).join('\n') || '暂无';
})()}

【已有的关系变化历史】
${existingRcs || '暂无记录'}

【主线大纲】
${mainPlot}

【全部支线及章节范围】
${(() => {
  const total = outlineFormData.chapterCount || chapters.length || 100;
  return (outlines[0]?.subPlots || []).map(s => {
    const start = s.startChapterNumber || s.startChapter || '?';
    const end = s.endChapterNumber || s.endChapter || '?';
    const rangeNote = currentChapterNum >= start && currentChapterNum <= end
      ? ` ← 【本章第${currentChapterNum}章在本支线范围内】`
      : currentChapterNum > end
      ? ` ← 【本支线已结束（第${end}章完结）】`
      : '';
    return `· [ID:${s.id || s._id}] ${s.title} [第${start}~${end}章]（${s.status || 'pending'}）${rangeNote}`;
  }).join('\n') || '暂无';
})()}

【伏笔列表】
${foreshadowings}

═══════════════════════════════════════
【当前章节与支线的对应关系】
本章是第${currentChapterNum}章，请精确判断每个支线在本章的状态：
${(() => {
  const total = outlineFormData.chapterCount || chapters.length || 100;
  const inRangeSubplots = (outlines[0]?.subPlots || []).filter(s => {
    const start = s.startChapterNumber || s.startChapter || 1;
    const end = s.endChapterNumber || s.endChapter || 9999;
    return currentChapterNum >= start && currentChapterNum <= end;
  });
  return inRangeSubplots.length > 0 ? inRangeSubplots.map(s => {
    const start = s.startChapterNumber || s.startChapter || 1;
    const end = s.endChapterNumber || s.endChapter || 9999;
    const remaining = end - currentChapterNum;
    let hint = '';
    if (currentChapterNum === start) hint = ' ← 本章是支线开头章节，状态应为 active';
    else if (remaining <= 2 && remaining > 0) hint = ` ← 接近支线结尾（第${end}章），应在本章收尾，状态应为 completed`;
    else hint = ` ← 支线进行中（还剩${remaining}章），状态应为 active`;
    return `· ${s.title}（大纲计划第${start}~${end}章）${hint}`;
  }).join('\n') : '（当前章节不在任何支线范围内）';
})()}

═══════════════════════════════════════
【当前章节与伏笔的对应关系】
本章是第${currentChapterNum}章，以下伏笔按计划应在本章被处理（必须精确判断状态）：
${(() => {
  const list = (outlines[0]?.foreshadowing || []).filter(f => {
    if (f.status === 'revealed') return false;
    const p = f.plantedChapterId ? (chapters.find(c => c._id === f.plantedChapterId)?.chapterNumber || f.plantedChapterNumber) : f.plantedChapterNumber;
    const r = f.revealChapterId ? (chapters.find(c => c._id === f.revealChapterId)?.chapterNumber || f.revealChapterNumber) : f.revealChapterNumber;
    if (!p && !r) return false;
    if (p && currentChapterNum < p - 3) return false;
    if (r && currentChapterNum > r + 2) return false;
    return true;
  });
  return list.length > 0 ? list.map(f => {
    const p = f.plantedChapterId ? (chapters.find(c => c._id === f.plantedChapterId)?.chapterNumber || f.plantedChapterNumber) : f.plantedChapterNumber;
    const r = f.revealChapterId ? (chapters.find(c => c._id === f.revealChapterId)?.chapterNumber || f.revealChapterNumber) : f.revealChapterNumber;
    const currentFs = progressData?.foreshadowingChapterStates?.filter(x => x.foreshadowingId === (f.id || f._id)).sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
    const isPlanted = currentFs?.status === 'planted' || f.status === 'planted';
    const isAdvancing = isPlanted && f.status !== 'revealed';
    let action = '';
    if (p && currentChapterNum === p) action = ' ← 本章是铺垫章节，必须在正文中铺垫，newStatus=planted';
    else if (r && currentChapterNum === r) action = ' ← 本章是揭示章节，必须在正文中揭示，newStatus=revealed（必须回填 revealChapterId）';
    else if (isAdvancing) action = ' ← 伏笔已铺垫，本章正在推进情节，newStatus=advancing';
    return `· ${f.title}（铺垫第${p || '?'}章，揭示第${r || '?'}章，当前状态：${f.status || 'pending'}）${action}`;
  }).join('\n') : '（无在本章需要处理的伏笔）';
})()}

═══════════════════════════════════════
【前10章故事脉络】（帮助你理解故事演进，防止本章分析与前文矛盾）
${prev10Block}

═══════════════════════════════════════
【已有的伏笔状态变化历史】← 判断本章伏笔状态变化时必须参考此历史
${(progressData?.foreshadowingChapterStates || []).length > 0
  ? (progressData?.foreshadowingChapterStates || []).slice(-10).map(f => {
      const statusLabel = f.status === 'planted' ? '已铺垫' : f.status === 'revealed' ? '已揭示' : f.status === 'advancing' ? '推进中' : f.status || '未知';
      return `· 第${f.chapterNumber}章：「${f.foreshadowingTitle || f.title || '?'}」→ ${statusLabel} ${f.note || ''}`;
    }).join('\n')
  : '暂无记录'}

【已有的支线进展历史】← 判断本章支线状态变化时必须参考此历史
${(progressData?.subplotChapterStates || []).length > 0
  ? (progressData?.subplotChapterStates || []).slice(-10).map(s => {
      const statusLabel = s.status === 'active' ? '进行中' : s.status === 'completed' ? '已完成' : s.status || '未知';
      return `· 第${s.chapterNumber}章 ·「${s.subplotTitle || s.title || '?'}」→ ${statusLabel} ${s.note || ''}`;
    }).join('\n')
  : '暂无记录'}

═══════════════════════════════════════
请分析本章内容后，严格按以下JSON格式输出：

{
  "mainLinePhase": "推断的主线阶段（beginning发展development高潮climax结局ending）",
  "characterStateChanges": [
    {
      "characterId": "角色数据库ID（格式如 char_xxx，来源于【全部角色库】中的[ID:xxx]）",
      "characterName": "角色名",
      "previousState": "变化前状态（如果本章该角色状态无变化则填\"无变化\"）",
      "newState": "变化后状态（如果本章该角色状态无变化则填\"无变化\"）",
      "lifeStatus": "alive受伤injured被俘captured失踪missing死亡dead无变化nochange",
      "emotionStatus": "情感状态（无变化填\"无变化\"）",
      "description": "变化描述（如果无变化填\"该角色本章未发生状态变化\"）"
    }
  ],
  "relationshipChanges": [
    {
      "fromCharacterId": "角色A的数据库ID（格式如 char_xxx，来源于【全部角色库】中的[ID:xxx]）",
      "fromCharacterName": "角色A名",
      "toCharacterId": "角色B的数据库ID（格式如 char_xxx，来源于【全部角色库】中的[ID:xxx]）",
      "toCharacterName": "角色B名",
      "relationType": "关系类型（恋人/朋友/仇人导师家人宿敌暗恋恩人陌路其他）",
      "fromStatus": "变化前关系状态（如果本章该关系无变化则填\"无变化\"）",
      "toStatus": "变化后关系状态（如果本章该关系无变化则填\"无变化\"）",
      "reason": "变化原因（如果无变化填\"该关系对本章无变化\"）"
    }
  ],
  "nextChapterCorePoints": [
    {
      "point": "本章结尾后，下一章需要推进的1-2个核心情节要点（每个要点10-30字）",
      "characters": ["涉及的已出场角色名，数组可为空但不能省略"]
    }
  ],
  "nextChapterConnection": "下一章（如果有）如何自然衔接本章结尾的内容，包括时间、地点、人物状态的承接描述",
  "chapterEndingHook": "本章结尾的悬念钩子描写，1-2句话，需要制造紧张感或留下未解之谜，吸引读者继续阅读",
  "foreshadowingStateChanges": [
    {
      "foreshadowingId": "伏笔数据库ID（格式如 foreshadowing_xxx，来源于【伏笔列表】中的[ID:xxx]）",
      "foreshadowingTitle": "伏笔标题（必须与【伏笔列表】中的标题完全一致）",
      "newStatus": "pending铺垫未执行/planted已铺垫/advancing推进中/revealed已揭示/forgotten已遗忘",
      "plantedChapterNumber": "如果本章铺垫了此伏笔，填写本章章节号（数字），否则填null",
      "revealChapterNumber": "如果本章揭示了此伏笔，填写本章章节号（数字），否则填null",
      "narrativeNote": "本章中如何处理此伏笔的描述"
    }
  ],
  "subplotStateChanges": [
    {
      "subplotId": "支线数据库ID（格式如 subplot_xxx，来源于【全部支线及章节范围】中的[ID:xxx]）",
      "subplotTitle": "支线标题（必须与【全部支线及章节范围】中的标题完全一致）",
      "newStatus": "pending未开始/active进行中/completed已完成/abandoned已放弃",
      "progress": "本章中此支线的进展描述",
      "narrativeNote": "本章中支线如何推进的描述"
    }
  ]
}

注意：
1. mainLinePhase 严格按照章节进度推断：第1-10%章为开局，10-50%为发展，50-85%为高潮，85-100%为结局
2. 【重要】characterStateChanges 和 relationshipChanges 采用动态记录：
   - 只要角色在本章有出场（含状态无变化），就要记录，填"无变化"即可
   - 不要只报告"有变化"的情况，无变化也必须记录，确保每章时间轴完整
   - 这用于构建全局进度时间轴，必须完整记录
3. 【重要·识别新角色】：如果正文中出现了【全部角色库】里标记为"尚未在任何章节出场"的角色，说明该角色在本章首次出场，应将其加入 characterStateChanges，lifeStatus 填 alive，previousState 填"首次出场"，newState 填本章该角色的具体状态
4. 【重要·伏笔状态判断】：
   - 铺垫章节（【当前章节与伏笔的对应关系】中标记为"铺垫章节"的）：必须将 newStatus 填 planted
   - 铺垫后的推进章节（伏笔已 planted 但尚未 reveal，且本章正在推进相关情节）：newStatus 填 advancing
   - 揭示章节（标记为"揭示章节"的）：newStatus 填 revealed，并必须回填 revealChapterId 为本章章节号
   - 不在本章处理范围内的伏笔：不要加入 foreshadowingStateChanges
5. 【重要·支线状态判断】：
   - 支线开头章节（【当前章节与支线的对应关系】中标记为"本章是支线开头章节"的）：newStatus 填 active
   - 支线进行中：newStatus 填 active
   - 支线收尾章节（标记为"应在本章收尾"的）：newStatus 填 completed
   - 不在本章范围内的支线不要加入 subplotStateChanges
6. nextChapterCorePoints 和 nextChapterConnection 如果没有下一章则填"（已是最后一章）"
7. nextChapterCorePoints 的 characters 数组必须从【已出场的角色】中选取角色名，本章没有出场的角色不能出现
8. foreshadowingStateChanges 中的伏笔必须从【伏笔列表】中选取，不存在的新伏笔不要添加
9. subplotStateChanges 中的支线必须从【全部支线及章节范围】中选取，不存在的新支线不要添加
10. 输出必须是合法JSON格式`;

      console.log(`[全局进度] 【阶段AI】发送AI分析请求，prompt长度=${analysisPrompt.length}`);
      const analysisResp = await apiClient.post('/assist/ai/proxy-ai', {
        messages: [{ role: 'user', content: analysisPrompt }],
        model: 'deepseek-v4-flash',
        extra_body: {},
        temperature: 0.3,
        max_tokens: 3000,
        stream: false,
        billingInfo: {
          novelName: novelTitle,
          chapterNumber: currentChapterNum,
          operationType: '全局进度分析'
        }
      });

      const aiText = analysisResp.data?.choices?.[0]?.message?.content || analysisResp.data?.data?.content || '';
      console.log('[全局进度] AI分析结果原始文本:', aiText);

      // 解析 JSON（🔥 robustJsonParse 容错）
      let analysis = null;
      try {
        const rjpResult = robustJsonParse(aiText);
        if (rjpResult.ok && rjpResult.data) {
          analysis = rjpResult.data;
        }
      } catch (parseErr) {
        console.error('[全局进度] 解析AI分析结果失败:', parseErr, aiText);
      }

      if (!analysis) {
        console.log('[全局进度] ❌ 未能解析AI分析结果，跳过自动更新');
        return;
      }

      // ── 验证：AI 返回的 ID 是否正确（关键诊断日志）─────────────
      console.group('[全局进度] 【AI返回ID验证】');
      console.log('[全局进度] AI返回的 characterStateChanges:', JSON.stringify(analysis.characterStateChanges?.map(c => ({ id: c.characterId, name: c.characterName })) || []));
      console.log('[全局进度] AI返回的 relationshipChanges:', JSON.stringify(analysis.relationshipChanges?.map(r => ({ fromId: r.fromCharacterId, fromName: r.fromCharacterName, toId: r.toCharacterId, toName: r.toCharacterName })) || []));
      console.log('[全局进度] AI返回的 foreshadowingStateChanges:', JSON.stringify(analysis.foreshadowingStateChanges?.map(f => ({ id: f.foreshadowingId, title: f.foreshadowingTitle, status: f.newStatus })) || []));
      console.log('[全局进度] AI返回的 subplotStateChanges:', JSON.stringify(analysis.subplotStateChanges?.map(s => ({ id: s.subplotId, title: s.subplotTitle, status: s.newStatus })) || []));
      console.groupEnd();

      // 必须用 progressData 中已保存的 phase 做比较，避免异步导致 prevPhase 过时
      const prevSavedPhase = progressData?.mainLinePhase || currentPhase;
      const newPhase = analysis.mainLinePhase || prevSavedPhase;

      // ── 调试：打印 AI 分析结果 ───────────────────────────────────
      console.group(`[全局进度] AI分析结果（第${currentChapterNum}章）`);
      console.log('[全局进度] 主线阶段:', analysis.mainLinePhase);
      console.log('[全局进度] 角色状态变化:', JSON.stringify(analysis.characterStateChanges));
      console.log('[全局进度] 关系变化:', JSON.stringify(analysis.relationshipChanges));
      console.log('[全局进度] 伏笔状态变化:', JSON.stringify(analysis.foreshadowingStateChanges));
      console.log('[全局进度] 支线状态变化:', JSON.stringify(analysis.subplotStateChanges));
      console.groupEnd();

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段A】主线阶段 & 主线章节时间轴 ═══════════════════════════
      // ══════════════════════════════════════════════════════════════════
      console.log(`[全局进度] 【阶段A】主线阶段判断 — prevPhase=${prevSavedPhase} → newPhase=${newPhase}`);
      // 主线阶段：始终保存（即使与之前相同也要写入，用于第1章初始化）
      try {
        const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
        const isChange = newPhase !== prevSavedPhase;
        await apiClient.post(`/assist/novels/${assistNovelId}/progress`, { mainLinePhase: newPhase });
        setProgressData(prev => prev ? { ...prev, mainLinePhase: newPhase } : { mainLinePhase: newPhase });
        console.log(`[全局进度] 【阶段A】${isChange ? '✅' : '💾'} 主线阶段已保存${isChange ? '（阶段变化）' : '（无变化，第1章初始化）'}：${phaseLabels[newPhase]}`);
      } catch (e) {
        console.error('[全局进度] 【阶段A】❌ 保存主线阶段失败:', e);
      }
      // ── 2b. 写入主线章节状态主线 mainLineChapterStates ─────────────
      {
        try {
          const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
          const newMlEntry = {
            id: 'mlcs_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
            chapterNumber: currentChapterNum,
            chapterId: currentChapter._id,
            chapterTitle: currentChapter.title || '',
            progressed: true,
            phase: newPhase,
            phaseLabel: phaseLabels[newPhase] || newPhase,
            description: newPhase !== prevSavedPhase
              ? `主线由「${phaseLabels[prevSavedPhase]}」推进至「${phaseLabels[newPhase]}」`
              : `主线持续推进（当前阶段：${phaseLabels[newPhase]}）`,
            updatedAt: new Date()
          };
          const existing = progressData?.mainLineChapterStates || [];
          // 同一章节只保留最新记录
          const updated = [
            ...existing.filter(e => e.chapterNumber !== currentChapterNum),
            newMlEntry
          ];
          setProgressData(prev => prev ? { ...prev, mainLineChapterStates: updated } : prev);
          await saveProgressData('mainLineChapterStates', updated);
          console.log(`[全局进度] 【阶段A】✅ mainLineChapterStates写入内容=${JSON.stringify(newMlEntry)}`);
          console.log(`[全局进度] 【阶段A】mainLineChapterStates累计现有${updated.length}条: ${JSON.stringify(updated.map(e => ({ ch: e.chapterNumber, phase: e.phase, desc: e.description })))}`);
        } catch (e) {
          console.error('[全局进度] 【阶段A】❌ 保存主线章节状态失败:', e);
        }
      }

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段B】角色状态 & 角色章节时间轴 ════════════════════════
      // ══════════════════════════════════════════════════════════════════
      console.log(`[全局进度] 【阶段B】角色状态处理 — AI返回${(analysis.characterStateChanges || []).length}条（有变化/出场）`);
      // 修复：即使 AI 返回空数组（无变化），本章出场的角色也必须记录到时间轴
      //       否则百万字后角色出场历史全丢失，后续章纲生成的 charTimelineBlock 断裂
      const hasAiCharChanges = !!(analysis.characterStateChanges && analysis.characterStateChanges.length > 0);
      if (hasAiCharChanges) {
        for (const change of analysis.characterStateChanges) {
          try {
            // 找到角色（按ID精确匹配，或按名字模糊匹配作为后备）
            const char = characters.find(c => c._id === change.characterId || c.name === change.characterName);
            if (!char) continue;

            // 准备更新数据
            const updateData = {
              currentState: change.newState || change.description || char.currentState || '',
              lifeStatus: change.lifeStatus || char.lifeStatus || 'alive',
              emotionStatus: change.emotionStatus || char.emotionStatus || ''
            };

            // 添加状态历史记录
            const newHistoryEntry = {
              chapterNumber: currentChapterNum,
              state: change.newState || change.description || '',
              description: change.description || '',
              lifeStatus: change.lifeStatus || char.lifeStatus || 'alive',
              emotionStatus: change.emotionStatus || char.emotionStatus || '',
              timestamp: new Date().toISOString()
            };

            const existingHistory = char.stateHistory || [];
            const updatedHistory = [...existingHistory.filter(h => !(h.chapterNumber === currentChapterNum && h.state === newHistoryEntry.state)), newHistoryEntry];

            await apiClient.put(`/assist/novels/${assistNovelId}/characters/${char._id}/state`, {
              ...updateData,
              chapterNumber: currentChapterNum,
              chapterId: currentChapter._id,
              description: change.description || '',
              state: change.newState || change.description || ''
            });

            // 同时更新本地状态
            setCharacters(prev => prev.map(c => c._id === char._id ? {
              ...c,
              ...updateData,
              stateHistory: updatedHistory.slice(-20) // 保留最近20条
            } : c));

            console.log(`[全局进度] ✅ 角色「${change.characterName}」状态已更新至AssistCharacter`);
          } catch (e) {
            console.error(`[全局进度] ❌ 更新角色「${change.characterName}」状态失败:`, e);
          }
        }
        // ── 3b. 将角色状态写入 progressData.characterChapterStates（按章时间轴）─────────
        // 动态记录核心：只要 AI 分析到了该角色（无论是否有状态变化），都记录本章状态
        {
          const newEntries = (analysis.characterStateChanges || []).map(change => {
            const char = characters.find(c => c._id === change.characterId || c.name === change.characterName);
            return {
              characterId: change.characterId || char?._id || '',
              chapterNumber: currentChapterNum,
              chapterId: currentChapter._id,
              lifeStatus: change.lifeStatus || char?.lifeStatus || 'alive',
              emotionStatus: change.emotionStatus || '',
              narrativeNote: change.description || change.newState || '',
              updatedAt: new Date().toISOString()
            };
          });
          // 去重：同一角色在本章只保留最新记录
          const existing = (progressData?.characterChapterStates || []).filter(
            e => !(e.characterId && e.chapterNumber === currentChapterNum &&
              newEntries.some(ne => ne.characterId === e.characterId))
          );
          const updated = [...existing, ...newEntries];
          setProgressData(prev => prev ? { ...prev, characterChapterStates: updated } : prev);
          await saveProgressData('characterChapterStates', updated);
          if (newEntries.length > 0) {
            console.log(`[全局进度] ✅ 角色章节状态已写入 progressData（${newEntries.length}条）`);
          }
        }
      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段B-结束】characterChapterStates 写入完成 ══════════
      // ══════════════════════════════════════════════════════════════════
      } else {
        // ── 修复：即使 AI 返回空数组，本章在章纲中指定的角色也必须记录到时间轴 ──
        // 防止 AI 遗漏导致百万字后角色出场历史完全断裂
        const chapterCharIds = currentChapter.characterIds || [];
        if (chapterCharIds.length > 0) {
          const newEntries = chapterCharIds.map(charId => {
            const char = characters.find(c => c._id === charId);
            return {
              characterId: charId,
              chapterNumber: currentChapterNum,
              chapterId: currentChapter._id,
              lifeStatus: char?.lifeStatus || 'alive',
              emotionStatus: char?.emotionStatus || '',
              narrativeNote: '本章出场（AI分析无状态变化）',
              updatedAt: new Date().toISOString()
            };
          });
          const existing = (progressData?.characterChapterStates || []).filter(
            e => !(e.characterId && e.chapterNumber === currentChapterNum &&
              newEntries.some(ne => ne.characterId === e.characterId))
          );
          const updated = [...existing, ...newEntries];
          setProgressData(prev => prev ? { ...prev, characterChapterStates: updated } : prev);
          await saveProgressData('characterChapterStates', updated);
          console.log(`[全局进度] 【阶段B】✅ AI无返回，降级记录本章章纲角色（${newEntries.length}条）`);
        } else {
          console.log(`[全局进度] 【阶段B】⏭ AI无返回，且本章章纲未指定角色，跳过`);
        }
      }

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段C】关系变化 & 关系章节时间轴 ═══════════════════════
      // ══════════════════════════════════════════════════════════════════
      console.log(`[全局进度] 【阶段C】关系变化判断 — AI返回${(analysis.relationshipChanges || []).length}条关系变化`);
      if (analysis.relationshipChanges && analysis.relationshipChanges.length > 0) {
        for (const rc of analysis.relationshipChanges) {
          try {
            // 查找角色ID
            const fromChar = characters.find(c => c._id === rc.fromCharacterId || c.name === rc.fromCharacterName);
            const toChar = characters.find(c => c._id === rc.toCharacterId || c.name === rc.toCharacterName);

            const rcData = {
              fromCharacterId: fromChar?._id || rc.fromCharacterId || '',
              fromCharacterName: rc.fromCharacterName || fromChar?.name || '',
              toCharacterId: toChar?._id || rc.toCharacterId || '',
              toCharacterName: rc.toCharacterName || toChar?.name || '',
              relationType: rc.relationType || '其他',
              fromStatus: rc.fromStatus || '未知',
              toStatus: rc.toStatus || '未知',
              chapterId: currentChapter._id,
              chapterNumber: currentChapterNum,
              reason: rc.reason || '本章情节推进'
            };

            // 调用关系记录 API → 更新 AssistProgress.relationshipChanges + AssistCharacter.relations
            const resp = await apiClient.post(`/assist/novels/${assistNovelId}/relationship-change`, rcData);
            if (resp.data?.data) {
              // API 返回最新数据，同步到本地状态
              setProgressData(prev => {
                if (!prev) return prev;
                const existingRcs = prev.relationshipChanges || [];
                // 去重：同一角色对只保留最新记录
                const filtered = existingRcs.filter(
                  e => !(e.fromCharacterId === resp.data.data.fromCharacterId && e.toCharacterId === resp.data.data.toCharacterId)
                );
                return { ...prev, relationshipChanges: [...filtered, resp.data.data] };
              });
            }
            console.log(`[全局进度] ✅ 关系变化已记录：${rcData.fromCharacterName} ${rcData.fromStatus}→${rcData.toStatus} ${rcData.toCharacterName}`);
          } catch (e) {
            console.error(`[全局进度] 记录关系变化失败:`, e);
          }
        }
        // ── 4b. 将关系状态写入 progressData.relationshipChapterStates（按章时间轴）─────────
        const newRelEntries = analysis.relationshipChanges.map(rc => {
          const fromChar = characters.find(c => c._id === rc.fromCharacterId || c.name === rc.fromCharacterName);
          const toChar = characters.find(c => c._id === rc.toCharacterId || c.name === rc.toCharacterName);
          return {
            fromCharacterId: fromChar?._id || rc.fromCharacterId || '',
            fromCharacterName: rc.fromCharacterName || fromChar?.name || '',
            toCharacterId: toChar?._id || rc.toCharacterId || '',
            toCharacterName: rc.toCharacterName || toChar?.name || '',
            relationType: rc.relationType || '其他',
            chapterNumber: currentChapterNum,
            chapterId: currentChapter._id,
            fromStatus: rc.fromStatus || '未知',
            toStatus: rc.toStatus || '未知',
            reason: rc.reason || '本章情节推进',
            updatedAt: new Date().toISOString()
          };
        });
        if (newRelEntries.length > 0) {
          try {
            // 修复去重逻辑：改用 characterName 配对（而非 characterId），防止 AI 随机生成 ID 导致去重失效
            const existing = (progressData?.relationshipChapterStates || []).filter(
              e => !(e.chapterNumber === currentChapterNum &&
                newRelEntries.some(ne => ne.fromCharacterName === e.fromCharacterName && ne.toCharacterName === e.toCharacterName))
            );
            const updated = [...existing, ...newRelEntries];
            setProgressData(prev => prev ? { ...prev, relationshipChapterStates: updated } : prev);
            await saveProgressData('relationshipChapterStates', updated);
            console.log(`[全局进度] ✅ 关系章节状态已写入 progressData（${newRelEntries.length}条）`);
          } catch (e) {
            console.error('[全局进度] 保存 relationshipChapterStates 失败:', e);
          }
        }
        // 注：relationshipChanges 由 deriveFromTimeAxis 动态派生，无需单独写入
      } else {
        console.log(`[全局进度] 【阶段C】⏭ AI未返回任何关系变化，跳过`);
      }

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段D】伏笔状态 & 伏笔章节时间轴 ═══════════════════════
      // ══════════════════════════════════════════════════════════════════
      console.log(`[全局进度] 【阶段D】伏笔状态判断 — AI返回${(analysis.foreshadowingStateChanges || []).length}条伏笔状态`);
      if (analysis?.foreshadowingStateChanges && analysis.foreshadowingStateChanges.length > 0) {
        try {
          const outlineId = outlines[0]?._id;
          if (!outlineId) throw new Error('大纲ID不存在');
          console.log(`[全局进度] 【阶段D】大纲伏笔更新准备，AI返回=${JSON.stringify(analysis.foreshadowingStateChanges.map(af => ({ title: af.foreshadowingTitle, status: af.newStatus, planted: af.plantedChapterNumber, reveal: af.revealChapterNumber })))}`);
          const currentOutline = outlines[0];
          const updatedForeshadowing = currentOutline.foreshadowing.map(f => {
            const aiUpdate = analysis.foreshadowingStateChanges.find(
              af => af.foreshadowingTitle === f.title
            );
            if (!aiUpdate) return f;
            const newStatus = aiUpdate.newStatus || f.status;
            // 收集状态变化详情
            const changes = [];
            if (f.status !== newStatus) changes.push(`status: ${f.status}→${newStatus}`);
            if (aiUpdate.plantedChapterNumber && !f.plantedChapterNumber) changes.push(`plantedChapterNumber: ${aiUpdate.plantedChapterNumber}`);
            if (aiUpdate.plantedChapterNumber && !f.plantedChapterId) changes.push(`plantedChapterId: ${currentChapter._id}`);
            if (aiUpdate.revealChapterNumber && !f.revealChapterNumber) changes.push(`revealChapterNumber: ${aiUpdate.revealChapterNumber}`);
            if (aiUpdate.revealChapterNumber && !f.revealChapterId) changes.push(`revealChapterId: ${currentChapter._id}`);
            if (changes.length > 0) console.log(`[全局进度] 【阶段D】大纲伏笔「${f.title}」将更新: ${changes.join(', ')}`);
            return {
              ...f,
              status: newStatus,
              plantedChapterId: f.plantedChapterId || (aiUpdate.plantedChapterNumber ? currentChapter._id : undefined),
              plantedChapterNumber: f.plantedChapterNumber || aiUpdate.plantedChapterNumber || undefined,
              revealChapterId: f.revealChapterId || (aiUpdate.revealChapterNumber ? currentChapter._id : undefined),
              revealChapterNumber: f.revealChapterNumber || aiUpdate.revealChapterNumber || undefined,
              updatedAt: new Date().toISOString()
            };
          });
          console.log(`[全局进度] 【阶段D】即将POST更新大纲foreshadowing，条数=${updatedForeshadowing.length}`);
          await apiClient.post(`/assist/novels/${assistNovelId}/outline`, {
            foreshadowing: updatedForeshadowing
          });
          setOutlines(prev => prev.map(o => o._id === outlineId ? { ...o, foreshadowing: updatedForeshadowing } : o));
          console.log(`[全局进度] 【阶段D】✅ 大纲伏笔状态已更新，共${analysis.foreshadowingStateChanges.length}条被处理`);
          // 验证：打印更新后的大纲伏笔状态
          updatedForeshadowing.filter(f => analysis.foreshadowingStateChanges.some(af => af.foreshadowingTitle === f.title)).forEach(f => {
            const aiUpdate = analysis.foreshadowingStateChanges.find(af => af.foreshadowingTitle === f.title);
            console.log(`[全局进度] 【阶段D】验证大纲伏笔「${f.title}」: status=${f.status}, plantedCh=${f.plantedChapterNumber}, revealCh=${f.revealChapterNumber}（AI期望: status=${aiUpdate.newStatus}, planted=${aiUpdate.plantedChapterNumber}, reveal=${aiUpdate.revealChapterNumber}）`);
          });

          // ── 将伏笔状态变化写入 progressData.foreshadowingChapterStates ─────────────────
          // 注意：plantedChapterId/plantedChapterNumber/revealChapterId/revealChapterNumber
          // 已在上方 updatedForeshadowing 合并写入，本段只生成 progressData 时间轴记录
          const newFsEntries = analysis.foreshadowingStateChanges
            .map(af => {
              const f = (outlines[0]?.foreshadowing || []).find(x => x.title === af.foreshadowingTitle);
              return {
                foreshadowingId: f?.id || f?._id || '',
                title: af.foreshadowingTitle,
                chapterNumber: currentChapterNum,
                chapterId: currentChapter._id,
                chapterTitle: currentChapter.title || '',
                status: af.newStatus,
                narrativeNote: af.narrativeNote || '',
                updatedAt: new Date()
              };
            })
            .filter(entry => entry.title); // 过滤无效条目
          if (newFsEntries.length > 0) {
            const existing = progressData?.foreshadowingChapterStates || [];
            // 修复去重逻辑：改用 title（伏笔标题唯一性），而非可能为空的 foreshadowingId
            const updated = [
              ...existing.filter(e => !(e.chapterNumber === currentChapterNum && newFsEntries.some(ne => ne.title === e.title))),
              ...newFsEntries
            ];
            setProgressData(prev => prev ? { ...prev, foreshadowingChapterStates: updated } : prev);
            await saveProgressData('foreshadowingChapterStates', updated);
            console.log(`[全局进度] 【阶段D】✅ foreshadowingChapterStates写入=${JSON.stringify(newFsEntries.map(e => ({ title: e.title, status: e.status })))}`);
          }
        } catch (e) {
          console.error('[全局进度] AI自动更新伏笔状态失败:', e);
        }
      } else {
        console.log(`[全局进度] 【阶段D】⏭ AI未返回任何伏笔状态变化，跳过`);
      }

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段E】支线状态 & 支线章节时间轴（主动检测+AI补充）═════
      // ══════════════════════════════════════════════════════════════════
      console.log(`[全局进度] 【阶段E】支线状态判断 — AI返回${(analysis?.subplotStateChanges || []).length}条支线状态`);
      try {
        const currentOutline = outlines[0];
        const allSubplots = currentOutline?.subPlots || [];

        // 已在时间轴记录过的支线ID（去重）
        const recordedSubplotIds = new Set(
          (progressData?.subplotChapterStates || [])
            .filter(e => e.chapterNumber === currentChapterNum)
            .map(e => e.subplotId)
        );

        // 规则①：主动检测本章关联支线（本章在支线章节范围内 startChapterNumber~endChapterNumber）
        console.log(`[全局进度] 【阶段E】主动检测：当前第${currentChapterNum}章，大纲支线=${JSON.stringify(allSubplots.map(s => ({ title: s.title, status: s.status, start: s.startChapterNumber, end: s.endChapterNumber })))}`);
        const thisChapterSpEntries = allSubplots
          .filter(s => {
            const spId = s.id || s._id || '';
            if (recordedSubplotIds.has(spId)) return false;
            const start = s.startChapterNumber || s.startChapter || 1;
            const end = s.endChapterNumber || s.endChapter || 9999;
            return currentChapterNum >= start && currentChapterNum <= end;
          })
          .map(s => ({
            subplotId: s.id || s._id || '',
            title: s.title || '',
            chapterNumber: currentChapterNum,
            chapterId: currentChapter._id,
            chapterTitle: currentChapter.title || '',
            status: 'active',
            progress: s.description?.slice(0, 50) || '',
            narrativeNote: `第${currentChapterNum}章首次触发支线「${s.title}」`,
            updatedAt: new Date()
          }));

        // 规则②：AI 报告的状态变化
        console.log(`[全局进度] 【阶段E】AI报告的支线变化=${JSON.stringify(analysis?.subplotStateChanges || [])}`);
        const aiSubplotChanges = (analysis?.subplotStateChanges || [])
          .filter(as => {
            const s = allSubplots.find(x => x.title === as.subplotTitle);
            return s && as.newStatus; // 过滤无效
          })
          .map(as => {
            const s = allSubplots.find(x => x.title === as.subplotTitle);
            return {
              subplotId: s?.id || s?._id || '',
              title: as.subplotTitle,
              chapterNumber: currentChapterNum,
              chapterId: currentChapter._id,
              chapterTitle: currentChapter.title || '',
              status: as.newStatus,
              progress: as.progress || '',
              narrativeNote: as.narrativeNote || '',
              updatedAt: new Date()
            };
          });

        // 合并且去重（AI 变化优先，覆盖主动检测的 active 条目）
        const allNewSpEntries = [...thisChapterSpEntries];
        aiSubplotChanges.forEach(ai => {
          const idx = allNewSpEntries.findIndex(e => e.subplotId === ai.subplotId);
          if (idx >= 0) {
            allNewSpEntries[idx] = ai; // AI 数据覆盖
          } else {
            allNewSpEntries.push(ai);
          }
        });

        // 更新大纲支线状态
        console.log(`[全局进度] 【阶段E】大纲支线更新准备，更新前大纲支线状态=${JSON.stringify(currentOutline.subPlots.map(s => ({ title: s.title, status: s.status })))}`);
        console.log(`[全局进度] 【阶段E】allNewSpEntries=${JSON.stringify(allNewSpEntries.map(e => ({ title: e.title, status: e.status })))}`);
        if (allNewSpEntries.length > 0) {
          const updatedSubplots = currentOutline.subPlots.map(s => {
            const change = allNewSpEntries.find(e => e.title === s.title);
            if (!change) return s;
            console.log(`[全局进度] 【阶段E】大纲支线「${s.title}」将更新: ${s.status}→${change.status}`);
            return { ...s, status: change.status, updatedAt: new Date().toISOString() };
          });
          await apiClient.post(`/assist/novels/${assistNovelId}/outline`, {
            subPlots: updatedSubplots
          });
          setOutlines(prev => prev.map(o => o._id === currentOutline._id ? { ...o, subPlots: updatedSubplots } : o));
          console.log(`[全局进度] 【阶段E】✅ 大纲支线已更新，共${allNewSpEntries.length}条`);
        } else {
          console.log(`[全局进度] 【阶段E】⏭ allNewSpEntries为空，跳过大纲支线更新`);
        }

        // 写入时间轴
        if (allNewSpEntries.length > 0) {
          const existing = progressData?.subplotChapterStates || [];
          // 修复去重逻辑：改用 title（支线标题唯一性），防止 AI 返回格式错误的 subplotId 导致去重失效
          const updated = [
            ...existing.filter(e => !(e.chapterNumber === currentChapterNum && allNewSpEntries.some(ne => ne.title === e.title))),
            ...allNewSpEntries
          ];
          setProgressData(prev => prev ? { ...prev, subplotChapterStates: updated } : prev);
          await saveProgressData('subplotChapterStates', updated);
          console.log(`[全局进度] 【阶段E】✅ 支线章节状态已写入 progressData（现有${updated.length}条，新增${allNewSpEntries.length}条）`);
        } else {
          console.log(`[全局进度] 【阶段E】⏭ 本章无支线状态变化，跳过`);
        }
        // 打印支线主动检测详情
        console.log(`[全局进度] 【阶段E】支线主动检测：本章在范围内数量=${thisChapterSpEntries.length}，AI补充=${aiSubplotChanges.length}，最终=${allNewSpEntries.length}`);
        thisChapterSpEntries.forEach(s => console.log(`[全局进度] 【阶段E】主动触发 → 「${s.title}」status=${s.status}`));
        aiSubplotChanges.forEach(s => console.log(`[全局进度] 【阶段E】AI补充 → 「${s.title}」status=${s.status}`));
      } catch (e) {
        console.error('[全局进度] 支线状态更新失败:', e);
      }

      // ── 5. 保存本章 outline（核心事件/承接上文/为下章埋钩）到数据库 ───
      if (analysis) {
        try {
          const currentChapterCoreText = Array.isArray(analysis.nextChapterCorePoints)
            ? analysis.nextChapterCorePoints.map(pt => pt.point).join('；')
            : (analysis.nextChapterCorePoints || '');
          const currentChapterConn = analysis.nextChapterConnection || '';
          const isPlaceholder = (val) => !val || ['（本章核心事件）', '（未填写）', '未填写', ''].includes(val);
          // 核心事件：优先用章纲生成的设计值（已有非占位符内容），其次用 AI 分析结果
          const designedCoreEv = currentChapter.outline?.coreEvent || '';
          const finalCoreEvent = !isPlaceholder(designedCoreEv)
            ? designedCoreEv
            : (!isPlaceholder(currentChapterCoreText)
              ? currentChapterCoreText
              : '');
          // 承接上文：优先用章纲生成的设计值，其次用 AI 分析结果
          const designedConn = currentChapter.outline?.connectionToPrevious || '';
          const finalConnectionToPrevious = !isPlaceholder(designedConn)
            ? designedConn
            : (!isPlaceholder(currentChapterConn) ? currentChapterConn : '');
          // 为下章埋钩：优先用章纲生成的设计值（最关键，保护它），其次用 AI 分析结果
          const designedHook = currentChapter.outline?.hookForNext || '';
          const finalHookForNext = !isPlaceholder(designedHook)
            ? designedHook
            : (!isPlaceholder(analysis.chapterEndingHook) ? analysis.chapterEndingHook : currentChapterConn);
          await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${currentChapter._id}/outline`, {
            outline: {
              ...(currentChapter.outline || {}),
              coreEvent: finalCoreEvent,
              connectionToPrevious: finalConnectionToPrevious,
              hookForNext: finalHookForNext,
            },
            status: currentChapter.outline?.status || 'outline_only',
          });
          // 更新本地章节状态
          setChapters(prev => prev.map(c => c._id === currentChapter._id ? {
            ...c,
            outline: {
              ...(c.outline || {}),
              coreEvent: finalCoreEvent,
              connectionToPrevious: finalConnectionToPrevious,
              hookForNext: finalHookForNext,
            }
          } : c));
          console.log(`[全局进度] ✅ 本章（第${currentChapterNum}章）核心事件/承接上文/为下章埋钩已写入数据库`);
        } catch (e) {
          console.error('[全局进度] 保存本章 outline 失败:', e);
        }
      }

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段F】下章章纲写入 — 已禁用 ══
      // ══════════════════════════════════════════════════════════════════
      // 说明：用户希望手动控制章纲生成，禁止自动写入下一章的章纲内容
      //       下一章的章纲必须由用户手动点击「生成章纲」按钮生成
      console.log('[全局进度] 【阶段F已禁用】自动写入下一章章纲功能已禁用，章纲需手动生成');

      console.log(`[全局进度] ====== 第${currentChapterNum}章自动跟进全部完成 ======`);
      console.log(`[全局进度] 汇总: 主线阶段=${newPhase} | 角色状态=${(analysis.characterStateChanges || []).length}条 | 关系变化=${(analysis.relationshipChanges || []).length}条 | 伏笔变化=${(analysis.foreshadowingStateChanges || []).length}条 | 支线变化=${(analysis.subplotStateChanges || []).length}条`);
      // ── 最终状态快照 ──────────────────────────────────────────
      console.group('[全局进度] 【最终状态快照】');
      console.log('[全局进度] 最终 progressData.mainLinePhase=', progressData?.mainLinePhase);
      console.log('[全局进度] 最终 mainLineChapterStates 条数=', (progressData?.mainLineChapterStates || []).length, '内容:', JSON.stringify(progressData?.mainLineChapterStates || []));
      console.log('[全局进度] 最终 characterChapterStates 条数=', (progressData?.characterChapterStates || []).length);
      console.log('[全局进度] 最终 relationshipChapterStates 条数=', (progressData?.relationshipChapterStates || []).length);
      console.log('[全局进度] 最终 foreshadowingChapterStates 条数=', (progressData?.foreshadowingChapterStates || []).length);
      console.log('[全局进度] 最终 subplotChapterStates 条数=', (progressData?.subplotChapterStates || []).length);
      console.log('[全局进度] 最终 nextChapterCorePoints 条数=', (progressData?.nextChapterCorePoints || []).length);
      console.groupEnd();

      // ══════════════════════════════════════════════════════════════════
      // ══ 【阶段G】自动生成下章完整章纲 — 已禁用 ══
      // ══════════════════════════════════════════════════════════════════
      // 说明：用户反馈希望手动控制章纲生成，避免自动生成的章纲不符合预期
      //       已禁用在「插入正文」时自动生成下一章章纲的功能
      //       如需启用，请参考 Git 历史记录恢复代码
      console.log('[全局进度] 【阶段G已禁用】自动生成下章章纲功能已禁用');

    } catch (error) {
      console.error('[全局进度] ❌ 自动跟进过程中出错:', error);
    } finally {
      setGlobalProgressSyncing(false);
    }
  }, [assistNovelId, autoSyncAfterGenerate, characters, chapters, novel, outlines, progressData, getMainLinePhase, saveProgressData]);

  // 关系变化类型（用于全局进度「关系变化」表单）
  const RELATION_CHANGE_TYPES = ['恋人', '朋友', '仇人', '导师', '家人', '宿敌', '暗恋', '恩人', '陌路', '其他'];
  // 角色生命状态
  const CHARACTER_LIFE_STATUS = [
    { value: 'alive', label: '活着', color: '#38bdf8' },
    { value: 'injured', label: '受伤', color: '#f97316' },
    { value: 'captured', label: '被俘', color: '#ef4444' },
    { value: 'missing', label: '失踪', color: '#64748b' },
    { value: 'dead', label: '死亡', color: '#374151' },
  ];
  // 情感状态类型（用于关系变化追踪）
  const EMOTION_TYPES = ['陌生', '初识', '接触', '好感', '热恋', '暧昧', '分手', '反目成仇', '重逢', '和解', '死亡', '正常', '癫狂'];
  // 章节状态选项
  const CHAPTER_STATUS_OPTIONS = [
    { value: 'outline_only', label: '仅章纲', icon: '○', color: 'rgba(255,255,255,0.5)' },
    { value: 'in_progress', label: '写作中', icon: '>', color: '#ffd700' },
    { value: 'completed', label: '已完成', icon: 'V', color: '#38bdf8' },
    { value: 'revised', label: '已修订', icon: 'R', color: '#3b82f6' },
  ];
  // 角色定位选项
  const CHARACTER_ROLES = [
    { value: 'protagonist', label: '主角' },
    { value: 'antagonist', label: '反派' },
    { value: 'mentor', label: '导师' },
    { value: 'lover', label: '恋人' },
    { value: 'friend', label: '友人' },
    { value: 'family', label: '家人' },
    { value: 'rival', label: '宿敌' },
    { value: 'npc', label: 'NPC' },
    { value: 'other', label: '其他' },
  ];
  // 伏笔状态选项
  const FORESHADOWING_STATUS_OPTIONS = [
    { value: 'pending', label: '待埋设', icon: '待', color: '#ec4899' },
    { value: 'planted', label: '已埋设', icon: '埋', color: '#ffd700' },
    { value: 'advancing', label: '推进中', icon: '推', color: '#06b6d4' },
    { value: 'revealed', label: '已揭露', icon: '揭', color: '#38bdf8' },
    { value: 'forgotten', label: '已放弃', icon: '弃', color: '#ef4444' },
  ];
  // 支线状态选项
  const SUBPLOT_STATUS_OPTIONS = [
    { value: 'pending', label: '待启动', icon: '待', color: '#ffd700' },
    { value: 'active', label: '进行中', icon: '进', color: '#38bdf8' },
    { value: 'completed', label: '已完成', icon: '完', color: '#0ea5e9' },
    { value: 'abandoned', label: '已放弃', icon: '弃', color: '#ef4444' },
  ];

  // 渲染全局进度面板（完全重构：五大区块全部可编辑）
  const renderProgressPanel = () => {
    const stats = progressStats;
    const pg = progressData || {};
    const phaseForStatRow = getMainLinePhase();
    const phaseOrderStat = ['beginning', 'development', 'climax', 'ending'];
    const phaseLabelsStat = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
    const mainlinePhaseStep = Math.max(1, phaseOrderStat.indexOf(phaseForStatRow) + 1);
    const relationChangeCount = (pg.relationshipChanges || []).length;
    const strictWarningCount = stats.warnings.filter(w => w.type === 'warning').length;
    const infoHintCount = stats.warnings.filter(w => w.type === 'info').length;
    /** 进度表 slim 行用 characterId/characterName；角色列表用 _id/name — 统一后再渲染 */
    /** 全局进度内：把角色数据里的薄荷/霓虹绿统一成天青，避免与章节列表风格冲突 */
    const progressSanitizeCharAccent = (raw) => {
      const h = String(raw || '').trim().toLowerCase();
      if (!h || h === 'var(--ws-accent)') return '#38bdf8';
      const mintish = new Set([
        '#00f0ff', '#00ff9f', '#00ff96', '#00ffa0', '#39ff14', '#2ecc71', '#06b6d4', '#22d3ee',
        '#6ee7b7', '#4ade80', '#22c55e', '#2dd4bf', '#5eead4', '#14b8a6', '#00e676',
      ]);
      if (mintish.has(h)) return '#38bdf8';
      return raw || '#7dd3fc';
    };
    const normalizeProgressCharacterRow = (row) => {
      if (!row) return null;
      const charId = row._id ?? row.id ?? row.characterId;
      const full = charId != null ? characters.find(c => String(c._id) === String(charId)) : null;
      const name = String(row.name || row.characterName || full?.name || '').trim() || '未命名角色';
      const color = progressSanitizeCharAccent(row.color || full?.color || '#7dd3fc');
      const lifeStatusVal = row.lifeStatus || row.status || full?.lifeStatus || 'alive';
      const narrativeState = String(full?.currentState ?? row.currentState ?? '').trim();
      const emotionStatus = full?.emotionStatus ?? row.emotionStatus;
      const currentEmotion = full?.currentEmotion ?? row.currentEmotion;
      return {
        charId,
        name,
        color,
        lifeStatusVal,
        narrativeState,
        emotionStatus,
        currentEmotion,
        fullChar: full,
        appearanceCountFromRow: row.appearanceCount,
        raw: row,
      };
    };

    /** 全局进度专用：与左侧「章节列表」同款天青，避免主题霓虹绿/薄荷色 */
    const progressChapterSky = {
      text: '#e0f2fe',
      textMuted: '#7dd3fc',
      border: 'rgba(125, 211, 252, 0.48)',
      borderHi: 'rgba(186, 230, 253, 0.72)',
      bgHover: 'rgba(56, 189, 248, 0.14)',
    };

    return (
      <Box className="panel-content workspace-progress-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'visible' }}>

          {/* 标题栏：标题独占一行，避免「总」「控」被挤换行 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: { xs: 1.5, sm: 2 },
              borderBottom: '1px solid rgba(125,211,252,0.12)',
              flexShrink: 0,
              background: 'linear-gradient(180deg, rgba(56,189,248,0.04) 0%, transparent 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
              {isChaptersNarrow && (
                <Tooltip title="返回编辑">
                  <IconButton
                    size="small"
                    onClick={handleClosePanel}
                    aria-label="返回编辑"
                    sx={{
                      flexShrink: 0,
                      minWidth: { xs: 34, sm: 36 },
                      minHeight: { xs: 34, sm: 36 },
                      color: 'rgba(255,255,255,0.75)',
                      border: '1px solid rgba(255,255,255,0.18)',
                      bgcolor: 'rgba(255,255,255,0.04)',
                      '&:hover': { bgcolor: 'rgba(56,189,248,0.14)', borderColor: progressChapterSky.border },
                    }}
                  >
                    <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                  </IconButton>
                </Tooltip>
              )}
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: progressChapterSky.text,
                  fontWeight: 700,
                  fontSize: { xs: 15, sm: 17 },
                  letterSpacing: '0.02em',
                  lineHeight: 1.25,
                  textShadow: '0 0 20px rgba(56,189,248,0.25)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                全局进度总控
              </Typography>
            </Box>
            <Box className="workspace-progress-header-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
            <Chip label={`${stats.totalChapters}章`} size="small" sx={{ height: 22, fontSize: 11, bgcolor: 'rgba(56,189,248,0.12)', color: '#bae6fd', border: '1px solid rgba(125,211,252,0.35)' }} />
            {stats.warnings.filter(w => w.type === 'warning').length > 0 && (
              <Chip label={`${stats.warnings.filter(w => w.type === 'warning').length}项警告`} size="small"
                sx={{ height: 22, fontSize: 11, bgcolor: 'rgba(249,115,22,0.18)', color: '#fb923c', border: '1px solid rgba(249,115,22,0.3)' }} />
            )}
            {pg.lastAutoSync && (
              <Chip
                label={`同步于 ${new Date(pg.lastAutoSync).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`}
                size="small"
                icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#38bdf8', ml: 0.5, boxShadow: '0 0 8px rgba(56,189,248,0.7)' }} />}
                sx={{ height: 22, fontSize: 10, bgcolor: 'rgba(56,189,248,0.1)', color: '#7dd3fc', border: '1px solid rgba(125,211,252,0.32)' }}
              />
            )}
            <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.04 }} whileTap={motionReduced ? undefined : { scale: 0.96 }} transition={{ type: 'spring', stiffness: 420, damping: 22 }}>
              <Button size="small" variant="outlined"
                className={`workspace-progress-header-ending-btn${novel?.plannedEndingChapter ? ' workspace-progress-header-ending-btn--active' : ''}`}
                startIcon={<Box component="span" sx={{ fontSize: 13 }}>🔚</Box>}
                onClick={() => {
                  const maxCh = Math.max(0, ...chapters.map(c => c.chapterNumber));
                  setEndingChaptersInput(maxCh > 0 ? Math.max(10, Math.min(20, maxCh)) : 10);
                  setEndingDialogOpen(true);
                }}
                sx={{ height: 26, fontSize: 10, borderColor: novel?.plannedEndingChapter ? 'rgba(239,68,68,0.55)' : 'rgba(239,68,68,0.28)', color: novel?.plannedEndingChapter ? '#f87171' : 'rgba(239,68,68,0.7)', '&:hover': { borderColor: '#ef4444', bgcolor: 'rgba(239,68,68,0.1)' } }}
              >
                {novel?.plannedEndingChapter ? `收尾(${novel.plannedEndingChapter}章止)` : '标记收尾'}
              </Button>
            </Box>
            <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.04 }} whileTap={motionReduced ? undefined : { scale: 0.96 }} transition={{ type: 'spring', stiffness: 420, damping: 22 }}>
              <Button size="small" variant="outlined" startIcon={<SaveIcon sx={{ fontSize: 13 }} />}
                className="workspace-progress-header-save-btn workspace-progress-btn-breathe"
                onClick={async () => {
                  try { await saveProgressData('all', progressData); setSnackbar({ open: true, message: '全局进度已保存', severity: 'success' }); }
                  catch (e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                }}
                sx={{ height: 26, fontSize: 10, borderColor: progressChapterSky.border, color: progressChapterSky.text, '&:hover': { borderColor: progressChapterSky.borderHi, bgcolor: progressChapterSky.bgHover } }}
              >
                保存
              </Button>
            </Box>
            <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.04 }} whileTap={motionReduced ? undefined : { scale: 0.96 }} transition={{ type: 'spring', stiffness: 420, damping: 22 }}>
              <Button size="small" variant="text" startIcon={<RefreshIcon sx={{ fontSize: 13 }} />}
                onClick={() => {
                  const autoSyncData = syncProgressToState();
                  setProgressData(prev => prev ? { ...prev, ...autoSyncData } : autoSyncData);
                  setSnackbar({ open: true, message: '已从章节数据刷新同步', severity: 'success' });
                }}
                sx={{ height: 26, fontSize: 10, borderColor: 'rgba(125,211,252,0.35)', color: '#bae6fd', '&:hover': { borderColor: '#7dd3fc', bgcolor: 'rgba(56,189,248,0.1)' } }}
                className="workspace-progress-btn-breathe"
              >
                刷新
              </Button>
            </Box>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, px: { xs: 1.5, sm: 2 }, py: 0.75 }}>
            {novel?.title || '小说'} · 点击统计卡切换 Tab · 数据来自章纲/角色/大纲面板实时统计
          </Typography>

          {/* 统计概览（overflow 可见，避免选中卡 drop-shadow 呼吸被裁切） */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, px: { xs: 1.5, sm: 2 }, py: 0.75, overflow: 'visible' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, overflow: 'visible' }}>
              {[
                { label: '角色', value: stats.totalCharacters, sub: '人物', color: '#7dd3fc', tab: 1 },
                { label: '伏笔', value: pg.foreshadowingProgress?.length || stats.pendingForeshadowings, sub: `${stats.revealedForeshadowings}已揭`, color: '#f472b6', tab: 3 },
                { label: '支线', value: stats.totalSubplots, sub: `${stats.activeSubplots}活跃`, color: '#7dd3fc', tab: 4 },
              ].map((stat, idx) => {
                const sel = progressTab === stat.tab;
                return (
                <Box component={motion.div} key={`r1-${idx}`} layout
                  className={`workspace-progress-stat-card${sel ? ' workspace-progress-stat-card--selected' : ''}`}
                  initial={motionReduced ? false : { opacity: 0, y: 14, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: motionReduced ? 0 : idx * 0.07, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={motionReduced ? undefined : sel ? { y: -1, scale: 1.012 } : { y: -2, scale: 1.025, boxShadow: `0 6px 22px ${stat.color}30` }}
                  whileTap={motionReduced ? undefined : { scale: 0.975 }}
                  onClick={() => setProgressTab(stat.tab)}
                  sx={{
                    borderRadius: 1.5,
                    p: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    ...(!sel ? {
                      bgcolor: 'rgba(255,255,255,0.028)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { bgcolor: `${stat.color}14`, borderColor: `${stat.color}44` },
                    } : { border: '1px solid transparent', bgcolor: 'transparent' }),
                  }}>
                  <Typography sx={{ color: stat.color, fontSize: { xs: 17, sm: 19 }, fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}>{stat.value}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600 }}>{stat.label}</Typography>
                  {stat.sub && <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: 9 }}>{stat.sub}</Typography>}
                </Box>
              );})}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5, overflow: 'visible' }}>
              {[
                { label: '主线进度', value: mainlinePhaseStep, sub: `${phaseLabelsStat[phaseForStatRow] || '开局'}`, color: '#7dd3fc', tab: 0 },
                { label: '关系变化', value: relationChangeCount, sub: '人物关系', color: '#bae6fd', tab: 2 },
                { label: '警告', value: strictWarningCount, sub: infoHintCount > 0 ? `${infoHintCount}提示` : '风险提示', color: '#fb923c', tab: 5 },
              ].map((stat, idx) => {
                const sel = progressTab === stat.tab;
                return (
                <Box component={motion.div} key={`r2-${idx}`} layout
                  className={`workspace-progress-stat-card${sel ? ' workspace-progress-stat-card--selected' : ''}`}
                  initial={motionReduced ? false : { opacity: 0, y: 14, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: motionReduced ? 0 : (3 + idx) * 0.07, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={motionReduced ? undefined : sel ? { y: -1, scale: 1.012 } : { y: -2, scale: 1.025, boxShadow: `0 6px 22px ${stat.color}30` }}
                  whileTap={motionReduced ? undefined : { scale: 0.975 }}
                  onClick={() => setProgressTab(stat.tab)}
                  sx={{
                    borderRadius: 1.5,
                    p: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    ...(!sel ? {
                      bgcolor: 'rgba(255,255,255,0.028)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                      transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
                      '&:hover': { bgcolor: `${stat.color}14`, borderColor: `${stat.color}44` },
                    } : { border: '1px solid transparent', bgcolor: 'transparent' }),
                  }}>
                  <Typography sx={{ color: stat.color, fontSize: { xs: 17, sm: 19 }, fontWeight: 700, lineHeight: 1.2, mb: 0.25 }}>{stat.value}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600 }}>{stat.label}</Typography>
                  {stat.sub && <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: 9 }}>{stat.sub}</Typography>}
                </Box>
              );})}
            </Box>
          </Box>

          {/* ── Tab 内容（可滚动）·切换 Tab 时模糊滑入 / 滑出 ── */}
          <Box sx={{ flex: 1, overflow: 'auto', px: { xs: 1.5, sm: 2 }, pb: 3 }}>
          <AnimatePresence mode="wait">
          {/* ── Tab 0: 主线进度 ── */}
          {progressTab === 0 && (
            <motion.div
              key="progress-tab-0"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {(outlines.length === 0 || (!outlines[0]?.mainPlot?.storyline && !outlines[0]?.mainPlot?.description)) ? (
                <EmptyState icon="" msg="暂无主线" sub="在「大纲」面板中设置主线剧情" />
              ) : (
                <Box>
                  {/* 主线基本信息 */}
                  <Box component={motion.div}
                    initial={motionReduced ? false : { opacity: 0, y: 14, scale: 0.975 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={motionReduced ? undefined : { y: -2, boxShadow: '0 8px 28px rgba(56,189,248,0.2)' }}
                    sx={{
                      bgcolor: 'rgba(56,189,248,0.07)', border: '1px solid rgba(125,211,252,0.28)',
                      borderRadius: 1.5, p: { xs: 1.5, sm: 2 }, mb: 2,
                      boxShadow: '0 2px 12px rgba(56,189,248,0.1)',
                      position: 'relative', overflow: 'hidden',
                      '&::before': { content: '""', position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, borderRadius: '0 2px 2px 0', bgcolor: '#38bdf8', boxShadow: '0 0 12px rgba(56,189,248,0.55)' }, pl: 2.25,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ color: '#7dd3fc', fontSize: 13, fontWeight: 700, textShadow: '0 0 10px rgba(56,189,248,0.35)' }}>
                        一句话主线
                      </Typography>
                      <IconButton size="small"
                        onClick={() => {
                          const newLine = prompt('修改主线剧情', outlines[0]?.mainPlot?.storyline || '');
                          if (newLine !== null && newLine.trim()) {
                            handleUpdateMainLine(newLine);
                          }
                        }}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#7dd3fc', bgcolor: 'rgba(56,189,248,0.12)' }, minWidth: 28, height: 28 }}>
                        <EditIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    <Typography sx={{ color: '#f1f5f9', fontSize: 14, fontWeight: 600, mb: 1, letterSpacing: '0.01em' }}>
                      {outlines[0]?.mainPlot?.storyline}
                    </Typography>
                    {outlines[0]?.mainPlot?.description && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, mt: 1 }}>
                        {outlines[0].mainPlot.description}
                      </Typography>
                    )}
                  </Box>

                  {/* 主线阶段划分 */}
                  <SectionLabel color="#38bdf8">主线阶段进度</SectionLabel>
                  <Box sx={{ mb: 2 }}>
                    {/* 四个阶段 */}
                    {[
                      { key: 'beginning', label: '开局', icon: '', desc: outlines[0]?.storyStructure?.beginning || '尚未设置开局阶段', color: '#38bdf8' },
                      { key: 'development', label: '发展', icon: '', desc: outlines[0]?.storyStructure?.development || '尚未设置发展阶段', color: '#3b82f6' },
                      { key: 'climax', label: '高潮', icon: '', desc: outlines[0]?.storyStructure?.climax || '尚未设置高潮阶段', color: '#ef4444' },
                      { key: 'ending', label: '结局', icon: '', desc: outlines[0]?.storyStructure?.ending || '尚未设置结局阶段', color: '#0ea5e9' },
                    ].map((phase, idx) => {
                      const currentPhase = getMainLinePhase();
                      const phaseOrder = ['beginning', 'development', 'climax', 'ending'];
                      const currentIdx = phaseOrder.indexOf(currentPhase);
                      const isPast = phaseOrder.indexOf(phase.key) < currentIdx;
                      const isCurrent = phase.key === currentPhase;
                      const bColor = isCurrent ? phase.color : isPast ? 'rgba(125,211,252,0.55)' : 'rgba(255,255,255,0.2)';

                      return (
                        <Box component={motion.div} key={phase.key} layout
                          initial={motionReduced ? false : { opacity: 0, x: -18, scale: 0.97 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          transition={{ delay: motionReduced ? 0 : idx * 0.1, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                          whileHover={motionReduced ? undefined : { y: -2, scale: 1.012, boxShadow: `0 8px 24px ${phase.color}28` }}
                          whileTap={motionReduced ? undefined : { scale: 0.984 }}
                          sx={{
                            display: 'flex', alignItems: 'flex-start', gap: 1.5,
                            p: 1.5, mb: 1, borderRadius: 1.5,
                            bgcolor: isCurrent ? `${phase.color}12` : 'rgba(255,255,255,0.025)',
                            border: `1px solid ${isCurrent ? phase.color + '45' : 'rgba(255,255,255,0.07)'}`,
                            cursor: 'pointer',
                            boxShadow: isCurrent ? `0 0 14px ${phase.color}20` : '0 2px 8px rgba(0,0,0,0.18)',
                            transition: 'background 0.2s',
                            '&:hover': { borderColor: phase.color + '60' },
                          }}
                          onClick={() => {
                            const newPhase = prompt(`设置当前进度到「${phase.label}」阶段？\n1=开局 2=发展 3=高潮 4=结局\n输入数字1-4：`, String(phaseOrder.indexOf(phase.key) + 1));
                            if (newPhase && ['1','2','3','4'].includes(newPhase.trim())) {
                              const phases = ['beginning', 'development', 'climax', 'ending'];
                              handleUpdateMainLinePhase(phases[parseInt(newPhase) - 1]);
                            }
                          }}
                        >
                          <Box component={motion.div} whileHover={motionReduced ? undefined : { rotate: 12, scale: 1.08 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                            sx={{
                              width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              bgcolor: isPast ? 'rgba(56,189,248,0.14)' : isCurrent ? `${phase.color}25` : 'rgba(255,255,255,0.05)',
                              border: `1px solid ${bColor}`,
                              boxShadow: isCurrent ? `0 0 12px ${phase.color}50` : 'none',
                            }}>
                            <Typography sx={{ fontSize: 15 }}>{phase.icon}</Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                              <Typography sx={{ color: isCurrent ? phase.color : '#f1f5f9', fontSize: 13, fontWeight: 700 }}>
                                {phase.label}
                              </Typography>
                              {isCurrent && (
                                <Chip label="当前" size="small" sx={{ height: 17, fontSize: 9, fontWeight: 700, bgcolor: `${phase.color}28`, color: phase.color }} />
                              )}
                              {isPast && (
                                <Chip label="已完成" size="small" sx={{ height: 17, fontSize: 9, bgcolor: 'rgba(56,189,248,0.15)', color: '#7dd3fc', border: '1px solid rgba(125,211,252,0.3)' }} />
                              )}
                            </Box>
                            <Typography sx={{ color: 'rgba(255,255,255,0.48)', fontSize: 11, lineHeight: 1.5 }}>
                              {phase.desc}
                            </Typography>
                            {/* 关联章节 */}
                            {phase.key === 'beginning' && chapters.filter(c => c.chapterNumber <= Math.ceil(chapters.length * 0.25)).length > 0 && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.75 }}>
                                {chapters.filter(c => c.chapterNumber <= Math.ceil(chapters.length * 0.25) && (c.status === 'completed' || c.status === 'revised')).slice(-5).map(c => (
                                  <Chip key={c._id} label={`第${c.chapterNumber}章`} size="small"
                                    onClick={() => { setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                                    sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.14)', color: '#7dd3fc', cursor: 'pointer', border: '1px solid rgba(125,211,252,0.28)' }} />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* 主线章节时间轴 */}
                  {(progressData?.mainLineChapterStates || []).length > 0 && (
                    <>
                      <SectionLabel color="#38bdf8">主线章节记录</SectionLabel>
                      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {[...(progressData.mainLineChapterStates || [])]
                          .sort((a, b) => a.chapterNumber - b.chapterNumber)
                          .slice(-8)
                          .map((ml, idx, arr) => {
                            const next = arr[idx + 1];
                            const phaseColors = { beginning: '#38bdf8', development: '#3b82f6', climax: '#ef4444', ending: '#0ea5e9' };
                            const color = phaseColors[ml.phase] || '#38bdf8';
                            return (
                              <Box key={ml.chapterNumber} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1.5, border: `1px solid ${color}22` }}>
                                <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: `${color}18`, border: `1px solid ${color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Typography sx={{ fontSize: 11, color, fontWeight: 700 }}>{ml.chapterNumber}</Typography>
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <Typography sx={{ fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                                      第{ml.chapterNumber}章
                                    </Typography>
                                    <Chip label={ml.phase === 'beginning' ? '开局' : ml.phase === 'development' ? '发展' : ml.phase === 'climax' ? '高潮' : '结局'} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: `${color}20`, color }} />
                                    {next && next.phase !== ml.phase && <Typography sx={{ fontSize: 10, color: '#7dd3fc' }}>→ {next.phase === 'beginning' ? '开局' : next.phase === 'development' ? '发展' : next.phase === 'climax' ? '高潮' : '结局'}</Typography>}
                                  </Box>
                                  <Typography sx={{ fontSize: 10.5, color: 'rgba(255,255,255,0.42)', lineHeight: 1.45, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {ml.description || ml.narrativeNote || ''}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                      </Box>
                    </>
                  )}

                  {/* 主线核心冲突 */}
                  {(outlines[0]?.coreConflicts || []).length > 0 && (
                    <>
                      <SectionLabel color="#ef4444">核心冲突</SectionLabel>
                      <Box sx={{ mb: 2 }}>
                        {(outlines[0].coreConflicts || []).map((conf, idx) => (
                          <Box
                            component={motion.div}
                            key={`conf-${idx}`}
                            initial={motionReduced ? false : { opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: motionReduced ? 0 : idx * 0.06, duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                            whileHover={motionReduced ? undefined : { x: 4, boxShadow: '0 4px 16px rgba(239,68,68,0.14)' }}
                            sx={{
                              display: 'flex', alignItems: 'center', gap: 1, p: 1, mb: 0.75,
                              borderRadius: 1,
                            }}
                          >
                            <Typography sx={{ color: '#ef4444', fontSize: 12 }}>【冲突】</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{conf.title || conf.description || ''}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}

                  {/* 主线高潮设计 */}
                  {(outlines[0]?.climaxes || []).length > 0 && (
                    <>
                      <SectionLabel color="#f97316">高潮设计</SectionLabel>
                      <Box sx={{ mb: 2 }}>
                        {(outlines[0].climaxes || []).map((cl, idx) => (
                          <Box
                            component={motion.div}
                            key={`climax-${idx}`}
                            initial={motionReduced ? false : { opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: motionReduced ? 0 : idx * 0.07, duration: motionReduced ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
                            whileHover={motionReduced ? undefined : { y: -2, boxShadow: '0 6px 20px rgba(249,115,22,0.14)' }}
                            sx={{ p: 1.5, mb: 0.75, borderRadius: 1.5 }}
                          >
                            <Typography sx={{ color: '#f97316', fontSize: 12, fontWeight: 600, mb: 0.25 }}>
                              {cl.title || `高潮点 ${idx + 1}`}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                              {cl.description || cl.content || ''}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
            </motion.div>
          )}

          {/* ── Tab 1: 角色状态 ── */}
          {progressTab === 1 && (
            <motion.div
              key="progress-tab-1"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {(pg.characterProgress || characters).length === 0 ? (
                <EmptyState icon="" msg="暂无角色" sub="在「角色」面板中添加角色后，这里会自动显示状态" />
              ) : (
                <>
                  {/* ── 角色总览 ── */}
                  <Box
                    component={motion.div}
                    initial={motionReduced ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                    className="workspace-progress-overview"
                    sx={{ mb: 1.5, p: 1.5, pl: 2.25, borderRadius: 1.5 }}
                  >
                    <Typography sx={{ color: '#e0f2fe', fontSize: 11, fontWeight: 700, mb: 1, letterSpacing: '0.03em' }}>
                      角色总览 · 出场与章节状态
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(pg.characterProgress || characters).map((ch, idx) => {
                        const n = normalizeProgressCharacterRow(ch);
                        if (!n) return null;
                        const { charId, name, color: chColor, lifeStatusVal } = n;
                        const chAppearances = chapters.filter(c => {
                          if (charId == null) return false;
                          if (c.characterIds?.includes(charId)) return true;
                          return (c.outline?.plotPoints || []).some(pt => (pt.characterIds || []).includes(charId));
                        });
                        const chAliveLabel = CHARACTER_LIFE_STATUS.find(s => s.value === lifeStatusVal)?.label || '活着';
                        return (
                          <Box key={charId != null ? String(charId) : `ov-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'default' }}>
                            <Box sx={{
                              width: 10, height: 10, borderRadius: '50%',
                              bgcolor: chAliveLabel === '死亡' ? '#ef4444' : chAliveLabel === '失踪' ? '#f97316' : '#38bdf8',
                              flexShrink: 0
                            }} />
                            <Typography sx={{ color: chColor, fontSize: 11, fontWeight: 600 }}>
                              {name}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                              {chAppearances.length > 0 ? `第${chAppearances[0].chapterNumber}章` : '未出场'}
                              {chAppearances.length > 0 ? `·${chAppearances.length}次` : ''}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {(pg.characterProgress || characters).map((char, idx) => {
                    const n = normalizeProgressCharacterRow(char);
                    if (!n) return null;
                    const { charId, name, color: charColor, lifeStatusVal, narrativeState, appearanceCountFromRow, fullChar } = n;
                    const appearances = chapters.filter(c => {
                      if (charId == null) return false;
                      if (c.characterIds?.includes(charId)) return true;
                      return (c.outline?.plotPoints || []).some(pt => (pt.characterIds || []).includes(charId));
                    }).sort((a, b) => a.chapterNumber - b.chapterNumber);
                    const firstChapter = appearances[0];
                    const lifeStatusInfo = CHARACTER_LIFE_STATUS.find(s => s.value === lifeStatusVal) || { label: '活着', color: '#38bdf8' };
                    const emotionInfo = (n.emotionStatus && n.emotionStatus !== 'normal' && n.emotionStatus !== '正常') ? n.emotionStatus : (n.currentEmotion && n.currentEmotion !== '正常' ? n.currentEmotion : null);
                    const appearanceTotal = appearances.length || appearanceCountFromRow || 0;

                    return (
                      <Box
                        component={motion.div}
                        key={charId != null ? String(charId) : `card-${idx}`}
                        layout
                        initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: motionReduced ? 0 : Math.min(idx * 0.055, 0.44), duration: motionReduced ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -3, boxShadow: `0 12px 32px ${charColor}18` }}
                        className="workspace-progress-item-card"
                        sx={{ borderRadius: 1.5, p: 1.5, pl: 2.25, mb: 1, '&:hover': { borderColor: `${charColor}55` } }}
                      >
                        {/* 角色头部 */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.5 }}>
                          <Box sx={{ position: 'relative' }}>
                            <Avatar sx={{ width: 40, height: 40, bgcolor: charColor + '33', fontSize: 16, fontWeight: 700, color: charColor, border: `1px solid ${charColor}50` }}>
                              {name?.charAt(0)}
                            </Avatar>
                            <Box sx={{
                              position: 'absolute', bottom: -2, right: -2,
                              width: 11, height: 11, borderRadius: '50%',
                              bgcolor: lifeStatusInfo.color,
                              border: '2px solid rgba(10,11,18,0.8)'
                            }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                              <Typography sx={{ color: '#fff', fontSize: 15, fontWeight: 700, textShadow: `0 0 8px ${charColor}55` }}>
                                {name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Chip label={lifeStatusInfo.label} size="small"
                                  sx={{ height: 17, fontSize: 9, bgcolor: `${lifeStatusInfo.color}20`, color: lifeStatusInfo.color }} />
                                {emotionInfo && (
                                  <Chip label={emotionInfo} size="small"
                                    sx={{ height: 17, fontSize: 9, bgcolor: 'rgba(236,72,153,0.2)', color: '#f9a8d4' }} />
                                )}
                              </Box>
                            </Box>
                            {narrativeState && lifeStatusInfo.label !== '活着' && (
                              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mt: 0.25 }}>{narrativeState}</Typography>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                            <Chip label={`出场 ${appearanceTotal} 次`} size="small"
                              sx={{ height: 19, fontSize: 9, bgcolor: 'rgba(56,189,248,0.12)', color: '#bae6fd', border: '1px solid rgba(125,211,252,0.28)',
                                fontWeight: 600 }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>
                              {firstChapter ? `首：第${firstChapter.chapterNumber}章` : '未出场'}
                            </Typography>
                          </Box>
                        </Box>
                        {/* 当前状态描述 */}
                      {(narrativeState || lifeStatusInfo.label !== '活着') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>状态：</Typography>
                          <Typography sx={{ color: charColor, fontSize: 11, fontWeight: 500, flex: 1 }}>{narrativeState || lifeStatusInfo.label}</Typography>
                          <IconButton size="small" onClick={() => setEditCharacterDialog(fullChar || char)}
                            sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#7dd3fc' }, minWidth: 24, height: 24 }}>
                            <EditIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      )}
                      {/* 出场章节 */}
                      {appearances.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {appearances.map(c => (
                            <Chip key={c._id} label={`第${c.chapterNumber}章${c.title ? `·${c.title.slice(0, 4)}` : ''}`} size="small"
                              onClick={() => { setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                              sx={{
                                height: 20, fontSize: 9,
                                bgcolor: c.status === 'completed' || c.status === 'revised' ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.06)',
                                color: c.status === 'completed' || c.status === 'revised' ? '#7dd3fc' : 'rgba(255,255,255,0.5)',
                                border: '1px solid',
                                borderColor: c.status === 'completed' || c.status === 'revised' ? 'rgba(125,211,252,0.38)' : 'rgba(255,255,255,0.08)',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                '&:hover': { transform: 'scale(1.06)', boxShadow: '0 4px 14px rgba(56,189,248,0.2)' },
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: 10, fontStyle: 'italic' }}>未在任何章节中出场</Typography>
                      )}

                      {/* 按章状态时间轴（存于全局进度 characterChapterStates） */}
                      <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <SectionLabel color="#7dd3fc">章节状态时间轴</SectionLabel>
                        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 9, mb: 0.75 }}>
                          为某一章单独记录生命状态、情感与剧情说明，形成完整时间线（可多条、可编辑）。
                        </Typography>
                        {(pg.characterChapterStates || [])
                          .filter((s) => String(s.characterId) === String(charId))
                          .sort((a, b) => a.chapterNumber - b.chapterNumber)
                          .map((rec) => {
                            const ls = CHARACTER_LIFE_STATUS.find((l) => l.value === rec.lifeStatus);
                            return (
                              <Box
                                component={motion.div}
                                layout
                                key={rec.id || `${charId}-${rec.chapterNumber}`}
                                initial={motionReduced ? false : { opacity: 0, x: -12 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: motionReduced ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={motionReduced ? undefined : { x: 4, boxShadow: '0 6px 20px rgba(56,189,248,0.15)' }}
                                className="workspace-progress-timeline-row"
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.75,
                                  p: 1,
                                  mb: 0.5,
                                }}
                              >
                                <Chip
                                  label={`第${rec.chapterNumber}章`}
                                  size="small"
                                  sx={{ height: 20, fontSize: 9, bgcolor: 'rgba(56,189,248,0.18)', color: '#bae6fd', flexShrink: 0, border: '1px solid rgba(125,211,252,0.35)' }}
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography sx={{ fontSize: 10, color: '#fff' }}>
                                    <Box component="span" sx={{ color: ls?.color || '#94a3b8' }}>{ls?.label || rec.lifeStatus}</Box>
                                    {rec.emotionStatus ? ` · ${rec.emotionStatus}` : ''}
                                  </Typography>
                                  {rec.narrativeNote ? (
                                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                      {rec.narrativeNote}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <IconButton
                                  size="small"
                                  onClick={() => setCharacterChapterStateDialog({
                                    open: true,
                                    characterId: charId,
                                    characterName: name,
                                    recordId: rec.id,
                                    chapterNumber: String(rec.chapterNumber),
                                    chapterId: rec.chapterId || '',
                                    lifeStatus: rec.lifeStatus || 'alive',
                                    emotionStatus: rec.emotionStatus || '',
                                    narrativeNote: rec.narrativeNote || '',
                                  })}
                                  sx={{ color: 'rgba(255,255,255,0.35)', '&:hover': { color: '#7dd3fc' } }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={async () => {
                                    const list = (pg.characterChapterStates || []).filter((x) => x.id !== rec.id);
                                    try {
                                      await saveProgressData('characterChapterStates', list);
                                      setProgressData((prev) => (prev ? { ...prev, characterChapterStates: list } : prev));
                                      setSnackbar({ open: true, message: '已删除该条记录', severity: 'success' });
                                    } catch (err) { /* saveProgressData 已 toast */ }
                                  }}
                                  sx={{ color: 'rgba(255,255,255,0.25)', '&:hover': { color: '#f87171' } }}
                                >
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            );
                          })}
                        <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.02 }} whileTap={motionReduced ? undefined : { scale: 0.97 }} sx={{ display: 'inline-block', mt: 0.75 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          className="assist-chapter-ai-glow-btn workspace-progress-add-record-btn"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => setCharacterChapterStateDialog({
                            open: true,
                            characterId: charId,
                            characterName: name,
                            recordId: null,
                            chapterNumber: firstChapter ? String(firstChapter.chapterNumber) : (chapters[0] ? String(chapters[0].chapterNumber) : ''),
                            chapterId: firstChapter?._id || chapters[0]?._id || '',
                            lifeStatus: lifeStatusVal || 'alive',
                            emotionStatus: emotionInfo || '',
                            narrativeNote: '',
                          })}
                          sx={{ fontSize: 10, minHeight: 32, px: 1.25 }}
                        >
                          添加本章状态记录
                        </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
                </>
              )}
            </Box>
            </motion.div>
          )}

          {/* ── Tab 2: 人物关系变化 ── */}
          {progressTab === 2 && (
            <motion.div
              key="progress-tab-2"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {characters.length < 2 ? (
                <EmptyState icon="💑" msg="角色不足2人" sub="至少添加2个角色后才能建立关系" />
              ) : (
                <>
                  {/* ── 人物对分组视图 ── */}
                  {(() => {
                    // 按（角色对 + 关系类型）分组
                    const pairMap = {};
                    (pg.relationshipChapterStates || []).forEach((rc) => {
                      const key = `${rc.fromCharacterId}__${rc.toCharacterId}__${rc.relationType}`;
                      if (!pairMap[key]) pairMap[key] = [];
                      pairMap[key].push(rc);
                    });
                    const pairs = Object.entries(pairMap);
                    return pairs.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        {pairs.map(([key, records], idx) => {
                          const sorted = records.sort((a, b) => a.chapterNumber - b.chapterNumber);
                          const first = sorted[0];
                          const pairKey = key;
                    return (
                      <Box
                        component={motion.div}
                        layout
                        key={`pair-${idx}`}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2 }}
                        className="workspace-progress-item-card"
                        sx={{ borderRadius: 1.5, p: 1.5, pl: 2.25, mb: 1 }}
                      >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#38bdf8' }}>
                                  {first.fromCharacterName?.charAt(0) || '?'}
                                </Avatar>
                                <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
                                  {first.fromCharacterName}
                                </Typography>
                                <Typography sx={{ color: '#bae6fd', fontSize: 11 }}>{first.relationType}</Typography>
                                <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: '#ec4899' }}>
                                  {first.toCharacterName?.charAt(0) || '?'}
                                </Avatar>
                                <Typography sx={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>
                                  {first.toCharacterName}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, ml: 'auto' }}>
                                  {sorted.length}条记录
                                </Typography>
                              </Box>
                              {sorted.map((rec, i) => (
                                <Box key={rec.id || i} sx={{
                                  display: 'flex', alignItems: 'flex-start', gap: 0.75, p: 0.875, mb: 0.5,
                                  bgcolor: 'rgba(56,189,248,0.05)', borderRadius: 1,
                                  border: '1px solid rgba(125,211,252,0.16)'
                                }}>
                                  <Chip label={`第${rec.chapterNumber}章`} size="small"
                                    sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.16)', color: '#e0f2fe', flexShrink: 0 }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{rec.fromStatus}</Typography>
                                      <ArrowForwardIcon sx={{ fontSize: 10, color: '#7dd3fc' }} />
                                      <Typography sx={{ color: '#e0f2fe', fontSize: 10, fontWeight: 600 }}>{rec.toStatus}</Typography>
                                    </Box>
                                    {rec.reason && (
                                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                        {rec.reason}
                                      </Typography>
                                    )}
                                  </Box>
                                  <IconButton size="small"
                                    onClick={() => setRelationshipChapterStateDialog({
                                      open: true,
                                      fromCharacterId: first.fromCharacterId,
                                      fromCharacterName: first.fromCharacterName,
                                      toCharacterId: first.toCharacterId,
                                      toCharacterName: first.toCharacterName,
                                      relationType: first.relationType || '其他',
                                      recordId: rec.id,
                                      chapterNumber: String(rec.chapterNumber),
                                      chapterId: rec.chapterId || '',
                                      fromStatus: rec.fromStatus || '陌生',
                                      toStatus: rec.toStatus || '陌生',
                                      reason: rec.reason || '',
                                    })}
                                    sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#7dd3fc' }, minWidth: 24, height: 24 }}>
                                    <EditIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                  <IconButton size="small"
                                    onClick={async () => {
                                      const list = (pg.relationshipChapterStates || []).filter(x => x.id !== rec.id);
                                      try {
                                        await saveProgressData('relationshipChapterStates', list);
                                        setProgressData(prev => prev ? { ...prev, relationshipChapterStates: list } : prev);
                                        setSnackbar({ open: true, message: '已删除', severity: 'success' });
                                      } catch (err) {}
                                    }}
                                    sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#f87171' }, minWidth: 24, height: 24 }}>
                                    <DeleteIcon sx={{ fontSize: 13 }} />
                                  </IconButton>
                                </Box>
                              ))}
                              <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.02 }} whileTap={motionReduced ? undefined : { scale: 0.97 }} sx={{ display: 'inline-block', mt: 0.5 }}>
                              <Button size="small" variant="outlined"
                                className="assist-chapter-ai-glow-btn workspace-progress-add-record-btn"
                                startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                                onClick={() => setRelationshipChapterStateDialog({
                                  open: true,
                                  fromCharacterId: first.fromCharacterId,
                                  fromCharacterName: first.fromCharacterName,
                                  toCharacterId: first.toCharacterId,
                                  toCharacterName: first.toCharacterName,
                                  relationType: first.relationType || '其他',
                                  recordId: null,
                                  chapterNumber: chapters[0] ? String(chapters[0].chapterNumber) : '',
                                  chapterId: chapters[0]?._id || '',
                                  fromStatus: sorted.length > 0 ? sorted[sorted.length - 1].toStatus : '陌生',
                                  toStatus: '陌生',
                                  reason: '',
                                })}
                                sx={{ fontSize: 10, minHeight: 30, px: 1.25 }}>
                                添加本章关系变化
                              </Button>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : null;
                  })()}
                  {/* 旧版关系变化历史（兼容） */}
                  {(pg.relationshipChanges || []).length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <SectionLabel>关系变化历史</SectionLabel>
                      {(pg.relationshipChanges || []).map((rc, idx) => (
                        <Box
                          component={motion.div}
                          layout
                          key={rc.id || `rc-${idx}`}
                          initial={motionReduced ? false : { opacity: 0, x: -12 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: motionReduced ? 0 : 0.32, ease: [0.22, 1, 0.36, 1] }}
                          whileHover={motionReduced ? undefined : { x: 4, boxShadow: '0 4px 18px rgba(56,189,248,0.12)' }}
                          className="workspace-progress-timeline-row"
                          sx={{ p: 1.25, mb: 0.75 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                            <Chip label={`第${rc.chapterNumber}章`} size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.16)', color: '#e0f2fe' }} />
                            <Typography sx={{ color: '#fff', fontSize: 11, fontWeight: 600 }}>
                              {rc.fromCharacterName} ←→ {rc.toCharacterName}
                            </Typography>
                            <Chip label={rc.relationType || '其他'} size="small" sx={{ height: 16, fontSize: 9, bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{rc.fromStatus}</Typography>
                            <ArrowForwardIcon sx={{ fontSize: 12, color: '#7dd3fc' }} />
                            <Typography sx={{ color: '#e0f2fe', fontSize: 11, fontWeight: 600 }}>{rc.toStatus}</Typography>
                            {rc.reason && <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, fontStyle: 'italic' }}>· {rc.reason}</Typography>}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* 添加新关系变化（布局对齐角色面板「角色关系网」） */}
                  <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
                    添加关系变化
                  </Typography>
                  <AddRelationChangeForm
                    characters={characters}
                    chapters={chapters}
                    onAdd={async (data) => {
                      await apiClient.post(`/assist/novels/${assistNovelId}/relationship-change`, data);
                      await loadProgressData();
                      setSnackbar({ open: true, message: '关系变化已记录', severity: 'success' });
                    }}
                  />
                </>
              )}
            </Box>
            </motion.div>
          )}

          {/* ── Tab 3: 伏笔全局追踪 ── */}
          {progressTab === 3 && (
            <motion.div
              key="progress-tab-3"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {(outlines[0]?.foreshadowing || []).length === 0 ? (
                <EmptyState icon="🔮" msg="暂无伏笔" sub="在大纲面板中添加伏笔后可在此追踪进度" />
              ) : (
                (outlines[0].foreshadowing || []).slice(0, fsVisibleCount).map(f => {
                  const fsId = f.id || f._id;
                  // 性能优化：使用 chapterIndexes 索引查找，替代逐卡片 chapters.find/filter 全表扫描
                  const plantedChapter = f.plantedChapterId ? chapterIndexes.byId.get(f.plantedChapterId) : null;
                  const revealChapter = f.revealChapterId ? chapterIndexes.byId.get(f.revealChapterId) : null;
                  const advanceChapters = (f.advanceChapterIds || []).map(id => chapterIndexes.byId.get(id)).filter(Boolean).sort((a, b) => a.chapterNumber - b.chapterNumber);
                  const relatedChapters = (chapterIndexes.byForeshadowingId.get(fsId) || []).slice().sort((a, b) => a.chapterNumber - b.chapterNumber);
                  const fsStatus = FORESHADOWING_STATUS_OPTIONS.find(s => s.value === f.status) || FORESHADOWING_STATUS_OPTIONS[0];
                  const relatedCharNames = (f.relatedCharacterIds || []).map(cid => characters.find(c => c._id === cid)?.name).filter(Boolean);
                  // 计算伏笔推进进度
                  const allRelevantNums = [plantedChapter?.chapterNumber, ...advanceChapters.map(c => c.chapterNumber)].filter(Boolean).sort((a, b) => a - b);
                  const latestRelevant = allRelevantNums.length > 0 ? allRelevantNums[allRelevantNums.length - 1] : null;
                  const totalSpan = plantedChapter && revealChapter ? (revealChapter.chapterNumber - plantedChapter.chapterNumber + 1) : null;
                  const progressStep = plantedChapter && latestRelevant ? (latestRelevant - plantedChapter.chapterNumber + 1) : null;

                  return (
                    <Box
                      component={motion.div}
                      layout={heavyFsList ? false : undefined}
                      key={fsId}
                      initial={motionReduced ? false : { opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={motionReduced ? undefined : { y: -2 }}
                      className="workspace-progress-item-card workspace-progress-item-card--fs"
                      sx={{ borderRadius: 1.5, p: 1.5, pl: 2.25, mb: 1, '&:hover': { borderColor: `${fsStatus.color}45` } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.5, bgcolor: fsStatus.color, flexShrink: 0 }} />
                          <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{typeof f.title === 'string' ? f.title : '未命名伏笔'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {f.status !== 'planted' && (
                            <Tooltip title="标记为已埋设">
                              <IconButton size="small" onClick={async () => {
                                await apiClient.post(`/assist/novels/${assistNovelId}/foreshadowings/${fsId}/status`, { status: 'planted', action: 'plant' });
                                if (outlines[0]) { const o = { ...outlines[0] }; o.foreshadowing = o.foreshadowing.map(fi => (fi.id || fi._id) === fsId ? { ...fi, status: 'planted' } : fi); setOutlines([o]); }
                              }} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ffd700' }, minWidth: 24, height: 24 }}>
                                <GrassIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {f.status !== 'advancing' && (
                            <Tooltip title="标记为推进中">
                              <IconButton size="small" onClick={async () => {
                                await apiClient.post(`/assist/novels/${assistNovelId}/foreshadowings/${fsId}/status`, { status: 'advancing', action: 'advance' });
                                if (outlines[0]) { const o = { ...outlines[0] }; o.foreshadowing = o.foreshadowing.map(fi => (fi.id || fi._id) === fsId ? { ...fi, status: 'advancing' } : fi); setOutlines([o]); }
                              }} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#06b6d4' }, minWidth: 24, height: 24 }}>
                                <TrendingUpIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {f.status !== 'revealed' && (
                            <Tooltip title="标记为已揭露">
                              <IconButton size="small" onClick={async () => {
                                await apiClient.post(`/assist/novels/${assistNovelId}/foreshadowings/${fsId}/status`, { status: 'revealed', action: 'reveal' });
                                if (outlines[0]) { const o = { ...outlines[0] }; o.foreshadowing = o.foreshadowing.map(fi => (fi.id || fi._id) === fsId ? { ...fi, status: 'revealed' } : fi); setOutlines([o]); }
                              }} sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#7dd3fc' }, minWidth: 24, height: 24 }}>
                                <CheckCircleIcon sx={{ fontSize: 12 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Chip label={fsStatus.label} size="small"
                            onClick={() => setEditForeshadowingDialog(f)}
                            sx={{ height: 20, fontSize: 10, bgcolor: fsStatus.color + '20', color: fsStatus.color, cursor: 'pointer',
                              border: `1px solid ${fsStatus.color}40` }} />
                          <IconButton size="small" onClick={() => setEditForeshadowingDialog(f)}
                            sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#06b6d4' }, minWidth: 24, height: 24 }}>
                            <EditIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      </Box>

                      {/* 伏笔进度时间线：起始铺垫 → 推进章 → 揭露章 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75, flexWrap: 'wrap' }}>
                        {plantedChapter ? (
                          <Chip label={`铺垫第${plantedChapter.chapterNumber}章`} size="small"
                            onClick={() => { const c = plantedChapter; setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                            sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(255,215,0,0.15)', color: '#ffd700', cursor: 'pointer' }} />
                        ) : (
                          <Chip label="未设铺垫章" size="small"
                            onClick={() => setEditForeshadowingDialog(f)}
                            sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(249,115,22,0.15)', color: '#fb923c', cursor: 'pointer' }} />
                        )}
                        {advanceChapters.length > 0 && advanceChapters.map(c => (
                          <Chip key={c._id} label={`第${c.chapterNumber}章`} size="small"
                            onClick={() => { setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                            sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(6,182,212,0.15)', color: '#67e8f9', cursor: 'pointer' }} />
                        ))}
                        {revealChapter ? (
                          <Chip label={`第${revealChapter.chapterNumber}章揭露`} size="small"
                            onClick={() => { const c = revealChapter; setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                            sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.15)', color: '#7dd3fc', cursor: 'pointer' }} />
                        ) : (
                          f.status !== 'revealed' && (
                            <Chip label="未设揭露章" size="small"
                              onClick={() => setEditForeshadowingDialog(f)}
                              sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(249,115,22,0.15)', color: '#fb923c', cursor: 'pointer' }} />
                          )
                        )}
                        {totalSpan && (
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, ml: 0.5 }}>
                            {progressStep ? `推进第${progressStep}/${totalSpan}章` : `共${totalSpan}章跨度`}
                          </Typography>
                        )}
                      </Box>

                      {f.clue && <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, mb: 0.5 }}>线索：{f.clue}</Typography>}
                      {f.clueDistribution && <Typography sx={{ color: 'rgba(255,215,0,0.5)', fontSize: 10, mb: 0.5 }}>线索分布：{f.clueDistribution}</Typography>}
                      {f.revealMethod && <Typography sx={{ color: 'rgba(6,182,212,0.6)', fontSize: 10, mb: 0.5 }}>揭示方式：{f.revealMethod}</Typography>}
                      {relatedCharNames.length > 0 && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, mb: 0.5 }}>
                          涉及角色：{relatedCharNames.join('、')}
                        </Typography>
                      )}
                      {f.relatedForeshadowings && f.relatedForeshadowings.length > 0 && (
                        <Typography sx={{ color: 'rgba(168,85,247,0.5)', fontSize: 10, mb: 0.5 }}>
                          ↗ 关联伏笔：{f.relatedForeshadowings.join('、')}
                        </Typography>
                      )}
                      {/* 实际引用本章纲的章节列表 */}
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                        已引用：{relatedChapters.length > 0
                          ? relatedChapters.map(c => `第${c.chapterNumber}章`).join('、')
                          : '暂无章节引用'}
                      </Typography>
                      {relatedChapters.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {relatedChapters.map(c => (
                            <Chip key={c._id} label={`第${c.chapterNumber}章`} size="small"
                              onClick={() => { setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                              sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(236,72,153,0.15)', color: '#f9a8d4', cursor: 'pointer' }} />
                          ))}
                        </Box>
                      )}

                      {/* 章节状态时间轴（存于全局进度 foreshadowingChapterStates） */}
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <Typography sx={{ color: '#ec4899', fontSize: 9, fontWeight: 700, mb: 0.5 }}>章节状态记录</Typography>
                        {(pg.foreshadowingChapterStates || [])
                          .filter(s => String(s.foreshadowingId) === String(fsId))
                          .sort((a, b) => a.chapterNumber - b.chapterNumber)
                          .map((rec) => {
                            const fsSt = FORESHADOWING_STATUS_OPTIONS.find(x => x.value === rec.status) || FORESHADOWING_STATUS_OPTIONS[0];
                            return (
                              <Box
                                component={motion.div}
                                layout
                                key={rec.id || `${fsId}-${rec.chapterNumber}`}
                                initial={motionReduced ? false : { opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={motionReduced ? undefined : { x: 4, boxShadow: '0 4px 16px rgba(236,72,153,0.14)' }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.75,
                                  p: 0.875,
                                  mb: 0.5,
                                  borderRadius: 1,
                                }}
                              >
                                <Chip label={`第${rec.chapterNumber}章`} size="small"
                                  sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(236,72,153,0.2)', color: '#f9a8d4', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 10, color: fsSt.color, fontWeight: 600 }}>{fsSt.label}</Typography>
                                  </Box>
                                  {rec.narrativeNote ? (
                                    <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                      {rec.narrativeNote}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <IconButton size="small"
                                  onClick={() => setForeshadowingChapterStateDialog({
                                    open: true,
                                    foreshadowingId: fsId,
                                    title: f.title || '',
                                    recordId: rec.id,
                                    chapterNumber: String(rec.chapterNumber),
                                    chapterId: rec.chapterId || '',
                                    chapterTitle: rec.chapterTitle || '',
                                    status: rec.status || 'pending',
                                    narrativeNote: rec.narrativeNote || '',
                                  })}
                                  sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#ec4899' }, minWidth: 24, height: 24 }}>
                                  <EditIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                                <IconButton size="small"
                                  onClick={async () => {
                                    const list = (pg.foreshadowingChapterStates || []).filter(x => x.id !== rec.id);
                                    try {
                                      await saveProgressData('foreshadowingChapterStates', list);
                                      setProgressData(prev => prev ? { ...prev, foreshadowingChapterStates: list } : prev);
                                      setSnackbar({ open: true, message: '已删除', severity: 'success' });
                                    } catch (err) {}
                                  }}
                                  sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#f87171' }, minWidth: 24, height: 24 }}>
                                  <DeleteIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            );
                          })}
                        <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.02 }} whileTap={motionReduced ? undefined : { scale: 0.97 }} sx={{ display: 'inline-block', mt: 0.5 }}>
                        <Button size="small" variant="outlined"
                          className="assist-chapter-ai-glow-btn workspace-progress-add-record-btn"
                          startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                          onClick={() => setForeshadowingChapterStateDialog({
                            open: true,
                            foreshadowingId: fsId,
                            title: f.title || '',
                            recordId: null,
                            chapterNumber: chapters[0] ? String(chapters[0].chapterNumber) : '',
                            chapterId: chapters[0]?._id || '',
                            chapterTitle: chapters[0]?.title || '',
                            status: f.status || 'pending',
                            narrativeNote: '',
                          })}
                          sx={{ mt: 0, fontSize: 10, minHeight: 30, px: 1.25 }}>
                          添加本章伏笔记录
                        </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
              {/* 分批渲染：伏笔数量超过当前可见数时显示加载更多 */}
              {(outlines[0]?.foreshadowing || []).length > fsVisibleCount && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <Button size="small" variant="outlined"
                    onClick={() => setFsVisibleCount(v => v + PROGRESS_LIST_PAGE_SIZE)}
                    sx={{ fontSize: 11, color: 'rgba(236,72,153,0.8)', borderColor: 'rgba(236,72,153,0.3)', '&:hover': { borderColor: 'rgba(236,72,153,0.6)', bgcolor: 'rgba(236,72,153,0.08)' } }}>
                    加载更多伏笔（已显示 {fsVisibleCount}/{(outlines[0]?.foreshadowing || []).length}）
                  </Button>
                </Box>
              )}
            </Box>
            </motion.div>
          )}

          {/* ── Tab 4: 支线进展 ── */}
          {progressTab === 4 && (
            <motion.div
              key="progress-tab-4"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {(outlines[0]?.subPlots || []).length === 0 ? (
                <EmptyState icon="" msg="暂无支线" sub="在大纲面板中添加支线后可在此追踪进展" />
              ) : (
                (outlines[0].subPlots || []).slice(0, spVisibleCount).map(s => {
                  const spStatus = SUBPLOT_STATUS_OPTIONS.find(x => x.value === s.status) || SUBPLOT_STATUS_OPTIONS[0];
                  // 性能优化：使用 chapterIndexes 索引查找，替代逐卡片 chapters.filter 全表扫描
                  const relatedChapters = (chapterIndexes.bySubplotId.get(s.id || s._id) || []).slice().sort((a, b) => a.chapterNumber - b.chapterNumber);

                  return (
                    <Box
                      component={motion.div}
                      layout={heavySpList ? false : undefined}
                      key={s.id || s._id}
                      initial={motionReduced ? false : { opacity: 0, y: 14 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
                      whileHover={motionReduced ? undefined : { y: -2 }}
                      className="workspace-progress-item-card workspace-progress-item-card--sp"
                      sx={{ borderRadius: 1.5, p: 1.5, pl: 2.25, mb: 1, '&:hover': { borderColor: 'rgba(125, 211, 252, 0.38)' } }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.5, bgcolor: spStatus.color, flexShrink: 0 }} />
                          <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{typeof s.title === 'string' ? s.title : '未命名支线'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9 }}>优先级 {s.priority}</Typography>
                          <Chip label={spStatus.label} size="small"
                            onClick={() => setEditSubplotDialog(s)}
                            sx={{ height: 20, fontSize: 10, bgcolor: spStatus.color + '20', color: spStatus.color, cursor: 'pointer' }} />
                          <IconButton size="small" onClick={() => setEditSubplotDialog(s)}
                            sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#7dd3fc' }, minWidth: 24, height: 24 }}>
                            <EditIcon sx={{ fontSize: 12 }} />
                          </IconButton>
                        </Box>
                      </Box>
                      {s.description && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mb: 0.75 }}>{s.description}</Typography>
                      )}
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                        关联章节：{relatedChapters.length > 0
                          ? relatedChapters.map(c => `第${c.chapterNumber}章${c.title ? `·${c.title.slice(0, 5)}` : ''}`).join('、')
                          : '暂无关联'}
                      </Typography>
                      {relatedChapters.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {relatedChapters.map(c => (
                            <Chip key={c._id} label={`第${c.chapterNumber}章`} size="small"
                              onClick={() => { setSelectedChapterForEdit(c); setCurrentChapter(c); setChapterContent(c.content || ''); setActivePanel('chapters'); }}
                              sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.12)', color: '#bae6fd', cursor: 'pointer' }} />
                          ))}
                        </Box>
                      )}

                      {/* 章节状态时间轴（存于全局进度 subplotChapterStates） */}
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                        <Typography sx={{ color: '#7dd3fc', fontSize: 9, fontWeight: 700, mb: 0.5 }}>章节状态记录</Typography>
                        {(pg.subplotChapterStates || [])
                          .filter(rec => String(rec.subplotId) === String((s.id || s._id)))
                          .sort((a, b) => a.chapterNumber - b.chapterNumber)
                          .map((rec) => {
                            const spSt = SUBPLOT_STATUS_OPTIONS.find(x => x.value === rec.status) || SUBPLOT_STATUS_OPTIONS[0];
                            return (
                              <Box
                                component={motion.div}
                                layout
                                key={rec.id || `sp-${s.id || s._id}-${rec.chapterNumber}`}
                                initial={motionReduced ? false : { opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                                whileHover={motionReduced ? undefined : { x: 4, boxShadow: '0 4px 16px rgba(56,189,248,0.18)' }}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 0.75,
                                  p: 0.875,
                                  mb: 0.5,
                                  borderRadius: 1,
                                }}
                              >
                                <Chip label={`第${rec.chapterNumber}章`} size="small"
                                  sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.16)', color: '#e0f2fe', flexShrink: 0 }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: 10, color: spSt.color, fontWeight: 600 }}>{spSt.label}</Typography>
                                    {rec.progress && <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>· {rec.progress}</Typography>}
                                  </Box>
                                  {rec.narrativeNote ? (
                                    <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', mt: 0.25, whiteSpace: 'pre-wrap' }}>
                                      {rec.narrativeNote}
                                    </Typography>
                                  ) : null}
                                </Box>
                                <IconButton size="small"
                                  onClick={() => setSubplotChapterStateDialog({
                                    open: true,
                                    subplotId: s.id || s._id,
                                    title: s.title || '',
                                    recordId: rec.id,
                                    chapterNumber: String(rec.chapterNumber),
                                    chapterId: rec.chapterId || '',
                                    chapterTitle: rec.chapterTitle || '',
                                    status: rec.status || 'active',
                                    progress: rec.progress || '',
                                    narrativeNote: rec.narrativeNote || '',
                                  })}
                                  sx={{ color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#7dd3fc' }, minWidth: 24, height: 24 }}>
                                  <EditIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                                <IconButton size="small"
                                  onClick={async () => {
                                    const list = (pg.subplotChapterStates || []).filter(x => x.id !== rec.id);
                                    try {
                                      await saveProgressData('subplotChapterStates', list);
                                      setProgressData(prev => prev ? { ...prev, subplotChapterStates: list } : prev);
                                      setSnackbar({ open: true, message: '已删除', severity: 'success' });
                                    } catch (err) {}
                                  }}
                                  sx={{ color: 'rgba(255,255,255,0.2)', '&:hover': { color: '#f87171' }, minWidth: 24, height: 24 }}>
                                  <DeleteIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Box>
                            );
                          })}
                        <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.02 }} whileTap={motionReduced ? undefined : { scale: 0.97 }} sx={{ display: 'inline-block', mt: 0.5 }}>
                        <Button size="small" variant="outlined"
                          className="assist-chapter-ai-glow-btn workspace-progress-add-record-btn"
                          startIcon={<AddIcon sx={{ fontSize: 12 }} />}
                          onClick={() => setSubplotChapterStateDialog({
                            open: true,
                            subplotId: s.id || s._id,
                            title: s.title || '',
                            recordId: null,
                            chapterNumber: chapters[0] ? String(chapters[0].chapterNumber) : '',
                            chapterId: chapters[0]?._id || '',
                            chapterTitle: chapters[0]?.title || '',
                            status: s.status || 'active',
                            progress: '',
                            narrativeNote: '',
                          })}
                          sx={{ mt: 0, fontSize: 10, minHeight: 30, px: 1.25 }}>
                          添加本章支线记录
                        </Button>
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
              {/* 分批渲染：支线数量超过当前可见数时显示加载更多 */}
              {(outlines[0]?.subPlots || []).length > spVisibleCount && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <Button size="small" variant="outlined"
                    onClick={() => setSpVisibleCount(v => v + PROGRESS_LIST_PAGE_SIZE)}
                    sx={{ fontSize: 11, color: 'rgba(125,211,252,0.8)', borderColor: 'rgba(125,211,252,0.3)', '&:hover': { borderColor: 'rgba(125,211,252,0.6)', bgcolor: 'rgba(125,211,252,0.08)' } }}>
                    加载更多支线（已显示 {spVisibleCount}/{(outlines[0]?.subPlots || []).length}）
                  </Button>
                </Box>
              )}
            </Box>
            </motion.div>
          )}

          {/* ── Tab 5: 警告 ── */}
          {progressTab === 5 && (
            <motion.div
              key="progress-tab-5"
              initial={motionReduced ? false : { opacity: 0, x: 32, filter: 'blur(8px)' }}
              animate={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
              exit={motionReduced ? undefined : { opacity: 0, x: -28, filter: 'blur(6px)' }}
              transition={{ duration: motionReduced ? 0 : 0.42, ease: [0.16, 1, 0.3, 1] }}
              style={{ width: '100%' }}
            >
            <Box>
              {stats.warnings.length === 0 ? (
                <EmptyState icon="" msg="暂无警告" sub="小说整体逻辑一致，继续加油！" />
              ) : (
                stats.warnings.map((w, idx) => (
                  <Box
                    component={motion.div}
                    layout
                    key={`warn-${idx}`}
                    initial={motionReduced ? false : { opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: motionReduced ? 0 : idx * 0.07, duration: motionReduced ? 0 : 0.35, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={motionReduced ? undefined : { y: -2 }}
                    className="workspace-progress-item-card"
                    sx={{
                      borderRadius: 1.5, p: 1.25, mb: 0.75,
                      '&:hover': { borderColor: w.type === 'warning' ? 'rgba(249,115,22,0.38)' : 'rgba(125,211,252,0.38)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: w.type === 'warning' ? '#f97316' : '#38bdf8', flexShrink: 0, boxShadow: w.type === 'warning' ? '0 0 8px rgba(249,115,22,0.6)' : '0 0 8px rgba(56,189,248,0.6)' }} />
                      <Typography sx={{ color: w.type === 'warning' ? '#fdba74' : '#bae6fd', fontSize: 12, fontWeight: 600 }}>
                        {w.title}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, pl: 1.5 }}>
                      {w.description}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
            </motion.div>
          )}
          </AnimatePresence>
        </Box>

        {/* 章节状态修改弹窗 */}
        {editForeshadowingDialog && (
          <Dialog open onClose={() => setEditForeshadowingDialog(null)} maxWidth="sm" fullWidth
            PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(236,72,153,0.3)' } }}>
            <DialogTitle sx={{ color: '#ec4899', pb: 1 }}>
              编辑伏笔
            </DialogTitle>
            <DialogContent>
              <ForeshadowingEditForm
                initial={editForeshadowingDialog}
                chapters={chapters}
                characters={characters}
                onSave={async (data) => {
                  try {
                    const outline = { ...outlines[0] };
                    outline.foreshadowing = outline.foreshadowing.map(f => (f.id === editForeshadowingDialog.id || f._id === editForeshadowingDialog._id) ? { ...f, ...data } : f);
                    await apiClient.post(`/assist/novels/${assistNovelId}/outline`, outline);
                    setOutlines([outline]);
                    setSnackbar({ open: true, message: '伏笔已更新', severity: 'success' });
                  } catch(e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                  setEditForeshadowingDialog(null);
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setEditForeshadowingDialog(null)} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* 支线编辑弹窗 */}
        {editSubplotDialog && (
          <Dialog open onClose={() => setEditSubplotDialog(null)} maxWidth="sm" fullWidth
            PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(125,211,252,0.38)' } }}>
            <DialogTitle sx={{ color: '#7dd3fc', pb: 1 }}>
              编辑支线
            </DialogTitle>
            <DialogContent>
              <SubplotEditForm
                initial={editSubplotDialog}
                chapters={chapters}
                characters={characters}
                onSave={async (data) => {
                  try {
                    // 关键修复：确保 outlines[0] 存在且 subPlots 数组存在
                    if (!outlines[0]) {
                      setSnackbar({ open: true, message: '大纲数据异常，请刷新页面', severity: 'error' });
                      return;
                    }
                    const outline = { ...outlines[0] };
                    if (!outline.subPlots) outline.subPlots = [];
                    outline.subPlots = outline.subPlots.map(s => (s.id === editSubplotDialog.id || s._id === editSubplotDialog._id) ? { ...s, ...data } : s);
                    const saveResp = await apiClient.post(`/assist/novels/${assistNovelId}/outline`, outline);
                    // 保存后用后端返回的完整数据更新状态
                    if (saveResp.data?.success && saveResp.data?.data) {
                      setOutlines([saveResp.data.data]);
                    } else {
                      setOutlines([outline]);
                    }
                    setSnackbar({ open: true, message: '支线已更新', severity: 'success' });
                  } catch(e) {
                    console.error('[支线编辑] 保存失败:', e);
                    setSnackbar({ open: true, message: '保存失败', severity: 'error' });
                  }
                  setEditSubplotDialog(null);
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setEditSubplotDialog(null)} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* 角色状态编辑弹窗 - 完整版 */}
        {editCharacterDialog && (
          <Dialog open onClose={() => setEditCharacterDialog(null)} maxWidth="sm" fullWidth
            PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(125,211,252,0.38)' } }}>
            <DialogTitle sx={{ color: '#7dd3fc', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: editCharacterDialog.color || '#38bdf8', fontSize: 14 }}>{editCharacterDialog.name?.charAt(0)}</Avatar>
              修改角色状态
            </DialogTitle>
            <DialogContent dividers sx={{ bgcolor: 'rgba(0,0,0,0.3)', borderColor: 'rgba(125,211,252,0.2)' }}>
              {/* 状态选择 */}
              <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>角色状态</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                {['alive', 'injured', 'captured', 'missing', 'dead'].map(status => {
                  const s = CHARACTER_LIFE_STATUS.find(x => x.value === status);
                  return (
                    <Chip key={status} label={s?.label} size="small"
                      onClick={async () => {
                        try {
                          await apiClient.post(`/assist/novels/${assistNovelId}/characters`, { ...editCharacterDialog, lifeStatus: status });
                          setCharacters(prev => prev.map(c => c._id === editCharacterDialog._id ? { ...c, lifeStatus: status } : c));
                          setSnackbar({ open: true, message: `角色状态已更新为「${s?.label}」`, severity: 'success' });
                        } catch(e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                      }}
                      sx={{ cursor: 'pointer', bgcolor: editCharacterDialog.lifeStatus === status ? `${s?.color}25` : 'rgba(255,255,255,0.06)', color: editCharacterDialog.lifeStatus === status ? s?.color : 'rgba(255,255,255,0.5)', border: `1px solid ${editCharacterDialog.lifeStatus === status ? s?.color + '60' : 'rgba(255,255,255,0.1)'}` }}
                    />
                  );
                })}
              </Box>

              {/* 情感状态 */}
              <Typography sx={{ color: '#ec4899', fontSize: 11, mb: 0.5, fontWeight: 600 }}>情感状态</Typography>
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                  value={editCharacterDialog.emotionStatus || 'normal'}
                  onChange={async (e) => {
                    try {
                      await apiClient.post(`/assist/novels/${assistNovelId}/characters`, { ...editCharacterDialog, emotionStatus: e.target.value });
                      setCharacters(prev => prev.map(c => c._id === editCharacterDialog._id ? { ...c, emotionStatus: e.target.value } : c));
                      setSnackbar({ open: true, message: '情感状态已更新', severity: 'success' });
                    } catch(e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                  }}
                  sx={{ fontSize: 12, bgcolor: 'rgba(236,72,153,0.05)' }}
                >
                  {EMOTION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>

              {/* 当前状态描述 */}
              <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>当前状态描述</Typography>
              <TextField
                fullWidth size="small" multiline minRows={2} maxRows={4}
                defaultValue={editCharacterDialog.currentState || ''}
                placeholder="例如：重伤逃亡中，暗中积蓄力量，准备复仇..."
                onBlur={async (e) => {
                  if (e.target.value !== (editCharacterDialog.currentState || '')) {
                    try {
                      await apiClient.post(`/assist/novels/${assistNovelId}/characters`, { ...editCharacterDialog, currentState: e.target.value });
                      setCharacters(prev => prev.map(c => c._id === editCharacterDialog._id ? { ...c, currentState: e.target.value } : c));
                    } catch(e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                  }
                }}
                sx={{ mb: 2, '& .MuiInputBase-root': { bgcolor: 'rgba(56,189,248,0.06)' } }}
              />

              {/* 关联关系 */}
              {(() => {
                const rels = (progressData?.relationshipChanges || []).filter(r => r.fromCharacterId === editCharacterDialog._id || r.toCharacterId === editCharacterDialog._id);
                if (rels.length === 0) return null;
                return (
                  <>
                    <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>与其他角色的关系</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {rels.map((r, idx) => {
                        const otherCharId = r.fromCharacterId === editCharacterDialog._id ? r.toCharacterId : r.fromCharacterId;
                        const otherChar = characters.find(c => c._id === otherCharId);
                        return (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(56,189,248,0.06)', borderRadius: 1, border: '1px solid rgba(125,211,252,0.22)' }}>
                            <Avatar sx={{ width: 24, height: 24, bgcolor: otherChar?.color || '#38bdf8', fontSize: 10 }}>{otherChar?.name?.charAt(0) || '?'}</Avatar>
                            <Typography sx={{ color: '#fff', fontSize: 12 }}>{otherChar?.name || '未知'}</Typography>
                            <Chip label={r.relationType || '其他'} size="small" sx={{ height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.18)', color: '#e0f2fe' }} />
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, ml: 'auto' }}>第{r.chapterNumber}章</Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setEditCharacterDialog(null)} sx={{ color: 'rgba(255,255,255,0.5)' }}>关闭</Button>
            </DialogActions>
          </Dialog>
        )}

        {/* 按章记录角色状态（全局进度时间轴） */}
        <Dialog
          open={characterChapterStateDialog.open}
          onClose={() => setCharacterChapterStateDialog((d) => ({ ...d, open: false }))}
          maxWidth="xs"
          PaperProps={{ sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(125,211,252,0.42)' } }}
        >
          <DialogTitle sx={{ color: '#e0f2fe', fontSize: 14, pb: 0 }}>
            记录角色状态 · 第{characterChapterStateDialog.chapterNumber || '?'}章
          </DialogTitle>
          <DialogContent sx={{ pt: 1.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, mb: 1.5 }}>
              {characterChapterStateDialog.characterName || '角色'} — 选择章节并填写本章在该角色身上的状态（可与角色库「当前状态」不同，用于时间轴）。
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>对应章节</InputLabel>
              <Select
                label="对应章节"
                value={characterChapterStateDialog.chapterNumber || ''}
                onChange={(e) => {
                  const num = e.target.value;
                  const ch = chapters.find((c) => String(c.chapterNumber) === String(num));
                  setCharacterChapterStateDialog((d) => ({
                    ...d,
                    chapterNumber: num,
                    chapterId: ch?._id || '',
                  }));
                }}
                sx={{ fontSize: 12, bgcolor: 'rgba(255,255,255,0.04)' }}
              >
                {chapters.slice().sort((a, b) => a.chapterNumber - b.chapterNumber).map((c) => (
                  <MenuItem key={c._id} value={String(c.chapterNumber)}>
                    第{c.chapterNumber}章{c.title ? ` · ${c.title.slice(0, 12)}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>生命状态</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={characterChapterStateDialog.lifeStatus || 'alive'}
                onChange={(e) => setCharacterChapterStateDialog((d) => ({ ...d, lifeStatus: e.target.value }))}
                sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.08)' }}
              >
                {CHARACTER_LIFE_STATUS.map((l) => (
                  <MenuItem key={l.value} value={l.value}>{l.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#ec4899', fontSize: 11, mb: 0.5, fontWeight: 600 }}>情感状态</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={characterChapterStateDialog.emotionStatus || ''}
                onChange={(e) => setCharacterChapterStateDialog((d) => ({ ...d, emotionStatus: e.target.value }))}
                displayEmpty
                sx={{ fontSize: 12, bgcolor: 'rgba(236,72,153,0.06)' }}
              >
                <MenuItem value=""><em>（未填写）</em></MenuItem>
                {EMOTION_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>剧情状态描述</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={characterChapterStateDialog.narrativeNote || ''}
              onChange={(e) => setCharacterChapterStateDialog((d) => ({ ...d, narrativeNote: e.target.value }))}
              placeholder="例如：本章被伏击重伤，与某某决裂，开始暗中复仇……"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(56,189,248,0.06)', fontSize: 12 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => setCharacterChapterStateDialog((d) => ({ ...d, open: false }))} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            <Button
              variant="contained"
              onClick={async () => {
                const d = characterChapterStateDialog;
                const chNum = parseInt(String(d.chapterNumber), 10);
                if (!d.characterId || Number.isNaN(chNum)) {
                  setSnackbar({ open: true, message: '请选择章节', severity: 'warning' });
                  return;
                }
                const pgLocal = progressData || {};
                const list = [...(pgLocal.characterChapterStates || [])];
                const id = d.recordId || `ccs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const entry = {
                  id,
                  characterId: d.characterId,
                  chapterNumber: chNum,
                  chapterId: d.chapterId || '',
                  lifeStatus: d.lifeStatus || 'alive',
                  emotionStatus: d.emotionStatus || '',
                  narrativeNote: (d.narrativeNote || '').trim(),
                  updatedAt: new Date().toISOString(),
                };
                const sameIdx = list.findIndex(
                  (x) => String(x.characterId) === String(d.characterId) && Number(x.chapterNumber) === chNum);
                if (d.recordId) {
                  const i = list.findIndex((x) => x.id === d.recordId);
                  if (i >= 0) list[i] = entry;
                } else if (sameIdx >= 0) {
                  list[sameIdx] = { ...entry, id: list[sameIdx].id || id };
                } else {
                  list.push(entry);
                }
                list.sort((a, b) => a.chapterNumber - b.chapterNumber);
                try {
                  await saveProgressData('characterChapterStates', list);
                  setProgressData((prev) => (prev ? { ...prev, characterChapterStates: list } : { characterChapterStates: list }));
                  setCharacterChapterStateDialog((prev) => ({ ...prev, open: false }));
                  setSnackbar({ open: true, message: '已保存本章角色状态', severity: 'success' });
                } catch (err) { /* saveProgressData 已提示 */ }
              }}
              sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}
            >
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── 关系章节状态弹窗 ── */}
        <Dialog open={relationshipChapterStateDialog.open} onClose={() => setRelationshipChapterStateDialog((d) => ({ ...d, open: false }))} maxWidth="sm" fullWidth
          PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(125,211,252,0.42)' } }}>
          <DialogTitle sx={{ color: '#7dd3fc', pb: 1 }}>
            记录关系变化 · 第{relationshipChapterStateDialog.chapterNumber || '?'}章
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'rgba(56,189,248,0.04)', borderColor: 'rgba(125,211,252,0.18)' }}>
            <Typography sx={{ color: '#fff', fontSize: 12, mb: 1.5 }}>
              {relationshipChapterStateDialog.fromCharacterName || '?'} → {relationshipChapterStateDialog.toCharacterName || '?'}
              <Chip label={relationshipChapterStateDialog.relationType || '其他'} size="small" sx={{ ml: 1, height: 18, fontSize: 9, bgcolor: 'rgba(56,189,248,0.18)', color: '#e0f2fe' }} />
            </Typography>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>选择章节</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={relationshipChapterStateDialog.chapterNumber || ''}
                onChange={(e) => {
                  const sel = chapters.find(c => String(c.chapterNumber) === String(e.target.value));
                  setRelationshipChapterStateDialog((d) => ({ ...d, chapterNumber: String(e.target.value), chapterId: sel?._id || '' }));
                }}
                displayEmpty
                sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.07)' }}
              >
                <MenuItem value=""><em>（请选择章节）</em></MenuItem>
                {chapters.map(c => <MenuItem key={c._id} value={String(c.chapterNumber)}>第{c.chapterNumber}章{c.title ? ` · ${c.title}` : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: 11 }}>变化前状态</InputLabel>
                <Select
                  value={relationshipChapterStateDialog.fromStatus || '陌生'}
                  onChange={(e) => setRelationshipChapterStateDialog((d) => ({ ...d, fromStatus: e.target.value }))}
                  label="变化前状态"
                  sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.06)' }}
                >
                  {['陌生', '初识', '好感', '暧昧', '热恋', '冷战', '分手', '反目', '决裂', '和解', '恩人', '陌路'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7dd3fc', flexShrink: 0 }}>
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </Box>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: 11 }}>变化后状态</InputLabel>
                <Select
                  value={relationshipChapterStateDialog.toStatus || '陌生'}
                  onChange={(e) => setRelationshipChapterStateDialog((d) => ({ ...d, toStatus: e.target.value }))}
                  label="变化后状态"
                  sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.06)' }}
                >
                  {['陌生', '初识', '好感', '暧昧', '热恋', '冷战', '分手', '反目', '决裂', '和解', '恩人', '陌路'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>变化原因</Typography>
            <TextField
              fullWidth multiline minRows={2}
              value={relationshipChapterStateDialog.reason || ''}
              onChange={(e) => setRelationshipChapterStateDialog((d) => ({ ...d, reason: e.target.value }))}
              placeholder="本章中发生了什么导致关系变化……"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(56,189,248,0.06)', fontSize: 12 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => setRelationshipChapterStateDialog((d) => ({ ...d, open: false }))} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            <Button variant="contained"
              onClick={async () => {
                const d = relationshipChapterStateDialog;
                const chNum = parseInt(String(d.chapterNumber), 10);
                if (Number.isNaN(chNum)) { setSnackbar({ open: true, message: '请选择章节', severity: 'warning' }); return; }
                const pgLocal = progressData || {};
                const list = [...(pgLocal.relationshipChapterStates || [])];
                const id = d.recordId || `rcs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const entry = {
                  id,
                  fromCharacterId: d.fromCharacterId,
                  fromCharacterName: d.fromCharacterName,
                  toCharacterId: d.toCharacterId,
                  toCharacterName: d.toCharacterName,
                  relationType: d.relationType || '其他',
                  chapterNumber: chNum,
                  chapterId: d.chapterId || '',
                  fromStatus: d.fromStatus || '陌生',
                  toStatus: d.toStatus || '陌生',
                  reason: (d.reason || '').trim(),
                  updatedAt: new Date().toISOString(),
                };
                if (d.recordId) {
                  const i = list.findIndex(x => x.id === d.recordId);
                  if (i >= 0) list[i] = entry;
                } else {
                  list.push(entry);
                }
                list.sort((a, b) => a.chapterNumber - b.chapterNumber);
                try {
                  await saveProgressData('relationshipChapterStates', list);
                  setProgressData(prev => prev ? { ...prev, relationshipChapterStates: list } : { relationshipChapterStates: list });
                  setRelationshipChapterStateDialog(prev => ({ ...prev, open: false }));
                  setSnackbar({ open: true, message: '关系变化已保存', severity: 'success' });
                } catch (err) {}
              }}
              sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── 伏笔章节状态弹窗 ── */}
        <Dialog open={foreshadowingChapterStateDialog.open} onClose={() => setForeshadowingChapterStateDialog((d) => ({ ...d, open: false }))} maxWidth="sm" fullWidth
          PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(236,72,153,0.4)' } }}>
          <DialogTitle sx={{ color: '#ec4899', pb: 1 }}>
            记录伏笔状态 · 第{foreshadowingChapterStateDialog.chapterNumber || '?'}章
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'rgba(236,72,153,0.03)', borderColor: 'rgba(236,72,153,0.15)' }}>
            <Typography sx={{ color: '#fff', fontSize: 12, mb: 1.5 }}>
              {foreshadowingChapterStateDialog.title || '伏笔'} — 选择本章伏笔的呈现状态（可与大纲设定不同，用于记录实际进展）。
            </Typography>
            <Typography sx={{ color: '#ec4899', fontSize: 11, mb: 0.5, fontWeight: 600 }}>选择章节</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={foreshadowingChapterStateDialog.chapterNumber || ''}
                onChange={(e) => {
                  const sel = chapters.find(c => String(c.chapterNumber) === String(e.target.value));
                  setForeshadowingChapterStateDialog((d) => ({ ...d, chapterNumber: String(e.target.value), chapterId: sel?._id || '', chapterTitle: sel?.title || '' }));
                }}
                displayEmpty
                sx={{ fontSize: 12, bgcolor: 'rgba(236,72,153,0.06)' }}
              >
                <MenuItem value=""><em>（请选择章节）</em></MenuItem>
                {chapters.map(c => <MenuItem key={c._id} value={String(c.chapterNumber)}>第{c.chapterNumber}章{c.title ? ` · ${c.title}` : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#ec4899', fontSize: 11, mb: 0.5, fontWeight: 600 }}>伏笔状态</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={foreshadowingChapterStateDialog.status || 'pending'}
                onChange={(e) => setForeshadowingChapterStateDialog((d) => ({ ...d, status: e.target.value }))}
                sx={{ fontSize: 12, bgcolor: 'rgba(236,72,153,0.06)' }}
              >
                {FORESHADOWING_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.icon} {o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#ec4899', fontSize: 11, mb: 0.5, fontWeight: 600 }}>剧情描述</Typography>
            <TextField
              fullWidth multiline minRows={3}
              value={foreshadowingChapterStateDialog.narrativeNote || ''}
              onChange={(e) => setForeshadowingChapterStateDialog((d) => ({ ...d, narrativeNote: e.target.value }))}
              placeholder="本章中伏笔如何呈现/推进……"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(236,72,153,0.05)', fontSize: 12 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => setForeshadowingChapterStateDialog((d) => ({ ...d, open: false }))} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            <Button variant="contained"
              onClick={async () => {
                const d = foreshadowingChapterStateDialog;
                const chNum = parseInt(String(d.chapterNumber), 10);
                if (Number.isNaN(chNum)) { setSnackbar({ open: true, message: '请选择章节', severity: 'warning' }); return; }
                const pgLocal = progressData || {};
                const list = [...(pgLocal.foreshadowingChapterStates || [])];
                const id = d.recordId || `fcs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const entry = {
                  id,
                  foreshadowingId: d.foreshadowingId,
                  title: d.title || '',
                  chapterNumber: chNum,
                  chapterId: d.chapterId || '',
                  chapterTitle: d.chapterTitle || '',
                  status: d.status || 'pending',
                  narrativeNote: (d.narrativeNote || '').trim(),
                  updatedAt: new Date().toISOString(),
                };
                if (d.recordId) {
                  const i = list.findIndex(x => x.id === d.recordId);
                  if (i >= 0) list[i] = entry;
                } else {
                  list.push(entry);
                }
                list.sort((a, b) => a.chapterNumber - b.chapterNumber);
                try {
                  await saveProgressData('foreshadowingChapterStates', list);
                  setProgressData(prev => prev ? { ...prev, foreshadowingChapterStates: list } : { foreshadowingChapterStates: list });
                  setForeshadowingChapterStateDialog(prev => ({ ...prev, open: false }));
                  setSnackbar({ open: true, message: '伏笔状态已保存', severity: 'success' });
                } catch (err) {}
              }}
              sx={{ bgcolor: '#ec4899', '&:hover': { bgcolor: '#db2777' } }}>
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── 支线章节状态弹窗 ── */}
        <Dialog open={subplotChapterStateDialog.open} onClose={() => setSubplotChapterStateDialog((d) => ({ ...d, open: false }))} maxWidth="sm" fullWidth
          PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(125,211,252,0.42)' } }}>
          <DialogTitle sx={{ color: '#7dd3fc', pb: 1 }}>
            记录支线进展 · 第{subplotChapterStateDialog.chapterNumber || '?'}章
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'rgba(56,189,248,0.04)', borderColor: 'rgba(125,211,252,0.18)' }}>
            <Typography sx={{ color: '#fff', fontSize: 12, mb: 1.5 }}>
              {subplotChapterStateDialog.title || '支线'} — 选择本章支线的呈现状态（可与大纲设定不同，用于记录实际进展）。
            </Typography>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>选择章节</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={subplotChapterStateDialog.chapterNumber || ''}
                onChange={(e) => {
                  const sel = chapters.find(c => String(c.chapterNumber) === String(e.target.value));
                  setSubplotChapterStateDialog((d) => ({ ...d, chapterNumber: String(e.target.value), chapterId: sel?._id || '', chapterTitle: sel?.title || '' }));
                }}
                displayEmpty
                sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.07)' }}
              >
                <MenuItem value=""><em>（请选择章节）</em></MenuItem>
                {chapters.map(c => <MenuItem key={c._id} value={String(c.chapterNumber)}>第{c.chapterNumber}章{c.title ? ` · ${c.title}` : ''}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>支线状态</Typography>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <Select
                value={subplotChapterStateDialog.status || 'active'}
                onChange={(e) => setSubplotChapterStateDialog((d) => ({ ...d, status: e.target.value }))}
                sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.07)' }}
              >
                {SUBPLOT_STATUS_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.icon} {o.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>进展摘要</Typography>
            <TextField
              fullWidth size="small"
              value={subplotChapterStateDialog.progress || ''}
              onChange={(e) => setSubplotChapterStateDialog((d) => ({ ...d, progress: e.target.value }))}
              placeholder="本章支线有何进展……"
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(56,189,248,0.06)', fontSize: 12 } }}
            />
            <Typography sx={{ color: '#7dd3fc', fontSize: 11, mb: 0.5, fontWeight: 600 }}>剧情描述</Typography>
            <TextField
              fullWidth multiline minRows={3}
              value={subplotChapterStateDialog.narrativeNote || ''}
              onChange={(e) => setSubplotChapterStateDialog((d) => ({ ...d, narrativeNote: e.target.value }))}
              placeholder="本章中支线如何推进……"
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(56,189,248,0.06)', fontSize: 12 } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={() => setSubplotChapterStateDialog((d) => ({ ...d, open: false }))} sx={{ color: 'rgba(255,255,255,0.5)' }}>取消</Button>
            <Button variant="contained"
              onClick={async () => {
                const d = subplotChapterStateDialog;
                const chNum = parseInt(String(d.chapterNumber), 10);
                if (Number.isNaN(chNum)) { setSnackbar({ open: true, message: '请选择章节', severity: 'warning' }); return; }
                const pgLocal = progressData || {};
                const list = [...(pgLocal.subplotChapterStates || [])];
                const id = d.recordId || `scs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const entry = {
                  id,
                  subplotId: d.subplotId,
                  title: d.title || '',
                  chapterNumber: chNum,
                  chapterId: d.chapterId || '',
                  chapterTitle: d.chapterTitle || '',
                  status: d.status || 'active',
                  progress: (d.progress || '').trim(),
                  narrativeNote: (d.narrativeNote || '').trim(),
                  updatedAt: new Date().toISOString(),
                };
                if (d.recordId) {
                  const i = list.findIndex(x => x.id === d.recordId);
                  if (i >= 0) list[i] = entry;
                } else {
                  list.push(entry);
                }
                list.sort((a, b) => a.chapterNumber - b.chapterNumber);
                try {
                  await saveProgressData('subplotChapterStates', list);
                  setProgressData(prev => prev ? { ...prev, subplotChapterStates: list } : { subplotChapterStates: list });
                  setSubplotChapterStateDialog(prev => ({ ...prev, open: false }));
                  setSnackbar({ open: true, message: '支线进展已保存', severity: 'success' });
                } catch (err) {}
              }}
              sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' } }}>
              保存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 标记本书收尾弹窗 */}
        <Dialog open={endingDialogOpen} onClose={() => setEndingDialogOpen(false)} maxWidth="xs" fullWidth
          PaperProps={{ className: 'character-panel-cyber', sx: { bgcolor: 'rgba(10,11,18,0.98)', border: '1px solid rgba(239,68,68,0.4)' } }}>
          <DialogTitle sx={{ color: '#f87171', pb: 1 }}>
            🔚 标记本书收尾
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'rgba(239,68,68,0.03)', borderColor: 'rgba(239,68,68,0.15)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 12, mb: 2 }}>
              标记后，系统将从当前章节起开始为全书做收尾准备，并在「全局进度」中显示剩余章数。
            </Typography>
            {novel?.plannedEndingChapter && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 1 }}>
                <Typography sx={{ color: '#f87171', fontSize: 11, mb: 0.5 }}>当前已设置收尾计划</Typography>
                <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                  第{novel.plannedEndingChapter}章为全书最后一章
                  {(() => {
                    const cur = selectedChapterForEdit?.chapterNumber || 0;
                    const rem = cur > 0 && cur < novel.plannedEndingChapter ? novel.plannedEndingChapter - cur : null;
                    return rem != null ? `，剩余约 ${rem} 章` : '（已进入收尾章）';
                  })()}
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ color: '#f87171', fontSize: 11, fontWeight: 600, mb: 0.5 }}>
                距结尾还有多少章？
              </Typography>
              <TextField
                type="number"
                size="small"
                fullWidth
                value={endingChaptersInput}
                onChange={e => setEndingChaptersInput(Math.max(10, parseInt(e.target.value) || 10))}
                inputProps={{ min: 10, style: { color: '#fff', fontSize: 16, textAlign: 'center' } }}
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: 'rgba(239,68,68,0.06)', '& fieldset': { borderColor: 'rgba(239,68,68,0.3)' } }
                }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, mt: 0.5 }}>
                最少10章，用于收尾铺垫。可随时修改。
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5, p: 1, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 1 }}>
              <Typography sx={{ color: '#fbbf24', fontSize: 11, fontWeight: 600, mb: 0.5 }}>收尾阶段会做什么</Typography>
              <Box component="ul" sx={{ m: 0, pl: 2, '& li': { color: 'rgba(255,255,255,0.55)', fontSize: 11, mb: 0.25 } }}>
                <li>最后章跳过「为下章埋钩」，转为收尾提示</li>
                <li>全局进度突出显示待回收伏笔</li>
                <li>AI 写作在结尾章自动引导伏笔回收、人物归宿</li>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            {novel?.plannedEndingChapter && (
              <Button
                size="small"
                onClick={async () => {
                  try {
                    await apiClient.put(`/assist/novels/${assistNovelId}`, { plannedEndingChapter: null });
                    setNovel(prev => prev ? { ...prev, plannedEndingChapter: null } : prev);
                    setSnackbar({ open: true, message: '已取消收尾计划', severity: 'info' });
                  } catch (e) { setSnackbar({ open: true, message: '操作失败', severity: 'error' }); }
                  setEndingDialogOpen(false);
                }}
                sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, mr: 'auto' }}
              >
                取消收尾
              </Button>
            )}
            <Button onClick={() => setEndingDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>取消</Button>
            <Button
              variant="contained"
              onClick={async () => {
                const lastChapterNum = Math.max(0, ...chapters.map(c => c.chapterNumber));
                const targetChapter = lastChapterNum > 0 ? lastChapterNum + endingChaptersInput : null;
                if (!targetChapter) { setSnackbar({ open: true, message: '请先创建至少一章', severity: 'warning' }); return; }
                try {
                  await apiClient.put(`/assist/novels/${assistNovelId}`, { plannedEndingChapter: targetChapter });
                  setNovel(prev => prev ? { ...prev, plannedEndingChapter: targetChapter } : prev);
                  setSnackbar({ open: true, message: `已设置：第${targetChapter}章为全书最后一章，距今还有${endingChaptersInput}章收尾`, severity: 'success' });
                } catch (e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                setEndingDialogOpen(false);
              }}
              sx={{ bgcolor: '#ef4444', fontSize: 11, '&:hover': { bgcolor: '#dc2626' } }}
            >
              确认收尾
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    );
  };

  // ── 子组件：章节 Chip 网格 ──
  function ChapterChipGrid({ chapters, onChapterClick }) {
    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        {chapters.map(ch => {
          const isCompleted = ch.status === 'completed' || ch.status === 'revised';
          const isInProgress = ch.status === 'in_progress';
          const sColor = isCompleted ? '#6ee7b7' : isInProgress ? '#fde047' : 'rgba(255,255,255,0.4)';
          return (
            <Tooltip key={ch._id} title={`第${ch.chapterNumber}章${ch.title ? `「${ch.title}」` : ''} · ${ch.status === 'outline_only' ? '仅章纲' : ch.status === 'in_progress' ? '写作中' : ch.status === 'completed' ? '已完成' : '已修订'}`}>
              <Box onClick={() => onChapterClick && onChapterClick(ch)}
                sx={{
                  width: 28, height: 28, borderRadius: 1, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: isCompleted ? 'rgba(6,182,212,0.2)' : isInProgress ? 'rgba(255,215,0,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${isCompleted ? 'rgba(6,182,212,0.4)' : isInProgress ? 'rgba(255,215,0,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'scale(1.15)', boxShadow: '0 0 8px rgba(6,182,212,0.4)' }
                }}>
                <Typography sx={{ color: sColor, fontSize: 10, fontWeight: 600 }}>{ch.chapterNumber}</Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  // ── 子组件：空状态 ──
  function EmptyState({ icon, msg, sub }) {
    return (
      <Box
        component={motion.div}
        initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.94 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: motionReduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}
        sx={{ textAlign: 'center', py: 8, color: 'rgba(255,255,255,0.4)' }}
      >
        {icon && (
          <motion.div animate={motionReduced ? {} : { y: [0, -5, 0] }} transition={motionReduced ? {} : { duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
            <Typography sx={{ fontSize: 38, mb: 1.5, filter: 'drop-shadow(0 0 12px rgba(255,255,255,0.15))' }}>{icon}</Typography>
          </motion.div>
        )}
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.55)', mb: 0.5 }}>{msg}</Typography>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>{sub}</Typography>
      </Box>
    );
  }

  // ── 子组件：章节标签 ──
  function SectionLabel({ children, color = 'rgba(255,255,255,0.52)' }) {
    const hexGlow = typeof color === 'string' && color.startsWith('#');
    const lineBg = hexGlow ? `${color}22` : 'rgba(255,255,255,0.1)';
    const textShadow = hexGlow ? `0 0 10px ${color}55` : 'none';
    return (
      <Box component={motion.div}
        initial={motionReduced ? false : { opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
        sx={{ mb: 0.75, mt: 1.5 }}
      >
        <Typography sx={{ color, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', textShadow }}>
          {children}
        </Typography>
        <Box sx={{ height: 1, mt: 0.4, bgcolor: lineBg, borderRadius: 1 }} />
      </Box>
    );
  }

  // ── 子组件：添加关系变化表单（UI 对齐角色面板「角色关系网」：实线内框 + 白灰控件 + cyber 按钮） ──
  function AddRelationChangeForm({ characters, chapters, onAdd }) {
    const [fromCharId, setFromCharId] = useState('');
    const [toCharId, setToCharId] = useState('');
    const [relationType, setRelationType] = useState('其他');
    const [fromStatus, setFromStatus] = useState('陌生');
    const [toStatus, setToStatus] = useState('陌生');
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const relationChangeMenuProps = {
      PaperProps: {
        style: {
          backgroundColor: '#0c0c10',
          background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
          border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 8,
          boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
          marginTop: 4,
        },
        sx: {
          '& .MuiMenuItem-root': {
            fontSize: 11,
            color: 'rgba(255,255,255,0.88)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)' },
            '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
          },
        },
      },
    };

    const selectSx = {
      fontSize: 11,
      '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', backgroundColor: 'rgba(255,255,255,0.04)' },
      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' },
    };

    return (
      <Box className="workspace-relation-net-style-block">
        <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
          <Typography className="workspace-progress-relation-block-title">
            选择双方角色并填写关系如何变化
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mb: 1.5 }}>
            <FormControl fullWidth size="small" className="character-panel-select-wrap">
              <InputLabel shrink>角色 A</InputLabel>
              <Select
                label="角色 A"
                displayEmpty
                value={fromCharId}
                onChange={(e) => { setFromCharId(e.target.value); if (e.target.value === toCharId) setToCharId(''); }}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                MenuProps={relationChangeMenuProps}
                renderValue={(v) => {
                  if (!v) return <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>选择已创建的角色</span>;
                  const c = characters.find(ch => ch._id === v);
                  return c ? <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11 }}>{c.name}</span> : v;
                }}
                sx={selectSx}
              >
                {characters.map(c => (
                  <MenuItem key={c._id} value={c._id} sx={{ fontSize: 11 }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" className="character-panel-select-wrap">
              <InputLabel shrink>角色 B</InputLabel>
              <Select
                label="角色 B"
                displayEmpty
                value={toCharId}
                onChange={(e) => setToCharId(e.target.value)}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                MenuProps={relationChangeMenuProps}
                renderValue={(v) => {
                  if (!v) return <span style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}>选择已创建的角色</span>;
                  const c = characters.find(ch => ch._id === v);
                  return c ? <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: 11 }}>{c.name}</span> : v;
                }}
                sx={selectSx}
              >
                {characters.filter(c => c._id !== fromCharId).map(c => (
                  <MenuItem key={c._id} value={c._id} sx={{ fontSize: 11 }}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1, mb: 1.5 }}>
            <FormControl fullWidth size="small" className="character-panel-select-wrap">
              <InputLabel>关系类型</InputLabel>
              <Select
                label="关系类型"
                value={relationType}
                onChange={(e) => setRelationType(e.target.value)}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                MenuProps={relationChangeMenuProps}
                sx={selectSx}
              >
                {RELATION_CHANGE_TYPES.map(t => (
                  <MenuItem key={t} value={t} sx={{ fontSize: 11 }}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" className="character-panel-select-wrap">
              <InputLabel>变化前</InputLabel>
              <Select
                label="变化前"
                value={fromStatus}
                onChange={(e) => setFromStatus(e.target.value)}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                MenuProps={relationChangeMenuProps}
                sx={selectSx}
              >
                {EMOTION_TYPES.map(s => (
                  <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small" className="character-panel-select-wrap">
              <InputLabel>变化后</InputLabel>
              <Select
                label="变化后"
                value={toStatus}
                onChange={(e) => setToStatus(e.target.value)}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                MenuProps={relationChangeMenuProps}
                sx={selectSx}
              >
                {EMOTION_TYPES.map(s => (
                  <MenuItem key={s} value={s} sx={{ fontSize: 11 }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            size="small"
            label="变化原因（可选）"
            placeholder="简述本章中导致关系变化的原因…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.04)',
                fontSize: 11,
                color: 'rgba(255,255,255,0.9)',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
              },
            }}
            InputProps={{ style: { fontSize: 11 } }}
          />
          <Button
            fullWidth
            variant="outlined"
            size="small"
            className="workspace-btn-cyber workspace-progress-relation-record-btn"
            startIcon={loading ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : <AddIcon />}
            disabled={loading || !fromCharId || !toCharId || fromCharId === toCharId}
            onClick={async () => {
              const fromChar = characters.find(c => c._id === fromCharId);
              const toChar = characters.find(c => c._id === toCharId);
              setLoading(true);
              await onAdd({
                fromCharacterId: fromCharId, fromCharacterName: fromChar?.name || '',
                toCharacterId: toCharId, toCharacterName: toChar?.name || '',
                relationType, fromStatus, toStatus, reason,
                chapterId: currentChapter?._id || '',
                chapterNumber: currentChapter?.chapterNumber || (chapters.length + 1)
              });
              setLoading(false);
              setFromCharId(''); setToCharId(''); setReason('');
            }}
            sx={{ fontSize: 12, '&:disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' } }}
          >
            记录关系变化
          </Button>
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            py: 2.5,
            border: '1px dashed rgba(255,255,255,0.12)',
            borderRadius: 2,
          }}
        >
          <Typography className="character-panel-relation-hint">
            提交后记录将出现在上方时间轴；请确保两角为不同角色
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── 子组件：伏笔编辑表单 ──
  function ForeshadowingEditForm({ initial, chapters, characters, onSave }) {
    const sortedChapters = [...chapters].sort((a, b) => a.chapterNumber - b.chapterNumber);
    const [form, setForm] = useState({
      title: initial.title || '',
      description: initial.description || '',
      clue: initial.clue || '',
      clueDistribution: initial.clueDistribution || '',
      revealMethod: initial.revealMethod || '',
      status: initial.status || 'pending',
      relatedCharacterIds: initial.relatedCharacterIds || [],
      relatedForeshadowings: initial.relatedForeshadowings || [],
      plantedChapterId: initial.plantedChapterId || '',
      revealChapterId: initial.revealChapterId || '',
    });

    return (
      <Box>
        <TextField fullWidth size="small" label="伏笔标题" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <TextField fullWidth size="small" label="描述" value={form.description} multiline rows={2}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <TextField fullWidth size="small" label="线索描述" value={form.clue}
          onChange={e => setForm(f => ({ ...f, clue: e.target.value }))}
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <TextField fullWidth size="small" label="线索分布" value={form.clueDistribution} multiline rows={2}
          onChange={e => setForm(f => ({ ...f, clueDistribution: e.target.value }))}
          placeholder="如：第1章出现；第100章发热；第500章裂痕"
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <TextField fullWidth size="small" label="揭示方式" value={form.revealMethod}
          onChange={e => setForm(f => ({ ...f, revealMethod: e.target.value }))}
          placeholder="如：老鬼解读印记编码"
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
          <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }}>
            <InputLabel sx={{ fontSize: 12 }}>起始铺垫章</InputLabel>
            <Select label="起始铺垫章" value={form.plantedChapterId} onChange={e => setForm(f => ({ ...f, plantedChapterId: e.target.value }))}
              sx={{ fontSize: 12 }}>
              <MenuItem value="" sx={{ fontSize: 11 }}><em>未设置</em></MenuItem>
              {sortedChapters.map(c => (
                <MenuItem key={c._id} value={c._id} sx={{ fontSize: 11 }}>
                  第{c.chapterNumber}章 {c.title ? '· ' + c.title.slice(0, 5) : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" sx={{ '& .MuiInputBase-root': { bgcolor: 'rgba(6,182,212,0.05)' } }}>
            <InputLabel sx={{ fontSize: 12 }}>揭露章</InputLabel>
            <Select label="揭露章" value={form.revealChapterId} onChange={e => setForm(f => ({ ...f, revealChapterId: e.target.value }))}
              sx={{ fontSize: 12 }}>
              <MenuItem value="" sx={{ fontSize: 11 }}><em>未设置</em></MenuItem>
              {sortedChapters.map(c => (
                <MenuItem key={c._id} value={c._id} sx={{ fontSize: 11 }}>
                  第{c.chapterNumber}章 {c.title ? '· ' + c.title.slice(0, 5) : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControl fullWidth size="small" sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }}>
          <InputLabel sx={{ fontSize: 12 }}>伏笔状态</InputLabel>
          <Select label="伏笔状态" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
            sx={{ fontSize: 12 }}>
            {FORESHADOWING_STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.icon} {s.label}</MenuItem>)}
          </Select>
        </FormControl>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.75 }}>涉及角色（可多选）</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
          {characters.map(c => (
            <Chip key={c._id} label={c.name} size="small"
              onClick={() => {
                const ids = form.relatedCharacterIds.includes(c._id)
                  ? form.relatedCharacterIds.filter(id => id !== c._id)
                  : [...form.relatedCharacterIds, c._id];
                setForm(f => ({ ...f, relatedCharacterIds: ids }));
              }}
              sx={{
                height: 22, fontSize: 11, cursor: 'pointer',
                bgcolor: form.relatedCharacterIds.includes(c._id) ? 'rgba(0,240,255,0.2)' : 'rgba(255,255,255,0.06)',
                color: form.relatedCharacterIds.includes(c._id) ? '#67e8f9' : 'rgba(255,255,255,0.5)',
                border: `1px solid ${form.relatedCharacterIds.includes(c._id) ? 'rgba(0,240,255,0.4)' : 'rgba(255,255,255,0.1)'}`
              }} />
          ))}
        </Box>
        <TextField fullWidth size="small" label="关联其他伏笔" value={(form.relatedForeshadowings || []).join('、')}
          onChange={e => setForm(f => ({ ...f, relatedForeshadowings: e.target.value.split('、').map(s => s.trim()).filter(Boolean) }))}
          placeholder="填写伏笔标题，多个用顿号分隔"
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(236,72,153,0.05)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <Button fullWidth variant="outlined" onClick={() => onSave(form)}
          sx={{ color: '#7dd3fc', borderColor: 'rgba(125,211,252,0.55)', '&:hover': { borderColor: '#7dd3fc', bgcolor: 'rgba(56,189,248,0.12)' } }}
          className="workspace-progress-btn-breathe"
        >
          保存伏笔
        </Button>
      </Box>
    );
  }

  // ── 子组件：支线编辑表单 ──
  function SubplotEditForm({ initial, chapters, onSave, characters }) {
    const [form, setForm] = useState({
      title: initial.title || '',
      description: initial.description || '',
      status: initial.status || 'pending',
      priority: initial.priority || 1,
      relatedChapterIds: initial.relatedChapterIds || [],
      relatedCharacterIds: initial.relatedCharacterIds || [],
    });

    return (
      <Box>
        <TextField fullWidth size="small" label="支线标题" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(56,189,248,0.06)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <TextField fullWidth size="small" label="描述" value={form.description} multiline rows={2}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          sx={{ mb: 1.5, '& .MuiInputBase-root': { bgcolor: 'rgba(56,189,248,0.06)' } }} InputProps={{ style: { fontSize: 12 } }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: 12 }}>支线状态</InputLabel>
            <Select label="支线状态" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              sx={{ fontSize: 12, bgcolor: 'rgba(56,189,248,0.06)' }}>
              {SUBPLOT_STATUS_OPTIONS.map(s => <MenuItem key={s.value} value={s.value}>{s.icon} {s.label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth size="small" type="number" label="优先级(1-5)" value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))}
            sx={{ '& .MuiInputBase-root': { bgcolor: 'rgba(56,189,248,0.06)' } }} InputProps={{ style: { fontSize: 12 } }} />
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.75 }}>关联章节</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
          {chapters.map(c => (
            <Chip key={c._id} label={`第${c.chapterNumber}章${c.title ? `·${c.title.slice(0, 4)}` : ''}`} size="small"
              onClick={() => {
                const ids = form.relatedChapterIds.includes(c._id)
                  ? form.relatedChapterIds.filter(id => id !== c._id)
                  : [...form.relatedChapterIds, c._id];
                setForm(f => ({ ...f, relatedChapterIds: ids }));
              }}
              sx={{
                height: 22, fontSize: 11, cursor: 'pointer',
                bgcolor: form.relatedChapterIds.includes(c._id) ? 'rgba(56,189,248,0.18)' : 'rgba(255,255,255,0.06)',
                color: form.relatedChapterIds.includes(c._id) ? '#e0f2fe' : 'rgba(255,255,255,0.5)',
                border: `1px solid ${form.relatedChapterIds.includes(c._id) ? 'rgba(125,211,252,0.45)' : 'rgba(255,255,255,0.1)'}`
              }} />
          ))}
        </Box>
        {characters && characters.length > 0 && (
          <>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 0.75 }}>相关角色（可多选）</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
              {characters.map(c => (
                <Chip key={c._id} label={c.name} size="small"
                  onClick={() => {
                    const ids = form.relatedCharacterIds.includes(c._id)
                      ? form.relatedCharacterIds.filter(id => id !== c._id)
                      : [...form.relatedCharacterIds, c._id];
                    setForm(f => ({ ...f, relatedCharacterIds: ids }));
                  }}
                  sx={{
                    height: 22, fontSize: 11, cursor: 'pointer',
                    bgcolor: form.relatedCharacterIds.includes(c._id) ? 'rgba(0,240,255,0.18)' : 'rgba(255,255,255,0.06)',
                    color: form.relatedCharacterIds.includes(c._id) ? '#67e8f9' : 'rgba(255,255,255,0.5)',
                    border: `1px solid ${form.relatedCharacterIds.includes(c._id) ? 'rgba(0,240,255,0.45)' : 'rgba(255,255,255,0.1)'}`
                  }} />
              ))}
            </Box>
          </>
        )}
        <Button fullWidth variant="outlined" onClick={() => onSave(form)}
          sx={{ color: '#7dd3fc', borderColor: 'rgba(125,211,252,0.55)', '&:hover': { borderColor: '#7dd3fc', bgcolor: 'rgba(56,189,248,0.12)' } }}
          className="workspace-progress-btn-breathe"
        >
          保存支线
        </Button>
      </Box>
    );
  }

  // 辅助函数：渲染章节总览的一行
  // 备忘录 — 配套章节列表样式 + framer-motion 错帧动画
  const renderMemoPanel = () => {
    const narrow = isChaptersNarrow;
    const filteredMemos = memos.filter(
      (m) => (memoType === 'all' || m.type === memoType) && (m.title || '').includes(memoSearch)
    );

    return (
      <Box className="panel-content workspace-memo-panel" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* 标题栏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          {narrow && (
            <Tooltip title="返回编辑">
              <IconButton
                size="small"
                onClick={handleClosePanel}
                aria-label="返回编辑"
                sx={{
                  flexShrink: 0,
                  minWidth: { xs: 34, sm: 36 },
                  minHeight: { xs: 34, sm: 36 },
                  color: 'rgba(255,255,255,0.75)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  bgcolor: 'rgba(255,255,255,0.04)',
                  '&:hover': { bgcolor: 'rgba(0,240,255,0.12)', borderColor: 'rgba(0,240,255,0.35)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography sx={{ color: '#fbbf24', fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: '0 0 16px rgba(251,191,36,0.35)', flex: 1, letterSpacing: '0.02em', minWidth: 0 }}>
            备忘录
          </Typography>
          <Chip label={memos.length} size="small" sx={{ bgcolor: 'rgba(251,191,36,0.18)', color: '#fde68a', height: 24, fontSize: 12, fontWeight: 700, border: '1px solid rgba(251,191,36,0.35)' }} />
        </Box>

        {/* 搜索栏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.32, delay: motionReduced ? 0 : 0.04, ease: [0.22, 1, 0.36, 1] }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}
        >
          <TextField
            fullWidth size="small" placeholder="搜索备忘录..."
            value={memoSearch} onChange={(e) => setMemoSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(251,191,36,0.28)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(251,191,36,0.55)' },
              },
            }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.5, fontSize: 18 }} /> }}
          />
        </Box>

        {/* 筛选行 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.28, delay: motionReduced ? 0 : 0.08 }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}
        >
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap', overflowX: 'auto', pb: 0.25, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: 5 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(251,191,36,0.25)', borderRadius: 2 } }}>
            <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.04 }} whileTap={motionReduced ? undefined : { scale: 0.96 }} transition={{ type: 'spring', stiffness: 420, damping: 22 }}>
              <Chip label="全部" size="small" onClick={() => setMemoType('all')}
                sx={{ flexShrink: 0, bgcolor: memoType === 'all' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.04)', color: memoType === 'all' ? '#f1f5f9' : 'rgba(255,255,255,0.72)', border: `1px solid ${memoType === 'all' ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.1)'}`, fontSize: 11, height: 28, cursor: 'pointer', fontWeight: memoType === 'all' ? 700 : 500, '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }} />
            </Box>
            {MEMO_CATEGORIES.slice(0, 8).map((cat, ci) => (
              <Box key={cat.id} component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.04 }} whileTap={motionReduced ? undefined : { scale: 0.96 }} transition={{ type: 'spring', stiffness: 420, damping: 22 }}>
                <Chip label={cat.name} size="small" onClick={() => setMemoType(cat.id)}
                  sx={{ flexShrink: 0, bgcolor: memoType === cat.id ? `${cat.color}22` : 'rgba(255,255,255,0.04)', color: memoType === cat.id ? cat.color : 'rgba(255,255,255,0.72)', border: `1px solid ${memoType === cat.id ? cat.color : 'rgba(255,255,255,0.1)'}`, fontSize: 11, height: 28, cursor: 'pointer', fontWeight: memoType === cat.id ? 700 : 500, boxShadow: memoType === cat.id && !motionReduced ? `0 0 12px ${cat.color}40` : 'none', '&:hover': { bgcolor: `${cat.color}14` } }} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* 添加按钮 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.3, delay: motionReduced ? 0 : 0.1, ease: [0.22, 1, 0.36, 1] }}
          sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}
        >
          <Button fullWidth variant="outlined" startIcon={<AddIcon />}
            onClick={() => { setEditingMemo(null); setMemoFormData({ title: '', content: '', type: '灵感', priority: 'medium', status: 'pending' }); setActivePanel('memoEdit'); }}
            className="workspace-btn-cyber"
            sx={{ fontSize: { xs: 12, sm: 13 }, minHeight: { xs: 36, sm: 40 }, textTransform: 'none', justifyContent: 'center' }}
          >
            添加备忘录
          </Button>
        </Box>

        {/* 列表区 */}
        <Box className="memo-list" sx={{ flex: 1, overflow: 'auto', px: { xs: 1, sm: 1.25 }, py: 1, pb: 2 }}>
          {memos.length === 0 ? (
            <motion.div initial={motionReduced ? false : { opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: motionReduced ? 0 : 0.45, ease: [0.22, 1, 0.36, 1] }}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <motion.div animate={motionReduced ? {} : { y: [0, -4, 0] }} transition={motionReduced ? {} : { duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}>
                  <NoteIcon sx={{ fontSize: 52, mb: 2, opacity: 0.35, color: '#fbbf24', filter: 'drop-shadow(0 0 12px rgba(251,191,36,0.4))' }} />
                </motion.div>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 14, mb: 0.5 }}>暂无备忘录</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: 12 }}>点击上方「添加备忘录」开始记录</Typography>
              </Box>
            </motion.div>
          ) : filteredMemos.length === 0 ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', py: 6, fontSize: 13 }}>没有符合筛选的备忘录</Typography>
          ) : (
            <AnimatePresence mode="popLayout" initial={false}>
              {filteredMemos.map((memo, idx) => {
                const categoryColor = MEMO_CATEGORY_COLORS[memo.type] || '#fbbf24';
                const categoryInfo = MEMO_CATEGORIES.find(c => c.id === memo.type);
                const priorityInfo = MEMO_PRIORITIES.find(p => p.id === memo.priority);
                const statusInfo = MEMO_STATUS.find(s => s.id === memo.status);
                const hasContent = !!(memo.content && String(memo.content).trim());
                const bodyBg = hasContent ? 'rgba(251,191,36,0.07)' : 'rgba(255,255,255,0.03)';
                const bodyBorder = hasContent ? 'rgba(251,191,36,0.28)' : 'rgba(255,255,255,0.07)';
                const bodyHover = hasContent ? 'rgba(251,191,36,0.12)' : 'rgba(255,255,255,0.06)';
                const bodyShadow = hasContent ? '0 2px 12px rgba(251,191,36,0.14), inset 0 1px 0 rgba(255,255,255,0.05)' : '0 2px 8px rgba(0,0,0,0.22)';
                const animDelay = motionReduced ? 0 : Math.min(idx * 0.042, 0.52);

                return (
                  <Box
                    component={motion.div}
                    layout
                    key={memo._id}
                    initial={motionReduced ? false : { opacity: 0, x: -16, scale: 0.97 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={motionReduced ? false : { opacity: 0, x: 14, scale: 0.94, transition: { duration: 0.22, ease: [0.4, 0, 1, 1] } }}
                    transition={{ delay: animDelay, duration: motionReduced ? 0 : 0.34, ease: [0.22, 1, 0.36, 1], layout: { duration: motionReduced ? 0 : 0.24, ease: [0.22, 1, 0.36, 1] } }}
                    whileHover={motionReduced ? undefined : { y: -2, scale: 1.009, boxShadow: `0 8px 28px ${categoryColor}2e, 0 4px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.07)` }}
                    whileTap={motionReduced ? undefined : { scale: 0.982 }}
                    onClick={() => { setEditingMemo(memo); setMemoFormData(memo); setActivePanel('memoEdit'); }}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1,
                      willChange: 'transform', transform: 'translateZ(0)',
                      p: narrow ? '6px 8px' : { xs: 1.15, sm: 1.25 }, mb: narrow ? 0.4 : 0.75,
                      bgcolor: bodyBg, border: `1px solid ${bodyBorder}`, borderRadius: narrow ? 1 : 1.5,
                      cursor: 'pointer', boxShadow: bodyShadow, position: 'relative', overflow: 'hidden',
                      '&:hover': { bgcolor: bodyHover },
                      '&::before': { content: '""', position: 'absolute', left: 0, top: '14%', bottom: '14%', width: 3, borderRadius: '0 2px 2px 0', bgcolor: categoryColor, boxShadow: `0 0 10px ${categoryColor}` },
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, pl: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, flexWrap: 'wrap' }}>
                        <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: narrow ? 13 : { xs: 14, sm: 15 }, letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                          {memo.title}
                        </Typography>
                        <Chip label={categoryInfo?.name || memo.type} size="small"
                          sx={{ height: 18, fontSize: 9, fontWeight: 800, flexShrink: 0, bgcolor: `${categoryColor}22`, color: categoryColor, border: `1px solid ${categoryColor}50`, '& .MuiChip-label': { px: 0.5 } }} />
                        {priorityInfo && <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.2 }} transition={{ type: 'spring', stiffness: 500, damping: 16 }} sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: priorityInfo.color, flexShrink: 0, boxShadow: `0 0 5px ${priorityInfo.color}` }} title={`优先级: ${priorityInfo.name}`} />}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {statusInfo && <Typography sx={{ color: statusInfo.color, fontSize: narrow ? 11 : 12 }}>{statusInfo.name}</Typography>}
                      </Box>
                      {memo.content && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.32)', fontSize: narrow ? 10 : 11, mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', lineHeight: 1.45 }}>
                          {memo.content}
                        </Typography>
                      )}
                    </Box>
                    <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.08 }} transition={{ type: 'spring', stiffness: 400, damping: 20 }} sx={{ flexShrink: 0, color: 'rgba(255,255,255,0.4)', pt: 0.25 }}>
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); setEditingMemo(memo); setMemoFormData(memo); setActivePanel('memoEdit'); }}
                        sx={{ color: 'rgba(255,255,255,0.45)', '&:hover': { color: '#fbbf24', bgcolor: 'rgba(251,191,36,0.12)' }, ...(narrow ? { p: 0.35, minWidth: 30, minHeight: 30 } : { minWidth: 34, minHeight: 34 }) }}>
                        <EditIcon sx={{ fontSize: narrow ? 15 : 17 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </AnimatePresence>
          )}
        </Box>
      </Box>
    );
  };

  // 关系类型 → 统一赛博风配色（柔和协调，不土）
  const RELATION_TYPES = ['父子', '母女', '兄弟', '姐妹', '恋人', '夫妻', '师徒', '好友', '宿敌', '主仆', '竞争对手', '其他', '自定义'];
  const RELATION_TYPE_COLORS = {
    父子: '#b8d4e8', 母女: '#e8c8e0', 兄弟: '#c8e0d8', 姐妹: '#d8c8e8',
    恋人: '#e8b8b8', 夫妻: '#e8d8b0', 师徒: '#b0c8e8', 好友: '#b8e8d0',
    宿敌: '#e8b0b0', 主仆: '#d0c0e8', 竞争对手: '#e8c8a0', 其他: '#c0c8d4', 自定义: '#a8b8c8'
  };
  const glowStyle = (hex) => ({ textShadow: `0 0 6px ${hex}99, 0 0 12px ${hex}55` });

  // 渲染角色编辑面板 — 与章节列表 / 角色库配套分区 + 错帧入场与悬停动画
  const renderCharacterEditPanel = () => {
    const narrow = isChaptersNarrow;
    const rawCol = characterFormData.color || '#00f0ff';
    const accentCol = String(rawCol).startsWith('#') ? rawCol : rawCol === 'var(--ws-accent)' ? '#a78bfa' : '#00f0ff';
    const displayNameForm = getCharacterChapterOutlineDisplayName(characterFormData);
    const avatarLetter = (displayNameForm || characterFormData.name || '?').charAt(0);

    /** 章节列表同款：左色条 + 浅底卡片；delayIdx 控制入场错帧 */
    const editSectionProps = (delayIdx, extraSx = {}) => ({
      component: motion.div,
      initial: motionReduced ? false : { opacity: 0, y: 16, scale: 0.985 },
      animate: { opacity: 1, y: 0, scale: 1 },
      transition: {
        delay: motionReduced ? 0 : 0.05 + delayIdx * 0.054,
        duration: motionReduced ? 0 : 0.38,
        ease: [0.22, 1, 0.36, 1],
      },
      whileHover: motionReduced ? undefined : { y: -2, transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] } },
      sx: {
        position: 'relative',
        mb: 2,
        flexShrink: 0,
        p: { xs: 1.35, sm: 1.5 },
        pt: { xs: 1.5, sm: 1.65 },
        borderRadius: narrow ? 1 : 1.5,
        bgcolor: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '10%',
          bottom: '10%',
          width: 3,
          borderRadius: '0 2px 2px 0',
          bgcolor: accentCol,
          boxShadow: `0 0 12px ${accentCol}`,
        },
        pl: { xs: 1.75, sm: 2 },
        ...extraSx,
      },
    });

    /** 表单项错帧入场 + 轻悬停（与章节行同 easing） */
    const charFormFieldWrap = (fieldIdx) => ({
      component: motion.div,
      initial: motionReduced ? false : { opacity: 0, x: -14, y: 10 },
      animate: { opacity: 1, x: 0, y: 0 },
      transition: {
        delay: motionReduced ? 0 : 0.03 + fieldIdx * 0.046,
        duration: motionReduced ? 0 : 0.34,
        ease: [0.22, 1, 0.36, 1],
      },
      whileHover: motionReduced ? undefined : { y: -1, transition: { duration: 0.2 } },
    });

    const chapterLikeInputSx = {
      '& .MuiOutlinedInput-root': {
        bgcolor: 'rgba(255,255,255,0.04)',
        fontSize: { xs: 12, sm: 13 },
        fontFamily: "'Noto Sans SC', system-ui, sans-serif",
        borderRadius: 1.5,
      },
      '& .MuiOutlinedInput-input': { fontFamily: "'Noto Sans SC', system-ui, sans-serif" },
    };
    const detailMultilineInnerSx = {
      ...chapterLikeInputSx,
      '& .MuiOutlinedInput-root': { ...chapterLikeInputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
      '& textarea': { lineHeight: 1.5, padding: '10px 14px' },
      '@media (max-width: 599.95px)': {
        '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
      },
    };

    return (
      <Box
        className="panel-content character-panel-cyber workspace-character-edit-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: 2,
          position: 'relative',
          zIndex: 1,
          minHeight: '100%',
        }}
      >
        {/* 头部 — 与章纲·章节顶栏同款节奏 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.06 }} whileTap={motionReduced ? undefined : { scale: 0.92 }}>
            <IconButton
              className="character-panel-back-btn"
              onClick={() => {
                setActivePanel('character');
                setEditingCharacter(null);
              }}
              sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.07)' } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            sx={{
              flex: 1,
              color: '#00f0ff',
              fontWeight: 700,
              fontSize: { xs: 15, sm: 17 },
              textShadow: '0 0 16px rgba(0,240,255,0.35)',
              letterSpacing: '0.02em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {editingCharacter
              ? (getCharacterChapterOutlineDisplayName(characterFormData) || characterFormData.name?.trim() || '编辑角色')
              : (characterFormData.name?.trim() || '添加角色')}
          </Typography>
        </Box>

        {/* 头像 + 代表色 — 分区卡片 + 弹簧头像 */}
        <Box {...editSectionProps(0, { mb: 2 })}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'column' },
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              component={motion.div}
              whileHover={motionReduced ? undefined : { scale: 1.07, rotate: -3 }}
              transition={{ type: 'spring', stiffness: 340, damping: 19 }}
              sx={{ flexShrink: 0 }}
            >
              <Box
                sx={{
                  width: { xs: 58, sm: 68 },
                  height: { xs: 58, sm: 68 },
                  borderRadius: '50%',
                  bgcolor: characterFormData.color || '#00f0ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 24, sm: 28 },
                  fontWeight: 700,
                  fontFamily: 'Orbitron, Noto Sans SC, sans-serif',
                  color: '#0a0a0c',
                  boxShadow: `0 0 28px ${accentCol}66, inset 0 0 22px rgba(255,255,255,0.18)`,
                  border: '2px solid rgba(255,255,255,0.28)',
                }}
              >
                {avatarLetter}
              </Box>
            </Box>
            <Box sx={{ flex: 1, width: { xs: 'auto', sm: '100%' }, minWidth: 0 }}>
              <Typography sx={{ display: 'block', mb: 1.25, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
                角色代表色
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.55, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-start' } }}>
                {[
                  '#00f0ff', '#00ff9f', '#ff6b35', '#ff00aa',
                  '#ffd700', '#ff4444', 'var(--ws-accent)', '#3b82f6',
                  '#06b6d4', '#f59e0b', '#ec4899', '#6366f1',
                ].map((color) => (
                  <Box
                    key={color}
                    component={motion.div}
                    whileHover={motionReduced ? undefined : { scale: 1.18 }}
                    whileTap={motionReduced ? undefined : { scale: 0.9 }}
                    onClick={() => setCharacterFormData({ ...characterFormData, color })}
                    sx={{
                      width: { xs: 24, sm: 28 },
                      height: { xs: 24, sm: 28 },
                      borderRadius: '50%',
                      bgcolor: color,
                      cursor: 'pointer',
                      border: characterFormData.color === color ? '2px solid #fff' : '2px solid rgba(0,0,0,0.35)',
                      boxShadow:
                        characterFormData.color === color
                          ? `0 0 14px ${accentCol}, 0 0 0 1px rgba(255,255,255,0.4)`
                          : '0 2px 8px rgba(0,0,0,0.35)',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* AI 生成 — 生成中呼吸光 */}
        <Box
          {...editSectionProps(1)}
          animate={
            generatingCharacter && !motionReduced
              ? {
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  boxShadow: [
                    '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
                    '0 4px 28px rgba(245,158,11,0.22), inset 0 1px 0 rgba(255,255,255,0.06)',
                    '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
                  ],
                }
              : { opacity: 1, y: 0, scale: 1 }
          }
          transition={{
            delay: motionReduced ? 0 : 0.05 + 1 * 0.054,
            duration: motionReduced ? 0 : 0.38,
            ease: [0.22, 1, 0.36, 1],
            ...(generatingCharacter && !motionReduced
              ? { boxShadow: { repeat: Infinity, duration: 1.85, ease: 'easeInOut' } }
              : {}),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box component="motion.span" sx={{ display: 'inline-flex' }} animate={motionReduced ? {} : { rotate: [0, 8, -8, 0] }} transition={motionReduced ? {} : { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}>
              <AutoFixHighIcon sx={{ color: '#fcd34d', fontSize: 17, filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.45))' }} />
            </Box>
            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(254,243,199,0.95)', letterSpacing: '0.06em' }}>
              AI 智能生成
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            {...getDynamicTextAreaStyle(characterGenPrompt, 1, 8)}
            placeholder="描述你想要的角色，如：冷酷的剑客，身负血海深仇..."
            value={characterGenPrompt}
            onChange={(e) => setCharacterGenPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                handleAIGenerateCharacter();
              }
            }}
            className="character-panel-textarea"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0,0,0,0.25)',
                fontSize: 13,
                alignItems: 'flex-start',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1,
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: 'rgba(255,255,255,0.14)' },
                '&.Mui-focused': { borderColor: 'rgba(255,255,255,0.22)', boxShadow: '0 0 0 1px rgba(255,255,255,0.1)' }
              },
              '& textarea': { lineHeight: 1.6 },
              '@media (max-width: 599.95px)': {
                '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
              }
            }}
          />
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={generatingCharacter ? <CircularProgress size={14} color="inherit" /> : undefined}
            onClick={handleAIGenerateCharacter}
            disabled={generatingCharacter || !characterGenPrompt.trim()}
            className="workspace-btn-cyber character-panel-action-btn"
            sx={{
              bgcolor: 'transparent',
              color: 'rgba(255,255,255,0.92)',
              fontSize: 13,
              py: 1,
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 1,
              textTransform: 'none',
              transition: 'all 0.25s ease',
              textShadow: '0 0 8px rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.06)',
                borderColor: 'rgba(255,255,255,0.35)',
                boxShadow: '0 0 14px rgba(255,255,255,0.08)'
              },
              '&:disabled': { color: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.06)', textShadow: 'none' }
            }}
          >
            {generatingCharacter ? '生成中...' : '生成角色人设'}
          </Button>
        </Box>

        {/* 角色信息表单 - 不再单独滚动，整个面板可滚动 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.25, delay: motionReduced ? 0 : 0.12 }}
          sx={{ pr: { xs: 0.25, sm: 0.5 } }}
        >
          {/* 基础信息 */}
          <Box {...editSectionProps(2)}>
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              基础信息
            </Typography>

          {/* 第一行：姓名 + 性别 */}
          <Box {...charFormFieldWrap(0)} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              className="workspace-character-edit-field"
              label="角色名"
              value={characterFormData.name}
              onChange={(e) => setCharacterFormData({ ...characterFormData, name: e.target.value })}
              sx={{ ...chapterLikeInputSx, flex: 1, minWidth: 0 }}
            />
            <FormControl size="small" sx={{ width: 100, flexShrink: 0 }}>
              <StyledSelect
                displayEmpty
                value={characterFormData.gender || '未知'}
                onChange={(e) => setCharacterFormData({ ...characterFormData, gender: e.target.value })}
                SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)', fontFamily: "'Noto Sans SC', system-ui, sans-serif" } }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: '#0c0c10',
                      background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
                      marginTop: 4
                    },
                    sx: {
                      '& .MuiMenuItem-root': { fontSize: 11, fontFamily: "'Noto Sans SC', system-ui, sans-serif", '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)' }, '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }
                    }
                  }
                }}
                renderValue={(v) => {
                  const genderColors = { 男: '#cbd5e1', 女: '#fbcfe8', 未知: 'rgba(148,163,184,0.95)' };
                  const color = genderColors[v] || 'rgba(226,232,240,0.9)';
                  return <span style={{ color, fontSize: 11, fontFamily: "'Noto Sans SC', system-ui, sans-serif", fontWeight: 600 }}>{v}</span>;
                }}
                sx={{
                  fontSize: 11,
                  color: 'rgba(241,245,249,0.92)',
                  fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    backgroundColor: 'rgba(255,255,255,0.04)',
                    borderRadius: 1.5,
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(125,211,252,0.28)' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(125,211,252,0.45)' }
                }}
              >
                <MenuItem value="男" sx={{ fontSize: 11, color: '#cbd5e1', fontFamily: "'Noto Sans SC', system-ui, sans-serif" }}>男</MenuItem>
                <MenuItem value="女" sx={{ fontSize: 11, color: '#fbcfe8', fontFamily: "'Noto Sans SC', system-ui, sans-serif" }}>女</MenuItem>
                <MenuItem value="未知" sx={{ fontSize: 11, color: 'rgba(148,163,184,0.95)', fontFamily: "'Noto Sans SC', system-ui, sans-serif" }}>未知</MenuItem>
              </StyledSelect>
            </FormControl>
          </Box>

          {/* 称号 / 境界 / 势力 — 错帧 + 章节列表系边框 */}
          <Box {...charFormFieldWrap(1)} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              className="workspace-character-edit-field"
              label="称号/头衔"
              multiline
              {...getDynamicTextAreaStyle(characterFormData.title, 1, 4)}
              value={characterFormData.title}
              onChange={(e) => setCharacterFormData({ ...characterFormData, title: e.target.value })}
              placeholder="如：玄天宗首席弟子、仙尊、魔教教主..."
              sx={{
                ...chapterLikeInputSx,
                '& .MuiOutlinedInput-root': { ...chapterLikeInputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                '& textarea': { lineHeight: 1.5, padding: '10px 14px' },
                '@media (max-width: 599.95px)': {
                  '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
                }
              }}
            />
          </Box>
          <Box {...charFormFieldWrap(2)} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              className="workspace-character-edit-field"
              label="境界/等级"
              multiline
              {...getDynamicTextAreaStyle(characterFormData.level, 1, 4)}
              value={characterFormData.level}
              onChange={(e) => setCharacterFormData({ ...characterFormData, level: e.target.value })}
              placeholder="描述角色的修为境界或等级..."
              sx={{
                ...chapterLikeInputSx,
                '& .MuiOutlinedInput-root': { ...chapterLikeInputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                '& textarea': { lineHeight: 1.5, padding: '10px 14px' },
                '@media (max-width: 599.95px)': {
                  '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
                }
              }}
            />
          </Box>
          <Box {...charFormFieldWrap(3)} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              className="workspace-character-edit-field"
              label="势力/门派"
              multiline
              {...getDynamicTextAreaStyle(characterFormData.realm, 1, 4)}
              value={characterFormData.realm}
              onChange={(e) => setCharacterFormData({ ...characterFormData, realm: e.target.value })}
              placeholder="角色所属的势力、门派或组织..."
              sx={{
                ...chapterLikeInputSx,
                '& .MuiOutlinedInput-root': { ...chapterLikeInputSx['& .MuiOutlinedInput-root'], alignItems: 'flex-start' },
                '& textarea': { lineHeight: 1.5, padding: '10px 14px' },
                '@media (max-width: 599.95px)': {
                  '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
                }
              }}
            />
          </Box>
          </Box>

          {/* 角色标签 */}
          <Box {...editSectionProps(3)}>
            <Typography sx={{ display: 'block', mb: 1, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              角色标签
            </Typography>

          {/* 预设标签 — 错帧弹出 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: motionReduced ? 0 : 0.25 }}
            sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}
          >
            {[
              { id: '主角', label: '主角', color: '#00ff9f' },
              { id: '配角', label: '配角', color: '#ff6b35' },
              { id: '反派', label: '反派', color: '#ff4444' },
              { id: '导师', label: '导师', color: 'var(--ws-accent)' },
              { id: '恋人', label: '恋人', color: '#ec4899' },
              { id: '友人', label: '友人', color: '#3b82f6' },
              { id: '宿敌', label: '宿敌', color: '#ef4444' },
              { id: 'NPC', label: 'NPC', color: '#ffd700' }
            ].map((tag, ti) => {
              const isSelected = characterFormData.tags?.includes(tag.id);
              return (
                <Box
                  key={tag.id}
                  component={motion.div}
                  initial={motionReduced ? false : { opacity: 0, scale: 0.86, y: 6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: motionReduced ? 0 : 0.05 + ti * 0.035, duration: motionReduced ? 0 : 0.28, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={motionReduced ? undefined : { scale: 1.05 }}
                  whileTap={motionReduced ? undefined : { scale: 0.95 }}
                >
                <Chip
                  label={tag.label}
                  size="small"
                  onClick={() => {
                    if (isSelected) {
                      setCharacterFormData({
                        ...characterFormData,
                        tags: characterFormData.tags?.filter(t => t !== tag.id) || []
                      });
                    } else {
                      setCharacterFormData({
                        ...characterFormData,
                        tags: [...(characterFormData.tags || []), tag.id]
                      });
                    }
                  }}
                  sx={{
                    bgcolor: isSelected ? `${tag.color}22` : 'rgba(255,255,255,0.05)',
                    color: isSelected ? tag.color : 'rgba(255,255,255,0.72)',
                    border: `1px solid ${isSelected ? tag.color : 'rgba(255,255,255,0.1)'}`,
                    fontSize: 11,
                    cursor: 'pointer',
                    textShadow: isSelected ? `0 0 8px ${tag.color}99, 0 0 14px ${tag.color}55` : 'none',
                    '&:hover': {
                      bgcolor: `${tag.color}18`,
                      borderColor: tag.color,
                      textShadow: `0 0 6px ${tag.color}88, 0 0 12px ${tag.color}44`
                    }
                  }}
                />
                </Box>
              );
            })}
          </Box>

          {/* 自定义标签输入：桌面端同行，移动端上下排列避免按钮挡住输入框 */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'stretch' }, gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="添加自定义标签..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newTag.trim()) {
                  e.preventDefault();
                  if (!characterFormData.tags?.includes(newTag.trim())) {
                    setCharacterFormData({
                      ...characterFormData,
                      tags: [...(characterFormData.tags || []), newTag.trim()]
                    });
                  }
                  setNewTag('');
                }
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: 12
                }
              }}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (newTag.trim() && !characterFormData.tags?.includes(newTag.trim())) {
                  setCharacterFormData({
                    ...characterFormData,
                    tags: [...(characterFormData.tags || []), newTag.trim()]
                  });
                }
                setNewTag('');
              }}
              disabled={!newTag.trim()}
              className="workspace-btn-cyber"
              sx={{
                flexShrink: 0,
                minWidth: { xs: '100%', sm: 56 },
                whiteSpace: 'nowrap',
                fontSize: 12,
                px: 1.5,
                borderColor: 'rgba(255,255,255,0.25)',
                color: 'rgba(255,255,255,0.9)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' }
              }}
            >
              添加
            </Button>
          </Box>
          </Box>

          {/* 详细信息 */}
          <Box {...editSectionProps(4)}>
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              详细信息
            </Typography>

          <Box {...charFormFieldWrap(4)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="外貌特征"
              placeholder="身高、发型、服装特点..."
              value={characterFormData.appearance}
              onChange={(e) => setCharacterFormData({ ...characterFormData, appearance: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.appearance, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(5)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="性格特点"
              placeholder="内向、外向、阴险..."
              value={characterFormData.personality}
              onChange={(e) => setCharacterFormData({ ...characterFormData, personality: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.personality, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(6)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="目标/追求"
              placeholder="复仇、成为强者、守护..."
              value={characterFormData.goal}
              onChange={(e) => setCharacterFormData({ ...characterFormData, goal: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.goal, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(7)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="能力/特长"
              placeholder="剑术、炼丹、阵法..."
              value={characterFormData.abilities}
              onChange={(e) => setCharacterFormData({ ...characterFormData, abilities: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.abilities, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(8)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="武器/装备"
              value={characterFormData.equipment}
              onChange={(e) => setCharacterFormData({ ...characterFormData, equipment: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.equipment, 1, 8)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(9)} sx={{ mb: 2 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="秘密/把柄"
              placeholder="不为人知的秘密..."
              value={characterFormData.secret}
              onChange={(e) => setCharacterFormData({ ...characterFormData, secret: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.secret, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(10)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="角色简介"
              placeholder="简要描述这个角色..."
              value={characterFormData.description}
              onChange={(e) => setCharacterFormData({ ...characterFormData, description: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.description, 1, 10)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          <Box {...charFormFieldWrap(11)} sx={{ mb: 1.5 }}>
            <TextField
              fullWidth
              className="character-panel-field workspace-character-edit-field"
              label="角色背景"
              placeholder="角色的过往经历、出身..."
              value={characterFormData.background}
              onChange={(e) => setCharacterFormData({ ...characterFormData, background: e.target.value })}
              multiline
              {...getDynamicTextAreaStyle(characterFormData.background, 1, 12)}
              sx={detailMultilineInnerSx}
            />
          </Box>
          </Box>

          {/* 角色关系网 */}
          <Box {...editSectionProps(5)}>
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              角色关系网
            </Typography>

          {/* 添加关系 - 下拉选择已创建角色，绑定完整人物信息 */}
          <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography className="character-panel-relation-label" sx={{ display: 'block', mb: 1.5 }}>
              添加与其他角色的关系
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, mb: 1.5 }}>
              <FormControl size="small" sx={{ flex: 1, minWidth: { xs: '100%', sm: 0 } }} className="character-panel-select-wrap">
                <StyledSelect
                  displayEmpty
                  value={newRelation.characterId}
                  onChange={(e) => setNewRelation({ ...newRelation, characterId: e.target.value })}
                  SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: '#0c0c10',
                        background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
                        marginTop: 4
                      },
                      sx: {
                        '& .MuiMenuItem-root': { fontSize: 11, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,0.45)', opacity: 1 } }
                      }
                    }
                  }}
                  renderValue={(v) => {
                    if (!v) return <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, textShadow: '0 0 8px rgba(255,255,255,0.2)' }}>选择已创建的角色</span>;
                    const c = characters.find(ch => ch._id === v);
                    const color = c?.color || '#94a3b8';
                    return c ? <span style={{ color, fontSize: 11, textShadow: `0 0 8px ${color}cc, 0 0 16px ${color}66` }}>{c.name}</span> : v;
                  }}
                  sx={{
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.9)',
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.04)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' }
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>选择角色</MenuItem>
                  {characters
                    .filter(c => c._id !== (characterFormData._id || ''))
                    .filter(c => !(characterFormData.relations || []).some(r => r.characterId === c._id || (!r.characterId && r.name === c.name)))
                    .map(c => {
                      const color = c.color || '#94a3b8';
                      return (
                        <MenuItem key={c._id} value={c._id} sx={{ fontSize: 11, color, ...glowStyle(color) }}>{c.name}</MenuItem>
                      );
                    })}
                </StyledSelect>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 90 } }} className="character-panel-select-wrap">
                <StyledSelect
                  value={newRelation.type}
                  onChange={(e) => setNewRelation({ ...newRelation, type: e.target.value })}
                  SelectDisplayProps={{ style: { backgroundColor: 'rgba(255,255,255,0.04)' } }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: '#0c0c10',
                        background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.06)',
                        marginTop: 4
                      },
                      sx: {
                        '& .MuiMenuItem-root': { fontSize: 11, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)' }, '&.Mui-selected:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }
                      }
                    }
                  }}
                  renderValue={(v) => {
                    const displayType = v === '自定义' && newRelation.customType ? newRelation.customType : v;
                    const color = RELATION_TYPE_COLORS[v] || RELATION_TYPE_COLORS['其他'];
                    return <span style={{ color, fontSize: 11, textShadow: `0 0 6px ${color}99, 0 0 12px ${color}55` }}>{displayType}</span>;
                  }}
                  sx={{
                    fontSize: 11,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.04)',
                      backgroundColor: 'rgba(255,255,255,0.04)',
                    },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' }
                  }}
                >
                  {RELATION_TYPES.map(type => {
                    const color = RELATION_TYPE_COLORS[type];
                    return (
                      <MenuItem key={type} value={type} sx={{ fontSize: 11, color, ...glowStyle(color) }}>{type}</MenuItem>
                    );
                  })}
                </StyledSelect>
              </FormControl>
            </Box>
            {/* 选择「自定义」时显示自定义关系输入 */}
            {newRelation.type === '自定义' && (
              <TextField
                fullWidth
                size="small"
                placeholder="输入自定义关系，如：表亲、同门、债主"
                value={newRelation.customType}
                onChange={(e) => setNewRelation({ ...newRelation, customType: e.target.value })}
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.04)',
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.9)',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' }
                  }
                }}
              />
            )}
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              className="workspace-btn-cyber"
              onClick={() => {
                if (newRelation.characterId) {
                  const linked = characters.find(c => c._id === newRelation.characterId);
                  const name = linked ? linked.name : '';
                  const relationType = newRelation.type === '自定义' ? (newRelation.customType?.trim() || '自定义') : newRelation.type;
                  setCharacterFormData({
                    ...characterFormData,
                    relations: [...(characterFormData.relations || []), { characterId: newRelation.characterId, characterName: name, type: relationType }]
                  });
                  setNewRelation({ characterId: '', type: '其他', customType: '' });
                }
              }}
              disabled={!newRelation.characterId || (newRelation.type === '自定义' && !newRelation.customType?.trim())}
              sx={{ fontSize: 12, '&:disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' } }}
            >
              添加关系
            </Button>
          </Box>

          {/* 关系列表 */}
          {characterFormData.relations?.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              {characterFormData.relations.map((relation, idx) => {
                // 根据关系类型设置颜色
                const relationColors = {
                  '恋人': '#ec4899',
                  '友人': '#3b82f6',
                  '导师': 'var(--ws-accent)',
                  '宿敌': '#ef4444',
                  '亲属': '#22c55e',
                  '师徒': 'var(--ws-violet)',
                  '同门': '#06b6d4',
                  '对立': '#f97316',
                  'NPC': '#ffd700',
                  '其他': '#94a3b8'
                };
                const color = relationColors[relation.type] || relationColors['其他'];
                
                return (
                <Box
                  key={idx}
                  component={motion.div}
                  layout
                  initial={motionReduced ? false : { opacity: 0, x: -14, scale: 0.98 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={motionReduced ? false : { opacity: 0, x: 8, scale: 0.96 }}
                  transition={{
                    delay: motionReduced ? 0 : Math.min(idx * 0.045, 0.35),
                    duration: motionReduced ? 0 : 0.28,
                    ease: [0.22, 1, 0.36, 1],
                    layout: { duration: motionReduced ? 0 : 0.2 },
                  }}
                  whileHover={motionReduced ? undefined : { y: -2, boxShadow: `0 8px 22px ${color}28` }}
                  whileTap={motionReduced ? undefined : { scale: 0.99 }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    bgcolor: 'rgba(255,255,255,0.04)',
                    borderRadius: 1.5,
                    border: `1px solid ${color}40`,
                    borderLeft: `3px solid ${color}`,
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                    '&:hover': {
                      bgcolor: `${color}10`,
                      borderColor: `${color}60`,
                    }
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: `${color}20`,
                      border: `1px solid ${color}40`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      color: color,
                      fontWeight: 600,
                      textShadow: `0 0 8px ${color}`
                    }}
                  >
                    {(relation.characterName || relation.name)?.charAt(0)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 12, fontWeight: 500 }}>
                      {relation.characterName || relation.name}
                    </Typography>
                    <Typography sx={{ color, fontSize: 10, textShadow: `0 0 6px ${color}` }}>
                      {relation.type}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => {
                      const newRelations = [...(characterFormData.relations || [])];
                      newRelations.splice(idx, 1);
                      setCharacterFormData({
                        ...characterFormData,
                        relations: newRelations
                      });
                    }}
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      '&:hover': { color: '#ff4444', bgcolor: 'rgba(255,68,68,0.1)' }
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )})}
            </Box>
          )}

          {/* 空关系提示 */}
          {(!characterFormData.relations || characterFormData.relations.length === 0) && (
            <Box
              sx={{
                textAlign: 'center',
                py: 3,
                border: '1px dashed rgba(255,255,255,0.12)',
                borderRadius: 2,
                mb: 2
              }}
            >
              <Typography className="character-panel-relation-hint">
                暂无关系记录，点击上方添加
              </Typography>
            </Box>
          )}
          </Box>
        </Box>

        {/* 底部按钮 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.4, delay: motionReduced ? 0 : 0.2, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', gap: 1, pt: 2, pb: 1, borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSaveCharacter(characterFormData)}
            disabled={!characterFormData.name?.trim()}
            startIcon={<SaveIcon />}
            className="workspace-btn-cyber"
            sx={{ py: 1.2, fontSize: { xs: 12, sm: 13 }, '&:disabled': { borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.25)' } }}
          >
            保存角色
          </Button>
          {editingCharacter && (
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                handleDeleteCharacter(editingCharacter._id);
                setActivePanel('character');
              }}
              className="workspace-btn-neon-danger"
              sx={{ py: 1.2, fontSize: { xs: 12, sm: 13 } }}
            >
              删除
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  // 渲染词条编辑面板 - 与角色编辑面板样式统一
  const renderItemEditPanel = () => {
    return (
      <Box
        className="panel-content character-panel-cyber workspace-item-edit-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: 2,
          position: 'relative',
          zIndex: 1,
          minHeight: '100%'
        }}
      >
        {/* 头部 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.06 }} whileTap={motionReduced ? undefined : { scale: 0.92 }}>
            <IconButton
              onClick={() => { setActivePanel('item'); setEditingItem(null); }}
              sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.07)' } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ flex: 1, color: '#ff6b35', fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: '0 0 16px rgba(255,107,53,0.35)', letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {editingItem ? '编辑词条' : '添加词条'}
          </Typography>
        </Box>

        {/* AI智能生成区域 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: motionReduced ? 0 : 0.05, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          whileHover={motionReduced ? undefined : { y: -2 }}
          sx={{
            position: 'relative', mb: 2, mx: { xs: 1.5, sm: 2 }, mt: { xs: 1.5, sm: 2 },
            flexShrink: 0, p: { xs: 1.35, sm: 1.5 }, pt: { xs: 1.5, sm: 1.65 },
            borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
            overflow: 'hidden', '&::before': {
              content: '""', position: 'absolute', left: 0, top: '10%', bottom: '10%',
              width: 3, borderRadius: '0 2px 2px 0', bgcolor: '#ff6b35', boxShadow: '0 0 12px #ff6b35',
            }, pl: 2.25,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Box component="motion.span" sx={{ display: 'inline-flex' }} animate={motionReduced ? {} : { rotate: [0, 8, -8, 0] }} transition={motionReduced ? {} : { duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}>
              <AutoFixHighIcon sx={{ color: '#fcd34d', fontSize: 17, filter: 'drop-shadow(0 0 8px rgba(251,191,36,0.45))' }} />
            </Box>
            <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(254,243,199,0.95)', letterSpacing: '0.06em' }}>
              AI 智能生成
            </Typography>
          </Box>

          {/* 分类选择 */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: 'rgba(148,163,184,0.9)', fontSize: 11, mb: 0.5 }}>选择分类：</Typography>
            <FormControl fullWidth size="small">
              <Select
                value={itemFormData.category}
                onChange={(e) => { setItemFormData({ ...itemFormData, category: e.target.value }); setItemGenTemplate('custom'); }}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.04)',
                  color: '#f1f5f9',
                  fontSize: 12,
                  fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 1.5,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover': { borderColor: 'rgba(251,146,60,0.28)' },
                  '&.Mui-focused': { borderColor: 'rgba(251,146,60,0.48)', boxShadow: '0 0 0 1px rgba(251,146,60,0.12)' },
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: '#0c0c10',
                      background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 8,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.7)'
                    },
                    sx: {
                      '& .MuiMenuItem-root': {
                        color: 'rgba(226,232,240,0.9)',
                        fontSize: 12,
                        fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                        py: 1,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                        '&.Mui-selected': { bgcolor: 'rgba(251,146,60,0.15)', color: '#fb923c' }
                      }
                    }
                  }
                }}
              >
                {ITEM_CATEGORIES.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{cat.icon}</span>
                      <span>{cat.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* 模板详情展示 */}
          {itemGenTemplate !== 'custom' && (
            <Box sx={{
              mb: 1.5,
              p: 1.5,
              bgcolor: 'rgba(255,107,53,0.08)',
              border: '1px solid rgba(255,107,53,0.2)',
              borderRadius: 1,
              transition: 'all 0.3s ease'
            }}>
              <Typography sx={{ color: '#ff6b35', fontSize: 11, fontWeight: 600, mb: 0.5 }}>
                模板详情
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                {itemFormData.category === '势力' && itemGenTemplate === 'factory' && '传统修仙门派：有掌门/长老/弟子体系，正道门派风格'}
                {itemFormData.category === '势力' && itemGenTemplate === 'evil' && '魔道势力：修炼邪功，行事狠辣，黑暗风格'}
                {itemFormData.category === '势力' && itemGenTemplate === 'empire' && '帝国皇朝：国家机器，有皇帝/官员/军队体系'}
                {itemFormData.category === '势力' && itemGenTemplate === 'tribe' && '种族势力：妖/兽/灵等种族，有部落/王庭结构'}
                {itemFormData.category === '势力' && itemGenTemplate === 'alliance' && '联盟组织：多个势力联合，有盟主/成员体系'}
                {itemFormData.category === '势力' && itemGenTemplate === 'family' && '家族势力：家族传承，有族老/族人结构'}
                {itemFormData.category === '势力' && itemGenTemplate === 'secret' && '神秘组织：暗中操控，不为人知的隐藏势力'}

                {itemFormData.category === '功法' && itemGenTemplate === 'cultivation' && '修炼功法：从弱到强的修炼体系，适合主角'}
                {itemFormData.category === '功法' && itemGenTemplate === 'attack' && '攻击神通：以战斗为主，强大攻击力招数'}
                {itemFormData.category === '功法' && itemGenTemplate === 'defense' && '防御秘术：防御为主的技能秘法'}
                {itemFormData.category === '功法' && itemGenTemplate === 'auxiliary' && '辅助秘法：探测、隐身、逃遁等特殊能力'}
                {itemFormData.category === '功法' && itemGenTemplate === 'array' && '阵法禁制：阵法、禁制、结界类技能'}
                {itemFormData.category === '功法' && itemGenTemplate === 'body' && '炼体功法：肉体强度修炼流派'}
                {itemFormData.category === '功法' && itemGenTemplate === 'evil' && '魔功邪术：邪门功法，有代价的黑暗技能'}

                {itemFormData.category === '物品' && itemGenTemplate === 'sword' && '剑类武器：飞剑、本命剑类型'}
                {itemFormData.category === '物品' && itemGenTemplate === 'bead' && '珠子形态：珠子形态的空间或特殊至宝'}
                {itemFormData.category === '物品' && itemGenTemplate === 'scroll' && '卷轴类：传承卷轴、技能书类型'}
                {itemFormData.category === '物品' && itemGenTemplate === 'furnace' && '鼎炉类：炼丹/炼器用的鼎、炉、塔'}
                {itemFormData.category === '物品' && itemGenTemplate === 'crown' && '冠饰类：身份象征的头饰、冠冕'}
                {itemFormData.category === '物品' && itemGenTemplate === 'shield' && '防御型：盾牌、护甲等防护武器'}
                {itemFormData.category === '物品' && itemGenTemplate === 'pill' && '丹药：辅助修炼的各类丹药'}

                {itemFormData.category === '副本' && itemGenTemplate === 'continent' && '修仙大陆：大型疆域，多势力并存'}
                {itemFormData.category === '副本' && itemGenTemplate === 'secret' && '秘境洞天：独立空间，有传承/危险'}
                {itemFormData.category === '副本' && itemGenTemplate === 'city' && '主城都城：繁华都市，势力交汇'}
                {itemFormData.category === '副本' && itemGenTemplate === 'forest' && '深山老林：人迹罕至，适合隐居'}
                {itemFormData.category === '副本' && itemGenTemplate === 'ocean' && '海洋领域：海上势力，资源丰富'}
                {itemFormData.category === '副本' && itemGenTemplate === 'danger' && '绝地险境：修炼禁地，九死一生'}
                {itemFormData.category === '副本' && itemGenTemplate === 'border' && '边陲小镇：新手之地，机遇较多'}

                {/* 角色模板详情 */}
                {itemFormData.category === '角色' && itemGenTemplate === 'protagonist' && '主角：故事核心人物，有成长路线'}
                {itemFormData.category === '角色' && itemGenTemplate === 'antagonist' && '反派：与主角对立，推动剧情'}
                {itemFormData.category === '角色' && itemGenTemplate === 'mentor' && '导师：帮助主角成长的人物'}
                {itemFormData.category === '角色' && itemGenTemplate === 'love' && '恋人：情感线关键人物'}
                {itemFormData.category === '角色' && itemGenTemplate === 'friend' && '好友：主角的伙伴和支持者'}
                {itemFormData.category === '角色' && itemGenTemplate === 'rival' && '宿敌：与主角竞争的对手'}
                {itemFormData.category === '角色' && itemGenTemplate === 'npc' && 'NPC：功能性配角，情节推动者'}

                {/* 种族模板详情 */}
                {itemFormData.category === '种族' && itemGenTemplate === 'dragon' && '龙族：强大种族，天赋异禀'}
                {itemFormData.category === '种族' && itemGenTemplate === 'phoenix' && '凤凰族：浴火重生，传承血脉'}
                {itemFormData.category === '种族' && itemGenTemplate === 'demon' && '恶魔族：魔界种族，强大肉身'}
                {itemFormData.category === '种族' && itemGenTemplate === 'spirit' && '精灵族：自然之力，天赋灵性'}
                {itemFormData.category === '种族' && itemGenTemplate === 'god' && '神族：天生神力，高高在上'}

                {/* 地域模板详情 */}
                {itemFormData.category === '地域' && itemGenTemplate === 'mountain' && '山脉：险峻之地，天材地宝'}
                {itemFormData.category === '地域' && itemGenTemplate === 'river' && '河流：水系资源，灵脉所在'}
                {itemFormData.category === '地域' && itemGenTemplate === 'forest' && '森林：秘境众多，危险与机遇并存'}
                {itemFormData.category === '地域' && itemGenTemplate === 'desert' && '沙漠：荒芜之地，有古遗迹'}
                {itemFormData.category === '地域' && itemGenTemplate === 'island' && '岛屿：孤立海外，可能有传承'}

                {/* 王朝模板详情 */}
                {itemFormData.category === '王朝' && itemGenTemplate === 'empire' && '大一统王朝：统治广阔疆域'}
                {itemFormData.category === '王朝' && itemGenTemplate === 'kingdom' && '王国：中等规模国家'}
                {itemFormData.category === '王朝' && itemGenTemplate === 'clan' && '部落：原始社会组织'}

                {/* 组织模板详情 */}
                {itemFormData.category === '组织' && itemGenTemplate === 'secret' && '秘密组织：暗中活动，神秘莫测'}
                {itemFormData.category === '组织' && itemGenTemplate === 'assassin' && '杀手组织：暗杀与情报'}
                {itemFormData.category === '组织' && itemGenTemplate === 'merchant' && '商会：商业网络，消息灵通'}
                {itemFormData.category === '组织' && itemGenTemplate === 'religious' && '宗教：信仰力量，精神控制'}

                {/* 血脉体质模板详情 */}
                {itemFormData.category === '血脉' && itemGenTemplate === 'ancient' && '远古血脉：古老传承，强大潜力'}
                {itemFormData.category === '血脉' && itemGenTemplate === 'divine' && '神级体质：天生神体，修炼速度惊人'}
                {itemFormData.category === '血脉' && itemGenTemplate === 'mysterious' && '特殊体质：独一无二的能力'}

                {/* 灵根资质模板详情 */}
                {itemFormData.category === '灵根' && itemGenTemplate === 'single' && '单一灵根：纯粹但限制发展'}
                {itemFormData.category === '灵根' && itemGenTemplate === 'dual' && '双灵根：平衡发展潜力大'}
                {itemFormData.category === '灵根' && itemGenTemplate === 'multiple' && '多灵根：杂而不精但适应性强'}
                {itemFormData.category === '灵根' && itemGenTemplate === 'none' && '无灵根：特殊情况，可能有奇遇'}

                {/* 灵兽模板详情 */}
                {itemFormData.category === '灵兽' && itemGenTemplate === 'beast' && '野兽型：战斗型灵兽'}
                {itemFormData.category === '灵兽' && itemGenTemplate === 'bird' && '鸟类：速度快，侦查优秀'}
                {itemFormData.category === '灵兽' && itemGenTemplate === 'aquatic' && '水生：水中生存，特殊能力'}
                {itemFormData.category === '灵兽' && itemGenTemplate === 'insect' && '虫类：数量优势，繁殖能力'}

                {/* 材料模板详情 */}
                {itemFormData.category === '材料' && itemGenTemplate === 'herb' && '灵草：炼丹必备材料'}
                {itemFormData.category === '材料' && itemGenTemplate === 'mineral' && '矿物：炼器材料'}
                {itemFormData.category === '材料' && itemGenTemplate === 'crystal' && '晶体：能量结晶，稀有珍宝'}
                {itemFormData.category === '材料' && itemGenTemplate === 'bone' && '骨骼：兽骨、化石'}

                {/* 阵法模板详情 */}
                {itemFormData.category === '阵法' && itemGenTemplate === 'attack' && '攻击阵法：杀伐果断'}
                {itemFormData.category === '阵法' && itemGenTemplate === 'defense' && '防御阵法：固若金汤'}
                {itemFormData.category === '阵法' && itemGenTemplate === 'trap' && '陷阱阵法：困敌杀敌'}
                {itemFormData.category === '阵法' && itemGenTemplate === 'teleport' && '传送阵法：空间移动'}

                {/* 秘宝模板详情 */}
                {itemFormData.category === '秘宝' && itemGenTemplate === 'weapon' && '武器类：攻防利器'}
                {itemFormData.category === '秘宝' && itemGenTemplate === 'armor' && '防具类：护身宝甲'}
                {itemFormData.category === '秘宝' && itemGenTemplate === 'accessory' && '饰品类：辅助功能'}
                {itemFormData.category === '秘宝' && itemGenTemplate === 'space' && '空间类：储物或洞天'}

                {/* 传承模板详情 */}
                {itemFormData.category === '传承' && itemGenTemplate === 'ancient' && '远古传承：古老强大'}
                {itemFormData.category === '传承' && itemGenTemplate === 'family' && '家族传承：血脉延续'}
                {itemFormData.category === '传承' && itemGenTemplate === 'secret' && '隐藏传承：不为人知'}
                {itemFormData.category === '传承' && itemGenTemplate === 'divine' && '神级传承：天神遗泽'}
              </Typography>
            </Box>
          )}

          {/* 模板选择 */}
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mb: 1 }}>选择模板：</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {/* 势力模板 */}
              {itemFormData.category === '势力' && (
                <>
                  <Chip
                    label="自定义"
                    size="small"
                    onClick={() => setItemGenTemplate('custom')}
                    sx={{
                      bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'custom' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'custom' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="修仙门派"
                    size="small"
                    onClick={() => setItemGenTemplate('factory')}
                    sx={{
                      bgcolor: itemGenTemplate === 'factory' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'factory' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'factory' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'factory' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="魔道势力"
                    size="small"
                    onClick={() => setItemGenTemplate('evil')}
                    sx={{
                      bgcolor: itemGenTemplate === 'evil' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'evil' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'evil' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'evil' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="帝国皇朝"
                    size="small"
                    onClick={() => setItemGenTemplate('empire')}
                    sx={{
                      bgcolor: itemGenTemplate === 'empire' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'empire' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'empire' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'empire' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="种族势力"
                    size="small"
                    onClick={() => setItemGenTemplate('tribe')}
                    sx={{
                      bgcolor: itemGenTemplate === 'tribe' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'tribe' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'tribe' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'tribe' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="联盟组织"
                    size="small"
                    onClick={() => setItemGenTemplate('alliance')}
                    sx={{
                      bgcolor: itemGenTemplate === 'alliance' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'alliance' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'alliance' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'alliance' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="家族势力"
                    size="small"
                    onClick={() => setItemGenTemplate('family')}
                    sx={{
                      bgcolor: itemGenTemplate === 'family' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'family' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'family' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'family' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="神秘组织"
                    size="small"
                    onClick={() => setItemGenTemplate('secret')}
                    sx={{
                      bgcolor: itemGenTemplate === 'secret' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'secret' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'secret' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'secret' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                </>
              )}
              {/* 功法模板 */}
              {itemFormData.category === '功法' && (
                <>
                  <Chip
                    label="自定义"
                    size="small"
                    onClick={() => setItemGenTemplate('custom')}
                    sx={{
                      bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'custom' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'custom' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="修炼功法"
                    size="small"
                    onClick={() => setItemGenTemplate('cultivation')}
                    sx={{
                      bgcolor: itemGenTemplate === 'cultivation' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'cultivation' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'cultivation' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'cultivation' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="攻击神通"
                    size="small"
                    onClick={() => setItemGenTemplate('attack')}
                    sx={{
                      bgcolor: itemGenTemplate === 'attack' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'attack' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'attack' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'attack' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="防御秘术"
                    size="small"
                    onClick={() => setItemGenTemplate('defense')}
                    sx={{
                      bgcolor: itemGenTemplate === 'defense' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'defense' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'defense' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'defense' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="辅助秘法"
                    size="small"
                    onClick={() => setItemGenTemplate('auxiliary')}
                    sx={{
                      bgcolor: itemGenTemplate === 'auxiliary' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'auxiliary' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'auxiliary' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'auxiliary' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="阵法禁制"
                    size="small"
                    onClick={() => setItemGenTemplate('array')}
                    sx={{
                      bgcolor: itemGenTemplate === 'array' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'array' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'array' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'array' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="炼体功法"
                    size="small"
                    onClick={() => setItemGenTemplate('body')}
                    sx={{
                      bgcolor: itemGenTemplate === 'body' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'body' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'body' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'body' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="魔功邪术"
                    size="small"
                    onClick={() => setItemGenTemplate('evil')}
                    sx={{
                      bgcolor: itemGenTemplate === 'evil' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'evil' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'evil' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'evil' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                </>
              )}
              {/* 物品模板 */}
              {itemFormData.category === '物品' && (
                <>
                  <Chip
                    label="自定义"
                    size="small"
                    onClick={() => setItemGenTemplate('custom')}
                    sx={{
                      bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'custom' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'custom' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="剑类武器"
                    size="small"
                    onClick={() => setItemGenTemplate('sword')}
                    sx={{
                      bgcolor: itemGenTemplate === 'sword' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'sword' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'sword' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'sword' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="珠子形态"
                    size="small"
                    onClick={() => setItemGenTemplate('bead')}
                    sx={{
                      bgcolor: itemGenTemplate === 'bead' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'bead' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'bead' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'bead' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="卷轴类"
                    size="small"
                    onClick={() => setItemGenTemplate('scroll')}
                    sx={{
                      bgcolor: itemGenTemplate === 'scroll' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'scroll' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'scroll' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'scroll' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="鼎炉类"
                    size="small"
                    onClick={() => setItemGenTemplate('furnace')}
                    sx={{
                      bgcolor: itemGenTemplate === 'furnace' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'furnace' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'furnace' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'furnace' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="冠饰类"
                    size="small"
                    onClick={() => setItemGenTemplate('crown')}
                    sx={{
                      bgcolor: itemGenTemplate === 'crown' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'crown' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'crown' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'crown' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="防御型"
                    size="small"
                    onClick={() => setItemGenTemplate('shield')}
                    sx={{
                      bgcolor: itemGenTemplate === 'shield' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'shield' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'shield' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'shield' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="丹药"
                    size="small"
                    onClick={() => setItemGenTemplate('pill')}
                    sx={{
                      bgcolor: itemGenTemplate === 'pill' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'pill' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'pill' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'pill' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                </>
              )}
              {/* 副本模板 */}
              {itemFormData.category === '副本' && (
                <>
                  <Chip
                    label="自定义"
                    size="small"
                    onClick={() => setItemGenTemplate('custom')}
                    sx={{
                      bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'custom' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'custom' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'custom' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="修仙大陆"
                    size="small"
                    onClick={() => setItemGenTemplate('continent')}
                    sx={{
                      bgcolor: itemGenTemplate === 'continent' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'continent' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'continent' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'continent' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="秘境洞天"
                    size="small"
                    onClick={() => setItemGenTemplate('secret')}
                    sx={{
                      bgcolor: itemGenTemplate === 'secret' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'secret' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'secret' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'secret' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="主城都城"
                    size="small"
                    onClick={() => setItemGenTemplate('city')}
                    sx={{
                      bgcolor: itemGenTemplate === 'city' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'city' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'city' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'city' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="深山老林"
                    size="small"
                    onClick={() => setItemGenTemplate('forest')}
                    sx={{
                      bgcolor: itemGenTemplate === 'forest' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'forest' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'forest' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'forest' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="海洋领域"
                    size="small"
                    onClick={() => setItemGenTemplate('ocean')}
                    sx={{
                      bgcolor: itemGenTemplate === 'ocean' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'ocean' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'ocean' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'ocean' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="绝地险境"
                    size="small"
                    onClick={() => setItemGenTemplate('danger')}
                    sx={{
                      bgcolor: itemGenTemplate === 'danger' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'danger' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'danger' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'danger' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                  <Chip
                    label="边陲小镇"
                    size="small"
                    onClick={() => setItemGenTemplate('border')}
                    sx={{
                      bgcolor: itemGenTemplate === 'border' ? 'rgba(255,107,53,0.3)' : 'rgba(255,255,255,0.08)',
                      color: itemGenTemplate === 'border' ? '#ff6b35' : 'rgba(255,255,255,0.7)',
                      border: itemGenTemplate === 'border' ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.15)',
                      fontSize: 11,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: itemGenTemplate === 'border' ? 'rgba(255,107,53,0.4)' : 'rgba(255,255,255,0.12)' }
                    }}
                  />
                </>
              )}

              {/* 角色模板 */}
              {itemFormData.category === '角色' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="主角" size="small" onClick={() => setItemGenTemplate('protagonist')} sx={chipStyle('protagonist')} />
                  <Chip label="反派" size="small" onClick={() => setItemGenTemplate('antagonist')} sx={chipStyle('antagonist')} />
                  <Chip label="导师" size="small" onClick={() => setItemGenTemplate('mentor')} sx={chipStyle('mentor')} />
                  <Chip label="恋人" size="small" onClick={() => setItemGenTemplate('love')} sx={chipStyle('love')} />
                  <Chip label="好友" size="small" onClick={() => setItemGenTemplate('friend')} sx={chipStyle('friend')} />
                  <Chip label="宿敌" size="small" onClick={() => setItemGenTemplate('rival')} sx={chipStyle('rival')} />
                  <Chip label="NPC" size="small" onClick={() => setItemGenTemplate('npc')} sx={chipStyle('npc')} />
                </>
              )}

              {/* 种族模板 */}
              {itemFormData.category === '种族' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="龙族" size="small" onClick={() => setItemGenTemplate('dragon')} sx={chipStyle('dragon')} />
                  <Chip label="凤凰族" size="small" onClick={() => setItemGenTemplate('phoenix')} sx={chipStyle('phoenix')} />
                  <Chip label="恶魔族" size="small" onClick={() => setItemGenTemplate('demon')} sx={chipStyle('demon')} />
                  <Chip label="精灵族" size="small" onClick={() => setItemGenTemplate('spirit')} sx={chipStyle('spirit')} />
                  <Chip label="神族" size="small" onClick={() => setItemGenTemplate('god')} sx={chipStyle('god')} />
                </>
              )}

              {/* 地域模板 */}
              {itemFormData.category === '地域' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="山脉" size="small" onClick={() => setItemGenTemplate('mountain')} sx={chipStyle('mountain')} />
                  <Chip label="河流" size="small" onClick={() => setItemGenTemplate('river')} sx={chipStyle('river')} />
                  <Chip label="森林" size="small" onClick={() => setItemGenTemplate('forest')} sx={chipStyle('forest')} />
                  <Chip label="沙漠" size="small" onClick={() => setItemGenTemplate('desert')} sx={chipStyle('desert')} />
                  <Chip label="岛屿" size="small" onClick={() => setItemGenTemplate('island')} sx={chipStyle('island')} />
                </>
              )}

              {/* 王朝模板 */}
              {itemFormData.category === '王朝' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="大一统王朝" size="small" onClick={() => setItemGenTemplate('empire')} sx={chipStyle('empire')} />
                  <Chip label="王国" size="small" onClick={() => setItemGenTemplate('kingdom')} sx={chipStyle('kingdom')} />
                  <Chip label="部落" size="small" onClick={() => setItemGenTemplate('clan')} sx={chipStyle('clan')} />
                </>
              )}

              {/* 组织模板 */}
              {itemFormData.category === '组织' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="秘密组织" size="small" onClick={() => setItemGenTemplate('secret')} sx={chipStyle('secret')} />
                  <Chip label="杀手组织" size="small" onClick={() => setItemGenTemplate('assassin')} sx={chipStyle('assassin')} />
                  <Chip label="商会" size="small" onClick={() => setItemGenTemplate('merchant')} sx={chipStyle('merchant')} />
                  <Chip label="宗教" size="small" onClick={() => setItemGenTemplate('religious')} sx={chipStyle('religious')} />
                </>
              )}

              {/* 血脉体质模板 */}
              {itemFormData.category === '血脉' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="远古血脉" size="small" onClick={() => setItemGenTemplate('ancient')} sx={chipStyle('ancient')} />
                  <Chip label="神级体质" size="small" onClick={() => setItemGenTemplate('divine')} sx={chipStyle('divine')} />
                  <Chip label="特殊体质" size="small" onClick={() => setItemGenTemplate('mysterious')} sx={chipStyle('mysterious')} />
                </>
              )}

              {/* 灵根资质模板 */}
              {itemFormData.category === '灵根' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="单一灵根" size="small" onClick={() => setItemGenTemplate('single')} sx={chipStyle('single')} />
                  <Chip label="双灵根" size="small" onClick={() => setItemGenTemplate('dual')} sx={chipStyle('dual')} />
                  <Chip label="多灵根" size="small" onClick={() => setItemGenTemplate('multiple')} sx={chipStyle('multiple')} />
                  <Chip label="无灵根" size="small" onClick={() => setItemGenTemplate('none')} sx={chipStyle('none')} />
                </>
              )}

              {/* 灵兽模板 */}
              {itemFormData.category === '灵兽' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="野兽型" size="small" onClick={() => setItemGenTemplate('beast')} sx={chipStyle('beast')} />
                  <Chip label="鸟类" size="small" onClick={() => setItemGenTemplate('bird')} sx={chipStyle('bird')} />
                  <Chip label="水生" size="small" onClick={() => setItemGenTemplate('aquatic')} sx={chipStyle('aquatic')} />
                  <Chip label="虫类" size="small" onClick={() => setItemGenTemplate('insect')} sx={chipStyle('insect')} />
                </>
              )}

              {/* 材料模板 */}
              {itemFormData.category === '材料' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="灵草" size="small" onClick={() => setItemGenTemplate('herb')} sx={chipStyle('herb')} />
                  <Chip label="矿物" size="small" onClick={() => setItemGenTemplate('mineral')} sx={chipStyle('mineral')} />
                  <Chip label="晶体" size="small" onClick={() => setItemGenTemplate('crystal')} sx={chipStyle('crystal')} />
                  <Chip label="骨骼" size="small" onClick={() => setItemGenTemplate('bone')} sx={chipStyle('bone')} />
                </>
              )}

              {/* 阵法模板 */}
              {itemFormData.category === '阵法' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="攻击" size="small" onClick={() => setItemGenTemplate('attack')} sx={chipStyle('attack')} />
                  <Chip label="防御" size="small" onClick={() => setItemGenTemplate('defense')} sx={chipStyle('defense')} />
                  <Chip label="陷阱" size="small" onClick={() => setItemGenTemplate('trap')} sx={chipStyle('trap')} />
                  <Chip label="传送" size="small" onClick={() => setItemGenTemplate('teleport')} sx={chipStyle('teleport')} />
                </>
              )}

              {/* 秘宝模板 */}
              {itemFormData.category === '秘宝' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="武器" size="small" onClick={() => setItemGenTemplate('weapon')} sx={chipStyle('weapon')} />
                  <Chip label="防具" size="small" onClick={() => setItemGenTemplate('armor')} sx={chipStyle('armor')} />
                  <Chip label="饰品" size="small" onClick={() => setItemGenTemplate('accessory')} sx={chipStyle('accessory')} />
                  <Chip label="空间" size="small" onClick={() => setItemGenTemplate('space')} sx={chipStyle('space')} />
                </>
              )}

              {/* 传承模板 */}
              {itemFormData.category === '传承' && (
                <>
                  <Chip label="自定义" size="small" onClick={() => setItemGenTemplate('custom')} sx={chipStyle('custom')} />
                  <Chip label="远古" size="small" onClick={() => setItemGenTemplate('ancient')} sx={chipStyle('ancient')} />
                  <Chip label="家族" size="small" onClick={() => setItemGenTemplate('family')} sx={chipStyle('family')} />
                  <Chip label="隐藏" size="small" onClick={() => setItemGenTemplate('secret')} sx={chipStyle('secret')} />
                  <Chip label="神级" size="small" onClick={() => setItemGenTemplate('divine')} sx={chipStyle('divine')} />
                </>
              )}
            </Box>
          </Box>

          {/* AI生成输入框 - 动态高度 */}
          <TextField
            fullWidth
            multiline
            {...getDynamicTextAreaStyle(itemGenPrompt, 2, 8)}
            placeholder={getPlaceholderByCategory(itemFormData.category)}
            value={itemGenPrompt}
            onChange={(e) => setItemGenPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                e.preventDefault();
                handleAIGenerateItem();
              }
            }}
            className="character-panel-textarea"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0,0,0,0.25)',
                fontSize: 13,
                alignItems: 'flex-start',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1,
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: 'rgba(255,255,255,0.14)' },
                '&.Mui-focused': { borderColor: 'rgba(255,255,255,0.22)', boxShadow: '0 0 0 1px rgba(255,255,255,0.1)' }
              },
              '& textarea': { lineHeight: 1.6 }
            }}
          />
          <Button
            fullWidth
            variant="outlined"
            size="small"
            onClick={handleAIGenerateItem}
            disabled={generatingItem || !itemGenPrompt.trim()}
            className="workspace-btn-cyber character-panel-action-btn"
            sx={{
              bgcolor: generatingItem ? 'rgba(255,107,53,0.15)' : 'transparent',
              color: generatingItem ? '#ff6b35' : 'rgba(255,255,255,0.92)',
              fontSize: 13,
              py: 1.2,
              border: generatingItem ? '1px solid #ff6b35' : '1px solid rgba(255,255,255,0.22)',
              borderRadius: 1,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              textShadow: generatingItem ? '0 0 12px rgba(255,107,53,0.5)' : '0 0 8px rgba(255,255,255,0.2)',
              boxShadow: generatingItem ? '0 0 20px rgba(255,107,53,0.3), inset 0 0 10px rgba(255,107,53,0.1)' : 'none',
              '&:hover': {
                bgcolor: generatingItem ? 'rgba(255,107,53,0.2)' : 'rgba(255,255,255,0.06)',
                borderColor: generatingItem ? '#ff6b35' : 'rgba(255,255,255,0.35)',
                boxShadow: generatingItem ? '0 0 25px rgba(255,107,53,0.4)' : '0 0 14px rgba(255,255,255,0.08)'
              },
              '&:disabled': {
                color: 'rgba(255,255,255,0.25)',
                borderColor: 'rgba(255,255,255,0.06)',
                textShadow: 'none',
                boxShadow: 'none'
              },
              ...(generatingItem && {
                animation: 'pulse 1.5s ease-in-out infinite'
              })
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {generatingItem ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#ff6b35', animation: 'spin 1s linear infinite' }} />
                  <span style={{ animation: 'flicker 0.5s ease-in-out infinite alternate' }}>生成中...</span>
                </>
              ) : (
                <span>AI生成词条</span>
              )}
            </Box>
          </Button>
          <style>{`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.85; }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            @keyframes flicker {
              0% { opacity: 0.7; }
              100% { opacity: 1; }
            }
          `}</style>
        </Box>

        {/* 词条信息表单 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.25, delay: motionReduced ? 0 : 0.1 }}
          sx={{ pr: { xs: 0.25, sm: 0.5 } }}
        >
          {/* 基础信息 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: motionReduced ? 0 : 0.06, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            whileHover={motionReduced ? undefined : { y: -2 }}
            sx={{
              position: 'relative', mb: 2, mx: { xs: 1.5, sm: 2 }, mt: 1.5,
              p: { xs: 1.35, sm: 1.5 }, pt: { xs: 1.5, sm: 1.65 },
              borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
              overflow: 'hidden', '&::before': {
                content: '""', position: 'absolute', left: 0, top: '10%', bottom: '10%',
                width: 3, borderRadius: '0 2px 2px 0', bgcolor: '#ff6b35', boxShadow: '0 0 12px #ff6b35',
              }, pl: 2.25,
            }}
          >
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              基础信息
            </Typography>
            <Box component={motion.div} initial={motionReduced ? false : { opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: motionReduced ? 0 : 0.08, duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
              sx={{ mb: 2 }}>
              <TextField
                fullWidth size="small" label="词条名"
                value={itemFormData.name}
                onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5 } }}
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 0 }}>
              <FormControl fullWidth size="small">
                <InputLabel>分类</InputLabel>
                <Select
                  value={itemFormData.category}
                  label="分类"
                  onChange={(e) => { setItemFormData({ ...itemFormData, category: e.target.value }); setItemGenTemplate('custom'); }}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 },
                    fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(251,146,60,0.28)' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(251,146,60,0.48)' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: { backgroundColor: '#0c0c10', background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, boxShadow: '0 12px 40px rgba(0,0,0,0.7)' },
                      sx: { '& .MuiMenuItem-root': { color: 'rgba(226,232,240,0.9)', fontFamily: "'Noto Sans SC', system-ui, sans-serif", fontSize: 12, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' }, '&.Mui-selected': { bgcolor: 'rgba(251,146,60,0.15)', color: '#fb923c' } } }
                    }
                  }}
                >
                  {ITEM_CATEGORIES.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><span style={{ color: cat.color }}>{cat.icon}</span><span>{cat.name}</span></Box></MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box component={motion.div} initial={motionReduced ? false : { opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: motionReduced ? 0 : 0.1, duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}>
                <TextField fullWidth size="small" label="等级/品级" value={itemFormData.level}
                  onChange={(e) => setItemFormData({ ...itemFormData, level: e.target.value })}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5 } }}
                />
              </Box>
            </Box>
          </Box>

          {/* 词条描述 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: motionReduced ? 0 : 0.1, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            whileHover={motionReduced ? undefined : { y: -2 }}
            sx={{
              position: 'relative', mb: 2, mx: { xs: 1.5, sm: 2 },
              p: { xs: 1.35, sm: 1.5 }, pt: { xs: 1.5, sm: 1.65 },
              borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
              overflow: 'hidden', '&::before': {
                content: '""', position: 'absolute', left: 0, top: '10%', bottom: '10%',
                width: 3, borderRadius: '0 2px 2px 0', bgcolor: '#ff6b35', boxShadow: '0 0 12px #ff6b35',
              }, pl: 2.25,
            }}
          >
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              词条描述
            </Typography>
            <TextField fullWidth multiline {...getDynamicTextAreaStyle(itemFormData.description, 3, 10)}
              placeholder="描述词条的详细信息..." value={itemFormData.description}
              onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5, alignItems: 'flex-start' }, '& textarea': { lineHeight: 1.6 } }}
            />
            {/* 位置/所在地 */}
            <TextField fullWidth multiline {...getDynamicTextAreaStyle(itemFormData.location, 1, 4)}
              label="位置/所在地" placeholder="如：天火山·玄天峰" value={itemFormData.location || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, location: e.target.value })}
              sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5, alignItems: 'flex-start' } }}
            />
            {/* 掌控者/所属势力 */}
            <TextField fullWidth multiline {...getDynamicTextAreaStyle(itemFormData.master, 1, 4)}
              label="掌控者/所属势力" placeholder="如：北玄宗" value={itemFormData.master || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, master: e.target.value })}
              sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5, alignItems: 'flex-start' } }}
            />
            {/* 特征/能力 */}
            <TextField fullWidth multiline {...getDynamicTextAreaStyle(itemFormData.features, 2, 6)}
              label="特征/能力" placeholder="词条的主要特征或能力" value={itemFormData.features || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, features: e.target.value })}
              sx={{ mt: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5, alignItems: 'flex-start' } }}
            />
            {/* 历史背景 */}
            <TextField
              fullWidth
              multiline
              {...getDynamicTextAreaStyle(itemFormData.history, 2, 6)}
              label="历史背景"
              placeholder="词条的由来和历���"
              value={itemFormData.history || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, history: e.target.value })}
              sx={{
                mt: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: { xs: 12, sm: 13 },
                  alignItems: 'flex-start'
                }
              }}
            />

            {/* 价值/重要性 - 动态高度 */}
            <TextField
              fullWidth
              multiline
              {...getDynamicTextAreaStyle(itemFormData.value, 1, 4)}
              label="价值/重要性"
              placeholder="词条的价值或在故事中的重要性"
              value={itemFormData.value || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, value: e.target.value })}
              sx={{
                mt: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: { xs: 12, sm: 13 },
                  alignItems: 'flex-start'
                }
              }}
            />
          </Box>

          {/* 写作关联 - 卡片 */}
          <Box className="character-panel-card" sx={{ mb: 2 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, fontSize: { xs: 13, sm: 14 } }}>
              写作关联
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <TextField
                fullWidth
                size="small"
                label="首次出场章节"
                placeholder="如：5"
                value={itemFormData.firstChapter || ''}
                onChange={(e) => setItemFormData({ ...itemFormData, firstChapter: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)',
                    fontSize: { xs: 12, sm: 13 }
                  }
                }}
              />

              {/* 重要剧情点 - 动态高度 */}
              <TextField
                fullWidth
                multiline
                {...getDynamicTextAreaStyle(itemFormData.plotPoints, 1, 4)}
                label="重要剧情点"
                placeholder="用逗号分隔"
                value={itemFormData.plotPoints || ''}
                onChange={(e) => setItemFormData({ ...itemFormData, plotPoints: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)',
                    fontSize: { xs: 12, sm: 13 },
                    alignItems: 'flex-start'
                  }
                }}
              />
            </Box>

            {/* 伏笔/后续计划 - 动态高度 */}
            <TextField
              fullWidth
              multiline
              {...getDynamicTextAreaStyle(itemFormData.foreshadowing, 2, 5)}
              label="伏笔/后续计划"
              placeholder="记录需要回收的伏笔和后续计划"
              value={itemFormData.foreshadowing || ''}
              onChange={(e) => setItemFormData({ ...itemFormData, foreshadowing: e.target.value })}
              sx={{
                mt: 1.5,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: { xs: 12, sm: 13 },
                  alignItems: 'flex-start'
                }
              }}
            />
          </Box>

          {/* 操作按钮 */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => handleSaveItem(itemFormData)}
              className="workspace-btn-cyber character-panel-action-btn"
              sx={{
                bgcolor: 'transparent',
                color: 'rgba(255,255,255,0.92)',
                fontSize: 13,
                py: 1,
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: 1,
                textTransform: 'none',
                transition: 'all 0.25s ease',
                textShadow: '0 0 8px rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderColor: 'rgba(255,255,255,0.35)',
                  boxShadow: '0 0 14px rgba(255,255,255,0.08)'
                }
              }}
            >
              保存
            </Button>
            {editingItem && (
              <Button
                fullWidth
                variant="outlined"
                onClick={() => { handleDeleteItem(editingItem._id); handleClosePanel(); }}
                className="workspace-btn-neon-danger"
                sx={{
                  bgcolor: 'transparent',
                  color: 'rgba(255,68,68,0.9)',
                  fontSize: 13,
                  py: 1,
                  border: '1px solid rgba(255,68,68,0.4)',
                  borderRadius: 1,
                  textTransform: 'none',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,68,68,0.1)',
                    borderColor: 'rgba(255,68,68,0.7)',
                    boxShadow: '0 0 14px rgba(255,68,68,0.2)'
                  }
                }}
              >
                删除
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // 渲染备忘录编辑面板 - 与角色编辑面板样式统一
  const renderMemoEditPanel = () => {
    const currentCategory = MEMO_CATEGORIES.find(c => c.id === memoFormData.type) || MEMO_CATEGORIES[0];
    return (
      <Box
        className="panel-content character-panel-cyber workspace-memo-edit-panel"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          pb: 2,
          position: 'relative',
          zIndex: 1,
          minHeight: '100%'
        }}
      >
        {/* 头部 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
        >
          <Box component={motion.div} whileHover={motionReduced ? undefined : { scale: 1.06 }} whileTap={motionReduced ? undefined : { scale: 0.92 }}>
            <IconButton
              onClick={() => { setActivePanel('memo'); setEditingMemo(null); }}
              sx={{ color: 'rgba(255,255,255,0.65)', '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.07)' } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography sx={{ flex: 1, color: currentCategory.color, fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: `0 0 16px ${currentCategory.color}55`, letterSpacing: '0.02em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
            {editingMemo ? '编辑备忘录' : '添加备忘录'}
          </Typography>
        </Box>

        {/* 备忘录信息表单 */}
        <Box
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: motionReduced ? 0 : 0.25, delay: motionReduced ? 0 : 0.08 }}
          sx={{ pr: { xs: 0.25, sm: 0.5 }, overflowY: 'auto', flex: 1 }}
        >
          {/* 基本信息 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: motionReduced ? 0 : 0.06, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            whileHover={motionReduced ? undefined : { y: -2 }}
            sx={{
              position: 'relative', mb: 2, mx: { xs: 1.5, sm: 2 }, mt: 1.5,
              p: { xs: 1.35, sm: 1.5 }, pt: { xs: 1.5, sm: 1.65 },
              borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              boxShadow: '0 2px 12px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
              overflow: 'hidden', '&::before': { content: '""', position: 'absolute', left: 0, top: '10%', bottom: '10%', width: 3, borderRadius: '0 2px 2px 0', bgcolor: currentCategory.color, boxShadow: `0 0 12px ${currentCategory.color}` }, pl: 2.25,
            }}
          >
            <Typography sx={{ display: 'block', mb: 1.5, fontSize: { xs: 12, sm: 13 }, fontWeight: 700, color: 'rgba(226,232,240,0.92)', letterSpacing: '0.06em' }}>
              基本信息
            </Typography>

            <Box component={motion.div} initial={motionReduced ? false : { opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: motionReduced ? 0 : 0.08, duration: motionReduced ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
              sx={{ mb: 2 }}>
              <TextField fullWidth size="small" label="标题" placeholder="输入备忘录标题"
                value={memoFormData.title}
                onChange={(e) => setMemoFormData({ ...memoFormData, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', fontSize: { xs: 12, sm: 13 }, borderRadius: 1.5 } }}
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mb: 2 }}>
              {/* 类型选择 */}
              <FormControl fullWidth size="small">
                <InputLabel>分类</InputLabel>
                <Select
                  value={memoFormData.type}
                  label="分类"
                  onChange={(e) => setMemoFormData({ ...memoFormData, type: e.target.value })}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: '#f1f5f9',
                    fontSize: 12,
                    fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 1.5,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                    '&:hover': { borderColor: 'rgba(251,191,36,0.28)' },
                    '&.Mui-focused': { borderColor: 'rgba(251,191,36,0.48)', boxShadow: '0 0 0 1px rgba(251,191,36,0.12)' },
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: '#0c0c10',
                        background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7)'
                      },
                      sx: {
                        '& .MuiMenuItem-root': {
                          color: 'rgba(226,232,240,0.9)',
                          fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                          fontSize: 12,
                          py: 1,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                          '&.Mui-selected': { bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }
                        }
                      }
                    }
                  }}
                >
                  {MEMO_CATEGORIES.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>{cat.icon}</span>
                        <span>{cat.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 优先级选择 */}
              <FormControl fullWidth size="small">
                <InputLabel>优先级</InputLabel>
                <Select
                  value={memoFormData.priority || 'medium'}
                  label="优先级"
                  onChange={(e) => setMemoFormData({ ...memoFormData, priority: e.target.value })}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.04)',
                    color: '#f1f5f9',
                    fontSize: 12,
                    fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 1.5,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                    '&:hover': { borderColor: 'rgba(251,191,36,0.28)' },
                    '&.Mui-focused': { borderColor: 'rgba(251,191,36,0.48)', boxShadow: '0 0 0 1px rgba(251,191,36,0.12)' },
                    '& .MuiSelect-select': {
                      py: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        backgroundColor: '#0c0c10',
                        background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 8,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7)'
                      },
                      sx: {
                        '& .MuiMenuItem-root': {
                          color: 'rgba(226,232,240,0.9)',
                          fontFamily: "'Noto Sans SC', system-ui, sans-serif",
                          fontSize: 12,
                          py: 1,
                          '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                          '&.Mui-selected': { bgcolor: 'rgba(251,191,36,0.15)', color: '#fbbf24' }
                        }
                      }
                    }
                  }}
                >
                  {MEMO_PRIORITIES.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color }} />
                        <span>{p.name}</span>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* 状态选择 */}
            <FormControl fullWidth size="small">
              <InputLabel>状态</InputLabel>
              <Select
                value={memoFormData.status || 'pending'}
                label="状态"
                onChange={(e) => setMemoFormData({ ...memoFormData, status: e.target.value })}
                sx={{
                  bgcolor: 'rgba(0,0,0,0.35)',
                  color: '#fff',
                  fontSize: 13,
                  border: '1px solid rgba(255,107,53,0.3)',
                  borderRadius: 1,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                  '&:hover': { borderColor: 'rgba(255,107,53,0.5)' },
                  '&.Mui-focused': { borderColor: 'rgba(255,107,53,0.8)', boxShadow: '0 0 8px rgba(255,107,53,0.2)' },
                  '& .MuiSelect-select': {
                    py: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }
                }}
                MenuProps={{
                  PaperProps: {
                    style: {
                      backgroundColor: '#0c0c10',
                      background: 'linear-gradient(180deg, #0e0e12 0%, #08080c 100%)',
                      border: '1px solid rgba(255,107,53,0.3)',
                      borderRadius: 8,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,107,53,0.1)'
                    },
                    sx: {
                      '& .MuiMenuItem-root': {
                        color: 'rgba(255,255,255,0.9)',
                        fontSize: 13,
                        py: 1,
                        '&:hover': { bgcolor: 'rgba(255,107,53,0.15)' },
                        '&.Mui-selected': { bgcolor: 'rgba(255,107,53,0.25)', color: '#ff6b35' }
                      }
                    }
                  }
                }}
              >
                {MEMO_STATUS.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                      <span>{s.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* 详细内容 - 卡片 */}
          <Box className="character-panel-card" sx={{ mb: 2 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, fontSize: { xs: 13, sm: 14 } }}>
              详细内容
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={6}
              placeholder="详细记录你的想法、灵感、设定内容..."
              value={memoFormData.content}
              onChange={(e) => setMemoFormData({ ...memoFormData, content: e.target.value })}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: { xs: 12, sm: 13 },
                  alignItems: 'flex-start',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 1,
                  '&:hover': { borderColor: 'rgba(255,255,255,0.14)' },
                  '&.Mui-focused': { borderColor: currentCategory.color, boxShadow: `0 0 0 1px ${currentCategory.color}33` }
                },
                '& textarea': { lineHeight: 1.6, overflow: 'visible !important' }
              }}
            />
          </Box>
        </Box>

        {/* 保存按钮 */}
        <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSaveMemo(memoFormData)}
            className="workspace-btn-cyber character-panel-action-btn"
            sx={{
              bgcolor: 'transparent',
              color: 'rgba(255,255,255,0.92)',
              fontSize: 14,
              py: 1.2,
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 1,
              textTransform: 'none',
              transition: 'all 0.25s ease',
              textShadow: '0 0 8px rgba(255,255,255,0.2)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.06)',
                borderColor: 'rgba(255,255,255,0.35)',
                boxShadow: '0 0 14px rgba(255,255,255,0.08)'
              }
            }}
          >
            保存备忘录
          </Button>
        </Box>
      </Box>
    );
  };

  // 渲染 AI 工具选择面板（样式对齐辅助创作章纲：天青描边 + 渐变底 + 入场动画）
  const renderAIPanel = () => (
    <motion.div
      className="panel-content ai-tools-grid assist-ai-tool-panel"
      initial={motionReduced ? false : { opacity: 0, y: 10 }}
      animate={motionReduced ? false : { opacity: 1, y: 0 }}
      transition={motionReduced ? { duration: 0 } : { duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <Box
        className="assist-ai-tool-hero"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px solid ${ASSIST_AI_SKY.border}`,
          background: ASSIST_AI_SKY.cardGrad,
          boxShadow: '0 4px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
        }}
      >
        {isChaptersNarrow && (
          <Tooltip title="返回编辑">
            <IconButton
              size="small"
              onClick={handleClosePanel}
              aria-label="返回编辑"
              sx={{
                flexShrink: 0,
                minWidth: { xs: 34, sm: 36 },
                minHeight: { xs: 34, sm: 36 },
                color: 'rgba(255,255,255,0.8)',
                border: `1px solid ${ASSIST_AI_SKY.borderSoft}`,
                bgcolor: 'rgba(56,189,248,0.08)',
                '&:hover': { bgcolor: 'rgba(56,189,248,0.16)', borderColor: ASSIST_AI_SKY.border },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Tooltip>
        )}
        <BookIcon sx={{ fontSize: { xs: 22, sm: 24 }, color: ASSIST_AI_SKY.main, filter: 'drop-shadow(0 0 10px rgba(125,211,252,0.35))' }} />
        <Typography sx={{ flex: 1, minWidth: 0, color: ASSIST_AI_SKY.title, fontWeight: 700, fontSize: { xs: 15, sm: 17 }, letterSpacing: '0.02em' }}>
          AI 工具集
        </Typography>
      </Box>

      <Grid container spacing={1.25}>
        {aiToolsMainGrid.map((tool, idx) => (
          <Grid item xs={6} sm={4} key={tool.id}>
            <motion.div
              initial={motionReduced ? false : { opacity: 0, y: 14 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.32, delay: 0.04 + idx * 0.05, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -3, transition: { duration: 0.2 } }}
              whileTap={motionReduced ? undefined : { scale: 0.98 }}
            >
              <Card
                onClick={() => handleOpenPanel(tool.panel)}
                className="assist-ai-tool-card"
                sx={{
                  background: ASSIST_AI_SKY.cardGrad,
                  border: `1px solid ${ASSIST_AI_SKY.borderSoft}`,
                  borderLeft: `3px solid ${tool.color}`,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.25s ease, border-color 0.25s ease',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.28)',
                  '&:hover': {
                    borderColor: ASSIST_AI_SKY.border,
                    boxShadow: `0 8px 28px rgba(56,189,248,0.14), 0 0 0 1px ${tool.color}44`,
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                  <tool.icon sx={{ fontSize: 28, color: tool.color, mb: 0.75, filter: `drop-shadow(0 0 8px ${tool.color}55)` }} />
                  <Typography sx={{ color: ASSIST_AI_SKY.title, fontWeight: 700, fontSize: 13 }}>{tool.name}</Typography>
                  <Typography sx={{ color: 'rgba(226,232,240,0.55)', fontSize: 11, mt: 0.25 }}>{tool.desc}</Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </motion.div>
  );

  // 渲染对话面板（消息区可滚动，输入条贴底；多行输入高度随内容增长，上限为面板高度 33%）
  const renderChatPanel = () => {
    const chatTextareaMaxPx = Math.max(52, chatComposerMaxPx - 40);
    const currentConv = conversations.find(c => c._id === currentConversationId);

    return (
      <Box
        ref={chatPanelLayoutRef}
        className="panel-content chat-panel"
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* 顶部：对话选择器区域 */}
        <Box sx={{ flexShrink: 0, px: { xs: 1, sm: 1.5 }, pt: 1, pb: 1, borderBottom: '1px solid rgba(0,240,255,0.12)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            {/* 窄屏：关闭全屏侧栏放在标题行左侧，避免与右上角全局浮动按钮重叠 */}
            {isChaptersNarrow && (
              <Tooltip title="返回编辑">
                <IconButton
                  size="small"
                  onClick={handleClosePanel}
                  aria-label="返回编辑"
                  sx={{
                    flexShrink: 0,
                    minWidth: { xs: 34, sm: 36 },
                    minHeight: { xs: 34, sm: 36 },
                    color: 'rgba(255,255,255,0.75)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    '&:hover': { bgcolor: 'rgba(0,240,255,0.12)', borderColor: 'rgba(0,240,255,0.35)' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography variant="h6" className="workspace-panel-title" sx={{ flex: 1, minWidth: 0, fontSize: { xs: 13, sm: 16 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {currentConv?.title || 'AI 创作助手'}
            </Typography>
            <Tooltip title={conversationListOpen ? '收起' : '切换对话'}>
              <IconButton
                size="small"
                onClick={() => setConversationListOpen(!conversationListOpen)}
                sx={{
                  minWidth: { xs: 32, sm: 36 },
                  minHeight: { xs: 32, sm: 36 },
                  color: conversationListOpen ? '#00f0ff' : 'rgba(255,255,255,0.5)',
                  bgcolor: conversationListOpen ? 'rgba(0,240,255,0.15)' : 'transparent',
                  border: '1px solid',
                  borderColor: conversationListOpen ? 'rgba(0,240,255,0.4)' : 'rgba(255,255,255,0.15)',
                  '&:hover': { bgcolor: 'rgba(0,240,255,0.15)', borderColor: 'rgba(0,240,255,0.4)' }
                }}
              >
                <PsychologyIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="新建对话">
              <IconButton
                size="small"
                onClick={handleCreateConversation}
                sx={{
                  minWidth: { xs: 32, sm: 36 },
                  minHeight: { xs: 32, sm: 36 },
                  color: '#00ff9f',
                  bgcolor: 'rgba(0,255,159,0.08)',
                  border: '1px solid rgba(0,255,159,0.25)',
                  '&:hover': { bgcolor: 'rgba(0,255,159,0.15)', borderColor: 'rgba(0,255,159,0.4)' }
                }}
              >
                <AddIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* 对话列表（展开时显示） */}
          {conversationListOpen && (
            <Box sx={{
              mt: 1,
              maxHeight: { xs: 320, sm: 240 },
              overflowY: 'auto',
              bgcolor: 'rgba(15, 15, 30, 0.98)',
              borderRadius: 1.5,
              border: '1px solid rgba(139, 92, 246, 0.2)',
              p: { xs: 0.5, sm: 0.5 },
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }}>
              {conversationLoading ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <CircularProgress size={20} sx={{ color: '#a78bfa' }} />
                </Box>
              ) : conversations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无对话记录</Typography>
                </Box>
              ) : (
                conversations.map(conv => (
                  <Box
                    key={conv._id}
                    onClick={() => handleSwitchConversation(conv._id)}
                    sx={{
                      px: { xs: 1, sm: 1.5 },
                      py: { xs: 0.75, sm: 1 },
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      mb: 0.5,
                      bgcolor: conv._id === currentConversationId ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                      border: '1px solid',
                      borderColor: conv._id === currentConversationId ? 'rgba(139, 92, 246, 0.3)' : 'transparent',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'rgba(139, 92, 246, 0.1)',
                        borderColor: 'rgba(139, 92, 246, 0.2)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <PsychologyIcon sx={{ fontSize: 14, color: conv._id === currentConversationId ? '#a78bfa' : 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, color: conv._id === currentConversationId ? '#fff' : 'rgba(255,255,255,0.7)', fontSize: { xs: 12, sm: 13 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, fontWeight: conv._id === currentConversationId ? 600 : 400 }}>
                        {conv.title || '新对话'}
                      </Typography>
                      <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                        {conv.messageCount || 0}条
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteConversation(conv._id, e)}
                        sx={{
                          p: { xs: 0.125, sm: 0.25 },
                          minWidth: { xs: 24, sm: 24 },
                          minHeight: { xs: 24, sm: 24 },
                          color: 'rgba(255,255,255,0.3)',
                          '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.1)' }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                    {conv.lastMessagePreview && (
                      <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', pl: { xs: 2.5, sm: 3.5 } }}>
                        {conv.lastMessagePreview}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', mt: 0.25, pl: { xs: 2.5, sm: 3.5 } }}>
                      {conv.lastActiveAt ? formatTimeDisplay(conv.lastActiveAt) : ''}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>

        <Box
          className="chat-messages"
          ref={el => {
            if (el) {
              el.scrollTop = el.scrollHeight;
            }
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            mb: 0,
            px: { xs: 0.5, sm: 0.5 },
            pt: 0.5,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              width: 4,
            },
            '&::-webkit-scrollbar-track': {
              bgcolor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(139, 92, 246, 0.3)',
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'rgba(139, 92, 246, 0.5)'
              }
            }
          }}
        >
          {chatMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 2, sm: 4 }, px: 1 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 2,
                  borderRadius: '50%',
                  bgcolor: 'rgba(139, 92, 246, 0.15)',
                  border: '2px solid rgba(139, 92, 246, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'float 3s ease-in-out infinite',
                  '@keyframes float': {
                    '0%, 100%': { transform: 'translateY(0)' },
                    '50%': { transform: 'translateY(-8px)' }
                  }
                }}
              >
                <PsychologyIcon sx={{ fontSize: 32, color: '#a78bfa' }} />
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.8, fontWeight: 500 }}>
                你好！我是你的创作伙伴
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: { xs: 12, sm: 13 }, mt: 1, lineHeight: 1.6 }}>
                可以说出你的需求<br />
                比如「帮我续写下一章」
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                {['帮我续写', '润色文笔', '生成情节', '角色设定'].map((tip) => (
                  <Box
                    key={tip}
                    onClick={() => setChatInput(tip)}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.25)',
                      color: '#a78bfa',
                      fontSize: 11,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(139, 92, 246, 0.2)',
                        transform: 'scale(1.05)'
                      }
                    }}
                  >
                    {tip}
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, mt: 2 }}>
                Shift+Enter 换行 · Enter 发送
              </Typography>
            </Box>
          ) : (
            chatMessages.map((msg, idx) => (
              <Box
                key={idx}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  animation: 'slideIn 0.3s ease-out',
                  '@keyframes slideIn': {
                    from: { opacity: 0, transform: 'translateY(10px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  }
                }}
              >
                <Box sx={{ display: 'flex', gap: { xs: 0.75, sm: 1 }, alignItems: 'flex-start' }}>
                  <Avatar
                    sx={{
                      width: { xs: 28, sm: 32 },
                      height: { xs: 28, sm: 32 },
                      bgcolor: msg.role === 'ai'
                        ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                        : '#ff6b35',
                      background: msg.role === 'ai'
                        ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
                        : '#ff6b35',
                      fontSize: { xs: 11, sm: 12 },
                      fontWeight: 700,
                      flexShrink: 0,
                      boxShadow: msg.role === 'ai'
                        ? '0 0 12px rgba(139, 92, 246, 0.4)'
                        : '0 0 8px rgba(255, 107, 53, 0.3)'
                    }}
                  >
                    {msg.role === 'ai' ? 'AI' : '你'}
                  </Avatar>
                  <Box
                    sx={{
                      bgcolor: msg.role === 'ai'
                        ? 'linear-gradient(135deg, rgba(88, 28, 135, 0.4), rgba(30, 27, 75, 0.5))'
                        : 'rgba(255, 107, 53, 0.12)',
                      borderRadius: 2,
                      p: { xs: 1.25, sm: 1.5 },
                      flex: 1,
                      minWidth: 0,
                      border: `1px solid ${msg.role === 'ai'
                        ? 'rgba(139, 92, 246, 0.3)'
                        : 'rgba(255, 107, 53, 0.25)'}`,
                      boxShadow: msg.role === 'ai'
                        ? '0 4px 20px rgba(139, 92, 246, 0.15), inset 0 1px 0 rgba(255,255,255,0.05)'
                        : '0 2px 12px rgba(255, 107, 53, 0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': msg.role === 'ai' ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '1px',
                        background: 'linear-gradient(90deg, transparent, rgba(139, 92, 246, 0.5), transparent)'
                      } : {}
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#fff',
                        fontSize: { xs: 13, sm: 14 },
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        lineHeight: 1.7
                      }}
                    >
                      {msg.content || (msg._id && chatLoading && idx === chatMessages.length - 1 ? '' : msg.content)}
                    </Typography>
                    {msg._id && chatLoading && idx === chatMessages.length - 1 && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 0.5
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: '#a78bfa',
                            animation: 'pulse 1.2s ease-in-out infinite',
                            '@keyframes pulse': {
                              '0%, 100%': { opacity: 0.4, transform: 'scale(0.8)' },
                              '50%': { opacity: 1, transform: 'scale(1.2)' }
                            }
                          }}
                        />
                        <Box
                          component="span"
                          sx={{
                            animation: 'blink 0.8s step-end infinite',
                            color: '#a78bfa',
                            fontWeight: 600,
                            '@keyframes blink': {
                              '0%, 100%': { opacity: 1 },
                              '50%': { opacity: 0 }
                            }
                          }}
                        >
                          |
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            ))
          )}
          {/* 流式输出光标已内嵌在最后一条消息的气泡中，无需额外「正在思考」占位 */}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: { xs: 0.75, sm: 1 },
            pt: 1,
            pb: 'max(12px, env(safe-area-inset-bottom, 0px))',
            px: { xs: 0.75, sm: 1 },
            borderTop: '1px solid rgba(139, 92, 246, 0.2)',
            bgcolor: 'rgba(8, 9, 18, 0.98)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <TextField
            multiline
            fullWidth
            minRows={1}
            maxRows={6}
            size="small"
            inputRef={chatInputRef}
            placeholder="输入你的需求…（Shift+Enter 换行）"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleChatSend();
              }
            }}
            sx={{
              flex: 1,
              minWidth: 0,
              '& .MuiOutlinedInput-root': {
                alignItems: 'flex-start',
                py: 1,
                bgcolor: 'rgba(139, 92, 246, 0.08)',
                borderRadius: 2.5,
                border: '1px solid rgba(139, 92, 246, 0.2)',
                transition: 'all 0.2s ease',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
                '& fieldset': { borderColor: 'transparent' },
                '&:hover fieldset': { borderColor: 'rgba(139, 92, 246, 0.3)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(139, 92, 246, 0.5)' },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(0,0,0,0.2)'
                }
              },
              '& .MuiInputBase-inputMultiline': {
                maxHeight: 120,
                overflowY: 'auto !important',
                lineHeight: 1.6,
                resize: 'none',
                color: '#fff',
                fontSize: 14,
              },
              '& .MuiInputBase-inputMultiline::placeholder': {
                color: 'rgba(255,255,255,0.4)',
                fontSize: 13,
              }
            }}
          />
          <IconButton
            onClick={handleChatSend}
            disabled={chatLoading || !chatInput.trim()}
            sx={{
              flexShrink: 0,
              mb: 0.5,
              width: 44,
              height: 44,
              bgcolor: chatLoading || !chatInput.trim()
                ? 'rgba(139, 92, 246, 0.15)'
                : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              background: chatLoading || !chatInput.trim()
                ? 'rgba(139, 92, 246, 0.15)'
                : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              color: chatLoading || !chatInput.trim()
                ? 'rgba(255,255,255,0.3)'
                : '#fff',
              boxShadow: chatLoading || !chatInput.trim()
                ? 'none'
                : '0 4px 15px rgba(139, 92, 246, 0.4)',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: chatLoading || !chatInput.trim()
                  ? 'rgba(139, 92, 246, 0.15)'
                  : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                background: chatLoading || !chatInput.trim()
                  ? 'rgba(139, 92, 246, 0.15)'
                  : 'linear-gradient(135deg, #7c3aed, #4f46e5)',
                boxShadow: chatLoading || !chatInput.trim()
                  ? 'none'
                  : '0 6px 20px rgba(139, 92, 246, 0.5)',
                transform: chatLoading || !chatInput.trim() ? 'none' : 'scale(1.05)'
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(139, 92, 246, 0.1)',
                color: 'rgba(255,255,255,0.3)'
              },
            }}
          >
            {chatLoading ? (
              <CircularProgress size={20} sx={{ color: '#a78bfa' }} />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>
      </Box>
    );
  };

  // ==================== 卷管理函数 ====================
  // 创建新卷
  const handleSaveVolume = async () => {
    if (editingVolumeId) {
      // 编辑已有卷
      const updatedVolumes = volumes.map(v =>
        v._id === editingVolumeId
          ? { ...v, ...volumeFormData, updatedAt: new Date().toISOString() }
          : v
      );
      setVolumes(updatedVolumes);
      setSnackbar({ open: true, message: '卷更新成功', severity: 'success' });
      setVolumeDialogOpen(false);
      setEditingVolumeId(null);
      setVolumeFormData({ name: '', title: '', chapterCount: 10 });
      return;
    }

    // 新建卷
    const payload = {
      name: volumeFormData.name || getSuggestedVolumeName(volumes),
      title: volumeFormData.title || '',
      chapterCount: volumeFormData.chapterCount || 10,
    };

    try {
      const resp = await apiClient.post(`/assist/novels/${assistNovelId}/volumes`, payload);
      if (!resp.data?.success) throw new Error(resp.data?.message || '创建失败');

      const newVolume = resp.data.data;
      const targetCount = payload.chapterCount || 10;
      const newChapters = [];

      const globalMax = chapters.length > 0 ? Math.max(...chapters.map(c => c.chapterNumber || 0)) : 0;
      for (let i = 1; i <= targetCount; i++) {
        const chapterData = {
          title: `第${globalMax + i}章`,
          content: '',
          chapterNumber: globalMax + i,
          volumeId: newVolume._id,
          status: 'outline_only',
          outline: { points: [], coreEvent: '', plotPoints: [], connectionToPrevious: '', hookForNext: '' },
          targetWordCount: 3000,
          wordCount: 0,
          characterIds: [],
          foreshadowingIds: [],
        };
        const cresp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters`, chapterData);
        if (cresp.data?.success) {
          cresp.data.data.chapterNumber = globalMax + i;
          newChapters.push(cresp.data.data);
        }
      }

      setVolumes(prev => [...prev, newVolume]);
      setChapters(prev => [...prev, ...newChapters]);
      setSnackbar({ open: true, message: `「${newVolume.name}」创建成功`, severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: '卷创建失败：' + (e.response?.data?.message || e.message), severity: 'error' });
    }
    setVolumeDialogOpen(false);
    setEditingVolumeId(null);
    setVolumeFormData({ name: '', title: '', chapterCount: 10 });
  };

  // 删除卷
  const handleDeleteVolume = (volumeId) => {
    const newVolumes = volumes.filter(v => v._id !== volumeId);
    setVolumes(newVolumes);
    // 同时删除该卷下的章节
    const newChapters = chapters.filter(c => c.volumeId !== volumeId);
    setChapters(newChapters);
    setSnackbar({ open: true, message: '卷已删除', severity: 'info' });
  };

  // 切换卷展开状态（展开后将大卷标题栏滚入可视区，标题栏 sticky 吸顶）
  const toggleVolumeExpand = (volumeId) => {
    const prevVal = expandedVolumes[volumeId];
    const shouldExpand = prevVal === undefined ? false : !prevVal;
    setExpandedVolumes(prev => ({ ...prev, [volumeId]: shouldExpand }));
    if (shouldExpand) {
      setTimeout(() => {
        document.getElementById(`vol-header-${volumeId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 420);
    }
  };

  // ==================== 章纲编辑函数 ====================
  // 打开章纲编辑弹窗（chapter 为 null 且 volumeIdForNew 有值时为“新建章节”模式）
  const handleOpenChapterOutline = (chapter, volumeIdForNew = null) => {
    setEditingChapterOutline(chapter);
    setNewChapterVolumeId(volumeIdForNew || null);
    setNewChapterTitle('');
    setChapterOutlineDialogOpen(true);
  };

  // 在弹窗内执行“新建章节”创建（仅成功时关闭弹窗）
  const handleCreateChapterFromDialog = async () => {
    if (!newChapterVolumeId) {
      setSnackbar({ open: true, message: '未选择所属卷，请关闭后点击卷旁的「+」添加章节', severity: 'warning' });
      return;
    }
    const ok = await handleCreateChapter(newChapterVolumeId, newChapterTitle || undefined);
    if (ok) {
      setChapterOutlineDialogOpen(false);
      setNewChapterVolumeId(null);
      setNewChapterTitle('');
    }
  };

  // 保存章纲要点（调用后端API）
  const handleSaveChapterOutline = async () => {
    if (!editingChapterOutline) return;
    if (!assistNovelId) {
      setSnackbar({ open: true, message: '作品未加载完成，请稍后重试', severity: 'warning' });
      return;
    }
    try {
      await persistChapterTitle(editingChapterOutline);
      // 调用后端API保存章纲
      await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${editingChapterOutline._id}/outline`, {
        outline: editingChapterOutline.outline,
        status: editingChapterOutline.outline?.status || 'outline_only',
        characterIds: editingChapterOutline.characterIds || [],
        foreshadowingIds: editingChapterOutline.foreshadowingIds || [],
        itemIds: editingChapterOutline.itemIds || [],
      });

      // ========== 【数据联动】章节保存后自动更新相关状态 ==========
      const isAssistNovel = String(assistNovelId || '').startsWith('assist_');
      if (isAssistNovel) {
        // 1. 更新角色出场记录
        const charIds = editingChapterOutline.characterIds || [];
        if (charIds.length > 0) {
          for (const charId of charIds) {
            const char = characters.find(c => c._id === charId);
            if (char) {
              const currentAppearances = char.appearanceChapterIds || [];
              if (!currentAppearances.includes(editingChapterOutline._id)) {
                await apiClient.put(`/assist/novels/${assistNovelId}/characters/${charId}/state`, {
                  action: 'appearance',
                  chapterId: editingChapterOutline._id,
                  chapterNumber: editingChapterOutline.chapterNumber
                });
              }
            }
          }
        }
        // 2. 更新伏笔状态
        const fsIds = editingChapterOutline.foreshadowingIds || [];
        const outlinePoints = editingChapterOutline.outline?.plotPoints || [];
        if (fsIds.length > 0 || outlinePoints.length > 0) {
          for (const fsId of fsIds) {
            const fs = (outlines[0]?.foreshadowing || []).find(f => f.id === fsId || f._id === fsId);
            if (fs) {
              let newStatus = fs.status;
              let plantedChapterId = fs.plantedChapterId;
              let advanceChapterIds = [...(fs.advanceChapterIds || [])];
              // 判断伏笔状态
              if (!plantedChapterId) {
                newStatus = 'planted';
                plantedChapterId = editingChapterOutline._id;
              } else if (!advanceChapterIds.includes(editingChapterOutline._id)) {
                newStatus = 'advancing';
                advanceChapterIds.push(editingChapterOutline._id);
              }
              await apiClient.post(`/assist/novels/${assistNovelId}/foreshadowings/${fsId}/status`, {
                status: newStatus,
                plantedChapterId,
                advanceChapterIds,
                chapterId: editingChapterOutline._id,
                chapterNumber: editingChapterOutline.chapterNumber
              });
            }
          }
          // 从情节点中提取伏笔ID
          for (const pt of outlinePoints) {
            const ptFsIds = pt.foreshadowingIds || [];
            for (const fsId of ptFsIds) {
              if (!fsIds.includes(fsId)) {
                const fs = (outlines[0]?.foreshadowing || []).find(f => f.id === fsId || f._id === fsId);
                if (fs && !fs.plantedChapterId) {
                  await apiClient.post(`/assist/novels/${assistNovelId}/foreshadowings/${fsId}/status`, {
                    status: 'planted',
                    plantedChapterId: editingChapterOutline._id,
                    advanceChapterIds: [],
                    chapterId: editingChapterOutline._id,
                    chapterNumber: editingChapterOutline.chapterNumber
                  });
                }
              }
            }
          }
        }
        // 3. 更新支线章节关联
        const outlineForSubplot = editingChapterOutline.outline;
        const subplotIds = outlineForSubplot?.subplotIds || outlineForSubplot?.relatedSubplotIds || [];
        const currentOutline = outlines[0];
        let updatedSubplots = currentOutline?.subPlots ? [...currentOutline.subPlots] : [];
        if (currentOutline && subplotIds.length > 0) {
          updatedSubplots = updatedSubplots.map(sp => {
            if (subplotIds.includes(sp.id)) {
              const relatedChapterIds = [...(sp.relatedChapterIds || [])];
              if (!relatedChapterIds.includes(editingChapterOutline._id)) {
                relatedChapterIds.push(editingChapterOutline._id);
              }
              return { ...sp, relatedChapterIds, status: sp.status === 'pending' ? 'active' : sp.status };
            }
            return sp;
          });
          await apiClient.post(`/assist/novels/${assistNovelId}/outline`, {
            ...currentOutline,
            subPlots: updatedSubplots,
            updatedAt: new Date().toISOString()
          });
        }
        // 4. 如果章节标记为完成，同步更新章纲状态
        if (editingChapterOutline.outline?.status === 'completed' || editingChapterOutline.status === 'completed') {
          await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${editingChapterOutline._id}/outline`, {
            outline: { ...editingChapterOutline.outline, actualWordCount: editingChapterOutline.outline?.actualWordCount || 0 },
            status: 'completed'
          });
        }

        // ── 重新获取最新的大纲数据，同步到前端状态 ──
        // 目的：确保 fFSIds 和 subplotIds 的更新能反映到 progressStats
        if (assistNovelId) {
          try {
            const outlineResp = await apiClient.get(`/assist/novels/${assistNovelId}/outline`);
            if (outlineResp.data?.success) {
              setOutlines(outlineResp.data.data ? [outlineResp.data.data] : []);
            }
          } catch (e) {
            console.warn('同步大纲状态失败:', e);
          }
        }
      }

      // 更新本地状态
      const updatedChapters = chapters.map(c =>
        c._id === editingChapterOutline._id ? editingChapterOutline : c
      );
      setChapters(updatedChapters);
      setChapterOutlineDialogOpen(false);
      setSnackbar({ open: true, message: '章纲已保存，全局进度已同步更新', severity: 'success' });

    } catch (error) {
      console.error('保存章纲失败:', error);
      const msg = error.response?.data?.message || error.message || '保存章纲失败';
      setSnackbar({ open: true, message: `保存失败：${msg}`, severity: 'error' });
    }
  };

  /** 卷按 order 排序，与章节列表折叠面板一致，用于推算「第 N 卷对应全局第几章~第几章」 */
  const volumesSortedForAssist = useMemo(
    () => [...volumes].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [volumes]
  );

  const getVolumeSlotRange = (volume, volIdxInSorted) => {
    let prev = 0;
    for (let i = 0; i < volIdxInSorted; i++) {
      prev += volumesSortedForAssist[i]?.chapterCount || 0;
    }
    const cnt = volume.chapterCount || 0;
    return { start: prev + 1, end: prev + cnt };
  };

  /**
   * 卷标题「已有 / 计划」分子：优先 volumeId；无则按全局章节号落入本卷规划槽位；仍无则首卷用「未归类」章节数兜底（解决缺 volumeId、chapterNumber 为 0 时一直显示 0）
   */
  const getChapterCountForVolumePlan = (volume, volIdxInSorted) => {
    const matched = chapters.filter((c) => c.volumeId === volume._id).length;
    if (matched > 0) return matched;
    const { start, end } = getVolumeSlotRange(volume, volIdxInSorted);
    if (end >= start) {
      const byRange = chapters.filter((c) => {
        const n = Number(c.chapterNumber) || 0;
        return n >= start && n <= end;
      }).length;
      if (byRange > 0) return byRange;
    }
    if (volIdxInSorted === 0) {
      const orphans = chapters.filter(
        (c) => !c.volumeId || !volumes.some((v) => v._id === c.volumeId)
      );
      if (orphans.length > 0) {
        return Math.min(orphans.length, volume.chapterCount || orphans.length);
      }
    }
    return 0;
  };

  /**
   * 卷下列表章节（与 getChapterCountForVolumePlan 一致），避免标题写 5 章、列表却在「未归类」
   */
  const getChaptersForVolumeList = (volume, volIdxInSorted) => {
    const byVid = chapters.filter((c) => c.volumeId === volume._id);
    if (byVid.length > 0) {
      return [...byVid].sort((a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0));
    }
    const { start, end } = getVolumeSlotRange(volume, volIdxInSorted);
    if (end >= start) {
      const byRange = chapters.filter((c) => {
        const n = Number(c.chapterNumber) || 0;
        return n >= start && n <= end;
      });
      if (byRange.length > 0) {
        return [...byRange].sort((a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0));
      }
    }
    if (volIdxInSorted === 0) {
      const orphans = chapters.filter(
        (c) => !c.volumeId || !volumes.some((v) => v._id === c.volumeId)
      );
      if (orphans.length > 0) {
        const sorted = [...orphans].sort(
          (a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0)
        );
        const cap = volume.chapterCount || sorted.length;
        return sorted.slice(0, cap);
      }
    }
    return [];
  };

  /** 未被任何卷列表认领的章节（避免与首卷兜底重复显示） */
  const getUnclassifiedChapters = () => {
    const inAny = new Set();
    volumesSortedForAssist.forEach((vol, i) => {
      getChaptersForVolumeList(vol, i).forEach((c) => inAny.add(c._id));
    });
    return chapters
      .filter((c) => !inAny.has(c._id))
      .sort((a, b) => (Number(a.chapterNumber) || 0) - (Number(b.chapterNumber) || 0));
  };

  // 渲染章纲面板 - 完整版（卷-章-节三级结构）
  const renderOutlinePanel = () => (
    <Box className="panel-content" sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {/* 头部：标题 + 新建卷 + AI生成 + 搜索 */}
      <Box
        className="panel-header"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          mb: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" className="workspace-panel-title" sx={{ color: '#ffd700', flex: 1, textShadow: '0 0 10px rgba(255,215,0,0.5)', fontSize: { xs: 15, sm: 18 } }}>
          章纲库
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            onClick={() => { setAiGenerateType('volume'); setAiGenerateVolumeId(volumes[0]?._id || null); setAiGenerateCoreContent(''); setAiGenerateDialogOpen(true); }}
            sx={{
              color: 'var(--ws-accent)',
              border: '1px solid rgba(var(--ws-accent-rgb),0.4)',
              fontSize: 12,
              minHeight: { xs: 44, sm: 32 },
              '&:hover': { bgcolor: 'rgba(var(--ws-accent-rgb),0.1)' },
            }}
          >
            AI生成
          </Button>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setVolumeDialogOpen(true)}
            sx={{
              color: '#ffd700',
              border: '1px solid rgba(255,215,0,0.4)',
              fontSize: 12,
              minHeight: { xs: 44, sm: 32 },
              '&:hover': { bgcolor: 'rgba(255,215,0,0.1)' },
            }}
          >
            新建卷
          </Button>
        </Box>
      </Box>

      {/* 搜索框 + 筛选/排序（设计文档 2.1） */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="搜索章纲内容..."
          value={''}
          onChange={() => {}}
          sx={{
            '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
            fontSize: 13,
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }} /></InputAdornment>,
          }}
        />
      </Box>

      {/* 卷和章节列表：移动端加大触控区域 */}
      <Box sx={{ flex: 1, overflow: 'auto', pr: { xs: 0, sm: 0.5 } }}>
        {volumes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: 'rgba(255,255,255,0.5)' }}>
            <SummarizeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.3, color: '#ffd700' }} />
            <Typography sx={{ fontSize: 14, mb: 2 }}>暂无卷，点击上方按钮创建</Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.6 }}>建议：长篇小说按篇章/卷划分更好管理</Typography>
          </Box>
        ) : (
          volumesSortedForAssist.map((volume, volIdx) => (
            <Box key={volume._id} sx={{ mb: 2 }}>
              {/* 卷标题栏 */}
              <Box
                onClick={() => toggleVolumeExpand(volume._id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: { xs: 2, sm: 1.5 },
                  minHeight: { xs: 44, sm: 'auto' },
                  borderRadius: 2,
                  bgcolor: 'rgba(255,215,0,0.08)',
                  border: '1px solid rgba(255,215,0,0.2)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255,215,0,0.12)',
                    borderColor: 'rgba(255,215,0,0.4)',
                  },
                }}
              >
                <Box sx={{ color: '#ffd700', mr: 1 }}>
                  {expandedVolumes[volume._id] ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: '#ffd700', fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}>
                    {volume.name} {volume.title && `· ${volume.title}`}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    {getChapterCountForVolumePlan(volume, volIdx)} / {volume.chapterCount ?? 0} 章
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); setVolumeFormData({ name: volume.name, title: volume.title || '', chapterCount: volume.chapterCount || 10 }); setEditingVolumeId(volume._id); setVolumeDialogOpen(true); }}
                    sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 44, minHeight: 44, '&:hover': { color: '#00ff9f' } }}
                    title="编辑卷"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <Button
                    size="small"
                    variant="outlined"
                    className="assist-chapter-ai-glow-btn"
                    onClick={(e) => { e.stopPropagation(); setAiGenerateType('single'); setAiGenerateVolumeId(volume._id); setAiGenerateStartChapter(getChapterCountForVolumePlan(volume, volIdx) + 1); setAiGenerateCoreContent(''); setAiGenerateDialogOpen(true); }}
                    sx={{
                      fontSize: 11, minWidth: 0, px: 1, height: 28,
                    }}
                    title="AI生成章纲"
                  >
                    AI
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleOpenChapterOutline(null, volume._id); }}
                    sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 44, minHeight: 44, '&:hover': { color: '#ffd700' } }}
                    title="添加章节"
                  >
                    <AddIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleDeleteVolume(volume._id); }}
                    sx={{ color: 'rgba(255,255,255,0.5)', minWidth: 44, minHeight: 44, '&:hover': { color: '#ff4444' } }}
                    title="删除卷"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {/* 章节列表（展开时显示） */}
              {expandedVolumes[volume._id] && (
                <Box sx={{ ml: { xs: 1, sm: 2 }, mt: 1 }}>
                  {getChaptersForVolumeList(volume, volIdx).length === 0 ? (
                    <Box sx={{ p: 2, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                      该卷暂无章节
                    </Box>
                  ) : (
                    getChaptersForVolumeList(volume, volIdx).map((chapter, idx) => {
                      const targetWord = chapter.outline?.targetWordCount || 3000;
                      const actualWord = chapter.outline?.actualWordCount || 0;
                      const progress = targetWord > 0 ? Math.round(actualWord / targetWord * 100) : 0;
                      const created = chapter.createdAt ? new Date(chapter.createdAt).toLocaleDateString('zh-CN') : '-';
                      const updated = chapter.updatedAt ? new Date(chapter.updatedAt).toLocaleDateString('zh-CN') : '-';
                      // 从要点中提取标签（取前3个要点的关键词，如果有点点的话显示为标签）
                      const tags = (chapter.outline?.plotPoints || []).slice(0, 3).map(p => p.content?.substring(0, 4) || '要点').filter(Boolean);
                      const charCount = chapter.characterIds?.length || 0;
                      const fsCount = chapter.foreshadowingIds?.length || 0;
                      const hasWrittenBody = chapterHasWrittenBody(chapter);
                      const isChapterSelected = currentChapter?._id === chapter._id;
                      return (
                        <Box
                          key={chapter._id || idx}
                          onClick={() => handleOpenChapterOutline(chapter)}
                          sx={{
                            p: { xs: 2, sm: 1.5 },
                            mb: 1,
                            borderRadius: 2,
                            bgcolor: isChapterSelected
                              ? 'rgba(0,255,159,0.1)'
                              : hasWrittenBody
                                ? 'rgba(6,182,212,0.07)'
                                : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${
                              isChapterSelected
                                ? 'rgba(0,255,159,0.4)'
                                : hasWrittenBody
                                  ? 'rgba(6,182,212,0.45)'
                                  : 'rgba(255,255,255,0.08)'
                            }`,
                            boxShadow: hasWrittenBody && !isChapterSelected ? '0 0 12px rgba(6,182,212,0.12)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: isChapterSelected ? 'rgba(0,255,159,0.12)' : hasWrittenBody ? 'rgba(6,182,212,0.1)' : 'rgba(0,255,159,0.08)',
                              borderColor: isChapterSelected ? 'rgba(0,255,159,0.45)' : hasWrittenBody ? 'rgba(6,182,212,0.55)' : 'rgba(0,255,159,0.3)',
                            }
                          }}
                        >
                          {/* 第一行：第N章：标题 + 状态 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', mb: 0.5, gap: 1, minWidth: 0, textAlign: 'left' }}>
                            <Typography
                              sx={{
                                color: hasWrittenBody ? '#6ee7b7' : '#fff',
                                fontSize: 13,
                                flex: 1,
                                minWidth: 0,
                                fontWeight: hasWrittenBody ? 600 : 500,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textAlign: 'left',
                              }}
                              title={formatChapterColonTitle(chapter)}
                            >
                              {formatChapterColonTitle(chapter)}
                            </Typography>
                            <Chip
                              label={(chapter.outline?.status || chapter.status) === 'completed' ? 'V 完成' : (chapter.outline?.status || chapter.status) === 'in_progress' ? '> 进行' : '○ 待写'}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: 10,
                                bgcolor: (chapter.outline?.status || chapter.status) === 'completed' ? 'rgba(6,182,212,0.2)' : (chapter.outline?.status || chapter.status) === 'in_progress' ? 'rgba(245,158,11,0.2)' : 'rgba(99,102,241,0.15)',
                                color: (chapter.outline?.status || chapter.status) === 'completed' ? '#06b6d4' : (chapter.outline?.status || chapter.status) === 'in_progress' ? '#f59e0b' : '#818cf8',
                              }}
                            />
                          </Box>
                          {/* 第二行：字数进度 + 关联指示 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, flex: 'none' }}>
                              {actualWord} / {targetWord} 字
                            </Typography>
                            <Box sx={{ flex: 1, height: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1, overflow: 'hidden' }}>
                              <Box sx={{ height: '100%', width: `${Math.min(progress, 100)}%`, bgcolor: progress >= 100 ? '#06b6d4' : '#00f0ff', borderRadius: 1 }} />
                            </Box>
                            {charCount > 0 && (
                              <Chip label={`@${charCount}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(0,240,255,0.15)', color: '#67e8f9' }} />
                            )}
                            {fsCount > 0 && (
                              <Chip label={`&${fsCount}`} size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(236,72,153,0.15)', color: '#f9a8d4' }} />
                            )}
                          </Box>
                          {/* 第三行：要点标签 */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {tags.map((tag, i) => (
                                <Chip key={i} label={tag} size="small" sx={{ height: 16, fontSize: 9, bgcolor: 'rgba(var(--ws-violet-rgb),0.2)', color: 'var(--ws-accent)' }} />
                              ))}
                              {chapter.outline?.coreContent && (
                                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 9, alignSelf: 'center', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {chapter.outline.coreContent}
                                </Typography>
                              )}
                            </Box>
                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                              {updated !== '-' ? updated : ''}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                  {/* 添加章节按钮：移动端加大触控区域 */}
                  <Box
                    onClick={() => handleCreateChapter(volume._id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: { xs: 2, sm: 1 },
                      minHeight: { xs: 44, sm: 'auto' },
                      borderRadius: 2,
                      border: '1px dashed rgba(255,255,255,0.2)',
                      cursor: 'pointer',
                      color: 'rgba(255,255,255,0.5)',
                      fontSize: 12,
                      '&:hover': {
                        borderColor: '#00ff9f',
                        color: '#00ff9f',
                      },
                    }}
                  >
                    <AddIcon fontSize="small" sx={{ mr: 0.5 }} />
                    添加章节
                  </Box>
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>

      {/* 底部操作按钮：设计文档 2.1 - 从大纲生成/从模板创建 */}
      <Box sx={{ pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button
          size="small"
          startIcon={<span style={{ fontSize: 14 }}></span>}
          onClick={() => {
            if (volumes.length > 0) {
              setAiGenerateType('volume');
              setAiGenerateVolumeId(volumes[0]._id);
              setAiGenerateCoreContent(outlines[0]?.mainPlot?.description || '');
              setAiGenerateDialogOpen(true);
            } else {
              setSnackbar({ open: true, message: '请先创建卷', severity: 'warning' });
            }
          }}
          sx={{ color: '#ffd700', fontSize: 11, minHeight: 32, border: '1px solid rgba(255,215,0,0.3)', '&:hover': { bgcolor: 'rgba(255,215,0,0.1)' } }}
        >
          从大纲生成
        </Button>
        <Button
          size="small"
          startIcon={<span style={{ fontSize: 14 }}></span>}
          onClick={() => { setAiGenerateType('volume'); setAiGenerateVolumeId(volumes[0]?._id || null); setAiGenerateCoreContent(''); setAiGenerateDialogOpen(true); }}
          sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, minHeight: 32 }}
        >
          从模板创建
        </Button>
        <Button
          size="small"
          startIcon={<span style={{ fontSize: 14 }}>📥</span>}
          onClick={() => setSnackbar({ open: true, message: '导入功能即将上线', severity: 'info' })}
          sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, minHeight: 32 }}
        >
          导入
        </Button>
        <Button
          size="small"
          startIcon={<span style={{ fontSize: 14 }}>📤</span>}
          onClick={() => setSnackbar({ open: true, message: '导出功能即将上线', severity: 'info' })}
          sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, minHeight: 32 }}
        >
          导出
        </Button>
      </Box>

      {/* AI生成弹窗：重构版 - 4步流程 */}
      <Dialog
        open={aiGenerateDialogOpen}
        onClose={() => setAiGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          className: 'character-panel-cyber',
          sx: {
            bgcolor: 'rgba(10, 11, 18, 0.98)',
            backgroundImage: 'none',
            border: '1px solid rgba(255,215,0,0.3)',
            borderRadius: 2,
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 120px rgba(255,215,0,0.15)'
          }
        }}
      >
        <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffd700 !important', borderBottom: '1px solid rgba(255,215,0,0.2)', py: 1.5, flexShrink: 0 }}>
          AI 生成章纲
          <Typography sx={{ ml: 1, color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
            （角色/伏笔/支线请使用「章纲向导」）
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box sx={{ p: { xs: 1.5, sm: 2 }, overflow: 'auto', flex: 1, minHeight: 0, pb: 2 }}>


            {/* 第1步：选择生成范围 */}
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 0.5, color: '#ffd700 !important', fontSize: { xs: 13, sm: 14 } }}>
              1. 生成范围
            </Typography>
            <RadioGroup
              row
              value={aiGenerateType}
              onChange={(e) => {
                const v = e.target.value;
                setAiGenerateType(v);
                if (v === 'volume' && aiGenerateVolumeId) {
                  const vol = volumes.find(x => x._id === aiGenerateVolumeId);
                  if (vol?.chapterCount) setAiGenerateChapterCount(vol.chapterCount);
                }
                if (v === 'book') setAiGenerateChapterCount(80);
              }}
              sx={{ mb: 1 }}
            >
              <FormControlLabel value="single" control={<Radio sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }} />} label={<Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 } }}>单章</Typography>} />
              <FormControlLabel value="volume" control={<Radio sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }} />} label={<Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 } }}>按卷</Typography>} />
              <FormControlLabel value="book" control={<Radio sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }} />} label={<Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 } }}>全书</Typography>} />
            </RadioGroup>

            {/* 卷选择、起始章节等 */}
            {aiGenerateType !== 'book' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small" className="character-panel-select-wrap">
                  <InputLabel className="character-panel-field">所属卷</InputLabel>
                  <Select
                    value={aiGenerateVolumeId || ''}
                    onChange={(e) => {
                      const vid = e.target.value;
                      setAiGenerateVolumeId(vid);
                      if (aiGenerateType === 'volume') {
                        const vol = volumes.find(x => x._id === vid);
                        if (vol?.chapterCount) setAiGenerateChapterCount(vol.chapterCount);
                      }
                    }}
                    label="所属卷"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255,255,255,0.04)',
                        fontSize: { xs: 12, sm: 13 }
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.22)' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.28)' }
                    }}
                  >
                    {volumes.map(v => (
                      <MenuItem key={v._id} value={v._id} sx={{ fontSize: { xs: 12, sm: 13 } }}>{v.name} {v.title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  className="character-panel-field"
                  label="起始章节序号"
                  type="number"
                  value={aiGenerateStartChapter}
                  onChange={(e) => setAiGenerateStartChapter(parseInt(e.target.value, 10) || 1)}
                  helperText="本卷内第几章"
                  FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)', fontSize: 10 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.03)',
                      fontSize: { xs: 12, sm: 13 }
                    }
                  }}
                />
              </Box>
            )}

            {/* 全书模式的章数 */}
            {aiGenerateType === 'book' && (
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  className="character-panel-field"
                  type="number"
                  label="全书计划生成章数"
                  value={aiGenerateChapterCount}
                  onChange={(e) => setAiGenerateChapterCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  helperText="例如：80 表示生成 80 章章纲"
                  FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)', fontSize: 10 } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.03)',
                      fontSize: { xs: 12, sm: 13 }
                    }
                  }}
                />
                {volumes[0] && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, mt: 0.5 }}>生成结果将写入卷：{volumes[0].name}</Typography>
                )}
              </Box>
            )}

            {/* 按卷模式的章数 */}
            {aiGenerateType === 'volume' && (
              <TextField
                fullWidth
                size="small"
                className="character-panel-field"
                label="本卷要生成的章数"
                type="number"
                value={aiGenerateChapterCount}
                onChange={(e) => setAiGenerateChapterCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
                helperText="默认同步该卷「目标章节数」"
                FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.45)', fontSize: 10 } }}
                sx={{
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)',
                    fontSize: { xs: 12, sm: 13 }
                  }
                }}
              />
            )}

            {/* 第2步：上下文预览（自动加载，只读展示） */}
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, color: '#ffd700 !important', fontSize: { xs: 13, sm: 14 } }}>
              2. 上下文预览
            </Typography>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.1)' }}>
              {(() => {
                // 构建上下文预览数据
                const targetVolumeId = aiGenerateType === 'book' ? (volumes[0]?._id) : aiGenerateVolumeId;
                const targetVolume = volumes.find(v => v._id === targetVolumeId);
                const previousChapters = chapters
                  .filter(c => c.volumeId === targetVolumeId && c.chapterNumber < aiGenerateStartChapter)
                  .sort((a, b) => b.chapterNumber - a.chapterNumber)
                  .slice(0, 4)
                  .reverse();

                return (
                  <>
                    {/* 前几章摘要 */}
                    <Box sx={{ mb: 2 }}>
                <Typography sx={{ color: 'rgba(255,215,0,0.8)', fontSize: { xs: 12, sm: 13 }, fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  前几章摘要
                      </Typography>
                      {previousChapters.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {previousChapters.map((ch, idx) => (
                            <Box key={ch._id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, minWidth: 50 }}>
                                第{ch.chapterNumber}章
                              </Typography>
                              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: 11, sm: 12 }, flex: 1 }}>
                                {ch.title || '无标题'} {ch.outline?.coreContent ? `— ${ch.outline.coreContent.slice(0, 60)}${ch.outline.coreContent.length > 60 ? '…' : ''}` : ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontStyle: 'italic' }}>
                          暂无前章节记录
                        </Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography sx={{ color: 'rgba(255,215,0,0.8)', fontSize: { xs: 12, sm: 13 }, fontWeight: 600, mb: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        本卷目标
                      </Typography>
                      {targetVolume ? (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: 11, sm: 12 } }}>
                          {targetVolume.description?.slice(0, 100) || '暂无卷描述'}{targetVolume.description?.length > 100 ? '…' : ''}
                        </Typography>
                      ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontStyle: 'italic' }}>
                          暂无卷信息
                        </Typography>
                      )}
                    </Box>
                  </>
                );
              })()}
            </Box>

            {/* 第3步：写作要求 */}
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 0.5, color: '#ffd700 !important', fontSize: { xs: 13, sm: 14 } }}>
              3. 写作要求
            </Typography>
            <TextField
              fullWidth
              size="small"
              className="character-panel-field"
              multiline
              {...getDynamicTextAreaStyle(aiGenerateCoreContent, 1, 8)}
              value={aiGenerateCoreContent}
              onChange={(e) => setAiGenerateCoreContent(e.target.value)}
              placeholder="例如：本卷要写主角从废柴到一鸣惊人，节奏要快、多打脸……"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255,255,255,0.03)',
                  fontSize: { xs: 12, sm: 13 },
                  alignItems: 'flex-start'
                },
                '& textarea': { lineHeight: 1.5, padding: '10px 14px' },
                '@media (max-width: 599.95px)': {
                  '& textarea': { lineHeight: 1.4, padding: '6px 12px', fontSize: '14px', minHeight: 'auto !important' }
                }
              }}
            />

            {/* 详细程度与每章要点数 */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: { xs: 13, sm: 14 }, mb: 0.5 }}>详细程度</Typography>
                <RadioGroup row value={aiGenerateDetailLevel} onChange={(e) => setAiGenerateDetailLevel(e.target.value)}>
                  <FormControlLabel value="detailed" control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }} />} label={<Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 } }}>详细</Typography>} />
                  <FormControlLabel value="brief" control={<Radio size="small" sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }} />} label={<Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 } }}>简洁</Typography>} />
                </RadioGroup>
              </Box>
              <TextField
                fullWidth
                size="small"
                className="character-panel-field"
                label="每章要点数"
                type="number"
                value={aiGeneratePointsCount}
                onChange={(e) => setAiGeneratePointsCount(parseInt(e.target.value) || 5)}
                inputProps={{ min: 1, max: 10 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.03)',
                    fontSize: { xs: 12, sm: 13 }
                  }
                }}
              />
            </Box>

            {/* 第4步：引用设置 - 保留大纲主线引用 */}
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, color: '#ffd700 !important', fontSize: { xs: 13, sm: 14 } }}>
              4. 引用设置
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, mb: 1, fontStyle: 'italic' }}>
              角色、伏笔、支线请在「章纲向导」中设置
            </Typography>
            <Box sx={{ mb: 2 }}>
              {/* 大纲主线引用 */}
              {(() => {
                const o0 = outlines[0];
                const fullOutline = (o0?.content || '').trim() || (o0?.mainPlot?.storyline || '').trim();
                return (
                  <FormControlLabel
                    disabled={!fullOutline}
                    control={
                      <Checkbox
                        checked={aiGenerateReference.outlineFullText && !!fullOutline}
                        onChange={(e) => setAiGenerateReference({ ...aiGenerateReference, outlineFullText: e.target.checked })}
                        sx={{ color: 'rgba(255,255,255,0.35)', '&.Mui-checked': { color: '#ffd700' }, '& .MuiSvgIcon-root': { fontSize: 18 }, transition: 'all 0.2s' }}
                      />
                    }
                    label={<Typography sx={{ color: fullOutline ? '#fff' : 'rgba(255,255,255,0.4)', fontSize: { xs: 13, sm: 14 } }}>引用大纲主线</Typography>}
                    sx={{ mb: 1.5, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { mt: 0.2 } }}
                  />
                );
              })()}
            </Box>

            {/* 第5步：生成结果预览（设计文档 3.1.2） */}
            {aiGenerateStep === 'preview' && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,215,0,0.15)' }}>
                <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, color: '#ffd700 !important', fontSize: { xs: 13, sm: 14 } }}>
                  章纲预览
                </Typography>
                {aiGeneratedPreviews.length === 0 ? (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, textAlign: 'center', py: 4 }}>
                    暂无生成结果
                  </Typography>
                ) : (
                  aiGeneratedPreviews.map((ch, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ color: '#00ff9f', fontWeight: 700, fontSize: 14 }}>
                          第{ch.chapterNumber}章 {ch.title || '未命名'}
                        </Typography>
                        {(() => {
                          const totalCh = outlineFormData.chapterCount || 50;
                          const ratio = ch.chapterNumber / totalCh;
                          const p = ratio < 0.1 ? 'beginning' : ratio < 0.5 ? 'development' : ratio < 0.85 ? 'climax' : 'ending';
                          const labelMap = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
                          const colorMap = { beginning: '#38bdf8', development: '#3b82f6', climax: '#ef4444', ending: '#0ea5e9' };
                          return <Chip label={labelMap[p]} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: colorMap[p] + '22', color: colorMap[p], border: '1px solid ' + colorMap[p] + '44' }} />;
                        })()}
                      </Box>
                      {ch.outline?.coreEvent && (
                        <Box sx={{ mb: 1 }}>
                          <Typography sx={{ color: 'rgba(255,215,0,0.8)', fontSize: 11, fontWeight: 600, mb: 0.25 }}>核心事件</Typography>
                          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>{ch.outline.coreEvent}</Typography>
                        </Box>
                      )}
                      {ch.outline?.plotPoints?.length > 0 && (
                        <Box>
                          <Typography sx={{ color: 'rgba(var(--ws-accent-rgb),0.8)', fontSize: 11, fontWeight: 600, mb: 0.5 }}>情节点（{ch.outline.plotPoints.length}个）</Typography>
                          {ch.outline.plotPoints.slice(0, 5).map((pt, pi) => (
                            <Box key={pi} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'flex-start' }}>
                              <Typography sx={{ color: 'var(--ws-accent)', fontSize: 11, minWidth: 16 }}>{pi + 1}.</Typography>
                              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, flex: 1 }}>{pt.content}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {ch.outline?.connectionToPrevious && (
                        <Typography sx={{ color: 'rgba(0,240,255,0.6)', fontSize: 11, mt: 0.75 }}>← {ch.outline.connectionToPrevious}</Typography>
                      )}
                      {ch.outline?.hookForNext && (
                        <Typography sx={{ color: 'rgba(255,215,0,0.6)', fontSize: 11, mt: 0.5 }}>→ {ch.outline.hookForNext}</Typography>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'transparent', borderTop: '1px solid rgba(255,215,0,0.2)', flexWrap: 'wrap', flexShrink: 0, px: { xs: 2, sm: 3 }, py: 2 }}>
          {aiGenerateStep === 'preview' ? (
            <>
              <Button
                onClick={() => { setAiGenerateStep('form'); setAiGeneratedPreviews([]); }}
                className="workspace-btn-cyber"
                sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)', minHeight: 44, fontSize: { xs: 13, sm: 14 }, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' } }}
              >
                ← 重新生成
              </Button>
              <Button
                onClick={async () => {
                  if (aiGeneratedPreviews.length > 0) {
                    const totalChapters = outlineFormData.chapterCount || chapters.length || 50;
                    const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
                    const getPhaseByRatio = (chNum) => {
                      const ratio = chNum / totalChapters;
                      if (ratio < 0.1) return 'beginning';
                      if (ratio < 0.5) return 'development';
                      if (ratio < 0.85) return 'climax';
                      return 'ending';
                    };
                    // 批量写入章纲维度的进度（章节级阶段 + 主线阶段）
                    const generatedChapterStates = aiGeneratedPreviews.map(ch => ({
                      chapterNumber: ch.chapterNumber,
                      phase: getPhaseByRatio(ch.chapterNumber),
                      phaseLabel: phaseLabels[getPhaseByRatio(ch.chapterNumber)],
                      status: 'outline_only',
                      hasContent: false,
                      summary: ch.outline?.coreEvent || '',
                      outlineNote: ch.outline?.foreshadowingHandling || '',
                    }));
                    const lastPhase = getPhaseByRatio(aiGeneratedPreviews[aiGeneratedPreviews.length - 1].chapterNumber);
                    const existingChapterStates = progressData?.mainLineChapterStates || [];
                    const mergedChapterStates = [...existingChapterStates];
                    generatedChapterStates.forEach(gs => {
                      const idx = mergedChapterStates.findIndex(s => s.chapterNumber === gs.chapterNumber);
                      if (idx >= 0) mergedChapterStates[idx] = gs; else mergedChapterStates.push(gs);
                    });
                    mergedChapterStates.sort((a, b) => a.chapterNumber - b.chapterNumber);
                    setChapters(prev => [...prev, ...aiGeneratedPreviews]);
                    setAiGenerateDialogOpen(false);
                    setAiGenerateStep('form');
                    setAiGeneratedPreviews([]);
                    // ── 提取章纲中的伏笔/角色信息，写入全局进度追踪 ──────────────────
                    const extractedForeshadowingStates = [];
                    const extractedCharStates = [];
                    const existingFs = progressData?.foreshadowingChapterStates || [];
                    const existingChar = progressData?.characterChapterStates || [];
                    aiGeneratedPreviews.forEach(ch => {
                      // 从章纲中提取伏笔处理描述
                      if (ch.outline?.foreshadowingHandling) {
                        const fsEntry = {
                          chapterNumber: ch.chapterNumber,
                          foreshadowingTitle: ch.outline?.coreEvent || '',
                          status: 'advancing',
                          note: ch.outline.foreshadowingHandling.slice(0, 100),
                        };
                        const exists = existingFs.find(f => f.chapterNumber === ch.chapterNumber);
                        if (!exists) extractedForeshadowingStates.push(fsEntry);
                      }
                      // 从章纲中提取角色状态变化
                      if (ch.outline?.characterStateChanges) {
                        const charEntry = {
                          chapterNumber: ch.chapterNumber,
                          note: ch.outline.characterStateChanges.slice(0, 100),
                        };
                        const exists = existingChar.find(c => c.chapterNumber === ch.chapterNumber);
                        if (!exists) extractedCharStates.push(charEntry);
                      }
                    });
                    const mergedFs = [...existingFs, ...extractedForeshadowingStates].sort((a, b) => a.chapterNumber - b.chapterNumber);
                    const mergedChar = [...existingChar, ...extractedCharStates].sort((a, b) => a.chapterNumber - b.chapterNumber);
                    // 保存到后端
                    try {
                      await saveProgressData('mainLinePhase', lastPhase);
                      await saveProgressData('mainLineChapterStates', mergedChapterStates);
                      if (extractedForeshadowingStates.length > 0) await saveProgressData('foreshadowingChapterStates', mergedFs);
                      if (extractedCharStates.length > 0) await saveProgressData('characterChapterStates', mergedChar);
                    } catch (e) { console.warn('[章纲保存] 进度写入失败:', e); }
                    setSnackbar({ open: true, message: `已保存 ${aiGeneratedPreviews.length} 章章纲（${phaseLabels[lastPhase]}阶段），可前往章节管理查看`, severity: 'success' });
                  }
                }}
                sx={{ color: '#00ff9f', border: '1px solid #00ff9f', minHeight: 44, fontSize: { xs: 13, sm: 14 }, '&:hover': { bgcolor: 'rgba(0,255,159,0.1)' } }}
              >
                💾 保存章纲
              </Button>
              <Button
                onClick={() => { setAiGenerateDialogOpen(false); setAiGenerateStep('form'); setAiGeneratedPreviews([]); }}
                sx={{ color: 'rgba(255,255,255,0.5)', minHeight: 44, fontSize: { xs: 12, sm: 13 } }}
              >
                取消
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => setAiGenerateDialogOpen(false)}
                className="workspace-btn-cyber"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderColor: 'rgba(255,255,255,0.25)',
                  minHeight: 44,
                  fontSize: { xs: 13, sm: 14 },
                  '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' }
                }}
              >
                取消
              </Button>
            </>
          )}
          {aiGenerateStep === 'form' && (
            <>
              <Button
                onClick={async () => {
              const o0 = outlines[0];
              const fullOutline = (o0?.content || '').trim();
              const hasOutlineRef = aiGenerateReference.outlineFullText && fullOutline.length > 0;
              // 角色/伏笔/支线已移至章纲向导，此处只检查大纲引用和写作要求
              if (!aiGenerateCoreContent.trim() && !hasOutlineRef) {
                setSnackbar({ open: true, message: '请填写「写作要求」或勾选「引用大纲主线」', severity: 'warning' });
                return;
              }
              if (!assistNovelId) {
                setSnackbar({ open: true, message: '作品未加载完成，请从「工具」页重新进入工作台', severity: 'warning' });
                return;
              }
              if (!characters || characters.length === 0) {
                setSnackbar({ open: true, message: '角色库为空，请先去「角色库」创建角色后再生成章纲', severity: 'warning' });
                return;
              }
              setOutlineGenerating(true);
              setOutlineGeneratingProgress('准备生成章纲…');
              try {
                // ==================== 1. 获取章纲生成上下文 ====================
                const targetVolumeId = aiGenerateType === 'book' ? (volumes[0]?._id || null) : (aiGenerateVolumeId || null);
                if (!targetVolumeId) {
                  setSnackbar({ open: true, message: '请先选择目标卷', severity: 'warning' });
                  setOutlineGenerating(false);
                  return;
                }
                let contextData = null;
                try {
                  const ctxResp = await apiClient.get(`/assist/novels/${assistNovelId}/volumes/${targetVolumeId}/context/${aiGenerateStartChapter}`);
                  if (ctxResp.data?.success) contextData = ctxResp.data.data;
                } catch (e) {
                  console.warn('获取上下文失败，使用简化模式:', e);
                }

                // ==================== 2. 构建 AI 提示词 ====================
                const prevChapters = (contextData?.previousOutlines || []).slice(0, 4).reverse();
                const targetVol = contextData?.volume || volumes.find(v => v._id === targetVolumeId) || {};

                let promptParts = [];
                promptParts.push(`【小说基本信息】\n类型：${o0?.type || '仙侠'}  |  目标读者：${o0?.targetAudience || '全年龄'}  |  节奏：${o0?.rhythm || '快节奏'}`);
                if (o0?.worldView) promptParts.push(`【世界观】\n${o0.worldView}`);

                if (hasOutlineRef && fullOutline) {
                  promptParts.push(`【小说大纲】\n${fullOutline.slice(0, 800)}${fullOutline.length > 800 ? '…（已截断）' : ''}`);
                }
                if (o0?.mainPlot?.description) {
                  promptParts.push(`【主线】\n${o0.mainPlot.description}`);
                }
                if (targetVol.volumeGoal) promptParts.push(`【本卷目标】\n${targetVol.volumeGoal}`);
                if (targetVol.volumeConflict) promptParts.push(`【本卷冲突】\n${targetVol.volumeConflict}`);

                // ── 主线阶段 + 四阶段描述 + 下章要点（来自全局进度 progressData）────────
                {
                  const phase = progressData?.mainLinePhase || 'beginning';
                  const phaseLabel = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' }[phase] || '开局';
                  promptParts.push(`【主线阶段】${phaseLabel}（全局进度自动跟踪）`);
                  // 注入 storyStructure 四阶段，让章纲生成时就知道每阶段的大致走向
                  const storyStructure = outlines[0]?.storyStructure || {};
                  if (storyStructure.beginning || storyStructure.development || storyStructure.climax || storyStructure.ending) {
                    promptParts.push('【故事四阶段】（来自大纲解析，生成章纲时必须严格遵循）');
                    if (storyStructure.beginning) promptParts.push(`开局（第1~${Math.round((outlineFormData.chapterCount || 100) * 0.1)}章）：${storyStructure.beginning}`);
                    if (storyStructure.development) promptParts.push(`发展（第${Math.round((outlineFormData.chapterCount || 100) * 0.1 + 1)}~${Math.round((outlineFormData.chapterCount || 100) * 0.5)}章）：${storyStructure.development}`);
                    if (storyStructure.climax) promptParts.push(`高潮（第${Math.round((outlineFormData.chapterCount || 100) * 0.5 + 1)}~${Math.round((outlineFormData.chapterCount || 100) * 0.85)}章）：${storyStructure.climax}`);
                    if (storyStructure.ending) promptParts.push(`结局（第${Math.round((outlineFormData.chapterCount || 100) * 0.85 + 1)}~${outlineFormData.chapterCount || 100}章）：${storyStructure.ending}`);
                  }
                  // 注入上章末尾 AI 分析出的下章核心要点
                  const nextPts = progressData?.nextChapterCorePoints || [];
                  if (nextPts.length > 0) {
                    const latest = nextPts[nextPts.length - 1];
                    promptParts.push(`【上章提示·下章核心要点】第${latest.chapterNumber}章：${(latest.points || []).map(p => p.point).join('；')}`);
                    if (latest.connection) promptParts.push(`【上章提示·衔接方式】${latest.connection}`);
                  }
                }

                if (prevChapters.length > 0) {
                  promptParts.push(`【前${prevChapters.length}章摘要】`);
                  prevChapters.forEach(ch => {
                    promptParts.push(`第${ch.chapterNumber}章「${ch.title}」：${ch.coreEvent || ch.outline?.coreEvent || '无'}`);
                  });
                } else {
                  promptParts.push(`【前情】这是第 ${aiGenerateStartChapter} 章，暂无前章记录`);
                }

                // 提示：角色/伏笔/支线将在章纲向导中设置
                if (characters.length > 0) {
                  promptParts.push(`\n【角色列表】（${characters.length}个，详细设定见章纲向导）`);
                  characters.slice(0, 5).forEach(c => {
                    promptParts.push(`· ${c.name}`);
                  });
                  if (characters.length > 5) {
                    promptParts.push(`…等共${characters.length}个角色`);
                  }
                }
                // 注入伏笔/支线进度追踪，让章纲生成时就规划伏笔铺垫和揭示的节奏
                {
                  const fsStates = progressData?.foreshadowingChapterStates || [];
                  const spStates = progressData?.subplotChapterStates || [];
                  const totalCh = outlineFormData.chapterCount || chapters.length || 50;
                  if (fsStates.length > 0 || spStates.length > 0) {
                    promptParts.push('\n【全局伏笔/支线进度追踪】（来自正文生成后的AI分析，章纲中应规划好铺垫/揭示节奏）');
                    const recentFs = fsStates.slice(-8);
                    if (recentFs.length > 0) {
                      promptParts.push('【伏笔进度】');
                      recentFs.forEach(f => {
                        const statusMap = { planted: '已铺垫', revealed: '已揭示', advancing: '推进中', pending: '待铺垫', cancelled: '已取消' };
                        promptParts.push(`第${f.chapterNumber}章：${f.foreshadowingTitle || f.title || ''}（${statusMap[f.status] || f.status || ''}）${f.note || ''}`);
                      });
                    }
                    const recentSp = spStates.slice(-8);
                    if (recentSp.length > 0) {
                      promptParts.push('【支线进度】');
                      recentSp.forEach(s => {
                        const statusMap = { active: '进行中', completed: '已完成', pending: '待启动' };
                        promptParts.push(`第${s.chapterNumber}章：${s.subplotTitle || s.title || ''}（${statusMap[s.status] || s.status || ''}）${s.note || ''}`);
                      });
                    }
                  }
                  // 从大纲中的伏笔/支线中推算本章应铺垫/揭示的伏笔
                  const allFs = outlines[0]?.foreshadowing || [];
                  const plannedFss = allFs.filter(f => f.status !== 'revealed' && (f.plantedChapterNumber || f.revealChapterNumber));
                  const thisChFss = plannedFss.filter(f => f.plantedChapterNumber === aiGenerateStartChapter || f.revealChapterNumber === aiGenerateStartChapter);
                  if (thisChFss.length > 0) {
                    promptParts.push('\n【本章必须处理的伏笔】（来自大纲规划，章纲中必须包含）');
                    thisChFss.forEach(f => {
                      if (f.plantedChapterNumber === aiGenerateStartChapter) promptParts.push(`铺垫伏笔：${f.title}（${f.description || ''}）`);
                      if (f.revealChapterNumber === aiGenerateStartChapter) promptParts.push(`揭示伏笔：${f.title}（${f.description || ''}）`);
                    });
                  }
                }

                const genCount = aiGenerateType === 'single' ? 1 : aiGenerateChapterCount;
                promptParts.push(`\n【本章写作要求】\n${aiGenerateCoreContent.trim() || '无特殊要求，按正常节奏推进剧情'}`);
                promptParts.push(`\n详细程度：${aiGenerateDetailLevel === 'detailed' ? '详细（每个情节点100字描述）' : '简洁（每个情节点30字描述）'}`);
                promptParts.push(`每章 ${aiGeneratePointsCount} 个情节点`);
                promptParts.push(`本次共生成 ${genCount} 章`);

                const systemPrompt = `你是一位专业的小说创作助手，负责生成高质量的章节章纲。
你的任务是：根据完整的上下文信息，生成具有连续性、逻辑性、可读性的章节章纲。
核心原则：
1. 连续性优先：每章必须承接上文、引出下文
2. 伏笔管理：合理推进和回收伏笔
3. 角色成长：角色状态要有变化
4. 节奏把控：情节要有起伏
5. 逻辑自洽：情节发展要符合设定`;

                const userPrompt = promptParts.join('\n') + `\n\n请按以下JSON格式输出${genCount}章章纲（直接输出JSON，不要其他内容）：
{
  "chapters": [
    {
      "chapterNumber": 数字,
      "title": "章节标题",
      "coreEvent": "本章核心事件（1-2句话）",
      "plotPoints": [
        {
          "sequence": 数字,
          "content": "情节点详细描述",
          "type": "setup|conflict|climax|resolution|transition",
          "foreshadowingHint": "涉及哪些伏笔（可为空）"
        }
      ],
      "characterStateChanges": "本章角色状态变化",
      "foreshadowingHandling": "本章如何处理伏笔",
      "connectionToPrevious": "承接上章的内容",
      "hookForNext": "为下章埋下的钩子"
    }
  ]
}`;

                // ==================== 3. 调用 AI（🔥 真流式：实时看到生成进度） ====================
                let aiResponse = '';
                const currentNovelName = novel?.title || o0?.name || '未知项目';
                try {
                  const result = await handleStreamGeneration(
                    API_BASE_URL,
                    {
                      messages: [
                        { role: 'system', content: systemPrompt },
                        { role: 'user', content: userPrompt }
                      ],
                      model: 'deepseek-v4-flash',
                      extra_body: {},
                      temperature: 0.7,
                      max_tokens: Math.max(4000, aiGenerateChapterCount * 800),  // 🔥 动态：按章数缩放，每章 ~800 tokens，保底 4000
                      stream: true,                  // 🔥 真流式：后端 onChunk 实时转发，不再干等整段
                      billingInfo: {
                        novelName: currentNovelName,
                        chapterNumber: aiGenerateStartChapter,
                        operationType: 'AI生成章纲'
                      }
                    },
                    'AI生成章纲',
                    // 实时增量回调：JSON 累积中，更新进度提示（字数）让用户看到生成进行
                    (chunk, fullContent, currentWordCount) => {
                      setOutlineGeneratingProgress(`AI 章纲生成中…已生成 ${currentWordCount} 字`);
                    },
                    (statusMessage) => {
                      console.log('[AI生成章纲]', statusMessage);
                      if (statusMessage) setOutlineGeneratingProgress(statusMessage);
                    },
                    '/assist/ai/proxy-ai'
                  );
                  aiResponse = result.content || '';
                  if (!aiResponse) {
                    throw new Error('AI 返回空内容');
                  }
                } catch (aiErr) {
                  console.error('AI 调用失败:', aiErr);
                  setSnackbar({ open: true, message: 'AI 生成失败：' + (aiErr.response?.data?.message || aiErr.message), severity: 'error' });
                  setOutlineGenerating(false);
                  setOutlineGeneratingProgress('');
                  return;
                }

                // ==================== 4. 解析 AI 返回结果（🔥 robustJsonParse 容错） ====================
                let parsedChapters = [];
                const parseResult = robustJsonParse(aiResponse);
                if (parseResult.ok && parseResult.data) {
                  try {
                    const parsed = parseResult.data;
                    parsedChapters = parsed.chapters || [];
                  } catch (parseErr) {
                    console.error('解析章纲 JSON 失败:', parseErr);
                  }
                }

                if (parsedChapters.length === 0) {
                  setSnackbar({ open: true, message: 'AI 返回格式异常，已生成空白章纲', severity: 'warning' });
                  parsedChapters = [];
                }

                // ==================== 5. 创建章节 + 保存章纲 ====================
                const targetVolId = aiGenerateType === 'book' ? (volumes[0]?._id || null) : (aiGenerateVolumeId || null);
                const inVol = chapters.filter(c => c.volumeId === targetVolId);
                let baseNum = aiGenerateStartChapter;
                if (aiGenerateType === 'book' && targetVolId) {
                  const nums = inVol.map(c => c.chapterNumber || 0).filter(n => n > 0);
                  baseNum = nums.length ? Math.max(...nums) + 1 : aiGenerateStartChapter;
                }

                const newChapters = [];
                for (let i = 0; i < genCount; i++) {
                  const aiChapter = parsedChapters[i] || {};
                  const chapterNum = baseNum + i;
                  const chapterTitle = aiChapter.title || `第${chapterNum}章 章节${chapterNum}`;

                  // 先创建章节（角色/伏笔将在章纲向导中设置）
                  let createdChapter = null;
                  try {
                    const createResp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters`, {
                      title: chapterTitle,
                      content: '',
                      chapterNumber: chapterNum,
                      volumeId: targetVolId,
                      status: 'outline_only',
                      targetWordCount: 3000,
                    });
                    if (createResp.data?.success) {
                      createdChapter = createResp.data.data;
                    }
                  } catch (createErr) {
                    console.error('创建章节失败:', createErr);
                  }

                  // 构建章纲
                  const plotPoints = (aiChapter.plotPoints || []).map((pt, j) => ({
                    id: 'pt_' + Date.now() + '_' + i + '_' + j,
                    content: pt.content || `情节点${j + 1}`,
                    sequence: j + 1,
                    type: pt.type || 'conflict',
                    characterIds: [],
                    itemIds: [],
                    foreshadowingIds: [],
                  }));
                  if (plotPoints.length < aiGeneratePointsCount) {
                    for (let k = plotPoints.length; k < aiGeneratePointsCount; k++) {
                      plotPoints.push({
                        id: 'pt_' + Date.now() + '_' + i + '_' + k,
                        content: `情节点${k + 1}：待补充`,
                        sequence: k + 1,
                        type: 'conflict',
                        characterIds: [],
                        itemIds: [],
                        foreshadowingIds: [],
                      });
                    }
                  }

                  const outlineData = {
                    coreEvent: aiChapter.coreEvent || '',
                    plotPoints: plotPoints,
                    connectionToPrevious: aiChapter.connectionToPrevious || '',
                    hookForNext: aiChapter.hookForNext || '',
                    characterStateChanges: aiChapter.characterStateChanges || '',
                    foreshadowingHandling: aiChapter.foreshadowingHandling || '',
                  };

                  // 保存章纲到章节
                  if (createdChapter?._id) {
                    try {
                      await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${createdChapter._id}/outline`, {
                        outline: outlineData,
                        status: 'outline_only',
                      });
                    } catch (saveErr) {
                      console.error('保存章纲失败:', saveErr);
                    }
                  }

                  // 使用后端返回的章节数据（包含章纲）
                  const finalChapter = createdChapter ? {
                    ...createdChapter,
                    outline: {
                      ...outlineData,
                      targetWordCount: 3000,
                      actualWordCount: 0,
                    }
                  } : {
                    _id: 'ch_' + Date.now() + '_' + i,
                    novelId: assistNovelId,
                    volumeId: targetVolId,
                    chapterNumber: chapterNum,
                    title: chapterTitle,
                    outline: { ...outlineData, targetWordCount: 3000, actualWordCount: 0 },
                    status: 'outline_only',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  };
                  newChapters.push(finalChapter);
                }

                // ==================== 6. 先显示预览，等用户确认保存 ====================
                setAiGeneratedPreviews(newChapters);
                setAiGenerateStep('preview');
                setOutlineGenerating(false);
                setOutlineGeneratingProgress('');
              } catch (error) {
                console.error('生成章纲失败:', error);
                setSnackbar({ open: true, message: '生成失败：' + (error.response?.data?.message || error.message), severity: 'error' });
              } finally {
                setOutlineGenerating(false);
                setOutlineGeneratingProgress('');
              }
            }}
            disabled={outlineGenerating || globalProgressSyncing}
            className="workspace-btn-cyber"
            sx={{
              color: '#ffd700',
              borderColor: '#ffd700',
              minHeight: 44,
              fontSize: { xs: 13, sm: 14 },
              '&:hover': { borderColor: '#ffd700', bgcolor: 'rgba(255,215,0,0.1)', boxShadow: '0 0 12px rgba(255,215,0,0.3)' }
            }}
          >
            {outlineGenerating ? '生成中…' : '开始生成'}
          </Button>
            {outlineGenerating && outlineGeneratingProgress && (
              <Typography sx={{ color: '#00ff9f', fontSize: 12, mt: 1, textAlign: 'center', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%,100%': { opacity: 0.6 }, '50%': { opacity: 1 } } }}>
                {outlineGeneratingProgress}
              </Typography>
            )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* 章纲编辑弹窗 / 新建章节弹窗：移动端全屏 */}
      <Dialog
        open={chapterOutlineDialogOpen}
        onClose={() => { setChapterOutlineDialogOpen(false); setNewChapterVolumeId(null); setNewChapterTitle(''); }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { maxHeight: isMobile ? '100%' : '90vh' } }}
      >
        <DialogTitle sx={{ bgcolor: 'rgba(0,0,0,0.8)', color: '#00ff9f', borderBottom: '1px solid rgba(0,255,159,0.2)', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            {editingChapterOutline?.title ? `编辑章纲：${editingChapterOutline.title}` : '新建章节'}
          </Box>
          {outlineAutoSaved && (
            <Typography sx={{ color: '#22c55e', fontSize: 11, fontWeight: 600, animation: 'fadeIn 0.3s', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
              ✓ 已保存
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(0,0,0,0.9)', maxHeight: { xs: '85vh', sm: '70vh' }, overflow: 'auto', px: { xs: 2, sm: 3 } }}>

          {/* ── 数据联动说明（新加） ── */}
          <Box sx={{ mt: 1, mb: 2.5, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(0,255,159,0.06)', border: '1px solid rgba(0,255,159,0.2)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <Typography sx={{ color: '#00ff9f', fontSize: 11, fontWeight: 700 }}>保存本章纲将自动联动以下数据</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { label: '→ 全局进度', icon: '', desc: '章节状态自动更新', color: '#06b6d4' },
                { label: '→ 角色出场', icon: '', desc: '关联角色自动记录出场', color: '#00f0ff' },
                { label: '→ 伏笔状态', icon: '', desc: '标记伏笔推进/揭露', color: '#ec4899' },
                { label: '→ 支线进展', icon: '', desc: '关联支线自动激活', color: 'var(--ws-accent)' },
              ].map(item => (
                <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {item.icon && <Typography sx={{ fontSize: 10 }}>{item.icon}</Typography>}
                  <Typography sx={{ color: item.color, fontSize: 10, fontWeight: 600 }}>{item.label}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 9 }}>{item.desc}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* 新建章节模式：仅标题输入 */}
          {/* ── 上章预览（N+1 智能推荐来源） */}
          {(() => {
            const prevCh = chapters
              .filter(c => c.volumeId === editingChapterOutline?.volumeId && c.chapterNumber < editingChapterOutline?.chapterNumber)
              .sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
            if (!prevCh?.outline?.nextChapterPlan) return null;
            const ncp = prevCh.outline.nextChapterPlan;
            const recChars = (ncp.characterIds || []).map(id => characters.find(c => c._id === id)?.name).filter(Boolean);
            const recFs = (ncp.foreshadowingIds || []).map(id => outlines[0]?.foreshadowing?.find(f => (f.id || f._id) === id)?.title).filter(Boolean);
            const recSp = (ncp.subplotIds || []).map(id => outlines[0]?.subPlots?.find(s => (s.id || s._id) === id)?.title).filter(Boolean);
            if (!recChars.length && !recFs.length && !recSp.length && !ncp.autoSummary) return null;
            return (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, bgcolor: 'rgba(var(--ws-accent-rgb),0.06)', border: '1px solid rgba(var(--ws-accent-rgb),0.2)' }}>
                <Typography sx={{ color: 'var(--ws-accent)', fontSize: 11, fontWeight: 700, mb: 0.75 }}>
                  上章（第{prevCh.chapterNumber}章）推荐的本章内容
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: ncp.autoSummary ? 0.5 : 0 }}>
                  {recChars.map(n => (
                    <Chip key={n} label={`@ ${n}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(0,240,255,0.12)', color: '#67e8f9' }} />
                  ))}
                  {recFs.map(t => (
                    <Chip key={t} label={`& ${t}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(236,72,153,0.12)', color: '#f9a8d4' }} />
                  ))}
                  {recSp.map(t => (
                    <Chip key={t} label={`* ${t}`} size="small" sx={{ height: 20, fontSize: 10, bgcolor: 'rgba(var(--ws-accent-rgb),0.12)', color: 'var(--ws-accent-muted)' }} />
                  ))}
                </Box>
                {ncp.autoSummary && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 10, mt: 0.5 }}>
                    @ {ncp.autoSummary}
                  </Typography>
                )}
              </Box>
            );
          })()}
          {newChapterVolumeId && !editingChapterOutline && (
            <Box sx={{ py: { xs: 1, sm: 2 } }}>
              <TextField
                fullWidth
                autoFocus
                label="章节标题"
                value={newChapterTitle}
                onChange={(e) => setNewChapterTitle(e.target.value)}
                placeholder="如：第一章 开端"
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
              />
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>创建后将出现在当前卷下，可在列表中点击该章节继续编辑章纲。</Typography>
            </Box>
          )}
          {editingChapterOutline && (
            <Box>
              {/* 章节基本信息：序号由系统自动填写，只读 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#00ff9f', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(0,255,159,0.5)' }}>章节信息</Typography>
                <TextField
                  fullWidth
                  label="章节标题"
                  value={editingChapterOutline.title || ''}
                  onChange={(e) => setEditingChapterOutline(prev => prev ? { ...prev, title: e.target.value } : prev)}
                  onBlur={(e) => { void persistChapterTitle({ ...editingChapterOutline, title: e.target.value }); }}
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    fullWidth
                    label="章节序号"
                    type="number"
                    value={editingChapterOutline.chapterNumber ?? 1}
                    InputProps={{ readOnly: true }}
                    helperText="由系统根据卷内顺序自动填写"
                    sx={{ '& .MuiOutlinedInput-root': { color: 'rgba(255,255,255,0.8)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }, '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.4)' } }}
                  />
                  <TextField
                    fullWidth
                    label="目标字数"
                    type="number"
                    value={editingChapterOutline.outline?.targetWordCount || 3000}
                    onChange={(e) => setEditingChapterOutline({
                      ...editingChapterOutline,
                      outline: { ...editingChapterOutline.outline, targetWordCount: parseInt(e.target.value) || 3000 }
                    })}
                    sx={{ '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                  />
                </Box>
              </Box>

              {/* 本章核心内容：可手写，也可作为 AI 生成要点的输入 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#00ff9f', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(0,255,159,0.5)' }}>本章核心内容</Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  value={editingChapterOutline.outline?.coreContent ?? ''}
                  onChange={(e) => setEditingChapterOutline({
                    ...editingChapterOutline,
                    outline: { ...editingChapterOutline.outline, coreContent: e.target.value }
                  })}
                  placeholder="请描述本章要写什么，如：主角穿越到异世界，觉醒逆天血脉… 可手写，也可填写后点击下方「AI 生成要点」自动生成本章要点。"
                  sx={{ mb: 1, '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <Button
                  size="small"
                  onClick={() => { setAiGenerateType('volume'); setAiGenerateVolumeId(volumes[0]?._id || null); setAiGenerateCoreContent(''); setAiGenerateDialogOpen(true); }}
                  sx={{ color: '#38bdf8', border: '1px solid rgba(56,189,248,0.4)', '&:hover': { bgcolor: 'rgba(56,189,248,0.08)' } }}
                >
                  AI 生成要点
                </Button>
              </Box>

              {/* 核心事件 / 承接上文 / 为下章埋钩 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#ffd700', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(255,215,0,0.4)' }}>核心事件 · 承接 · 埋钩</Typography>
                <TextField
                  fullWidth multiline minRows={2}
                  label="核心事件（本章最重要的情节）"
                  value={editingChapterOutline.outline?.coreEvent || ''}
                  onChange={(e) => setEditingChapterOutline(prev => prev ? {
                    ...prev,
                    outline: { ...prev.outline, coreEvent: e.target.value }
                  } : prev)}
                  onBlur={() => { clearTimeout(saveOutlineTimerRef.current); autoSaveOutlineFields(editingChapterOutline); }}
                  placeholder="一句话概括本章最重要要写的情节…"
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)', alignItems: 'flex-start' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <TextField
                  fullWidth multiline minRows={2}
                  label="承接上文（如何衔接上一章结尾）"
                  value={editingChapterOutline.outline?.connectionToPrevious || ''}
                  onChange={(e) => setEditingChapterOutline(prev => prev ? {
                    ...prev,
                    outline: { ...prev.outline, connectionToPrevious: e.target.value }
                  } : prev)}
                  onBlur={() => { clearTimeout(saveOutlineTimerRef.current); autoSaveOutlineFields(editingChapterOutline); }}
                  placeholder="本章如何承接上一章结尾的内容、场景或情绪…"
                  sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)', alignItems: 'flex-start' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
                <TextField
                  fullWidth multiline minRows={2}
                  label="为下章埋钩（本章结尾如何引出下一章）"
                  value={editingChapterOutline.outline?.hookForNext || ''}
                  onChange={(e) => setEditingChapterOutline(prev => prev ? {
                    ...prev,
                    outline: { ...prev.outline, hookForNext: e.target.value }
                  } : prev)}
                  onBlur={() => { clearTimeout(saveOutlineTimerRef.current); autoSaveOutlineFields(editingChapterOutline); }}
                  placeholder="本章结尾如何制造悬念/转折/冲突，引出下一章…"
                  sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)', alignItems: 'flex-start' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
                />
              </Box>

              {/* 本章要点：可手写添加，也可由上方「AI 生成要点」生成 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: 'var(--ws-accent)', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(var(--ws-accent-rgb),0.5)' }}>本章要点</Typography>
                {(editingChapterOutline.outline?.plotPoints || []).map((point, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={point.content || ''}
                      onChange={(e) => {
                        const newPoints = [...(editingChapterOutline.outline?.plotPoints || [])];
                        newPoints[idx] = { ...newPoints[idx], content: e.target.value };
                        setEditingChapterOutline(prev => prev ? { ...prev, outline: { ...prev.outline, plotPoints: newPoints } } : prev);
                      }}
                      placeholder={`要点 ${idx + 1}`}
                      sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(255,255,255,0.03)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        const newPoints = (editingChapterOutline.outline?.plotPoints || []).filter((_, i) => i !== idx);
                        setEditingChapterOutline(prev => prev ? { ...prev, outline: { ...prev.outline, plotPoints: newPoints } } : prev);
                      }}
                      sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff4444' } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setEditingChapterOutline(prev => {
                      if (!prev) return prev;
                      const newPoints = [...(prev.outline?.plotPoints || []), { id: 'pt_' + Date.now(), content: '', sortOrder: (prev.outline?.plotPoints?.length || 0) + 1 }];
                      return { ...prev, outline: { ...prev.outline, plotPoints: newPoints } };
                    });
                  }}
                  sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}
                >
                  添加要点
                </Button>
              </Box>

              {/* 字数统计 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#00f0ff', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(0,240,255,0.5)' }}>字数进度</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>当前字数</Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={editingChapterOutline.outline?.actualWordCount || 0}
                      onChange={(e) => setEditingChapterOutline({
                        ...editingChapterOutline,
                        outline: { ...editingChapterOutline.outline, actualWordCount: parseInt(e.target.value) || 0 }
                      })}
                      sx={{ '& .MuiOutlinedInput-root': { color: '#fff' } }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>目标字数</Typography>
                    <Typography sx={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>{editingChapterOutline.outline?.targetWordCount || 3000}</Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>完成度</Typography>
                    <Typography sx={{ color: '#06b6d4', fontSize: 16, fontWeight: 600 }}>
                      {editingChapterOutline.outline?.targetWordCount ? Math.round((editingChapterOutline.outline?.actualWordCount || 0) / editingChapterOutline.outline?.targetWordCount * 100) : 0}%
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* 状态选择 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: '#f59e0b', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(245,158,11,0.5)' }}>章节状态</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {['outline_only', 'in_progress', 'completed'].map(status => (
                    <Chip
                      key={status}
                      label={status === 'outline_only' ? '待开始' : status === 'in_progress' ? '进行中' : '已完成'}
                      onClick={() => setEditingChapterOutline({
                        ...editingChapterOutline,
                        outline: { ...editingChapterOutline.outline, status }
                      })}
                      sx={{
                        bgcolor: (editingChapterOutline.outline?.status || 'outline_only') === status ? 'rgba(245,158,11,0.3)' : 'rgba(255,255,255,0.1)',
                        color: (editingChapterOutline.outline?.status || 'outline_only') === status ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                        border: `1px solid ${(editingChapterOutline.outline?.status || 'outline_only') === status ? '#f59e0b' : 'rgba(255,255,255,0.2)'}`,
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* AI 生成建议：基于大纲建议爽点 */}
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ color: 'var(--ws-accent)', fontSize: 12, mb: 1, textShadow: '0 0 8px rgba(var(--ws-accent-rgb),0.5)' }}>AI 生成建议</Typography>
                <Box sx={{ p: 2, bgcolor: 'rgba(var(--ws-accent-rgb),0.05)', borderRadius: 2, border: '1px solid rgba(var(--ws-accent-rgb),0.2)' }}>
                  {outlines.length > 0 ? (
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 1 }}>
                        基于当前大纲，建议本章增加以下爽点：
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {[
                          { type: '打脸', content: '打脸情节：族人轻视→震惊→反差', icon: '' },
                          { type: '升级', content: '升级暗示：混沌体觉醒，潜力无穷', icon: '' },
                          { type: '伏笔', content: '伏笔铺垫：玉佩发光，为后续争夺埋下线索', icon: '' },
                        ].map((suggestion, idx) => (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
                            {suggestion.icon && <Typography sx={{ fontSize: 12 }}>{suggestion.icon}</Typography>}
                            <Typography sx={{ color: '#fff', fontSize: 12 }}>{suggestion.content}</Typography>
                            <Button
                              size="small"
                              onClick={() => {
                                setEditingChapterOutline(prev => {
                                  if (!prev) return prev;
                                  const newPoints = [...(prev.outline?.plotPoints || []), { id: 'pt_' + Date.now() + '_' + idx, content: suggestion.content, sortOrder: (prev.outline?.plotPoints?.length || 0) + 1, characterIds: [], itemIds: [], foreshadowingIds: [] }];
                                  return { ...prev, outline: { ...prev.outline, plotPoints: newPoints } };
                                });
                                setSnackbar({ open: true, message: `已添加"${suggestion.type}"建议到要点`, severity: 'success' });
                              }}
                              sx={{ ml: 'auto', color: 'var(--ws-accent)', fontSize: 10, minHeight: 24 }}
                            >
                              采纳
                            </Button>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>暂无大纲，无法生成建议</Typography>
                  )}
                </Box>
              </Box>

              {/* ──── 四大关联卡片 ──── */}

              {/* 本章出场角色 */}
              <Box sx={{ mb: 2, p: 1.75, bgcolor: 'rgba(236,72,153,0.04)', borderRadius: 2, border: '1px solid rgba(236,72,153,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ec4899', flexShrink: 0 }} />
                    <Typography sx={{ color: '#ec4899', fontSize: 13, fontWeight: 600 }}>本章出场角色</Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setSelectCharDialogOpen(true)}
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, minHeight: 28 }}
                  >
                    添加角色
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(editingChapterOutline.characterIds || []).length > 0 ? (
                    (editingChapterOutline.characterIds || []).map((charId) => {
                      const char = characters.find(c => c._id === charId);
                      if (!char) return null;
                      const roleLb = getCharacterDisplayRoleLabel(char);
                      const rv = getCharacterRoleVisual(roleLb);
                      return (
                        <Tooltip key={charId} title={buildCharacterChapterOutlineTooltip(char)} {...chapterOutlineCharTooltipProps}>
                          <Chip
                            label={(
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 220 }}>
                                <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.4, py: 0.1, borderRadius: 0.5, flexShrink: 0, lineHeight: 1.2, bgcolor: rv.bg, color: rv.color, border: rv.border }}>
                                  {roleLb}
                                </Box>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{getCharacterChapterOutlineDisplayName(char)}</span>
                              </Box>
                            )}
                            size="small"
                            onDelete={() => {
                              setEditingChapterOutline(prev => {
                                if (!prev) return prev;
                                const newIds = (prev.characterIds || []).filter(id => id !== charId);
                                return { ...prev, characterIds: newIds };
                              });
                            }}
                            sx={{ bgcolor: 'rgba(236,72,153,0.16)', color: '#fce7f3', border: '1px solid rgba(236,72,153,0.38)', maxWidth: 280, '& .MuiChip-label': { px: 0.75, display: 'block' } }}
                          />
                        </Tooltip>
                      );
                    })
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无出场角色</Typography>
                  )}
                </Box>
              </Box>

              {/* 🔮 关联伏笔 */}
              <Box sx={{ mb: 2, p: 1.75, bgcolor: 'rgba(236,72,153,0.04)', borderRadius: 2, border: '1px solid rgba(236,72,153,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: 13 }}>🔮</Typography>
                    <Typography sx={{ color: '#f9a8d4', fontSize: 13, fontWeight: 600 }}>关联伏笔</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(outlines[0]?.foreshadowing || []).length > 0 ? (
                    (outlines[0].foreshadowing || []).map(fs => {
                      const currentIds = editingChapterOutline.foreshadowingIds || [];
                      const isSelected = currentIds.includes(fs.id);
                      return (
                        <Chip
                          key={fs.id}
                          label={typeof fs.title === 'string' ? fs.title : (fs.description?.slice(0, 12) || '未命名')}
                          size="small"
                          onClick={() => {
                            setEditingChapterOutline(prev => {
                              if (!prev) return prev;
                              const prevIds = prev.foreshadowingIds || [];
                              const isSelected = prevIds.includes(fs.id);
                              const newIds = isSelected
                                ? prevIds.filter(id => id !== fs.id)
                                : [...prevIds, fs.id];
                              return { ...prev, foreshadowingIds: newIds };
                            });
                          }}
                          sx={{
                            bgcolor: isSelected ? 'rgba(236,72,153,0.25)' : 'rgba(255,255,255,0.05)',
                            color: isSelected ? '#f9a8d4' : 'rgba(255,255,255,0.5)',
                            border: `1px solid ${isSelected ? 'rgba(236,72,153,0.5)' : 'rgba(255,255,255,0.1)'}`,
                            cursor: 'pointer',
                            fontSize: 11,
                            height: 26,
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无伏笔（请先在大纲面板创建）</Typography>
                  )}
                </Box>
              </Box>

              {/* 关联支线 */}
              <Box sx={{ mb: 2, p: 1.75, bgcolor: 'rgba(var(--ws-accent-rgb),0.04)', borderRadius: 2, border: '1px solid rgba(var(--ws-accent-rgb),0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ color: 'var(--ws-accent-muted)', fontSize: 13, fontWeight: 600 }}>关联支线</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(outlines[0]?.subPlots || []).length > 0 ? (
                    (outlines[0].subPlots || []).map(sp => {
                      const currentIds = editingChapterOutline.outline?.subplotIds || [];
                      const isSelected = currentIds.includes(sp.id);
                      return (
                        <Chip
                          key={sp.id}
                          label={typeof sp.title === 'string' ? sp.title : (sp.description?.slice(0, 12) || '未命名')}
                          size="small"
                          onClick={() => {
                            const newIds = isSelected
                              ? currentIds.filter(id => id !== sp.id)
                              : [...currentIds, sp.id];
                            setEditingChapterOutline({
                              ...editingChapterOutline,
                              outline: { ...editingChapterOutline.outline, subplotIds: newIds }
                            });
                          }}
                          sx={{
                            bgcolor: isSelected ? 'rgba(var(--ws-accent-rgb),0.25)' : 'rgba(255,255,255,0.05)',
                            color: isSelected ? 'var(--ws-accent-muted)' : 'rgba(255,255,255,0.5)',
                            border: `1px solid ${isSelected ? 'rgba(var(--ws-accent-rgb),0.5)' : 'rgba(255,255,255,0.1)'}`,
                            cursor: 'pointer',
                            fontSize: 11,
                            height: 26,
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无支线（请先在大纲面板创建）</Typography>
                  )}
                </Box>
              </Box>

              {/* 📦 涉及词条 */}
              <Box sx={{ mb: 2, p: 1.75, bgcolor: 'rgba(255,107,53,0.04)', borderRadius: 2, border: '1px solid rgba(255,107,53,0.2)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ff6b35', flexShrink: 0 }} />
                    <Typography sx={{ color: '#ff6b35', fontSize: 13, fontWeight: 600 }}>涉及词条</Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setSelectItemDialogOpen(true)}
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, minHeight: 28 }}
                  >
                    添加词条
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {(editingChapterOutline.itemIds || []).length > 0 ? (
                    (editingChapterOutline.itemIds || []).map((itemId, idx) => {
                      const item = items.find(i => i._id === itemId);
                      return item ? (
                        <Chip
                          key={idx}
                          label={item.name}
                          size="small"
                          onDelete={() => {
                            setEditingChapterOutline(prev => {
                              if (!prev) return prev;
                              const newIds = (prev.itemIds || []).filter(id => id !== itemId);
                              return { ...prev, itemIds: newIds };
                            });
                          }}
                          sx={{ bgcolor: 'rgba(255,107,53,0.2)', color: '#ff6b35', border: '1px solid rgba(255,107,53,0.4)' }}
                        />
                      ) : null;
                    })
                  ) : (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无涉及词条</Typography>
                  )}
                </Box>
              </Box>

              {/* 章内节划分（可选） */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ color: 'var(--ws-violet)', fontSize: 12, textShadow: '0 0 8px rgba(var(--ws-violet-rgb),0.5)' }}>章内节划分（可选）</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingChapterOutline(prev => {
                        if (!prev) return prev;
                        const newSections = [...(prev.sections || []), { id: 'sec_' + Date.now(), title: '', sortOrder: (prev.sections?.length || 0) + 1 }];
                        return { ...prev, sections: newSections };
                      });
                    }}
                    sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, minHeight: 28 }}
                  >
                    添加节
                  </Button>
                </Box>
                {(editingChapterOutline.sections || []).length > 0 ? (
                  <Box>
                    {(editingChapterOutline.sections || []).map((section, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          value={section.title || ''}
                          onChange={(e) => {
                            setEditingChapterOutline(prev => {
                              if (!prev) return prev;
                              const newSections = [...(prev.sections || [])];
                              newSections[idx] = { ...newSections[idx], title: e.target.value };
                              return { ...prev, sections: newSections };
                            });
                          }}
                          placeholder={`第${idx + 1}节`}
                          sx={{ '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(var(--ws-violet-rgb),0.05)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingChapterOutline(prev => {
                              if (!prev) return prev;
                              const newSections = (prev.sections || []).filter((_, i) => i !== idx);
                              return { ...prev, sections: newSections };
                            });
                          }}
                          sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff4444' } }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>暂无节划分（如需要可添加）</Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)', borderTop: '1px solid rgba(0,255,159,0.2)', flexWrap: 'wrap', px: { xs: 2, sm: 3 } }}>
          <Button onClick={() => { setChapterOutlineDialogOpen(false); setNewChapterVolumeId(null); setNewChapterTitle(''); }} sx={{ color: 'rgba(255,255,255,0.7)', minHeight: 44 }}>取消</Button>
          {newChapterVolumeId && !editingChapterOutline ? (
            <Button onClick={handleCreateChapterFromDialog} sx={{ color: '#00ff9f', border: '1px solid #00ff9f', minHeight: 44 }}>创建</Button>
          ) : (
            <Button onClick={handleSaveChapterOutline} sx={{ color: '#00ff9f', border: '1px solid #00ff9f', minHeight: 44 }}>💾 保存</Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );

  // 获取当前唯一大纲的正文内容（兼容 content / mainPlot.description / mainPlot.storyline）
  const getCurrentOutlineContent = () => {
    const o = outlines.length > 0 ? outlines[0] : null;
    if (!o) return '';
    return o.content || o.mainPlot?.description || o.mainPlot?.storyline || '';
  };

  // 【辅助创作】统一的字段级 PATCH 保存（仅在 onBlur / 按钮点击时调用）
  const saveOutlinePatch = async (patch) => {
    if (!assistNovelId || creationType !== 'assist') return null;
    try {
      const resp = await apiClient.post(`/assist/novels/${assistNovelId}/outline`, { patch });
      const saved = resp?.data?.data;
      if (saved) {
        console.log('[大纲PATCH保存] 返回数据检查:', {
          hasMainPlot: !!saved.mainPlot,
          mainPlot: saved.mainPlot,
          subPlotsLength: saved.subPlots?.length || 0,
          foreshadowingLength: saved.foreshadowing?.length || 0,
        });
        setOutlines([saved]);
      }
      return saved;
    } catch (err) {
      console.error('【辅助创作】PATCH保存失败:', err);
      return null;
    }
  };

  // 【辅助创作】大纲内容变化时更新状态（不自动保存；失焦时 PATCH 保存）
  const updateCurrentOutlineContent = (content) => {
    if (outlines.length === 0) {
      // 如果没有大纲，先创建一个新的（使用正确的 _id 格式）
      const newOutline = {
        _id: 'outline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9),
        name: '新大纲',
        content: content,
        mainPlot: { storyline: '', description: '' },
        subPlots: [],
        foreshadowing: [],
        updatedAt: new Date().toISOString()
      };
      setOutlines([newOutline]);
      return;
    }
    const prev = outlines[0];
    const updatedAt = new Date().toISOString();
    let name = prev.name;
    if (!name || !name.trim() || name === '未命名大纲') {
      const parsed = extractBookNameFromContent(content);
      if (parsed) name = parsed;
      else {
        const nt = (novel?.title || '').trim();
        if (nt) name = nt;
      }
    }
    // 【修复】确保更新时保留正确的 _id 格式
    let outlineId = prev._id;
    if (!outlineId || outlineId.startsWith('outline_new_')) {
      outlineId = 'outline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }
    setOutlines([{ ...prev, _id: outlineId, content, name: name || prev.name, updatedAt }]);
  };

  // 渲染大纲列表界面（单一大纲：蓝色框内直接显示并可编辑，失焦自动保存）
  const renderOutlineMainPanel = () => {
    const currentOutline = outlines.length > 0 ? outlines[0] : null;
    // 全书大纲正文只读写在 content：不用主线概括回填，避免解析后大编辑框只剩「一句话」
    const outlineContent = currentOutline ? String(currentOutline.content ?? '') : '';
    /** 结构化信息区：主线琥珀 / 支线青蓝(var) / 伏笔暖橙 — 与辅助创作赛博风一致，不用紫粉 */
    const osMain = {
      rail: '#f59e0b',
      chipBg: 'rgba(245, 158, 11, 0.22)',
      chipColor: '#fde68a',
      chipBorder: 'rgba(251, 191, 36, 0.5)',
      field: 'rgba(251, 191, 36, 0.35)',
      fieldHi: 'rgba(251, 191, 36, 0.55)',
      fieldFocus: '#fbbf24',
      fieldGlow: '0 0 12px rgba(251, 191, 36, 0.22)',
    };
    const osSub = {
      rail: 'var(--ws-accent)',
      chipBg: 'rgba(var(--ws-accent-rgb), 0.16)',
      chipColor: 'var(--ws-accent)',
      chipBorder: 'rgba(var(--ws-accent-rgb), 0.45)',
      cardBg: 'rgba(var(--ws-accent-rgb), 0.07)',
      cardBorder: 'rgba(var(--ws-accent-rgb), 0.28)',
      cardGlow: '0 0 18px rgba(var(--ws-accent-rgb), 0.12)',
      idxBg: 'rgba(var(--ws-accent-rgb), 0.35)',
      idxColor: '#ecfeff',
      field: 'rgba(var(--ws-accent-rgb), 0.4)',
      fieldHi: 'rgba(var(--ws-accent-rgb), 0.62)',
      fieldFocus: 'var(--ws-accent)',
      fieldGlow: '0 0 10px rgba(var(--ws-accent-rgb), 0.28)',
      label: 'rgba(var(--ws-accent-rgb), 0.82)',
      inputNum: '#67e8f9',
    };
    const osFs = {
      rail: '#fb923c',
      chipBg: 'rgba(251, 146, 72, 0.2)',
      chipColor: '#fdba74',
      chipBorder: 'rgba(251, 146, 72, 0.5)',
      cardBg: 'rgba(251, 146, 72, 0.08)',
      cardBorder: 'rgba(251, 146, 72, 0.3)',
      cardGlow: '0 0 18px rgba(251, 146, 72, 0.12)',
      idxBg: 'rgba(251, 146, 72, 0.38)',
      idxColor: '#fff7ed',
      field: 'rgba(251, 146, 72, 0.42)',
      fieldHi: 'rgba(251, 146, 72, 0.68)',
      fieldFocus: '#fb923c',
      fieldGlow: '0 0 10px rgba(251, 146, 72, 0.28)',
      label: 'rgba(253, 186, 116, 0.9)',
      inputNum: '#fed7aa',
    };

    return (
      <Box
        className="panel-content character-panel-cyber"
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', pb: 2 }}
      >
        {/* 头部 - 发光标题 */}
        <Box
          className="panel-header"
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionReduced ? 0 : 0.4, ease: [0.22, 1, 0.36, 1] }}
          sx={{ pb: 1.5, mb: 1, borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isChaptersNarrow && (
              <Tooltip title="返回编辑">
                <IconButton
                  size="small"
                  onClick={handleClosePanel}
                  aria-label="返回编辑"
                  sx={{
                    flexShrink: 0,
                    minWidth: { xs: 34, sm: 36 },
                    minHeight: { xs: 34, sm: 36 },
                    color: 'rgba(255,255,255,0.75)',
                    border: '1px solid rgba(255,255,255,0.18)',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    '&:hover': { bgcolor: 'rgba(0,240,255,0.12)', borderColor: 'rgba(0,240,255,0.35)' },
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                </IconButton>
              </Tooltip>
            )}
            <Typography component="span" className="character-panel-title-glow" sx={{ flex: 1, minWidth: 0 }}>
              全书大纲
            </Typography>
            {currentOutline && (
              <Chip
                label="已设置"
                size="small"
                sx={{
                  bgcolor: 'rgba(var(--ws-accent-rgb), 0.2)',
                  color: 'var(--ws-accent)',
                  height: 22,
                  fontSize: 11,
                  fontWeight: 600,
                  border: '1px solid rgba(var(--ws-accent-rgb), 0.3)'
                }}
              />
            )}
          </Box>
        </Box>

        {/* 操作按钮：两列布局，查找替换+角色换名同一行，带发光呼吸灯 */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr 1fr',
              sm: currentOutline ? '1fr 1fr' : '1fr 1fr',
            },
            gap: { xs: 0.6, sm: 0.75 },
            mb: { xs: 1, sm: 1.5 },
            flexShrink: 0,
            width: '100%',
          }}
        >
          {/* 手动添加 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: motionReduced ? 0 : 0.04, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            whileHover={motionReduced ? undefined : { y: -1.5 }}
            whileTap={motionReduced ? undefined : { scale: 0.975 }}
            sx={{ width: '100%' }}
          >
            <Button
              fullWidth
              color="inherit"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 14, color: 'currentColor', opacity: 0.88 }} />}
              onClick={() => setShowSimpleOutlineDialog(true)}
              sx={{
                py: { xs: 0.35, sm: 0.4 },
                px: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: 30, sm: 32 },
                fontSize: { xs: 11.5, sm: 12.5 },
                fontWeight: 600,
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                borderRadius: 1.5,
                color: 'rgba(226, 232, 240, 0.92)',
                borderColor: 'rgba(255,255,255,0.2)',
                bgcolor: 'rgba(255,255,255,0.035)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 10px rgba(0,0,0,0.35)',
                transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease',
                '&:hover': {
                  color: 'rgba(248, 250, 252, 0.98)',
                  bgcolor: 'rgba(255,255,255,0.065)',
                  borderColor: 'rgba(255,255,255,0.32)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 14px rgba(0,0,0,0.4)',
                },
              }}
            >
              手动添加
            </Button>
          </Box>

          {/* AI生成 */}
          <Box
            component={motion.div}
            initial={motionReduced ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: motionReduced ? 0 : 0.1, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
            whileHover={motionReduced ? undefined : { y: -1.5 }}
            whileTap={motionReduced ? undefined : { scale: 0.975 }}
            sx={{ width: '100%' }}
          >
            <Button
              fullWidth
              color="inherit"
              variant="outlined"
              startIcon={<AutoFixHighIcon sx={{ fontSize: 14, color: 'currentColor', opacity: 0.88 }} />}
              onClick={() => setShowOutlineGenDialog(true)}
              sx={{
                py: { xs: 0.35, sm: 0.4 },
                px: { xs: 1.25, sm: 1.5 },
                minHeight: { xs: 30, sm: 32 },
                fontSize: { xs: 11.5, sm: 12.5 },
                fontWeight: 600,
                letterSpacing: '0.02em',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                borderRadius: 1.5,
                color: 'rgba(226, 232, 240, 0.92)',
                borderColor: 'rgba(255,255,255,0.2)',
                bgcolor: 'rgba(255,255,255,0.035)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 10px rgba(0,0,0,0.35)',
                transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease',
                '&:hover': {
                  color: 'rgba(248, 250, 252, 0.98)',
                  bgcolor: 'rgba(255,255,255,0.065)',
                  borderColor: 'rgba(255,255,255,0.32)',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 14px rgba(0,0,0,0.4)',
                },
              }}
            >
              AI生成
            </Button>
          </Box>

          {/* AI解析 */}
          {currentOutline && (
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: motionReduced ? 0 : 0.14, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -1.5 }}
              whileTap={motionReduced ? undefined : { scale: 0.975 }}
              sx={{ width: '100%' }}
            >
              <Button
                fullWidth
                color="inherit"
                variant="outlined"
                startIcon={<SummarizeIcon sx={{ fontSize: 14, color: 'currentColor', opacity: 0.88 }} />}
                onClick={() => {
                  const src = String(currentOutline.content || '').trim()
                    || String(currentOutline.mainPlot?.storyline || '').trim()
                    || String(currentOutline.mainPlot?.description || '').trim();
                  setOutlineParseContent(src);
                  setShowOutlineParseDialog(true);
                }}
                sx={{
                  py: { xs: 0.35, sm: 0.4 },
                  px: { xs: 1.25, sm: 1.5 },
                  minHeight: { xs: 30, sm: 32 },
                  fontSize: { xs: 11.5, sm: 12.5 },
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  color: '#38bdf8',
                  borderColor: 'rgba(56, 189, 248, 0.5)',
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  boxShadow: '0 0 12px rgba(56, 189, 248, 0.2), inset 0 1px 0 rgba(255,255,255,0.08)',
                  animation: 'pulseBlue 2.5s ease-in-out infinite',
                  '@keyframes pulseBlue': {
                    '0%, 100%': { boxShadow: '0 0 12px rgba(56, 189, 248, 0.2), inset 0 1px 0 rgba(255,255,255,0.08)' },
                    '50%': { boxShadow: '0 0 20px rgba(56, 189, 248, 0.4), inset 0 1px 0 rgba(255,255,255,0.08)' },
                  },
                  transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease',
                  '&:hover': {
                    color: '#7dd3fc',
                    bgcolor: 'rgba(56, 189, 248, 0.18)',
                    borderColor: 'rgba(56, 189, 248, 0.75)',
                    boxShadow: '0 0 24px rgba(56, 189, 248, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                    animation: 'none',
                  },
                }}
              >
                AI解析
              </Button>
            </Box>
          )}

          {/* 角色换名 — 粉色发光呼吸灯 */}
          {currentOutline && (
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: motionReduced ? 0 : 0.18, duration: motionReduced ? 0 : 0.38, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -1.5 }}
              whileTap={motionReduced ? undefined : { scale: 0.975 }}
              sx={{ width: '100%' }}
            >
              <Button
                fullWidth
                color="inherit"
                variant="outlined"
                startIcon={<PersonIcon sx={{ fontSize: 14, color: 'currentColor', opacity: 0.88 }} />}
                onClick={() => setCharacterRenameOpen(true)}
                sx={{
                  py: { xs: 0.35, sm: 0.4 },
                  px: { xs: 1.25, sm: 1.5 },
                  minHeight: { xs: 30, sm: 32 },
                  fontSize: { xs: 11.5, sm: 12.5 },
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  color: '#ec4899',
                  borderColor: 'rgba(236, 72, 153, 0.5)',
                  bgcolor: 'rgba(236, 72, 153, 0.1)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                  boxShadow: '0 0 12px rgba(236, 72, 153, 0.2), inset 0 1px 0 rgba(255,255,255,0.08)',
                  animation: 'pulsePink 2.5s ease-in-out infinite',
                  '@keyframes pulsePink': {
                    '0%, 100%': { boxShadow: '0 0 12px rgba(236, 72, 153, 0.2), inset 0 1px 0 rgba(255,255,255,0.08)' },
                    '50%': { boxShadow: '0 0 20px rgba(236, 72, 153, 0.4), inset 0 1px 0 rgba(255,255,255,0.08)' },
                  },
                  transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, color 0.22s ease',
                  '&:hover': {
                    color: '#f472b6',
                    bgcolor: 'rgba(236, 72, 153, 0.18)',
                    borderColor: 'rgba(236, 72, 153, 0.75)',
                    boxShadow: '0 0 24px rgba(236, 72, 153, 0.5), inset 0 1px 0 rgba(255,255,255,0.1)',
                    animation: 'none',
                  },
                }}
              >
                角色换名
              </Button>
            </Box>
          )}
        </Box>

        {/* 蓝色框内：直接显示并编辑大纲内容（铺满容器，失焦自动保存，底部显示更新时间） */}
        <Box
          className="outline-list"
          component={motion.div}
          initial={motionReduced ? false : { opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: motionReduced ? 0 : 0.08, duration: motionReduced ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] }}
          sx={{
            flex: 1,
            flexShrink: 0,
            minHeight: { xs: 260, sm: 320 },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '2px solid rgba(59, 130, 246, 0.5)',
            borderRadius: 2,
            bgcolor: 'rgba(59, 130, 246, 0.04)',
            p: 2,
            boxShadow: '0 0 40px rgba(34, 211, 238, 0.06)',
          }}
        >
          {!currentOutline ? (
            <Box sx={{ textAlign: 'center', py: 6, flex: 1 }}>
              <DescriptionIcon sx={{ fontSize: 56, mb: 2, opacity: 0.25, color: 'var(--ws-accent)' }} />
              <Typography sx={{ fontSize: 14, mb: 1, color: 'rgba(255,255,255,0.7)' }}>暂无大纲</Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.5, color: 'rgba(255,255,255,0.5)' }}>点击上方「手动添加」或「AI生成」创建大纲，内容将直接显示在此处并可编辑</Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* 标题行：书名/类型 + 删除（书名可换行，整段完整可见） */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0, width: '100%', minWidth: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    component="div"
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      fontWeight: 600,
                      fontSize: 14,
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal',
                    }}
                  >
                    {currentOutline.name || '未命名大纲'}
                  </Typography>
                </Box>
                <Chip
                  label={currentOutline.type || '仙侠'}
                  size="small"
                  sx={{ flexShrink: 0, alignSelf: 'center', bgcolor: 'rgba(var(--ws-accent-rgb), 0.15)', color: 'var(--ws-accent)', height: 18, fontSize: 10 }}
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    setOutlines([]);
                    setSnackbar({ open: true, message: '已删除大纲', severity: 'info' });
                  }}
                  sx={{ flexShrink: 0, alignSelf: 'flex-start', color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#ff4444' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              {/* 可编辑正文：占满剩余高度，框内滚动，有滚动条（始终保持大高度） */}
              <Box sx={{ flex: 1, minHeight: 260, display: 'flex', flexDirection: 'column' }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={10}
                  placeholder="在此编辑全书大纲内容，失去焦点后自动保存..."
                  value={outlineContent}
                  onChange={(e) => updateCurrentOutlineContent(e.target.value)}
                  onBlur={async () => {
                    const cur = outlines.length > 0 ? outlines[0] : null;
                    if (!cur) return;
                    await saveOutlinePatch({ kind: 'outline.field', field: 'content', value: String(cur.content ?? '') });
                    if (cur.name !== undefined) {
                      await saveOutlinePatch({ kind: 'outline.field', field: 'name', value: String(cur.name ?? '') });
                    }
                  }}
                  sx={{
                    height: '100%',
                    '& .MuiOutlinedInput-root': {
                      height: '100%',
                      alignItems: 'stretch',
                      bgcolor: 'rgba(0,0,0,0.2)',
                      fontSize: 13,
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 1,
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: 'rgba(255,255,255,0.14)' },
                      '&.Mui-focused': { borderColor: 'rgba(var(--ws-accent-rgb), 0.4)', boxShadow: '0 0 0 1px rgba(var(--ws-accent-rgb), 0.2)' },
                    },
                    '& .MuiOutlinedInput-input': {
                      height: '100% !important',
                      minHeight: '120px !important',
                      overflowY: 'auto !important',
                    },
                  }}
                />
              </Box>
              {/* 底部：自动保存时间（不弹窗提示） */}
              <Typography sx={{ flexShrink: 0, fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                更新于 {(() => {
                  const raw = currentOutline.updatedAt;
                  if (!raw) return '--';
                  const d = new Date(raw);
                  if (isNaN(d.getTime())) return '--';
                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                })()}
              </Typography>
            </Box>
          )}
        </Box>

        {/* 结构化信息显示区：主线、支线、伏笔 — 与大纲正文同框滚动 */}
        {currentOutline && (
          <Box
            sx={{
              mt: 2,
              p: { xs: 1.5, sm: 2 },
              bgcolor: 'rgba(255,255,255,0.03)',
              borderRadius: 2,
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <Box sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: 13, sm: 14 }, fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14 }}></span> 结构化信息
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              <Chip
                size="small"
                label="主线"
                onClick={() => setOutlineStructuredTab('main')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  height: 24,
                  ...(outlineStructuredTab === 'main'
                    ? { bgcolor: osMain.chipBg, color: osMain.chipColor, border: `1px solid ${osMain.chipBorder}`, boxShadow: osMain.fieldGlow }
                    : { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)' }),
                }}
              />
              <Chip
                size="small"
                label={`支线（${(currentOutline?.subPlots || []).length}）`}
                onClick={() => setOutlineStructuredTab('sub')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  height: 24,
                  ...(outlineStructuredTab === 'sub'
                    ? { bgcolor: osSub.chipBg, color: osSub.chipColor, border: `1px solid ${osSub.chipBorder}`, boxShadow: osSub.cardGlow }
                    : { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)' }),
                }}
              />
              <Chip
                size="small"
                label={`伏笔（${(currentOutline?.foreshadowing || []).length}）`}
                onClick={() => setOutlineStructuredTab('foreshadow')}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 700,
                  height: 24,
                  ...(outlineStructuredTab === 'foreshadow'
                    ? { bgcolor: osFs.chipBg, color: osFs.chipColor, border: `1px solid ${osFs.chipBorder}`, boxShadow: osFs.fieldGlow }
                    : { bgcolor: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.12)' }),
                }}
              />
            </Box>

            {outlineStructuredTab === 'main' && (
            <Box sx={{ mb: 2.5, pl: 1.5, borderLeft: `4px solid ${osMain.rail}`, borderRadius: '0 4px 4px 0' }}>
              <Chip size="small" label="主线剧情" sx={{ mb: 1, bgcolor: osMain.chipBg, color: osMain.chipColor, fontWeight: 700, border: `1px solid ${osMain.chipBorder}` }} />
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={currentOutline?.mainPlot?.description || ''}
                onChange={(e) => {
                  const updated = { ...currentOutline, mainPlot: { ...currentOutline.mainPlot, description: e.target.value }, updatedAt: new Date().toISOString() };
                  setOutlines([updated]);
                }}
                onBlur={async (e) => {
                  await saveOutlinePatch({ kind: 'mainPlot.field', field: 'description', value: e.target.value });
                }}
                placeholder="描述主线剧情..."
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    fontSize: 14,
                    borderRadius: 1.25,
                    bgcolor: 'rgba(245, 158, 11, 0.04)',
                    '& fieldset': { borderColor: osMain.field },
                    '&:hover fieldset': { borderColor: osMain.fieldHi },
                    '&.Mui-focused fieldset': { borderColor: osMain.fieldFocus, borderWidth: 1.5, boxShadow: osMain.fieldGlow },
                  },
                  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
                }}
              />
            </Box>
            )}

            {outlineStructuredTab === 'sub' && (
            <Box sx={{ mb: 2.5, pl: 1.5, borderLeft: `4px solid ${osSub.rail}`, borderRadius: '0 4px 4px 0' }}>
              <Box sx={{ mb: 1 }}>
                <Chip size="small" label={`支线剧情（${(currentOutline?.subPlots || []).length}条）`} sx={{ bgcolor: osSub.chipBg, color: osSub.chipColor, fontWeight: 700, border: `1px solid ${osSub.chipBorder}` }} />
              </Box>
              {(currentOutline?.subPlots || []).length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {currentOutline.subPlots.map((sp, idx) => (
                    <Box
                      key={sp.id}
                      sx={{
                        p: 1.25,
                        bgcolor: osSub.cardBg,
                        borderRadius: 1.5,
                        border: `1px solid ${osSub.cardBorder}`,
                        boxShadow: osSub.cardGlow,
                      }}
                    >
                      {/* 编号行 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Chip size="small" label={`${idx + 1}`} sx={{ bgcolor: osSub.idxBg, color: osSub.idxColor, fontWeight: 800, minWidth: 32, height: 22, flexShrink: 0, px: 0.5 }} />
                        <TextField
                          fullWidth
                          size="small"
                          value={typeof sp.title === 'string' ? sp.title : ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.subPlots];
                            newList[idx] = { ...newList[idx], title: e.target.value };
                            const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'title', value: e.target.value });
                          }}
                          placeholder="支线标题"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              fontSize: 14,
                              fontWeight: 600,
                              borderRadius: 1.25,
                              bgcolor: 'rgba(var(--ws-accent-rgb), 0.05)',
                              '& fieldset': { borderColor: osSub.field },
                              '&:hover fieldset': { borderColor: osSub.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, borderWidth: 1.5, boxShadow: osSub.fieldGlow },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: 12 },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={async () => {
                            // 先本地删，再 PATCH 删除
                            const newList = currentOutline.subPlots.filter((_, i) => i !== idx);
                            setOutlines([{ ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() }]);
                            await saveOutlinePatch({ kind: 'subPlot.delete', id: sp.id });
                          }}
                          sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff4444' } }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      {/* 章节范围行 - 移动端适配，不换行不溢出 */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 0.25, sm: 0.5 },
                          flexWrap: 'nowrap',
                          flexShrink: 1,
                          overflowX: 'hidden',
                          pb: 0.25,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: osSub.label,
                            fontSize: { xs: 10, sm: 12 },
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                        >
                          章节：
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={sp.startChapterNumber ?? ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.subPlots];
                            newList[idx] = { ...newList[idx], startChapterNumber: e.target.value === '' ? null : parseInt(e.target.value, 10) };
                            const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'startChapterNumber', value: val });
                          }}
                          InputProps={{ inputProps: { min: 1, style: { textAlign: 'center', padding: '2px 4px', fontSize: 11, color: osSub.inputNum } } }}
                          sx={{
                            width: { xs: 48, sm: 64 },
                            flexShrink: 1,
                            minWidth: 40,
                            '& .MuiOutlinedInput-root': {
                              height: 24,
                              borderRadius: 1,
                              '& fieldset': { borderColor: osSub.field },
                              '&:hover fieldset': { borderColor: osSub.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, boxShadow: osSub.fieldGlow },
                            },
                          }}
                          placeholder="起始"
                        />
                        <Typography component="span" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: 10, sm: 12 }, flexShrink: 0, whiteSpace: 'nowrap' }}>至</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={sp.endChapterNumber ?? ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.subPlots];
                            newList[idx] = { ...newList[idx], endChapterNumber: e.target.value === '' ? null : parseInt(e.target.value, 10) };
                            const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'endChapterNumber', value: val });
                          }}
                          InputProps={{ inputProps: { min: 1, style: { textAlign: 'center', padding: '2px 4px', fontSize: 11, color: osSub.inputNum } } }}
                          sx={{
                            width: { xs: 48, sm: 64 },
                            flexShrink: 1,
                            minWidth: 40,
                            '& .MuiOutlinedInput-root': {
                              height: 24,
                              borderRadius: 1,
                              '& fieldset': { borderColor: osSub.field },
                              '&:hover fieldset': { borderColor: osSub.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, boxShadow: osSub.fieldGlow },
                            },
                          }}
                          placeholder="结束"
                        />
                      </Box>
                      {/* 所属阶段 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                        <Typography component="span" sx={{ color: osSub.label, fontSize: 11, flexShrink: 0, whiteSpace: 'nowrap' }}>所属阶段：</Typography>
                        <TextField
                          size="small"
                          value={subPlotsEditRef.current[`${sp.id}_phase`] ?? sp.phase ?? ''}
                          onChange={(e) => {
                            subPlotsEditRef.current[`${sp.id}_phase`] = e.target.value;
                            const newList = [...currentOutline.subPlots];
                            newList[idx] = { ...newList[idx], phase: e.target.value };
                            const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async () => {
                            const val = subPlotsEditRef.current[`${sp.id}_phase`] ?? '';
                            await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'phase', value: val });
                          }}
                          placeholder="开局/发展/高潮/结局"
                          sx={{
                            flex: 1,
                            minWidth: 80,
                            '& .MuiOutlinedInput-root': {
                              height: 26,
                              fontSize: 11,
                              borderRadius: 1,
                              bgcolor: 'rgba(var(--ws-accent-rgb), 0.04)',
                              '& fieldset': { borderColor: osSub.field },
                              '&:hover fieldset': { borderColor: osSub.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, boxShadow: osSub.fieldGlow },
                            },
                          }}
                        />
                      </Box>
                      {/* 核心冲突（结构化字段：coreConflict） */}
                      <TextField
                        fullWidth
                        size="small"
                        value={subPlotsEditRef.current[`${sp.id}_coreConflict`] ?? sp.coreConflict ?? ''}
                        onChange={(e) => {
                          subPlotsEditRef.current[`${sp.id}_coreConflict`] = e.target.value;
                          const newList = [...currentOutline.subPlots];
                          newList[idx] = { ...newList[idx], coreConflict: e.target.value };
                          setOutlines([{ ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() }]);
                        }}
                        onBlur={async () => {
                          const val = subPlotsEditRef.current[`${sp.id}_coreConflict`] ?? '';
                          await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'coreConflict', value: val });
                        }}
                        placeholder="核心冲突（结构化，用于章纲/正文提示）..."
                        sx={{
                          mt: 0.75,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 12,
                            borderRadius: 1,
                            bgcolor: 'rgba(var(--ws-accent-rgb), 0.03)',
                            '& fieldset': { borderColor: osSub.field },
                            '&:hover fieldset': { borderColor: osSub.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, borderWidth: 1, boxShadow: osSub.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />

                      {/* 支线完整描述（结构化字段：description） */}
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        value={subPlotsEditRef.current[`${sp.id}_description`] ?? sp.description ?? ''}
                        onChange={(e) => {
                          subPlotsEditRef.current[`${sp.id}_description`] = e.target.value;
                          const newList = [...currentOutline.subPlots];
                          newList[idx] = { ...newList[idx], description: e.target.value };
                          setOutlines([{ ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() }]);
                        }}
                        onBlur={async () => {
                          const val = subPlotsEditRef.current[`${sp.id}_description`] ?? '';
                          await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'description', value: val });
                        }}
                        placeholder="支线完整描述（必须完整保留原文所有内容）..."
                        sx={{
                          mt: 0.75,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 12,
                            borderRadius: 1,
                            bgcolor: 'rgba(var(--ws-accent-rgb), 0.03)',
                            '& fieldset': { borderColor: osSub.field },
                            '&:hover fieldset': { borderColor: osSub.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, borderWidth: 1, boxShadow: osSub.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />
                      {/* 与主线关系（失焦保存，避免每字触发保存） */}
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        size="small"
                        value={subPlotsEditRef.current[`${sp.id}_mainPlotRelation`] ?? sp.mainPlotRelation ?? ''}
                        onChange={(e) => {
                          subPlotsEditRef.current[`${sp.id}_mainPlotRelation`] = e.target.value;
                          const newList = [...currentOutline.subPlots];
                          newList[idx] = { ...newList[idx], mainPlotRelation: e.target.value };
                          const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                          setOutlines([updated]);
                        }}
                        onBlur={async () => {
                          const val = subPlotsEditRef.current[`${sp.id}_mainPlotRelation`] ?? '';
                          await saveOutlinePatch({ kind: 'subPlot.field', id: sp.id, field: 'mainPlotRelation', value: val });
                        }}
                        placeholder="与主线关系（必须完整保留原文所有内容）..."
                        sx={{
                          mt: 0.75,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.85)',
                            fontSize: 12,
                            borderRadius: 1,
                            bgcolor: 'rgba(var(--ws-accent-rgb), 0.03)',
                            '& fieldset': { borderColor: osSub.field },
                            '&:hover fieldset': { borderColor: osSub.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osSub.fieldFocus, borderWidth: 1, boxShadow: osSub.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newItem = { id: 'sp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8), title: '', coreConflict: '', phase: '', mainPlotRelation: '', description: '' };
                      const newList = [...(currentOutline?.subPlots || []), newItem];
                      const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                      setOutlines([updated]);
                      saveOutlinePatch({ kind: 'subPlot.add', item: newItem });
                    }}
                    sx={{
                      color: 'var(--ws-accent)',
                      fontSize: 12,
                      minHeight: 24,
                      alignSelf: 'flex-start',
                      opacity: 0.92,
                      '&:hover': { opacity: 1, bgcolor: 'rgba(var(--ws-accent-rgb),0.1)' },
                    }}
                  >
                    添加支线
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, fontStyle: 'italic', mb: 1 }}>暂无支线，可手动添加或通过「AI解析」提取</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newItem = { id: 'sp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8), title: '', coreConflict: '', phase: '', mainPlotRelation: '', description: '' };
                      const newList = [newItem];
                      const updated = { ...currentOutline, subPlots: newList, updatedAt: new Date().toISOString() };
                      setOutlines([updated]);
                      saveOutlinePatch({ kind: 'subPlot.add', item: newItem });
                    }}
                    sx={{
                      color: 'var(--ws-accent)',
                      fontSize: 12,
                      minHeight: 24,
                      opacity: 0.92,
                      '&:hover': { opacity: 1, bgcolor: 'rgba(var(--ws-accent-rgb),0.1)' },
                    }}
                  >
                    添加支线
                  </Button>
                </Box>
              )}
            </Box>
            )}

            {outlineStructuredTab === 'foreshadow' && (
            <Box sx={{ pl: 1.5, borderLeft: `4px solid ${osFs.rail}`, borderRadius: '0 4px 4px 0' }}>
              <Box sx={{ mb: 1 }}>
                <Chip size="small" label={`伏笔悬念（${(currentOutline?.foreshadowing || []).length}条）`} sx={{ bgcolor: osFs.chipBg, color: osFs.chipColor, fontWeight: 700, border: `1px solid ${osFs.chipBorder}` }} />
              </Box>
              {(currentOutline?.foreshadowing || []).length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {currentOutline.foreshadowing.map((fs, idx) => (
                    <Box
                      key={fs.id}
                      sx={{
                        p: 1.25,
                        bgcolor: osFs.cardBg,
                        borderRadius: 1.5,
                        border: `1px solid ${osFs.cardBorder}`,
                        boxShadow: osFs.cardGlow,
                        minWidth: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {/* 编号行 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, minWidth: 0, overflow: 'hidden' }}>
                        <Chip size="small" label={`${idx + 1}`} sx={{ bgcolor: osFs.idxBg, color: osFs.idxColor, fontWeight: 800, minWidth: 32, height: 22, flexShrink: 0, px: 0.5 }} />
                        <TextField
                          fullWidth
                          size="small"
                          value={typeof fs.title === 'string' ? fs.title : ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.foreshadowing];
                            newList[idx] = { ...newList[idx], title: e.target.value };
                            const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'title', value: e.target.value });
                          }}
                          placeholder="伏笔标题"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              color: '#fff',
                              fontSize: 14,
                              fontWeight: 600,
                              borderRadius: 1.25,
                              bgcolor: 'rgba(251, 146, 72, 0.06)',
                              '& fieldset': { borderColor: osFs.field },
                              '&:hover fieldset': { borderColor: osFs.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, borderWidth: 1.5, boxShadow: osFs.fieldGlow },
                            },
                            '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)', fontSize: 12 },
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={async () => {
                            const newList = currentOutline.foreshadowing.filter((_, i) => i !== idx);
                            setOutlines([{ ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() }]);
                            await saveOutlinePatch({ kind: 'foreshadow.delete', id: fs.id });
                          }}
                          sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff4444' }, flexShrink: 0 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <TextField
                        fullWidth
                        multiline
                        minRows={1}
                        size="small"
                        value={foreshadowEditRef.current[`${idx}_description`] ?? fs.description ?? ''}
                        onChange={(e) => {
                          foreshadowEditRef.current[`${idx}_description`] = e.target.value;
                          const newList = [...currentOutline.foreshadowing];
                          newList[idx] = { ...newList[idx], description: e.target.value };
                          const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                          setOutlines([updated]);
                        }}
                        onBlur={async () => {
                          const val = foreshadowEditRef.current[`${idx}_description`] ?? '';
                          await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'description', value: val });
                        }}
                        placeholder="伏笔描述..."
                        sx={{
                          mb: 1,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.88)',
                            fontSize: 13,
                            borderRadius: 1.25,
                            bgcolor: 'rgba(251, 146, 72, 0.05)',
                            '& fieldset': { borderColor: osFs.field },
                            '&:hover fieldset': { borderColor: osFs.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, borderWidth: 1.5, boxShadow: osFs.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />
                      {/* 伏笔章节号行 - 参考支线章节范围的紧凑布局 */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: { xs: 0.25, sm: 0.5 },
                          flexShrink: 1,
                          overflowX: 'hidden',
                          pb: 0.25,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            color: osFs.label,
                            fontSize: { xs: 10, sm: 12 },
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            lineHeight: 1.2,
                          }}
                        >
                          铺垫：
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={fs.plantedChapterNumber ?? ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.foreshadowing];
                            newList[idx] = { ...newList[idx], plantedChapterNumber: e.target.value === '' ? null : parseInt(e.target.value, 10) };
                            const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'plantedChapterNumber', value: val });
                          }}
                          InputProps={{ inputProps: { min: 1, style: { textAlign: 'center', padding: '2px 4px', fontSize: 11, color: osFs.inputNum } } }}
                          sx={{
                            width: { xs: 48, sm: 64 },
                            flexShrink: 1,
                            minWidth: 40,
                            '& .MuiOutlinedInput-root': {
                              height: 24,
                              borderRadius: 1,
                              '& fieldset': { borderColor: osFs.field },
                              '&:hover fieldset': { borderColor: osFs.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, boxShadow: osFs.fieldGlow },
                            },
                          }}
                          placeholder="—"
                        />
                        <Typography component="span" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: 10, sm: 12 }, flexShrink: 0, whiteSpace: 'nowrap' }}>揭示：</Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={fs.revealChapterNumber ?? ''}
                          onChange={(e) => {
                            const newList = [...currentOutline.foreshadowing];
                            newList[idx] = { ...newList[idx], revealChapterNumber: e.target.value === '' ? null : parseInt(e.target.value, 10) };
                            const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                            setOutlines([updated]);
                          }}
                          onBlur={async (e) => {
                            const val = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'revealChapterNumber', value: val });
                          }}
                          InputProps={{ inputProps: { min: 1, style: { textAlign: 'center', padding: '2px 4px', fontSize: 11, color: osFs.inputNum } } }}
                          sx={{
                            width: { xs: 48, sm: 64 },
                            flexShrink: 1,
                            minWidth: 40,
                            '& .MuiOutlinedInput-root': {
                              height: 24,
                              borderRadius: 1,
                              '& fieldset': { borderColor: osFs.field },
                              '&:hover fieldset': { borderColor: osFs.fieldHi },
                              '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, boxShadow: osFs.fieldGlow },
                            },
                          }}
                          placeholder="—"
                        />
                      </Box>
                      {/* 线索分布 */}
                      <TextField
                        fullWidth
                        size="small"
                        value={foreshadowEditRef.current[`${idx}_clueDistribution`] ?? fs.clueDistribution ?? ''}
                        onChange={(e) => {
                          foreshadowEditRef.current[`${idx}_clueDistribution`] = e.target.value;
                          const newList = [...currentOutline.foreshadowing];
                          newList[idx] = { ...newList[idx], clueDistribution: e.target.value };
                          const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                          setOutlines([updated]);
                        }}
                        onBlur={async () => {
                          const val = foreshadowEditRef.current[`${idx}_clueDistribution`] ?? '';
                          await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'clueDistribution', value: val });
                        }}
                        placeholder="线索分布（如：第1章觉醒时出现；第100章印记发热；第500章印记裂痕）"
                        sx={{
                          mt: 0.75,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.82)',
                            fontSize: 12,
                            borderRadius: 1,
                            bgcolor: 'rgba(251, 146, 72, 0.03)',
                            '& fieldset': { borderColor: osFs.field },
                            '&:hover fieldset': { borderColor: osFs.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, boxShadow: osFs.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />
                      {/* 揭示方式 */}
                      <TextField
                        fullWidth
                        size="small"
                        value={foreshadowEditRef.current[`${idx}_revealMethod`] ?? fs.revealMethod ?? ''}
                        onChange={(e) => {
                          foreshadowEditRef.current[`${idx}_revealMethod`] = e.target.value;
                          const newList = [...currentOutline.foreshadowing];
                          newList[idx] = { ...newList[idx], revealMethod: e.target.value };
                          const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                          setOutlines([updated]);
                        }}
                        onBlur={async () => {
                          const val = foreshadowEditRef.current[`${idx}_revealMethod`] ?? '';
                          await saveOutlinePatch({ kind: 'foreshadow.field', id: fs.id, field: 'revealMethod', value: val });
                        }}
                        placeholder="揭示方式（如：老鬼解读印记编码）"
                        sx={{
                          mt: 0.75,
                          '& .MuiOutlinedInput-root': {
                            color: 'rgba(255,255,255,0.82)',
                            fontSize: 12,
                            borderRadius: 1,
                            bgcolor: 'rgba(251, 146, 72, 0.03)',
                            '& fieldset': { borderColor: osFs.field },
                            '&:hover fieldset': { borderColor: osFs.fieldHi },
                            '&.Mui-focused fieldset': { borderColor: osFs.fieldFocus, boxShadow: osFs.fieldGlow },
                          },
                          '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
                        }}
                      />
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newItem = { id: 'fs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8), title: '', description: '', clueDistribution: '', revealMethod: '' };
                      const newList = [...(currentOutline?.foreshadowing || []), newItem];
                      const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                      setOutlines([updated]);
                      saveOutlinePatch({ kind: 'foreshadow.add', item: newItem });
                    }}
                    sx={{
                      color: osFs.chipColor,
                      fontSize: 12,
                      minHeight: 24,
                      alignSelf: 'flex-start',
                      opacity: 0.92,
                      '&:hover': { opacity: 1, bgcolor: 'rgba(251,146,72,0.08)' },
                    }}
                  >
                    添加伏笔
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 12, fontStyle: 'italic', mb: 1 }}>暂无伏笔，可手动添加或通过「AI解析」提取</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      const newItem = { id: 'fs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8), title: '', description: '', clueDistribution: '', revealMethod: '' };
                      const newList = [newItem];
                      const updated = { ...currentOutline, foreshadowing: newList, updatedAt: new Date().toISOString() };
                      setOutlines([updated]);
                      saveOutlinePatch({ kind: 'foreshadow.add', item: newItem });
                    }}
                    sx={{
                      color: osFs.chipColor,
                      fontSize: 12,
                      minHeight: 24,
                      opacity: 0.92,
                      '&:hover': { opacity: 1, bgcolor: 'rgba(251,146,72,0.08)' },
                    }}
                  >
                    添加伏笔
                  </Button>
                </Box>
              )}
            </Box>
            )}
          </Box>
        )}
      </Box>
    );
  };

  // AI 解析大纲弹窗：从已保存的整段大纲提取结构化信息（主线、支线、伏笔等）
  const renderOutlineParseDialog = () => {
    const outlineParseChips = [
      { label: '主线剧情', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '世界观设定', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '核心冲突', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '高潮设计', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '角色关系', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '支线剧情', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
      { label: '伏笔埋设', sx: { bgcolor: 'rgba(0,240,255,0.08)', color: 'rgba(0,240,255,0.7)', border: '1px solid rgba(0,240,255,0.25)' } },
    ];
    return (
    <Dialog
      open={showOutlineParseDialog}
      onClose={() => {
        if (parsingOutline) return;
        setShowOutlineParseDialog(false);
        setOutlineParseContent('');
      }}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={parsingOutline}
      TransitionProps={{ timeout: { enter: 320, exit: 220 } }}
      PaperProps={{
        className: `character-panel-cyber${parsingOutline ? ' outline-parse-dialog-parsing' : ''}`,
        sx: {
          bgcolor: 'rgba(10, 11, 18, 0.98)',
          backgroundImage: 'none',
          border: '1px solid rgba(0,240,255,0.3)',
          borderRadius: 2,
          maxHeight: '80vh',
          boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 120px rgba(0,240,255,0.15)',
          overflow: 'hidden',
        }
      }}
    >
      <DialogTitle
        className={`character-panel-title-glow ${parsingOutline ? 'outline-parse-title-parsing' : ''}`}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00f0ff !important', borderBottom: '1px solid rgba(0,240,255,0.2)', py: 1.5, flexShrink: 0 }}
      >
        <motion.div
          style={{ display: 'inline-block', fontSize: 18 }}
          animate={parsingOutline ? { rotate: [0, 18, -18, 0], scale: [1, 1.15, 1] } : { rotate: 0, scale: 1 }}
          transition={parsingOutline ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
        >
          *
        </motion.div>
        <motion.div
          initial={false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          AI 解析大纲（提取结构化信息）
        </motion.div>
      </DialogTitle>
      <DialogContent className="character-panel-cyber" sx={{ p: 0, overflow: 'hidden' }}>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
        <Box className="character-panel-cyber" sx={{ p: 2, overflow: 'auto', maxHeight: 'calc(80vh - 80px)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 1.5 }}>
            系统将自动分析下方的「大纲正文」，提取其中的：
          </Typography>
          <Box
            className={`outline-parse-chipbar ${parsingOutline ? 'outline-parse-chipbar-parsing' : ''}`}
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              mb: 2,
              mt: 0.25,
              py: 0.75,
              overflow: 'visible',
              alignItems: 'center',
            }}
          >
            {outlineParseChips.map((c, idx) => {
              // 动画索引循环 0-9，但 Chip 只有 7 个，用取模映射
              const chipActive = parsingOutline && outlineParsePhaseIdx === idx;
              return (
              <motion.div
                key={c.label}
                style={{ borderRadius: 9999 }}
                initial={{ opacity: 0, y: 10, scale: 0.92 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: chipActive ? 1.05 : 1,
                  boxShadow: chipActive
                    ? `0 0 12px rgba(0,240,255,0.35)`
                    : '0 0 0px rgba(0,0,0,0)',
                }}
                transition={{
                  delay: 0.04 * idx,
                  type: 'spring',
                  stiffness: 380,
                  damping: 22,
                  scale: { duration: 0.3 },
                }}
              >
                <Chip
                  label={c.label}
                  size="small"
                  sx={{
                    ...c.sx,
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 9999,
                    transition: 'all 0.3s ease',
                    transform: chipActive ? 'scale(1.05)' : 'scale(1)',
                    boxShadow: chipActive ? '0 0 10px rgba(0,240,255,0.3)' : 'none',
                  }}
                />
              </motion.div>
              );
            })}
          </Box>
          <Box sx={{ position: 'relative', mb: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              value={outlineParseContent}
              disabled={parsingOutline}
              onChange={(e) => setOutlineParseContent(e.target.value)}
              placeholder="请确保大纲正文在此框内，系统将分析内容并提取关键信息..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.25)',
                  fontSize: 13,
                  color: '#fff',
                  transition: 'box-shadow 0.35s ease',
                  '& fieldset': { borderColor: 'rgba(0,240,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,240,255,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#00f0ff' },
                  ...(parsingOutline ? {
                    boxShadow: 'inset 0 0 28px rgba(34, 211, 238, 0.12)',
                  } : {}),
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />
            <AnimatePresence>
              {parsingOutline && (
                <motion.div
                  className="outline-parse-field-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="outline-parse-shimmer-grid" aria-hidden />
                  <div className="outline-parse-scan-line" aria-hidden />
                  <div className="outline-parse-particles" aria-hidden />
                  <Box sx={{ position: 'absolute', left: 12, right: 12, bottom: 44, textAlign: 'center', pointerEvents: 'none' }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={outlineParsePhaseIdx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                          color: 'rgba(0,240,255,0.75)',
                          fontSize: 13,
                          fontWeight: 500,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {outlineParseProgressText || OUTLINE_PARSE_PHASE_LABELS[outlineParsePhaseIdx]}
                      </motion.div>
                    </AnimatePresence>
                  </Box>
                  <LinearProgress
                    className="outline-parse-linear-progress"
                    variant="indeterminate"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      borderRadius: 0,
                      bgcolor: 'rgba(0,0,0,0.35)',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}
          >
          <Box sx={{ p: 2, bgcolor: 'rgba(0,240,255,0.05)', borderRadius: 1, border: '1px solid rgba(0,240,255,0.15)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, lineHeight: 1.7 }}>
              <strong>使用场景：</strong>如果您已经通过「手动添加」保存了完整的一大段大纲，但想让系统把里面的结构化信息拆出来（主线、世界观、核心冲突、高潮设计、角色关系、支线、伏笔等），可以点此按钮让 AI 自动解析。<br /><br />
              <strong>解析结果：</strong>点击「开始解析」后，AI 会提取以下结构化信息：主线剧情、故事四阶段、世界观设定、核心冲突、高潮设计、角色关系网络、支线剧情、伏笔埋设。提取后，原来的「整段正文」仍保留在 <code>content</code> 字段，不会丢失。
            </Typography>
          </Box>
          </motion.div>
        </Box>
        </motion.div>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,240,255,0.2)', gap: 1.5, flexShrink: 0, bgcolor: 'transparent' }}>
        <Button
          disabled={parsingOutline}
          onClick={() => { setShowOutlineParseDialog(false); setOutlineParseContent(''); }}
          className="workspace-btn-cyber"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(255,255,255,0.22)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            flex: 1,
            minWidth: 120
          }}
        >
          取消
        </Button>
        <Button
          variant="outlined"
          disabled={parsingOutline}
          onClick={async () => {
            if (!outlineParseContent.trim()) {
              setSnackbar({ open: true, message: '请确保大纲正文不为空', severity: 'warning' });
              return;
            }
            setOutlineParsePhaseIdx(0);
            setParsingOutline(true);
            // 真实 AI 解析
            try {
              // ══════════════════════════════════════════════════════════════════
              // 🔥 分段解析（1000 章大体量场景专用）
              // 单次请求 → max_tokens 截断 → JSON 残缺 → 解析失败。
              // 改为：Phase1 全局字段（小输出）+ Phase2 支线/伏笔按 ~1 万字段落分批。
              // 每段独立 callAiParseJson（含 robustJsonParse 容错 + 失败重试 1 次）。
              // ══════════════════════════════════════════════════════════════════
              const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
              const fullText = outlineParseContent.trim();
              const segments = splitOutlineText(fullText, 10000);
              const isSegmented = segments.length > 1;
              console.log(`[大纲解析] 全文 ${fullText.length} 字，切分为 ${segments.length} 段（${isSegmented ? '分段模式' : '单段模式'}）`);

              // 先获取 prev（如果存在）
              const prevOutline = outlines.length > 0 ? outlines[0] : null;

              // ── Phase 1：全局字段（主线/四阶段/世界观/核心冲突/高潮/角色关系） ──
              // 全文输入，但只输出小体量全局字段（不含 subPlots/foreshadowings），避免截断
              setOutlineParseProgressText(isSegmented ? '正在提取全局信息（主线/世界观/核心冲突/高潮/角色关系）…' : '正在解析大纲…');

              const globalPrompt = `你是一个专业的小说大纲结构化提取助手。请仔细阅读下面的小说大纲，提取全局结构化信息。

⚠️ 核心原则：
1. description 字段原封不动复制原文，不缩写、不简化。
2. 只提取大纲中明确写明的内容，不推测、不脑补。
3. 章节号必须是纯数字。
4. 如果大纲中没有某类信息，返回空数组[]或空字符串。

请严格按照以下JSON格式输出（只需输出JSON，不要其他任何内容）：
{
  "mainPlot": "一句话概括主线剧情（来自大纲）",
  "storyStructure": { "beginning": "开局阶段描述（原文完整保留）", "development": "发展阶段描述（原文完整保留）", "climax": "高潮阶段描述（原文完整保留）", "ending": "结局阶段描述（原文完整保留）" },
  "worldSettings": { "background": "世界背景描述（原文完整保留）", "levelSystem": "修炼体系/等级制度描述（原文完整保留）", "factions": ["势力门派1"] },
  "coreConflicts": [ { "title": "冲突标题", "description": "冲突描述（原文完整保留）", "relatedCharacters": ["角色1"] } ],
  "climaxes": [ { "title": "高潮标题", "description": "高潮描述（原文完整保留）", "chapterRange": { "start": 1, "end": 100 } } ],
  "characterNetwork": [ { "from": "角色A", "to": "角色B", "relationship": "关系描述（原文完整保留）", "type": "relationship" } ]
}

${isSegmented ? '注意：支线剧情(subPlots)和伏笔(foreshadowings)会单独分批提取，本次不需要输出这两个字段。' : '请同时提取 subPlots 和 foreshadowings 字段（格式同全局字段一致）。'}

以下是小说大纲内容：
${fullText}`;

              const globalParsed = await callAiParseJson(
                [
                  { role: 'system', content: '你是一个专业的小说大纲分析助手，擅长提取大纲中的结构化信息。只输出JSON，不要任何其他内容。' },
                  { role: 'user', content: globalPrompt }
                ],
                { novelName: currentNovelName, operationType: 'AI解析大纲-全局', maxTokens: 16000 }
              );

              let allSubPlots = [];
              let allForeshadowings = [];

              if (isSegmented) {
                // ── Phase 2：支线/伏笔分批提取（每段 ~1 万字） ──
                for (let i = 0; i < segments.length; i++) {
                  setOutlineParseProgressText(`正在提取支线/伏笔（第${i + 1}/${segments.length}段）…`);
                  console.log(`[大纲解析] Phase2 段 ${i + 1}/${segments.length}（${segments[i].length} 字）`);
                  const segPrompt = `你是大纲结构化提取助手。以下是小说大纲的第 ${i + 1} 段（共 ${segments.length} 段）。请只提取该段中出现的支线剧情和伏笔信息。

⚠️ 核心原则：
1. description 字段原封不动复制原文，不缩写、不简化。原文描述几百字就保留几百字。
2. 只提取大纲中明确写明的内容，不推测、不脑补。
3. 章节号必须是纯数字。
4. 如果该段没有支线或伏笔，返回空数组[]。

请严格按照以下JSON格式输出（只需输出JSON，不要其他任何内容）：
{
  "subPlots": [
    { "title": "支线标题", "phase": "所属阶段（开局/发展/高潮/结局）", "startChapter": 1, "endChapter": 100, "coreConflict": "核心冲突描述（原文完整保留）", "mainPlotRelation": "与主线关系描述（原文完整保留）", "relatedCharacters": ["角色名1"], "description": "支线完整描述（原文完整保留，一个字都不能少）" }
  ],
  "foreshadowings": [
    { "title": "伏笔标题", "description": "伏笔描述（原文完整保留）", "plantedChapter": 1, "advanceChapters": [100], "revealChapter": 500, "clueDistribution": "线索分布原文", "revealMethod": "揭示方式原文", "relatedForeshadowings": ["关联伏笔标题1"] }
  ]
}

以下是第 ${i + 1} 段大纲内容：
${segments[i]}`;

                  const segResult = await callAiParseJson(
                    [
                      { role: 'system', content: '你是专业的大纲结构化提取助手。只输出JSON，不要任何其他内容。' },
                      { role: 'user', content: segPrompt }
                    ],
                    { novelName: currentNovelName, operationType: `AI解析大纲-段${i + 1}`, maxTokens: 12000 }
                  );
                  if (segResult) {
                    allSubPlots.push(...(segResult.subPlots || []));
                    allForeshadowings.push(...(segResult.foreshadowings || []));
                  }
                }
              } else {
                // 单段：直接从全局结果取 subPlots/foreshadowings
                allSubPlots = globalParsed?.subPlots || [];
                allForeshadowings = globalParsed?.foreshadowings || [];
              }

              // ── Phase 3：合并 ──
              setOutlineParseProgressText('正在拼装结构化数据…');
              const parsed = {
                mainPlot: globalParsed?.mainPlot || '',
                storyStructure: globalParsed?.storyStructure || {},
                worldSettings: globalParsed?.worldSettings || {},
                coreConflicts: globalParsed?.coreConflicts || [],
                climaxes: globalParsed?.climaxes || [],
                characterNetwork: globalParsed?.characterNetwork || [],
                subPlots: allSubPlots,
                foreshadowings: allForeshadowings,
              };
              console.log('[大纲解析] 合并完成：subPlots=', allSubPlots.length, 'foreshadowings=', allForeshadowings.length);
              if (parsed) {
                console.log('[大纲解析] 解析后的JSON对象:');
                console.log(JSON.stringify(parsed, null, 2));
                console.log('[大纲解析] 各字段检查:');
                console.log('  - mainPlot:', parsed.mainPlot ? '✓ 有值' : '✗ 为空');
                console.log('  - storyStructure:', parsed.storyStructure ? '✓ 有值' : '✗ 为空');
                console.log('  - worldSettings:', parsed.worldSettings ? '✓ 有值' : '✗ 为空', parsed.worldSettings ? JSON.stringify(parsed.worldSettings) : '');
                console.log('  - coreConflicts:', parsed.coreConflicts ? '✓ 有值，数量=' + parsed.coreConflicts.length : '✗ 为空');
                console.log('  - climaxes:', parsed.climaxes ? '✓ 有值，数量=' + parsed.climaxes.length : '✗ 为空');
                console.log('  - characterNetwork:', parsed.characterNetwork ? '✓ 有值，数量=' + parsed.characterNetwork.length : '✗ 为空');
                console.log('  - subPlots:', parsed.subPlots ? '✓ 有值，数量=' + parsed.subPlots.length : '✗ 为空');
                console.log('  - foreshadowings:', parsed.foreshadowings ? '✓ 有值，数量=' + parsed.foreshadowings.length : '✗ 为空');
                console.log('═══════════════════════════════════════════════════════════════');
                // 辅助函数：从字符串中提取章节号（如"第1章" -> 1）
                const extractChapterNumber = (val) => {
                  if (val == null) return null;
                  const str = String(val);
                  const match = str.match(/第?\s*(\d+)\s*章/);
                  if (match) return parseInt(match[1], 10);
                  const num = parseInt(str, 10);
                  return isNaN(num) ? null : num;
                };
                // 宽松提取支线：有章节号保留章节号，没有也保留（避免过滤掉有效数据）
                const extractedSubPlots = (parsed.subPlots || [])
                  .filter(sp => sp.title)  // 至少要有标题
                  .map((sp, idx) => {
                    // 兼容 AI 返回的章节号字段格式
                    const startChapter = sp.startChapter || sp.startChapterNumber || null;
                    const endChapter = sp.endChapter || sp.endChapterNumber || null;
                    return {
                      id: sp.id || 'sp_' + Date.now() + '_' + idx,
                      title: sp.title || '',
                      phase: sp.phase || '',  // 所属阶段
                      startChapterNumber: extractChapterNumber(startChapter),
                      endChapterNumber: extractChapterNumber(endChapter),
                      coreConflict: sp.coreConflict || '',  // 核心冲突
                      mainPlotRelation: sp.mainPlotRelation || '',  // 与主线关系
                      description: sp.description || '',  // 完整描述
                      status: sp.status || 'pending',
                      priority: sp.priority || 1,
                      relatedChapterIds: Array.isArray(sp.relatedChapterIds) ? sp.relatedChapterIds : [],
                      relatedCharacterIds: Array.isArray(sp.relatedCharacters) ? sp.relatedCharacters : []
                    };
                  });
                // 宽松提取伏笔：有章节号保留章节号，没有也保留
                const extractedForeshadowings = (parsed.foreshadowings || [])
                  .filter(fs => fs.title)  // 至少要有标题
                  .map((fs, idx) => ({
                    id: 'fs_' + Date.now() + '_' + idx,
                    title: fs.title || '',
                    description: fs.description || '',
                    plantedChapterNumber: extractChapterNumber(fs.plantedChapter),
                    advanceChapterNumbers: Array.isArray(fs.advanceChapters)
                      ? fs.advanceChapters.map(n => extractChapterNumber(n)).filter(n => n != null)
                      : [],
                    revealChapterNumber: extractChapterNumber(fs.revealChapter),
                    status: 'pending',
                    clue: fs.clue || '',
                    clueDistribution: fs.clueDistribution || '',
                    revealMethod: fs.revealMethod || '',
                    relatedForeshadowings: Array.isArray(fs.relatedForeshadowings) ? fs.relatedForeshadowings : [],
                    relatedCharacterIds: []
                  }));
                // 解析世界观设定
                const extractedWorldSettings = parsed.worldSettings
                  ? {
                      background: parsed.worldSettings.background || '',
                      levelSystem: parsed.worldSettings.levelSystem || '',
                      factions: Array.isArray(parsed.worldSettings.factions) ? parsed.worldSettings.factions : [],
                      rules: Array.isArray(parsed.worldSettings.rules) ? parsed.worldSettings.rules : [],
                    }
                  : prevOutline?.worldSettings || { background: '', levelSystem: '', factions: [], rules: [] };
                console.log('[大纲解析] extractedWorldSettings:', JSON.stringify(extractedWorldSettings, null, 2));
                // 解析核心冲突
                const extractedCoreConflicts = (parsed.coreConflicts || [])
                  .filter(cc => cc.title || cc.description)
                  .map((cc, idx) => ({
                    id: 'cc_' + Date.now() + '_' + idx,
                    title: cc.title || '',
                    description: cc.description || '',
                    relatedCharacterIds: Array.isArray(cc.relatedCharacters) ? cc.relatedCharacters : []
                  }));
                console.log('[大纲解析] extractedCoreConflicts 数量:', extractedCoreConflicts.length, extractedCoreConflicts);
                // 解析高潮设计
                const extractedClimaxes = (parsed.climaxes || [])
                  .filter(cl => cl.title || cl.description)
                  .map((cl, idx) => ({
                    id: 'clx_' + Date.now() + '_' + idx,
                    title: cl.title || '',
                    description: cl.description || '',
                    chapterRange: cl.chapterRange || { start: null, end: null }
                  }));
                console.log('[大纲解析] extractedClimaxes 数量:', extractedClimaxes.length, extractedClimaxes);
                // 解析角色关系网络
                const extractedCharacterNetwork = (parsed.characterNetwork || [])
                  .filter(cn => cn.from && cn.to)
                  .map((cn, idx) => ({
                    id: 'cn_' + Date.now() + '_' + idx,
                    from: cn.from || '',
                    to: cn.to || '',
                    relationship: cn.relationship || '',
                    type: cn.type || 'relationship'
                  }));
                console.log('[大纲解析] extractedCharacterNetwork 数量:', extractedCharacterNetwork.length, extractedCharacterNetwork);

                if (prevOutline) {
                  const parsedBody = String(outlineParseContent ?? '').trim();
                  const contentToKeep = parsedBody || String(prevOutline.content ?? '').trim() || '';
                  const extractedStoryStructure = parsed.storyStructure
                    ? {
                        beginning: parsed.storyStructure.beginning || '',
                        development: parsed.storyStructure.development || '',
                        climax: parsed.storyStructure.climax || '',
                        ending: parsed.storyStructure.ending || '',
                      }
                    : prevOutline.storyStructure || { beginning: '', development: '', climax: '', ending: '' };
                  const updated = {
                    ...prevOutline,
                    content: contentToKeep,
                    mainPlot: { ...prevOutline.mainPlot, storyline: parsed.mainPlot || prevOutline.mainPlot?.storyline || '', description: parsed.mainPlot || prevOutline.mainPlot?.description || '' },
                    storyStructure: extractedStoryStructure,
                    subPlots: extractedSubPlots,
                    foreshadowing: extractedForeshadowings,
                    worldSettings: extractedWorldSettings,
                    coreConflicts: extractedCoreConflicts,
                    climaxes: extractedClimaxes,
                    characterNetwork: extractedCharacterNetwork,
                    updatedAt: new Date().toISOString(),
                  };
                  console.log('[大纲解析] 即将保存到数据库的完整数据:');
                  console.log('[大纲解析] - mainPlot:', JSON.stringify(updated.mainPlot));
                  console.log('[大纲解析] - storyStructure:', JSON.stringify(updated.storyStructure));
                  console.log('[大纲解析] - worldSettings:', JSON.stringify(updated.worldSettings));
                  console.log('[大纲解析] - coreConflicts:', JSON.stringify(updated.coreConflicts));
                  console.log('[大纲解析] - climaxes:', JSON.stringify(updated.climaxes));
                  console.log('[大纲解析] - characterNetwork:', JSON.stringify(updated.characterNetwork));
                  console.log('[大纲解析] - subPlots 数量:', updated.subPlots.length);
                  console.log('[大纲解析] - foreshadowing 数量:', updated.foreshadowing.length);
                  // 解析弹窗内直接基于当前状态合并，避免依赖已移除的 outlinesRef
                  // 关键修复：先获取当前最新的 outliens 状态，再与解析结果合并
                  const baseOutline = (outlines && outlines.length > 0 ? outlines[0] : null) || prevOutline;
                  // 深度合并：保留 baseOutline 中已有的 subPlots、foreshadowing 等关键数据，
                  // 但用 updated 中的数据覆盖（如果 updated 有值的话）
                  const latestOutline = {
                    ...(baseOutline || {}),
                    ...updated,
                    // 明确保留数组类型字段，避免被 undefined 覆盖
                    subPlots: updated.subPlots !== undefined ? updated.subPlots : (baseOutline?.subPlots || []),
                    foreshadowing: updated.foreshadowing !== undefined ? updated.foreshadowing : (baseOutline?.foreshadowing || []),
                    mainPlot: updated.mainPlot || baseOutline?.mainPlot || { storyline: '', description: '' },
                    storyStructure: updated.storyStructure || baseOutline?.storyStructure || { beginning: '', development: '', climax: '', ending: '' },
                    worldSettings: updated.worldSettings || baseOutline?.worldSettings || { background: '', levelSystem: '', factions: [] },
                    coreConflicts: updated.coreConflicts !== undefined ? updated.coreConflicts : (baseOutline?.coreConflicts || []),
                    climaxes: updated.climaxes !== undefined ? updated.climaxes : (baseOutline?.climaxes || []),
                    characterNetwork: updated.characterNetwork !== undefined ? updated.characterNetwork : (baseOutline?.characterNetwork || []),
                  };
                  // 打印合并后的数据，确保 subPlots 不为空
                  console.log('[大纲解析] 合并后 subPlots:', JSON.stringify(latestOutline.subPlots));
                  console.log('[大纲解析] 合并后 foreshadowing:', JSON.stringify(latestOutline.foreshadowing));
                  try {
                    const saveResp = await apiClient.post(`/assist/novels/${assistNovelId}/outline`, latestOutline);
                    console.log('[大纲解析] 保存响应 success=', saveResp.data?.success, ', subPlots=', saveResp.data?.data?.subPlots?.length, ', foreshadowing=', saveResp.data?.data?.foreshadowing?.length);
                    if (saveResp.data?.success && saveResp.data.data) {
                      // 关键修复：用后端返回的完整数据更新状态，确保前端与后端完全一致
                      // 但如果后端返回的 subPlots 为空或 undefined，保留本地的数据
                      const savedOutline = saveResp.data.data;
                      const finalOutline = {
                        ...savedOutline,
                        // 如果后端返回的 subPlots 为空但本地有数据，保留本地数据
                        subPlots: (savedOutline.subPlots && savedOutline.subPlots.length > 0)
                          ? savedOutline.subPlots
                          : (latestOutline.subPlots || []),
                        foreshadowing: (savedOutline.foreshadowing && savedOutline.foreshadowing.length > 0)
                          ? savedOutline.foreshadowing
                          : (latestOutline.foreshadowing || []),
                      };
                      setOutlines([finalOutline]);
                      console.log('[大纲解析] ✅ 大纲已保存到数据库，包括主线剧情、四阶段、支线、伏笔、世界观、核心冲突、高潮设计、角色关系');
                      console.log('[大纲解析] 最终保存的 subPlots 数量:', finalOutline.subPlots.length);
                    } else {
                      console.warn('[大纲解析] ⚠️ 保存响应无data，使用前端数据');
                      setOutlines([latestOutline]);
                    }
                  } catch (e) {
                    console.error('[大纲解析] ❌ 保存大纲失败:', e);
                    setOutlines([latestOutline]);
                  }
                }
                const storyStructOk = !!(parsed.storyStructure?.beginning || parsed.storyStructure?.development || parsed.storyStructure?.climax || parsed.storyStructure?.ending);
                setSnackbar({ open: true, message: `解析完成！已提取主线${storyStructOk ? '、故事四阶段' : ''}、${extractedSubPlots.length} 条支线、${extractedForeshadowings.length} 条伏笔${extractedWorldSettings?.background ? '、世界观设定' : ''}${extractedCoreConflicts.length > 0 ? '、' + extractedCoreConflicts.length + ' 条核心冲突' : ''}${extractedClimaxes.length > 0 ? '、' + extractedClimaxes.length + ' 个高潮设计' : ''}${extractedCharacterNetwork.length > 0 ? '、' + extractedCharacterNetwork.length + ' 条角色关系' : ''}`, severity: 'success' });
              } else {
                setSnackbar({ open: true, message: '解析结果格式有误，请重试', severity: 'warning' });
              }
            } catch (err) {
              console.error('AI解析失败:', err);
              setSnackbar({ open: true, message: 'AI解析失败，请重试', severity: 'error' });
            } finally {
              setParsingOutline(false);
              setOutlineParseProgressText('');
              setShowOutlineParseDialog(false);
              setOutlineParseContent('');
            }
          }}
          className={`workspace-btn-cyber${parsingOutline ? ' outline-parse-primary-loading' : ''}`}
          sx={{
            bgcolor: 'transparent',
            color: '#38bdf8',
            border: '1px solid rgba(56,189,248,0.5)',
            fontSize: 14,
            fontWeight: 500,
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              bgcolor: 'rgba(56,189,248,0.1)',
              borderColor: '#38bdf8'
            },
            '&.Mui-disabled': {
              color: 'rgba(56,189,248,0.5)',
              borderColor: 'rgba(56,189,248,0.3)',
              opacity: 1,
            },
          }}
        >
          {parsingOutline ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
              <CircularProgress size={20} thickness={5} sx={{ color: '#38bdf8' }} />
              <motion.div
                animate={{ opacity: [0.65, 1, 0.65] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                解析中…
              </motion.div>
            </Box>
          ) : (
            '开始解析'
          )}
        </Button>
      </DialogActions>
    </Dialog>
    );
  };

  // AI解析角色弹窗：从大纲中提取角色信息（解析中与「AI 解析大纲」共用同一套输入区 overlay 动效）
  const renderCharacterParseDialog = () => {
    const characterParseChips = [
      { label: '主角', sx: { bgcolor: 'rgba(0,240,255,0.2)', color: '#00f0ff', border: '1px solid rgba(0,240,255,0.45)' } },
      { label: '配角', sx: { bgcolor: 'rgba(var(--ws-accent-rgb),0.2)', color: 'var(--ws-accent)', border: '1px solid rgba(var(--ws-accent-rgb),0.42)' } },
      { label: '反派', sx: { bgcolor: 'rgba(239,68,68,0.2)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.45)' } },
      { label: 'NPC', sx: { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)' } },
    ];
    return (
    <Dialog
      open={showCharacterParseDialog}
      onClose={() => {
        if (parsingCharacter) return;
        setShowCharacterParseDialog(false);
        setCharacterParseContent('');
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={parsingCharacter}
      TransitionProps={{ timeout: { enter: 320, exit: 220 } }}
      className={isChaptersNarrow ? 'character-parse-dialog-above-tooltray' : undefined}
      sx={isChaptersNarrow ? { zIndex: 1600 } : undefined}
      PaperProps={{
        className: `character-panel-cyber${parsingCharacter ? ' outline-parse-dialog-parsing' : ''}${isMobile ? ' character-parse-dialog-mobile-paper' : ''}`,
        sx: {
          bgcolor: 'rgba(10, 11, 18, 0.98)',
          backgroundImage: 'none',
          border: '1px solid rgba(0,240,255,0.3)',
          borderRadius: isMobile ? 0 : 2,
          boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 120px rgba(0,240,255,0.15)',
          overflow: 'hidden',
          ...(isMobile
            ? {
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                maxHeight: '100%',
                m: 0,
                maxWidth: '100%',
              }
            : {
                maxHeight: '80vh',
              }),
        }
      }}
    >
      <DialogTitle
        className="character-panel-title-glow"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          color: '#00f0ff !important',
          borderBottom: '1px solid rgba(0,240,255,0.2)',
          py: { xs: 1, sm: 1.5 },
          px: { xs: 0.75, sm: 2 },
          flexShrink: 0,
          ...(isMobile
            ? {
                textTransform: 'none',
                letterSpacing: '0.04em',
                fontSize: '0.875rem !important',
              }
            : {}),
        }}
      >
        {isMobile && (
          <IconButton
            className="character-panel-back-btn"
            onClick={() => {
              if (parsingCharacter) return;
              setShowCharacterParseDialog(false);
              setCharacterParseContent('');
            }}
            disabled={parsingCharacter}
            edge="start"
            aria-label="返回"
            sx={{ color: 'rgba(255,255,255,0.88)', flexShrink: 0, ml: -0.5, mr: 0.25 }}
          >
            <ArrowBackIcon sx={{ fontSize: 22 }} />
          </IconButton>
        )}
        <motion.div
          style={{ display: 'inline-block', fontSize: isMobile ? 16 : 18 }}
          animate={parsingCharacter ? { rotate: [0, 18, -18, 0], scale: [1, 1.15, 1] } : { rotate: 0, scale: 1 }}
          transition={parsingCharacter ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
        >
          *
        </motion.div>
        <motion.div
          initial={false}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          style={{ flex: 1, minWidth: 0, lineHeight: 1.35, display: 'block' }}
        >
          AI 解析角色（从大纲提取）
        </motion.div>
      </DialogTitle>
      <DialogContent
        className={`character-panel-cyber${isMobile ? ' character-parse-dialog-mobile-content' : ''}`}
        sx={{
          p: 0,
          overflow: 'hidden',
          ...(isMobile
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
            : {}),
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={isMobile ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' } : undefined}
        >
        <Box
          className={`character-panel-cyber${isMobile ? ' character-parse-dialog-scroll' : ''}`}
          sx={{
            p: { xs: 1, sm: 2 },
            px: { xs: 0.75, sm: 2 },
            overflow: 'auto',
            ...(isMobile
              ? { flex: 1, minHeight: 0, maxHeight: 'none' }
              : { maxHeight: 'calc(80vh - 80px)' }),
          }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: { xs: 10.5, sm: 12 }, lineHeight: 1.45, mb: { xs: 1, sm: 1.5 } }}>
            系统将自动分析下方的「大纲内容」，提取其中的角色信息并添加到角色库：
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              mb: 2,
              mt: 0.25,
              py: 0.75,
              overflow: 'visible',
              alignItems: 'center',
            }}
          >
            {characterParseChips.map((c, idx) => {
              const chipActive = parsingCharacter && characterParsePhaseIdx === idx;
              return (
              <motion.div
                key={c.label}
                style={{ borderRadius: 9999 }}
                initial={{ opacity: 0, y: 10, scale: 0.92 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: chipActive ? 1.06 : 1,
                  boxShadow: chipActive
                    ? '0 0 22px rgba(34, 211, 238, 0.45), 0 0 36px rgba(251, 191, 36, 0.2)'
                    : 'none',
                }}
                transition={{
                  delay: 0.05 * idx,
                  type: 'spring',
                  stiffness: 420,
                  damping: 26,
                  scale: { duration: 0.35 },
                }}
              >
                <Chip
                  label={c.label}
                  size="small"
                  sx={{ ...c.sx, fontWeight: 600, borderRadius: 9999 }}
                />
              </motion.div>
              );
            })}
          </Box>
          <Box sx={{ position: 'relative', mb: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={12}
              value={characterParseContent}
              disabled={parsingCharacter}
              onChange={(e) => setCharacterParseContent(e.target.value)}
              placeholder="请确保大纲内容在此框内，系统将分析并提取角色信息..."
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.25)',
                  fontSize: 13,
                  color: '#fff',
                  transition: 'box-shadow 0.35s ease',
                  '& fieldset': { borderColor: 'rgba(0,240,255,0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(0,240,255,0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#00f0ff' },
                  ...(parsingCharacter ? {
                    boxShadow: 'inset 0 0 28px rgba(34, 211, 238, 0.12)',
                  } : {}),
                },
                '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
              }}
            />
            <AnimatePresence>
              {parsingCharacter && (
                <motion.div
                  className="outline-parse-field-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="outline-parse-shimmer-grid" aria-hidden />
                  <div className="outline-parse-scan-line" aria-hidden />
                  <div className="outline-parse-particles" aria-hidden />
                  <Box sx={{ position: 'absolute', left: 12, right: 12, bottom: 44, textAlign: 'center', pointerEvents: 'none' }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={characterParsePhaseIdx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        style={{
                          color: 'rgba(0,240,255,0.75)',
                          fontSize: 13,
                          fontWeight: 500,
                          letterSpacing: '0.04em',
                        }}
                      >
                        {CHARACTER_PARSE_PHASE_LABELS[characterParsePhaseIdx]}
                      </motion.div>
                    </AnimatePresence>
                  </Box>
                  <LinearProgress
                    className="outline-parse-linear-progress"
                    variant="indeterminate"
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      borderRadius: 0,
                      bgcolor: 'rgba(0,0,0,0.35)',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.35 }}
          >
          <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: 'rgba(0,240,255,0.05)', borderRadius: 1, border: '1px solid rgba(0,240,255,0.15)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: { xs: 10, sm: 11 }, lineHeight: 1.65 }}>
              <strong>提取内容：</strong>系统会分析大纲，提取角色的：姓名、性别、身份/定位、性格特点、角色定位（主角/配角/反派/NPC）、与主角关系、出场背景等。<br /><br />
              <strong>使用场景：</strong>如果大纲中已经描述了很多人设，想快速批量生成角色库条目，可以点此按钮。<br /><br />
              <strong>结果：</strong>解析后的角色会直接添加到角色库，可在角色面板查看和编辑。
            </Typography>
          </Box>
          </motion.div>
        </Box>
        </motion.div>
      </DialogContent>
      <DialogActions
        className={isMobile ? 'character-parse-dialog-mobile-actions' : undefined}
        sx={{
          p: { xs: 1, sm: 2 },
          px: { xs: 0.75, sm: 2 },
          pt: { xs: 1, sm: 2 },
          pb: { xs: 'max(10px, env(safe-area-inset-bottom, 0px))', sm: 2 },
          borderTop: '1px solid rgba(0,240,255,0.2)',
          gap: { xs: 1, sm: 1.5 },
          flexShrink: 0,
          bgcolor: 'transparent',
        }}
      >
        <Button
          disabled={parsingCharacter}
          onClick={() => { setShowCharacterParseDialog(false); setCharacterParseContent(''); }}
          className="workspace-btn-cyber"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(255,255,255,0.22)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            flex: 1,
            minWidth: 120
          }}
        >
          取消
        </Button>
        <Button
          variant="outlined"
          disabled={parsingCharacter}
          onClick={async () => {
            if (!characterParseContent.trim()) {
              setSnackbar({ open: true, message: '请确保大纲内容不为空', severity: 'warning' });
              return;
            }
            setCharacterParsePhaseIdx(0);
            setParsingCharacter(true);
            // 真实 AI 解析角色（包含关系）
            try {
              const parsePrompt = `你是一个小说角色分析助手。请仔细阅读下面的小说大纲，提取其中的角色信息和角色关系。

请严格按照以下JSON格式输出（只需输出JSON，不要其他内容）：
{
  "characters": [
    {
      "name": "角色姓名（必须准确）",
      "gender": "性别（男/女/未知）",
      "title": "称号/头衔",
      "level": "境界/等级",
      "realm": "势力/门派",
      "description": "角色简介（简要描述）",
      "appearance": "外貌特征（身高、发型、服装特点等）",
      "personality": "性格特点（内向、外向、阴险等）",
      "goal": "目标/追求（复仇、成为强者、守护等）",
      "abilities": "能力/特长（剑术、炼丹、阵法等）",
      "equipment": "武器/装备",
      "secret": "秘密/把柄（不为人知的秘密）",
      "background": "角色背景（过往经历、出身等）"
    }
  ],
  "relations": [
    {
      "from": "角色A姓名（必须与上方完全一致）",
      "to": "角色B姓名（必须与上方完全一致）",
      "type": "关系类型（师徒/父子/兄弟/姐妹/恋人/夫妻/仇敌/朋友/主仆/同门/对手/其他）",
      "description": "关系描述（简要说明他们是什么关系）"
    }
  ]
}

重要要求：
1. characters数组中每个角色的name必须准确，这是建立关系的基础
2. relations数组中的from和to必须与characters中的name完全一致
3. 关系类型尽量使用预设类型：师徒、父子、母子、兄弟、姐妹、恋人、夫妻、仇敌、朋友、主仆、同门、对手、盟友、其他
4. 如果大纲中没有提到关系，可以不输出relations或输出空数组
5. 每个角色都要尽可能填写所有字段，如果没有相关信息则为空字符串
6. 角色定位（主角/配角/反派/NPC）写入title字段
7. 性别必须明确写出：男、女或未知

以下是小说大纲内容：
${characterParseContent}`;

              const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
              
              const response = await apiClient.post('/assist/ai/proxy-ai', {
                messages: [
                  { role: 'system', content: '你是一个专业的小说角色分析助手，擅长从大纲中提取角色信息。请尽可能详细地提取每个角色的所有信息字段。' },
                  { role: 'user', content: parsePrompt }
                ],
                model: 'deepseek-v4-flash',
                extra_body: {},
                temperature: 0.3,
                max_tokens: 32000,
                stream: false,
                billingInfo: {
                  novelName: currentNovelName,
                  chapterNumber: '',
                  operationType: 'AI解析角色'
                }
              });

              const aiData = response.data;
              let content = '';
              if (aiData && aiData.choices && aiData.choices[0] && aiData.choices[0].message) {
                content = aiData.choices[0].message.content;
              } else if (aiData && aiData.success && aiData.data) {
                content = aiData.data.content || aiData.data;
              }

              console.log('[AI角色解析] 返回内容长度:', content.length, '字符');

              // 解析 JSON（🔥 robustJsonParse 多级容错：平衡括号提取 + 尾逗号 + 截断闭合 + 未转义换行 + 逐对象兜底）
              const charParseResult = robustJsonParse(content);
              let parsed = (charParseResult.ok && charParseResult.data) ? charParseResult.data : null;
              if (charParseResult.ok && charParseResult.strategy !== 'direct') {
                console.log('[AI角色解析] ✅ 容错解析成功（策略:', charParseResult.strategy, '，原始', content.length, '字）');
              }
              if (!charParseResult.ok) {
                console.error('[AI角色解析] ❌ 解析失败（策略:', charParseResult.strategy, '）');
              }

              if (!parsed) {
                setSnackbar({ open: true, message: 'AI解析失败（内容被截断），请重试或减少大纲字数', severity: 'error' });
              } else {
                const extractedCharacters = (parsed.characters || []).map((char, idx) => {
                // 根据角色定位确定颜色和标签
                let color = '#94a3b8';
                let tags = [];
                const title = char.title || '';
                const level = char.level || '';

                if (title.includes('主角') || title.includes('男主') || title.includes('女主') || level.includes('主角')) {
                  color = '#00ff9f';
                  tags = ['主角'];
                } else if (title.includes('反派') || title.includes('boss') || title.includes('敌人') || level.includes('反派')) {
                  color = '#ff4444';
                  tags = ['反派'];
                } else if (title.includes('配角') || title.includes('伙伴') || title.includes('弟子') || level.includes('配角')) {
                  color = '#ff6b35';
                  tags = ['配角'];
                } else if (title.includes('导师') || title.includes('师父') || level.includes('导师')) {
                  color = 'var(--ws-accent)';
                  tags = ['导师'];
                } else if (title.includes('恋人') || title.includes('女友') || title.includes('男友') || level.includes('恋人')) {
                  color = '#ec4899';
                  tags = ['恋人'];
                } else if (title.includes('友人') || title.includes('朋友')) {
                  color = '#3b82f6';
                  tags = ['友人'];
                } else if (title.includes('宿敌') || title.includes('仇人')) {
                  color = '#ef4444';
                  tags = ['宿敌'];
                } else {
                  color = '#ffd700';
                  tags = ['NPC'];
                }

                return {
                  _id: 'char_parsed_' + Date.now() + '_' + idx,
                  name: char.name || '',
                  gender: char.gender || '未知',
                  title: char.title || '',
                  level: char.level || '',
                  realm: char.realm || '',
                  description: char.description || '',
                  appearance: char.appearance || '',
                  personality: char.personality || '',
                  goal: char.goal || '',
                  abilities: char.abilities || '',
                  equipment: char.equipment || '',
                  secret: char.secret || '',
                  background: char.background || '',
                  color: color,
                  tags: tags,
                  relations: [],
                  novelId: novelId,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                };
              });

                // 【新增】建立角色关系映射
                const parsedRelations = parsed.relations || [];
                if (parsedRelations.length > 0 && extractedCharacters.length > 0) {
                  // 创建角色名到ID的映射
                  const nameToIdMap = {};
                  extractedCharacters.forEach(char => {
                    if (char.name) {
                      nameToIdMap[char.name] = char._id;
                    }
                  });
                  
                  // 为每个角色添加关系
                  extractedCharacters.forEach(char => {
                    const charRelations = [];
                    parsedRelations.forEach(rel => {
                      // 检查 from 或 to 是否是这个角色
                      const isFromThis = rel.from === char.name;
                      const isToThis = rel.to === char.name;
                      
                      if (isFromThis || isToThis) {
                        const otherName = isFromThis ? rel.to : rel.from;
                        const otherId = nameToIdMap[otherName];
                        
                        if (otherId) {
                          charRelations.push({
                            id: 'rel_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
                            characterId: otherId,
                            name: otherName,
                            type: rel.type || '其他',
                            description: rel.description || ''
                          });
                        }
                      }
                    });
                    
                    if (charRelations.length > 0) {
                      char.relations = charRelations;
                    }
                  });
                }

                if (extractedCharacters.length > 0) {
                  const relationCount = extractedCharacters.reduce((sum, char) => sum + (char.relations?.length || 0), 0) / 2;
                  if (creationType === 'assist') {
                    // 【辅助创作】批量保存角色到数据库
                    try {
                      console.log('【辅助创作】开始保存角色到数据库');
                      const saveResponse = await apiClient.post(`/assist/novels/${assistNovelId}/characters/batch`, {
                        characters: extractedCharacters
                      });
                      console.log('【辅助创作】保存响应:', JSON.stringify(saveResponse.data).substring(0, 500));
                      
                      if (saveResponse.data && saveResponse.data.success) {
                        const savedChars = saveResponse.data.data || [];
                        console.log('【辅助创作】保存成功，返回角色数:', savedChars.length);
                        setCharacters(savedChars);

                        // ── 将角色初始关系写入全局进度（relationshipChapterStates）───────────
                        try {
                          const charNameToId = {};
                          savedChars.forEach(c => { charNameToId[c.name] = c._id; });

                          const initialRelEntries = [];
                          extractedCharacters.forEach(char => {
                            (char.relations || []).forEach(rel => {
                              const fromId = charNameToId[rel.from];
                              const toId = charNameToId[rel.to];
                              if (!fromId || !toId) return;
                              // 避免重复（双向关系只存一个方向即可）
                              if (initialRelEntries.some(e => e.fromCharacterId === fromId && e.toCharacterId === toId)) return;
                              initialRelEntries.push({
                                fromCharacterId: fromId,
                                fromCharacterName: rel.from,
                                toCharacterId: toId,
                                toCharacterName: rel.to,
                                relationType: rel.type || '其他',
                                chapterNumber: 0,
                                chapterId: '',
                                fromStatus: '初始关系',
                                toStatus: '初始关系',
                                reason: rel.description || '故事开篇设定',
                                updatedAt: new Date()
                              });
                            });
                          });

                          if (initialRelEntries.length > 0) {
                            const existing = progressData?.relationshipChapterStates || [];
                            const deduped = [
                              ...existing,
                              ...initialRelEntries.filter(ne =>
                                !existing.some(e => e.fromCharacterId === ne.fromCharacterId && e.toCharacterId === ne.toCharacterId && e.chapterNumber === 0)
                              )
                            ];
                            setProgressData(prev => prev ? { ...prev, relationshipChapterStates: deduped } : prev);
                            await saveProgressData('relationshipChapterStates', deduped);
                            console.log(`[全局进度] ✅ 角色初始关系已写入（${initialRelEntries.length}条）`);
                          }
                        } catch (relErr) {
                          console.warn('[全局进度] 写入初始关系失败（非致命）:', relErr);
                        }

                        setSnackbar({ open: true, message: `解析完成！已提取 ${savedChars.length} 个角色、${Math.round(relationCount)} 条关系`, severity: 'success' });
                      } else {
                        console.error('【辅助创作】保存失败:', saveResponse.data);
                        setSnackbar({ open: true, message: '保存失败：' + (saveResponse.data?.message || '未知错误'), severity: 'error' });
                      }
                    } catch (saveError) {
                      console.error('【辅助创作】保存角色失败:', saveError);
                      setCharacters([...characters, ...extractedCharacters]);
                      setSnackbar({ open: true, message: `解析完成！但保存失败：${saveError.message}`, severity: 'warning' });
                    }
                  } else {
                    // 【全自动写作】仅保存到内存
                    setCharacters([...characters, ...extractedCharacters]);
                    setSnackbar({ open: true, message: `解析完成！已提取 ${extractedCharacters.length} 个角色、${Math.round(relationCount)} 条关系`, severity: 'success' });
                  }
                } else {
                  setSnackbar({ open: true, message: '未能从大纲中提取到角色信息', severity: 'warning' });
                }
              }
            } catch (err) {
                console.error('AI解析角色失败:', err);
                setSnackbar({ open: true, message: 'AI解析失败，请重试', severity: 'error' });
              } finally {
                setParsingCharacter(false);
                setShowCharacterParseDialog(false);
                setCharacterParseContent('');
              }
            }}
            className={`workspace-btn-cyber${parsingCharacter ? ' outline-parse-primary-loading' : ''}`}
          sx={{
            bgcolor: 'transparent',
            color: '#38bdf8',
            border: '1px solid rgba(56,189,248,0.5)',
            fontSize: 14,
            fontWeight: 500,
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              bgcolor: 'rgba(56,189,248,0.1)',
              borderColor: '#38bdf8'
            },
            '&.Mui-disabled': {
              color: 'rgba(56,189,248,0.5)',
              borderColor: 'rgba(56,189,248,0.3)',
              opacity: 1,
            },
          }}
        >
          {parsingCharacter ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
              <CircularProgress size={20} thickness={5} sx={{ color: '#38bdf8' }} />
              <motion.div
                animate={{ opacity: [0.65, 1, 0.65] }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                解析中…
              </motion.div>
            </Box>
          ) : (
            '开始解析'
          )}
        </Button>
      </DialogActions>
    </Dialog>
    );
  };

  // AI解析角色关系弹窗：从大纲中提取角色关系信息
  const renderRelationParseDialog = () => (
    <Dialog
      open={showRelationParseDialog}
      onClose={() => { setShowRelationParseDialog(false); setRelationParseContent(''); }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'character-panel-cyber',
        sx: {
          bgcolor: 'rgba(10, 11, 18, 0.98)',
          backgroundImage: 'none',
          border: '1px solid rgba(var(--ws-accent-rgb),0.3)',
          borderRadius: 2,
          maxHeight: '80vh',
          boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 120px rgba(var(--ws-accent-rgb),0.15)'
        }
      }}
    >
      <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--ws-accent) !important', borderBottom: '1px solid rgba(var(--ws-accent-rgb),0.2)', py: 1.5, flexShrink: 0 }}>
        <span style={{ fontSize: 18 }}></span> AI 解析角色关系（从大纲提取）
      </DialogTitle>
      <DialogContent className="character-panel-cyber" sx={{ p: 0, overflow: 'hidden' }}>
        <Box className="character-panel-cyber" sx={{ p: 2, overflow: 'auto', maxHeight: 'calc(80vh - 80px)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 1.5 }}>
            系统将自动分析下方的「大纲内容」，提取角色之间的关系并赋值到对应角色：
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label="恋人" size="small" sx={{ bgcolor: 'rgba(236,72,153,0.2)', color: '#ec4899' }} />
            <Chip label="友人" size="small" sx={{ bgcolor: 'rgba(59,130,246,0.2)', color: '#3b82f6' }} />
            <Chip label="导师" size="small" sx={{ bgcolor: 'rgba(var(--ws-accent-rgb),0.2)', color: 'var(--ws-accent)' }} />
            <Chip label="宿敌" size="small" sx={{ bgcolor: 'rgba(239,68,68,0.2)', color: '#ef4444' }} />
            <Chip label="NPC" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }} />
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={12}
            value={relationParseContent}
            onChange={(e) => setRelationParseContent(e.target.value)}
            placeholder="请确保大纲内容在此框内，系统将分析并提取角色关系信息..."
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0,0,0,0.25)',
                fontSize: 13,
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(var(--ws-accent-rgb),0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(var(--ws-accent-rgb),0.5)' },
                '&.Mui-focused fieldset': { borderColor: 'var(--ws-accent)' }
              },
              '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' }
            }}
          />
          <Box sx={{ p: 2, bgcolor: 'rgba(var(--ws-accent-rgb),0.05)', borderRadius: 1, border: '1px solid rgba(var(--ws-accent-rgb),0.15)' }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 11, lineHeight: 1.7 }}>
              <strong>提取内容：</strong>系统会分析大纲，提取角色之间的关系：谁是谁的恋人、友人、导师、宿敌等。<br /><br />
              <strong>使用场景：</strong>如果大纲中描述了角色之间的关系（如「主角与女主是恋人」「反派是主角的宿敌」），可以点此按钮批量解析。<br /><br />
              <strong>结果：</strong>解析后的关系会直接赋值到对应角色的「角色关系网」中，可在角色面板查看。
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(var(--ws-accent-rgb),0.2)', gap: 1.5, flexShrink: 0, bgcolor: 'transparent' }}>
        <Button
          onClick={() => { setShowRelationParseDialog(false); setRelationParseContent(''); }}
          className="workspace-btn-cyber"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(255,255,255,0.22)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            flex: 1,
            minWidth: 120
          }}
        >
          取消
        </Button>
        <Button
          variant="outlined"
          onClick={async () => {
            if (!relationParseContent.trim()) {
              setSnackbar({ open: true, message: '请确保大纲内容不为空', severity: 'warning' });
              return;
            }
            if (characters.length < 2) {
              setSnackbar({ open: true, message: '至少需要2个角色才能解析关系', severity: 'warning' });
              return;
            }
            setParsingRelation(true);
            try {
              const currentNovelName = novel?.title || currentOutline?.name || '未知项目';
              const charNames = characters.map(c => c.name).join('、');

              // ══════════════════════════════════════════════════════════════════
              // 🔥 分段解析关系（1000 章大纲上百条关系，单次 max_tokens:2000 必截断）
              // 按 ~1 万字段落切分，逐段提取关系后合并。
              // ══════════════════════════════════════════════════════════════════
              const fullText = relationParseContent.trim();
              const segments = splitOutlineText(fullText, 10000);
              console.log(`[关系解析] 全文 ${fullText.length} 字，切分为 ${segments.length} 段`);

              let allRelations = [];
              for (let i = 0; i < segments.length; i++) {
                console.log(`[关系解析] 段 ${i + 1}/${segments.length}（${segments[i].length} 字）`);
                const segPrompt = `你是小说角色关系分析助手。以下是小说大纲的第 ${i + 1} 段（共 ${segments.length} 段）。请提取该段中角色之间的关系。

请严格按照以下JSON格式输出（只需输出JSON，不要其他内容）：
{
  "relations": [
    { "character1": "角色1姓名", "character2": "角色2姓名", "relation": "关系类型（恋人、友人、导师、宿敌、NPC、其他）" }
  ]
}

要求：
1. 提取该段中所有明确描述的角色关系
2. character1 和 character2 必须是角色库中已存在的角色姓名（参考：${charNames}）
3. relation 必须是以下类型之一：恋人、友人、导师、宿敌、NPC、其他
4. 如果该段没有明确的关系描述，返回空数组
5. 如果角色不在角色库中，不要提取

以下是第 ${i + 1} 段大纲内容：
${segments[i]}`;

                const segResult = await callAiParseJson(
                  [
                    { role: 'system', content: '你是专业的角色关系分析助手。只输出JSON，不要任何其他内容。' },
                    { role: 'user', content: segPrompt }
                  ],
                  { novelName: currentNovelName, operationType: `AI解析关系-段${i + 1}`, maxTokens: 8000 }
                );
                if (segResult && segResult.relations) {
                  allRelations.push(...segResult.relations);
                }
              }

              console.log(`[关系解析] 合并完成：共 ${allRelations.length} 条关系`);

              // 🔥 后续处理：robustJsonParse 容错已在 callAiParseJson 内完成
              const parseResult = { ok: allRelations.length >= 0, data: { relations: allRelations }, strategy: 'segmented' };
              if (parseResult.ok && parseResult.data) {
                const parsed = parseResult.data;
                const relations = parsed.relations || [];
                
                let updatedCount = 0;
                const updatedCharacters = [...characters];
                
                relations.forEach(rel => {
                  const char1 = updatedCharacters.find(c => c.name === rel.character1);
                  const char2 = updatedCharacters.find(c => c.name === rel.character2);
                  
                  if (char1 && char2) {
                    // 为角色1添加与角色2的关系
                    const relId1 = 'rel_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                    if (!char1.relations) char1.relations = [];
                    char1.relations.push({
                      id: relId1,
                      characterId: char2._id,
                      name: char2.name,
                      type: rel.relation
                    });
                    updatedCount++;
                    
                    // 为角色2添加与角色1的关系（双向关系）
                    const relId2 = 'rel_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
                    if (!char2.relations) char2.relations = [];
                    char2.relations.push({
                      id: relId2,
                      characterId: char1._id,
                      name: char1.name,
                      type: rel.relation
                    });
                  }
                });

                setCharacters(updatedCharacters);
                
                // 【辅助创作】同时保存关系更新到数据库
                if (creationType === 'assist' && assistNovelId) {
                  try {
                    await Promise.all(updatedCharacters.map(async (char) => {
                      if (char.relations && char.relations.length > 0) {
                        await apiClient.put(`/assist/novels/${assistNovelId}/characters/${char._id}/relations`, {
                          relations: char.relations
                        });
                      }
                    }));
                  } catch (saveError) {
                    console.error('【辅助创作】保存角色关系失败:', saveError);
                  }
                }
                
                if (updatedCount > 0) {
                  setSnackbar({ open: true, message: `关系解析完成！已为 ${updatedCount} 对角色添加关系`, severity: 'success' });
                } else {
                  setSnackbar({ open: true, message: '未能从大纲中提取到明确的关系描述', severity: 'warning' });
                }
              } else {
                setSnackbar({ open: true, message: '解析结果格式有误，请重试', severity: 'warning' });
              }
            } catch (err) {
              console.error('AI解析关系失败:', err);
              setSnackbar({ open: true, message: 'AI解析失败，请重试', severity: 'error' });
            } finally {
              setParsingRelation(false);
              setShowRelationParseDialog(false);
              setRelationParseContent('');
            }
          }}
          className="workspace-btn-cyber"
          sx={{
            bgcolor: 'transparent',
            color: 'var(--ws-accent)',
            border: '1px solid rgba(var(--ws-accent-rgb),0.5)',
            fontSize: 14,
            fontWeight: 500,
            flex: 1,
            '&:hover': {
              bgcolor: 'rgba(var(--ws-accent-rgb),0.1)',
              borderColor: 'var(--ws-accent)'
            }
          }}
        >
          {parsingRelation ? '◆ 解析中...' : '开始解析'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // 大纲弹窗：基调选项（与角色标签风格一致，多选）
  // ====================
  const OUTLINE_TONE_OPTIONS = [
    { id: '', label: '无', color: '#94a3b8' },
    { id: '热血', label: '热血', color: '#ff4444' },
    { id: '逆袭', label: '逆袭', color: '#00ff9f' },
    { id: '轻松', label: '轻松', color: '#3b82f6' },
    { id: '虐心', label: '虐心', color: '#ec4899' },
    { id: '黑暗', label: '黑暗', color: '#6366f1' },
    { id: '搞笑', label: '搞笑', color: '#ffd700' },
    { id: '甜蜜', label: '甜蜜', color: '#ff00aa' },
    { id: '暧昧', label: '暧昧', color: '#f59e0b' },
    { id: '悬疑', label: '悬疑', color: 'var(--ws-violet)' },
    { id: '治愈', label: '治愈', color: '#06b6d4' },
    { id: '惊悚', label: '惊悚', color: '#ef4444' },
    { id: '史诗', label: '史诗', color: 'var(--ws-accent)' },
    { id: '沙雕', label: '沙雕', color: '#ff6b35' },
    { id: '权谋', label: '权谋', color: '#64748b' },
    { id: '甜宠', label: '甜宠', color: '#ec4899' },
    { id: '虐恋', label: '虐恋', color: '#be185d' },
    { id: '正剧', label: '正剧', color: 'rgba(255,252,245,0.9)' },
    { id: '日常', label: '日常', color: '#94a3b8' },
  ];
  // 小说类型
  const OUTLINE_GENRE_OPTIONS = ['仙侠', '都市', '玄幻', '历史', '科幻', '游戏', '武侠', '西幻', '悬疑', '轻小说', '二次元', '灵异', '军事', '体育', '现实', '都市脑洞', '玄幻奇幻', '仙侠洪荒'];
  // 目标受众
  const OUTLINE_AUDIENCE_OPTIONS = ['全年龄', '青少年', '成人', '老年', '男性向', '女性向', '大学生', '白领', '中学生'];
  // 篇幅
  const OUTLINE_LENGTH_OPTIONS = [
    { id: 'short', label: '短篇（5-10万字）', value: 10 },
    { id: 'medium', label: '中篇（20-50万字）', value: 50 },
    { id: 'long', label: '长篇（80-150万字）', value: 150 },
    { id: 'ultra', label: '超长篇（200万字+）', value: 300 },
  ];
  // 节奏
  const OUTLINE_RHYTHM_OPTIONS = [
    { id: 'none', label: '无', desc: '不指定节奏风格，由AI自由把握', color: '#94a3b8' },
    { id: 'fast', label: '快节奏', desc: '爽点密集，情节推进迅速', color: '#ff4444' },
    { id: 'medium', label: '中节奏', desc: '张弛有度，平衡情节与描写', color: '#3b82f6' },
    { id: 'slow', label: '慢节奏', desc: '细腻描写，注重人物心理', color: '#06b6d4' },
  ];
  // 爽点类型 - 扩展更多选项
  const OUTLINE_HOTSPOT_OPTIONS = [
    // 其他爽点
    { id: 'none', label: '无', category: '其他', color: '#94a3b8' },
    // 战斗爽点
    { id: 'datians', label: '打脸', category: '战斗', color: '#ff4444' },
    { id: 'shengji', label: '升级', category: '战斗', color: '#00ff9f' },
    { id: 'tu碾压', label: '碾压', category: '战斗', color: 'var(--ws-accent)' },
    { id: 'zhuangbi', label: '装逼', category: '战斗', color: '#ffd700' },
    { id: 'tianfu', label: '天赋觉醒', category: '战斗', color: '#3b82f6' },
    { id: 'tiancai', label: '天才流', category: '战斗', color: 'var(--ws-violet)' },
    { id: 'wushuang', label: '无敌流', category: '战斗', color: '#ef4444' },
    { id: 'wuxia', label: '武侠战斗', category: '战斗', color: '#6366f1' },
    // 资源收获
    { id: 'caifu', label: '财富自由', category: '资源', color: '#f59e0b' },
    { id: 'meinv', label: '美女如云', category: '资源', color: '#ec4899' },
    { id: 'shouxia', label: '小弟成群', category: '资源', color: '#6366f1' },
    { id: 'fushou', label: '富二代', category: '资源', color: '#f97316' },
    { id: 'kaifa', label: '领地开发', category: '资源', color: '#22c55e' },
    { id: 'zhilian', label: '炼器炼丹', category: '资源', color: '#eab308' },
    // 权势地位
    { id: 'yongheng', label: '永恒不朽', category: '权势', color: '#ff6b35' },
    { id: 'tongzhan', label: '统御万族', category: '权势', color: '#14b8a6' },
    { id: 'xianzhun', label: '仙尊', category: '权势', color: '#06b6d4' },
    { id: 'huangzi', label: '皇子皇女', category: '权势', color: '#ef4444' },
    { id: 'mogu', label: '魔道至尊', category: '权势', color: '#be185d' },
    { id: 'jiayou', label: '家族崛起', category: '权势', color: '#0ea5e9' },
    // 情感爽点
    { id: 'lianai', label: '甜蜜恋爱', category: '情感', color: '#f472b6' },
    { id: 'aimi', label: '暧昧拉扯', category: '情感', color: '#fb7185' },
    { id: 'fanfu', label: '反哺报恩', category: '情感', color: '#22d3ee' },
    { id: 'jiating', label: '家庭温情', category: '情感', color: '#fbbf24' },
    { id: 'youqing', label: '生死之交', category: '情感', color: '#60a5fa' },
    // 智斗谋略
    { id: 'jiming', label: '计谋布局', category: '智斗', color: '#64748b' },
    { id: 'fanzhuan', label: '绝地反转', category: '智斗', color: '#f59e0b' },
    { id: 'zuozhan', label: '运筹帷幄', category: '智斗', color: '#0d9488' },
    { id: 'canyin', label: '参悟天道', category: '智斗', color: '#0ea5e9' },
    // 其他爽点
    { id: 'tanxian', label: '探险寻宝', category: '其他', color: '#f59e0b' },
    { id: 'chuanyue', label: '跨时空', category: '其他', color: '#06b6d4' },
    { id: 'xuanshi', label: '悬疑解密', category: '其他', color: 'var(--ws-violet)' },
    { id: 'mubiao', label: '目标达成', category: '其他', color: '#06b6d4' },
  ];

  // 世界观设定选项
  const OUTLINE_WORLD_OPTIONS = [
    { id: 'none', label: '无', desc: '不指定世界观，由AI根据故事创意自由设定', color: '#94a3b8' },
    { id: 'xianxia', label: '修仙世界', desc: '修炼体系、灵根金丹', color: 'var(--ws-accent)' },
    { id: 'xuanhuan', label: '玄幻异界', desc: '斗气魔法、种族林立', color: '#f59e0b' },
    { id: 'dushi', label: '都市现实', desc: '现代都市、商战职场', color: '#3b82f6' },
    { id: 'lishi', label: '历史穿越', desc: '古代王朝、历史改写', color: '#ef4444' },
    { id: 'kehuan', label: '科幻未来', desc: '星际文明、科技进化', color: '#06b6d4' },
    { id: 'wuxia', label: '武侠江湖', desc: '门派恩怨、武功绝学', color: '#06b6d4' },
    { id: 'xihuan', label: '西方奇幻', desc: '魔法龙族、种族王国', color: 'var(--ws-violet)' },
    { id: 'moyu', label: '魔域异界', desc: '恶魔位面、黑暗法则', color: '#be185d' },
    { id: 'moshi', label: '末世危机', desc: '丧尸末日、生存挣扎', color: '#64748b' },
    { id: 'wuxian', label: '无限副本', desc: '主神空间、多元宇宙', color: '#ec4899' },
    { id: 'qingchu', label: '轻松日常', desc: '校园生活、轻松治愈', color: '#22d3ee' },
    { id: 'zhengju', label: '正剧史诗', desc: '宏大叙事、史诗传奇', color: '#f59e0b' },
  ];

  // 金手指类型选项
  const OUTLINE_GOLDEN_FINGER_OPTIONS = [
    { id: 'none', label: '无', desc: '不指定金手指，由AI根据故事创意自由设计', color: '#94a3b8' },
    { id: 'xueyuan', label: '血脉觉醒', desc: '神兽血脉、逆天体质', color: '#ef4444' },
    { id: 'xitong', label: '系统傍身', desc: '任务系统、奖励兑换', color: 'var(--ws-accent)' },
    { id: 'yisheng', label: '重生先知', desc: '先知先觉、预知未来', color: '#3b82f6' },
    { id: 'kongjian', label: '随身空间', desc: '种植空间、储物法宝', color: '#06b6d4' },
    { id: 'jineng', label: '技能复制', desc: '过目不忘、一学就会', color: '#f59e0b' },
    { id: 'tongling', label: '宠物灵兽', desc: '神宠异兽、契约灵兽', color: '#22c55e' },
    { id: 'fashen', label: '分身多元', desc: '身外化身、多重人格', color: 'var(--ws-violet)' },
    { id: 'shijie', label: '世界修正', desc: '修改现实、法则制定', color: '#ec4899' },
    { id: 'liyi', label: '福利掉落', desc: '神兵利器、灵丹妙药', color: '#f97316' },
    { id: 'tuandui', label: '团队加成', desc: '最强阵容、豪华队友', color: '#06b6d4' },
  ];

  // 核心冲突选项
  const OUTLINE_CONFLICT_OPTIONS = [
    { id: 'none', label: '无', desc: '不指定核心冲突，由AI根据故事创意自由设计', color: '#94a3b8' },
    { id: 'fuchou', label: '家族仇恨', desc: '血海深仇、灭门之灾', color: '#ef4444' },
    { id: 'tianti', label: '对抗天道', desc: '逆天改命、与天争锋', color: 'var(--ws-accent)' },
    { id: 'menpai', label: '门派之争', desc: '正魔大战、门派恩怨', color: '#3b82f6' },
    { id: 'guojia', label: '王朝更迭', desc: '皇权争霸、帝国兴衰', color: '#f59e0b' },
    { id: 'zhongzu', label: '种族存亡', desc: '人魔大战、种族延续', color: '#06b6d4' },
    { id: 'aiqing', label: '爱恨情仇', desc: '三角虐恋、生死相许', color: '#ec4899' },
    { id: 'jingzheng', label: '资源争夺', desc: '天才地宝、传承秘境', color: '#f97316' },
    { id: 'mimi', label: '惊天秘密', desc: '上古隐秘、史诗真相', color: '#6366f1' },
    { id: 'mingyun', label: '命运抗争', desc: '宿命轮回、预言成真', color: 'var(--ws-violet)' },
    { id: 'daode', label: '道德困境', desc: '正邪抉择、道义两难', color: '#64748b' },
  ];

  // 主CP类型
  const OUTLINE_CP_OPTIONS = [
    { id: 'none', label: '无', desc: '不指定CP类型，由AI根据故事创意自由设计', color: '#94a3b8' },
    { id: 'danmei', label: '单女主', desc: '专情一对一', color: '#ec4899' },
    { id: 'nuanshou', label: '女主', desc: '开后宫', color: '#f472b6' },
    { id: 'wangcai', label: '旺夫', desc: '软饭王', color: '#fb7185' },
    { id: 'lianai', label: '纯爱', desc: '甜甜甜', color: '#f9a8d4' },
    { id: 'kuiku', label: '虐恋', desc: '玻璃渣', color: '#be185d' },
    { id: 'danke', label: '单男主', desc: '兄弟情', color: '#3b82f6' },
    { id: 'wuke', label: '无CP', desc: '专注事业', color: '#64748b' },
  ];

  // 小说流派风格
  const OUTLINE_STYLE_OPTIONS = [
    { id: 'rexe', label: '热血沸腾', desc: '燃到爆', color: '#ef4444' },
    { id: 'qingyi', label: '轻松惬意', desc: '日常向', color: '#22c55e' },
    { id: 'dongnao', label: '脑洞大开', desc: '创意无限', color: 'var(--ws-accent)' },
    { id: 'hencai', label: '内核深度', desc: '有思考', color: '#3b82f6' },
    { id: 'chunhui', label: '纯真无害', desc: '小白文', color: '#22d3ee' },
    { id: 'heian', label: '黑暗深沉', desc: '压抑向', color: '#1e293b' },
    { id: 'xiaoyuan', label: '校园青春', desc: '青春校园', color: '#60a5fa' },
    { id: 'shangzhan', label: '商战职场', desc: '职场博弈', color: '#f59e0b' },
  ];
  // 虐点类型
  const OUTLINE_NUAN_OPTIONS = [
    { id: 'none', label: '无虐', color: '#06b6d4' },
    { id: 'light', label: '微虐', color: '#3b82f6' },
    { id: 'medium', label: '中虐', color: '#f59e0b' },
    { id: 'heavy', label: '重虐', color: '#ef4444' },
    { id: 'glass', label: '玻璃渣', color: '#ec4899' },
  ];

  // 渲染AI生成大纲弹窗（与角色添加面板统一）
  const renderOutlineGenDialog = () => (
    <Dialog
      open={showOutlineGenDialog}
      onClose={() => { setShowOutlineGenDialog(false); setOutlineValidation({}); }}
      maxWidth={false}
      TransitionComponent={CyberDialogTransition}
      transitionDuration={motionReduced ? 0 : { enter: 420, exit: 260 }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(4, 6, 14, 0.78)',
          backdropFilter: motionReduced ? 'none' : 'saturate(1.08) blur(10px)',
        },
      }}
      PaperProps={{
        className: 'character-panel-cyber',
        sx: {
          bgcolor: 'rgba(10, 11, 18, 0.98)',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          height: '90vh',
          width: '90vw',
          maxWidth: '1200px',
          boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), 0 0 120px rgba(var(--ws-accent-rgb), 0.15)'
        }
      }}
    >
      <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.95) !important', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 1.5, flexShrink: 0 }}>
        <AutoFixHighIcon sx={{ color: 'rgba(255,230,200,0.95)', fontSize: 20, filter: 'drop-shadow(0 0 6px rgba(255,230,200,0.5))' }} />
        AI 生成大纲
      </DialogTitle>
      <DialogContent className="character-panel-cyber" sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'calc(90vh - 65px)' }}>
        <Box
          className="character-panel-cyber outline-gen-scroll-container"
          sx={{ px: 2.5, py: 2, overflowY: 'auto', flex: 1, height: '100%' }}
          ref={outlineScrollContainerRef}
        >

          {/* ==================== 基础信息区域（书名/类型/受众 三列均分、容器框统一） ==================== */}
          <Box sx={{ mb: 2.5 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: 'var(--ws-accent) !important', fontSize: '11px !important' }}>
              ◆ 基础信息
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* 书名（可选）- 与小说类型、目标受众同风格容器 */}
              <Box className="character-panel-card" sx={{ flex: '1 1 0', minWidth: '140px', mb: 0 }}>
                <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, fontSize: '10px !important' }}>书名（可选）</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="输入书名，不填则从生成内容中自动识别"
                  value={outlineFormData.name}
                  onChange={(e) => setOutlineFormData({ ...outlineFormData, name: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.92)', fontSize: 13,
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1,
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: 'rgba(255,255,255,0.18)' },
                      '&.Mui-focused': { borderColor: 'rgba(255,255,255,0.28)' }
                    }
                  }}
                />
              </Box>
              {/* 小说类型 */}
              <Box className="character-panel-card" sx={{ flex: '1 1 0', minWidth: '140px', mb: 0, border: outlineValidation.type === false ? '1px solid #ff4444 !important' : undefined }}>
                <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, fontSize: '10px !important', color: outlineValidation.type === false ? '#ff4444 !important' : undefined }}>
                  小说类型 <Typography component="span" sx={{ color: '#ff4444', fontSize: 10 }}>*</Typography>
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={outlineFormData.type}
                    onChange={(e) => { setOutlineFormData({ ...outlineFormData, type: e.target.value }); setOutlineValidation(prev => ({ ...prev, type: true })); }}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.92)', fontSize: 13,
                      border: outlineValidation.type === false ? '1px solid #ff4444 !important' : '1px solid rgba(255,255,255,0.1)',
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: outlineValidation.type === false ? '#ff4444' : 'rgba(255,255,255,0.18)' },
                      '&.Mui-focused': { borderColor: outlineValidation.type === false ? '#ff4444' : 'rgba(255,255,255,0.28)' }
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#0c0c10', border: '1px solid rgba(255,255,255,0.1)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } } } } }}
                  >
                    {OUTLINE_GENRE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              {/* 目标受众 */}
              <Box className="character-panel-card" sx={{ flex: '1 1 0', minWidth: '140px', mb: 0 }}>
                <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, fontSize: '10px !important' }}>目标受众</Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={outlineFormData.targetAudience || '全年龄'}
                    onChange={(e) => setOutlineFormData({ ...outlineFormData, targetAudience: e.target.value })}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.92)', fontSize: 13,
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1,
                      '& fieldset': { border: 'none' },
                      '&:hover': { borderColor: 'rgba(255,255,255,0.18)' },
                      '&.Mui-focused': { borderColor: 'rgba(255,255,255,0.28)' }
                    }}
                    MenuProps={{ PaperProps: { sx: { bgcolor: '#0c0c10', border: '1px solid rgba(255,255,255,0.1)', '& .MuiMenuItem-root': { color: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } } } } }}
                  >
                    {OUTLINE_AUDIENCE_OPTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Box>

          {/* ==================== 模板选择区域 ==================== */}
          <Box sx={{ mb: 2.5 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: 'var(--ws-accent) !important', fontSize: '11px !important' }}>
              ◆ 选择模板 <Typography component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（决定故事核心框架）</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
              {aiOutlineTemplates.map(template => {
                const isSelected = selectedOutlineTemplate === template.id;
                return (
                  <Chip
                    key={template.id}
                    label={template.name}
                    size="small"
                    onClick={() => setSelectedOutlineTemplate(template.id)}
                    sx={{
                      bgcolor: isSelected ? 'rgba(var(--ws-accent-rgb), 0.25)' : 'rgba(255,255,255,0.05)',
                      color: isSelected ? 'var(--ws-accent)' : 'rgba(255,255,255,0.72)',
                      border: `1px solid ${isSelected ? 'var(--ws-accent)' : 'rgba(255,255,255,0.1)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: isSelected ? 'rgba(var(--ws-accent-rgb), 0.35)' : 'rgba(255,255,255,0.1)', borderColor: isSelected ? 'var(--ws-accent)' : 'rgba(255,255,255,0.2)' }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 篇幅与节奏 ==================== */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box className="character-panel-card" sx={{ flex: '1 1 200px', mb: 0 }}>
              <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, fontSize: '10px !important' }}>计划总章节数</Typography>
              <TextField
                size="small"
                type="number"
                value={outlineFormData.chapterCount || ''}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setOutlineFormData({ ...outlineFormData, chapterCount: isNaN(v) ? 100 : Math.max(1, v) });
                }}
                placeholder="输入计划章节数，如 80"
                inputProps={{ min: 1, style: { fontSize: 13, color: '#ff6b35' } }}
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,107,53,0.08)', borderColor: 'rgba(255,107,53,0.3)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,107,53,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b35' },
                  width: 140
                }}
              />
            </Box>
            <Box className="character-panel-card" sx={{ flex: '1 1 200px', mb: 0, border: outlineValidation.rhythm === false ? '1px dashed #ff4444' : undefined, p: outlineValidation.rhythm === false ? 1 : undefined }}>
              <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.rhythm === false ? '#ff4444 !important' : undefined, fontSize: '10px !important' }}>节奏风格 *必选</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {OUTLINE_RHYTHM_OPTIONS.map(rhythm => {
                  const isSelected = outlineFormData.rhythm === rhythm.id;
                  return (
                    <Chip
                      key={rhythm.id}
                      label={rhythm.label}
                      size="small"
                      onClick={() => { setOutlineFormData({ ...outlineFormData, rhythm: rhythm.id }); setOutlineValidation(prev => ({ ...prev, rhythm: true })); }}
                      sx={{
                        bgcolor: isSelected ? `${rhythm.color}22` : 'rgba(255,255,255,0.05)',
                        color: isSelected ? rhythm.color : 'rgba(255,255,255,0.72)',
                        border: `1px solid ${isSelected ? rhythm.color : 'rgba(255,255,255,0.1)'}`,
                        fontSize: 11, cursor: 'pointer',
                        '&:hover': { bgcolor: isSelected ? `${rhythm.color}30` : 'rgba(255,255,255,0.1)', borderColor: rhythm.color }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* ==================== 世界观设定 ==================== */}
          <Box sx={{ mb: 2.5, border: outlineValidation.worldView === false ? '1px dashed #ff4444' : undefined, borderRadius: 1, p: outlineValidation.worldView === false ? 1 : 0 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.worldView === false ? '#ff4444 !important' : '#00e5c4 !important', fontSize: '11px !important' }}>
              ◆ 世界观设定 <Typography component="span" sx={{ color: outlineValidation.worldView === false ? '#ff4444' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（故事发生的世界）*必选</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
              {OUTLINE_WORLD_OPTIONS.map(world => {
                const isSelected = outlineFormData.worldView === world.id;
                return (
                  <Chip
                    key={world.id}
                    label={world.label}
                    size="small"
                    onClick={() => { setOutlineFormData({ ...outlineFormData, worldView: world.id }); setOutlineValidation(prev => ({ ...prev, worldView: true })); }}
                    title={world.desc}
                    sx={{
                      bgcolor: isSelected ? `${world.color}22` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? world.color : 'rgba(255,255,255,0.7)',
                      border: `1px solid ${isSelected ? world.color : 'rgba(255,255,255,0.08)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: `${world.color}15`, borderColor: world.color }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 基调选择（多选） ==================== */}
          <Box sx={{ mb: 2.5, border: outlineValidation.tags === false ? '1px dashed #ff4444' : undefined, borderRadius: 1, p: outlineValidation.tags === false ? 1 : 0 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.tags === false ? '#ff4444 !important' : '#ffd700 !important', fontSize: '11px !important' }}>
              ◆ 基调（可多选） <Typography component="span" sx={{ color: outlineValidation.tags === false ? '#ff4444' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（故事整体氛围）*必选</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {OUTLINE_TONE_OPTIONS.map(tone => {
                const isSelected = outlineFormData.tags.includes(tone.id);
                return (
                  <Chip
                    key={tone.id}
                    label={tone.label}
                    size="small"
                    onClick={() => {
                      let tags;
                      if (tone.id === '') {
                        // 选"无"：清空所有基调
                        tags = [''];
                      } else {
                        // 选其他基调：移除"无"选项
                        tags = isSelected
                          ? outlineFormData.tags.filter(t => t !== tone.id)
                          : [...outlineFormData.tags.filter(t => t !== ''), tone.id];
                      }
                      setOutlineFormData({ ...outlineFormData, tags });
                      if (tags.length > 0) setOutlineValidation(prev => ({ ...prev, tags: true }));
                    }}
                    sx={{
                      bgcolor: isSelected ? `${tone.color}22` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? tone.color : 'rgba(255,255,255,0.65)',
                      border: `1px solid ${isSelected ? tone.color : 'rgba(255,255,255,0.06)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: `${tone.color}15`, borderColor: tone.color }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 爽点类型（多选） ==================== */}
          <Box sx={{ mb: 2.5, border: outlineValidation.hotSpots === false ? '1px dashed #ff4444' : undefined, borderRadius: 1, p: outlineValidation.hotSpots === false ? 1 : 0 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.hotSpots === false ? '#ff4444 !important' : '#ff6b35 !important', fontSize: '11px !important' }}>
              ◆ 爽点类型（可多选） <Typography component="span" sx={{ color: outlineValidation.hotSpots === false ? '#ff4444' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（读者最期待的剧情）*必选</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {OUTLINE_HOTSPOT_OPTIONS.map(spot => {
                const isSelected = (outlineFormData.hotSpots || []).includes(spot.id);
                return (
                  <Chip
                    key={spot.id}
                    label={spot.label}
                    size="small"
                    onClick={() => {
                      let spots;
                      if (spot.id === 'none') {
                        spots = ['none'];
                      } else {
                        spots = isSelected
                          ? (outlineFormData.hotSpots || []).filter(s => s !== spot.id)
                          : [...(outlineFormData.hotSpots || []).filter(s => s !== 'none'), spot.id];
                      }
                      setOutlineFormData({ ...outlineFormData, hotSpots: spots });
                      if (spots.length > 0) setOutlineValidation(prev => ({ ...prev, hotSpots: true }));
                    }}
                    title={spot.category}
                    sx={{
                      bgcolor: isSelected ? `${spot.color}22` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? spot.color : 'rgba(255,255,255,0.65)',
                      border: `1px solid ${isSelected ? spot.color : 'rgba(255,255,255,0.06)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: `${spot.color}15`, borderColor: spot.color }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 金手指设定 ==================== */}
          <Box sx={{ mb: 2.5, border: outlineValidation.goldenFinger === false ? '1px dashed #ff4444' : undefined, borderRadius: 1, p: outlineValidation.goldenFinger === false ? 1 : 0 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.goldenFinger === false ? '#ff4444 !important' : '#00f0ff !important', fontSize: '11px !important' }}>
              ◆ 金手指设定 <Typography component="span" sx={{ color: outlineValidation.goldenFinger === false ? '#ff4444' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（主角的特殊优势）*必选</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
              {OUTLINE_GOLDEN_FINGER_OPTIONS.map(gf => {
                const isSelected = outlineFormData.goldenFinger === gf.id;
                return (
                  <Chip
                    key={gf.id}
                    label={gf.label}
                    size="small"
                    onClick={() => { setOutlineFormData({ ...outlineFormData, goldenFinger: isSelected ? null : gf.id }); setOutlineValidation(prev => ({ ...prev, goldenFinger: true })); }}
                    title={gf.desc}
                    sx={{
                      bgcolor: isSelected ? `${gf.color}22` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? gf.color : 'rgba(255,255,255,0.7)',
                      border: `1px solid ${isSelected ? gf.color : 'rgba(255,255,255,0.08)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: `${gf.color}15`, borderColor: gf.color }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 核心冲突 ==================== */}
          <Box sx={{ mb: 2.5, border: outlineValidation.coreConflict === false ? '1px dashed #ff4444' : undefined, borderRadius: 1, p: outlineValidation.coreConflict === false ? 1 : 0 }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.coreConflict === false ? '#ff4444 !important' : '#ef4444 !important', fontSize: '11px !important' }}>
              ◆ 核心冲突 <Typography component="span" sx={{ color: outlineValidation.coreConflict === false ? '#ff4444' : 'rgba(255,255,255,0.5)', fontSize: 10, fontWeight: 'normal' }}>（故事的主要矛盾）*必选</Typography>
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
              {OUTLINE_CONFLICT_OPTIONS.map(conflict => {
                const isSelected = outlineFormData.coreConflict === conflict.id;
                return (
                  <Chip
                    key={conflict.id}
                    label={conflict.label}
                    size="small"
                    onClick={() => { setOutlineFormData({ ...outlineFormData, coreConflict: isSelected ? null : conflict.id }); setOutlineValidation(prev => ({ ...prev, coreConflict: true })); }}
                    title={conflict.desc}
                    sx={{
                      bgcolor: isSelected ? `${conflict.color}22` : 'rgba(255,255,255,0.03)',
                      color: isSelected ? conflict.color : 'rgba(255,255,255,0.7)',
                      border: `1px solid ${isSelected ? conflict.color : 'rgba(255,255,255,0.08)'}`,
                      fontSize: 11, cursor: 'pointer',
                      '&:hover': { bgcolor: `${conflict.color}15`, borderColor: conflict.color }
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* ==================== 感情线设置 ==================== */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
            <Box className="character-panel-card" sx={{ flex: '1 1 200px', mb: 0, border: outlineValidation.cpType === false ? '1px dashed #ff4444' : undefined, p: outlineValidation.cpType === false ? 1 : undefined }}>
              <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, color: outlineValidation.cpType === false ? '#ff4444 !important' : '#ec4899 !important', fontSize: '10px !important' }}>CP类型 *必选</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {OUTLINE_CP_OPTIONS.map(cp => {
                  const isSelected = outlineFormData.cpType === cp.id;
                  return (
                    <Chip
                      key={cp.id}
                      label={cp.label}
                      size="small"
                      onClick={() => { setOutlineFormData({ ...outlineFormData, cpType: isSelected ? null : cp.id }); if (!isSelected) setOutlineValidation(prev => ({ ...prev, cpType: true })); }}
                      title={cp.desc}
                      sx={{
                        bgcolor: isSelected ? `${cp.color}22` : 'rgba(255,255,255,0.03)',
                        color: isSelected ? cp.color : 'rgba(255,255,255,0.65)',
                        border: `1px solid ${isSelected ? cp.color : 'rgba(255,255,255,0.06)'}`,
                        fontSize: 11, cursor: 'pointer',
                        '&:hover': { bgcolor: `${cp.color}15`, borderColor: cp.color }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box className="character-panel-card" sx={{ flex: '1 1 180px', mb: 0 }}>
              <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, fontSize: '10px !important' }}>虐点程度</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {OUTLINE_NUAN_OPTIONS.map(nuan => {
                  const isSelected = outlineFormData.nuan === nuan.id;
                  return (
                    <Chip
                      key={nuan.id}
                      label={nuan.label}
                      size="small"
                      onClick={() => setOutlineFormData({ ...outlineFormData, nuan: nuan.id })}
                      sx={{
                        bgcolor: isSelected ? `${nuan.color}22` : 'rgba(255,255,255,0.03)',
                        color: isSelected ? nuan.color : 'rgba(255,255,255,0.65)',
                        border: `1px solid ${isSelected ? nuan.color : 'rgba(255,255,255,0.06)'}`,
                        fontSize: 11, cursor: 'pointer',
                        '&:hover': { bgcolor: `${nuan.color}15`, borderColor: nuan.color }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
            <Box className="character-panel-card" sx={{ flex: '1 1 140px', mb: 0 }}>
              <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1.5, fontSize: '10px !important' }}>详细程度</Typography>
              <RadioGroup row value={outlineGenDetail} onChange={(e) => setOutlineGenDetail(e.target.value)} sx={{ gap: 2 }}>
                <FormControlLabel value="detailed" control={<Radio sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--ws-accent)' } }} />} label={<Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>详细</Typography>} />
                <FormControlLabel value="simple" control={<Radio sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: 'var(--ws-accent)' } }} />} label={<Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 12 }}>简洁</Typography>} />
              </RadioGroup>
            </Box>
          </Box>

          {/* 用户需求 - 最重要的输入区域 */}
          <Box className="character-panel-card character-panel-card-ai" sx={{ mb: 2, border: outlineValidation.userRequirement === false ? '1px solid #ff4444' : undefined }}>
            <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 1, color: outlineValidation.userRequirement === false ? '#ff4444 !important' : 'rgba(255,235,220,0.95) !important' }}>
              故事创意描述 <Typography component="span" sx={{ color: outlineValidation.userRequirement === false ? '#ff4444' : '#ff6b35', fontSize: 11 }}>*必填</Typography>
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, mb: 1.5 }}>
              请详细描述你的故事创意，包括：主角特点、核心冲突、期望的剧情走向、特别想看的设定等。越详细AI生成的大纲越符合你的期望！
            </Typography>
            <TextField
              fullWidth multiline
              {...getDynamicTextAreaStyle(outlineUserRequirement, 4, 16)}
              placeholder="例如：废物少主觉醒逆天血脉，从被打压到横扫万族，成为最强者。也可以描述你希望出现的情节、人物、设定等。"
              value={outlineUserRequirement}
              onChange={(e) => { setOutlineUserRequirement(e.target.value); if (e.target.value.trim()) setOutlineValidation(prev => ({ ...prev, userRequirement: true })); }}
              className="character-panel-textarea"
              disabled={generatingOutline || isEditingOutline}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: generatingOutline || isEditingOutline ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.25)',
                  fontSize: 13, alignItems: 'flex-start',
                  border: outlineValidation.userRequirement === false ? '1px solid #ff4444 !important' : '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 1,
                  '& fieldset': { border: 'none' },
                  '&:hover': { borderColor: outlineValidation.userRequirement === false ? '#ff4444' : 'rgba(255,255,255,0.14)' },
                  '&.Mui-focused': { borderColor: outlineValidation.userRequirement === false ? '#ff4444' : 'rgba(255,255,255,0.22)', boxShadow: outlineValidation.userRequirement === false ? '0 0 0 1px #ff4444' : '0 0 0 1px rgba(255,255,255,0.1)' }
                },
                '& textarea': { lineHeight: 1.6 }
              }}
            />
          </Box>

          {/* 流式生成结果显示区域 */}
          {(generatingOutline || streamingOutline || isEditingOutline) && (
            <Box
              className="character-panel-card"
              ref={outlineContentRef}
              sx={{ mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography className="character-panel-section-label" sx={{ display: 'block', mb: 0, color: '#00e5c4 !important' }}>
                  {generatingOutline ? '◆ AI 正在生成大纲...' : '◆ 生成的大纲'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {generatingOutline && autoScrollEnabled && (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                      正在跟踪生成内容
                    </Typography>
                  )}
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    {outlineWordCount > 0 ? `字数：${outlineWordCount} 字` : ''}
                  </Typography>
                </Box>
              </Box>

              {generatingOutline && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <CircularProgress size={16} sx={{ color: 'var(--ws-accent)' }} />
                  <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                    AI 正在创作中，请稍候...
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                multiline
                value={streamingOutline}
                onChange={(e) => {
                  setStreamingOutline(e.target.value);
                  setOutlineWordCount(e.target.value.length);
                }}
                placeholder={generatingOutline ? 'AI 正在生成中...' : '点击"开始生成"获取大纲'}
                disabled={generatingOutline}
                className="character-panel-textarea"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(0,0,0,0.35)',
                    fontSize: 13,
                    minHeight: '200px',
                    alignItems: 'flex-start',
                    border: '1px solid rgba(0,229,196,0.15)',
                    borderRadius: 1,
                    '& fieldset': { border: 'none' },
                    '&:hover': { borderColor: 'rgba(0,229,196,0.25)' },
                    '&.Mui-focused': { borderColor: 'rgba(0,229,196,0.35)', boxShadow: '0 0 0 1px rgba(0,229,196,0.15)' }
                  },
                  '& textarea': {
                    lineHeight: 1.8,
                    minHeight: '180px !important'
                  }
                }}
              />

              {outlineWordCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                    字数统计：<Typography component="span" sx={{ color: '#00e5c4', fontWeight: 600 }}>{outlineWordCount}</Typography> 字
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10 }}>
                      生成完成后可手动修改内容
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)', gap: 1.5, flexShrink: 0, bgcolor: 'transparent' }}>
        <Button
          onClick={() => {
            setShowOutlineGenDialog(false);
            setStreamingOutline('');
            setOutlineWordCount(0);
            setIsEditingOutline(false);
          }}
          className="workspace-btn-cyber"
          sx={{
            color: 'rgba(255,255,255,0.9)',
            border: '1px solid rgba(255,255,255,0.22)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            flex: 1,
            minWidth: 120
          }}
        >
          取消
        </Button>
        {isEditingOutline ? (
          <Button
            variant="outlined"
            onClick={handleSaveOutline}
            className="workspace-btn-cyber"
            sx={{
              bgcolor: 'rgba(0,229,196,0.15)',
              color: '#00e5c4',
              border: '1px solid #00e5c4',
              fontSize: 14,
              fontWeight: 500,
              flex: 1,
              '&:hover': {
                bgcolor: 'rgba(0,229,196,0.25)',
                borderColor: '#00e5c4'
              }
            }}
          >
            保存大纲
          </Button>
        ) : (
          <Button
            variant="outlined"
            onClick={handleGenerateOutline}
            disabled={generatingOutline}
            className="workspace-btn-cyber"
            sx={{
              bgcolor: generatingOutline ? 'rgba(var(--ws-accent-rgb), 0.15)' : 'transparent',
              color: generatingOutline ? 'var(--ws-accent)' : 'rgba(255,255,255,0.92)',
              border: generatingOutline ? '1px solid var(--ws-accent)' : '1px solid rgba(255,255,255,0.22)',
              fontSize: 14,
              fontWeight: 500,
              flex: 1,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.06)',
                borderColor: 'rgba(255,255,255,0.35)'
              },
              '&:disabled': {
                color: 'rgba(255,255,255,0.25)',
                borderColor: 'rgba(255,255,255,0.06)'
              }
            }}
          >
            {generatingOutline ? '生成中...' : '开始生成'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // 手动添加大纲弹窗（简洁模式：仅粘贴大纲内容，初始5行+动态高度）
  const renderSimpleOutlineDialog = () => (
    <Dialog
      open={showSimpleOutlineDialog}
      onClose={() => setShowSimpleOutlineDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: 'character-panel-cyber',
        sx: {
          bgcolor: 'rgba(10, 11, 18, 0.98)',
          backgroundImage: 'none',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          maxHeight: '80vh',
          boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), 0 0 120px rgba(var(--ws-accent-rgb), 0.15)'
        }
      }}
    >
      <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.95) !important', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 1.5, flexShrink: 0 }}>
        <AddIcon sx={{ color: 'rgba(255,230,200,0.95)', fontSize: 20 }} />
        手动添加大纲
      </DialogTitle>
      <DialogContent className="character-panel-cyber" sx={{ p: 0, overflow: 'hidden' }}>
        <Box className="character-panel-cyber" sx={{ p: 2, overflow: 'auto', maxHeight: 'calc(80vh - 80px)' }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Box sx={{ flex: '0 0 220px' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, mb: 0.5 }}>计划总章节数</Typography>
              <TextField
                size="small"
                type="number"
                value={simpleOutlineChapterCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setSimpleOutlineChapterCount(isNaN(v) ? 100 : Math.max(1, v));
                }}
                inputProps={{ min: 1, style: { fontSize: 13, color: '#ff6b35' } }}
                sx={{
                  '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,107,53,0.08)', borderColor: 'rgba(255,107,53,0.3)' },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,107,53,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b35' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#ff6b35' },
                }}
              />
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 11, flex: 1 }}>
              总章节数用于 AI 生成时的节奏计算，请填写真实计划值
            </Typography>
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 1.5 }}>
            请直接粘贴您准备好的大纲内容，系统会自动解析并保存。
          </Typography>
          <TextField
            fullWidth
            multiline
            {...getDynamicTextAreaStyle(simpleOutlineContent, 5, 30)}
            placeholder="在此粘贴大纲内容...&#10;&#10;例如：&#10;# 主线剧情&#10;主角xxx原本是废物，因意外觉醒xxx，开始逆袭之路...&#10;&#10;# 主角设定&#10;姓名：xxx&#10;性别：男&#10;..."
            value={simpleOutlineContent}
            onChange={(e) => setSimpleOutlineContent(e.target.value)}
            className="character-panel-textarea"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(0,0,0,0.25)',
                fontSize: 13,
                alignItems: 'flex-start',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 1,
                '& fieldset': { border: 'none' },
                '&:hover': { borderColor: 'rgba(255,255,255,0.14)' },
                '&.Mui-focused': { borderColor: 'rgba(255,255,255,0.22)', boxShadow: '0 0 0 1px rgba(255,255,255,0.1)' }
              },
              '& textarea': { lineHeight: 1.6 }
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)', gap: 1.5, flexShrink: 0, bgcolor: 'transparent' }}>
        <Button
          onClick={() => { setShowSimpleOutlineDialog(false); setSimpleOutlineContent(''); }}
          className="workspace-btn-cyber"
          sx={{ 
            color: 'rgba(255,255,255,0.9)', 
            border: '1px solid rgba(255,255,255,0.22)', 
            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.35)' },
            flex: 1,
            minWidth: 120
          }}
        >
          取消
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            if (!simpleOutlineContent.trim()) {
              setSnackbar({ open: true, message: '请先粘贴大纲内容', severity: 'warning' });
              return;
            }
            // 解析大纲内容，尝试提取书名等信息
            const nameMatch = simpleOutlineContent.match(/书名[：:]\s*(.+)/i);
            const name = nameMatch ? nameMatch[1].trim() : (extractBookNameFromContent(simpleOutlineContent) || `大纲${outlines.length + 1}`);
            // 创建大纲对象
            const newOutline = {
              name,
              type: '仙侠',
              content: simpleOutlineContent,
              tags: [],
              targetAudience: '全年龄',
              wordCount: 100,
              chapterCount: simpleOutlineChapterCount,
              mainPlot: { storyline: '', description: '' },
              protagonist: { name: '', gender: '男', age: '', personality: '', goal: '', goldenFinger: '' },
              worldSettings: { background: '', levelSystem: '', factions: [], rules: [] },
              storyStructure: { beginning: '', development: '', climax: '', ending: '' },
              coreConflicts: [],
              subPlots: [],
              foreshadowing: [],
              climaxes: [],
              characterNetwork: [],
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            setOutlines([newOutline]);
            setShowSimpleOutlineDialog(false);
            setSimpleOutlineContent('');
            setSnackbar({ open: true, message: '大纲添加成功！', severity: 'success' });
          }}
          className="workspace-btn-cyber"
          sx={{ 
            bgcolor: 'transparent',
            color: 'rgba(255,255,255,0.92)',
            border: '1px solid rgba(255,255,255,0.22)',
            fontSize: 14,
            fontWeight: 500,
            flex: 1,
            '&:hover': { 
              bgcolor: 'rgba(255,255,255,0.06)', 
              borderColor: 'rgba(255,255,255,0.35)' 
            }
          }}
        >
          保存大纲
        </Button>
      </DialogActions>
    </Dialog>
  );

  // AI生成大纲处理函数 - 使用流式生成
  const handleGenerateOutline = async () => {
    // 先验证所有选项并更新验证状态
    validateOutlineOptions();

    // 按顺序收集未填写的必填项（选"无"视为已填）
    const missingSettings = [];
    const tags = outlineFormData.tags || [];
    const hotSpots = outlineFormData.hotSpots || [];

    if (!outlineFormData.type) missingSettings.push('小说类型');
    if (tags.length === 0 && !tags.includes('')) missingSettings.push('基调');
    if (!outlineFormData.cpType && outlineFormData.cpType !== 'none') missingSettings.push('CP类型');
    if (!outlineFormData.worldView && outlineFormData.worldView !== 'none') missingSettings.push('世界观设定');
    if (hotSpots.length === 0 && !hotSpots.includes('none')) missingSettings.push('爽点类型');
    if (!outlineFormData.goldenFinger && outlineFormData.goldenFinger !== 'none') missingSettings.push('金手指设定');
    if (!outlineFormData.coreConflict && outlineFormData.coreConflict !== 'none') missingSettings.push('核心冲突');
    if (!outlineFormData.rhythm && outlineFormData.rhythm !== 'none') missingSettings.push('节奏风格');
    if (!outlineUserRequirement.trim()) missingSettings.push('故事创意描述');

    if (missingSettings.length > 0) {
      // 使用 snackbar 提示用户需要设置哪些选项
      setSnackbar({
        open: true,
        message: `请先设置以下选项：${missingSettings.join('、')}`,
        severity: 'warning'
      });
      return;
    }

    // 检查次数限制
    if (!checkAssistQuota('AI生成大纲')) {
      return;
    }

    // 重置状态
    setStreamingOutline('');
    setOutlineWordCount(0);
    setIsEditingOutline(false);
    setGeneratingOutline(true);
    setAutoScrollEnabled(true);  // 开始生成时启用自动滚动

    // 点击开始生成后，将界面滚动到 AI 生成内容编辑框，避免用户不知道是否在生成
    setTimeout(() => {
      outlineContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      const scrollEl = outlineScrollContainerRef.current;
      if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    }, 150);
    setTimeout(() => {
      const scrollEl = outlineScrollContainerRef.current;
      if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
    }, 400);

    try {
      const template = aiOutlineTemplates.find(t => t.id === selectedOutlineTemplate);

      // 构建完整的prompt
      const worldViewLabel = OUTLINE_WORLD_OPTIONS.find(w => w.id === outlineFormData.worldView)?.label || '';
      const goldenFingerLabel = OUTLINE_GOLDEN_FINGER_OPTIONS.find(g => g.id === outlineFormData.goldenFinger)?.label || '';
      const coreConflictLabel = OUTLINE_CONFLICT_OPTIONS.find(c => c.id === outlineFormData.coreConflict)?.label || '';
      const cpTypeLabel = OUTLINE_CP_OPTIONS.find(c => c.id === outlineFormData.cpType)?.label || '';

      const hotSpotsList = (outlineFormData.hotSpots || [])
        .map(s => {
          const spot = OUTLINE_HOTSPOT_OPTIONS.find(o => o.id === s);
          return `- ${spot?.label || s}`;
        })
        .join('\n') || '待定';

      // 提前计算各阶段章节范围，供 prompt 使用
    const totalCh = outlineFormData.chapterCount || 100;
    const ch1 = Math.ceil(totalCh * 0.1);
    const ch2 = Math.ceil(totalCh * 0.5);
    const ch3 = Math.ceil(totalCh * 0.85);

      // 根据章节总数计算各部分的动态数量（章节越多，支线/伏笔/高潮/配角也要相应增加）
      const totalChapter = totalCh;
      const minSubplots = Math.max(3, Math.floor(totalChapter / 30));
      const minForeshadow = Math.max(4, Math.floor(totalChapter / 40));
      const climaxCount = totalChapter >= 100 ? 4 : totalChapter >= 50 ? 3 : 2;
      const minSupporting = Math.max(4, totalChapter >= 80 ? 7 : 5);

      const detailRequirements = (outlineGenDetail === 'detailed'
        ? `## 输出格式要求
【重要】你必须严格按照以下格式输出，每个章节阶段的主线描述不得少于300字，大纲总字数不得少于6000字！

【小说标题】
（拟定一个有吸引力的书名，体现主角核心特征或爽点，如"血脉觉醒：废物少爷逆天改命"）

【主线剧情】
◆ 一句话主线（30字以内）：XXXXXXXX
◆ 开局主线（第1~${ch1}章，300字以上）：详细描述开局阶段的故事走向、核心事件、主角初期困境和第一次逆转
◆ 发展主线（第${ch1 + 1}~${ch2}章，500字以上）：详细描述发展阶段的故事走向、冲突升级、势力洗牌、主角的成长与挫折
◆ 高潮主线（第${ch2 + 1}~${ch3}章，500字以上）：详细描述高潮阶段的故事走向、最终对决、核心冲突爆发
◆ 结局主线（第${ch3 + 1}~${totalCh}章，300字以上）：详细描述结局阶段的故事走向、伏笔收束、角色最终命运

【主角设定】（姓名必须根据小说类型动态生成，符合世界观和文化背景！如修仙小说可用"清虚"、"玄清"，都市小说可用"陆辞"、"沈听雪"）
- 姓名：（根据小说类型、世界观、主角身份动态生成，必须与整体风格统一）
- 性别：
- 年龄：
- 出身背景：（详细描述主角的出身、为何受苦或被轻视、有什么不为人知的身世）
- 性格特点：（包括优点和性格缺陷，要有立体感）
- 核心追求：（主角内心最深处的渴望和目标）
- 金手指：（如果有，具体描述这个能力是什么、如何觉醒、有什么限制）
- 标志性特征：（主角的外貌特征、口头禅、独特习惯，让读者记住）
- 成长弧线：（主角从什么状态到什么状态的转变过程）

【配角设定】列出${minSupporting}个重要配角，每个配角必须包含以下全部字段：
- 姓名：（根据配角的身份、性格、与主角关系动态生成，符合小说整体风格）
- 性别：
- 角色定位：（挚友/对手/导师/恋人/势力首领/神秘人物等）
- 与主角关系：（与主角的具体关系，是如何产生交集的）
- 性格特点：（简明扼要的性格描述）
- 背景故事：（50字以上，说明这个角色的来历和背景）
- 关键作用：（在故事哪个阶段起作用，如何推动情节）
- 角色命运：（最后这个角色的结局如何）
- 对主角态度变化：（一开始对主角是什么态度，后来为什么转变）

【世界观设定】
- 世界背景：（300字以上，详细描述这个世界是什么样的，有哪些独特的规则或设定）
- 等级/能力体系：（如果有修炼体系，列出具体的等级名称和进阶条件）
- 主要势力：（${minSupporting}个主要势力，每个势力包含：名称、领袖、核心目标、与主角关系）
- 地理格局：（这个世界分为哪些区域或版块，各有什么特点）
- 特殊规则：（这个世界有什么独特规则，如血脉至上、资源稀缺、势力争霸等）

【支线剧情】请设计${minSubplots}条以上支线，每条必须包含：
- 标题：（支线名称，要体现这条支线的核心冲突）
- 所属阶段：（开局/发展/高潮/结局）
- 涉及章节：（第X章 - 第X章，必须在所属阶段范围内）
- 核心冲突：（100字以上，这条支线解决什么矛盾，产生了什么后果）
- 与主线关系：（这条支线如何影响主线剧情，是助攻还是制造障碍）
- 支线主角：（这条支线涉及哪些角色，这些角色的支线目标是什么）

【伏笔埋设】请设计${minForeshadow}个以上伏笔，每个必须包含：
- 标题：（伏笔的名称，要神秘但有规律可循）
- 描述：（100字以上，详细描述这个伏笔的内容，读者可能在哪里注意到线索）
- 线索分布：（分别在哪几个章节埋下了什么具体线索，要有具体情节）
- 揭示章节：（必须精确到第X章揭示）
- 揭示方式：（这个伏笔是如何被揭示的，揭示后产生了什么影响）
- 与其他伏笔关系：（这个伏笔和其他伏笔是否有关联）

【高潮设计】请设计${climaxCount}个高潮点，每个高潮包含：
- 标题：（高潮的名称，要让人热血沸腾）
- 所属阶段：（发展阶段/高潮阶段）
- 涉及章节：（第X章）
- 冲突双方：（谁和谁打/对抗，背景是什么）
- 核心转折：（这次高潮最重要的转折点是什么，局势如何逆转）
- 激战场面：（100字以上，描述最激烈的场面，要具体到招式、台词、场景）
- 结局影响：（高潮之后，局势发生了什么变化，对手/势力/格局如何改变）
- 对主角成长意义：（这次经历让主角获得了什么，是能力提升还是心境突破）

【章节钩子设计】（为每个阶段设计核心钩子，确保读者追读）
- 开局钩子（第1~${ch1}章，3个）：每个钩子用一句话描述，要让人想看下一章
- 发展钩子（第${ch1 + 1}~${ch2}章，3个）：每个钩子描述一个转折点或悬念
- 高潮钩子（第${ch2 + 1}~${ch3}章，3个）：每个钩子描述一个燃点或爆点
- 结局钩子（第${ch3 + 1}~${totalCh}章，2个）：每个钩子描述一个悬念或结局悬念

【结局设计】
- 结局概述：（200字以上，详细描述最终结局，包含主要角色的最终命运）
- 伏笔收束：（列举所有伏笔是如何收束的，确保每个伏笔都有交代）
- 主角最终状态：（主角最终成为什么样的人，站在什么位置）
- 其他角色结局：（${minSupporting}个主要配角的最终命运都要有交代）
- 世界格局变化：（故事结束后，这个世界变成了什么样子）
- 开放还是完结：（是否留有悬念，为后续埋下伏笔）

【强制要求】
- 总大纲字数不得低于6000字
- 所有角色姓名必须独特，禁止常见名字
- 所有角色必须明确性别
- 支线章节范围：开局第1~${ch1}章，发展第${ch1 + 1}~${ch2}章，高潮第${ch2 + 1}~${ch3}章，结局第${ch3 + 1}~${totalCh}章，严禁跨阶段
- 每个高潮点描述不得少于100字，每个配角设定不得少于80字`
        : `## 输出格式要求
请使用清晰易读的中文格式输出大纲，严格按照以下结构，不要输出任何其他无关内容：

【主线剧情】
一句话主线（30字以内）：XXXXXXXX
主线简述（100-150字）：概括故事的核心走向

【主角设定】
姓名：（必须独特有辨识度，禁止使用常见名字）
性别：男/女
性格：性格特点
核心追求：主角的目标

【配角设定】
列出${minSupporting}个重要配角，每个包含：姓名（根据角色身份和小说风格动态生成，必须与整体世界观统一）、性别、角色定位、与主角关系、性格特点、背景故事（50字以上）

【支线剧情】
请设计${minSubplots}条以上支线，每条包含：标题、所属阶段、涉及章节（第X章-X章）、核心冲突（50字以上）、与主线关系

【伏笔埋设】
请设计${minForeshadow}个以上伏笔，每个包含：标题、描述（50字以上）、线索分布、揭示章节（第X章）

【高潮设计】
请设计${climaxCount}个高潮点，每个包含：标题、涉及章节（第X章）、核心转折描述（50字以上）、结局影响

【结局设计】
结局描述（100字以上），包含所有角色最终命运

【强制要求】
- 大纲总字数不得低于3000字
- 所有角色姓名必须独特，禁止常见名字
- 所有角色必须明确性别
- 支线章节范围：开局第1~${ch1}章，发展第${ch1 + 1}~${ch2}章，高潮第${ch2 + 1}~${ch3}章，结局第${ch3 + 1}~${totalCh}章`);

      const nuanLabel = outlineFormData.nuan === 'none' ? '无虐'
        : outlineFormData.nuan === 'light' ? '微虐'
        : outlineFormData.nuan === 'medium' ? '中虐'
        : outlineFormData.nuan === 'heavy' ? '重虐'
        : outlineFormData.nuan === 'glass' ? '玻璃渣'
        : '微虐';

      const rhythmLabel = outlineFormData.rhythm === 'none' ? '不指定（AI自由把握）'
        : outlineFormData.rhythm === 'fast' ? '快节奏'
        : outlineFormData.rhythm === 'medium' ? '中节奏'
        : outlineFormData.rhythm === 'slow' ? '慢节奏'
        : '中节奏';

      const prompt = template.prompt.base + `

请根据以下详细信息生成小说大纲：

## 基础信息
- 书名：${outlineFormData.name || '待定'}
- 小说类型：${outlineFormData.type}
- 目标受众：${outlineFormData.targetAudience || '全年龄'}
- 计划总章节：${outlineFormData.chapterCount || 100} 章

## 风格设定
- 模板类型：${template.name}
- 节奏风格：${rhythmLabel}
- 基调：${(outlineFormData.tags || []).filter(t => t !== '').join('、') || '待定'}

## 世界观
- 世界观：${worldViewLabel || '待定'}
- 金手指：${goldenFingerLabel || '待定'}
- 核心冲突：${coreConflictLabel || '待定'}

## 感情线
- CP类型：${cpTypeLabel || '待定'}
- 虐点程度：${nuanLabel}

## 爽点类型（读者最期待的剧情）
${hotSpotsList}

## 用户需求描述
${outlineUserRequirement}

【角色命名原则】
根据上述小说类型"${outlineFormData.type}"、世界观"${worldViewLabel}"、基调"${(outlineFormData.tags || []).filter(t => t !== '').join('、') || '待定'}"，动态生成符合该小说风格的角色姓名。例如：
- 仙侠/玄幻类：可参考道教神话、山海经典故、诗词意境
- 都市/现代类：融合当代审美，可结合职业特色（如医生用中药名、律师用典故名）
- 穿越/历史类：参考目标朝代的文化背景和命名习惯
- 西幻/异世界：参考目标世界的文化神话体系

${detailRequirements}

【重要提醒】请直接输出大纲正文，不要任何前缀/后缀说明；禁止先输出分析或思考段落，立即开始写大纲。`;

      // 使用 proxyAI 接口进行流式生成
      const systemPrompt = `你是顶级网络小说大纲策划专家，拥有10年网文创作经验，精通都市、玄幻、穿越、系统流等各类网文类型的创作规律。

【质量标准】
你生成的大纲必须达到以下标准：
1. 内容详尽：每个章节阶段至少300-500字描述，绝不蜻蜓点水
2. 人物立体：每个角色有独特性格、完整背景故事、明确成长弧线
3. 情节扎实：爽点设计具体到场景，不写"主角变强"这种空话，要写出具体的打脸场景
4. 逻辑自洽：伏笔埋设和揭示前后呼应，不矛盾
5. 商业价值：每章都有钩子，让读者想追读下一章

【绝对禁止】
- 使用张三、李四、王五、林凡、叶凡、萧炎、陈枫、韩立、秦羽、林云、张伟等常见名字
- 写流水账式连接词
- 角色设定前后矛盾
- 情节简单重复（不能连续3章以上都是打脸）
- 大纲总字数低于5000字（本小说${totalCh}章，最低生成5000字以上详尽大纲）
- 支线章节号与主线阶段冲突（如开局阶段写第80章的内容）

【角色命名规范 — 根据小说设定动态适配】
1. 命名必须与小说类型、世界观、文化背景高度匹配
2. 命名思路引导：
   - 玄幻/修仙/古风：参考古典诗词、成语典故、神话题材（如基于"凤鸣岐山"可衍生"岐"、"凤"、"鸣"等意象）
   - 都市/现代：融合当代审美与角色身份（如医生可用"白术"、"青黛"等中药名，律师可用"庭安"、"令槐"等古雅名）
   - 穿越/系统：古今结合或有反差萌的名字
   - 西幻/异世界：参考目标世界的文化神话（如希腊、北欧、凯尔特等）
3. 姓氏与名字风格统一（古风小说避免出现"欧阳"配"伟强"这种违和组合）
4. 禁止使用常见名字组合（如"张伟"、"王芳"、"刘洋"等）

【输出纪律 — 与模型思考模式无关，必须遵守】
- 不要输出任何思考过程、推理步骤、内心独白或「我先分析…」「让我想想」等套话
- 不要使用 <think>、<thinking> 等标签；第一句起即为可读的大纲正文（标题或第一章节内容）
- 不要先写摘要再写正文；直接按大纲结构连续输出

【章节阶段划分】
- 开局阶段：第1~${ch1}章（建立世界观、主角登场、第一个冲突爆发）
- 发展阶段：第${ch1 + 1}~${ch2}章（冲突升级、支线展开、势力划分）
- 高潮阶段：第${ch2 + 1}~${ch3}章（核心冲突爆发、主角与BOSS决战）
- 结局阶段：第${ch3 + 1}~${totalCh}章（收束所有伏笔、角色命运揭晓）`;

      // 构建请求参数 — 必须走 /assist/ai/proxy-ai：禁用深度思考并过滤 reasoning_content
      const novelTitleForOutline = novel?.title || '未知项目';
      const params = {
        model: 'deepseek-v4-flash',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: prompt }
        ],
        temperature: 0.8,
        max_tokens: 32768,  // DeepSeek V4 Flash 最大支持 32K
        extra_body: {},
        stream: true,
        billingInfo: {
          novelName: novelTitleForOutline,
          chapterNumber: '',
          operationType: 'AI生成小说大纲'
        }
      };

      // 获取 API 基础 URL
      const API_BASE_URL = getApiBaseUrl();

      // ============================================================
      // 【调试日志】输出完整的请求参数
      // ============================================================
      console.group('🤖 AI生成大纲 - 请求参数详情');
      console.log('📌 模型: *** (前端隐藏具体模型)');  // 不显示具体模型名称
      console.log('📌 Temperature:', params.temperature);
      console.log('📌 Max Tokens:', params.max_tokens);
      console.log('📌 请求路径:', '/assist/ai/proxy-ai');
      console.log('');
      console.log('【System Prompt 内容】');
      console.log(systemPrompt);
      console.log('');
      console.log('【User Prompt 内容】');
      console.log(prompt);
      console.log('');
      console.log('【完整 messages】');
      console.log(JSON.stringify(params.messages, null, 2));
      console.log('【billingInfo】');
      console.log(JSON.stringify(params.billingInfo, null, 2));
      console.groupEnd();
      // ============================================================

      // 使用流式生成
      const result = await handleStreamGeneration(
        API_BASE_URL,
        params,
        '大纲生成',
        // 接收到内容块时的回调函数
        (chunk, fullContent, currentWordCount) => {
          // 清理 markdown 代码块标记，保留纯内容
          let cleanedContent = fullContent
            .replace(/^```json\s*/gm, '')
            .replace(/^```\s*/gm, '')
            .replace(/\s*```$/gm, '')
            .trim();
          setStreamingOutline(cleanedContent);
          setOutlineWordCount(currentWordCount);
        },
        // 状态更新回调函数
        (statusMessage) => {
          console.log('大纲生成状态:', statusMessage);
        },
        '/assist/ai/proxy-ai'
      );

      console.log('大纲生成完成:', result);

      // 检查生成是否被截断（max_tokens限制）
      const isTruncated = result.finishReason === 'length' || (result.content && !result.isComplete);

      if (isTruncated) {
        setSnackbar({
          open: true,
          message: '大纲被截断了（输出超出限制），内容可能不完整，建议缩短字数或分批生成',
          severity: 'warning'
        });
      }

      // 检查生成是否完整
      if (!result.isComplete && !isTruncated) {
        throw new Error('大纲生成未完成，请重试');
      }

      // 清理最终内容（移除可能的代码块标记）
      const finalContent = result.content
        .replace(/^```json\s*/gm, '')
        .replace(/^```\s*/gm, '')
        .replace(/\s*```$/gm, '')
        .trim();
      setStreamingOutline(finalContent);
      setOutlineWordCount(result.wordCount);
      setGeneratingOutline(false);
      setIsEditingOutline(true);
      setSnackbar({ open: true, message: '大纲生成成功！请查看下方内容，可手动修改后保存', severity: 'success' });

    } catch (error) {
      console.error('生成大纲失败:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || '生成失败，请重试',
        severity: 'error'
      });
      setGeneratingOutline(false);
    }
  };

  // 保存大纲（直接保存原文，不自动解析）
  const handleSaveOutline = async () => {
    if (!streamingOutline.trim()) {
      setSnackbar({ open: true, message: '请先生成大纲', severity: 'warning' });
      return;
    }

    
    // 保存原文，不自动解析
    const generatedContent = streamingOutline;

    // 【修复】确保新建的大纲有正确的 _id
    const existingOutlineId = outlines.length > 0 && outlines[0]._id && !outlines[0]._id.startsWith('outline_new_')
      ? outlines[0]._id
      : 'outline_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

    // 大纲显示名：弹窗内书名 > 正文解析出的标题 > 当前作品名 > 默认
    const explicitName = outlineFormData.name?.trim() || '';
    const fromContent = extractBookNameFromContent(generatedContent);
    const novelTitle = (novel?.title || '').trim();
    const resolvedOutlineName = explicitName || fromContent || novelTitle || '未命名大纲';

    const newOutline = {
      ...outlineFormData,
      _id: existingOutlineId,
      name: resolvedOutlineName,
      content: generatedContent,
      mainPlot: outlineFormData.mainPlot || {},
      protagonist: outlineFormData.protagonist || {},
      worldSettings: outlineFormData.worldSettings || {},
      subPlots: outlineFormData.subPlots || [],
      foreshadowing: outlineFormData.foreshadowing || [],
      climaxes: outlineFormData.climaxes || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setOutlines([newOutline]);

    // 持久化到后端
    try {
      const payload = {
        name: resolvedOutlineName,
        type: outlineFormData.type || '仙侠',
        targetAudience: outlineFormData.targetAudience || '全年龄',
        rhythm: outlineFormData.rhythm || '快节奏',
        worldView: outlineFormData.worldSettings?.background || '',
        goldenFinger: outlineFormData.protagonist?.goldenFinger || '',
        coreConflict: outlineFormData.coreConflict || '',
        cpType: outlineFormData.cpType || '',
        nuan: outlineFormData.nuan || '',
        wordCount: outlineFormData.wordCount || 0,
        chapterCount: outlineFormData.chapterCount || 0,
        mainPlot: newOutline.mainPlot,
        subPlots: newOutline.subPlots,
        foreshadowing: newOutline.foreshadowing,
        climaxes: newOutline.climaxes || [],
        tags: outlineFormData.tags || [],
        content: generatedContent,
        updatedAt: new Date().toISOString(),
        // 新增字段
        storyStructure: outlineFormData.storyStructure || { beginning: '', development: '', climax: '', ending: '' },
        worldSettings: outlineFormData.worldSettings || { background: '', levelSystem: '', factions: [] },
        coreConflicts: outlineFormData.coreConflicts || [],
        characterNetwork: outlineFormData.characterNetwork || [],
      };
      await apiClient.post(`/assist/novels/${assistNovelId}/outline`, payload);

      setSnackbar({ open: true, message: `大纲保存成功！请稍后点击「解析大纲」手动提取章节信息`, severity: 'success' });

    } catch (e) {
      console.error('大纲保存失败:', e);
      setSnackbar({ open: true, message: '大纲保存失败', severity: 'error' });
    }

    setShowOutlineGenDialog(false);
    setOutlineUserRequirement('');
    setStreamingOutline('');
    setOutlineWordCount(0);
    setIsEditingOutline(false);
  };

  // 辅助函数：从文本中提取章节内容
  const extractSection = (text, keyword) => {
    const regex = new RegExp(`${keyword}[：:]([\\s\\S]*?)(?=\\n##|\\n#|\\n\\d+\\.|\\n【|$)`, 'i');
    const match = text.match(regex);
    return match ? match[1].trim() : '';
  };

  // 辅助函数：从文本中提取字段
  const extractField = (text, field, defaultValue) => {
    const regex = new RegExp(`${field}[：:]\\s*([^\\n]+)`, 'i');
    const match = text.match(regex);
    return match ? match[1].trim() : defaultValue;
  };

  // 辅助函数：从文本中提取列表
  const extractList = (text, keyword) => {
    const regex = new RegExp(`${keyword}[：:]\\s*([\\s\\S]*?)(?=\\n##|\\n#|\\n\\n|$)`, 'i');
    const match = text.match(regex);
    if (!match) return [];
    return match[1].split('\n').filter(line => line.trim()).map(line => line.replace(/^[-*\d.]+\s*/, '').trim());
  };

  // 辅助函数：从文本中提取列表（对象格式）
  const extractListAsObjects = (text, keyword) => {
    const list = extractList(text, keyword);
    return list.map(item => ({ name: item, description: '' }));
  };

  // 从大纲内容中解析小说书名（用于保存时自动填大纲名称）
  const extractBookNameFromContent = (content) => {
    if (!content || typeof content !== 'string') return '';
    const trimmed = content.trim();
    // AI 生成大纲常见格式：【小说标题】\nxxx
    const bracketTitle = trimmed.match(/【\s*小说标题\s*】\s*\n?\s*([^\n【]+)/);
    if (bracketTitle) return bracketTitle[1].replace(/^[《"\s]+|[》"\s]+$/g, '').trim();
    // 《xxx》或《xxx》小说大纲
    const guillemet = trimmed.match(/[《]([^》]+)[》](?:小说大纲)?/);
    if (guillemet) return guillemet[1].trim();
    // 第一行 "xxx小说大纲" 或 书名：xxx
    const firstLine = trimmed.split(/\n/)[0] || '';
    const novelOutline = firstLine.match(/^(.+?)小说大纲\s*$/);
    if (novelOutline) return novelOutline[1].replace(/^[《"]|[》"]$/g, '').trim();
    const nameLabel = content.match(/书名[：:]\s*([^\n]+)/);
    if (nameLabel) return nameLabel[1].replace(/^[《"]|[》"]$/g, '').trim();
    return '';
  };

  // ==================== 数据复用接口 ====================
  // 供其他模块调用的大纲数据

  // 获取当前选中的大纲（用于章纲生成、角色生成等）
  const getCurrentOutline = () => {
    return currentOutline || (outlines.length > 0 ? outlines[0] : null);
  };

  // 获取主线剧情（用于AI续写上下文）
  const getMainPlot = () => {
    const outline = getCurrentOutline();
    return outline?.mainPlot?.storyline || '';
  };

  // 获取世界观摘要（用于AI生成时的上下文）
  const getWorldSummary = () => {
    const outline = getCurrentOutline();
    if (!outline) return '';
    
    const { worldSettings, protagonist } = outline;
    const parts = [];
    
    if (worldSettings?.background) parts.push(`世界背景：${worldSettings.background}`);
    if (worldSettings?.levelSystem) parts.push(`等级体系：${worldSettings.levelSystem}`);
    if (protagonist?.goldenFinger) parts.push(`金手指：${protagonist.goldenFinger}`);
    
    return parts.join('\n');
  };

  // 获取大纲风格信息（用于AI润色）
  const getOutlineStyle = () => {
    const outline = getCurrentOutline();
    return {
      type: outline?.type || '仙侠',
      tags: outline?.tags || [],
      protagonist: outline?.protagonist || {}
    };
  };

  // 获取主角人设（用于角色生成）
  const getProtagonist = () => {
    const outline = getCurrentOutline();
    return outline?.protagonist || {};
  };

  // 获取支线列表（用于章纲关联）
  const getSubPlots = () => {
    const outline = getCurrentOutline();
    return outline?.subPlots || [];
  };

  // 获取伏笔列表（用于章纲关联）
  const getForeshadowing = () => {
    const outline = getCurrentOutline();
    return outline?.foreshadowing || [];
  };

  // 获取人物关系（用于角色生成）
  const getCharacterNetwork = () => {
    const outline = getCurrentOutline();
    return outline?.characterNetwork || [];
  };

  // 生成AI上下文（用于AI续写/润色）
  const generateAIContext = (options = {}) => {
    const outline = getCurrentOutline();
    if (!outline) return '';
    
    const { includeWorld = true, includeMainPlot = true, includeStyle = true } = options;
    const parts = [];
    
    if (includeMainPlot && outline.mainPlot?.storyline) {
      parts.push(`【主线】${outline.mainPlot.storyline}`);
    }
    
    if (includeWorld) {
      const worldSummary = getWorldSummary();
      if (worldSummary) parts.push(`【世界观】${worldSummary}`);
    }
    
    if (includeStyle) {
      const { type, tags } = outline;
      if (type) parts.push(`【类型】${type}`);
      if (tags?.length) parts.push(`【基调】${tags.join('、')}`);
    }
    
    return parts.join('\n\n');
  };

  // ==================== 数据复用接口结束 ====================

  // 渲染章节项（窄屏 / 移动端由 isChaptersNarrow 启用紧凑样式；卷下列表勿传 showVolume，避免重复卷标）
  // index 用于错帧动画，传入 -1 时不产生延迟
  const renderChapterItem = (chapter, showVolume = false, index = -1) => {
    const statusConfig = getChapterStatus(chapter);
    const isCurrentChapter = currentChapter?._id === chapter._id;
    const isInlineEditing = selectedChapterForEdit?._id === chapter._id;
    const isTitleEditing = inlineEditingChapterId === chapter._id;
    const actualWord = chapter.outline?.actualWordCount || 0;
    const num = chapter.chapterNumber;
    const raw = (chapter.title || '').trim();
    const defaultTitle = `第${num}章`;
    const showSubtitle = raw && raw !== defaultTitle;
    const volName = showVolume && chapter.volumeId ? (volumes.find(v => v._id === chapter.volumeId)?.name || '默认卷') : null;
    const showVolBadge = Boolean(volName);
    const volChipSecondRow = showVolBadge && !isChaptersNarrow;
    const hasContent = chapterHasWrittenBody(chapter);

    // 有内容 → 琥珀绿边框/底色；无内容 → 深灰底细边
    const bodyBg   = hasContent ? 'rgba(6,182,212,0.09)'  : 'rgba(255,255,255,0.03)';
    const bodyBorder = hasContent ? 'rgba(6,182,212,0.38)' : 'rgba(255,255,255,0.06)';
    const bodyHover  = hasContent ? 'rgba(6,182,212,0.14)' : 'rgba(255,255,255,0.06)';
    const bodyShadow = hasContent
      ? '0 2px 12px rgba(6,182,212,0.18), inset 0 1px 0 rgba(255,255,255,0.06)'
      : '0 2px 8px rgba(0,0,0,0.2)';

    const animDelay = index >= 0 ? Math.min(index * 0.04, 0.5) : 0;
    const selGlowA = '0 0 0 1px rgba(125,211,252,0.65), 0 8px 32px rgba(56,189,248,0.32), inset 0 1px 0 rgba(255,255,255,0.08)';
    const selGlowB = '0 0 0 1px rgba(186,230,253,0.95), 0 14px 44px rgba(56,189,248,0.48), inset 0 1px 0 rgba(255,255,255,0.12)';

    return (
      <Box
        component={motion.div}
        layout
        initial={motionReduced ? false : { opacity: 0, x: -14, scale: 0.97 }}
        animate={{
          opacity: 1,
          x: 0,
          scale: isInlineEditing && !isChaptersNarrow ? [1, 1.01, 1] : 1,
          ...(isInlineEditing && !isChaptersNarrow
            ? { boxShadow: [selGlowA, selGlowB, selGlowA] }
            : {}),
        }}
        exit={motionReduced ? false : { opacity: 0, x: 8, scale: 0.97 }}
        transition={{
          delay: motionReduced ? 0 : animDelay,
          duration: motionReduced ? 0 : 0.32,
          ease: [0.22, 1, 0.36, 1],
          layout: { duration: motionReduced ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] },
          ...(isInlineEditing && !isChaptersNarrow
            ? {
                scale: { repeat: Infinity, duration: 2.5, ease: 'easeInOut' },
                boxShadow: { repeat: Infinity, duration: 2.5, ease: 'easeInOut' },
              }
            : {}),
        }}
        whileHover={{
          y: -2,
          scale: isInlineEditing ? 1.01 : 1.008,
          boxShadow: hasContent
            ? '0 8px 28px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.1)'
            : '0 8px 26px rgba(56,189,248,0.15), 0 4px 16px rgba(0,0,0,0.35)',
        }}
        whileTap={{ scale: 0.978 }}
        key={chapter._id}
        onClick={() => {
          if (isTitleEditing) return;
          setInlineEditingChapterId(null);
          setSelectedChapterForEdit(chapter);
          setCurrentChapter(chapter);
          setChapterContent(chapter.content || '');
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          willChange: 'transform',
          transform: 'translateZ(0)',
          p: isChaptersNarrow ? '4px 5px' : { xs: 1.5, sm: 1 },
          mb: isChaptersNarrow ? 0.25 : 0.75,
          bgcolor: isTitleEditing
            ? 'rgba(0,255,159,0.08)'
            : isInlineEditing
              ? 'rgba(56,189,248,0.16)'
              : isCurrentChapter
                ? 'rgba(56,189,248,0.09)'
                : bodyBg,
          border: isTitleEditing
            ? '1px solid rgba(52,211,153,0.5)'
            : isInlineEditing
              ? '1px solid rgba(125,211,252,0.85)'
              : isCurrentChapter
                ? '1px solid rgba(125,211,252,0.45)'
                : `1px solid ${bodyBorder}`,
          borderRadius: isChaptersNarrow ? 1 : 1.5,
          cursor: isTitleEditing ? 'default' : 'pointer',
          '&:hover': { bgcolor: isTitleEditing ? 'rgba(0,255,159,0.1)' : isInlineEditing ? 'rgba(56,189,248,0.2)' : bodyHover },
          minHeight: isChaptersNarrow ? 0 : { xs: 52, sm: 40 },
          boxShadow: isChaptersNarrow
            ? (isInlineEditing ? '0 0 0 1px rgba(125,211,252,0.55), 0 3px 14px rgba(56,189,248,0.2)' : 'none')
            : isInlineEditing
              ? undefined
              : isCurrentChapter
                ? '0 4px 22px rgba(56,189,248,0.2), inset 0 1px 0 rgba(255,255,255,0.06)'
                : bodyShadow,
          position: 'relative',
          overflow: 'hidden',
          '&::before': hasContent ? {
            content: '""',
            position: 'absolute',
            left: 0, top: '20%', bottom: '20%',
            width: 2.5,
            borderRadius: '0 2px 2px 0',
            bgcolor: '#06b6d4',
            boxShadow: '0 0 6px #06b6d4',
          } : {},
        }}
      >
        <Box
          className="workspace-chapter-list-item-main"
          sx={{
            display: 'flex',
            alignItems: volChipSecondRow ? 'flex-start' : 'center',
            gap: isChaptersNarrow ? 0.5 : 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: volChipSecondRow ? 'column' : 'row',
              alignItems: volChipSecondRow ? 'stretch' : 'center',
              gap: volChipSecondRow ? 0.5 : 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0, minWidth: 0, width: volChipSecondRow ? '100%' : 'auto', flex: volChipSecondRow ? undefined : 1 }}>
              <Typography
                component="span"
                sx={{
                  color: statusConfig.label === '仅章纲' ? 'rgba(255,255,255,0.35)' : statusConfig.color,
                  fontSize: isChaptersNarrow ? 13 : { xs: 13, sm: 14 },
                  fontWeight: isChaptersNarrow ? 500 : 600,
                  flexShrink: 0,
                  lineHeight: 1.25,
                  mr: showSubtitle || statusConfig.label !== '仅章纲' ? 0.2 : 0,
                }}
              >
                第{num}章
              </Typography>
              {isTitleEditing ? (
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  defaultValue={raw && raw !== defaultTitle ? raw : ''}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = e.target.value.trim();
                      handleSaveChapterTitle({ ...chapter, title: val });
                      setInlineEditingChapterId(null);
                    }
                    if (e.key === 'Escape') {
                      setInlineEditingChapterId(null);
                    }
                  }}
                  onBlur={(e) => {
                    const val = e.target.value.trim();
                    handleSaveChapterTitle({ ...chapter, title: val });
                    setInlineEditingChapterId(null);
                  }}
                  InputProps={{
                    sx: { color: '#fff', fontSize: 13, py: 0.25, px: 1 },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { color: '#fff', bgcolor: 'rgba(0,0,0,0.3)', height: 28 },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#00ff9f' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#00ff9f' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#00ff9f', borderWidth: 2 },
                    minWidth: 0,
                    flex: 1,
                  }}
                />
              ) : showSubtitle ? (
                <Typography
                  component="span"
                  sx={{
                    color: isInlineEditing ? 'var(--ws-accent)' : isCurrentChapter ? statusConfig.color : '#b8bcc8',
                    fontSize: isChaptersNarrow ? 12 : { xs: 13, sm: 14 },
                    fontWeight: isChaptersNarrow ? 400 : (isCurrentChapter || isInlineEditing ? 600 : 400),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.25,
                    textAlign: 'left',
                  }}
                >
                  ：{raw}
                </Typography>
              ) : statusConfig.label !== '仅章纲' ? (
                <Typography
                  component="span"
                  sx={{
                    color: 'rgba(255,255,255,0.28)',
                    fontSize: isChaptersNarrow ? 12 : { xs: 13, sm: 14 },
                    fontStyle: 'italic',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                    lineHeight: 1.25,
                    textAlign: 'left',
                  }}
                >
                  ：未命名
                </Typography>
              ) : (
                <Box component="span" sx={{ flex: 1, minWidth: 0 }} />
              )}
              {showVolBadge && isChaptersNarrow && (
                <Typography
                  component="span"
                  sx={{
                    flexShrink: 0,
                    ml: 0.5,
                    px: 0.5,
                    py: 0,
                    fontSize: 10,
                    lineHeight: 1.5,
                    color: '#facc15',
                    bgcolor: 'rgba(250,204,21,0.12)',
                    border: '1px solid rgba(250,204,21,0.35)',
                    borderRadius: 1,
                    maxWidth: '42%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={volName}
                >
                  {volName}
                </Typography>
              )}
            </Box>
            {volChipSecondRow && (
              <Chip
                label={volName}
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  flexShrink: 0,
                  height: 20,
                  maxWidth: '100%',
                  fontSize: 10,
                  bgcolor: 'rgba(255,215,0,0.15)',
                  color: '#ffd700',
                  '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                }}
              />
            )}
          </Box>
        </Box>
        {!isTitleEditing && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: 0.5 }}>
            {actualWord > 0 && (
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: isChaptersNarrow ? 10 : 11 }}>
                {actualWord >= 1000 ? (actualWord / 1000).toFixed(1) + 'k' : actualWord}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setInlineEditingChapterId(chapter._id);
              }}
              className="workspace-chapter-row-edit"
              sx={{
                color: 'rgba(255,255,255,0.45)',
                '&:hover': { color: '#00ff9f' },
                ...(isChaptersNarrow ? { p: 0.35, minWidth: 28, minHeight: 28 } : { minWidth: 32, minHeight: 32 }),
              }}
              title="修改章节标题"
            >
              <EditIcon sx={{ fontSize: isChaptersNarrow ? 16 : 18 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDeleteChapter(chapter);
                setConfirmDeleteDialogOpen(true);
              }}
              className="workspace-chapter-row-delete"
              sx={{
                color: 'rgba(255,255,255,0.35)',
                '&:hover': { color: '#f87171', bgcolor: 'rgba(239,68,68,0.12)' },
                ...(isChaptersNarrow ? { p: 0.35, minWidth: 28, minHeight: 28 } : { minWidth: 32, minHeight: 32 }),
              }}
              title="删除章节"
            >
              <DeleteIcon sx={{ fontSize: isChaptersNarrow ? 16 : 18 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  // 获取章节关联上下文信息（角色快照 + 伏笔 + 承接上文）
  const getChapterContext = (chapter) => {
    if (!chapter) return null;
    const vol = volumes.find(v => v._id === chapter.volumeId);
    const prevChapter = chapters
      .filter(c => c.volumeId === chapter.volumeId && c.chapterNumber < chapter.chapterNumber)
      .sort((a, b) => b.chapterNumber - a.chapterNumber)[0];
    const linkedCharIds = chapter.outline?.plotPoints
      ? [...new Set((chapter.outline.plotPoints || []).flatMap(pt => pt.characterIds || []))]
      : [];
    const linkedChars = characters.filter(c => linkedCharIds.includes(c._id));
    // 角色状态快照：从 characters 状态历史中汇总本章涉及的角色状态
    const charStates = {};
    (chapter.outline?.plotPoints || []).flatMap(pt => pt.characterIds || []).filter((v, i, a) => a.indexOf(v) === i).forEach(charId => {
      const char = characters.find(c => c._id === charId);
      if (char) {
        const latest = char.stateHistory?.length > 0 ? char.stateHistory[char.stateHistory.length - 1] : null;
        charStates[charId] = {
          currentState: latest?.state || char.currentState || '',
          description: latest?.description || '',
          lifeStatus: char.lifeStatus || 'alive',
          emotionStatus: char.emotionStatus || '',
        };
      }
    });
    const linkedFsIds = chapter.outline?.plotPoints
      ? [...new Set((chapter.outline.plotPoints || []).flatMap(pt => pt.foreshadowingIds || []))]
      : [];
    const allFs = outlines[0]?.foreshadowing || [];
    const linkedFs = allFs.filter(f => {
      const fid = f.id || f._id;
      return linkedFsIds.includes(fid);
    });
    // 关联词条：从 plotPoints[].itemIds 和 chapter.itemIds 合并
    const linkedItemIds = [
      ...(chapter.itemIds || []),
      ...(chapter.outline?.plotPoints
        ? (chapter.outline.plotPoints || []).flatMap(pt => pt.itemIds || [])
        : [])
    ];
    const linkedItemIdsUnique = [...new Set(linkedItemIds.filter(Boolean))];
    const linkedItems = items.filter(i => linkedItemIdsUnique.includes(i._id));
    return {
      volume: vol,
      previousChapter: prevChapter,
      linkedCharacters: linkedChars,
      characterStates: charStates,
      linkedForeshadowings: linkedFs,
      linkedItems,
      mainPlot: outlines[0]?.mainPlot,
      subPlots: outlines[0]?.subPlots || [],
    };
  };

  // 渲染卷折叠面板（卷头配色：天青 + 细边，避免大面积金黄）
  const renderVolumeAccordion = (volume, volIndex = 0) => {
    const isExpanded = expandedVolumes[volume._id] !== false; // 默认展开
    // 与章纲库一致：含 volumeId 匹配 + 序号槽位 + 首卷未归类兜底
    const sortedChapters = getChaptersForVolumeList(volume, volIndex);
    const plannedDen = volume.chapterCount ?? sortedChapters.length ?? 0;

    return (
      <Box key={volume._id} sx={{ mb: 1.25 }}>
        {/* 大卷标题栏：sticky 吸顶（勿在 sticky 节点上用 layout/transform，故包一层） */}
        <Box
          id={`vol-header-${volume._id}`}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 8 + Math.min(volIndex, 4),
            mb: isExpanded ? 0.5 : 0,
            borderRadius: 1.5,
            bgcolor: 'rgba(8, 14, 24, 0.92)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: isExpanded ? '0 4px 20px rgba(0,0,0,0.35)' : 'none',
          }}
        >
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: -10, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            whileHover={{ scale: 1.008, boxShadow: '0 0 24px rgba(56,189,248,0.2), 0 0 1px rgba(125,211,252,0.4)' }}
            whileTap={{ scale: 0.99 }}
            transition={motionReduced ? { duration: 0 } : { delay: volIndex * 0.07, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={() => toggleVolumeExpand(volume._id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: { xs: 1.5, sm: 1.25 },
              bgcolor: 'rgba(56,189,248,0.07)',
              border: '1px solid rgba(125,211,252,0.28)',
              borderRadius: 1.5,
              cursor: 'pointer',
              transition: 'background 0.28s ease, border-color 0.28s ease, box-shadow 0.28s ease',
              '&:hover': { bgcolor: 'rgba(56,189,248,0.11)', borderColor: 'rgba(125,211,252,0.48)' },
              minHeight: { xs: 44, sm: 'auto' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              <Box
                component={motion.div}
                animate={{ rotate: isExpanded ? 0 : -90 }}
                transition={{ type: 'spring', stiffness: 320, damping: 22 }}
                sx={{ color: '#7dd3fc', display: 'flex', alignItems: 'center' }}
              >
                <ExpandMoreIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{ color: '#e0f2fe', fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}>
                {volume.name || '第' + volume.order + '卷'} {volume.title && `· ${volume.title}`}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                ({getChapterCountForVolumePlan(volume, volIndex)}章)
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleCreateChapter(volume._id);
                }}
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#7dd3fc' }, minWidth: 36, minHeight: 36 }}
                title="添加章节"
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setVolumeFormData({ name: volume.name, title: volume.title || '', chapterCount: volume.chapterCount || 10 }); setEditingVolumeId(volume._id); setVolumeDialogOpen(true); }}
                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#00ff9f' }, minWidth: 36, minHeight: 36 }}
                title="编辑卷"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* 章节列表 — 弹性展开 + 错帧入场（motionReduced 时直接显示） */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={motionReduced ? false : { height: 0, opacity: 0 }}
              animate={motionReduced ? false : { height: 'auto', opacity: 1 }}
              exit={motionReduced ? false : { height: 0, opacity: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.36, ease: [0.22, 1, 0.36, 1] }}
              style={{ overflow: motionReduced ? 'visible' : 'hidden' }}
            >
              <Box sx={{ pl: { xs: 1, sm: 2 }, mt: 0.25, position: 'relative' }}>
                {sortedChapters.length === 0 ? (
                  <Box
                    sx={{ p: 1.5, textAlign: 'center' }}
                  >
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>
                      暂无章节，点击 + 添加
                    </Typography>
                  </Box>
                ) : (
                  <AnimatePresence>
                    {sortedChapters.map((chapter, idx) => (
                      <Box
                        key={chapter._id}
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, x: -8 }}
                        animate={motionReduced ? false : { opacity: 1, x: 0 }}
                        transition={motionReduced ? { duration: 0 } : { delay: Math.min(idx * 0.035, 0.35), duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                      >
                        {renderChapterItem(chapter, false, idx)}
                      </Box>
                    ))}
                  </AnimatePresence>
                )}
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    );
  };

  // ================================================================
  // 章纲·章节统一创作面板
  // ================================================================
  const renderChaptersPanel = () => {
    const ctx = selectedChapterForEdit ? getChapterContext(selectedChapterForEdit) : null;
    const plotPointTypes = [
      { value: 'setup', label: '建立', color: '#00f0ff' },
      { value: 'conflict', label: '冲突', color: '#ff6b35' },
      { value: 'climax', label: '高潮', color: '#ffd700' },
      { value: 'resolution', label: '解决', color: '#00ff9f' },
      { value: 'transition', label: '过渡', color: 'var(--ws-accent)' },
    ];
    const getPtColor = (t) => plotPointTypes.find(p => p.value === t)?.color || 'var(--ws-accent)';
    const getPtLabel = (t) => plotPointTypes.find(p => p.value === t)?.label || t;

    const handleSaveOutlinePanel = async () => {
      if (!assistNovelId || !selectedChapterForEdit) return;
      try {
        const resp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${selectedChapterForEdit._id}/outline`, {
          outline: selectedChapterForEdit.outline,
          status: selectedChapterForEdit.status || 'outline_only',
          characterIds: selectedChapterForEdit.characterIds || [],
          foreshadowingIds: selectedChapterForEdit.foreshadowingIds || [],
          itemIds: selectedChapterForEdit.itemIds || [],
        });
        if (resp.data?.success) {
          setChapters(prev => prev.map(c => c._id === selectedChapterForEdit._id ? { ...c, ...selectedChapterForEdit } : c));
          setSnackbar({ open: true, message: '章纲已保存', severity: 'success' });
        }
      } catch (e) {
        setSnackbar({ open: true, message: '保存失败：' + (e.response?.data?.message || e.message), severity: 'error' });
      }
    };

    /** 保存指定章节的章纲（用于手动保存按钮，不弹窗提示） */
    const persistMergedWizardOutlineForChapter = async (chapter) => {
      if (!assistNovelId || !chapter) {
        return;
      }
      const mergedOutline = {
        ...(chapter.outline || {}),
        coreEvent: wizardCoreEvent !== undefined && wizardCoreEvent !== null ? wizardCoreEvent : (chapter.outline?.coreEvent || ''),
        connectionToPrevious: chapter.outline?.connectionToPrevious,
        hookForNext: chapter.outline?.hookForNext,
      };
      const ch = {
        ...chapter,
        characterIds: chapter.characterIds || [],
        foreshadowingIds: chapter.foreshadowingIds || [],
        itemIds: chapter.itemIds || [],
        outline: mergedOutline,
        updatedAt: new Date().toISOString(),
      };
      setSelectedChapterForEdit(ch);
      try {
        const resp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${ch._id}/outline`, {
          outline: ch.outline,
          status: ch.status || 'outline_only',
          characterIds: ch.characterIds || [],
          foreshadowingIds: ch.foreshadowingIds || [],
          itemIds: ch.itemIds || [],
        });
        if (resp.data?.success) {
          setChapters(prev => prev.map(c => (c._id === ch._id ? { ...c, ...ch } : c)));
          // 不弹窗提示，只更新时间
          setChapterOutlineSavedTime(new Date());
        }
      } catch (e) {
        console.error('保存章纲失败:', e);
      }
    };

    /** 合并向导区 wizard* 与当前章并保存（桌面/移动章纲侧栏共用，勿用依赖 editingChapterOutline 的 handleSaveChapterOutline） */
    const persistMergedWizardOutline = async () => {
      if (!assistNovelId || !selectedChapterForEdit) {
        setSnackbar({ open: true, message: '作品或章节未就绪', severity: 'warning' });
        return;
      }
      const mergedOutline = {
        ...(selectedChapterForEdit.outline || {}),
        subplotIds: [...wizardSelectedSubplots],
        coreEvent: wizardCoreEvent !== undefined && wizardCoreEvent !== null ? wizardCoreEvent : (selectedChapterForEdit.outline?.coreEvent || ''),
        connectionToPrevious: selectedChapterForEdit.outline?.connectionToPrevious,
        hookForNext: selectedChapterForEdit.outline?.hookForNext,
      };
      const ch = {
        ...selectedChapterForEdit,
        characterIds: [...wizardSelectedChars],
        foreshadowingIds: [...wizardSelectedForeshadowing],
        itemIds: [...wizardSelectedItems],
        outline: mergedOutline,
      };
      setSelectedChapterForEdit(ch);
      try {
        const resp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${ch._id}/outline`, {
          outline: ch.outline,
          status: ch.status || 'outline_only',
          characterIds: ch.characterIds || [],
          foreshadowingIds: ch.foreshadowingIds || [],
          itemIds: ch.itemIds || [],
        });
        if (resp.data?.success) {
          setChapters(prev => prev.map(c => (c._id === ch._id ? { ...c, ...ch } : c)));
          setSnackbar({ open: true, message: '章纲已保存', severity: 'success' });
        }
      } catch (e) {
        setSnackbar({ open: true, message: '保存失败：' + (e.response?.data?.message || e.message), severity: 'error' });
      }
    };

    /** 智能章纲：核心事件+承上+埋钩（与桌面 Icon 逻辑一致，含自动落库） */
    const runChaptersPanelAiSmartOutlineGenerate = async () => {
      if (!assistNovelId) {
        setSnackbar({ open: true, message: '作品未加载，请稍候', severity: 'warning' });
        return;
      }
      if (!selectedChapterForEdit) return;
      if (!characters || characters.length === 0) {
        setSnackbar({ open: true, message: '角色库为空，请先去「角色库」创建角色后再生成章纲', severity: 'warning' });
        return;
      }

      // 检查次数限制
      if (!checkAssistQuota('AI生成章纲')) {
        return;
      }

      // 移除强制选择角色的检查，改为生成章纲时让AI自动分析角色

      // ── 等待全局进度同步完成（防止竞态条件）────────────────────────────
      if (globalProgressSyncing) {
        setSnackbar({ open: true, message: '全局进度正在同步中，请稍候...', severity: 'info' });
        let waitCount = 0;
        const maxWait = 30; // 最多等待30秒
        while (globalProgressSyncing && waitCount < maxWait) {
          await new Promise(resolve => setTimeout(resolve, 1000));
          waitCount++;
          console.log(`[章纲生成] 等待全局进度同步中... (${waitCount}/${maxWait}秒)`);
        }
        if (globalProgressSyncing) {
          setSnackbar({ open: true, message: '等待超时，请稍后重试', severity: 'warning' });
          return;
        }
        setSnackbar({ open: true, message: '全局进度已同步，开始生成章纲...', severity: 'success' });
      }

      // ── 前章内容检查 ────────────────────────────────────────────────
      const currentChNum = selectedChapterForEdit.chapterNumber || 1;
      if (currentChNum > 1) {
        const prevChapter = chapters.find(
          c => c.volumeId === selectedChapterForEdit.volumeId && c.chapterNumber === currentChNum - 1
        );
        const prevCoreEv = prevChapter?.outline?.coreEvent?.trim();
        const prevContent = prevChapter?.content?.trim();
        if (!prevCoreEv && !prevContent) {
          setPrevChapterWarning({
            open: true,
            prevChapter: { ...prevChapter, chapterNumber: currentChNum - 1 },
          });
          return;
        }
      }

      if (doRunSmartOutlineGenerateRef.current) doRunSmartOutlineGenerateRef.current();
    };

    /** 实际执行智能章纲生成的内部函数（由前章检查后调用，通过 doRunSmartOutlineGenerateRef 暴露给外层） */
    doRunSmartOutlineGenerateRef.current = async () => {
      setOutlineGenerating(true);
      try {
        // ══ 从数据库重新加载大纲，确保获取最新数据 ══
        let latestOutline = outlines.length > 0 ? outlines[0] : null;
        try {
          const outlineResp = await apiClient.get(`/assist/novels/${assistNovelId}/outline`);
          if (outlineResp.data?.success && outlineResp.data.data) {
            latestOutline = outlineResp.data.data;
            setOutlines([latestOutline]);
            console.log('[章纲生成] ✅ 已从数据库重新加载大纲');
          }
        } catch (e) {
          console.warn('[章纲生成] 重新加载大纲失败，使用当前状态:', e);
        }
        // ══ 调试日志：检查章纲数据状态 ══
        console.log('═══════════════════════════════════════════════════════════════');
        console.log('[章纲生成] outlines 状态检查（重新加载后）:');
        console.log('  latestOutline?._id:', latestOutline?._id);
        console.log('  latestOutline?.mainPlot?.storyline:', (latestOutline?.mainPlot?.storyline || '暂无').substring(0, 50) + '...');
        console.log('  latestOutline?.characterNetwork 数量:', latestOutline?.characterNetwork?.length || 0, '条');
        console.log('  latestOutline?.climaxes 数量:', latestOutline?.climaxes?.length || 0, '个');
        console.log('  latestOutline?.coreConflicts 数量:', latestOutline?.coreConflicts?.length || 0, '条');
        console.log('  latestOutline?.subPlots 总数量:', latestOutline?.subPlots?.length || 0, '条');
        console.log('  latestOutline?.subPlots 详情:', JSON.stringify(latestOutline?.subPlots?.map(s => ({ title: s.title, status: s.status, startCh: s.startChapterNumber, endCh: s.endChapterNumber }))));
        console.log('  latestOutline?.foreshadowing 总数量:', latestOutline?.foreshadowing?.length || 0, '条');
        console.log('  latestOutline?.foreshadowing 详情:', JSON.stringify(latestOutline?.foreshadowing?.map(f => ({ title: f.title, status: f.status, plantedCh: f.plantedChapterNumber }))));
        console.log('  latestOutline?.storyStructure:', JSON.stringify(latestOutline?.storyStructure || {}));
        console.log('  latestOutline?.worldSettings:', JSON.stringify(latestOutline?.worldSettings || {}));
        console.log('═══════════════════════════════════════════════════════════════');

        const currentChNum = selectedChapterForEdit.chapterNumber || 1;
        const novelTitle = novel?.title || '未知作品';
        const genre = novel?.type || latestOutline?.type || '玄幻';
        const totalChapters = outlineFormData.chapterCount || chapters.length || 30;
        const currentPhase = getMainLinePhase ? getMainLinePhase() : 'beginning';
        const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
        const prevAll = chapters.filter(c => c.volumeId === selectedChapterForEdit.volumeId && c.chapterNumber < currentChNum).sort((a, b) => b.chapterNumber - a.chapterNumber);
        const prev3 = prevAll.slice(0, 3);
        const prevSummaryBlock = prev3.length > 0
          ? prev3.map(ch => {
            const prevChapterHook = ch.outline?.hookForNext || '';
            const prevChapterConn = ch.outline?.connectionToPrevious || '';
            return `第${ch.chapterNumber}章「${ch.title || '未命名'}」：核心事件【${ch.outline?.coreEvent || '无'}】${prevChapterConn ? '；承上：' + prevChapterConn.slice(0, 40) : ''}${prevChapterHook ? '；埋钩：' + prevChapterHook.slice(0, 40) : ''}`;
          }).join('\n')
          : '（暂无前章）';
        const prev10 = prevAll.slice(0, 10);
        // ── 构建完整前10章正文内容（每个字段200字摘要 + 完整正文）──────────────
        // 核心改进：提交完整的正文内容，让AI能够理解故事的演进过程
        const prev10Block = prev10.length > 0
          ? prev10.map(ch => {
              const chChars = (ch.outline?.characterIds || []).map(id => characters.find(c => c._id === id)?.name || '?').join('、');
              const chContent = ch.content?.trim() || '';
              const chCore = ch.outline?.coreEvent || '无';
              const chConn = ch.outline?.connectionToPrevious || '';
              const chHook = ch.outline?.hookForNext || '';
              const chFullContent = chContent || '（无正文内容）';
              return `第${ch.chapterNumber}章「${ch.title || '未命名'}」
核心事件：${chCore}
承接上文：${chConn || '无'}
埋钩：${chHook || '无'}
出场角色：${chChars || '无'}
完整正文：
${chFullContent}`;
            }).join('\n\n')
          : '（暂无前10章内容）';
        const ctx = getChapterContext(selectedChapterForEdit);
        const allCharsWithStates = characters.map(c => {
          const cs = ctx?.characterStates?.[c._id] || {};
          return `${c.name}（${c.role || '角色'}）：${c.description || '暂无描述'}${cs.currentState ? '；当前状态：' + cs.currentState : ''}${c.emotionStatus ? '；情感状态：' + c.emotionStatus : ''}`;
        }).join('\n') || '暂无';
        const relList = (latestOutline?.characterNetwork || []).map(r => `${r.from || '?'}与${r.to || '?'}：${r.relationship || '未知关系'}（${r.type || '关系'})`).join('\n') || '暂无';
        const allSubplots = (latestOutline?.subPlots || []).filter(s => s.status === 'active');
        const relevantSubplots = allSubplots.map(s => {
          const range = (s.startChapterNumber && s.endChapterNumber) ? ` [第${s.startChapterNumber}~${s.endChapterNumber}章]` : '';
          const phase = s.phase ? `【${s.phase}】` : '';
          const coreConflict = s.coreConflict ? `核心冲突：${s.coreConflict}` : '';
          const mainPlotRelation = s.mainPlotRelation ? `与主线关系：${s.mainPlotRelation}` : '';
          const description = s.description || '';
          return `${s.title}${phase}${range}\n${coreConflict}${mainPlotRelation ? '\n' + mainPlotRelation : ''}${description ? '\n' + description : ''}`;
        }).join('\n\n') || '暂无';
        const allForeshadowings = (latestOutline?.foreshadowing || []);
        const relevantForeshadowings = allForeshadowings.map(f => {
          const planInfo = f.plantedChapterNumber || f.revealChapterNumber ? ` [铺垫第${f.plantedChapterNumber || '?'}章/揭示第${f.revealChapterNumber || '?'}章]` : '';
          const clueDist = f.clueDistribution ? `\n线索分布：${f.clueDistribution}` : '';
          const revealMethod = f.revealMethod ? `\n揭示方式：${f.revealMethod}` : '';
          const relatedFs = f.relatedForeshadowings?.length > 0 ? `\n关联伏笔：${f.relatedForeshadowings.join('、')}` : '';
          return `${f.title}（${f.status === 'revealed' ? '已揭示' : f.status === 'planted' ? '已铺垫' : '未铺垫'}）${planInfo}\n${f.description || ''}${clueDist}${revealMethod}${relatedFs}`;
        }).join('\n\n') || '暂无';
        // ══ 调试日志：检查注入章纲的支线和伏笔数据 ══
        console.log('[章纲生成] 即将注入章纲提示词的关键数据:');
        console.log('  [支线] allSubplots 总数:', latestOutline?.subPlots?.length || 0, '| 活跃支线 relevantSubplots 数量:', allSubplots.length);
        console.log('  [支线] relevantSubplots 内容:\n', relevantSubplots);
        console.log('  [伏笔] allForeshadowings 总数:', latestOutline?.foreshadowing?.length || 0, '| relevantForeshadowings 数量:', allForeshadowings.length);
        console.log('  [伏笔] relevantForeshadowings 内容:\n', relevantForeshadowings);
        const climaxesList = (latestOutline?.climaxes || []).map(c => `【${c.title || '高潮点'}】${c.description || ''}`).join('\n') || '暂无';
        const coreConflictsList = (latestOutline?.coreConflicts || []).map(c => `【${c.title || '核心冲突'}】${c.description || ''}`).join('\n') || '暂无';
        const storyStructure = latestOutline?.storyStructure || {};
        const structureBlock = [
          storyStructure.beginning ? `开局：${storyStructure.beginning}` : '',
          storyStructure.development ? `发展：${storyStructure.development}` : '',
          storyStructure.climax ? `高潮：${storyStructure.climax}` : '',
          storyStructure.ending ? `结局：${storyStructure.ending}` : '',
        ].filter(Boolean).join('\n') || '暂无';
        const worldSettings = latestOutline?.worldSettings || {};
        const worldBlock = [
          worldSettings.background ? `世界观背景：${worldSettings.background}` : '',
          worldSettings.levelSystem ? `修炼体系：${worldSettings.levelSystem}` : '',
          worldSettings.factions ? `势力门派：${(worldSettings.factions || []).join('、')}` : '',
        ].filter(Boolean).join('\n') || '暂无';
        const charStateEntries = Object.entries(progressData?.characterStates || {}).filter(([_, s]) => s && s.currentState);
        const charStateBlock = charStateEntries.length > 0
          ? charStateEntries.map(([charId, s]) => {
            const ch = characters.find(c => c._id === charId);
            return `${ch?.name || charId}：${s.currentState || ''}${s.description ? '（' + s.description + '）' : ''}`;
          }).join('\n')
          : '暂无';
        const relChangeBlock = (progressData?.relationshipChanges || []).map(rc => `${rc.fromCharacterName}对${rc.toCharacterName}：${rc.fromStatus} → ${rc.toStatus}`).join('\n') || '暂无';
        // ── 补充：角色出场时间轴、伏笔状态变化历史、支线进展历史 ───────────────────
        const charTimelineBlock = (() => {
          const timeline = progressData?.characterChapterStates || [];
          if (timeline.length === 0) return '暂无';
          const recent = [...timeline].sort((a, b) => b.chapterNumber - a.chapterNumber).slice(0, 15);
          return recent.map(s => {
            const ch = characters.find(c => c._id === s.characterId);
            return `第${s.chapterNumber}章 · ${ch?.name || s.characterId || '?'}：${s.lifeStatus === 'alive' ? '存活' : s.lifeStatus === 'injured' ? '受伤' : s.lifeStatus === 'dead' ? '死亡' : s.lifeStatus || '未知'}，${s.emotionStatus || '情感未变'}，${s.state || s.currentState || '状态未记录'}`;
          }).join('\n');
        })();
        const fsStateHistoryBlock = (() => {
          const history = progressData?.foreshadowingChapterStates || [];
          if (history.length === 0) return '暂无';
          const recent = [...history].sort((a, b) => b.chapterNumber - a.chapterNumber).slice(0, 10);
          return recent.map(f => {
            const statusLabel = f.status === 'planted' ? '已铺垫' : f.status === 'revealed' ? '已揭示' : f.status === 'advancing' ? '推进中' : f.status || '未知';
            return `第${f.chapterNumber}章：「${f.foreshadowingTitle || f.title || '?'}」→ ${statusLabel} ${f.note || ''}`;
          }).join('\n');
        })();
        const spStateHistoryBlock = (() => {
          const history = progressData?.subplotChapterStates || [];
          if (history.length === 0) return '暂无';
          const recent = [...history].sort((a, b) => b.chapterNumber - a.chapterNumber).slice(0, 10);
          return recent.map(s => {
            const statusLabel = s.status === 'active' ? '进行中' : s.status === 'completed' ? '已完成' : s.status || '未知';
            return `第${s.chapterNumber}章 ·「${s.subplotTitle || s.title || '?'}」→ ${statusLabel} ${s.note || ''}`;
          }).join('\n');
        })();
        const nextPointsBlock = (() => {
          const pts = progressData?.nextChapterCorePoints || [];
          if (pts.length === 0) return '暂无';
          return pts.map(ch => `第${ch.chapterNumber}章：${(ch.points || []).map(p => p.point).join('；')}`).join('\n');
        })();
        const nextCh = chapters.find(c => c.chapterNumber === currentChNum + 1);
        const nextChBlock = nextCh
          ? `第${nextCh.chapterNumber}章「${nextCh.title || '未命名'}」已有章纲：核心事件【${nextCh.outline?.coreEvent || '未定义'}】；为下章埋钩【${nextCh.outline?.hookForNext || '暂无'}】`
          : '（下章尚未创建）';
        const existingCore = selectedChapterForEdit.outline?.coreEvent || '';
        const existingConn = selectedChapterForEdit.outline?.connectionToPrevious || '';
        const existingHook = selectedChapterForEdit.outline?.hookForNext || '';
        const existingPlotPoints = (selectedChapterForEdit.outline?.plotPoints || []).map(p => `[${getPtLabel(p.type || 'conflict')}] ${p.content || ''}`).join('；') || '';
        const existingBlock = [
          existingCore ? `已有核心事件：${existingCore}` : '',
          existingConn ? `已有承接上文：${existingConn}` : '',
          existingHook ? `已有为下章埋钩：${existingHook}` : '',
          existingPlotPoints ? `已有情节展开：${existingPlotPoints}` : '',
        ].filter(Boolean).join('\n');
        const prompt = `【章纲生成任务】
你是资深网文策划专家，基于小说全局设计，为第${currentChNum}章生成章纲三项。
类型：${genre}  |  主线阶段：【${phaseLabels[currentPhase] || '开局'}】

═══════════════════════════════════════
【小说全局设定】← 你必须先理解这些，再生成章纲
═══════════════════════════════════════
【全局主线】
${outlines[0]?.mainPlot?.storyline || outlines[0]?.mainPlot?.description || '暂无主线大纲'}

【主线四阶段】
${structureBlock}

【世界观设定】
${worldBlock}

【当前活跃支线】← 章纲需推进至少1条
${relevantSubplots}

【伏笔总览】← 章纲需铺垫或引爆至少1个
${relevantForeshadowings}

【全部角色及当前状态】
${allCharsWithStates}

【角色关系网络】
${relList}

【全局高潮设计】
${climaxesList}

【核心冲突】
${coreConflictsList}

【已发生的关系变化】
${relChangeBlock}

【下章应发展的核心要点】
${nextPointsBlock !== '暂无' ? nextPointsBlock : '暂无（请根据主线自行判断）'}

═══════════════════════════════════════
【角色出场时间轴】← 章纲生成必须参考此时间轴，理解角色在各章的状态变化
${charTimelineBlock}

【伏笔状态变化历史】← 章纲生成必须知道每个伏笔当前推进到哪个阶段
${fsStateHistoryBlock}

【支线进展历史】← 章纲生成必须知道每个支线当前进展到什么程度
${spStateHistoryBlock}

═══════════════════════════════════════
【故事脉络】← 你必须保证本章章纲与前10章逻辑连贯
${prev10Block}

═══════════════════════════════════════
【下章已有章纲】（生成埋钩时必须衔接）
${nextChBlock}

═══════════════════════════════════════
【当前章节】
第${currentChNum}章「${selectedChapterForEdit.title || '未命名'}」
${existingBlock ? '【已有章纲（请补充或修正）】\n' + existingBlock : ''}
${selectedChapterForEdit.outline?.coreContent ? '【用户写作要求】' + selectedChapterForEdit.outline.coreContent : ''}

═══════════════════════════════════════
【文风规划指令】← 本章必须统一文风，禁止风格突变
1. 叙事视角：保持一致的第三人称限知视角/全知视角
2. 语气基调：根据当前阶段调整（开局偏轻松好奇，发展偏紧张悬疑，高潮偏激烈悲壮）
3. 句式节奏：保持与前10章相似的长短句搭配比例
4. 描写偏好：延续前几章的环境描写、对话描写、动作描写的比例
5. 用词风格：避免出现网络用语/谐音梗，保持文字的文学性

═══════════════════════════════════════
生成章纲必须严格遵循以下逻辑链，禁止凭空编造：

【核心事件】
必须同时满足：
① 是全局主线的当前阶段推进（开局→成长/发展→冲突/高潮→引爆/结局→收束）
② 承接了前章的内容，让故事自然向前推进
③ 推动了本章出场的角色状态变化（关系变化或情感变化）
④ **必须推进以下伏笔**（根据【伏笔状态变化历史】判断当前应处于哪个阶段）：
  - 【伏笔状态变化历史】中标记为"推进中/已铺垫"但尚未揭示的伏笔 → 本章继续推进或引爆
  - 【伏笔状态变化历史】中记录了"已铺垫"的伏笔 → 检查是否到了揭示时机
  - 大纲伏笔中计划在本次章推进的支线/伏笔 → 本章必须推进
⑤ **必须推进以下支线**（根据【支线进展历史】判断当前应处于哪个阶段）：
  - 【支线进展历史】中标记为"进行中"的支线 → 本章必须推进至少1条
  - 【支线进展历史】中接近结束但未完成的支线 → 本章应考虑收尾
写法：2-4句话，第1句说明时间地点人物，第2-3句说明发生了什么事件及影响

【承接上文】
请根据前10章的故事脉络，特别是上一章的结尾片段，自然地描述本章如何从前文继续。
写法：1-3句话，直接说"上章结束时……，本章开头……"

【为下章埋钩】
必须满足：
① 与当前主线阶段匹配（开局埋成长/资源钩，发展埋冲突/背叛钩，高潮埋生死/终极秘密钩，结局埋余波/番外钩）
② **必须同时埋设伏笔钩子**：根据【伏笔状态变化历史】，在结尾处铺垫或引爆下一个伏笔点
③ 与下章已有章纲衔接（如果有下章的话）
写法：1-2句，设置一个明确的悬念点（什么人/事/秘密出了问题），同时留下伏笔痕迹

═══════════════════════════════════════
【本章标题生成要求】← 禁止AI味标题，必须有画面感和文学性
1. 禁止使用：直接复述情节、套路化爽文标题、带"第"字或章节编号、双句式对仗、过度使用四字成语
2. 推荐：有画面感的短句（5-8字）、悬念式提问、意象象征、动词+名词的动态感
3. 参考本章核心事件，用形象化语言概括本章最精彩的瞬间作为标题

═══════════════════════════════════════
输出格式（严格按此，每行一个字段，不要任何前缀说明）：
【核心事件】
【承接上文】
【为下章埋钩】
【本章出场角色】（根据本章章纲内容，从角色列表中分析哪些角色必须出场，只列出角色名，多个角色用顿号分隔，不要添加其他说明）
【本章标题】（3-8个汉字，禁止套路化/AI味标题）`;

        // ============================================================
        // [章纲生成] 详细调试日志 - 输出所有关键数据
        // ============================================================
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [章纲生成] 开始构建提示词 - 数据汇总日志            ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');
        console.log('[基础信息]', {
          novelTitle, genre, totalChapters, currentChNum, currentPhase,
          selectedChapterId: selectedChapterForEdit._id,
          selectedChapterTitle: selectedChapterForEdit.title
        });
        console.log('[前章摘要] prevSummaryBlock:', prevSummaryBlock);
        console.log('[前10章数量] prev10.length:', prev10.length);
        console.log('[前10章章号列表]', prev10.map(c => c.chapterNumber));
        console.log('[前10章正文内容检查]');
        prev10.forEach((ch, idx) => {
          console.log('  第' + (idx+1) + '章 (第' + ch.chapterNumber + '章 "' + ch.title + '") 正文长度: ' + (ch.content || '').length + '字');
        });
        console.log('[前10章完整正文块长度]', prev10Block.length, '字符');
        console.log('[大纲主线]', latestOutline?.mainPlot?.storyline || latestOutline?.mainPlot?.description || '暂无');
        console.log('[大纲主线四阶段]', structureBlock ? '已构建' : '为空');
        console.log('[世界观设定]', worldBlock ? '已构建' : '为空');
        console.log('[核心冲突数量]', (latestOutline?.coreConflicts || []).length);
        console.log('[活跃支线数量]', (relevantSubplots || '暂无') !== '暂无' ? relevantSubplots.split('\n').length : 0);
        console.log('[伏笔数量]', allForeshadowings.length);
        console.log('[角色数量]', characters.length);
        console.log('[角色关系网络数量]', (latestOutline?.characterNetwork || []).length);
        console.log('[角色出场时间轴]', charTimelineBlock);
        console.log('[伏笔状态变化历史]', fsStateHistoryBlock);
        console.log('[支线进展历史]', spStateHistoryBlock);
        console.log('[下章章纲块]', nextChBlock);
        console.log('[已有章纲]', existingBlock || '无');
        console.log('[主线阶段标签]', phaseLabels[currentPhase] || '开局');
        console.log('[下一章核心要点]', nextPointsBlock);
        console.log('[文风规划] 当前阶段:', phaseLabels[currentPhase] || '开局');

        const currentNovelName = novel?.title || '未知项目';
        // 注意：前端不打印具体模型名称，避免暴露后端配置
        console.log('[API调用参数]', {
          url: '/assist/ai/proxy-ai',
          model: '***', // 前端隐藏具体模型名称
          temperature: 0.75,
          max_tokens: 5000,
          operationType: 'AI生成章纲',
          billingInfo: { novelName: currentNovelName, chapterNumber: currentChNum }
        });
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [章纲生成] 即将发送API请求                         ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');

        // [章纲生成] 输出完整提示词内容供调试
        console.log('\n========== [章纲生成] 完整提示词内容 ==========\n');
        console.log(prompt);
        console.log('\n========== [章纲生成] 完整提示词结束 ==========\n');

        const resp = await apiClient.post('/assist/ai/proxy-ai', {
          messages: [{ role: 'user', content: prompt }],
          model: 'deepseek-v4-flash', temperature: 0.75, max_tokens: 5000, stream: false,
          billingInfo: {
            novelName: currentNovelName,
            chapterNumber: currentChNum,
            operationType: 'AI生成章纲'
          }
        });
        const raw = (resp.data?.choices?.[0]?.message?.content || '').replace(/```/g, '').trim();

        // [章纲生成] API响应日志
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [章纲生成] API响应日志                              ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');
        console.log('[API状态码]', resp.status, resp.statusText);
        console.log('[原始响应长度]', raw.length, '字符');
        console.log('[原始响应内容]', raw);
        console.log('[原始响应前500字]', raw.slice(0, 500));

        const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.length > 0);
        /** AI 常把正文写在「【核心事件】」单独一行下面，旧逻辑只匹配标题行会得到空串 */
        const parseSmartOutlineSections = (allLines) => {
          const section = (name) => {
            const re = new RegExp(`【\\s*${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*】`);
            let i = allLines.findIndex((l) => re.test(l));
            if (i < 0) return '';
            const line = allLines[i];
            const m = line.match(re);
            let after = m ? line.slice((m.index || 0) + m[0].length).trim() : line;
            after = after.replace(/^[：:]\s*/, '').trim();
            if (after) return after;
            const buf = [];
            for (let j = i + 1; j < allLines.length; j += 1) {
              if (/^【[^】]+】/.test(allLines[j])) break;
              buf.push(allLines[j]);
            }
            return buf.join('\n').trim();
          };
          let coreEvent = section('核心事件');
          let connectionToPrevious = section('承接上文');
          let hookForNext = section('为下章埋钩');
          let chapterChars = section('本章出场角色');
          let generatedTitle = section('本章标题');
          if (!coreEvent && !connectionToPrevious && !hookForNext) {
            const extractVal = (label) => {
              const hit = allLines.find((l) => l.includes(label));
              if (!hit) return '';
              const withoutBrackets = hit.replace(/^【[^】]*】\s*/, '').trim();
              const ci = Math.max(withoutBrackets.indexOf('：'), withoutBrackets.indexOf(':'));
              if (ci < 0) return withoutBrackets;
              return withoutBrackets.slice(ci + 1).trim();
            };
            coreEvent = extractVal('核心事件');
            connectionToPrevious = extractVal('承接上文');
            hookForNext = extractVal('为下章埋钩');
            chapterChars = extractVal('本章出场角色');
            generatedTitle = extractVal('本章标题');
          }
          return { coreEvent, connectionToPrevious, hookForNext, chapterChars, generatedTitle };
        };
        const { coreEvent: coreEv, connectionToPrevious: connVal, hookForNext: hookVal, chapterChars: chapterCharsRaw, generatedTitle: generatedTitleRaw } = parseSmartOutlineSections(lines);

        // [章纲生成] 解析结果日志
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [章纲生成] 解析结果日志                           ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');
        console.log('[解析-核心事件]', coreEv || '(空)');
        console.log('[解析-承接上文]', connVal || '(空)');
        console.log('[解析-为下章埋钩]', hookVal || '(空)');
        console.log('[解析-本章出场角色原始值]', chapterCharsRaw || '(空)');
        console.log('[解析-本章标题]', generatedTitleRaw || '(空)');
        console.log('[总解析字段数]', lines.length, '行');
        // 解析角色名并匹配到角色ID（支持精确匹配和模糊匹配）
        const resolveCharIds = (charNamesStr) => {
          if (!charNamesStr) return [];
          // 按顿号、逗号、分号、空格分割角色名
          const names = charNamesStr.split(/[、，；,\s]+/).map(n => n.trim()).filter(Boolean);
          const matchedIds = [];
          names.forEach(name => {
            // 精确匹配
            const exact = characters.find(c => c.name === name);
            if (exact) {
              if (!matchedIds.includes(exact._id)) matchedIds.push(exact._id);
              return;
            }
            // 模糊匹配（包含关系）
            const fuzzy = characters.find(c => c.name.includes(name) || name.includes(c.name));
            if (fuzzy && !matchedIds.includes(fuzzy._id)) {
              matchedIds.push(fuzzy._id);
            }
          });
          return matchedIds;
        };
        const autoCharIds = resolveCharIds(chapterCharsRaw);

        // [章纲生成] 角色解析日志
        console.log('[角色解析-输入原始值]', chapterCharsRaw);
        console.log('[角色解析-匹配到的ID数]', autoCharIds.length, autoCharIds);
        console.log('[角色解析-匹配到的角色名]', autoCharIds.map(id => characters.find(c => c._id === id)?.name).filter(Boolean));

        const finalCharIds = autoCharIds.length > 0 ? autoCharIds : [...wizardSelectedChars];
        console.log('[角色解析-最终选用ID]', finalCharIds);
        const mergedOutline = {
          ...(selectedChapterForEdit.outline || {}),
          subplotIds: [...wizardSelectedSubplots],
          coreEvent: coreEv,
          connectionToPrevious: connVal || selectedChapterForEdit.outline?.connectionToPrevious,
          hookForNext: hookVal || selectedChapterForEdit.outline?.hookForNext,
          generatedTitle: generatedTitleRaw || selectedChapterForEdit.outline?.generatedTitle,
        };
        const chapterIdForSave = selectedChapterForEdit._id;
        setWizardCoreEvent(coreEv);
        setSelectedChapterForEdit(prev => (prev ? {
          ...prev,
          characterIds: [...finalCharIds],
          foreshadowingIds: [...wizardSelectedForeshadowing],
          itemIds: [...wizardSelectedItems],
          outline: {
            ...mergedOutline,
          },
        } : prev));
        try {
          const saveResp = await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${chapterIdForSave}/outline`, {
            outline: mergedOutline,
            status: selectedChapterForEdit.status || 'outline_only',
            characterIds: [...finalCharIds],
            foreshadowingIds: [...wizardSelectedForeshadowing],
            itemIds: [...wizardSelectedItems],
          });
          if (saveResp.data?.success) {
            setChapters(prev => prev.map(c => (c._id === chapterIdForSave ? { ...c, outline: mergedOutline, characterIds: [...finalCharIds], foreshadowingIds: [...wizardSelectedForeshadowing], itemIds: [...wizardSelectedItems] } : c)));
            // 同步更新向导中的角色选择
            setWizardSelectedChars([...finalCharIds]);
          } else {
            setSnackbar({ open: true, message: saveResp.data?.message || '章纲保存未确认成功', severity: 'warning' });
          }
        } catch (saveErr) {
          console.error('自动保存失败:', saveErr);
          setSnackbar({ open: true, message: '章纲已生成但保存失败：' + (saveErr.response?.data?.message || saveErr.message), severity: 'error' });
        }
      } catch (e) {
        setSnackbar({ open: true, message: 'AI 生成失败', severity: 'error' });
      } finally {
        setOutlineGenerating(false);
      }
    };

    /** AI 生成正文（与桌面「🤖 AI 生成正文」按钮逻辑一致，含下一章自动章纲） */
    const runChaptersPanelAiGenerateBody = async () => {
      if (!assistNovelId) {
        setSnackbar({ open: true, message: '作品未加载', severity: 'warning' });
        return;
      }
      if (!selectedChapterForEdit) {
        setSnackbar({ open: true, message: '请先选中一个章节', severity: 'warning' });
        return;
      }
      const hasChars = (selectedChapterForEdit.characterIds || []).length > 0 || wizardSelectedChars.length > 0;
      const coreEv = (selectedChapterForEdit.outline?.coreEvent || wizardCoreEvent || '').trim();
      const hasCore = !!coreEv;
      const wordCount = parseInt(chapterWordCount, 10);
      const hasWordCount = !isNaN(wordCount) && wordCount >= 300;
      if (!hasChars || !hasCore || !hasWordCount) {
        const missing = [];
        if (!hasChars) missing.push('「本章出场角色」至少选一个');
        if (!hasCore) missing.push('「本章核心事件与衔接」未填写');
        if (!hasWordCount) missing.push('目标字数未设置或低于300字');
        setSnackbar({ open: true, message: `生成条件不满足：\n${missing.join('\n')}`, severity: 'error' });
        return;
      }

      // 检查次数限制
      if (!checkAssistQuota('AI生成正文')) {
        return;
      }

      setBodyGenerating(true);
      try {
        const chapterIdSnapshot = selectedChapterForEdit._id;
        const statusForSave = selectedChapterForEdit.status || 'outline_only';
        latestChapterBodyStreamTextRef.current = '';
        clearTimeout(chapterBodyStreamSaveTimerRef.current);
        chapterBodyStreamSaveTimerRef.current = null;
        setSelectedChapterForEdit(prev => (prev && prev._id === chapterIdSnapshot
          ? { ...prev, outline: { ...prev.outline, generatedContent: '', generatedTitle: '', lastGeneratedAt: null } }
          : prev));

        setChapterBodyStreamFollowOutput(true);
        setTimeout(() => {
          chapterBodyStreamSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 120);
        setTimeout(() => {
          chapterBodyStreamSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 400);

        const scheduleChapterBodyStreamSave = () => {
          clearTimeout(chapterBodyStreamSaveTimerRef.current);
          chapterBodyStreamSaveTimerRef.current = setTimeout(async () => {
            const text = latestChapterBodyStreamTextRef.current;
            const ch = selectedChapterForEditRef.current;
            if (!ch || ch._id !== chapterIdSnapshot || !assistNovelId) return;
            const outlineToSave = { ...ch.outline, generatedContent: text, lastGeneratedAt: new Date() };
            const charIds = (ch.characterIds || []).length ? [...ch.characterIds] : [...wizardSelectedChars];
            const fsIds = (ch.foreshadowingIds || []).length ? [...ch.foreshadowingIds] : [...wizardSelectedForeshadowing];
            const itIds = (ch.itemIds || []).length ? [...ch.itemIds] : [...wizardSelectedItems];
            try {
              const saveResp = await apiClient.post('/assist/novels/' + assistNovelId + '/chapters/' + chapterIdSnapshot + '/outline', {
                outline: outlineToSave,
                status: ch.status || statusForSave,
                characterIds: charIds,
                foreshadowingIds: fsIds,
                itemIds: itIds,
              });
              if (saveResp.data?.success) {
                setChapters(prev => prev.map(c => (c._id === chapterIdSnapshot
                  ? { ...c, outline: outlineToSave, characterIds: charIds, foreshadowingIds: fsIds, itemIds: itIds }
                  : c)));
              }
            } catch (saveErr) {
              console.error('[流式正文] 自动保存失败:', saveErr);
            }
          }, 1100);
        };

        const novelInfo = novel?.title || outlines[0]?.name || '未知作品';
        const genre = novel?.type || outlines[0]?.type || '玄幻';
        const currentChNum = selectedChapterForEdit.chapterNumber || 1;
        const pts = selectedChapterForEdit.outline?.plotPoints || [];
        let plotSummary = pts.length > 0
          ? pts.map((pt, i) => `${i + 1}. [${getPtLabel(pt.type || 'conflict')}] ${pt.content || '（未填写）'}`).join('\n')
          : `1. [核心情节] ${coreEv}`;
        const mainPlot = outlines[0]?.mainPlot?.storyline || outlines[0]?.mainPlot?.description || '暂无主线大纲';
        const allSubplots = outlines[0]?.subPlots || [];
        const relevantSubplots = allSubplots
          .filter(s => s.status === 'active' && (
            !s.startChapterNumber || !s.endChapterNumber
            || (currentChNum >= s.startChapterNumber && currentChNum <= s.endChapterNumber)
          ))
          .map(s => {
            const range = (s.startChapterNumber && s.endChapterNumber) ? ` [第${s.startChapterNumber}~${s.endChapterNumber}章]` : '';
            const phase = s.phase ? `【${s.phase}】` : '';
            const coreConflict = s.coreConflict ? `\n核心冲突：${s.coreConflict}` : '';
            const mainPlotRelation = s.mainPlotRelation ? `\n与主线关系：${s.mainPlotRelation}` : '';
            const description = s.description ? `\n${s.description}` : '';
            return `${s.title}${phase}${range}${coreConflict}${mainPlotRelation}${description}`;
          })
          .join('\n\n') || '暂无';
        const allForeshadowings = outlines[0]?.foreshadowing || [];
        // ══ 调试日志：检查正文生成时使用的支线和伏笔数据 ══
        console.log('[正文生成] 即将注入正文提示词的关键数据:');
        console.log('  [大纲] outlines[0]?._id:', outlines[0]?._id);
        console.log('  [支线] allSubplots 总数:', allSubplots.length, '| relevantSubplots 数量:', relevantSubplots.split('\n\n').filter(Boolean).length);
        console.log('  [支线] allSubplots 详情:', JSON.stringify(allSubplots.map(s => ({ title: s.title, status: s.status, startCh: s.startChapterNumber, endCh: s.endChapterNumber }))));
        console.log('  [伏笔] allForeshadowings 总数:', allForeshadowings.length);
        console.log('  [伏笔] allForeshadowings 详情:', JSON.stringify(allForeshadowings.map(f => ({ title: f.title, status: f.status, plantedCh: f.plantedChapterNumber, revealCh: f.revealChapterNumber }))));
        // ── 分离：必须铺垫/揭示的伏笔 vs 可参考的伏笔 ───────────────────────────
        const mandatoryForeshadowings = [];
        const optionalForeshadowings = [];
        allForeshadowings.forEach(f => {
          if (f.status === 'revealed') return;
          const plantedChapterId = f.plantedChapterId;
          const revealChapterId = f.revealChapterId;
          const planted = plantedChapterId
            ? (chapters.find(c => c._id === plantedChapterId)?.chapterNumber || f.plantedChapterNumber)
            : f.plantedChapterNumber;
          const revealed = revealChapterId
            ? (chapters.find(c => c._id === revealChapterId)?.chapterNumber || f.revealChapterNumber)
            : f.revealChapterNumber;
          if (!planted && !revealed) return;
          if (planted && currentChNum < planted - 3) return;
          if (revealed && currentChNum > revealed + 2) return;
          const plantedChNum = plantedChapterId ? chapters.find(c => c._id === plantedChapterId)?.chapterNumber : f.plantedChapterNumber;
          const revealChNum = revealChapterId ? chapters.find(c => c._id === revealChapterId)?.chapterNumber : f.revealChapterNumber;
          const hint = `铺垫第${plantedChNum || '?'}章${revealChNum ? '，揭示第' + revealChNum + '章' : ''}`;
          const clueDist = f.clueDistribution ? `\n线索分布：${f.clueDistribution}` : '';
          const revealMethod = f.revealMethod ? `\n揭示方式：${f.revealMethod}` : '';
          const relatedFs = f.relatedForeshadowings?.length > 0 ? `\n关联伏笔：${f.relatedForeshadowings.join('、')}` : '';
          const entry = `${f.title}【${hint}】\n${f.description || ''}${clueDist}${revealMethod}${relatedFs}`;
          if ((planted && currentChNum === planted) || (revealed && currentChNum === revealed)) {
            mandatoryForeshadowings.push(entry);
          } else {
            optionalForeshadowings.push(entry);
          }
        });
        const mandatoryFsBlock = mandatoryForeshadowings.join('\n') || '（本章无必须铺垫/揭示的伏笔）';
        const optionalFsBlock = optionalForeshadowings.join('\n') || '（无其他相关伏笔）';
        const linkedFsIds = selectedChapterForEdit.outline?.foreshadowingIds || [];
        const linkedFsSummaries = allForeshadowings
          .filter(f => linkedFsIds.includes(f.id || f._id))
          .map(f => {
            const clueDist = f.clueDistribution ? `\n线索分布：${f.clueDistribution}` : '';
            const revealMethod = f.revealMethod ? `\n揭示方式：${f.revealMethod}` : '';
            return `${f.title}：${f.description || ''}${clueDist}${revealMethod}`;
          })
          .join('\n') || '暂无';
        const phaseLabels = { beginning: '开局', development: '发展', climax: '高潮', ending: '结局' };
        const currentPhase = progressData?.mainLinePhase || getMainLinePhase() || 'beginning';
        const currentPhaseLabel = phaseLabels[currentPhase] || '开局';
        const charStates = progressData?.characterStates || {};
        const activeCharStateEntries = Object.entries(charStates).filter(([_, s]) => s && s.currentState);
        const charStateSummary = activeCharStateEntries.length > 0
          ? activeCharStateEntries.map(([charId, s]) => {
            const char = characters.find(c => c._id === charId);
            return `${char?.name || charId}：${s.currentState || '（无状态）'}${s.description ? '（' + s.description + '）' : ''}`;
          }).join('\n')
          : '暂无角色状态变化记录';
        const relChanges = progressData?.relationshipChanges || [];
        const relChangeSummary = relChanges.length > 0
          ? relChanges.map(rc => `${rc.fromCharacterName}对${rc.toCharacterName}：${rc.fromStatus} → ${rc.toStatus}（${rc.reason || '关系变化'}）`).join('\n')
          : '暂无关系变化记录';
        // ── 完整世界观设定 ───────────────────────────────────────────────────────────
        const worldSettings = outlines[0]?.worldSettings || {};
        const worldBlock = [
          worldSettings.background ? `世界背景：${worldSettings.background}` : '',
          worldSettings.levelSystem ? `修炼体系：${worldSettings.levelSystem}` : '',
          worldSettings.factions?.length > 0 ? `势力门派：${worldSettings.factions.join('、')}` : '',
        ].filter(Boolean).join('\n') || '暂无';
        // ── 核心冲突 ────────────────────────────────────────────────────────────────
        const coreConflictsList = (outlines[0]?.coreConflicts || []).map(c => `【${c.title || '冲突'}】${c.description || ''}`).join('\n') || '暂无';
        // ── 高潮设计 ────────────────────────────────────────────────────────────────
        const climaxesList = (outlines[0]?.climaxes || []).map(c => `【${c.title || '高潮'}】${c.description || ''}`).join('\n') || '暂无';
        // ── 角色关系网络 ───────────────────────────────────────────────────────────
        const relList = (outlines[0]?.characterNetwork || []).map(r => `${r.from || '?'}与${r.to || '?'}：${r.relationship || '关系'}`).join('\n') || '暂无';
        const nextCorePoints = progressData?.nextChapterCorePoints || [];
        const nextPointsSummary = nextCorePoints.length > 0
          ? nextCorePoints.map(p => {
              const pts = Array.isArray(p.points) ? p.points : [];
              return `第${p.chapterNumber}章：${pts.map(pt => pt.point).join('；')}`;
            }).join('\n')
          : '暂无';
        const ctx = getChapterContext(selectedChapterForEdit);
        const prevSummary = ctx?.previousChapter
          ? `第${ctx.previousChapter.chapterNumber}章「${ctx.previousChapter.title || '未命名'}」：${ctx.previousChapter.outline?.coreEvent || '无核心事件'}`
          : '暂无前章';
        const prevConn = selectedChapterForEdit.outline?.connectionToPrevious || '';
        const prevHook = ctx?.previousChapter?.outline?.hookForNext || '';
        const prev10All = chapters.filter(c => c.volumeId === selectedChapterForEdit.volumeId && c.chapterNumber < currentChNum).sort((a, b) => b.chapterNumber - a.chapterNumber);
        const prev10 = prev10All.slice(0, 10);
        // ── 构建完整前10章正文内容（每个字段200字摘要 + 完整正文）──────────────
        // 核心改进：提交完整的正文内容，让AI能够理解故事的演进过程
        const prev10Block = prev10.length > 0
          ? prev10.map(ch => {
              const chChars = (ch.outline?.characterIds || []).map(id => characters.find(c => c._id === id)?.name || '?').join('、');
              const chContent = ch.content?.trim() || '';
              const chCore = ch.outline?.coreEvent || '无';
              const chConn = ch.outline?.connectionToPrevious || '';
              const chHook = ch.outline?.hookForNext || '';
              const chFullContent = chContent || '（无正文内容）';
              return `第${ch.chapterNumber}章「${ch.title || '未命名'}」
核心事件：${chCore}
承接上文：${chConn || '无'}
埋钩：${chHook || '无'}
出场角色：${chChars || '无'}
完整正文：
${chFullContent}`;
            }).join('\n\n')
          : '（暂无前10章内容）';

        // ── 分析前几章的模式，用于避免重复 ──────────────────────────────────────────
        // 从前5章正文中提取开头模式、战斗模式、描写习惯等信息
        const analyzeChapterPatterns = (prevChapters) => {
          const patterns = {
            openingModes: [],      // 已使用的开头模式
            battleModes: [],       // 已使用的战斗模式
            reactionModes: [],     // 已使用的角色反应模式
            sentenceStarters: [],  // 已使用的句式开头
            usedPhrases: [],       // 高频使用的描写词
            recentCharacters: [],  // 最近出场的主角
          };

          // 分析前5章的开头模式
          prevChapters.slice(0, 5).forEach(ch => {
            const content = ch.content || '';
            if (!content) return;

            // 提取开头100字进行分析
            const opening = content.slice(0, 200);
            const lowerOpening = opening.toLowerCase();

            // ── 检测开头模式 ─────────────────────────────────────────────────
            if (/失重|坠落|下落|掉入|没顶|呛水|溺水|污水/.test(lowerOpening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '落水/失重类' });
            }
            if (/追|逃|包围|伏击|突袭|闯入/.test(lowerOpening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '被追逐/突袭类' });
            }
            if (/系统提示|叮|觉醒|力量涌入|能力觉醒/.test(lowerOpening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '觉醒/系统提示类' });
            }
            if (/["""].{10,30}["""]/.test(opening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '对话引入类' });
            }
            if (/黑暗|光芒|寒冷|寂静|空气/.test(lowerOpening) && opening.length > 50) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '场景描写类' });
            }
            if (/突然|蓦然|骤然|忽然|陡然/.test(lowerOpening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '突发事件类' });
            }
            if (/\.{3,}|——|就在这时|就在此时/.test(opening)) {
              patterns.openingModes.push({ chapter: ch.chapterNumber, type: '悬念留白类' });
            }

            // ── 检测战斗模式 ─────────────────────────────────────────────────
            if (/弱点|唯一|关键|节点|核心/.test(content)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '弱点攻击型' });
            }
            if (/逃跑|逃跑|躲避|隐藏|潜伏/.test(lowerOpening)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '逃亡躲避型' });
            }
            if (/陷阱|机关|环境|地形|利用/.test(lowerOpening)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '环境利用型' });
            }
            if (/一剑|一掌|一拳|一刀|一指点/.test(content)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '一招秒杀型' });
            }
            if (/缠斗|僵持|胶着|不分上下|势均力敌/.test(content)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '持久对抗型' });
            }
            if (/神识|灵魂|元神|神魂|意念/.test(content) && /攻击|攻击|侵蚀|压制/.test(content)) {
              patterns.battleModes.push({ chapter: ch.chapterNumber, type: '灵魂/神识战型' });
            }

            // ── 检测角色反应模式 ─────────────────────────────────────────────
            if (/脸色一变|面色一变|神色一变/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '脸色变化类' });
            }
            if (/倒吸一口凉气|倒抽一口冷气|心中一凛/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '倒吸凉气类' });
            }
            if (/眉头紧锁|眉头一皱|皱起眉头/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '皱眉蹙额类' });
            }
            if (/瞳孔微缩|瞳孔紧缩|眼瞳一缩/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '瞳孔变化类' });
            }
            if (/难以置信|不敢相信|瞪大双眼/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '难以置信类' });
            }
            if (/嘴角勾起|嘴角上扬|嘴角微扬/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '轻笑得意类' });
            }
            if (/身形一晃|身形暴退|身影一晃/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '身形移动类' });
            }
            if (/心头一跳|心中大惊|心中暗惊/.test(content)) {
              patterns.reactionModes.push({ chapter: ch.chapterNumber, type: '内心震动类' });
            }

            // ── 检测句式开头模式 ─────────────────────────────────────────────
            const firstSentence = content.split(/[。！？\n]/)[0] || '';
            if (firstSentence.length > 3) {
              if (/^[^，]{0,5}(看着|望着|注视着|盯着|看向)/.test(firstSentence)) {
                patterns.sentenceStarters.push({ chapter: ch.chapterNumber, type: '视觉观察类', example: firstSentence.slice(0, 20) });
              }
              if (/^[^，]{0,5}(发现|发觉|注意到|感知到)/.test(firstSentence)) {
                patterns.sentenceStarters.push({ chapter: ch.chapterNumber, type: '感知发现类', example: firstSentence.slice(0, 20) });
              }
              if (/^[^，]{0,5}(XXX说|XXX道|XXX冷笑道|XXX沉声道)/.test(firstSentence)) {
                patterns.sentenceStarters.push({ chapter: ch.chapterNumber, type: '对话引入类', example: firstSentence.slice(0, 20) });
              }
              if (/^[^，]{0,8}(就在|正当|恰好|恰好在这时)/.test(firstSentence)) {
                patterns.sentenceStarters.push({ chapter: ch.chapterNumber, type: '时机巧合类', example: firstSentence.slice(0, 20) });
              }
            }
          });

          return patterns;
        };

        const chapterPatterns = analyzeChapterPatterns(chapters);

        // ── 调用章纲上下文接口，获取上一章结束时 AI 分析出的「下章核心要点 + 衔接内容」─────────
        // 这是 autoSyncAfterChapterGenerate 中 AI 分析后写入 AssistProgress 的数据（闭环关键）
        let prevChapterCorePointsFromAI = [];
        let prevChapterConnectionFromAI = '';
        try {
          const ctxResp = await apiClient.get(`/assist/novels/${assistNovelId}/volumes/${selectedChapterForEdit.volumeId}/context/${currentChNum}`);
          if (ctxResp.data?.success && ctxResp.data.data) {
            const ctx = ctxResp.data.data;
            prevChapterCorePointsFromAI = Array.isArray(ctx.prevChapterCorePoints) ? ctx.prevChapterCorePoints : [];
            prevChapterConnectionFromAI = ctx.prevChapterConnection || '';
          }
        } catch (e) {
          console.warn('[正文生成] 获取上章AI分析提示失败:', e);
        }

        const linkedChars = ctx?.linkedCharacters || [];
        // 增强角色信息：包含关系、处境、当前任务
        const linkedCharSummaries = linkedChars.map(c => {
          const state = charStates[c._id];
          // 获取该角色与其他出场角色的关系
          const charRelationships = (progressData?.relationshipChanges || [])
            .filter(rc => rc.fromCharacterId === c._id || rc.toCharacterId === c._id)
            .slice(0, 3)
            .map(rc => {
              const otherChar = rc.fromCharacterId === c._id ? rc.toCharacterName : rc.fromCharacterName;
              return `${otherChar}：${rc.fromStatus} → ${rc.toStatus}`;
            });
          const relText = charRelationships.length > 0 ? '\n  关系变化：' + charRelationships.join('；') : '';
          return `${c.name}（${c.role || '角色'}）${state?.currentState ? '\n  当前状态：' + state.currentState : ''}${relText}`;
        }).join('\n\n') || '暂无';
        const linkedItems = ctx?.linkedItems || [];
        const linkedItemSummaries = linkedItems.map(i => `${i.name}（${i.category || '词条'}）：${i.description || ''}`).join('\n') || '暂无';
        const hookForNext = selectedChapterForEdit.outline?.hookForNext || '（未填写）';
        const targetWords = chapterWordCount || 2000;
        const prompt = `【小说正文写作任务】
你是一位番茄小说网/起点中文网签约作者，正在为本小说创作第${currentChNum}章正文。

═══════════════════════════════════
【核心指令：章纲三项】← 最高优先级，正文必须完全忠实执行
═══════════════════════════════════════
【本章核心事件】${coreEv}
【承接上文】${prevConn || '（无，已自衔接）'}
${prevHook ? '【前章埋钩】' + prevHook + ' ← 开篇必须回应此钩子' : ''}
【为下章埋钩】${hookForNext} ← 结尾必须完成此埋钩
${(prevChapterCorePointsFromAI.length > 0 || prevChapterConnectionFromAI) ? `
═══════════════════════════════════
【上章结尾提示】← 上章正文结束时 AI 分析出的下章必须要点，开头必须承接
═══════════════════════════════════════
${prevChapterCorePointsFromAI.length > 0 ? '【上章提示·核心要点】\n' + prevChapterCorePointsFromAI.map((p, i) => `${i + 1}. ${p.point || p}`).join('\n') : ''}
${prevChapterConnectionFromAI ? '【上章提示·衔接方式】' + prevChapterConnectionFromAI : ''}` : ''}

═══════════════════════════════════
【章节衔接】← 本章正文必须与前文自然衔接，禁止突兀转折
请仔细阅读前10章的故事脉络，特别是上一章的结尾片段，确保本章节能够自然流畅地承接上文。
从上一章结束的地方继续写作，不要重复已经描写过的内容，让故事自然向前推进。

═══════════════════════════════════
【输出格式规范】← 必须严格遵守，否则视为不合格
═══════════════════════════════════════
1. 段与段之间必须换行（一行空行），段落内不换行


═══════════════════════════════════
【文风一致性强制要求】← 本章必须与前文保持风格统一
═══════════════════════════════════════
【必须延续的前文风格】
- 叙事视角：第三人称限知视角/全知视角（与前10章保持一致）
- 语气基调：${currentPhaseLabel === '开局' ? '轻松好奇，带点幽默' : currentPhaseLabel === '发展' ? '紧张悬疑，暗流涌动' : currentPhaseLabel === '高潮' ? '激烈悲壮，节奏紧凑' : '沉稳收束，余韵悠长'}
- 句式节奏：保持与前10章相似的长短句搭配（避免通篇短句或通篇长句）
- 描写偏好：延续前几章的【环境描写:对话描写:动作描写】比例
- 用词风格：避免网络用语/谐音梗，保持文字的文学性和画面感

【必须避免的文风突变】
❌ 从轻松幽默突然变成严肃沉重
❌ 从简洁利落突然变成冗长堆砌
❌ 从动作戏为主突然变成大段内心独白
❌ 从短句快节奏突然变成长句慢节奏


════════════════════════════════════════════════════════════════════════
【写作心态·前置要求】← 这一段比后面所有规则都重要
════════════════════════════════════════════════════════════════════════

在你开始写之前，请先做一件事：

想象你要讲一件事给你最好的朋友听。他正叼着烟或者喝着奶茶，歪在沙发上听你讲。你得让他停下滑手机的手，眼睛抬起来看你。

怎么讲？
用你自己的话讲。不要背书。不要背小说。不要用形容词堆。不要写什么"眉头一皱""嘴角勾起""轰然""嗡"。

第一遍想出来的句子往往最真实。后面的修改才是润色，不是重新写。

现在，用最直白的口语化方式，把这件事讲出来。

开始了。


════════════════════════════════════════════════════════════════════════
【KM智能写作 · 风格革命宣言】← 核心理念，必须刻进骨子里
════════════════════════════════════════════════════════════════════════

【第一原则：先做人，再写作】
你的写作对象不是"读者"，是一个坐在马桶上刷手机的人。他随时可以划走。
所以正文第一要求是：让他停下来、读下去、不想划走。
做到这一点的方法只有一个——像真人写的。不是"像作家写的"，不是"像小说里写的"，是像你隔壁邻居、像你哥们姐们、像你真实认识的人写的。

【第二原则：删掉脑子里所有"小说写法"】
AI写作的本质是用"小说的套路"在堆文字。你要反着来。
真人写小说不会想"这段用什么修辞手法"。真人只会想"这事放我身上我怎么跟朋友说"。
想象你正在用微信语音跟朋友讲这件事，你会怎么讲？
——就怎么写。

【第三原则：宁可幼稚，不要油腻】
新手写得像新手，比老手写得像AI强一百倍。
宁可写："他推开门，看见里面坐满了人。"
不要写："他推门而入，但见厅堂之内，黑压压一片人头攒动。"
宁可写："她愣了一下。"
不要写："她怔了怔，那双眸子里闪过一丝不易察觉的惊愕。"

════════════════════════════════════════════════════════════════════════
【KM音效词与AI修饰词全面封杀】← 这是最高优先级禁令，无例外
════════════════════════════════════════════════════════════════════════

❌ 音效拟声词——AI最爱用，一用就是AI味，直接禁用：
  噗嗤、噗、轰、嘭、砰、啪、啪嗒、哐、哐当、咣、咣当、嗡、嗡鸣
  嗤、嗤笑、嘶、嘶哑、嘶鸣、咔、咔嚓、咔哒、嘎、嘎吱、嘎巴
  铮、铮铮、咚咚、咚咚咚、滴滴答答、哗、哗啦、哗然、嗖、嗖地
  嗤——、噗——、嗞、嗞嗞、噼啪、噼里啪啦、滋滋、嘎嘎

❌ AI高频使用的情绪词——禁用或替换：
  禁用：心有余悸、不寒而栗、芒刺在背、五味杂陈、百感交集、感同身受
        难以置信、不置可否、猝不及防、微不可察
        心中暗暗想到、忍不住在心里想到、不由得思索起来
  替换：把"他心有余悸"换成"他走了几步，又回头看了一眼"
       把"她百感交集"换成"她张了张嘴，最后什么都没说"

❌ AI高频副词——一切"X然"类副词，能不用就不用：
  禁用：旋即、蓦然、赫然、霎时、须臾、轰然、悄然、陡然、骤然
        猛然、骤然、陡然、已然、已然是、竟然、居然、居然是
  如果必须用副词，优先用：突然、一下、猛地、一下子的、随即

❌ AI常用的套路描写——写之前先自查：
  ✗ "嘴角勾起一抹弧度/冷笑/笑意" → 换成具体的动作
  ✗ "XXX的眼神中闪过一丝XXX" → 换成他做了什么
  ✗ "空气中弥漫着XXX的气息" → 换成具体闻到了什么/看到了什么
  ✗ "却见XXX，不知从何处……" → 直接写"XXX从……进来"
  ✗ "就在所有人以为……的时候" → 直接写结果，跳过这个废话
  ✗ "此话一出，众人皆惊" → 写谁什么反应，不要说"众人"
  ✗ "电光火石之间/说时迟那时快" → 直接写发生了什么，删掉这句废话
  ✗ "XXX不由得心中一凛" → 换成：他顿了顿，或者他脸色变了
  ✗ "XXX邪魅一笑" → 删掉
  ✗ "仿佛/如同"开头的比喻 → 先检查能不能删掉，不能删就用更土的比喻
  ✗ 比喻用流水账：时间如流水、人生如梦、世界如棋局、命运如戏

❌ AI特有的过渡词——少用，能删就删：
  禁用：然而、与此同时、众所周知、毋庸置疑、首先、其次、最后
  替换：然后、接着、没多久、过了一会儿、这之后

❌ AI特有的对话引词——禁止用"引出招式"：
  禁用：说时迟那时快、只见、却见、正欲说时、话音未落
  替换：直接写动作或结果

❌ AI万能结尾——结尾不要用这些套话：
  禁用：故事到这里暂告一段落、这一夜，注定无眠
  替换：戛然而止，让读者自己品

════════════════════════════════════════════════════════════════════════
【KM直白写作七条军规】← 违反任意一条视为不合格
════════════════════════════════════════════════════════════════════════

【军规一：名词要砸在地上】
不要写"一个杯子"，写"那只缺了口的搪瓷杯"。
不要写"外面下着雨"，写"雨顺着窗玻璃往下淌，汇成一条歪歪扭扭的线"。
不要写"他很紧张"，写"他把手在裤腿上蹭了两下"。

【军规二：动词要动起来】
少用"是""有""感到""觉得"。
✗ 他感到一阵寒意
✓ 寒意从后背窜上来，他缩了缩脖子
✓ 窗户缝里漏进来的风，像蛇一样往领子里钻
✓ 他把外套裹紧了

【军规三：对话要像吵架】
真人说话不会对仗工整。真人会打岔、会说半句、会吞字、会急眼。
✗ "此言差矣，"他摇了摇头，"我并不认同你的看法。"
✓ "不是，"他手一挥，"你听我说完行不行？"
✗ "哈哈哈！"她笑了起来，笑声清脆悦耳。
✓ 她笑得直拍大腿，"哈哈哈哈哈哈——不是，你认真的？"

【军规四：少写心理，多写动作】
AI最爱写心理。真人不是。真人想事情不会描述自己的内心。
✗ 他心中暗想，这次一定要赢。
✓ 他攥紧了拳头，指甲陷进肉里。
✓ 他没说话，盯着地上的烟头看了半天。
✓ 他把那张纸揉成一团，扔进了垃圾桶。

【军规五：战斗/冲突要快】
AI喜欢详细描写招式、对峙、你来我往。
真人写打架：一下就结束了，或者打到一半出事了。
✓ 他一拳砸在对方脸上，鼻血当场就下来了。
✓ 他还没反应过来，人已经倒了。
✓ 两边刚撕扯起来，旁边的人就过来拉架了。
不要写：双方对峙，气势如虹，招式名震天……直接写结果！

【军规六：形容词能删就删】
AI爱堆形容词，真人不会。
✗ 阴暗潮湿的地下室里，一个模糊的身影若隐若现
✓ 地下室里没开灯，角落里蹲着个人
✗ 气氛压抑得令人窒息
✓ 没人说话，他咽了口唾沫

【军规七：每一段第一句必须拽人】
开头第一句的任务只有一个：让读者想读第二句。
不要用废话开头（今天是个好日子、他是XX镇上一个普通的人）。
开头第一句公式 = 扔出一个事实/动作/问题/反常。
例："他推开门，屋里全是烟。"
例："第二天早上，她发现自己的车不见了。"
例："‘闭嘴。’他说。"
例："他迟到了整整两个小时。"

════════════════════════════════════════════════════════════════════════
【节奏基础标准】← ${targetWords}字章节的节奏框架
════════════════════════════════════════════════════════════════════════
每 ${Math.round(targetWords / 4)} 字 ≈ 一个完整情节点：
- 开头（前1/4）：直接回应前章钩子，快速进入本章核心事件，不水文
- 中段（中间1/2）：核心事件推进，加入至少2次对话冲突或1次战斗/势力碰撞
- 结尾（后1/4）：完成核心事件，自然引出下章埋钩，戛然而止留悬念

【叙事节奏要求】
- 开篇第一句必须抓住读者：场景/冲突/悬念三选一，不得用背景介绍开篇
- 事件推进节奏：起（冲突爆发）→ 承（对抗升级）→ 转（意外转折）→ 合（引出悬念）
- 每500-800字设置一个小高潮或转折点，避免流水账式叙事
- 战斗场景：动作简洁，不写超长招式描述，3-5句话解决一个回合

════════════════════════════════════════════════════════════════════════
【KM最终自检（写完必查）】← 有一条不合格就重写
════════════════════════════════════════════════════════════════════════

写完本章，对着以下清单逐条检查：

1. 【音效词扫描】全文搜索：噗嗤/轰/嗡/咔嚓/嗤/铮/咣/嗤笑/轰然/轰——/噗——/嗖——/噼啪——这些词出现任何一个，直接删或换
2. 【副词扫描】全文搜索："X然"类词（赫然/蓦然/旋即/霎时/须臾/悄然/轰然）出现超过2个，删到只剩1个以下
3. 【套路句扫描】出现"嘴角勾起""眼神闪过""此话一出""就在所有人以为""空气中弥漫"任一句式，全文删
4. 【比喻扫描】所有"仿佛/如同/好像"开头的比喻，问自己：能删吗？不能的话，换成什么更土/更具体的
5. 【对话扫描】连续2段以上"XXX说：'……'XXX答：'……'"格式，拆开，加动作或独白
6. 【心理扫描】连续3段以上都是内心独白（他觉得/他想/他心道），至少2段换成动作/对话
7. 【开头扫描】本章第一句和上一章第一句，如果都是"他/她+动作"结构，检查是否重复
8. 【形容词扫描】每段数一下形容词数量，超过3个的砍掉一半
9. 【过渡词扫描】"然而/与此同时/众所周知/毋庸置疑"出现任何一个，用更口语的词替换
10. 【标题扫描】标题是否符合：5-12字、有画面感、不对仗、不堆成语？否则重写

以上10条，任意1条有硬伤，视为不合格。

${(chapterPatterns.openingModes.length > 0 || chapterPatterns.battleModes.length > 0) ? `
════════════════════════════════════════════════════════════════════════
【前文模式分析·动态数据】← 本章必须避免与这些模式重复
════════════════════════════════════════════════════════════════════════
${chapterPatterns.openingModes.length > 0 ? `【已使用过的开头模式】（本章必须采用完全不同的开头方式）
${chapterPatterns.openingModes.map(m => `- 第${m.chapter}章：${m.type}`).join('\n')}` : ''}
${chapterPatterns.battleModes.length > 0 ? `【已使用过的战斗模式】（本章应采用不同的战斗方式）
${chapterPatterns.battleModes.map(m => `- 第${m.chapter}章：${m.type}`).join('\n')}` : ''}
${chapterPatterns.reactionModes.length > 0 ? `【已使用过的角色反应模式】（本章主角必须用不同的方式表达情绪）
${chapterPatterns.reactionModes.map(m => `- 第${m.chapter}章：${m.type}`).join('\n')}` : ''}
${chapterPatterns.sentenceStarters.length > 0 ? `【已使用过的句式开头】（本章第一句必须避开这些模式）
${chapterPatterns.sentenceStarters.slice(0, 5).map(m => `- 第${m.chapter}章：${m.type}（例："${m.example}"）`).join('\n')}` : ''}` : ''}

【本章参与人物】← 开篇必须让至少一人出场
${linkedCharSummaries}
${linkedItems.length > 0 ? '【涉及词条/道具】\n' + linkedItemSummaries : ''}
${linkedFsIds.length > 0 || mandatoryForeshadowings.length > 0 ? '【本章伏笔任务】← 强制要求，否则偏离大纲设计\n【必须铺垫/揭示】（本章是大纲指定的铺垫/揭示章节，正文必须明确包含以下伏笔）\n' + mandatoryFsBlock + '\n【其他相关伏笔】（在正文中有提及即可）\n' + optionalFsBlock : ''}
${relevantSubplots !== '暂无' ? '【本章支线任务】← 强制要求，必须在正文中推进至少1条支线\n' + relevantSubplots : ''}

═══════════════════════════════════
【全局一致性约束】← 仅作校验，不要让AI以此为主线
═══════════════════════════════════════
【主线阶段】${currentPhaseLabel}（开局→铺成长/发展→推冲突/高潮→引爆/结局→收束）
【世界观设定】← 正文写作时必须严格遵循此设定
${worldBlock || '暂无'}
【核心冲突】← 本章情节必须围绕至少一个核心冲突展开
${coreConflictsList}
【高潮设计】← 了解故事的高潮设计有助于把控节奏
${climaxesList}
${mainPlot !== '暂无主线大纲' ? '【全局主线】' + mainPlot.slice(0, 300) + (mainPlot.length > 300 ? '……' : '') : ''}
【角色关系网络】← 了解角色间关系有助于写出自然的互动
${relList}
${relevantSubplots !== '暂无' ? '【当前支线】' + relevantSubplots : ''}
${charStateSummary !== '暂无角色状态变化记录' ? '【角色状态快照】' + charStateSummary : ''}
${(() => {
  const fsStates = progressData?.foreshadowingChapterStates || [];
  const spStates = progressData?.subplotChapterStates || [];
  const fsLines = fsStates.filter(f => f.chapterNumber <= currentChNum && (f.status === 'planted' || f.status === 'revealed' || f.status === 'advancing'));
  const spLines = spStates.filter(s => s.chapterNumber <= currentChNum);
  if (fsLines.length === 0 && spLines.length === 0) return '';
  return '\n【伏笔/支线进度追踪】（来自正文生成后的AI分析，本章写作时必须推进这些伏笔/支线）\n' +
    (fsLines.length > 0 ? '【伏笔进度】\n' + fsLines.slice(-5).map(f => `第${f.chapterNumber}章：${f.foreshadowingTitle || f.title || ''} → 状态：${f.status === 'planted' ? '已铺垫' : f.status === 'revealed' ? '已揭示' : f.status === 'advancing' ? '推进中' : '待处理'} ${f.note || ''}`).join('\n') : '') +
    (spLines.length > 0 ? '\n【支线进度】\n' + spLines.slice(-5).map(s => `第${s.chapterNumber}章：${s.subplotTitle || s.title || ''} → 状态：${s.status === 'active' ? '进行中' : s.status || ''} ${s.note || ''}`).join('\n') : '');
})()}

═══════════════════════════════════
【前10章故事脉络】（理解故事演进，防止矛盾和重复）
${prev10Block}

═══════════════════════════════════
【通用写作原则】← 必须遵守，否则会导致逻辑漏洞
═══════════════════════════════════════
【战术决策原则】
- 每个战术行动必须有明确的胜算方案，不能让队友白白送死
- 如果需要有人做诱饵，必须说明撤退方案或替代方案
- 利用环境、道具、系统能力创造有利局面

【人物行为原则】
- 配角接受危险任务时，必须有合理的接受原因（被逼/利益/情感/性格/信任）
- 主角的每个决策必须对自己有明确的好处
- 重大行动的结果必须有合乎逻辑的解释

【技能使用原则】
- 新技能首次使用时，必须说明代价或限制
- 不能凭空出现解决危机

═══════════════════════════════════
【字数约束】← 严格控制，误差不超过100字
本章正文必须在 ${targetWords} 字以内，允许 ±${Math.min(100, Math.round(targetWords * 0.04))} 字误差。
- 宁可少写100字，也不要超过上限
- 低于 ${Math.round(targetWords * 0.96)} 字视为不完整
- 绝不允许超过 ${targetWords + 50} 字

═══════════════════════════════════
【本章标题】
要求：3-20个汉字，不要"第X章"前缀，有画面感或悬念感
禁止：直接复述情节、套路化爽文标题、双句式对仗、过度使用四字成语
推荐：有画面感的短句（5-8字）、悬念式提问、意象象征、动词+名词的动态感

输出格式：
【标题】你的标题
正文从这里开始，段落之间必须换行（一行空行隔开段落）

════════════════════════════════════════════════════════════════════════
【完稿前的人类审查】← 强制最后一步，未通过的不得输出
════════════════════════════════════════════════════════════════════════

正文写完之后，输出之前，请停下来读一遍。

如果读的时候脑子里出现了"这太AI了"，就重写。
如果读的时候出现了"这句话我能想象出他说话的样子"，就保留。

三个最快速的AI味检测：
1. 这一段能不能用一句话说清楚？AI喜欢把一件事说成三段，你一段就说完。
2. 这一段的形容词有没有超过3个？超过就砍一半。
3. 这一段有没有音效词（轰、嗡、噗嗤、咔嚓）和"X然"类副词（赫然、蓦然、旋即）？有就删。

如果你在输出之前最后读一遍，发现有明显AI味的段落，删掉重写。不要管字数够不够。字数不够AI会继续写，AI味重了整章就废了。
`;
        const targetWordsNum = chapterWordCount || 2000;
        const calculatedMaxTokens = Math.round(targetWordsNum * 1.8);
        const currentNovelName = novel?.title || '未知项目';

        // ============================================================
        // [正文生成] 详细调试日志 - 输出所有关键数据
        // ============================================================
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [正文生成] 开始构建提示词 - 数据汇总日志          ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');
        console.log('[基础信息]', {
          novelTitle: currentNovelName,
          genre,
          currentChNum,
          targetWords,
          targetWordsNum,
          calculatedMaxTokens
        });
        console.log('[章纲信息]', {
          coreEv: coreEv || '(空)',
          prevConn: prevConn || '(空)',
          prevHook: prevHook || '(空)',
          hookForNext: hookForNext || '(空)'
        });
        console.log('[主线阶段]', currentPhaseLabel);
        console.log('[当前选中章节]', {
          id: selectedChapterForEdit._id,
          title: selectedChapterForEdit.title,
          volumeId: selectedChapterForEdit.volumeId
        });
        console.log('[前10章数量] prev10.length:', prev10.length);
        console.log('[前10章章号列表]', prev10.map(c => c.chapterNumber));
        console.log('[前10章正文内容检查]');
        prev10.forEach((ch, idx) => {
          const contentLen = (ch.content || '').length;
          console.log('  第' + (idx+1) + '章 (第' + ch.chapterNumber + '章 "' + ch.title + '") 正文长度: ' + contentLen + '字');
        });
        console.log('[前10章完整正文块长度]', prev10Block.length, '字符');
        console.log('[模式分析结果]');
        console.log('  开头模式:', chapterPatterns.openingModes.length, '种', chapterPatterns.openingModes.map(m => m.type));
        console.log('  战斗模式:', chapterPatterns.battleModes.length, '种', chapterPatterns.battleModes.map(m => m.type));
        console.log('  反应模式:', chapterPatterns.reactionModes.length, '种', chapterPatterns.reactionModes.map(m => m.type));
        console.log('  句式开头:', chapterPatterns.sentenceStarters.length, '种', chapterPatterns.sentenceStarters.map(m => m.type));
        console.log('[伏笔信息]', {
          mandatoryFsCount: mandatoryForeshadowings.length,
          optionalFsCount: optionalForeshadowings.length,
          mandatoryFsBlock: mandatoryFsBlock || '(无强制伏笔)'
        });
        console.log('[支线信息]', relevantSubplots !== '暂无' ? '有 ' + relevantSubplots.split('\n').length + ' 条活跃支线' : '暂无活跃支线');
        console.log('[角色信息]', {
          linkedCharCount: linkedChars.length,
          charStateCount: activeCharStateEntries.length,
          linkedCharSummaries长度: linkedCharSummaries.length
        });
        console.log('[上下文信息]', {
          prevSummary: prevSummary || '(无)',
          prevChapterCorePointsCount: prevChapterCorePointsFromAI.length,
          prevChapterConnectionFromAI: prevChapterConnectionFromAI || '(无)'
        });
        console.log('[全局进度数据]', {
          progressData存在: !!progressData,
          characterStates数量: Object.keys(charStates).length,
          relationshipChanges数量: relChanges.length,
          foreshadowingChapterStates数量: (progressData?.foreshadowingChapterStates || []).length,
          subplotChapterStates数量: (progressData?.subplotChapterStates || []).length,
          nextChapterCorePoints数量: nextCorePoints.length
        });
        console.log('[大纲数据]', {
          mainPlot: (outlines[0]?.mainPlot?.storyline || outlines[0]?.mainPlot?.description || '暂无').slice(0, 100) + '...',
          subplotsCount: (outlines[0]?.subPlots || []).length,
          foreshadowingCount: (outlines[0]?.foreshadowing || []).length,
          characterNetworkCount: (outlines[0]?.characterNetwork || []).length,
          climaxesCount: (outlines[0]?.climaxes || []).length,
          coreConflictsCount: (outlines[0]?.coreConflicts || []).length,
          worldSettings: worldBlock ? '已构建' : '为空'
        });
        console.log('[API调用参数]', {
          model: '***', // 前端隐藏具体模型名称
          temperature: 0.45,
          max_tokens: Math.min(Math.max(calculatedMaxTokens, 2000), 20000),
          stream: true,
          operationType: 'AI生成章节正文'
        });
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [正文生成] 即将发送API请求                       ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');

        // [正文生成] 输出完整提示词内容供调试
        console.log('\n========== [正文生成] 完整提示词内容 ==========\n');
        console.log(prompt);
        console.log('\n========== [正文生成] 完整提示词结束 ==========\n');

        const streamParams = {
          model: 'deepseek-v4-flash',
          messages: [{ role: 'user', content: prompt }],
          temperature: 0.45,
          max_tokens: Math.min(Math.max(calculatedMaxTokens, 2000), 20000),
          stream: true,
          extra_body: {},
          billingInfo: {
            novelName: currentNovelName,
            chapterNumber: currentChNum,
            operationType: 'AI生成章节正文'
          }
        };
        const streamResult = await handleStreamGeneration(
          getApiBaseUrl(),
          streamParams,
          '章节正文',
          (_chunk, fullContent) => {
            latestChapterBodyStreamTextRef.current = fullContent;
            setSelectedChapterForEdit(prev => {
              if (!prev || prev._id !== chapterIdSnapshot) return prev;
              return {
                ...prev,
                outline: {
                  ...prev.outline,
                  generatedContent: fullContent,
                  lastGeneratedAt: new Date(),
                },
              };
            });
            scheduleChapterBodyStreamSave();
          },
          (msg) => { console.log('[章节正文·流式]', msg); },
          '/assist/ai/proxy-ai',
        );

        setBodyGenerating(false);
        chapterBodyStreamSaveTimerRef.current = null;

        const genText = streamResult.content || '';

        // [正文生成] 结果日志
        console.log('╔══════════════════════════════════════════════════════════════╗');
        console.log('║          [正文生成] 生成结果日志                          ║');
        console.log('╚══════════════════════════════════════════════════════════════╝');
        console.log('[生成结果总长度]', genText.length, '字符');
        console.log('[生成结果前500字]', genText.slice(0, 500));

        if (!genText.trim()) {
          setSnackbar({ open: true, message: '正文生成结果为空', severity: 'warning' });
        } else {
          // 从 AI 生成内容中解析标题 + 正文
          let parsedTitle = '';
          let bodyText = genText;
          // 优先匹配【标题】xxx 格式（AI应该严格遵循此格式）
          const titleMatch = genText.match(/^【标题】\s*(.+?)\s*[\n\r]+/um);
          if (titleMatch) {
            parsedTitle = titleMatch[1].trim();
            bodyText = genText.slice(titleMatch[0].length);
            console.log('[标题解析-方式]', '标准格式【标题】匹配成功');
          } else {
            // 备选方案：如果AI没有按格式返回，尝试从第一行提取（4-20个字符，无特殊前缀）
            const altMatch = genText.match(/^(.{4,20})[\n\r]+/);
            if (altMatch) {
              const firstLine = altMatch[1].trim();
              const hasBody = genText.slice(altMatch[0].length).trim();
              if (hasBody && firstLine.length >= 4 && firstLine.length <= 20 && !/^[#*=【】《》「」『』\s]/.test(firstLine)) {
                parsedTitle = firstLine;
                bodyText = genText.slice(altMatch[0].length);
                console.log('[标题解析-方式]', '备选方案-第一行提取');
              } else {
                console.log('[标题解析-方式]', '未能解析标题（不符合条件）');
                console.log('[备选方案-检测]', {
                  firstLine,
                  firstLine长度: firstLine.length,
                  hasBody: !!hasBody,
                  符合长度条件: firstLine.length >= 4 && firstLine.length <= 20,
                  无特殊前缀: !/^[#*=【】《》「」『』\s]/.test(firstLine)
                });
              }
            } else {
              console.log('[标题解析-方式]', '未能解析标题（无匹配行）');
            }
          }
          console.log('[标题提取结果]', {
            parsedTitle: parsedTitle || '(空)',
            bodyLength: bodyText.length,
            body前100字: bodyText.slice(0, 100)
          });

          // 标题仅暂存在章纲 outline.generatedTitle，等用户点「插入正文」再写入列表与数据库
          setSelectedChapterForEdit(prev => {
            if (!prev || prev._id !== chapterIdSnapshot) return prev;
            const nextOutline = {
              ...prev.outline,
              generatedContent: bodyText,
              lastGeneratedAt: new Date(),
            };
            if (parsedTitle) nextOutline.generatedTitle = parsedTitle;
            else delete nextOutline.generatedTitle;
            return { ...prev, outline: nextOutline };
          });
          let finalOutlineSaveFailed = false;
          try {
            const chForSave = selectedChapterForEditRef.current;
            if (chForSave && chForSave._id === chapterIdSnapshot) {
              const outlineToSave = { ...chForSave.outline, generatedContent: bodyText, lastGeneratedAt: new Date() };
              if (parsedTitle) outlineToSave.generatedTitle = parsedTitle;
              else delete outlineToSave.generatedTitle;
              const charIds = (chForSave.characterIds || []).length ? [...chForSave.characterIds] : [...wizardSelectedChars];
              const fsIds = (chForSave.foreshadowingIds || []).length ? [...chForSave.foreshadowingIds] : [...wizardSelectedForeshadowing];
              const itIds = (chForSave.itemIds || []).length ? [...chForSave.itemIds] : [...wizardSelectedItems];
              const saveResp = await apiClient.post('/assist/novels/' + assistNovelId + '/chapters/' + chapterIdSnapshot + '/outline', {
                outline: outlineToSave,
                status: chForSave.status || statusForSave,
                characterIds: charIds,
                foreshadowingIds: fsIds,
                itemIds: itIds,
              });
              if (saveResp.data?.success) {
                setChapters(prev => prev.map(c => (c._id === chapterIdSnapshot
                  ? { ...c, outline: outlineToSave, characterIds: charIds, foreshadowingIds: fsIds, itemIds: itIds }
                  : c)));
              }
            }
          } catch (finalSaveErr) {
            finalOutlineSaveFailed = true;
            console.error('[流式正文] 最终保存失败:', finalSaveErr);
            setSnackbar({ open: true, message: '正文已生成但保存失败：' + (finalSaveErr.response?.data?.message || finalSaveErr.message), severity: 'error' });
          }
          if (!finalOutlineSaveFailed) {
            if (!streamResult.isComplete) {
              setSnackbar({
                open: true,
                message: '正文已保存至章纲；流式连接可能提前结束，若内容不完整请重试',
                severity: 'warning',
              });
            } else {
              setSnackbar({
                open: true,
                message: parsedTitle
                  ? '正文生成完成；点击「插入正文」后标题将写入章节列表（已去掉书名号式括号）'
                  : '正文生成完成，已写入章纲（可预览、编辑后插入正文）',
                severity: 'success',
              });
            }

            // ── 生成后内容重复检测 ────────────────────────────────
            const chForDetect = selectedChapterForEditRef.current;
            if (chForDetect && bodyText.trim()) {
              const currentChNum = chForDetect.chapterNumber || 1;
              // 获取前3章内容用于对比（避免与前文重复）
              const prevChaptersForDetect = chapters
                .filter(c => c._id !== chapterIdSnapshot && c.content && c.content.trim())
                .sort((a, b) => b.chapterNumber - a.chapterNumber)
                .slice(0, 3)
                .map(c => ({
                  chapterNumber: c.chapterNumber,
                  title: c.title,
                  content: c.content
                }));
              
              if (prevChaptersForDetect.length > 0) {
                const repetitionResult = detectContentRepetition(bodyText, prevChaptersForDetect, 0.6);
                if (repetitionResult.hasRepetition) {
                  console.log('[重复检测] ⚠️ 第' + currentChNum + '章与前文重复度检测结果:', repetitionResult);
                  setSnackbar(prev => ({
                    ...prev,
                    open: true,
                    message: '⚠️ 重复检测提示：与前文相似度达' + Math.round(repetitionResult.maxSimilarity * 100) + '%，请检查后决定是否使用',
                    severity: 'warning',
                  }));
                } else {
                  console.log('[重复检测] ✅ 第' + currentChNum + '章内容无明显重复，相似度: ' + Math.round(repetitionResult.maxSimilarity * 100) + '%');
                }
              }
            }
          }
        }

        // ════ 自动生成下一章章纲已禁用 ════
        // ════ 如需启用，请联系开发人员 ════
        // 【原逻辑】当点击「插入正文」时，如果下一章没有章纲，会自动生成
        // 【原因】用户反馈希望手动控制章纲生成，避免自动生成的章纲不符合预期
        //
        // const nextCh = chapters.find(c => c.chapterNumber === currentChNum + 1);
        // if (nextCh && !nextCh.outline?.coreEvent) {
        //   try {
        //     ... (自动生成逻辑)
        //   } catch (nextErr) {
        //     console.error('自动生成下一章章纲失败:', nextErr);
        //   }
        // }
      } catch (e) {
        setSnackbar({ open: true, message: '生成失败：' + (e.response?.data?.message || e.message), severity: 'error' });
      } finally {
        setOutlineGenerating(false);
      }
    };

    const handleSaveChapterContentPanel = async () => {
      const ch = selectedChapterForEdit;
      if (!ch) return;
      const content = ch.content || '';
      const nid = assistNovelId || novel?._id;
      if (!nid) {
        setSnackbar({ open: true, message: '作品未加载', severity: 'warning' });
        return;
      }
      const isAssist = String(nid).startsWith('assist_');
      try {
        if (isAssist) {
          const resp = await apiClient.post('/assist/novels/' + nid + '/chapters', { ...ch, content });
          if (resp.data?.success) {
            const updated = resp.data.data;
            setChapters(prev => prev.map(c => c._id === updated._id ? updated : c));
            setSelectedChapterForEdit(updated);
            if (currentChapter?._id === updated._id) {
              setCurrentChapter(updated);
              setChapterContent(content);
            }
            setSnackbar({ open: true, message: '正文已保存', severity: 'success' });
          }
        } else {
          const idx = chapters.findIndex(c => c._id === ch._id);
          if (idx < 0) {
            setSnackbar({ open: true, message: '找不到该章节索引', severity: 'error' });
            return;
          }
          await apiClient.put('/novels/' + novel._id + '/chapters/' + (idx + 1), { content });
          const updated = { ...ch, content };
          setChapters(prev => prev.map(c => c._id === ch._id ? updated : c));
          setSelectedChapterForEdit(updated);
          if (currentChapter?._id === ch._id) {
            setCurrentChapter(updated);
            setChapterContent(content);
          }
          setSnackbar({ open: true, message: '正文已保存', severity: 'success' });
        }
      } catch (e) {
        setSnackbar({ open: true, message: '保存正文失败：' + (e.response?.data?.message || e.message), severity: 'error' });
      }
    };

    const handleSaveAndComplete = async () => {
      if (!assistNovelId || !selectedChapterForEdit) return;
      const updated = { ...selectedChapterForEdit, status: 'completed' };
      setSelectedChapterForEdit(updated);
      try {
        await apiClient.post('/assist/novels/' + assistNovelId + '/chapters/' + updated._id + '/outline', {
          outline: updated.outline,
          status: 'completed',
        });
        setChapters(prev => prev.map(c => c._id === updated._id ? updated : c));
        setSnackbar({ open: true, message: '章节已标记完成', severity: 'success' });
      } catch (e) {
        setSnackbar({ open: true, message: '操作失败：' + (e.response?.data?.message || e.message), severity: 'error' });
      }
    };

    // ================================================================
    // 移动端：章节列表视图
    // ================================================================
    const renderMobileListView = () => (
      <Box
        className="workspace-mobile-chapter-list"
        sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        /* 主流 App 做法：横向只留安全区 + 6px，避免「中间一条窄内容」 */
        pl: 'max(6px, env(safe-area-inset-left, 0px))',
        pr: 'max(6px, env(safe-area-inset-right, 0px))',
        }}
      >
        {/* 标题栏 — 正文字重、非海报风；左上角返回（原右上角浮动关闭已移除） */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.5,
          pl: 'max(2px, env(safe-area-inset-left, 0px))',
          pr: 'max(2px, env(safe-area-inset-right, 0px))',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexShrink: 0,
        }}>
          <Tooltip title="返回编辑">
            <IconButton
              size="small"
              onClick={handleClosePanel}
              aria-label="返回编辑"
              sx={{
                flexShrink: 0,
                minWidth: 34,
                minHeight: 34,
                p: 0.35,
                color: 'rgba(255,255,255,0.75)',
                border: '1px solid rgba(255,255,255,0.18)',
                bgcolor: 'rgba(255,255,255,0.04)',
                '&:hover': { bgcolor: 'rgba(0,255,159,0.12)', borderColor: 'rgba(0,255,159,0.35)' },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography sx={{ color: '#00ff9f', fontWeight: 600, fontSize: 14, letterSpacing: 0, flex: 1, lineHeight: 1.2, minWidth: 0 }}>
            章纲·章节创作台
          </Typography>
          <Tooltip title="新建卷">
            <IconButton size="small" onClick={handleAddVolume}
              sx={{
                color: '#fde047',
                bgcolor: 'rgba(255,215,0,0.06)',
                border: '1px solid rgba(255,215,0,0.28)',
                borderRadius: 1,
                p: 0.4,
                '&:hover': { bgcolor: 'rgba(255,215,0,0.12)' },
              }}>
              <CreateNewFolderIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* 搜索栏 */}
        <Box sx={{ py: 0.4, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
          <TextField
            fullWidth size="small" placeholder="搜索章节..."
            value={chapterSearch}
            onChange={(e) => setChapterSearch(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255,255,255,0.04)',
                borderRadius: 1.5,
                minHeight: 34,
                py: 0,
                '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                '&:hover fieldset': { borderColor: 'rgba(0,240,255,0.22)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(0,255,159,0.4)', borderWidth: 1 },
              },
              '& .MuiInputBase-input': { fontSize: 13, py: 0.5, fontWeight: 400 },
            }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.35, fontSize: 17 }} /> }}
          />
        </Box>

        {/* 章节列表（可滚动） */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 0.35, minHeight: 0 }}>
          {volumes.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SummarizeIcon sx={{ fontSize: 52, mb: 2, opacity: 0.2, color: '#00ff9f' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mb: 0.5 }}>还没有卷和章节</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, mb: 2 }}>点击右上角 + 创建第一个卷</Typography>
              <Button size="small" onClick={handleAddVolume}
                sx={{ color: '#ffd700', border: '1px solid rgba(255,215,0,0.3)', fontSize: 11 }}>
                + 新建卷
              </Button>
            </Box>
          ) : (
            <>
              {volumesSortedForAssist.map((volume, volIdx) => (
                <Box key={volume._id} sx={{ mb: 0.75 }}>
                  {/* 卷标题栏 — 紧凑但仍易点 */}
                  <Box
                    onClick={() => setExpandedVolumes(prev => ({ ...prev, [volume._id]: !prev[volume._id] }))}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 0.45,
                      px: 0.75,
                      bgcolor: 'rgba(255,215,0,0.06)',
                      border: '1px solid rgba(255,215,0,0.22)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      minHeight: 36,
                      '&:hover': { borderColor: 'rgba(255,215,0,0.4)', bgcolor: 'rgba(255,215,0,0.08)' },
                      '&:active': { opacity: 0.92 },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: '#fde047', fontSize: 11, flexShrink: 0, fontWeight: 500, width: 14, textAlign: 'center', lineHeight: 1 }}>
                        {expandedVolumes[volume._id] !== false ? '▼' : '▶'}
                      </Typography>
                      <Typography sx={{ color: '#fef08a', fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.25 }}>
                        {volume.name || '第' + volume.order + '卷'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                      <IconButton
                        className="workspace-volume-inline-action"
                        size="small"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await handleCreateChapter(volume._id);
                        }}
                        sx={{
                          color: '#fde047',
                          bgcolor: 'rgba(0,0,0,0.2)',
                          border: '1px solid rgba(255,215,0,0.25)',
                          borderRadius: 1.5,
                          p: 0.5,
                          minWidth: 36,
                          minHeight: 36,
                          '&:hover': { bgcolor: 'rgba(255,215,0,0.15)', borderColor: 'rgba(255,215,0,0.5)' },
                        }}>
                        <AddIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  {/* 章节列表（卷名已在上方展示，不再重复卷标） */}
                  {expandedVolumes[volume._id] !== false && (
                    <Box sx={{ pl: 0.5, mt: 0.25, borderLeft: '1px solid rgba(255,215,0,0.15)', ml: 0.25 }}>
                      {getChaptersForVolumeList(volume, volIdx).map(chapter => renderChapterItem(chapter, false))}
                    </Box>
                  )}
                </Box>
              ))}
              {(() => {
                const uncl = getUnclassifiedChapters();
                return uncl.length > 0 ? (
                  <Box sx={{ mt: 0.5, px: 0 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, mb: 0.35, px: 0.5 }}>未归类章节</Typography>
                    {uncl.map(ch => renderChapterItem(ch, true))}
                  </Box>
                ) : null;
              })()}
            </>
          )}
        </Box>
      </Box>
    );

    // ================================================================
    // 移动端：章节编辑视图（现代版 — 与桌面端四卡片布局保持一致）
    // ================================================================
    const renderMobileEditView = () => {
      const ch = selectedChapterForEdit;
      if (!ch) return null;
      const n = ch.chapterNumber;
      const raw = (ch.title || '').trim();
      const defaultTitle = `第${n}章`;
      const showSubtitle = raw && raw !== defaultTitle;

      const currentChNum = ch.chapterNumber || 0;
      const autoRelatedForeshadowing = (outlines[0]?.foreshadowing || []).filter(f => {
        if (f.status === 'revealed') return false;
        if (f.plantedChapterNumber) {
          if (f.revealChapterNumber) return currentChNum >= f.plantedChapterNumber && currentChNum <= f.revealChapterNumber;
          return currentChNum >= f.plantedChapterNumber;
        }
        if (!f.plantedChapterId) return false;
        const plantedChapter = chapters.find(c => c._id === f.plantedChapterId);
        if (!plantedChapter) return false;
        const pNum = plantedChapter.chapterNumber;
        if (f.revealChapterId) {
          const revealChapter = chapters.find(c => c._id === f.revealChapterId);
          if (revealChapter) return currentChNum >= pNum && currentChNum <= revealChapter.chapterNumber;
        }
        return currentChNum >= pNum;
      }).map(f => f.id || f._id);

      const autoRelatedSubplots = (outlines[0]?.subPlots || []).filter(s => {
        if (s.startChapterNumber && s.endChapterNumber) return currentChNum >= s.startChapterNumber && currentChNum <= s.endChapterNumber;
        return false;
      }).map(s => s.id || s._id);

      // 统一卡片样式（移动端收紧 padding/间距）
      const cardBase = (borderColor, bgColor) => ({
        p: { xs: 1.5, sm: 2 },
        borderRadius: { xs: 2, sm: 2.5 },
        border: '1px solid',
        borderColor,
        bgcolor: bgColor,
        boxShadow: '0 4px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
        mb: { xs: 1.5, sm: 2 },
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      });

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {/* 头部 — 固定 */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            pl: 'max(12px, env(safe-area-inset-left, 0px))',
            pr: 'max(12px, env(safe-area-inset-right, 0px))',
            py: 1.25,
            borderBottom: '1px solid rgba(0,255,159,0.2)',
            flexShrink: 0,
            bgcolor: 'rgba(6,12,24,0.92)',
            backdropFilter: 'blur(16px)',
            position: 'sticky', top: 0, zIndex: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
          }}>
            <IconButton size="small" onClick={() => { setSelectedChapterForEdit(null); }}
              sx={{ color: '#e2e8f0', bgcolor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 2, minWidth: 42, minHeight: 42, '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(0,255,159,0.35)' } }}>
              <ArrowBackIcon sx={{ fontSize: 22 }} />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: '#4ade80', fontWeight: 800, fontSize: { xs: 15, sm: 16 }, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                {showSubtitle ? defaultTitle : (raw || defaultTitle)}
              </Typography>
              {showSubtitle && (
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: { xs: 12, sm: 13 }, fontWeight: 600, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', mt: 0.25 }}>
                  {raw}
                </Typography>
              )}
            </Box>
            <Tooltip title="写正文">
              <IconButton size="small" onClick={() => { if (ch) { setCurrentChapter(ch); setChapterContent(ch.content || ''); setActivePanel(null); } }}
                sx={{ color: '#22d3ee', bgcolor: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.35)', borderRadius: 2, minWidth: 42, minHeight: 42, '&:hover': { bgcolor: 'rgba(34,211,238,0.2)' } }}>
                <OpenInNewIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            {/* 手动保存按钮 */}
            <Tooltip title="保存章纲">
              <IconButton size="small" onClick={() => { if (ch) { persistMergedWizardOutlineForChapter(ch); } }}
                sx={{ color: 'var(--ws-accent)', bgcolor: 'rgba(var(--ws-accent-rgb),0.1)', border: '1px solid rgba(var(--ws-accent-rgb),0.35)', borderRadius: 2, minWidth: 42, minHeight: 42, '&:hover': { bgcolor: 'rgba(var(--ws-accent-rgb),0.2)' } }}>
                <SaveIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            {/* 保存时间显示 */}
            {chapterOutlineSavedTime && (
              <Typography sx={{ fontSize: 10, color: 'rgba(var(--ws-accent-rgb),0.6)', whiteSpace: 'nowrap' }}>
                已保存 {formatTimeDisplay(chapterOutlineSavedTime)}
              </Typography>
            )}
          </Box>

          {/* 主体 — 可滚动 */}
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, px: 'max(12px, env(safe-area-inset-left, 12px))', py: 2, pr: 'max(12px, env(safe-area-inset-right, 12px))' }}>

            {/* 向导头部（与天青卷条同色） */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 14 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { boxShadow: '0 8px 28px rgba(56,189,248,0.12)', transition: { duration: 0.25 } }}
              sx={{ ...cardBase('rgba(125,211,252,0.38)', 'linear-gradient(145deg, rgba(56,189,248,0.1) 0%, rgba(8,14,24,0.92) 55%, rgba(10,14,22,0.88) 100%)'), mb: 2 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <BookIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#7dd3fc', flexShrink: 0, filter: 'drop-shadow(0 0 10px rgba(125,211,252,0.35))' }} />
                  <Typography sx={{ color: '#e0f2fe', fontSize: { xs: 13, sm: 15 }, fontWeight: 700, lineHeight: 1.35, wordBreak: 'break-word' }}>
                    第{n}章 · 智能章纲编辑
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={!selectedChapterForEdit ? '请先选择一个章节' : globalProgressSyncing ? '全局进度正在更新中，请稍候...' : outlineGenerating ? '正在生成章纲中...' : 'AI 辅助生成章纲（生成后自动保存）'}>
                    <span>
                      <Button
                        size="small"
                        className="assist-chapter-ai-glow-btn"
                        disabled={outlineGenerating || globalProgressSyncing || !selectedChapterForEdit}
                        onClick={() => runChaptersPanelAiSmartOutlineGenerate()}
                        sx={{ px: 1.5, py: 0.65, fontSize: 11, fontWeight: 700 }}
                      >
                        {outlineGenerating ? <CircularProgress size={12} sx={{ color: '#bae6fd' }} /> : (globalProgressSyncing ? <CircularProgress size={12} sx={{ color: '#38bdf8' }} /> : '生成章纲')}
                      </Button>
                    </span>
                  </Tooltip>
                  {globalProgressSyncing && (
                    <Typography sx={{ color: '#38bdf8', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      同步中
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ color: 'rgba(226,232,240,0.48)', fontSize: { xs: 10, sm: 11 }, lineHeight: 1.5 }}>
                  系统已根据章节号自动匹配相关伏笔和支线
                </Typography>
                <Chip label="智能模式" size="small" sx={{ bgcolor: 'rgba(56,189,248,0.14)', color: '#bae6fd', fontSize: 9, fontWeight: 700, height: 20, border: '1px solid rgba(125,211,252,0.35)', borderRadius: 1.5 }} />
                <Chip label="手动修改后需点击保存" size="small" sx={{ bgcolor: 'rgba(255,193,7,0.15)', color: '#ffc107', fontSize: 9, fontWeight: 700, height: 20, border: '1px solid rgba(255,193,7,0.3)', borderRadius: 1.5 }} />
              </Box>
            </Box>

            {/* ══ 四卡片区 ══ */}

            {/* 本章出场角色 */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 16 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
              sx={{ ...cardBase('rgba(125,211,252,0.32)', 'rgba(56,189,248,0.06)') }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                  <PersonIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#7dd3fc', flexShrink: 0, opacity: 0.95 }} />
                  <Typography sx={{ color: '#e0f2fe', fontSize: { xs: 12, sm: 13 }, fontWeight: 700, lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    本章出场角色
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  <IconButton size="small" onClick={() => setSelectCharDialogOpen(true)}
                    sx={{ color: '#7dd3fc', minWidth: 34, minHeight: 34, bgcolor: 'rgba(56,189,248,0.12)', border: '1px solid rgba(125,211,252,0.35)', borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(56,189,248,0.2)' } }}>
                    <AddIcon sx={{ fontSize: { xs: 15, sm: 17 } }} />
                  </IconButton>
                  <IconButton size="small"
                    onClick={() => {
                      const prevChapters = chapters.filter(c => c.chapterNumber < n).sort((a, b) => b.chapterNumber - a.chapterNumber).slice(0, 5);
                      const charCountMap = {};
                      prevChapters.forEach(pc => { [...(pc.characterIds || []), ...(pc.outline?.plotPoints || []).flatMap(p => p.characterIds || [])].forEach(id => { charCountMap[id] = (charCountMap[id] || 0) + 1; }); });
                      const recommended = Object.entries(charCountMap).sort((a, b) => b[1] - a[1]).filter(([_, c]) => c >= 1).slice(0, 5).map(([id]) => id);
                      if (recommended.length === 0) { const mainChar = characters.find(c => c.role === 'protagonist'); if (mainChar) recommended.push(mainChar._id); }
                      if (recommended.length === 0 && characters.length > 0) recommended = characters.slice(0, 3).map(c => c._id);
                      const merged = [...new Set([...wizardSelectedChars, ...recommended])];
                      setWizardSelectedChars(merged);
                      setSelectedChapterForEdit(prev => prev ? { ...prev, characterIds: merged, outline: { ...prev.outline, plotPoints: (prev.outline?.plotPoints || []).map((pt, idx) => idx === 0 ? { ...pt, characterIds: merged } : pt) } } : prev);
                      // 自动保存推荐角色到服务器
                      if (selectedChapterForEdit?._id) {
                        apiClient.post(`/assist/novels/${assistNovelId}/chapters/${selectedChapterForEdit._id}/outline`, {
                          outline: selectedChapterForEdit.outline || {},
                          status: selectedChapterForEdit.status || 'outline_only',
                          characterIds: merged,
                          foreshadowingIds: selectedChapterForEdit.foreshadowingIds || [],
                          itemIds: selectedChapterForEdit.itemIds || [],
                        }).then(() => {
                          setChapters(prev => prev.map(c => c._id === selectedChapterForEdit._id ? { ...c, characterIds: merged } : c));
                        }).catch(err => console.error('[智能推荐] 保存角色失败:', err));
                      }
                    }}
                    sx={{ color: '#7dd3fc', minWidth: 34, minHeight: 34, bgcolor: 'rgba(56,189,248,0.1)', border: '1px solid rgba(125,211,252,0.35)', borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(56,189,248,0.18)' } }}
                    aria-label="智能推荐角色"
                  >
                    <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>荐</Typography>
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {wizardSelectedChars.length === 0 && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: { xs: 11, sm: 12 } }}>点击「生成章纲」将自动推荐本章角色</Typography>
                )}
                {wizardSelectedChars.map(charId => {
                  const c = characters.find(ch => ch._id === charId);
                  if (!c) return null;
                  const roleLb = getCharacterDisplayRoleLabel(c);
                  const rv = getCharacterRoleVisual(roleLb);
                  return (
                    <Tooltip key={c._id} title={buildCharacterChapterOutlineTooltip(c)} {...chapterOutlineCharTooltipProps}>
                      <Box
                        component="span"
                        onClick={() => {
                          const newIds = wizardSelectedChars.filter(id => id !== c._id);
                          setWizardSelectedChars(newIds);
                          setSelectedChapterForEdit(prev => prev ? { ...prev, characterIds: newIds, outline: { ...prev.outline, plotPoints: (prev.outline?.plotPoints || []).map((pt, idx) => idx === 0 ? { ...pt, characterIds: newIds } : pt) } } : prev);
                        }}
                        sx={{
                          px: 0.75,
                          py: 0.375,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor: 'rgba(56,189,248,0.1)',
                          border: '1px solid rgba(125,211,252,0.32)',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.35,
                          maxWidth: '100%',
                          '&:hover': { borderColor: 'rgba(186,230,253,0.65)', bgcolor: 'rgba(56,189,248,0.16)' },
                        }}
                      >
                        <Typography component="span" sx={{ fontSize: 8, fontWeight: 800, px: 0.35, py: 0.05, borderRadius: 0.5, lineHeight: 1.25, flexShrink: 0, bgcolor: rv.bg, color: rv.color, border: rv.border }}>
                          {roleLb}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getCharacterChapterOutlineDisplayName(c)}</Typography>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* 关联伏笔 */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 16 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.11, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
              sx={{ ...cardBase('rgba(167,139,250,0.35)', 'rgba(139,92,246,0.07)') }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                  <HubIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#c4b5fd', flexShrink: 0 }} />
                  <Typography sx={{ color: '#ddd6fe', fontSize: { xs: 12, sm: 13 }, fontWeight: 700 }}>关联伏笔</Typography>
                </Box>
                {autoRelatedForeshadowing.length > 0 && (
                  <Chip label={`${autoRelatedForeshadowing.length}个相关`} size="small" sx={{ bgcolor: 'rgba(var(--ws-accent-rgb),0.15)', color: '#f9a8d4', fontSize: { xs: 8, sm: 9 }, border: '1px solid rgba(var(--ws-accent-rgb),0.3)', height: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(outlines[0]?.foreshadowing || []).filter(f => autoRelatedForeshadowing.includes(f.id || f._id)).length > 0 ? (
                  (outlines[0].foreshadowing || []).filter(f => autoRelatedForeshadowing.includes(f.id || f._id)).map(f => {
                    const fid = f.id || f._id;
                    const isSelected = wizardSelectedForeshadowing.includes(fid);
                    return (
                      <Box key={fid} onClick={() => {
                        const newIds = wizardSelectedForeshadowing.includes(fid) ? wizardSelectedForeshadowing.filter(id => id !== fid) : [...wizardSelectedForeshadowing, fid];
                        setWizardSelectedForeshadowing(newIds);
                        setSelectedChapterForEdit(prev => prev ? { ...prev, foreshadowingIds: newIds, outline: { ...prev.outline, plotPoints: (prev.outline?.plotPoints || []).map((pt, idx) => idx === 0 ? { ...pt, foreshadowingIds: newIds } : pt) } } : prev);
                      }}
                        sx={{ px: 1, py: 0.375, borderRadius: 1, cursor: 'pointer', bgcolor: isSelected ? 'rgba(var(--ws-accent-rgb),0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isSelected ? 'rgba(var(--ws-accent-rgb),0.45)' : 'rgba(255,255,255,0.1)'}`, display: 'flex', alignItems: 'center', gap: 0.375, '&:hover': { borderColor: 'rgba(var(--ws-accent-rgb),0.4)', bgcolor: 'rgba(var(--ws-accent-rgb),0.1)' } }}>
                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color: isSelected ? '#f9a8d4' : '#fff' }}>{f.title || '未命名'}</Typography>
                        <Typography sx={{ fontSize: { xs: 8, sm: 9 }, color: f.status === 'revealed' ? '#06b6d4' : f.status === 'advancing' ? '#06b6d4' : '#ffd700' }}>
                          {f.status === 'revealed' ? '已揭' : f.status === 'advancing' ? '推进' : '已铺'}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: { xs: 11, sm: 12 } }}>本章暂无相关伏笔</Typography>
                )}
              </Box>
            </Box>

            {/* 关联支线 */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 16 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
              sx={{ ...cardBase('rgba(45,212,191,0.32)', 'rgba(13,148,136,0.08)') }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                  <AccountTreeIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#5eead4', flexShrink: 0 }} />
                  <Typography sx={{ color: '#ccfbf1', fontSize: { xs: 12, sm: 13 }, fontWeight: 700 }}>关联支线</Typography>
                </Box>
                {autoRelatedSubplots.length > 0 && (
                  <Chip label={`${autoRelatedSubplots.length}个相关`} size="small" sx={{ bgcolor: 'rgba(var(--ws-accent-rgb),0.15)', color: 'var(--ws-accent-muted)', fontSize: { xs: 8, sm: 9 }, border: '1px solid rgba(var(--ws-accent-rgb),0.3)', height: { xs: 18, sm: 20 }, flexShrink: 0 }} />
                )}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(outlines[0]?.subPlots || []).filter(s => autoRelatedSubplots.includes(s.id || s._id)).length > 0 ? (
                  (outlines[0].subPlots || []).filter(s => autoRelatedSubplots.includes(s.id || s._id)).map(s => {
                    const sid = s.id || s._id;
                    const isSelected = wizardSelectedSubplots.includes(sid);
                    return (
                      <Box key={sid} onClick={() => {
                        const newIds = wizardSelectedSubplots.includes(sid) ? wizardSelectedSubplots.filter(id => id !== sid) : [...wizardSelectedSubplots, sid];
                        setWizardSelectedSubplots(newIds);
                        setSelectedChapterForEdit(prev => prev ? { ...prev, outline: { ...prev.outline, subplotIds: newIds } } : prev);
                      }}
                        sx={{ px: 1, py: 0.375, borderRadius: 1, cursor: 'pointer', bgcolor: isSelected ? 'rgba(var(--ws-accent-rgb),0.2)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isSelected ? 'rgba(var(--ws-accent-rgb),0.45)' : 'rgba(255,255,255,0.1)'}`, display: 'flex', alignItems: 'center', gap: 0.375, '&:hover': { borderColor: 'rgba(var(--ws-accent-rgb),0.4)', bgcolor: 'rgba(var(--ws-accent-rgb),0.1)' } }}>
                        <Typography sx={{ fontSize: { xs: 10, sm: 11 }, fontWeight: 600, color: isSelected ? 'var(--ws-accent-muted)' : '#fff' }}>{typeof s.title === 'string' ? s.title : '未命名'}</Typography>
                        <Typography sx={{ fontSize: { xs: 8, sm: 9 }, color: s.status === 'completed' ? '#06b6d4' : '#06b6d4' }}>
                          {s.status === 'completed' ? '完成' : '进行'}
                        </Typography>
                      </Box>
                    );
                  })
                ) : (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: { xs: 11, sm: 12 } }}>本章暂无相关支线</Typography>
                )}
              </Box>
            </Box>

            {/* 涉及词条 */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 16 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.21, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
              sx={{ ...cardBase('rgba(251,191,36,0.32)', 'rgba(245,158,11,0.07)') }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
                  <CategoryIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#fcd34d', flexShrink: 0 }} />
                  <Typography sx={{ color: '#fef3c7', fontSize: { xs: 12, sm: 13 }, fontWeight: 700 }}>涉及词条</Typography>
                </Box>
                <IconButton size="small" onClick={() => setSelectItemDialogOpen(true)}
                  sx={{ color: '#ff6b35', minWidth: 34, minHeight: 34, bgcolor: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.3)', borderRadius: 1.5, '&:hover': { bgcolor: 'rgba(255,107,53,0.2)' } }}>
                  <AddIcon sx={{ fontSize: { xs: 15, sm: 17 } }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {wizardSelectedItems.length === 0 && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: { xs: 11, sm: 12 } }}>暂无涉及词条，点击上方 + 添加</Typography>
                )}
                {wizardSelectedItems.map(itemId => {
                  const itm = items.find(i => i._id === itemId);
                  if (!itm) return null;
                  return (
                    <Box key={itemId} onClick={() => {
                      const newIds = wizardSelectedItems.filter(id => id !== itemId);
                      setWizardSelectedItems(newIds);
                      setSelectedChapterForEdit(prev => prev ? { ...prev, itemIds: newIds } : prev);
                    }}
                      sx={{ px: 1, py: 0.375, borderRadius: 1, cursor: 'pointer', bgcolor: 'rgba(255,107,53,0.15)', border: '1px solid rgba(255,107,53,0.35)', display: 'flex', alignItems: 'center', gap: 0.375, '&:hover': { borderColor: '#ff4444', bgcolor: 'rgba(255,68,68,0.15)' } }}
                      title="点击移除">
                      <Typography sx={{ fontSize: { xs: 10, sm: 11 }, color: '#ff6b35', fontWeight: 600 }}>{itm.name}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* 核心事件与衔接：与电脑端共用 runChaptersPanelAiSmartOutlineGenerate + 自适应多行输入 */}
            <Box
              component={motion.div}
              initial={motionReduced ? false : { opacity: 0, y: 16 }}
              animate={motionReduced ? false : { opacity: 1, y: 0 }}
              transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
              whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
              sx={{ ...cardBase('rgba(52,211,153,0.3)', 'rgba(6,182,212,0.07)'), mb: 2 }}
            >
              <Box sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <BoltIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: '#6ee7b7' }} />
                  <Typography sx={{ color: '#a7f3d0', fontSize: { xs: 12, sm: 13 }, fontWeight: 700 }}>
                    本章核心事件与衔接
                  </Typography>
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: { xs: 10, sm: 11 }, mt: 0.25 }}>
                  描述本章最重要的情节，以及与上章/下章的衔接；点击右上角「生成章纲」自动生成
                </Typography>
              </Box>

              {/* 核心事件：minRows=2 起算，随正文无限增高（Workspace.css 已抵消 MobileEditorFix 的 textarea min-height:200px） */}
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: 10, sm: 11 }, mb: 0.5 }}>核心事件（本章最重要的情节）</Typography>
                <TextField fullWidth multiline minRows={2}
                  value={wizardCoreEvent}
                  onChange={(e) => { const v = e.target.value; setWizardCoreEvent(v); setSelectedChapterForEdit(prev => prev ? { ...prev, outline: { ...prev.outline, coreEvent: v } } : prev); }}
                  placeholder="例如：张三发现李四的秘密，决定当面质问..."
                  sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start', bgcolor: 'rgba(6,182,212,0.05)', maxWidth: '100%', '& fieldset': { borderColor: 'rgba(6,182,212,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(6,182,212,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#06b6d4' } }, '& .MuiInputBase-input': { color: '#fff', fontSize: { xs: 12, sm: 13 }, py: { xs: 1, sm: 1.25 }, lineHeight: 1.5, boxSizing: 'border-box', width: '100%', maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' } }} />
              </Box>

              {/* 承接上文 + 为下章埋钩（移动端纵向叠排） */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography sx={{ color: 'rgba(var(--ws-accent-rgb),0.6)', fontSize: { xs: 10, sm: 11 }, mb: 0.5 }}>← 承接上文</Typography>
                  <TextField fullWidth size="small" multiline minRows={2}
                    value={selectedChapterForEdit?.outline?.connectionToPrevious || ''}
                    onChange={(e) => setSelectedChapterForEdit(prev => prev ? { ...prev, outline: { ...prev.outline, connectionToPrevious: e.target.value } } : prev)}
                    placeholder="本章如何承接上章..."
                    sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start', bgcolor: 'rgba(var(--ws-accent-rgb),0.04)', maxWidth: '100%', '& fieldset': { borderColor: 'rgba(var(--ws-accent-rgb),0.2)' }, '&:hover fieldset': { borderColor: 'rgba(var(--ws-accent-rgb),0.3)' }, '&.Mui-focused fieldset': { borderColor: 'var(--ws-accent)' } }, '& .MuiInputBase-input': { fontSize: { xs: 11, sm: 12 }, color: '#fff', py: { xs: 0.875, sm: 1 }, lineHeight: 1.5, boxSizing: 'border-box', width: '100%', maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' } }} />
                </Box>
                <Box>
                  <Typography sx={{ color: 'rgba(255,215,0,0.6)', fontSize: { xs: 10, sm: 11 }, mb: 0.5 }}>→ 为下章埋钩</Typography>
                  <TextField fullWidth size="small" multiline minRows={2}
                    value={selectedChapterForEdit?.outline?.hookForNext || ''}
                    onChange={(e) => setSelectedChapterForEdit(prev => prev ? { ...prev, outline: { ...prev.outline, hookForNext: e.target.value } } : prev)}
                    placeholder="为下章埋下什么悬念..."
                    sx={{ '& .MuiOutlinedInput-root': { alignItems: 'flex-start', bgcolor: 'rgba(255,215,0,0.04)', maxWidth: '100%', '& fieldset': { borderColor: 'rgba(255,215,0,0.2)' }, '&:hover fieldset': { borderColor: 'rgba(255,215,0,0.3)' }, '&.Mui-focused fieldset': { borderColor: '#ffd700' } }, '& .MuiInputBase-input': { fontSize: { xs: 11, sm: 12 }, color: '#fff', py: { xs: 0.875, sm: 1 }, lineHeight: 1.5, boxSizing: 'border-box', width: '100%', maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'break-word' } }} />
                </Box>
              </Box>
            </Box>

            {/* AI 辅助生成正文：与电脑端同一 state + runChaptersPanelAiGenerateBody */}
            <Box sx={{ mb: 2, p: { xs: 1.25, sm: 1.5 }, bgcolor: 'rgba(56,189,248,0.06)', border: '1px solid rgba(125,211,252,0.28)', borderRadius: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#e0f2fe', fontSize: { xs: 11, sm: 12 }, fontWeight: 600 }}>
                  AI 辅助生成正文
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: { xs: 9, sm: 10 }, width: '100%', pl: { xs: 0, sm: 0 } }}>
                  基于上方核心事件与章纲生成正文，逻辑与电脑端一致
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: { xs: 10, sm: 11 } }}>目标字数：</Typography>
                  <TextField
                    size="small"
                    type="number"
                    value={chapterWordCount}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val > 0) {
                        setChapterWordCount(val);
                      } else if (e.target.value === '') {
                        setChapterWordCount('');
                      }
                    }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>字</InputAdornment>,
                      inputProps: { min: 100, max: 50000, step: 100 },
                    }}
                    sx={{
                      width: { xs: '100%', sm: 110 },
                      '& .MuiInputBase-input': {
                        color: '#e0f2fe', fontSize: { xs: 12, sm: 12 }, py: 0.5, textAlign: 'center',
                        '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
                      },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(125,211,252,0.38)' },
                        '&:hover fieldset': { borderColor: 'rgba(186,230,253,0.55)' },
                        '&.Mui-focused fieldset': { borderColor: '#bae6fd' },
                      },
                    }}
                  />
                </Box>
              </Box>

              <Button
                fullWidth
                variant="outlined"
                color="inherit"
                size="small"
                className="assist-chapter-ai-glow-btn"
                onClick={() => runChaptersPanelAiGenerateBody()}
                disabled={bodyGenerating}
                startIcon={bodyGenerating ? <CircularProgress size={12} sx={{ color: '#bae6fd' }} /> : undefined}
                sx={{
                  fontSize: { xs: 12, sm: 12 },
                        mb: (bodyGenerating || (selectedChapterForEdit?.outline?.generatedContent || '').length > 0) ? 1.5 : 0,
                  borderRadius: 999,
                  py: 0.85,
                  '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.14)' },
                }}
              >
                {bodyGenerating ? '生成中…' : 'AI 生成正文'}
              </Button>

              {(bodyGenerating || (selectedChapterForEdit?.outline?.generatedContent || '').length > 0) && (
                <Box ref={chapterBodyStreamSectionRef}>
                  {bodyGenerating && (
                    <>
                      <Box
                        className="assist-chapter-body-stream-status"
                        sx={{ mb: 1, height: 3, borderRadius: 999, bgcolor: 'rgba(56,189,248,0.06)' }}
                      >
                        <Box component="span" className="assist-chapter-body-stream-status-inner" />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: { xs: 9, sm: 10 } }}>
                          {chapterBodyStreamFollowOutput ? '正在跟随最新生成位置' : '已暂停跟随 · 滚回正文最底部可恢复'}
                        </Typography>
                      </Box>
                    </>
                  )}
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: 9, sm: 10 }, mb: 0.5 }}>
                    {bodyGenerating
                      ? '流式生成中 · 已自动写入章纲'
                      : '生成内容'}
                    {`（${(selectedChapterForEdit?.outline?.generatedContent || '').length} 字）`}
                  </Typography>
                  <Box
                    className={`assist-chapter-body-stream-panel${bodyGenerating ? ' is-streaming' : ''}`}
                    sx={{ mb: 1, borderRadius: 1, overflow: 'hidden' }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      maxRows={24}
                      inputRef={chapterBodyStreamTextareaRef}
                      value={selectedChapterForEdit?.outline?.generatedContent || ''}
                      onChange={(e) => {
                        if (bodyGenerating) return;
                        const v = e.target.value;
                        setSelectedChapterForEdit(prev => (prev ? { ...prev, outline: { ...prev.outline, generatedContent: v } } : prev));
                      }}
                      onBlur={() => {
                        if (bodyGenerating) return;
                        const ch = selectedChapterForEditRef.current;
                        if (ch?._id) autoSaveOutlineFields({ ...ch, outline: { ...ch.outline } });
                      }}
                      placeholder={bodyGenerating ? '等待模型输出…' : ''}
                      inputProps={{ onScroll: handleChapterBodyStreamTextareaScroll }}
                      InputProps={{ readOnly: bodyGenerating }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          alignItems: 'flex-start',
                          bgcolor: 'rgba(0,0,0,0.35)',
                          '& fieldset': { borderColor: 'rgba(125,211,252,0.28)' },
                        },
                        '& .MuiInputBase-input': {
                          color: 'rgba(255,255,255,0.9)',
                          fontSize: { xs: 13, sm: 14 },
                          lineHeight: 1.8,
                          fontFamily: '"SimSun", "宋体", "Noto Serif SC", serif',
                          py: 1.25,
                        },
                      }}
                    />
                  </Box>
                  {!bodyGenerating && (
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        className="assist-chapter-ai-glow-btn"
                        onClick={async () => {
                          const gen = selectedChapterForEdit.outline.generatedContent || '';
                          const fullContent = (selectedChapterForEdit.content || '') + '\n\n' + gen;
                          const listTitle = sanitizeChapterTitleForList((selectedChapterForEdit.outline.generatedTitle || '').trim());
                          setChapterContent(prev => {
                            const sep = prev.trim() ? '\n\n' : '';
                            return prev + sep + gen;
                          });
                          const nextOutline = { ...selectedChapterForEdit.outline, generatedContent: '' };
                          delete nextOutline.generatedTitle;
                          const updatedChapter = {
                            ...selectedChapterForEdit,
                            content: (selectedChapterForEdit.content || '') + '\n\n' + gen,
                            outline: nextOutline,
                            ...(listTitle ? { title: listTitle } : {}),
                          };
                          setSelectedChapterForEdit(updatedChapter);
                          setCurrentChapter(cur => (cur && cur._id === updatedChapter._id
                            ? { ...cur, content: updatedChapter.content, ...(listTitle ? { title: listTitle } : {}) }
                            : cur));
                          setChapters(prev => prev.map(c => (c._id === updatedChapter._id
                            ? { ...c, content: updatedChapter.content, ...(listTitle ? { title: listTitle } : {}) }
                            : c)));
                          if (listTitle && assistNovelId) {
                            try {
                              await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${updatedChapter._id}`, { title: listTitle });
                            } catch (e) {
                              console.warn('[插入正文] 标题保存失败:', e);
                            }
                          }
                          // 保存正文到数据库
                          if (assistNovelId) {
                            try {
                              await apiClient.post(`/assist/novels/${assistNovelId}/chapters`, { ...updatedChapter });
                            } catch (e) {
                              console.warn('[插入正文] 正文保存失败:', e);
                            }
                          }
                          console.log(`[全局进度·插入正文] 第${updatedChapter.chapterNumber}章触发自动跟进分析...`);
                          await autoSyncAfterChapterGenerate(fullContent, updatedChapter);
                          console.log(`[全局进度·插入正文] 第${updatedChapter.chapterNumber}章自动跟进完成`);
                          setChapterOutlineSavedTime(new Date());
                          void autoSaveOutlineFields(updatedChapter);
                        }}
                        sx={{ fontSize: 11, borderRadius: 999, textTransform: 'none', py: 0.5, '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.14)' } }}
                      >
                        插入正文
                      </Button>
                      <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        className="assist-chapter-ai-glow-btn assist-chapter-clear-subtle"
                        onClick={() => setSelectedChapterForEdit(prev => ({
                          ...prev,
                          outline: { ...prev.outline, generatedContent: '', generatedTitle: '' },
                        }))}
                        sx={{ fontSize: 11, borderRadius: 999, textTransform: 'none', py: 0.5, '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.1)' } }}
                      >
                        清除
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      );
    };

    // ================================================================
    // PC 端双栏布局（md 及以上）
    // ================================================================
    // 移动端 xs/sm：单栏切换视图；PC md+：双栏视图
    // ================================================================
    if (isChaptersNarrow) {
      if (selectedChapterForEdit) {
        return (
          <Box className="workspace-mobile-shell">
            {renderMobileEditView()}
          </Box>
        );
      }
      return (
        <Box className="workspace-mobile-shell">
          {renderMobileListView()}
        </Box>
      );
    }

    return (
      <Box className="character-panel-cyber" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* ── 标题栏 ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: { xs: 1.5, sm: 2 }, borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
          <Typography sx={{ color: '#00ff9f', fontWeight: 700, fontSize: { xs: 15, sm: 17 }, textShadow: '0 0 16px rgba(0,255,159,0.3)', flex: 1 }}>
            章纲·章节创作台
          </Typography>
          <Tooltip title="新建卷">
            <IconButton size="small" onClick={handleAddVolume}
              sx={{ color: '#7dd3fc', '&:hover': { bgcolor: 'rgba(56,189,248,0.14)', color: '#bae6fd' } }}>
              <CreateNewFolderIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── 搜索栏 ── */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
          <TextField
            fullWidth size="small" placeholder="搜索章节..."
            value={chapterSearch}
            onChange={(e) => setChapterSearch(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.04)', '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' } } }}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)', mr: 0.5, fontSize: 16 }} /> }}
          />
        </Box>

        {/* ── 分栏布局（PC） ── */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>

          {/* ── 左栏：卷+章列表 ── */}
          <Box sx={{
            width: { xs: '100%', md: selectedChapterForEdit ? 300 : 320 },
            flexShrink: 0,
            overflow: 'auto',
            borderRight: '1px solid rgba(255,255,255,0.07)',
            transition: 'width 0.3s ease',
            display: { xs: selectedChapterForEdit ? 'none' : 'block', md: 'block' },
          }}>
            {/* 章节列表 */}
            {volumes.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <SummarizeIcon sx={{ fontSize: 52, mb: 2, opacity: 0.2, color: '#00ff9f' }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, mb: 0.5 }}>还没有卷和章节</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, mb: 2 }}>点击右上角 + 创建第一个卷</Typography>
                <Button size="small" onClick={handleAddVolume}
                  sx={{ color: '#7dd3fc', border: '1px solid rgba(125,211,252,0.35)', fontSize: 11, '&:hover': { bgcolor: 'rgba(56,189,248,0.08)' } }}>
                  + 新建卷
                </Button>
              </Box>
            ) : (
              <>
                {volumesSortedForAssist.map((volume, volIdx) => renderVolumeAccordion(volume, volIdx))}
                {(() => {
                  const uncl = getUnclassifiedChapters();
                  return uncl.length > 0 ? (
                    <Box sx={{ mt: 1, px: 1 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, mb: 0.5, px: 1 }}>未归类章节</Typography>
                      {uncl.map(ch => renderChapterItem(ch))}
                    </Box>
                  ) : null;
                })()}
              </>
            )}
          </Box>

          {/* ── 右栏：章节创作区（PC） ── */}
          {selectedChapterForEdit && (
            <Box sx={{ flex: 1, display: { xs: 'none', md: 'flex' }, flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

              {/* 创作区头部 */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.5, borderBottom: '1px solid rgba(0,255,159,0.15)', flexShrink: 0 }}>
                <IconButton size="small" onClick={() => setSelectedChapterForEdit(null)}
                  sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {(() => {
                    const n = selectedChapterForEdit.chapterNumber;
                    const raw = (selectedChapterForEdit.title || '').trim();
                    const defaultTitle = `第${n}章`;
                    const showSubtitle = raw && raw !== defaultTitle;
                    return (
                      <>
                        <Typography sx={{ color: '#00ff9f', fontWeight: 700, fontSize: { xs: 14, sm: 16 }, textShadow: '0 0 12px rgba(0,255,159,0.3)' }}>
                          {showSubtitle ? defaultTitle : (raw || defaultTitle)}
                        </Typography>
                        {showSubtitle && (
                          <Typography sx={{ color: '#fff', fontSize: { xs: 13, sm: 14 }, fontWeight: 600, mt: 0.25 }}>
                            {raw}
                          </Typography>
                        )}
                      </>
                    );
                  })()}
                </Box>
                <Tooltip title="跳转到主编辑器写正文">
                  <IconButton size="small"
                    onClick={() => {
                      if (selectedChapterForEdit) {
                        setCurrentChapter(selectedChapterForEdit);
                        setChapterContent(selectedChapterForEdit.content || '');
                        setActivePanel(null);
                      }
                    }}
                    sx={{ color: '#00f0ff', '&:hover': { bgcolor: 'rgba(0,240,255,0.15)' } }}>
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* 手动保存按钮 */}
                <Tooltip title="保存章纲">
                  <IconButton size="small"
                    onClick={() => {
                      if (selectedChapterForEdit) {
                        const ch = selectedChapterForEdit;
                        persistMergedWizardOutlineForChapter(ch);
                      }
                    }}
                    sx={{ color: 'var(--ws-accent)', '&:hover': { bgcolor: 'rgba(var(--ws-accent-rgb),0.15)' } }}>
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* 保存时间显示 */}
                {chapterOutlineSavedTime && (
                  <Typography sx={{ fontSize: 10, color: 'rgba(var(--ws-accent-rgb),0.6)', whiteSpace: 'nowrap', ml: 0.5 }}>
                    已保存 {formatTimeDisplay(chapterOutlineSavedTime)}
                  </Typography>
                )}
              </Box>

              {/* 创作区主体（可滚动） */}
              <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2 } }}>

                {/* ══════════════════════════════════════════════════════ */}
                {/* 章纲向导流程 */}
                {/* ══════════════════════════════════════════════════════ */}
                
                {/* 章纲向导容器 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                {/* 向导头部 - 与天青卷列表同系 */}
                <Box
                  component={motion.div}
                  initial={motionReduced ? false : { opacity: 0, y: 12 }}
                  animate={motionReduced ? false : { opacity: 1, y: 0 }}
                  transition={motionReduced ? { duration: 0 } : { duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={motionReduced ? undefined : { boxShadow: '0 8px 28px rgba(56,189,248,0.1)', transition: { duration: 0.25 } }}
                  sx={{
                    mb: 0,
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(125,211,252,0.35)',
                    background: 'linear-gradient(145deg, rgba(56,189,248,0.1) 0%, rgba(8,14,24,0.92) 55%, rgba(10,14,22,0.88) 100%)',
                    boxShadow: '0 4px 22px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.04)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BookIcon sx={{ fontSize: 20, color: '#7dd3fc', filter: 'drop-shadow(0 0 10px rgba(125,211,252,0.35))' }} />
                      <Typography sx={{ color: '#e0f2fe', fontSize: 16, fontWeight: 600 }}>
                        第{selectedChapterForEdit?.chapterNumber}章 · 智能章纲编辑
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={!selectedChapterForEdit ? '请先选择一个章节' : globalProgressSyncing ? '全局进度正在更新中，请稍候...' : outlineGenerating ? '正在生成章纲中...' : 'AI 辅助生成章纲（生成后自动保存）'}>
                        <span>
                          <Button
                            size="small"
                            className="assist-chapter-ai-glow-btn"
                            disabled={outlineGenerating || globalProgressSyncing || !selectedChapterForEdit}
                            onClick={() => runChaptersPanelAiSmartOutlineGenerate()}
                            sx={{ px: 1.5, py: 0.65, fontSize: 11, fontWeight: 700 }}
                          >
                            {outlineGenerating ? <CircularProgress size={12} sx={{ color: '#bae6fd' }} /> : (globalProgressSyncing ? <CircularProgress size={12} sx={{ color: '#38bdf8' }} /> : '生成章纲')}
                          </Button>
                        </span>
                      </Tooltip>
                      {globalProgressSyncing && (
                        <Typography sx={{ color: '#38bdf8', fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          同步中
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.75 }}>
                    <Chip
                      label="智能模式"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(56,189,248,0.14)',
                        color: '#bae6fd',
                        fontSize: 9,
                        border: '1px solid rgba(125,211,252,0.35)',
                      }}
                    />
                    <Chip
                      label="手动修改后需点击保存"
                      size="small"
                      icon={<SaveIcon sx={{ fontSize: 11 }} />}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.06)',
                        color: 'rgba(226,232,240,0.9)',
                        fontSize: 9,
                        border: '1px solid rgba(255,255,255,0.12)',
                        '& .MuiChip-icon': { color: '#94a3b8', ml: 0.5 },
                      }}
                    />
                  </Box>
                  <Typography sx={{ color: 'rgba(226,232,240,0.48)', fontSize: 11 }}>
                    系统已根据章节号自动匹配相关伏笔和支线，您可以直接修改或确认
                  </Typography>
                </Box>

                {/* ══════════════════════════════════════════════════════ */}
                {/* 智能章纲编辑界面（单页面） */}
                {/* ══════════════════════════════════════════════════════ */}
                {(() => {
                  const currentChNum = selectedChapterForEdit?.chapterNumber || 0;
                  
                  // 自动计算本章应关联的伏笔（根据起始章和揭露章自动判断）
                  const autoRelatedForeshadowing = (outlines[0]?.foreshadowing || []).filter(f => {
                    if (f.status === 'revealed') return false;
                    if (f.plantedChapterNumber) {
                      if (f.revealChapterNumber) {
                        return currentChNum >= f.plantedChapterNumber && currentChNum <= f.revealChapterNumber;
                      }
                      return currentChNum >= f.plantedChapterNumber;
                    }
                    if (!f.plantedChapterId) return false;
                    const plantedChapter = chapters.find(c => c._id === f.plantedChapterId);
                    if (!plantedChapter) return false;
                    const pNum = plantedChapter.chapterNumber;
                    if (f.revealChapterId) {
                      const revealChapter = chapters.find(c => c._id === f.revealChapterId);
                      if (revealChapter) {
                        return currentChNum >= pNum && currentChNum <= revealChapter.chapterNumber;
                      }
                    }
                    return currentChNum >= pNum;
                  }).map(f => f.id || f._id);

                  // 自动计算本章应关联的支线（根据章节范围自动判断）
                  const autoRelatedSubplots = (outlines[0]?.subPlots || []).filter(s => {
                    if (s.startChapterNumber && s.endChapterNumber) {
                      return currentChNum >= s.startChapterNumber && currentChNum <= s.endChapterNumber;
                    }
                    return false;
                  }).map(s => s.id || s._id);

                  return (
                    <Box>
                      {/* ═══════════════════════════════════════════════════════════ */}
                      {/* 四大关联卡片 — 每行一个，独立卡片式布局 */}
                      {/* ═══════════════════════════════════════════════════════════ */}

                      {/* 本章出场角色 */}
                      <Box
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={motionReduced ? false : { opacity: 1, y: 0 }}
                        transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.06, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(56,189,248,0.06)',
                          border: '1px solid rgba(125,211,252,0.32)',
                          borderRadius: 2,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PersonIcon sx={{ fontSize: 22, color: '#7dd3fc' }} />
                            <Typography sx={{ color: '#e0f2fe', fontSize: 13, fontWeight: 700 }}>
                              本章出场角色
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="添加已有角色">
                              <IconButton size="small" onClick={() => setSelectCharDialogOpen(true)} sx={{ color: '#7dd3fc', '&:hover': { bgcolor: 'rgba(56,189,248,0.15)' } }}>
                                <AddIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            {/* AI推荐角色按钮已移除：章纲生成时已自动推荐角色 */}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {wizardSelectedChars.length === 0 && (
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>点击「生成章纲」将自动推荐本章角色</Typography>
                          )}
                          {wizardSelectedChars.map(charId => {
                            const c = characters.find(ch => ch._id === charId);
                            if (!c) return null;
                            const roleLb = getCharacterDisplayRoleLabel(c);
                            const rv = getCharacterRoleVisual(roleLb);
                            return (
                              <Tooltip key={c._id} title={buildCharacterChapterOutlineTooltip(c)} {...chapterOutlineCharTooltipProps}>
                                <Box
                                  component="span"
                                  onClick={() => {
                                    const newIds = wizardSelectedChars.includes(c._id) ? wizardSelectedChars.filter(id => id !== c._id) : [...wizardSelectedChars, c._id];
                                    setWizardSelectedChars(newIds);
                                    setSelectedChapterForEdit(prev => prev ? { ...prev, characterIds: newIds, outline: { ...prev.outline, plotPoints: (prev.outline?.plotPoints || []).map((pt, idx) => idx === 0 ? { ...pt, characterIds: newIds } : pt) } } : prev);
                                  }}
                                  sx={{
                                    px: 1,
                                    py: 0.45,
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    bgcolor: 'rgba(56,189,248,0.1)',
                                    border: '1px solid rgba(125,211,252,0.32)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.45,
                                    maxWidth: 280,
                                    '&:hover': { borderColor: 'rgba(186,230,253,0.65)', bgcolor: 'rgba(56,189,248,0.16)' },
                                  }}
                                >
                                  <Typography component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.4, py: 0.1, borderRadius: 0.5, lineHeight: 1.2, flexShrink: 0, bgcolor: rv.bg, color: rv.color, border: rv.border }}>
                                    {roleLb}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{getCharacterChapterOutlineDisplayName(c)}</Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* 关联伏笔 */}
                      <Box
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={motionReduced ? false : { opacity: 1, y: 0 }}
                        transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.11, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(167,139,250,0.06)',
                          border: '1px solid rgba(196,181,253,0.28)',
                          borderRadius: 2,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <HubIcon sx={{ fontSize: 22, color: '#c4b5fd' }} />
                            <Typography sx={{ color: '#ede9fe', fontSize: 13, fontWeight: 700 }}>关联伏笔</Typography>
                          </Box>
                          {autoRelatedForeshadowing.length > 0 && (
                            <Chip label={`${autoRelatedForeshadowing.length}个相关`} size="small" sx={{ bgcolor: 'rgba(167,139,250,0.14)', color: '#ddd6fe', fontSize: 9, border: '1px solid rgba(196,181,253,0.35)', height: 20 }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {(outlines[0]?.foreshadowing || []).filter(f => autoRelatedForeshadowing.includes(f.id || f._id)).length > 0 ? (
                            (outlines[0].foreshadowing || []).filter(f => autoRelatedForeshadowing.includes(f.id || f._id)).map(f => {
                              const fid = f.id || f._id;
                              const isSelected = wizardSelectedForeshadowing.includes(fid);
                              return (
                                <Box key={fid} onClick={() => {
                                  const newIds = wizardSelectedForeshadowing.includes(fid) ? wizardSelectedForeshadowing.filter(id => id !== fid) : [...wizardSelectedForeshadowing, fid];
                                  setWizardSelectedForeshadowing(newIds);
                                  setSelectedChapterForEdit(prev => prev ? { ...prev, foreshadowingIds: newIds, outline: { ...prev.outline, plotPoints: (prev.outline?.plotPoints || []).map((pt, idx) => idx === 0 ? { ...pt, foreshadowingIds: newIds } : pt) } } : prev);
                                }}
                                  sx={{ px: 1.25, py: 0.625, borderRadius: 1, cursor: 'pointer', bgcolor: isSelected ? 'rgba(167,139,250,0.18)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isSelected ? 'rgba(196,181,253,0.45)' : 'rgba(255,255,255,0.1)'}`, display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { borderColor: 'rgba(196,181,253,0.4)', bgcolor: 'rgba(167,139,250,0.1)' } }}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#ddd6fe' : '#fff' }}>{f.title || '未命名'}</Typography>
                                  <Typography sx={{ fontSize: 9, color: f.status === 'revealed' ? '#06b6d4' : f.status === 'advancing' ? '#06b6d4' : f.status === 'planted' ? '#ffd700' : 'rgba(255,255,255,0.4)' }}>
                                    {f.status === 'revealed' ? '已揭露' : f.status === 'advancing' ? '推进中' : f.status === 'planted' ? '已铺垫' : '待铺垫'}
                                  </Typography>
                                </Box>
                              );
                            })
                          ) : (
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>本章暂无相关伏笔</Typography>
                          )}
                        </Box>
                      </Box>

                      {/* 关联支线 */}
                      <Box
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={motionReduced ? false : { opacity: 1, y: 0 }}
                        transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(45,212,191,0.06)',
                          border: '1px solid rgba(94,234,212,0.28)',
                          borderRadius: 2,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <AccountTreeIcon sx={{ fontSize: 22, color: '#5eead4' }} />
                            <Typography sx={{ color: '#ccfbf1', fontSize: 13, fontWeight: 700 }}>关联支线</Typography>
                          </Box>
                          {autoRelatedSubplots.length > 0 && (
                            <Chip label={`${autoRelatedSubplots.length}个相关`} size="small" sx={{ bgcolor: 'rgba(45,212,191,0.12)', color: '#99f6e4', fontSize: 9, border: '1px solid rgba(94,234,212,0.32)', height: 20 }} />
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {(outlines[0]?.subPlots || []).filter(s => autoRelatedSubplots.includes(s.id || s._id)).length > 0 ? (
                            (outlines[0].subPlots || []).filter(s => autoRelatedSubplots.includes(s.id || s._id)).map(s => {
                              const sid = s.id || s._id;
                              const isSelected = wizardSelectedSubplots.includes(sid);
                              return (
                                <Box key={sid} onClick={() => {
                                  const newIds = wizardSelectedSubplots.includes(sid) ? wizardSelectedSubplots.filter(id => id !== sid) : [...wizardSelectedSubplots, sid];
                                  setWizardSelectedSubplots(newIds);
                                  setSelectedChapterForEdit(prev => prev ? { ...prev, outline: { ...prev.outline, subplotIds: newIds } } : prev);
                                }}
                                  sx={{ px: 1.25, py: 0.625, borderRadius: 1, cursor: 'pointer', bgcolor: isSelected ? 'rgba(45,212,191,0.16)' : 'rgba(255,255,255,0.04)', border: `1px solid ${isSelected ? 'rgba(94,234,212,0.42)' : 'rgba(255,255,255,0.1)'}`, display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { borderColor: 'rgba(94,234,212,0.38)', bgcolor: 'rgba(45,212,191,0.08)' } }}>
                                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: isSelected ? '#99f6e4' : '#fff' }}>{typeof s.title === 'string' ? s.title : '未命名'}</Typography>
                                  <Typography sx={{ fontSize: 9, color: s.status === 'completed' ? '#06b6d4' : s.status === 'active' ? '#06b6d4' : 'rgba(255,255,255,0.4)' }}>
                                    {s.status === 'completed' ? '已完成' : s.status === 'active' ? '进行中' : '待处理'}
                                  </Typography>
                                </Box>
                              );
                            })
                          ) : (
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>本章暂无相关支线</Typography>
                          )}
                        </Box>
                      </Box>

                      {/* 涉及词条 */}
                      <Box
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={motionReduced ? false : { opacity: 1, y: 0 }}
                        transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.21, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(251,191,36,0.06)',
                          border: '1px solid rgba(252,211,77,0.28)',
                          borderRadius: 2,
                          mb: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CategoryIcon sx={{ fontSize: 22, color: '#fcd34d' }} />
                            <Typography sx={{ color: '#fef3c7', fontSize: 13, fontWeight: 700 }}>涉及词条</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="添加已有词条">
                              <IconButton size="small" onClick={() => setSelectItemDialogOpen(true)} sx={{ color: '#fcd34d', '&:hover': { bgcolor: 'rgba(251,191,36,0.14)' } }}>
                                <AddIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {wizardSelectedItems.length === 0 && (
                            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 11 }}>暂无涉及词条，点击上方 + 添加</Typography>
                          )}
                          {wizardSelectedItems.map(itemId => {
                            const itm = items.find(i => i._id === itemId);
                            if (!itm) return null;
                            return (
                              <Box key={itemId} onClick={() => {
                                const newIds = wizardSelectedItems.filter(id => id !== itemId);
                                setWizardSelectedItems(newIds);
                                setSelectedChapterForEdit(prev => prev ? { ...prev, itemIds: newIds } : prev);
                              }}
                                sx={{ px: 1.25, py: 0.5, borderRadius: 1, cursor: 'pointer', bgcolor: 'rgba(251,191,36,0.12)', border: '1px solid rgba(252,211,77,0.38)', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { borderColor: '#fbbf24', bgcolor: 'rgba(251,191,36,0.18)' } }}
                                title="点击移除">
                                <Typography sx={{ fontSize: 11, color: '#fde68a', fontWeight: 600 }}>{itm.name}</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>

                      {/* 核心事件 + 承接上文/埋钩 */}
                      <Box
                        component={motion.div}
                        initial={motionReduced ? false : { opacity: 0, y: 14 }}
                        animate={motionReduced ? false : { opacity: 1, y: 0 }}
                        transition={motionReduced ? { duration: 0 } : { duration: 0.4, delay: 0.26, ease: [0.22, 1, 0.36, 1] }}
                        whileHover={motionReduced ? undefined : { y: -2, transition: { duration: 0.2 } }}
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(52,211,153,0.06)',
                          border: '1px solid rgba(110,231,183,0.28)',
                          borderRadius: 2,
                          mb: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <BoltIcon sx={{ fontSize: 22, color: '#6ee7b7', mt: 0.15 }} />
                            <Box>
                              <Typography sx={{ color: '#d1fae5', fontSize: 12, fontWeight: 600 }}>
                                本章核心事件与衔接
                              </Typography>
                              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mt: 0.25 }}>
                                描述本章最重要的情节，以及与上章/下章的衔接
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                        {/* 生成章纲按钮已移至右上角智能模式区域 */}
                        
                        {/* 核心事件 */}
                        <Box sx={{ mb: 1.5 }}>
                          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, mb: 0.5 }}>核心事件（本章最重要的情节）</Typography>
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            value={wizardCoreEvent}
                            onChange={(e) => {
                              const v = e.target.value;
                              setWizardCoreEvent(v);
                              setSelectedChapterForEdit(prev => (prev ? { ...prev, outline: { ...prev.outline, coreEvent: v } } : prev));
                            }}
                            placeholder="例如：张三发现李四的秘密，决定当面质问..."
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(52,211,153,0.05)',
                                maxWidth: '100%',
                                '& fieldset': { borderColor: 'rgba(110,231,183,0.22)' },
                                '&:hover fieldset': { borderColor: 'rgba(110,231,183,0.35)' },
                                '&.Mui-focused fieldset': { borderColor: '#6ee7b7' },
                              },
                              '& .MuiInputBase-input': {
                                color: '#fff',
                                fontSize: 12,
                                width: '100%',
                                maxWidth: '100%',
                                boxSizing: 'border-box',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                              },
                            }}
                          />
                        </Box>
                        
                        {/* 承接上文 + 埋钩：≥sm 双列，各列高度随本列文本行数增高 */}
                        <Box className="chapter-ai-columns-grid" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, alignItems: 'start' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Typography sx={{ color: 'rgba(var(--ws-accent-rgb),0.65)', fontSize: 10, mb: 0.5, flexShrink: 0 }}>← 承接上文</Typography>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              value={selectedChapterForEdit?.outline?.connectionToPrevious || ''}
                              onChange={(e) => setSelectedChapterForEdit(prev => (prev ? { ...prev, outline: { ...prev.outline, connectionToPrevious: e.target.value } } : prev))}
                              placeholder="本章如何承接上章..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'rgba(var(--ws-accent-rgb),0.04)',
                                  maxWidth: '100%',
                                  '& fieldset': { borderColor: 'rgba(var(--ws-accent-rgb),0.2)' },
                                },
                                '& textarea.MuiInputBase-input': {
                                  fontSize: 11,
                                  color: '#fff',
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            <Typography sx={{ color: 'rgba(255,215,0,0.65)', fontSize: 10, mb: 0.5, flexShrink: 0 }}>→ 为下章埋钩</Typography>
                            <TextField
                              fullWidth
                              multiline
                              minRows={2}
                              value={selectedChapterForEdit?.outline?.hookForNext || ''}
                              onChange={(e) => setSelectedChapterForEdit(prev => (prev ? { ...prev, outline: { ...prev.outline, hookForNext: e.target.value } } : prev))}
                              placeholder="为下章埋下什么悬念..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  bgcolor: 'rgba(255,215,0,0.04)',
                                  maxWidth: '100%',
                                  '& fieldset': { borderColor: 'rgba(255,215,0,0.2)' },
                                },
                                '& textarea.MuiInputBase-input': {
                                  fontSize: 11,
                                  color: '#fff',
                                  width: '100%',
                                  maxWidth: '100%',
                                  boxSizing: 'border-box',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word',
                                },
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })()}

                {/* 向导容器结束 */}
                </Box>

                {/* 核心事件 / 承接与埋钩 在上方智能章纲区编辑；点 智能生成后会合并向导选项并写入数据库 */}

                {/* ── AI 生成正文区 ── */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(56,189,248,0.06)', border: '1px solid rgba(125,211,252,0.28)', borderRadius: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                    <Typography sx={{ color: '#e0f2fe', fontSize: 11, fontWeight: 600 }}>
                      AI 辅助生成正文
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                      基于核心事件与章纲（上方智能章纲区）生成完整正文
                    </Typography>
                  </Box>

                  {/* 字数设置输入框 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
                      目标字数：
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={chapterWordCount}
                      onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        if (!isNaN(val) && val > 0) {
                          setChapterWordCount(val);
                        } else if (e.target.value === '') {
                          setChapterWordCount('');
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>字</InputAdornment>,
                        inputProps: { min: 100, max: 50000, step: 100 }
                      }}
                      sx={{
                        width: 110,
                        '& .MuiInputBase-input': {
                          color: '#e0f2fe', fontSize: 12, py: 0.5, textAlign: 'center',
                          '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { WebkitAppearance: 'none' }
                        },
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: 'rgba(125,211,252,0.38)' },
                          '&:hover fieldset': { borderColor: 'rgba(186,230,253,0.55)' },
                          '&.Mui-focused fieldset': { borderColor: '#bae6fd' }
                        }
                      }}
                    />
                    <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 10 }}>
                      章节生成的目标字数，将作为AI写作的字数参考
                    </Typography>
                    {/* 全局进度同步状态指示器 */}
                    {globalProgressSyncing && (
                      <Tooltip title="全局进度正在同步中，请稍候...">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CircularProgress size={12} sx={{ color: '#38bdf8' }} />
                          <Typography sx={{ color: '#38bdf8', fontSize: 10 }}>同步中</Typography>
                        </Box>
                      </Tooltip>
                    )}
                  </Box>

                  {/* AI 生成按钮 */}
                  <Button
                    fullWidth variant="outlined" color="inherit" size="small"
                    className="assist-chapter-ai-glow-btn"
                    onClick={() => { void runChaptersPanelAiGenerateBody(); }}
                    disabled={bodyGenerating}
                    sx={{
                      fontSize: 12,
                      mb: (bodyGenerating || (selectedChapterForEdit?.outline?.generatedContent || '').length > 0) ? 1.5 : 0,
                      borderRadius: 999,
                      py: 0.85,
                      '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.14)' },
                    }}
                  >
                    {bodyGenerating ? '生成中…' : 'AI 生成正文'}
                  </Button>

                  {/* 生成结果预览：流式生成时同步展示并自动保存章纲 */}
                  {(bodyGenerating || (selectedChapterForEdit?.outline?.generatedContent || '').length > 0) && (
                    <Box ref={chapterBodyStreamSectionRef}>
                      {bodyGenerating && (
                        <>
                          <Box
                            className="assist-chapter-body-stream-status"
                            sx={{ mb: 1, height: 3, borderRadius: 999, bgcolor: 'rgba(56,189,248,0.06)' }}
                          >
                            <Box component="span" className="assist-chapter-body-stream-status-inner" />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.38)', fontSize: 10 }}>
                              {chapterBodyStreamFollowOutput ? '正在跟随最新生成位置' : '已暂停跟随 · 滚回正文最底部可恢复'}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, mb: 0.5 }}>
                        {bodyGenerating ? '流式生成中 · 已自动写入章纲' : '生成内容'}
                        {`（${(selectedChapterForEdit?.outline?.generatedContent || '').length} 字）`}
                      </Typography>
                      <Box
                    className={`assist-chapter-body-stream-panel${bodyGenerating ? ' is-streaming' : ''}`}
                    sx={{ mb: 1, borderRadius: 1, overflow: 'hidden' }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      minRows={6}
                      maxRows={20}
                      inputRef={chapterBodyStreamTextareaRef}
                      value={selectedChapterForEdit?.outline?.generatedContent || ''}
                      onChange={(e) => {
                        if (bodyGenerating) return;
                        const v = e.target.value;
                        setSelectedChapterForEdit(prev => (prev ? { ...prev, outline: { ...prev.outline, generatedContent: v } } : prev));
                      }}
                      onBlur={() => {
                        if (bodyGenerating) return;
                            const ch = selectedChapterForEditRef.current;
                            if (ch?._id) autoSaveOutlineFields({ ...ch, outline: { ...ch.outline } });
                          }}
                          placeholder={bodyGenerating ? '等待模型输出…' : ''}
                          inputProps={{ onScroll: handleChapterBodyStreamTextareaScroll }}
                          InputProps={{ readOnly: bodyGenerating }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              alignItems: 'flex-start',
                              bgcolor: 'rgba(0,0,0,0.35)',
                              '& fieldset': { borderColor: 'rgba(125,211,252,0.28)' },
                            },
                            '& .MuiInputBase-input': {
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: 13,
                              lineHeight: 1.8,
                              fontFamily: '"SimSun", "宋体", "Noto Serif SC", serif',
                              py: 1.25,
                            },
                          }}
                        />
                      </Box>
                      {!bodyGenerating && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            className="assist-chapter-ai-glow-btn"
                            onClick={async () => {
                              const gen = selectedChapterForEdit.outline.generatedContent || '';
                              const fullContent = (selectedChapterForEdit.content || '') + '\n\n' + gen;
                              const listTitle = sanitizeChapterTitleForList((selectedChapterForEdit.outline.generatedTitle || '').trim());
                              setChapterContent(prev => {
                                const sep = prev.trim() ? '\n\n' : '';
                                return prev + sep + gen;
                              });
                              const nextOutline = { ...selectedChapterForEdit.outline, generatedContent: '' };
                              delete nextOutline.generatedTitle;
                              const updatedChapter = {
                                ...selectedChapterForEdit,
                                content: (selectedChapterForEdit.content || '') + '\n\n' + gen,
                                outline: nextOutline,
                                ...(listTitle ? { title: listTitle } : {}),
                              };
                              setSelectedChapterForEdit(updatedChapter);
                              setCurrentChapter(cur => (cur && cur._id === updatedChapter._id
                                ? { ...cur, content: updatedChapter.content, ...(listTitle ? { title: listTitle } : {}) }
                                : cur));
                              setChapters(prev => prev.map(c => (c._id === updatedChapter._id
                                ? { ...c, content: updatedChapter.content, ...(listTitle ? { title: listTitle } : {}) }
                                : c)));
                              if (listTitle && assistNovelId) {
                                try {
                                  await apiClient.put(`/assist/novels/${assistNovelId}/chapters/${updatedChapter._id}`, { title: listTitle });
                                } catch (e) {
                                  console.warn('[插入正文] 标题保存失败:', e);
                                }
                              }
                              // 保存正文到数据库
                              if (assistNovelId) {
                                try {
                                  await apiClient.post(`/assist/novels/${assistNovelId}/chapters`, { ...updatedChapter });
                                } catch (e) {
                                  console.warn('[插入正文] 正文保存失败:', e);
                                }
                              }
                              console.log(`[全局进度·插入正文] 第${updatedChapter.chapterNumber}章触发自动跟进分析...`);
                              await autoSyncAfterChapterGenerate(fullContent, updatedChapter);
                              console.log(`[全局进度·插入正文] 第${updatedChapter.chapterNumber}章自动跟进完成`);
                              setChapterOutlineSavedTime(new Date());
                              void autoSaveOutlineFields(updatedChapter);
                            }}
                            sx={{ fontSize: 11, borderRadius: 999, textTransform: 'none', py: 0.5, '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.14)' } }}
                          >
                            插入正文
                          </Button>
                          <Button
                            variant="outlined"
                            color="inherit"
                            size="small"
                            className="assist-chapter-ai-glow-btn assist-chapter-clear-subtle"
                            onClick={() => setSelectedChapterForEdit(prev => ({
                              ...prev,
                              outline: { ...prev.outline, generatedContent: '', generatedTitle: '' }
                            }))}
                            sx={{ fontSize: 11, borderRadius: 999, textTransform: 'none', py: 0.5, '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(56,189,248,0.1)' } }}
                          >
                            清除
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}

                {/* 后续内容容器结束 */}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0a0b12' }}>
        <CircularProgress sx={{ color: '#00f0ff' }} />
      </Box>
    );
  }

  // 桌面端内容区宽度：无面板时只留工具栏60px，有面板时留面板+工具栏
  const rightReserved = 52;
  // 章纲章节为「左列表 + 右创作区」双栏，若仍用默认 ~380px 侧栏，右栏会被挤成一条缝（见设计文档 3.1 桌面分栏）
  const chaptersDesktopPanelWidth = 'min(920px, max(480px, calc(100vw - 52px - 320px)))';
  /** 与「章纲章节」同宽侧栏：审稿报告需要与列表+章纲区同等宽度便于阅读 */
  const useWideAssistSidePanel = activePanel === 'chapters' || activePanel === 'aiReview';
  const contentWidthDesktop = useWideAssistSidePanel
    ? `calc(100vw - ${rightReserved}px - ${chaptersDesktopPanelWidth})`
    : activePanel
      ? `calc(100vw - ${sidePanelWidth + rightReserved}px)`
      : `calc(100vw - ${rightReserved}px)`;
  // 窄屏侧栏在 Portal 全屏，主栏勿再按「桌面减侧栏」计算，否则 600–899px 主区会被压到两百多像素宽
  const mainContentWidth = isChaptersNarrow ? '100%' : contentWidthDesktop;

  /** 侧栏滚动区（桌面 / Portal 移动端共用，避免重复维护） */
  const workspaceSidePanelScrollArea = (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, overflow: 'hidden', width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
      {activePanel === 'chat' ? (
        /* 对话：整块不放进外层 scroll，输入条才能贴侧栏底且消息区独立滚动 */
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: '100%',
            p: { xs: 1.5, sm: 2 },
            pl: { md: 4 },
            boxSizing: 'border-box',
          }}
        >
          {renderChatPanel()}
        </Box>
      ) : (
        <Box
          className={isChaptersNarrow ? 'workspace-side-panel-scroll' : undefined}
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            p: activePanel === 'chapters' && isChaptersNarrow ? 0 : { xs: 1.5, sm: 2 },
            pl: { md: activePanel === 'chapters' ? 1.5 : 4 },
            minHeight: 0,
            width: '100%',
            minWidth: 0,
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {activePanel === 'aiContinue' && renderAIContinuePanel()}
          {activePanel === 'aiExpand' && renderAIExpandPanel()}
          {activePanel === 'aiPolish' && renderAIPolishPanel()}
          {activePanel === 'aiReview' && renderAIReviewPanel()}
          {activePanel === 'aiCorrect' && renderAICorrectPanel()}
          {activePanel === 'aiDenoise' && renderAIDenoisePanel()}
          {activePanel === 'character' && renderCharacterPanel()}
          {activePanel === 'characterEdit' && (
            <Slide direction="up" in={true} mountOnEnter timeout={{ enter: 400, exit: 300 }}>
              <div>{renderCharacterEditPanel()}</div>
            </Slide>
          )}
          {activePanel === 'item' && renderItemPanel()}
          {activePanel === 'itemEdit' && (
            <Slide direction="up" in={true} mountOnEnter timeout={{ enter: 400, exit: 300 }}>
              <div>{renderItemEditPanel()}</div>
            </Slide>
          )}
          {activePanel === 'memo' && renderMemoPanel()}
          {activePanel === 'memoEdit' && (
            <Slide direction="up" in={true} mountOnEnter timeout={{ enter: 400, exit: 300 }}>
              <div>{renderMemoEditPanel()}</div>
            </Slide>
          )}
          {activePanel === 'ai' && renderAIPanel()}
          {activePanel === 'outlineMain' && renderOutlineMainPanel()}
          {activePanel === 'progress' && renderProgressPanel()}
          {activePanel === 'chapters' && renderChaptersPanel()}
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      className={`nexus-layout workspace-container${creationType === 'assist' ? ' assist-workspace-theme' : ''}`}
      sx={{
        bgcolor: '#0a0b12',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        minWidth: 0,
        boxSizing: 'border-box',
        textAlign: 'left',
        flex: 1,
        alignSelf: 'stretch',
      }}
    >
      {/* 主内容区：用宽度控制，确保不弹窗时布满(仅留工具栏)、弹窗时压缩 */}
      <Box
        sx={{
          width: mainContentWidth,
          maxWidth: '100%',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          transition: 'width 0.3s ease',
          boxSizing: 'border-box',
        }}
      >
      {/* 顶部信息栏 */}
      <Box className="workspace-topbar" sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: { xs: 1, sm: 2 }, py: 1,
        flexWrap: 'wrap', gap: 1,
        position: 'sticky', top: 0, zIndex: 100
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: { xs: 1, sm: 'auto' } }}>
          <IconButton onClick={() => navigate(location.state?.from || '/tools')} sx={{ color: 'rgba(255,255,255,0.7)' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: 14, sm: 16 } }}>{novel?.title || '未命名作品'}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, ml: 'auto' }}>
          <Tooltip title="保存">
            <IconButton onClick={handleSaveChapter} disabled={saving} sx={{ color: saving ? 'rgba(255,255,255,0.3)' : '#00f0ff' }}>
              {saving ? <CircularProgress size={20} sx={{ color: '#00f0ff' }} /> : <SaveIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* 正在编辑：当前章节醒目展示 */}
      <Box className="workspace-editing-bar" sx={{ mx: { xs: 1, sm: 3 }, mt: 1 }}>
        <span className="editing-label">正在编辑</span>
        <span className="editing-chapter">
          {currentChapter ? `第 ${currentChapter.chapterNumber || 1} 章` : '—'}
        </span>
        {isEditingTitle ? (
          <TextField
            autoFocus
            size="small"
            value={editingChapterTitle}
            onChange={(e) => setEditingChapterTitle(e.target.value)}
            onBlur={handleSaveTopTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSaveTopTitle();
              if (e.key === 'Escape') { setIsEditingTitle(false); setEditingChapterId(null); }
            }}
            sx={{
              flex: 1,
              minWidth: 100,
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                bgcolor: 'rgba(0,0,0,0.3)',
                height: 28,
                '& fieldset': { borderColor: '#00f0ff' },
                '&:hover fieldset': { borderColor: '#00f0ff' },
                '&.Mui-focused fieldset': { borderColor: '#00f0ff', borderWidth: 2 }
              },
              '& input': { py: 0.5, px: 1 }
            }}
          />
        ) : (
          <span
            className="editing-title"
            title="点击修改标题"
            onClick={handleStartEditTopTitle}
            style={{ cursor: currentChapter ? 'pointer' : 'default' }}
          >
            {currentChapter?.title || '请选择或新建章节'}
          </span>
        )}
        <span className="editing-meta">{chapterContent.length} 字</span>
      </Box>

      {/* 主编辑区 - 用于限制悬浮按钮拖动范围 */}
      <Box
        ref={editorContainerRef}
        className="workspace-editor"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          px: { xs: 1, sm: 3 },
          py: 2,
          /* 窄屏底栏 fixed（约 54px + safe-area），仅保留一层留白，避免与 .workspace-container 的 pb 叠成大片黑底 */
          pb: isChaptersNarrow
            ? 'calc(56px + max(10px, env(safe-area-inset-bottom, 0px)))'
            : { sm: '80px' },
        }}
      >

        {/* ── 智能快速开始引导（首次使用 + 无内容时显示） ── */}
        {!currentChapter && chapters.length === 0 && outlines.length === 0 && characters.length === 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: { xs: 16, sm: 20 }, fontWeight: 700, mb: 0.5, textAlign: 'center' }}>
              欢迎使用 AI 小说创作助手
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: { xs: 12, sm: 13 }, mb: 4, textAlign: 'center', maxWidth: 400 }}>
              按以下步骤快速开始，创作属于你的小说
            </Typography>
            <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
              {[
                { step: '1', icon: '', title: '创建大纲', desc: '用 AI 快速生成完整故事框架', panel: 'outlineMain', color: 'var(--ws-accent)', gradient: 'rgba(var(--ws-accent-rgb),0.12)' },
                { step: '2', icon: '', title: '添加角色', desc: '建立主角和重要配角设定', panel: 'character', color: '#00f0ff', gradient: 'rgba(0,240,255,0.12)' },
                { step: '3', icon: '', title: '规划章纲', desc: '规划各章节核心事件和要点', panel: 'chapters', color: '#00ff9f', gradient: 'rgba(0,255,159,0.12)' },
              ].map(card => (
                <Box key={card.step} onClick={() => card.panel && setActivePanel(card.panel)}
                  sx={{ width: { xs: 140, sm: 160 }, p: 2, borderRadius: 2, bgcolor: card.gradient, border: `1px solid ${card.color}40`, cursor: card.panel ? 'pointer' : 'default', transition: 'all 0.25s ease', '&:hover': card.panel ? { borderColor: card.color + '80', transform: 'translateY(-4px)', boxShadow: `0 8px 24px ${card.color}25` } : {} }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: card.color + '20', border: `1px solid ${card.color}50`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                    {card.icon && <Typography sx={{ fontSize: 18 }}>{card.icon}</Typography>}
                  </Box>
                  <Typography sx={{ color: card.color, fontWeight: 700, fontSize: 13, mb: 0.5 }}>{card.step} · {card.title}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, lineHeight: 1.5 }}>{card.desc}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
                也可以点击右侧「AI」工具选择续写、润色等功能
              </Typography>
            </Box>
            <Button size="small" onClick={() => { setActivePanel('ai'); }}
              sx={{ color: '#ff00aa', borderColor: 'rgba(255,0,170,0.4)', border: '1px solid', fontSize: 12, '&:hover': { bgcolor: 'rgba(255,0,170,0.1)' } }}>
              打开 AI 工具集 →
            </Button>
          </Box>
        )}

        {/* 已有内容时的编辑器 - 双层结构：textarea + 高亮层 */}
        {(currentChapter || chapters.length > 0 || outlines.length > 0) && (
          <div className="editor-wrapper">
            <textarea
              ref={editorRef}
              className="editor-textarea"
              value={chapterContent}
              onChange={(e) => {
                const v = e.target.value;
                setChapterContent(v);
                if (selectedChapterForEdit && currentChapter?._id && currentChapter._id === selectedChapterForEdit._id) {
                  setSelectedChapterForEdit(prev => (prev ? { ...prev, content: v } : null));
                }
                // 内容变化时检查选区是否还存在，如果选区消失则清除高亮
                setTimeout(() => {
                  const selStart = e.target.selectionStart;
                  const selEnd = e.target.selectionEnd;
                  if (selStart === selEnd && selectionRange) {
                    setSelectedText('');
                    setSelectionRange(null);
                    setShowFloatingToolbar(false);
                  }
                }, 0);
              }}
              onSelect={syncChapterEditorSelection}
              onPointerUp={scheduleSyncChapterEditorSelection}
              onTouchEnd={() => {
                scheduleSyncChapterEditorSelection();
                setTimeout(syncChapterEditorSelection, 120);
              }}
              onKeyUp={() => {
                scheduleSyncChapterEditorSelection();
              }}
              onBlur={() => {
                // 焦点离开时自动保存正文（防用户忘保存）
                const contentToSave = chapterContent;
                const ch = selectedChapterForEdit;
                const nid = assistNovelId || novel?._id;
                if (!nid || !ch) return;
                const isAssist = String(nid).startsWith('assist_');
                if (isAssist) {
                  apiClient.post(`/assist/novels/${nid}/chapters`, { ...ch, content: contentToSave }).catch(() => {});
                } else {
                  const idx = chapters.findIndex(c => c._id === ch._id);
                  if (idx >= 0) apiClient.put(`/novels/${novel._id}/chapters/${idx + 1}`, { content: contentToSave }).catch(() => {});
                }
              }}
              placeholder="在这里开始你的创作..."
              style={{
                flex: 1,
                width: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: 16,
                lineHeight: 1.8,
                resize: 'none',
                fontFamily: '"Noto Sans SC", "Source Han Sans CN", sans-serif'
              }}
            />
            {/* 高亮层在 textarea 之后：pointer-events:none，不挡命中；z-index 低于正文框 */}
            <div className="editor-highlight-layer" aria-hidden="true">
              {selectionRange && renderHighlightLayer(chapterContent, selectionRange)}
            </div>
          </div>
        )}
      </Box>

      </Box>

      {/* 右侧竖向工具栏 - 布满整个高度，更精致窄宽度 */}
      <Box className="workspace-right-toolbar" sx={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100vh',
        width: 52,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 2,
        px: 0.5,
        bgcolor: 'rgba(10,10,26,0.9)',
        borderLeft: '1px solid rgba(0,240,255,0.15)',
        backdropFilter: 'blur(12px)',
        zIndex: 1100,
        boxShadow: '-4px 0 20px rgba(0,0,0,0.3)',
        gap: 0.5,
        display: { xs: 'none', md: 'flex' }
      }}>
        {rightTools.map((tool) => {
          const IconComponent = tool.icon;
          const isActive = activePanel === tool.panel;
          const twoLineLabel = DESKTOP_SIDEBAR_TWO_LINE_LABELS[tool.name];
          return (
            <Box
              key={tool.id}
              onClick={() => handleOpenPanel(tool.panel)}
              className="tool-item-cyber"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: tool.isMain ? 'scale(1.1)' : 'scale(1)',
                p: 1,
                position: 'relative',
                '&::before': isActive ? {
                  content: '""',
                  position: 'absolute',
                  bottom: -4,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 24,
                  height: 3,
                  bgcolor: tool.color,
                  borderRadius: 1,
                  boxShadow: `0 0 10px ${tool.color}, 0 0 20px ${tool.color}80`
                } : {},
                '&:hover': {
                  transform: tool.isMain ? 'scale(1.2)' : 'scale(1.15)',
                  '& .tool-icon-box': {
                    boxShadow: `0 0 20px ${tool.color}80, 0 0 40px ${tool.color}40`,
                    borderColor: tool.color,
                    transform: 'scale(1.05)'
                  },
                  '& .tool-name': {
                    textShadow: `0 0 10px ${tool.color}, 0 0 20px ${tool.color}80`
                  }
                }
              }}
            >
              <Box className="tool-icon-box" sx={{
                width: 38,
                height: 38,
                borderRadius: tool.isMain ? '50%' : '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isActive ? `${tool.color}20` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isActive ? tool.color : 'rgba(255,255,255,0.1)'}`,
                boxShadow: isActive 
                  ? `0 0 20px ${tool.color}60, inset 0 0 10px ${tool.color}20`
                  : `0 0 8px ${tool.color}20`,
                transition: 'all 0.3s ease'
              }}>
                <IconComponent sx={{ fontSize: tool.isMain ? 22 : 18, color: isActive ? tool.color : 'rgba(255,255,255,0.8)' }} />
              </Box>
              <Typography className="tool-name" sx={{
                color: isActive ? tool.color : 'rgba(255,255,255,0.6)',
                fontSize: 10,
                fontWeight: isActive ? 600 : 400,
                textShadow: isActive ? `0 0 8px ${tool.color}80` : 'none',
                transition: 'all 0.3s ease',
                mt: 0.5,
                letterSpacing: '0.3px',
                textAlign: 'center',
                lineHeight: twoLineLabel ? 1.22 : 1.2,
                whiteSpace: twoLineLabel ? 'normal' : 'nowrap',
              }}>
                {twoLineLabel ? (
                  <>
                    {twoLineLabel[0]}
                    <br />
                    {twoLineLabel[1]}
                  </>
                ) : (
                  tool.name
                )}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* 移动端底栏已移至下方 Portal（挂 body），避免嵌在 #root 内宽度被挤压 */}

      {/* 浮动工具栏（选中文字时）- 用 Portal 渲染到 body 避免被遮挡 */}
      {showFloatingToolbar && selectedText && (
        <Portal>
          <div
            className="floating-toolbar"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: floatingToolbarPos.x,
              top: floatingToolbarPos.y - 50,
              transform: 'translateX(-50%)',
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              backgroundColor: '#1a1a2e',
              border: '1px solid rgba(0, 240, 255, 0.5)',
              borderRadius: '10px',
              padding: '6px 10px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.7), 0 0 30px rgba(0, 240, 255, 0.15)',
              zIndex: 99999,
              animation: 'floatIn 0.2s ease-out'
            }}
          >
            <style>{`
              @keyframes floatIn {
                from { opacity: 0; transform: translateX(-50%) translateY(10px); }
                to { opacity: 1; transform: translateX(-50%) translateY(0); }
              }
              .floating-toolbar button { border: none !important; outline: none !important; cursor: pointer; }
              .floating-toolbar button:hover { opacity: 0.85; }
              .floating-toolbar .tool-btn {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 6px 10px;
                border-radius: 6px;
                transition: all 0.15s ease;
                min-width: 52px;
              }
              .floating-toolbar .tool-btn:hover { transform: translateY(-2px); }
              .floating-toolbar .tool-btn .tool-icon { font-size: 18px; margin-bottom: 2px; }
              .floating-toolbar .tool-btn .tool-label { font-size: 11px; font-weight: 500; }
              .floating-toolbar .tool-divider { width: 1px; height: 40px; background: rgba(255,255,255,0.15); margin: 0 6px; }
            `}</style>
            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('copy'); }}
              style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
              <span className="tool-label">复制</span>
            </button>

            <div className="tool-divider" />

            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('expand'); }}
              style={{ color: '#ff6b35', backgroundColor: 'rgba(255,107,53,0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                <line x1="12" y1="11" x2="12" y2="17"/>
                <line x1="9" y1="14" x2="15" y2="14"/>
              </svg>
              <span className="tool-label">扩写</span>
            </button>
            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('polish'); }}
              style={{ color: '#00f0ff', backgroundColor: 'rgba(0,240,255,0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2z"/>
              </svg>
              <span className="tool-label">润色</span>
            </button>
            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('continueWrite'); }}
              style={{ color: 'var(--ws-accent)', backgroundColor: 'rgba(var(--ws-accent-rgb),0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
              </svg>
              <span className="tool-label">续写</span>
            </button>

            <div className="tool-divider" />

            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('correct'); }}
              style={{ color: '#06b6d4', backgroundColor: 'rgba(6,182,212,0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                <line x1="8" y1="11" x2="14" y2="11"/>
                <line x1="11" y1="8" x2="11" y2="14"/>
              </svg>
              <span className="tool-label">纠错</span>
            </button>
            <button
              className="tool-btn"
              onClick={(e) => { e.stopPropagation(); handleFloatingAction('denoise'); }}
              style={{ color: '#f59e0b', backgroundColor: 'rgba(245,158,11,0.1)' }}
            >
              <svg className="tool-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18"/>
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
              <span className="tool-label">去痕</span>
            </button>
          </div>
        </Portal>
      )}

      {/* 桌面 md+：侧栏留在 React 树内；窄屏改由下方 Portal 挂 body，避免 #root/.App 导致全屏层宽度异常 */}
      {activePanel && !isChaptersNarrow && (
        <Box
          onClick={handleClosePanel}
          sx={{
            position: 'fixed',
            left: useWideAssistSidePanel ? `calc(100vw - ${rightReserved}px - ${chaptersDesktopPanelWidth} - 14px)` : `calc(100vw - ${52 + sidePanelWidth}px - 14px)`,
            right: 'auto',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 1060,
            width: 28,
            height: 64,
            bgcolor: 'rgba(10,10,26,0.95)',
            border: '1px solid rgba(0,240,255,0.4)',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 0 20px rgba(0,240,255,0.2)',
            '&:hover': {
              bgcolor: 'rgba(0,240,255,0.2)',
              borderColor: '#00f0ff',
              boxShadow: '0 0 30px rgba(0,240,255,0.5)',
              transform: 'translateY(-50%) scale(1.05)',
            },
          }}
        >
          <KeyboardArrowRightIcon sx={{ fontSize: 18, color: '#00f0ff' }} />
        </Box>
      )}

      {activePanel && !isChaptersNarrow && (
        <Box
          sx={{
            position: 'fixed',
            left: 'auto',
            right: 52,
            top: 0,
            width: useWideAssistSidePanel ? chaptersDesktopPanelWidth : sidePanelWidth,
            height: '100vh',
            bgcolor: '#0a0b12',
            borderLeft: '1px solid rgba(0,240,255,0.2)',
            zIndex: 1050,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none',
            transition: 'transform 0.25s ease, opacity 0.25s ease, width 0.3s ease',
            borderRadius: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
          }}
        >
          {workspaceSidePanelScrollArea}
        </Box>
      )}

      {/* 窄屏（< md）：底栏 + 全屏侧栏挂 document.body，与 #root 同级，保证 true 100vw */}
      {isChaptersNarrow && (
        <Portal>
          {activePanel && (
            <Box
              className="workspace-mobile-portal-panel"
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                maxWidth: '100vw',
                height: '100dvh',
                maxHeight: '100dvh',
                bgcolor: '#0a0b12',
                zIndex: 1300,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                boxSizing: 'border-box',
                pb: 'calc(54px + env(safe-area-inset-bottom, 0px))',
              }}
            >
              {workspaceSidePanelScrollArea}
            </Box>
          )}
          <Box
            className="workspace-tooltray tooltray-neon workspace-tooltray-mobile workspace-mobile-portal-tray"
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              width: '100%',
              maxWidth: '100vw',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'stretch',
              justifyContent: 'space-between',
              py: 0.5,
              px: 0.25,
              paddingBottom: 'max(8px, env(safe-area-inset-bottom, 0px))',
              bgcolor: 'rgba(8,9,18,0.96)',
              borderTop: '1px solid rgba(0,240,255,0.22)',
              backdropFilter: 'blur(16px)',
              zIndex: 1350,
              minHeight: 54,
              overflow: 'hidden',
              boxShadow: '0 -12px 40px rgba(0,0,0,0.45)',
            }}
          >
            {rightTools.map((tool) => {
              const IconComponent = tool.icon;
              const isActive = activePanel === tool.panel;
              return (
                <Box
                  key={tool.id}
                  onClick={() => handleOpenPanel(tool.panel)}
                  className="tool-item-cyber"
                  sx={{
                    flex: '1 1 0',
                    minWidth: 0,
                    maxWidth: `${100 / rightTools.length}%`,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    transform: tool.isMain ? 'scale(1.05)' : 'scale(1)',
                    py: 0.35,
                    px: 0.125,
                    position: 'relative',
                    '&::before': isActive
                      ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 'min(70%, 32px)',
                          height: 2,
                          bgcolor: tool.color,
                          borderRadius: '0 0 3px 3px',
                          boxShadow: `0 0 10px ${tool.color}, 0 0 20px ${tool.color}80`,
                        }
                      : {},
                    '&:hover': {
                      transform: tool.isMain ? 'scale(1.12)' : 'scale(1.08)',
                      '& .tool-icon-box-mobile': {
                        boxShadow: `0 0 16px ${tool.color}80, 0 0 32px ${tool.color}40`,
                        borderColor: tool.color,
                      },
                      '& .tool-name-mobile': {
                        textShadow: `0 0 8px ${tool.color}, 0 0 16px ${tool.color}80`,
                      }
                    }
                  }}
                >
                  <Box
                    className="tool-icon-box-mobile"
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: tool.isMain ? '50%' : (isActive ? '10px' : '8px'),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: isActive ? `${tool.color}18` : 'rgba(255,255,255,0.04)',
                      border: isActive ? `1px solid ${tool.color}66` : '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.3s ease',
                      flexShrink: 0,
                    }}
                  >
                    <IconComponent sx={{ fontSize: isActive ? 17 : 15, color: isActive ? tool.color : 'rgba(255,255,255,0.52)' }} />
                  </Box>
                  <Typography
                    className="tool-name-mobile"
                    sx={{
                      color: isActive ? tool.color : 'rgba(255,255,255,0.45)',
                      // ≥425px 约 11px；<425px 再缩小约 2px（约 9px）
                      fontSize: '0.6875rem',
                      '@media (max-width: 424.95px)': {
                        fontSize: '0.75rem',
                        letterSpacing: '-0.08em',
                      },
                      fontWeight: isActive ? 600 : 400,
                      textShadow: isActive ? `0 0 8px ${tool.color}80` : 'none',
                      transition: 'all 0.3s ease',
                      mt: 0.2,
                      width: '100%',
                      px: 0.125,
                      textAlign: 'center',
                      lineHeight: 1.05,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      WebkitTextSizeAdjust: '100%',
                      textSizeAdjust: '100%',
                    }}
                  >
                    {getMobileToolTrayLabel(tool.name)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Portal>
      )}

      {/* 删除章节确认弹窗（全局，与大纲生成弹窗同系赛博样式 + 统一过渡） */}
      <Dialog
        open={confirmDeleteDialogOpen}
        onClose={() => { setConfirmDeleteDialogOpen(false); setConfirmDeleteChapter(null); }}
        maxWidth="xs"
        fullWidth
        TransitionComponent={CyberDialogTransition}
        transitionDuration={motionReduced ? 0 : { enter: 420, exit: 260 }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(4, 6, 14, 0.78)',
            backdropFilter: motionReduced ? 'none' : 'saturate(1.08) blur(10px)',
          },
        }}
        PaperProps={{
          className: 'character-panel-cyber assist-dialog-compact',
          sx: {
            bgcolor: 'rgba(10, 11, 18, 0.98)',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), 0 0 100px rgba(239, 68, 68, 0.16)',
          },
        }}
      >
        <DialogTitle
          className="character-panel-title-glow"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'rgba(255,255,255,0.95) !important',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            py: 1.25,
            flexShrink: 0,
          }}
        >
          <WarningIcon
            sx={{
              color: 'rgba(248,113,113,0.95)',
              fontSize: 22,
              filter: 'drop-shadow(0 0 8px rgba(248,113,113,0.45))',
            }}
          />
          确认删除章节
        </DialogTitle>
          <DialogContent className="character-panel-cyber" sx={{ pt: 1.5, pb: 0.5, px: { xs: 2, sm: 2.5 } }}>
            {confirmDeleteChapter && (
              <>
                <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: 13, lineHeight: 1.6 }}>
                  确定删除「{formatChapterColonTitle(confirmDeleteChapter)}」吗？
                </Typography>
                <Typography sx={{ color: 'rgba(248,113,113,0.88)', fontSize: 12, mt: 1, lineHeight: 1.5 }}>
                  删除后该章节将被永久移除，后续章节序号将自动顺移。
                </Typography>
              </>
            )}
          </DialogContent>
        <DialogActions
          sx={{
            bgcolor: 'transparent',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            flexWrap: 'wrap',
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
          }}
        >
          <Button
            onClick={() => { setConfirmDeleteDialogOpen(false); setConfirmDeleteChapter(null); }}
            className="workspace-btn-cyber"
            sx={{
              color: 'rgba(255,255,255,0.7)',
              borderColor: 'rgba(255,255,255,0.25)',
              minHeight: 40,
              fontSize: { xs: 13, sm: 14 },
              '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' },
            }}
          >
            取消
          </Button>
          <Button
            onClick={async () => {
              if (confirmDeleteChapter) {
                await handleDeleteChapter(confirmDeleteChapter._id);
              }
              setConfirmDeleteDialogOpen(false);
              setConfirmDeleteChapter(null);
            }}
            className="workspace-btn-cyber"
            variant="outlined"
            sx={{
              color: '#f87171',
              borderColor: 'rgba(248,113,113,0.65)',
              minHeight: 40,
              fontSize: { xs: 13, sm: 14 },
              '&:hover': {
                borderColor: '#f87171',
                bgcolor: 'rgba(239,68,68,0.12)',
                boxShadow: '0 0 14px rgba(239,68,68,0.25)',
              },
            }}
          >
            确认删除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 章节状态修改弹窗（全局） */}
      {editChapterStatusDialog && (
        <Dialog
          open
          onClose={() => setEditChapterStatusDialog(null)}
          maxWidth="xs"
          fullWidth
          TransitionComponent={CyberDialogTransition}
          transitionDuration={motionReduced ? 0 : { enter: 420, exit: 260 }}
          BackdropProps={{
            sx: {
              backgroundColor: 'rgba(4, 6, 14, 0.78)',
              backdropFilter: motionReduced ? 'none' : 'saturate(1.08) blur(10px)',
            },
          }}
          PaperProps={{
            className: 'character-panel-cyber assist-dialog-compact',
            sx: {
              bgcolor: 'rgba(10,11,18,0.98)',
              backgroundImage: 'none',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 90px rgba(6,182,212,0.14)',
            },
          }}
        >
          <DialogTitle
            className="character-panel-title-glow"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              color: 'rgba(255,255,255,0.95) !important',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              py: 1.25,
              flexShrink: 0,
            }}
          >
            <Typography component="span" sx={{ color: '#22d3ee', fontSize: 20, lineHeight: 1, filter: 'drop-shadow(0 0 6px rgba(34,211,238,0.4))' }}>◆</Typography>
            修改章节状态
          </DialogTitle>
          <DialogContent className="character-panel-cyber" sx={{ pt: 1.5, pb: 0.5, px: { xs: 2, sm: 2.5 } }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.88)', fontSize: 13, mb: 1.5 }}>
              {formatChapterColonTitle(editChapterStatusDialog)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {CHAPTER_STATUS_OPTIONS.map(opt => (
                <Box key={opt.value}
                  onClick={async () => {
                    const updated = { ...editChapterStatusDialog, status: opt.value };
                    try {
                      await apiClient.post(`/assist/novels/${assistNovelId}/chapters/${updated._id}/outline`, { outline: updated.outline, status: opt.value });
                      setChapters(prev => prev.map(c => c._id === updated._id ? updated : c));
                      if (currentChapter?._id === updated._id) { setCurrentChapter(updated); }
                      setSnackbar({ open: true, message: `状态已更新为「${opt.label}」`, severity: 'success' });
                    } catch(e) { setSnackbar({ open: true, message: '保存失败', severity: 'error' }); }
                    setEditChapterStatusDialog(null);
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1,
                    borderRadius: 1, cursor: 'pointer', border: '1px solid',
                    borderColor: editChapterStatusDialog.status === opt.value ? opt.color + '80' : 'rgba(255,255,255,0.08)',
                    bgcolor: editChapterStatusDialog.status === opt.value ? opt.color + '15' : 'transparent',
                    '&:hover': { borderColor: opt.color, bgcolor: opt.color + '10' }
                  }}>
                  <Typography sx={{ color: opt.color, fontSize: 14 }}>{opt.icon}</Typography>
                  <Typography sx={{ color: opt.color, fontSize: 12, fontWeight: 600 }}>{opt.label}</Typography>
                  {editChapterStatusDialog.status === opt.value && (
                    <Typography sx={{ color: opt.color, fontSize: 10, ml: 'auto' }}>当前</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ bgcolor: 'transparent', borderTop: '1px solid rgba(255,255,255,0.1)', px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
            <Button
              onClick={() => setEditChapterStatusDialog(null)}
              className="workspace-btn-cyber"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                borderColor: 'rgba(255,255,255,0.25)',
                minHeight: 40,
                fontSize: { xs: 13, sm: 14 },
                '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' },
              }}
            >
              取消
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* 赛博朋克提示消息 */}
      {snackbar.open && (
        <Box
          className={`cyber-snackbar cyber-snackbar-content ${snackbar.severity}`}
          onClick={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Box className="cyber-snackbar-icon">
            {snackbar.severity === 'success' && '✓'}
            {snackbar.severity === 'error' && '✕'}
            {snackbar.severity === 'warning' && '⚠'}
            {snackbar.severity === 'info' && 'ℹ'}
          </Box>
          <Typography className="cyber-snackbar-message">{snackbar.message}</Typography>
          <IconButton className="cyber-snackbar-close" size="small" onClick={(e) => { e.stopPropagation(); setSnackbar(s => ({ ...s, open: false })); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* 新建/编辑卷弹窗（紧凑高度，与大纲弹窗同系过渡） */}
      <Dialog
        open={volumeDialogOpen}
        onClose={() => { setVolumeDialogOpen(false); setEditingVolumeId(null); setVolumeFormData({ name: '', title: '', chapterCount: 10 }); }}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={CyberDialogTransition}
        transitionDuration={motionReduced ? 0 : { enter: 420, exit: 260 }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(4, 6, 14, 0.78)',
            backdropFilter: motionReduced ? 'none' : 'saturate(1.08) blur(10px)',
          },
        }}
        PaperProps={{
          className: 'character-panel-cyber assist-dialog-compact',
          sx: {
            bgcolor: 'rgba(10, 11, 18, 0.98)',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 2,
            width: '100%',
            maxWidth: { xs: '100%', sm: 400 },
            boxShadow: '0 0 60px rgba(0,0,0,0.8), 0 0 120px rgba(255,215,0,0.12)',
          },
        }}
      >
        <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ffd700 !important', borderBottom: '1px solid rgba(255,215,0,0.2)', py: 1.25, flexShrink: 0 }}>
          {editingVolumeId ? '编辑卷' : '新建卷'}
        </DialogTitle>
        <DialogContent className="character-panel-cyber" sx={{ pt: 1.5, pb: 0.5, px: { xs: 2, sm: 2.5 }, overflow: 'unset' }}>
          {/* 卷名 + 自动建议标签 */}
          <Box sx={{ mb: 1.5, position: 'relative' }}>
            {!editingVolumeId && (
              <Typography
                component="span"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.12 }}
                sx={{
                  display: 'inline-block',
                  mb: 0.5,
                  px: 0.75,
                  py: 0.2,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  borderRadius: 1,
                  bgcolor: 'rgba(0,255,159,0.1)',
                  color: '#00ff9f',
                  border: '1px solid rgba(0,255,159,0.25)',
                }}
              >
                自动生成：{getSuggestedVolumeName(volumes)}
              </Typography>
            )}
            <TextField
              fullWidth size="small" className="character-panel-field"
              label="卷名" value={volumeFormData.name}
              onChange={(e) => setVolumeFormData({ ...volumeFormData, name: e.target.value })}
              placeholder="如：第一卷"
              sx={{
                '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.03)', fontSize: { xs: 12, sm: 13 } },
                '& .MuiInputLabel-root': { color: 'rgba(255,215,0,0.7)' },
              }}
            />
          </Box>

          {/* 卷标题（可选） */}
          <TextField
            fullWidth size="small" className="character-panel-field"
            label="卷标题（可选）" value={volumeFormData.title}
            onChange={(e) => setVolumeFormData({ ...volumeFormData, title: e.target.value })}
            placeholder="如：凡界篇"
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.03)', fontSize: { xs: 12, sm: 13 } } }}
          />

          {/* 章节数输入 */}
          <TextField
            fullWidth size="small"
            label="预设章节数" type="number"
            value={volumeFormData.chapterCount}
            onChange={(e) => setVolumeFormData({ ...volumeFormData, chapterCount: Math.max(1, parseInt(e.target.value) || 10) })}
            sx={{
              '& .MuiOutlinedInput-root': { bgcolor: 'rgba(255,255,255,0.03)', fontSize: { xs: 12, sm: 13 } },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'transparent', borderTop: '1px solid rgba(255,215,0,0.2)', px: { xs: 2, sm: 2.5 }, py: 1.5 }}>
          <Button
            onClick={() => { setVolumeDialogOpen(false); setEditingVolumeId(null); setVolumeFormData({ name: '', title: '', chapterCount: 10 }); }}
            className="workspace-btn-cyber"
            sx={{ color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.25)', minHeight: 40, fontSize: { xs: 13, sm: 14 }, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.06)' } }}
          >取消</Button>
          <Button
            onClick={handleSaveVolume}
            className="workspace-btn-cyber"
            sx={{ color: '#ffd700', borderColor: '#ffd700', minHeight: 40, fontSize: { xs: 13, sm: 14 }, '&:hover': { borderColor: '#ffd700', bgcolor: 'rgba(255,215,0,0.1)', boxShadow: '0 0 12px rgba(255,215,0,0.3)' } }}
          >{editingVolumeId ? '保存' : '创建'}</Button>
        </DialogActions>
      </Dialog>

      {/* AI解析角色弹窗 */}
      {renderCharacterParseDialog()}
      {renderRelationParseDialog()}
      {/* 全书大纲相关弹窗：挂根节点，避免仅在大纲侧栏挂载时切换面板导致弹窗卸载；亦减轻大纲面板首屏渲染 */}
      {renderOutlineGenDialog()}
      {renderSimpleOutlineDialog()}
      {renderOutlineParseDialog()}

      {/* 大纲查找替换对话框 */}
      <Dialog
        open={outlineFindReplaceOpen}
        onClose={() => setOutlineFindReplaceOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'rgba(0,0,0,0.9)', color: '#f59e0b', borderBottom: '1px solid rgba(245,158,11,0.2)' }}>
          查找并替换
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(0,0,0,0.95)', pt: 2 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mb: 2 }}>
            在大纲的所有字段中查找并替换文本（支持跨字段替换）
          </Typography>

          <TextField
            fullWidth
            label="查找内容"
            value={outlineFindText}
            onChange={(e) => setOutlineFindText(e.target.value)}
            placeholder="输入要查找的角色名或文本"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
          />

          <TextField
            fullWidth
            label="替换为"
            value={outlineReplaceText}
            onChange={(e) => setOutlineReplaceText(e.target.value)}
            placeholder="输入替换后的文本"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { color: '#fff' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)', borderTop: '1px solid rgba(245,158,11,0.2)' }}>
          <Button onClick={() => setOutlineFindReplaceOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>取消</Button>
          <Button
            onClick={() => {
              if (!outlineFindText || !outlineReplaceText || outlineFindText === outlineReplaceText) return;
              if (outlines.length === 0) return;

              const currentOutline = outlines[0];

              // 递归替换对象中的字符串
              const replaceInObject = (obj, searchStr, replaceStr) => {
                const replace = (val) => {
                  if (typeof val === 'string') {
                    return val.replace(new RegExp(searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replaceStr);
                  } else if (Array.isArray(val)) {
                    return val.map(replace);
                  } else if (val && typeof val === 'object') {
                    const result = {};
                    Object.entries(val).forEach(([k, v]) => {
                      result[k] = replace(v);
                    });
                    return result;
                  }
                  return val;
                };
                return replace(obj);
              };

              // 统计替换数量
              const countOccurrences = (obj, searchStr) => {
                let count = 0;
                const search = (val) => {
                  if (typeof val === 'string') {
                    const regex = new RegExp(searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                    const matches = val.match(regex);
                    if (matches) count += matches.length;
                  } else if (Array.isArray(val)) {
                    val.forEach(search);
                  } else if (val && typeof val === 'object') {
                    Object.values(val).forEach(search);
                  }
                };
                search(obj);
                return count;
              };

              const count = countOccurrences(currentOutline, outlineFindText);
              const newOutline = replaceInObject(currentOutline, outlineFindText, outlineReplaceText);

              setOutlines([{ ...newOutline, updatedAt: new Date().toISOString() }]);
              setSnackbar({ open: true, message: `已替换 ${count} 处「${outlineFindText}」为「${outlineReplaceText}」`, severity: 'success' });
              setOutlineFindReplaceOpen(false);
            }}
            disabled={!outlineFindText || !outlineReplaceText || outlineFindText === outlineReplaceText}
            sx={{ color: '#f59e0b', border: '1px solid #f59e0b' }}
          >
            替换全部
          </Button>
        </DialogActions>
      </Dialog>

      {/* 角色换名对话框 — 粉色赛博风格 */}
      <Dialog
        open={characterRenameOpen}
        onClose={() => setCharacterRenameOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(10, 8, 20, 0.98)',
            border: '1px solid rgba(236, 72, 153, 0.4)',
            boxShadow: '0 0 40px rgba(236, 72, 153, 0.15), 0 0 80px rgba(0, 0, 0, 0.8)',
            animation: 'dialogGlowPink 3s ease-in-out infinite',
            '@keyframes dialogGlowPink': {
              '0%, 100%': { boxShadow: '0 0 40px rgba(236, 72, 153, 0.15), 0 0 80px rgba(0, 0, 0, 0.8)' },
              '50%': { boxShadow: '0 0 60px rgba(236, 72, 153, 0.25), 0 0 100px rgba(236, 72, 153, 0.08)' },
            },
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'rgba(0,0,0,0.6)',
          color: '#ec4899',
          borderBottom: '1px solid rgba(236, 72, 153, 0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
        }}>
          <PersonIcon sx={{ fontSize: 22, color: '#ec4899' }} />
          角色换名
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(0,0,0,0.5)', pt: 2.5, pb: 1 }}>
          {/* 教程说明 */}
          <Box sx={{
            bgcolor: 'rgba(236, 72, 153, 0.08)',
            border: '1px solid rgba(236, 72, 153, 0.25)',
            borderRadius: 1.5,
            p: 2,
            mb: 2.5,
          }}>
            <Typography sx={{ color: '#f472b6', fontSize: 13, fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AutoAwesomeIcon sx={{ fontSize: 16 }} />
              使用教程
            </Typography>
            <Typography component="div" sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 12.5, lineHeight: 1.8 }}>
              <Box component="span" sx={{ color: '#fbbf24', fontWeight: 600 }}>第一步：</Box>在上方输入框填写要替换的原角色名（在大纲中显示的名字）<br />
              <Box component="span" sx={{ color: '#fbbf24', fontWeight: 600 }}>第二步：</Box>在下方输入框填写新的角色名<br />
              <Box component="span" sx={{ color: '#fbbf24', fontWeight: 600 }}>第三步：</Box>点击「确认换名」，系统将自动替换大纲中所有该角色名，并保存新大纲<br />
              <Box component="span" sx={{ color: '#86efac', fontWeight: 600 }}>提示：</Box>替换后系统会自动保存新的大纲内容，确保数据安全
            </Typography>
          </Box>

          {/* 原角色名输入 */}
          <TextField
            fullWidth
            label="原角色名"
            value={oldCharName}
            onChange={(e) => setOldCharName(e.target.value)}
            placeholder="例如：张三、李四、掌门师兄"
            InputProps={{
              sx: {
                color: '#fbbf24',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245, 158, 11, 0.4)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(245, 158, 11, 0.6)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f59e0b', boxShadow: '0 0 8px rgba(245, 158, 11, 0.3)' },
              }
            }}
            InputLabelProps={{ sx: { color: 'rgba(245, 158, 11, 0.8)' } }}
            sx={{ mb: 2 }}
          />

          {/* 新角色名输入 */}
          <TextField
            fullWidth
            label="新角色名"
            value={newCharName}
            onChange={(e) => setNewCharName(e.target.value)}
            placeholder="例如：张无忌、李逍遥、掌门师妹"
            InputProps={{
              sx: {
                color: '#86efac',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(134, 239, 172, 0.4)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(134, 239, 172, 0.6)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#86efac', boxShadow: '0 0 8px rgba(134, 239, 172, 0.3)' },
              }
            }}
            InputLabelProps={{ sx: { color: 'rgba(134, 239, 172, 0.8)' } }}
            sx={{ mb: 1 }}
          />

          {/* 预览提示 */}
          {(oldCharName && newCharName) && (
            <Box sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: 'rgba(255,255,255,0.03)',
              border: '1px dashed rgba(255,255,255,0.15)',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <CompareArrowsIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.5)' }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12.5 }}>
                替换效果：
                <Box component="span" sx={{ color: '#f59e0b', mx: 0.5 }}>{oldCharName}</Box>
                <Box component="span" sx={{ color: 'rgba(255,255,255,0.4)', mx: 0.5 }}>→</Box>
                <Box component="span" sx={{ color: '#86efac', mx: 0.5 }}>{newCharName}</Box>
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          bgcolor: 'rgba(0,0,0,0.4)',
          borderTop: '1px solid rgba(236, 72, 153, 0.2)',
          px: 2,
          py: 1.5,
        }}>
          <Button
            onClick={() => {
              setCharacterRenameOpen(false);
              setOldCharName('');
              setNewCharName('');
            }}
            sx={{ color: 'rgba(255,255,255,0.6)' }}
          >
            取消
          </Button>
          <Button
            onClick={async () => {
              if (!oldCharName.trim() || !newCharName.trim()) {
                setSnackbar({ open: true, message: '请填写原角色名和新角色名', severity: 'warning' });
                return;
              }
              if (oldCharName.trim() === newCharName.trim()) {
                setSnackbar({ open: true, message: '原角色名和新角色名不能相同', severity: 'warning' });
                return;
              }
              if (outlines.length === 0) {
                setSnackbar({ open: true, message: '暂无大纲可替换', severity: 'warning' });
                return;
              }

              const currentOutline = outlines[0];

              // 递归替换对象中的字符串
              const replaceInObject = (obj, searchStr, replaceStr) => {
                const replace = (val) => {
                  if (typeof val === 'string') {
                    return val.replace(new RegExp(searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), replaceStr);
                  } else if (Array.isArray(val)) {
                    return val.map(replace);
                  } else if (val && typeof val === 'object') {
                    const result = {};
                    Object.entries(val).forEach(([k, v]) => {
                      result[k] = replace(v);
                    });
                    return result;
                  }
                  return val;
                };
                return replace(obj);
              };

              // 统计替换数量
              const countOccurrences = (obj, searchStr) => {
                let count = 0;
                const search = (val) => {
                  if (typeof val === 'string') {
                    const regex = new RegExp(searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
                    const matches = val.match(regex);
                    if (matches) count += matches.length;
                  } else if (Array.isArray(val)) {
                    val.forEach(search);
                  } else if (val && typeof val === 'object') {
                    Object.values(val).forEach(search);
                  }
                };
                search(obj);
                return count;
              };

              const count = countOccurrences(currentOutline, oldCharName.trim());
              const newOutline = replaceInObject(currentOutline, oldCharName.trim(), newCharName.trim());
              const updatedOutline = { ...newOutline, updatedAt: new Date().toISOString() };

              // 更新本地状态
              setOutlines([updatedOutline]);

              // 自动保存到数据库
              if (assistNovelId && currentOutline._id) {
                try {
                  await apiClient.post(`/assist/novels/${assistNovelId}/outline`, {
                    name: updatedOutline.name,
                    content: updatedOutline.content,
                    type: updatedOutline.type,
                    mainPlot: updatedOutline.mainPlot,
                    subPlots: updatedOutline.subPlots || [],
                    foreshadowing: updatedOutline.foreshadowing || updatedOutline.foreshadowings || [],
                    updatedAt: updatedOutline.updatedAt,
                  });
                  console.log('角色换名后大纲已自动保存');
                } catch (e) {
                  console.error('角色换名后保存大纲失败:', e);
                }
              }

              setSnackbar({
                open: true,
                message: `已将大纲中 ${count} 处「${oldCharName}」替换为「${newCharName}」，新大纲已保存`,
                severity: 'success'
              });
              setCharacterRenameOpen(false);
              setOldCharName('');
              setNewCharName('');
            }}
            disabled={!oldCharName.trim() || !newCharName.trim() || oldCharName.trim() === newCharName.trim()}
            sx={{
              color: '#ec4899',
              border: '1px solid rgba(236, 72, 153, 0.5)',
              bgcolor: 'rgba(236, 72, 153, 0.1)',
              '&:hover': {
                bgcolor: 'rgba(236, 72, 153, 0.2)',
                borderColor: 'rgba(236, 72, 153, 0.7)',
              },
              '&.Mui-disabled': {
                color: 'rgba(236, 72, 153, 0.3)',
                borderColor: 'rgba(236, 72, 153, 0.2)',
              }
            }}
          >
            确认换名
          </Button>
        </DialogActions>
      </Dialog>

      {/* 角色选择弹窗（章纲编辑时选角色） */}
      <Dialog open={selectCharDialogOpen} onClose={() => setSelectCharDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'rgba(0,0,0,0.8)', color: '#ec4899', borderBottom: '1px solid rgba(236,72,153,0.2)' }}>
          选择出场角色
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(0,0,0,0.9)', maxHeight: '70vh', overflow: 'auto' }}>
          {characters.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {characters.map(char => {
                const isChecked = wizardSelectedChars.includes(char._id);
                const roleLbDlg = getCharacterDisplayRoleLabel(char);
                return (
                  <Box
                    key={char._id}
                    onClick={() => {
                      setWizardSelectedChars(
                        isChecked
                          ? wizardSelectedChars.filter(id => id !== char._id)
                          : [...wizardSelectedChars, char._id]
                      );
                      // 同步更新 selectedChapterForEdit
                      setSelectedChapterForEdit(prev => {
                        if (!prev) return prev;
                        const mergedIds = isChecked
                          ? (prev.characterIds || []).filter(id => id !== char._id)
                          : [...new Set([...(prev.characterIds || []), char._id])];
                        return { ...prev, characterIds: mergedIds };
                      });
                    }}
                    sx={{
                      p: 1.5, borderRadius: 1,
                      bgcolor: isChecked ? 'rgba(236,72,153,0.2)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${isChecked ? 'rgba(236,72,153,0.5)' : 'rgba(255,255,255,0.1)'}`,
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                      '&:hover': { bgcolor: 'rgba(236,72,153,0.1)' },
                    }}
                  >
                    <Checkbox checked={isChecked} sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#ec4899' } }} />
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(236,72,153,0.3)', fontSize: 14 }}>{getCharacterChapterOutlineDisplayName(char)?.[0] || char.name?.[0] || '?'}</Avatar>
                    <Box>
                      <Typography sx={{ color: '#fff', fontSize: 13 }}>{getCharacterChapterOutlineDisplayName(char)}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{roleLbDlg}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}>暂无角色，请先在角色库创建</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)', borderTop: '1px solid rgba(236,72,153,0.2)' }}>
          <Button onClick={() => setSelectCharDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>确定</Button>
        </DialogActions>
      </Dialog>

      {/* 词条选择弹窗（章纲编辑时选词条） */}
      <Dialog open={selectItemDialogOpen} onClose={() => setSelectItemDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'rgba(0,0,0,0.8)', color: '#ff6b35', borderBottom: '1px solid rgba(255,107,53,0.2)' }}>
          选择涉及词条
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'rgba(0,0,0,0.9)', maxHeight: '60vh', overflow: 'auto' }}>
          {items.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {items.map(item => (
                <Box
                  key={item._id}
                  onClick={() => {
                    setEditingChapterOutline(prev => {
                      if (!prev) return prev;
                      const currentIds = prev.itemIds || [];
                      const newIds = currentIds.includes(item._id)
                        ? currentIds.filter(id => id !== item._id)
                        : [...currentIds, item._id];
                      return { ...prev, itemIds: newIds };
                    });
                  }}
                  sx={{
                    p: 1.5, borderRadius: 1,
                    bgcolor: (editingChapterOutline?.itemIds || []).includes(item._id) ? 'rgba(255,107,53,0.2)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${(editingChapterOutline?.itemIds || []).includes(item._id) ? 'rgba(255,107,53,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1,
                    '&:hover': { bgcolor: 'rgba(255,107,53,0.1)' },
                  }}
                >
                  <Checkbox checked={(editingChapterOutline?.itemIds || []).includes(item._id)} sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#ff6b35' } }} />
                  <Typography sx={{ color: '#ff6b35', fontSize: 14 }}>{item.name}</Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}>暂无词条，请先在词条库创建</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'rgba(0,0,0,0.8)', borderTop: '1px solid rgba(255,107,53,0.2)' }}>
          <Button onClick={() => setSelectItemDialogOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)' }}>确定</Button>
        </DialogActions>
      </Dialog>

      {/* 前章未完成警告对话框 */}
      <Dialog open={prevChapterWarning.open} onClose={() => setPrevChapterWarning(p => ({ ...p, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#ffd700', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ fontSize: 20, color: '#ffd700' }} />
          前章未完成，无法保证剧情连贯
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: 'rgba(0,0,0,0.4)', borderColor: 'rgba(255,215,0,0.15)' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, mb: 1.5 }}>
            第{prevChapterWarning.prevChapter?.chapterNumber || '?'}章还没有填写核心事件或正文内容。
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, mb: 1 }}>
            为保证故事连贯衔接，请先为上一章生成章纲或补充正文，再生成本章章纲。
          </Typography>
          <Typography sx={{ color: 'rgba(255,215,0,0.7)', fontSize: 12 }}>
            如需强制继续，请点击下方「仍然生成」；建议先去完善前章。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setPrevChapterWarning(p => ({ ...p, open: false }))}
            sx={{ color: 'rgba(255,255,255,0.6)', borderColor: 'rgba(255,255,255,0.2)' }}
            variant="outlined"
          >
            取消
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setPrevChapterWarning(p => ({ ...p, open: false }));
              if (doRunSmartOutlineGenerateRef.current) doRunSmartOutlineGenerateRef.current();
            }}
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #ffd700 50%, #fbbf24 100%)',
              color: '#422006', fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' },
            }}
          >
            仍然生成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除对话确认（应用内对话框，不使用浏览器原生 confirm） */}
      <Dialog
        open={deleteChatDialog.open}
        onClose={() => !deleteChatLoading && setDeleteChatDialog({ open: false, id: null })}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          className: 'character-panel-cyber',
          sx: {
            bgcolor: 'rgba(10, 11, 18, 0.98)',
            backgroundImage: 'none',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            borderRadius: 2,
            boxShadow: '0 0 60px rgba(0, 0, 0, 0.8), 0 0 80px rgba(239, 68, 68, 0.1)',
          },
        }}
      >
        <DialogTitle className="character-panel-title-glow" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.95) !important', borderBottom: '1px solid rgba(255,255,255,0.08)', py: 1.5 }}>
          <DeleteIcon sx={{ color: '#ef4444', fontSize: 20 }} />
          删除对话
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 14, lineHeight: 1.6 }}>
            确定要删除这个对话吗？删除后无法恢复。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Button
            disabled={deleteChatLoading}
            onClick={() => setDeleteChatDialog({ open: false, id: null })}
            className="workspace-btn-cyber"
            sx={{
              flex: 1,
              color: 'rgba(255,255,255,0.85)',
              border: '1px solid rgba(255,255,255,0.18)',
              minHeight: 44,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.3)' },
            }}
          >
            取消
          </Button>
          <Button
            disabled={deleteChatLoading}
            onClick={confirmDeleteChatConversation}
            className="workspace-btn-cyber"
            sx={{
              flex: 1,
              color: '#ef4444',
              border: '1px solid rgba(239,68,68,0.45)',
              minHeight: 44,
              '&:hover': { bgcolor: 'rgba(239,68,68,0.12)', borderColor: 'rgba(239,68,68,0.7)' },
            }}
          >
            {deleteChatLoading ? '删除中…' : '确定删除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 弹窗自动关闭逻辑 */}
      {snackbar.open && (
        <style>{`
          .cyber-snackbar {
            animation: cyberSnackbarIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
          }
        `}</style>
      )}
    </Box>
  );
};

export default Workspace;
